// This file is auto-generated by @hey-api/openapi-ts

/**
 * Everything a client needs to decide what to show.
 *
 * Hiding a control is UX, not authorization: every endpoint checks again.
 * This exists so a page does not render a button that will 403.
 *
 * Attributes:
 * principal_id: The caller.
 * permissions: Every permission held anywhere, by name.
 * tenant_wide: The subset held across the whole organization.
 * scoped: Per permission held only at some scopes, the ids it reaches.
 * reporting_currency: The caller's organization's ISO 4217 reporting
 * currency: what a page converts into by default (``USD`` until
 * one is chosen). Never null.
 */
export type AccessRead = {
    principal_id: string;
    permissions: Array<string>;
    tenant_wide: Array<string>;
    scoped: Array<ScopedAccessRead>;
    reporting_currency: string;
};

/**
 * How much of the organization the caller's grants let the panel read.
 *
 * A hint for the page's "Showing N stores you can access" line; the figures
 * are already computed over exactly that set.
 *
 * Attributes:
 * stores: The stores the panel reads for the caller, in every currency,
 * before the filter bar narrows them.
 * restricted: Whether a grant pinned below the organization narrows any
 * of the panel's streams; false when every stream is read whole.
 */
export type AccessScope = {
    stores: number;
    restricted: boolean;
};

/**
 * The account id a store is keyed on (``external_seller_id``).
 */
export type AccountRefRead = {
    label: string;
    help: string;
    required: boolean;
};

/**
 * Amazon's answer to the latest acknowledgement we submitted.
 */
export type AckStatus = 'PENDING' | 'SUCCESS' | 'FAILURE';

/**
 * Amazon's answer to the latest acknowledgement we submitted.
 */
export const AckStatus = {
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
    FAILURE: 'FAILURE'
} as const;

/**
 * An action's outcome: each warehouse's result, and the PO as it now stands.
 */
export type ActionRead = {
    legs: Array<LegRead>;
    detail: DetailRead;
};

/**
 * What a Review action would do on Amazon (VentaHub ``action_taken``, RR:965-1131).
 */
export type ActionTaken = 'edit_target' | 'add_negative' | 'add_target';

/**
 * What a Review action would do on Amazon (VentaHub ``action_taken``, RR:965-1131).
 */
export const ActionTaken = {
    EDIT_TARGET: 'edit_target',
    ADD_NEGATIVE: 'add_negative',
    ADD_TARGET: 'add_target'
} as const;

/**
 * One event about an entity: created, retired, connected, a note added.
 *
 * Attributes:
 * id: The event's id.
 * type: The event type, e.g. ``warehouse.retired``.
 * occurred_at: Business time.
 * actor: The dv-auth principal, a service name, or ``system``.
 * payload: What changed; each event type's payload is its own small
 * contract.
 */
export type ActivityItem = {
    id: string;
    type: string;
    occurred_at: string;
    actor: string;
    payload: {
        [key: string]: unknown;
    };
};

/**
 * An entity's events, newest first.
 */
export type ActivityList = {
    items: Array<ActivityItem>;
};

/**
 * Somebody who made changes in the window, for the Person filter.
 *
 * Attributes:
 * actor_id: Their user id; null for the system.
 * name: Their name, or email when no name is set; null for the system
 * and for an id this organization has no user for.
 * count: Changes in the window they made.
 */
export type ActorOption = {
    actor_id: number | null;
    name: string | null;
    count: number;
};

/**
 * One ad group from the master.
 *
 * Attributes:
 * id: ``pk_ag_id``.
 * store_id: Which store.
 * store_name: Its name.
 * ad_group_id: Amazon's id.
 * ad_group_name: Its name.
 * ad_group_type: SP, SB or SD.
 * ad_group_label: BRANDED, COMPETITION, LONGTAIL, HIGH_PRIORITY, PT or EM.
 * state: As loaded.
 * brand_name: The brand it advertises.
 * campaign_id: Amazon's campaign id.
 * campaign_name: Its name.
 * budget: Its budget.
 * asp: Target average selling price.
 * target_acos: A fraction.
 * target_ctr: A fraction.
 * target_conversion: A fraction.
 * targeting_type: MANUAL-KEYWORD, MANUAL-PRODUCT or AUTO.
 * category: As loaded.
 * subcategory: As loaded.
 * siblings: Its linked ad groups.
 */
export type AdGroupMasterRead = {
    id: number;
    store_id: number;
    store_name: string;
    ad_group_id: string;
    ad_group_name: string;
    ad_group_type: string;
    ad_group_label: string | null;
    state: string;
    brand_name: string | null;
    campaign_id: string | null;
    campaign_name: string | null;
    budget: AdsMoney | null;
    asp: AdsMoney | null;
    target_acos: number | null;
    target_ctr: number | null;
    target_conversion: number | null;
    targeting_type: string | null;
    category: string | null;
    subcategory: string | null;
    siblings: Array<AdGroupSibling>;
};

/**
 * A linked ad group.
 *
 * Attributes:
 * role: exact_match, product_target, branded, competition, longtail or
 * high_priority.
 * ad_group_id: Amazon's id.
 * ad_group_name: Its name, when that ad group is in the master.
 */
export type AdGroupSibling = {
    role: string;
    ad_group_id: string;
    ad_group_name: string | null;
};

/**
 * Which report an ASIN row is resolved against (RSC:506-509).
 *
 * ``ad_product`` reads the advertised-product report (full metric set);
 * ``alt_product`` the purchased-product report (units and sales only).
 */
export type AdProductInstanceLevel = 'ad_product' | 'alt_product';

/**
 * Which report an ASIN row is resolved against (RSC:506-509).
 *
 * ``ad_product`` reads the advertised-product report (full metric set);
 * ``alt_product`` the purchased-product report (units and sales only).
 */
export const AdProductInstanceLevel = {
    AD_PRODUCT: 'ad_product',
    ALT_PRODUCT: 'alt_product'
} as const;

/**
 * How often a focused search term showed our product in a sponsored slot.
 *
 * Attributes:
 * search_term: The term.
 * store_id: The store whose ASINs were looked for.
 * store_name: Its name.
 * marketplace_code: The marketplace's short code.
 * country_code: Its country.
 * total: Scrapes where one of the store's focused ASINs showed at all.
 * winning: Of those, where one held a sponsored slot.
 * win_rate: ``winning / total``.
 */
export type AdSlot = {
    search_term: string;
    store_id: number;
    store_name: string | null;
    marketplace_code: string | null;
    country_code: string | null;
    total: number;
    winning: number;
    win_rate: number;
};

/**
 * Focused search terms over the last 7 days.
 */
export type AdSlots = {
    since: string;
    rows: Array<AdSlot>;
};

/**
 * What an upload would raise, before it is raised.
 *
 * Attributes:
 * rows: Lines read from the file.
 * new: Lines the store does not have yet.
 * updated: Lines whose values change.
 * unchanged: Lines identical to what is stored.
 * warnings: Values worth a second look; the upload is not refused for them.
 */
export type AdUploadCheck = {
    rows: number;
    new: number;
    updated: number;
    unchanged: number;
    warnings: Array<AdUploadWarning>;
};

/**
 * One upload a store takes.
 *
 * Attributes:
 * key: Its id, sent back with the file.
 * name: What the page calls it.
 * report_date: ``none`` (the file is not dated), ``optional`` (the day
 * fills rows with no date of their own) or ``required`` (the file
 * has no dates; the uploader picks the day it covers).
 */
export type AdUploadKindRead = {
    key: string;
    name: string;
    report_date: 'none' | 'optional' | 'required';
};

/**
 * One ads upload, for the page's upload history.
 *
 * Attributes:
 * ticket_id: The approval ticket, as URLs carry it.
 * ticket_number: Its ``#`` number.
 * upload: The upload's key (``adgroup_master``, ``noon_ads_report``…).
 * upload_name: What the page calls it.
 * platform_name: The platform it was for.
 * file_name: The file as the uploader named it.
 * store_name: The store it was for.
 * created_at: When it was uploaded.
 * uploaded_by: Who uploaded it.
 * outcome: ``pending``, ``accepted``, ``rejected`` or ``failed`` (an
 * accept that could not be written).
 * reviewed_by: Who decided it, once decided.
 * rows: Lines on the ticket.
 */
export type AdUploadRead = {
    ticket_id: string;
    ticket_number: number;
    upload: string;
    upload_name: string;
    platform_name: string;
    file_name: string | null;
    store_name: string | null;
    created_at: string;
    uploaded_by: string | null;
    outcome: string;
    reviewed_by: string | null;
    rows: number;
};

/**
 * What an upload raised: one T16 waiting for a reviewer.
 *
 * Attributes:
 * ticket_id: The approval ticket, as URLs carry it.
 * ticket_number: Its ``#`` number.
 * rows: Lines read from the file.
 * new: Lines the store does not have yet.
 * updated: Lines whose values change.
 * unchanged: Lines identical to what is stored, left off the ticket.
 */
export type AdUploadResult = {
    ticket_id: string;
    ticket_number: number;
    rows: number;
    new: number;
    updated: number;
    unchanged: number;
};

/**
 * A store the caller can upload ads data for, on any platform.
 *
 * Attributes:
 * store_id: The store.
 * store_name: Its name.
 * marketplace_id: Its marketplace.
 * marketplace: Its marketplace's name.
 * country_code: Its marketplace's country, for the flag.
 * platform: The marketplace short-code prefix (``AZ``, ``OT``, ``NN``).
 * platform_name: The platform as the page names it.
 * uploads: The uploads it takes, at least one.
 */
export type AdUploadStoreRead = {
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace: string;
    country_code: string;
    platform: string;
    platform_name: string;
    uploads: Array<AdUploadKindRead>;
};

/**
 * A value the upload accepts but the operator should confirm.
 *
 * Attributes:
 * row: The sheet row (the header is row 1).
 * field: The column.
 * value: The value as typed.
 * issue: What looks wrong.
 */
export type AdUploadWarning = {
    row: number;
    field: string;
    value: string;
    issue: string;
};

/**
 * A person's own daily limits; null means the default applies.
 */
export type AdminBudgetRead = {
    principal_id: string;
    daily_run_limit?: number | null;
    daily_usd_limit?: number | null;
    note?: string | null;
};

/**
 * An eval question promoted from review.
 */
export type AdminEvalQuestionRead = {
    id: string;
    slug: string;
    question: string;
    tags: Array<string>;
    active: boolean;
    source_run_id: string | null;
    created_at: string;
};

/**
 * One eval sweep.
 *
 * Attributes:
 * kind: ``nightly`` (the scheduled sweep), ``manual`` or ``smoke``.
 * config: The models and efforts swept.
 * mean_recall: Mean share of the reference numbers found, 0-1.
 * markdown: The full report.
 */
export type AdminEvalRunRead = {
    id: string;
    kind: string;
    config: string;
    passed: number;
    total: number;
    mean_recall: number;
    cost_usd: number;
    prompt_version: string | null;
    summary?: EvalSummary | null;
    markdown?: string | null;
    created_at: string;
};

/**
 * A review flag as reviewers see it.
 *
 * Attributes:
 * origin: ``agent`` (the analyst flagged itself), ``auto`` or ``user``.
 * fix_ref: The change that fixed it: a PR, a commit, a catalog edit.
 */
export type AdminFlagRead = {
    id: string;
    category: string;
    reason: string;
    origin: string;
    status: FlagStatus;
    admin_note: string | null;
    created_at: string;
    fix_ref?: string | null;
};

/**
 * Health and quality over the last ``days`` days.
 *
 * Attributes:
 * totals: The period's runs, success rate and cost.
 * cache_read_share_pct: Prompt tokens read from the cache, of all prompt tokens.
 * open_flags: Open flags by category (all time).
 * eval_runs: The latest sweeps, newest first.
 * eval_pass_by_domain: The latest scheduled sweep, per domain: where
 * coverage is weakest (plan §6.4).
 * sql_fallback: Open ``sql_fallback`` flags by dataset: the measures
 * to add next (plan §6.4).
 */
export type AdminOverview = {
    days: number;
    totals: OverviewTotals;
    daily: Array<OverviewDay>;
    by_depth: Array<OverviewDepth>;
    cache_read_share_pct: number | null;
    feedback: OverviewFeedback;
    by_prompt_version: Array<OverviewGroup>;
    by_variant: Array<OverviewGroup>;
    clarifying: OverviewClarifying;
    deep_suggestions: OverviewDeepSuggestions;
    open_flags: {
        [key: string]: number;
    };
    resolved_flags: OverviewResolvedFlags;
    eval_runs: Array<AdminEvalRunRead>;
    eval_pass_by_domain: Array<DomainPassRate>;
    sql_fallback: SqlFallbackBacklog;
};

/**
 * One run in the question log.
 *
 * Attributes:
 * principal_id: Who asked.
 * user_name: Their display name, when this tenant has been told one.
 * answer_preview: The answer's first 400 characters.
 * failed_steps: Queries and endpoint calls that failed in this run.
 * rating: The asker's own rating, +1 or -1.
 * signals: Implicit signals, in the order they arrived.
 * variant: The prompt variant (``a`` or ``b``).
 * route_kind: The router's verdict: ``lookup``, ``comparison``, ``diagnosis``.
 * clarifying: The answer was a clarifying question.
 * assumptions: What the answer assumed, as it reported them.
 */
export type AdminQuestionRead = {
    run_id: string;
    conversation_id: string;
    created_at: string;
    status: AnalystRunStatus;
    mode: RunMode;
    principal_id: string;
    user_name?: string | null;
    question: string;
    answer_preview: string;
    cost_usd: number;
    duration_seconds: number | null;
    query_count: number;
    failed_steps?: number;
    flags?: Array<AdminFlagRead>;
    rating?: number | null;
    rating_reason?: FeedbackReason | null;
    rating_comment?: string | null;
    signals?: Array<string>;
    prompt_version?: string | null;
    variant?: string | null;
    route_kind?: string | null;
    clarifying?: boolean;
    assumptions?: {
        [key: string]: unknown;
    };
};

/**
 * Everything that produced one answer.
 *
 * Attributes:
 * row: The run as the question log lists it.
 * answer_parts: The answer exactly as it streamed.
 * steps: The trace, in order.
 * models: The models configured and served, and any fallbacks.
 * scope: The scope the run read under, in business terms.
 */
export type AdminRunTrace = {
    row: AdminQuestionRead;
    answer_parts: Array<{
        [key: string]: unknown;
    }>;
    steps: Array<TraceStep>;
    models: {
        [key: string]: unknown;
    };
    scope: {
        [key: string]: unknown;
    };
    tokens: TokenCounts;
    error_code: string | null;
    error_detail: string | null;
};

/**
 * One person's usage over the window, with the limits that apply to them today.
 *
 * Attributes:
 * runs: Answers over the window.
 * today_runs: Answers today, in the budget's time zone.
 * has_override: Their limits are their own, not the defaults.
 */
export type AdminUsageRead = {
    principal_id: string;
    user_name?: string | null;
    runs: number;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    today_runs: number;
    today_cost_usd: number;
    daily_run_limit: number;
    daily_usd_limit: number;
    has_override: boolean;
};

/**
 * The Amazon Ads account behind the store's profile, as last refreshed.
 *
 * Stored in ``marketplace_meta.amazon`` under v1 VentaHub's keys
 * (``account_id``, ``account_name``, ``account_type``), which its Ads
 * stream consumer matches advertisers on.
 */
export type AdsAccountMeta = {
    account_id: string | null;
    name: string | null;
    type: string | null;
};

/**
 * What advertising can be grouped by.
 */
export type AdsGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'ad_type' | 'campaign' | 'ad_group' | 'product' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'day' | 'week' | 'month';

/**
 * What advertising can be grouped by.
 */
export const AdsGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    AD_TYPE: 'ad_type',
    CAMPAIGN: 'campaign',
    AD_GROUP: 'ad_group',
    PRODUCT: 'product',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * An amount and its currency.
 *
 * Attributes:
 * amount: Decimal, as a string — a JSON number is a double.
 * currency: ISO 4217.
 */
export type AdsMoney = {
    amount: string;
    currency: string;
};

/**
 * An Amazon Ads profile the credentials can reach.
 */
export type AdsProfile = {
    profile_id: number;
    country_code?: string | null;
    currency_code?: string | null;
    timezone?: string | null;
    marketplace_id?: string | null;
    account_id?: string | null;
    account_name?: string | null;
    account_type?: string | null;
};

/**
 * The Ads profiles the store's stored credentials reach, and which fits it.
 *
 * Attributes:
 * ok: Whether the probe reached Amazon Ads. ``false`` with ``error``
 * when the credentials were refused; ``profiles`` is then empty.
 * profiles: Every profile, as ingest listed them.
 * best_match: The profile whose account and country fit the store, if
 * exactly one does.
 * current_profile_id: ``com_store.profile_id``.
 * error: Why the probe failed, in words a person can act on.
 */
export type AdsProfiles = {
    ok: boolean;
    profiles: Array<AdsProfile>;
    best_match: number | null;
    current_profile_id: number | null;
    error: ProbeError | null;
};

/**
 * Advertising, grouped.
 */
export type AdsReport = {
    items: Array<AdsRow>;
    pagination: Pagination;
    totals: Array<AdsTotals>;
    meta: ReportMeta;
};

/**
 * One group of ads and days.
 *
 * ``product_ref`` is filled when grouped by product, the advertised ASIN,
 * as well as by listing.
 *
 * Attributes:
 * ad_type: Filled when grouped by ad type: ``SP`` Sponsored Products,
 * ``SB`` Sponsored Brands or ``SD`` Sponsored Display, as typed on
 * the ad group.
 * campaign_id: Filled when grouped by campaign.
 * campaign_name: Its name.
 * platform_campaign_id: Amazon's id for it.
 * ad_group_id: Filled when grouped by ad group.
 * ad_group_name: Its name.
 * platform_ad_group_id: Amazon's id for it.
 */
export type AdsRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    impressions?: number | null;
    clicks?: number | null;
    units?: number | null;
    spend?: MoneyRead | null;
    ad_sales?: MoneyRead | null;
    ctr?: string | null;
    cvr?: string | null;
    acos?: string | null;
    roas?: string | null;
    cpc?: MoneyRead | null;
    ad_type?: string | null;
    campaign_id?: number | null;
    campaign_name?: string | null;
    platform_campaign_id?: string | null;
    ad_group_id?: number | null;
    ad_group_name?: string | null;
    platform_ad_group_id?: string | null;
};

/**
 * An Amazon store with an Ads profile.
 *
 * Attributes:
 * store_id: ``com_store.pk_store_id``.
 * store_name: Display name.
 * profile_id: The Amazon Ads profile, as a string — it overflows a
 * JavaScript number.
 * marketplace: The marketplace's name, e.g. "Amazon UK".
 * country_code: ISO 3166-1 alpha-2.
 * currency: The marketplace's currency.
 */
export type AdsStoreRead = {
    store_id: number;
    store_name: string;
    profile_id: string;
    marketplace: string;
    country_code: string;
    currency: string;
};

/**
 * Advertising over every matching ad and day, in one currency.
 *
 * Amazon's ad-group report is the total wherever it has a row: what it
 * holds beyond the ads — Sponsored Brands, days the product report missed,
 * small signed differences — is in rows of their own, with no product,
 * listing or SKU (D22). The ratios are computed from the sums at the grain
 * shown, never averaged.
 *
 * Attributes:
 * currency_code: The store's marketplace currency.
 * impressions: Times the ads were shown.
 * clicks: Clicks on them.
 * units: Units Amazon attributes to the ads, for Sponsored Products in
 * the 14 days after the click.
 * spend: What the ads cost.
 * ad_sales: Sales Amazon attributes to the ads, in the same window.
 * ctr: Clicks ÷ impressions, a fraction (0.0125 is 1.25%).
 * cvr: Units ÷ clicks: orders were never stored, so units per click is
 * the conversion.
 * acos: Spend ÷ ad sales; ``null`` with no ad sales.
 * roas: Ad sales ÷ spend.
 * cpc: Spend ÷ clicks.
 */
export type AdsTotals = {
    currency_code?: string | null;
    impressions?: number | null;
    clicks?: number | null;
    units?: number | null;
    spend?: MoneyRead | null;
    ad_sales?: MoneyRead | null;
    ctr?: string | null;
    cvr?: string | null;
    acos?: string | null;
    roas?: string | null;
    cpc?: MoneyRead | null;
};

/**
 * ``ads`` in a settings PATCH: the profile, from ``POST .../ads/profiles``.
 */
export type AdsUpdate = {
    profile_id: number | null;
};

/**
 * How much of the warehouse's stock can be dated. The parts add up to ``total_units``.
 */
export type AgeingCoverageRead = {
    total_units: number;
    dated_units: number;
    pre_migration_units: number;
    unknown_units: number;
};

/**
 * What an aggregate groups by.
 */
export type AggregateLevel = 'sku' | 'brand' | 'category' | 'subcategory';

/**
 * What an aggregate groups by.
 */
export const AggregateLevel = {
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory'
} as const;

/**
 * A dataset rolled up at one level.
 */
export type AggregateRead = {
    level: AggregateLevel;
    metric_names: Array<string>;
    items: Array<AggregateRow>;
    totals: Array<AggregateTotals>;
};

/**
 * One group of an aggregate.
 *
 * Attributes:
 * key: The group's id — SKU code, brand id, category or subcategory
 * text — or ``''`` for rows with none.
 * label: What to show; ``Unassigned`` for the keyless group.
 * currency_code: The currency the money metrics are in, when any.
 * metrics: Metric name to value, in the dataset's metric order.
 */
export type AggregateRow = {
    key: string;
    label: string;
    currency_code: string | null;
    metrics: {
        [key: string]: string | number | null;
    };
};

/**
 * The same metrics over every row of one currency.
 */
export type AggregateTotals = {
    currency_code: string | null;
    metrics: {
        [key: string]: string | number | null;
    };
};

/**
 * Where a job is.
 *
 * One status per pipeline step, in the order they run, so the status *is*
 * the progress a seller sees. The column has no database constraint — the
 * vocabulary is the application's, as it was in the source.
 */
export type AiListingJobStatus = 'pending' | 'analyzing' | 'crafting_prompts' | 'generating_text' | 'generating_lifestyle' | 'generating_aplus' | 'generating_infographics' | 'validating' | 'completed' | 'failed';

/**
 * Where a job is.
 *
 * One status per pipeline step, in the order they run, so the status *is*
 * the progress a seller sees. The column has no database constraint — the
 * vocabulary is the application's, as it was in the source.
 */
export const AiListingJobStatus = {
    PENDING: 'pending',
    ANALYZING: 'analyzing',
    CRAFTING_PROMPTS: 'crafting_prompts',
    GENERATING_TEXT: 'generating_text',
    GENERATING_LIFESTYLE: 'generating_lifestyle',
    GENERATING_APLUS: 'generating_aplus',
    GENERATING_INFOGRAPHICS: 'generating_infographics',
    VALIDATING: 'validating',
    COMPLETED: 'completed',
    FAILED: 'failed'
} as const;

/**
 * Something about a store's credentials that needs a person.
 */
export type Alert = {
    id: string;
    severity: Severity;
    kind: AlertKind;
    store: AlertStore;
    surface: AuthSurface;
    title: string;
    detail: string;
    expires_at: string | null;
    days_left: number | null;
    detected_at: string;
};

/**
 * How a route delivers.
 */
export type AlertChannel = 'email' | 'slack';

/**
 * How a route delivers.
 */
export const AlertChannel = {
    EMAIL: 'email',
    SLACK: 'slack'
} as const;

/**
 * How many alerts of each severity.
 */
export type AlertCounts = {
    critical: number;
    warning: number;
    info: number;
};

/**
 * What an alert is about.
 */
export type AlertKind = 'credential_expired' | 'credential_expiring' | 'credential_failing' | 'circuit_open' | 'enabled_without_credentials' | 'amazon_secret_rotated' | 'data_stale' | 'write_freeze';

/**
 * What an alert is about.
 */
export const AlertKind = {
    CREDENTIAL_EXPIRED: 'credential_expired',
    CREDENTIAL_EXPIRING: 'credential_expiring',
    CREDENTIAL_FAILING: 'credential_failing',
    CIRCUIT_OPEN: 'circuit_open',
    ENABLED_WITHOUT_CREDENTIALS: 'enabled_without_credentials',
    AMAZON_SECRET_ROTATED: 'amazon_secret_rotated',
    DATA_STALE: 'data_stale',
    WRITE_FREEZE: 'write_freeze'
} as const;

/**
 * Every live alert, critical first, then soonest.
 */
export type AlertList = {
    items: Array<Alert>;
    counts: AlertCounts;
};

/**
 * Response of ``GET`` and ``PUT /v1/alert-routes``, by id.
 */
export type AlertRouteList = {
    items: Array<AlertRouteRead>;
};

/**
 * One alert route; its Slack webhook is never returned, only whether one is set.
 *
 * Attributes:
 * store_id: Only this store's alerts.
 * tag: Only stores carrying this tag.
 * group_id: Only stores in this group. With none of the three, every store.
 * min_severity: The least an alert must be to be sent.
 * channel: ``email`` or ``slack``.
 * emails: The addresses, for an email route.
 * slack_configured: Whether a Slack webhook is stored.
 */
export type AlertRouteRead = {
    id: number;
    store_id: number | null;
    tag: string | null;
    group_id: number | null;
    min_severity: Severity;
    channel: AlertChannel;
    emails: Array<string>;
    slack_configured: boolean;
};

/**
 * One route in ``PUT /v1/alert-routes``.
 *
 * Attributes:
 * id: An existing route to keep and change; omitted for a new one.
 * store_id: Only this store. At most one of ``store_id``, ``tag`` and
 * ``group_id``.
 * tag: Only stores carrying this tag.
 * group_id: Only stores in this group.
 * min_severity: The least an alert must be.
 * channel: ``email`` or ``slack``.
 * emails: At least one address for an email route; ignored for Slack.
 * slack_webhook_url: For a Slack route. Omitted on an existing Slack
 * route keeps the stored one; required on a new one.
 */
export type AlertRouteWrite = {
    id?: number | null;
    store_id?: number | null;
    tag?: string | null;
    group_id?: number | null;
    min_severity: Severity;
    channel: AlertChannel;
    emails?: Array<string>;
    slack_webhook_url?: string | null;
};

/**
 * Body of ``PUT /v1/alert-routes``: the whole list; routes left out are removed.
 */
export type AlertRoutesUpdate = {
    items: Array<AlertRouteWrite>;
};

/**
 * A tenant's alert settings.
 */
export type AlertSettingsRead = {
    recipients: Array<string>;
    auto_apply_amazon_rotation: boolean;
};

/**
 * Body of ``PUT /v1/settings/alerts``.
 */
export type AlertSettingsWrite = {
    recipients: Array<string>;
    auto_apply_amazon_rotation: boolean;
};

/**
 * The store an alert is about.
 */
export type AlertStore = {
    id: number;
    code: string;
    name: string;
};

/**
 * A glossary alias, prompted on every run.
 */
export type AliasRead = {
    id: string;
    term: string;
    meaning: string;
};

/**
 * A glossary alias: a word people use here and what it means.
 */
export type AliasWrite = {
    term: string;
    meaning: string;
};

/**
 * One line of a PO against what is allotted to it.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * sku_name: Its name.
 * retailer_product_code: The retailer's code for it.
 * quantity_ordered: Ordered.
 * item_price: Price per unit.
 * allotted: Units in boxes allotted to the PO.
 * blocked: Units blocked under the PO's code.
 * invoiced: Units invoiced against the PO.
 * open: Ordered less allotted, blocked and invoiced; never below zero.
 */
export type AllotmentLineRead = {
    sku_code: string | null;
    sku_name: string | null;
    retailer_product_code: string | null;
    quantity_ordered: number;
    item_price: string | null;
    allotted: number;
    blocked: number;
    invoiced: number;
    open: number;
};

/**
 * What is allotted to a PO, box by box and line by line.
 */
export type AllotmentRead = {
    boxes: Array<AllottedBoxRead>;
    lines: Array<AllotmentLineRead>;
};

/**
 * The boxes to allot to a PO: the whole set.
 *
 * Boxes allotted now and left out are released back to the shelf, so an
 * empty list releases every box.
 *
 * Attributes:
 * box_codes: Box codes in the PO's warehouse.
 */
export type AllotmentWrite = {
    box_codes: Array<string>;
};

/**
 * One box allotted to a PO.
 *
 * Attributes:
 * box_id: The box.
 * box_code: Its code.
 * units: What is in it.
 */
export type AllottedBoxRead = {
    box_id: number;
    box_code: string;
    units: number;
};

/**
 * One daily allowance.
 *
 * Attributes:
 * limit: Per day, or None when switched off.
 * used: Spent today.
 * remaining: Left today, or None when switched off.
 */
export type AllowanceRead = {
    limit: number | null;
    used: number;
    remaining: number | null;
};

/**
 * One Amazon PO in the list.
 */
export type AmazonPoRead = {
    store_id: number;
    store_name: string;
    po_number: string;
    ordered_at: string | null;
    /**
     * Amazon's own state: OPEN or CLOSED.
     */
    po_status: string | null;
    sku_count: number;
    units_ordered: number;
    units_confirmed: number;
    units_delivered: number;
    amount: MoneyRead | null;
    status: BridgeStatus;
    note: string | null;
    amazon_acknowledged_at: string | null;
    amazon_ack_status: AckStatus | null;
};

/**
 * What a successful Review action returns (RSC:612-616 ``ActionResponse``).
 *
 * Unreachable while Amazon writes are disabled — every action answers 423 —
 * and declared so the client is typed for the day they are not.
 *
 * Attributes:
 * success: Always true on a 2xx.
 * action_taken: What was done.
 * bid: The bid sent, a decimal string (money, be-arch §5.4).
 * reasoning: The bid's derivation, shown in the change-bid modal.
 */
export type AmsActionResponse = {
    success?: boolean;
    action_taken: ActionTaken;
    bid?: string | null;
    reasoning?: string | null;
};

/**
 * ``marketing_ad_group_master.ad_group_type`` values (RSC:44-72 ``Literal``).
 *
 * The aggregate route validated the same three values by hand
 * (``ams_view/routes.py:2934-2938``).
 */
export type AmsAdGroupType = 'SP' | 'SB' | 'SD';

/**
 * ``marketing_ad_group_master.ad_group_type`` values (RSC:44-72 ``Literal``).
 *
 * The aggregate route validated the same three values by hand
 * (``ams_view/routes.py:2934-2938``).
 */
export const AmsAdGroupType = {
    SP: 'SP',
    SB: 'SB',
    SD: 'SD'
} as const;

/**
 * Bucket size (S:3548 ``_TREND_GRANULARITIES``).
 *
 * VentaHub's ``hourly`` is not offered: it read the Amazon Marketing Stream's
 * hourly table, which VentaWiz does not have.
 */
export type AmsTrendGranularity = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * Bucket size (S:3548 ``_TREND_GRANULARITIES``).
 *
 * VentaHub's ``hourly`` is not offered: it read the Amazon Marketing Stream's
 * hourly table, which VentaWiz does not have.
 */
export const AmsTrendGranularity = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    YEARLY: 'yearly'
} as const;

/**
 * The five summable measures a trend can plot (S:3547 ``_TREND_METRICS``).
 */
export type AmsTrendMetric = 'impressions' | 'clicks' | 'sales' | 'spend' | 'units';

/**
 * The five summable measures a trend can plot (S:3547 ``_TREND_METRICS``).
 */
export const AmsTrendMetric = {
    IMPRESSIONS: 'impressions',
    CLICKS: 'clicks',
    SALES: 'sales',
    SPEND: 'spend',
    UNITS: 'units'
} as const;

/**
 * One bucket of a trend series (VentaHub ``CampaignTrendPoint``, SCH:440-457).
 *
 * Attributes:
 * period: ``YYYY-MM-DD`` (daily; weekly = the ISO Monday), ``YYYY-MM``,
 * ``YYYY-Qn`` or ``YYYY`` — see ``kit.ams.format_trend_period``.
 * value: The summed metric for the bucket. A decimal, so it reaches the
 * wire as a string, as VentaHub's ``Decimal`` field did.
 */
export type AmsTrendPoint = {
    period: string;
    value: string;
};

/**
 * Live ENABLED / PAUSED ad-group counts for one store (``AdGroupCountsItem``, SCH:410-413).
 *
 * Attributes:
 * store_id: ``com_store.pk_store_id``.
 * enabled: Ad groups whose upper-cased state is ENABLED.
 * paused: Ad groups whose upper-cased state is PAUSED.
 */
export type AmsViewAdGroupCount = {
    store_id: number;
    enabled: number;
    paused: number;
};

/**
 * Every eligible store, zero-filled (``AdGroupCountsResponse``, SCH:416-417).
 *
 * Attributes:
 * counts: One entry per store the sellers list returns, in its order.
 */
export type AmsViewAdGroupCountsResponse = {
    counts?: Array<AmsViewAdGroupCount>;
};

/**
 * Every requested store's rows in one response (R:574-593).
 *
 * VentaHub computed per-store totals and a search summary here and then
 * discarded them (R:577-587); they are not computed.
 *
 * Attributes:
 * items: The rows, store by store in request order, each store's rows in
 * VentaHub's order (status rank, then name).
 */
export type AmsViewAdGroupMultiResponse = {
    items?: Array<AmsViewAdGroupMultiRow>;
};

/**
 * One ad group with its window metrics, tagged with its store (R:588-591).
 *
 * VentaHub's ``AdGroupResponse`` (SCH:10-96) as the multi-store route emitted it
 * (``row_dict["store_id"] = sid``). Master-driven: every live ad group in scope
 * has a row, ``data_row_count == 0`` when it had no data in the window
 * (S:1185-1194). ``fk_store_id``, which VentaHub's untyped dict leaked beside
 * ``store_id``, has no reader and is not part of the contract.
 *
 * Attributes:
 * pk_ag_id: ``marketing_ad_group_master.pk_ag_id``; the page sends it back
 * as ``ad_group_ids`` to scope the aggregate levels.
 * ad_group_id: Amazon's ad group id.
 * ad_group_name: The ad group's name.
 * ad_group_type: SP / SB / SD.
 * targeting_type: MANUAL-KEYWORD, MANUAL-PRODUCT, AUTO ….
 * campaign_id: Amazon's campaign id.
 * campaign_name: The campaign's name.
 * status: ``state`` of the ad group.
 * campaign_status: ``campaign_state``; null until the campaign stream has
 * been seen (render as unknown, not ENABLED).
 * currency_code: VentaHub's label, ``marketing_ad_group_master.currency_code``
 * (BUILD-PLAN Q-U1).
 * seller_name: ``com_store.store_name``.
 * dt_create: When the master row was created.
 * target_acos: Stored fraction, e.g. 0.2 (not x100).
 * target_conversion: Stored fraction.
 * target_ctr: Stored fraction.
 * asp: The configured ASP bid-formula input (money).
 * impressions: Window sum.
 * clicks: Window sum.
 * ctr: Percent x100; 0.0 when impressions = 0.
 * spend: Window sum.
 * cpc: spend / clicks; 0.0 when clicks = 0.
 * units: Window sum.
 * conversion: units / clicks x100; 0.0 when clicks = 0.
 * sales: Window sum.
 * acos: spend / sales x100; 0.0 when sales = 0.
 * unique_targets_count: Target universe (master, plus served in the window).
 * unique_search_terms_count: Distinct search terms served in the window.
 * unique_negatives_count: Negatives in their latest snapshot (not dated).
 * unique_asins_count: Ad-product universe by ``ad_id``.
 * category: Master category.
 * subcategory: Master subcategory.
 * budget: The ad group's daily budget from the master.
 * first_data_date: Oldest day with data (not window-bounded).
 * last_data_date: Newest day with data (not window-bounded).
 * avg_7d_spend: Average spend of the 7 most recent dated rows (not
 * window-bounded).
 * alternate_sales: Other-ASIN (halo) sales in the window.
 * data_row_count: Daily rows the window matched.
 * data_refreshed_at: Latest ``data_refreshed_at`` in the window.
 * profile_id: The store's Ads profile.
 * brand_id: ``fk_brand_id``; null for unbranded.
 * brand_name: The live brand's name.
 * exact_match_adgroup_id: Sister ad group id (role exact match).
 * exact_match_adgroup_name: Its name, if it is this tenant's.
 * product_target_adgroup_id: Sister (product target).
 * product_target_adgroup_name: Its name.
 * branded_adgroup_id: Sister (branded).
 * branded_adgroup_name: Its name.
 * competition_adgroup_id: Sister (competition).
 * competition_adgroup_name: Its name.
 * longtail_adgroup_id: Sister (long tail).
 * longtail_adgroup_name: Its name.
 * high_priority_adgroup_id: Sister (high priority).
 * high_priority_adgroup_name: Its name.
 * store_id: The store the row was fetched for; the page copies it to
 * ``_source_store_id``.
 */
export type AmsViewAdGroupMultiRow = {
    pk_ag_id: number;
    ad_group_id: string;
    ad_group_name: string;
    ad_group_type?: string | null;
    targeting_type?: string | null;
    campaign_id?: string | null;
    campaign_name?: string | null;
    status: string;
    campaign_status?: string | null;
    currency_code?: string | null;
    seller_name: string;
    dt_create: string;
    target_acos?: number | null;
    target_conversion?: number | null;
    target_ctr?: number | null;
    asp?: string | null;
    impressions: number;
    clicks: number;
    ctr: number;
    spend: string;
    cpc: number;
    units: number;
    conversion: number;
    sales: string;
    acos: number;
    unique_targets_count: number;
    unique_search_terms_count: number;
    unique_negatives_count: number;
    unique_asins_count: number;
    category?: string | null;
    subcategory?: string | null;
    budget?: string | null;
    first_data_date?: string | null;
    last_data_date?: string | null;
    avg_7d_spend?: string | null;
    alternate_sales?: string | null;
    data_row_count?: number;
    data_refreshed_at?: string | null;
    profile_id?: number | null;
    brand_id?: number | null;
    brand_name?: string | null;
    exact_match_adgroup_id?: string | null;
    exact_match_adgroup_name?: string | null;
    product_target_adgroup_id?: string | null;
    product_target_adgroup_name?: string | null;
    branded_adgroup_id?: string | null;
    branded_adgroup_name?: string | null;
    competition_adgroup_id?: string | null;
    competition_adgroup_name?: string | null;
    longtail_adgroup_id?: string | null;
    longtail_adgroup_name?: string | null;
    high_priority_adgroup_id?: string | null;
    high_priority_adgroup_name?: string | null;
    store_id: number;
};

/**
 * The drilled ASINs tab (SCH:167-177).
 *
 * Attributes:
 * items: Every ad of the ad group, ENABLED / ACTIVE / PAUSED first, then by id.
 */
export type AmsViewAdProductListResponse = {
    total_impressions?: number;
    total_clicks?: number;
    total_spend?: string;
    total_units?: number;
    total_sales?: string;
    avg_ctr?: number;
    avg_cpc?: number;
    avg_conversion?: number;
    avg_acos?: number;
    items?: Array<AmsViewAdProductRow>;
};

/**
 * One advertised product (ad) of the drilled ad group (SCH:124-164).
 *
 * One row per ``ad_id`` over the live master roster plus the ads with a fact row
 * in the window; metrics are MAX-de-duplicated per (ad, day) before summing,
 * because Amazon repeats an ad's metrics on one row per advertised SKU.
 *
 * Attributes:
 * ad_group_id: Amazon's ad group id.
 * ad_id: Amazon's ad id, the row key and the handle the ASIN trend uses.
 * sku_code: Master ``sku`` first, else the report's ``mp_sku_code``; null
 * when neither has one.
 * asin: Master ``asin`` first, else the report's.
 * status: Master ``state`` first, else the report's ``status``.
 * currency_code: The ad group's currency label.
 * impressions: Impressions.
 * clicks: Clicks.
 * ctr: Clicks / impressions x100.
 * spend: Spend, as a decimal string.
 * cpc: Spend / clicks.
 * units: Ordered units.
 * conversion: Units / clicks x100.
 * sales: Attributed sales, as a decimal string.
 * acos: Spend / sales x100.
 * data_row_count: 0 means the ad exists but served nothing in the window.
 */
export type AmsViewAdProductRow = {
    ad_group_id: string;
    ad_id: string;
    sku_code?: string | null;
    asin?: string | null;
    status: string;
    currency_code?: string | null;
    impressions: number;
    clicks: number;
    ctr: number;
    spend: string;
    cpc: number;
    units: number;
    conversion: number;
    sales: string;
    acos: number;
    data_row_count?: number;
};

/**
 * Advertise a product in the ad group (SCH:490-493).
 *
 * Attributes:
 * asin: The product's ASIN.
 * sku: The seller SKU.
 * state: The new ad's state.
 */
export type AmsViewAddAdProductRequest = {
    asin: string;
    sku: string;
    state: 'ENABLED' | 'PAUSED';
};

/**
 * Add a negative keyword or negative ASIN to the ad group (SCH:477-481).
 *
 * Attributes:
 * keyword: The phrase, or an ASIN.
 * match_type: Either API generation's spelling; the gate decides which one
 * the ad group's type accepts.
 */
export type AmsViewAddNegativeRequest = {
    keyword: string;
    match_type: NegativeMatchType;
};

/**
 * Add a keyword or ASIN target, routed to a sister ad group (SCH:460-463).
 *
 * Attributes:
 * target: Keyword text, or an ASIN for a product target.
 * bid: The bid. A float, as VentaHub and the Amazon payload model it feeds.
 * target_type: Keyword match type (EXACT, PHRASE or BROAD from the modal).
 * Not validated, as in VentaHub; ignored for an ASIN.
 */
export type AmsViewAddTargetRequest = {
    target: string;
    bid: number;
    target_type: string;
};

/**
 * Totals for one currency over the whole returned set (SCH:583-603).
 *
 * Money is never summed across currencies: one bucket per currency, ratios
 * derived from the bucket's sums.
 *
 * Attributes:
 * currency: The upper-cased code, or ``"—"`` for rows with none (AGG:979).
 * impressions: Summed impressions (0 on alt products).
 * clicks: Summed clicks (0 on alt products).
 * units: Summed units.
 * spend: Summed spend (0 on alt products).
 * sales: Summed sales.
 * ctr: clicks / impressions x100, 0.0 without impressions.
 * cpc: spend / clicks, 0.0 without clicks.
 * conversion: units / clicks x100, 0.0 without clicks.
 * acos: spend / sales x100, 0.0 without sales.
 * asp: sales / units, null without units.
 */
export type AmsViewAggregateCurrencyTotals = {
    currency: string;
    impressions?: number;
    clicks?: number;
    units?: number;
    spend?: string;
    sales?: string;
    ctr?: number;
    cpc?: number;
    conversion?: number;
    acos?: number;
    asp?: number | null;
};

/**
 * The undrilled detail tabs, by URL slug (AGG:111-118 ``LEVEL_SLUGS``, minus placements).
 *
 * The slugs are the frontend's tab ids, so a tab and its endpoint read the
 * same (AGG:108-110).
 */
export type AmsViewAggregateLevel = 'ad-products' | 'targets' | 'search-terms' | 'negatives' | 'alt-products';

/**
 * The undrilled detail tabs, by URL slug (AGG:111-118 ``LEVEL_SLUGS``, minus placements).
 *
 * The slugs are the frontend's tab ids, so a tab and its endpoint read the
 * same (AGG:108-110).
 */
export const AmsViewAggregateLevel = {
    AD_PRODUCTS: 'ad-products',
    TARGETS: 'targets',
    SEARCH_TERMS: 'search-terms',
    NEGATIVES: 'negatives',
    ALT_PRODUCTS: 'alt-products'
} as const;

/**
 * One undrilled tab: the full row set, never paged (SCH:606-629).
 *
 * The page sorts, filters and paginates client-side over the complete set,
 * so its totals track in-table filtering (convention exception X3).
 *
 * Attributes:
 * items: The rows, at most 20,000.
 * total_count: ``len(items)``.
 * truncated: The 20,000-row cap clipped the result; the page discloses it.
 * row_cap: 20000 when truncated, else null.
 * totals_by_currency: One bucket per currency, biggest spend first;
 * empty on negatives.
 * currency_code: The single currency, when exactly one.
 * mixed_currency: More than one currency in the result.
 * skipped_store_ids: Requested stores the caller can see that are not
 * Amazon stores, in request order.
 */
export type AmsViewAggregateResponse = {
    items?: Array<AmsViewAggregateRow>;
    total_count?: number;
    truncated?: boolean;
    row_cap?: number | null;
    totals_by_currency?: Array<AmsViewAggregateCurrencyTotals>;
    currency_code?: string | null;
    mixed_currency?: boolean;
    skipped_store_ids?: Array<number>;
};

/**
 * One ``(store, entity)`` row across every ad group in scope (SCH:507-580).
 *
 * A superset model, as VentaHub's: the frontend renders every level through
 * one table, and a field that does not apply to the row's level is null.
 *
 * Attributes:
 * store_id: The store the row's numbers belong to; its money is in
 * ``currency_code`` without conversion (SCH:517-520).
 * currency_code: VentaHub's label, ``marketing_ad_group_master.currency_code``
 * (AGG:188-199; BUILD-PLAN Q-U1).
 * ad_group_count: Ad groups contributing to the row, within the store.
 * enabled_ad_group_count: Of those, the ENABLED ones (targets and search
 * terms only; SCH:524-527).
 * ad_group_id: Meaningful only when ``ad_group_count == 1``; otherwise an
 * arbitrary pick (SCH:528-530).
 * asin: Ad products: the ASIN.
 * sku_codes: Ad products: the distinct SKUs, ``", "``-joined.
 * target: Targets: the target text.
 * target_type: Targets: the match / expression type.
 * target_id: Targets: as ``ad_group_id``.
 * bid_min: Targets: lowest per-ad-group bid, live master first, then the
 * highest in-window daily bid (SCH:540-544).
 * bid_max: Targets: highest, likewise.
 * search_term: Search terms: the term.
 * match_types: Search terms: the distinct match types.
 * keywords: Search terms: the keywords that surfaced the term, ordered.
 * purchased_asin: Alt products: the ASIN bought.
 * advertised_asins: Alt products: the ads that drove it (null for SB/SD).
 * keyword_text: Negatives: the negative's text.
 * match_type: Negatives: the normalised match type.
 * negative_type: Negatives: the negative's type.
 * status: Negatives: ENABLED if any instance is, else PAUSED, else ARCHIVED.
 * latest_date: Negatives: the newest snapshot date.
 * impressions: Summed impressions (absent on negatives and alt products).
 * clicks: Summed clicks.
 * ctr: clicks / impressions x100.
 * cpc: spend / clicks.
 * units: Summed units (absent on negatives).
 * conversion: units / clicks x100.
 * sales: Summed sales.
 * asp: sales / units; null when no unit sold (SCH:576-578).
 * spend: Summed spend.
 * acos: spend / sales x100.
 */
export type AmsViewAggregateRow = {
    store_id: number;
    currency_code?: string | null;
    ad_group_count?: number;
    enabled_ad_group_count?: number | null;
    ad_group_id?: string | null;
    asin?: string | null;
    sku_codes?: string | null;
    target?: string | null;
    target_type?: string | null;
    target_id?: string | null;
    bid_min?: string | null;
    bid_max?: string | null;
    search_term?: string | null;
    match_types?: string | null;
    keywords?: string | null;
    purchased_asin?: string | null;
    advertised_asins?: string | null;
    keyword_text?: string | null;
    match_type?: string | null;
    negative_type?: string | null;
    status?: string | null;
    latest_date?: string | null;
    impressions?: number | null;
    clicks?: number | null;
    ctr?: number | null;
    cpc?: number | null;
    units?: number | null;
    conversion?: number | null;
    sales?: string | null;
    asp?: number | null;
    spend?: string | null;
    acos?: number | null;
};

/**
 * The undrilled tabs whose rows have a time series (AGG:1106-1110).
 *
 * ``negatives`` is configuration with no metrics, and ``alt-products`` rows are
 * ASINs the ad group did not advertise, so neither has a series (AGG:1106-1109);
 * ``placements`` is hidden with the hourly stream.
 */
export type AmsViewAggregateTrendLevel = 'ad-products' | 'targets' | 'search-terms';

/**
 * The undrilled tabs whose rows have a time series (AGG:1106-1110).
 *
 * ``negatives`` is configuration with no metrics, and ``alt-products`` rows are
 * ASINs the ad group did not advertise, so neither has a series (AGG:1106-1109);
 * ``placements`` is hidden with the hourly stream.
 */
export const AmsViewAggregateTrendLevel = {
    AD_PRODUCTS: 'ad-products',
    TARGETS: 'targets',
    SEARCH_TERMS: 'search-terms'
} as const;

/**
 * The drilled Alt Products tab (SCH:381-398).
 *
 * The purchased-product report has no impressions, clicks or spend, so the
 * envelope carries units, sales and ASP only.
 *
 * Attributes:
 * items: The window's (target, advertised, purchased) groups, by sales
 * descending then purchased ASIN.
 * total_units: Sum of the rows' units.
 * total_sales: Sum of the rows' sales, as a decimal string.
 * avg_asp: ``total_sales / total_units`` when any unit sold, else null —
 * summed then divided, never an average of the row ASPs.
 * currency_code: The last row's label; the store's marketplace currency when
 * there are no rows (S:5287-5290, S:5354-5355).
 */
export type AmsViewAltProductListResponse = {
    items?: Array<AmsViewAltProductRow>;
    total_units?: number;
    total_sales?: string;
    avg_asp?: number | null;
    currency_code?: string | null;
};

/**
 * Other-ASIN purchases attributed to the drilled ad group (SCH:354-378).
 *
 * Attributes:
 * ad_group_id: Amazon's ad group id.
 * target: The targeting text; null when the report row carried none.
 * advertised_asin: The ASIN that was advertised; null on SB / SD rows.
 * purchased_asin: The ASIN that was bought instead.
 * units: Units.
 * sales: Sales, as a decimal string (VentaHub sent a float; same value).
 * asp: Measured sales / units as a float; null when no unit sold. Not the ad
 * group's configured ``asp``.
 * currency_code: The ad group's currency label.
 */
export type AmsViewAltProductRow = {
    ad_group_id: string;
    target?: string | null;
    advertised_asin?: string | null;
    purchased_asin?: string | null;
    units?: number;
    sales?: string;
    asp?: number | null;
    currency_code?: string | null;
};

/**
 * Whether VentaWiz refuses changes to Amazon Ads (VentaHub ``AmazonWriteLockResponse``).
 *
 * VentaHub kept one lock per store (``com_store.marketplace_meta``) and listed
 * them for the banner (``src/modules/stores/routes.py:122-147``). VentaWiz has
 * one global switch, so this is a single object rather than a per-store list.
 *
 * Attributes:
 * locked: True while every write endpoint answers 423.
 * reason: Why, while locked; null otherwise.
 */
export type AmsViewAmazonWriteLock = {
    locked: boolean;
    reason?: string | null;
};

/**
 * A brand the caller may filter by.
 *
 * Attributes:
 * pk_brand_id: ``com_brand.pk_brand_id``; what the page sends back as
 * ``brand_ids``.
 * brand_name: ``com_brand.brand_name``.
 */
export type AmsViewBrand = {
    pk_brand_id: number;
    brand_name: string;
};

/**
 * Brand options for both pages' Brands filter (replaces VentaHub's ``user.brands``).
 *
 * Attributes:
 * items: Live brands the caller is granted, ordered by name: those the
 * tenant's catalogue uses (every one for a tenant-wide caller, the
 * pinned ones for a brand-pinned caller), and the brand of any ad group
 * the caller can read.
 * unbranded_visible: Whether the caller may pick "Unbranded" (sentinel
 * ``0``): VentaHub's ``ams_view:view:internal`` (R:103-105), here
 * ``kit.ams.is_internal``.
 */
export type AmsViewBrands = {
    items?: Array<AmsViewBrand>;
    unbranded_visible?: boolean;
};

/**
 * Detail-tab rows that carry a trend series (S:4067 ``_ENTITY_TREND_LEVELS``).
 *
 * VentaHub's ``placement`` level read the Marketing Stream's hourly table, which
 * VentaWiz does not have (decision 2), so it is not offered.
 */
export type AmsViewEntityLevel = 'asin' | 'target' | 'search_term';

/**
 * Detail-tab rows that carry a trend series (S:4067 ``_ENTITY_TREND_LEVELS``).
 *
 * VentaHub's ``placement`` level read the Marketing Stream's hourly table, which
 * VentaWiz does not have (decision 2), so it is not offered.
 */
export const AmsViewEntityLevel = {
    ASIN: 'asin',
    TARGET: 'target',
    SEARCH_TERM: 'search_term'
} as const;

/**
 * The drilled Negative Keywords tab (SCH:350-351): a configuration snapshot.
 *
 * Attributes:
 * items: The latest snapshot of each negative, ``ENABLED`` then ``enabled``
 * first, then by text.
 */
export type AmsViewNegativeListResponse = {
    items?: Array<AmsViewNegativeRow>;
};

/**
 * One negative of the drilled ad group, its latest snapshot (SCH:340-347).
 *
 * Attributes:
 * ad_group_id: Amazon's ad group id.
 * keyword_text: The negated keyword, or a product expression.
 * match_type: VentaHub's raw match type (``negativeExact``,
 * ``NEGATIVE_EXACT`` …), restored from the VentaWiz copy by the shared
 * shape adapter. Null when neither column holds one (a product negative);
 * VentaHub's non-Optional field answered a 500 there (fix BF-03).
 * status: The snapshot's status, as stored (case kept).
 * negative_type: ``keyword`` for keyword negatives, else the stored type.
 * target_or_keyword_id: Amazon's id, the handle the pause / enable action
 * sends.
 * currency_code: The ad group's currency label.
 */
export type AmsViewNegativeRow = {
    ad_group_id: string;
    keyword_text: string;
    match_type?: string | null;
    status: string;
    negative_type: string;
    target_or_keyword_id: string;
    currency_code?: string | null;
};

/**
 * One bucket of the page-level trend, per currency for money metrics.
 *
 * VentaHub converted spend and sales into one ``target_currency`` (S:4697-4782)
 * and its response model then dropped ``currency`` (R:850). VentaWiz has no
 * exchange rates (decision 2), so money is never summed across currencies: one
 * point per (period, marketplace currency).
 *
 * Attributes:
 * period: As ``AmsTrendPoint.period``.
 * value: Decimal string. For ``spend`` / ``sales`` the sum over the stores
 * whose marketplace currency is ``currency``; for counts the sum over
 * every store.
 * currency: Upper-cased ISO code for ``spend`` / ``sales``; ``""`` for
 * impressions, clicks and units (VentaHub's ``result_currency``,
 * S:4778-4782).
 */
export type AmsViewOverallTrendPoint = {
    period: string;
    value: string;
    currency: string;
};

/**
 * The drilled Search Terms tab (SCH:248-258).
 *
 * Attributes:
 * items: The window's terms, ENABLED / PAUSED / ARCHIVED first, then by term
 * and keyword.
 */
export type AmsViewSearchTermListResponse = {
    total_impressions?: number;
    total_clicks?: number;
    total_spend?: string;
    total_units?: number;
    total_sales?: string;
    avg_ctr?: number;
    avg_cpc?: number;
    avg_conversion?: number;
    avg_acos?: number;
    items?: Array<AmsViewSearchTermRow>;
};

/**
 * One (keyword, search term, status) row of the drilled ad group (SCH:232-245).
 *
 * Attributes:
 * ad_group_id: Amazon's ad group id.
 * keyword: The keyword or target the shopper's term matched through.
 * search_term: What the shopper typed.
 * status: Additive (convention exception X18). VentaHub grouped by the report
 * status without returning it, so a term reported under two statuses
 * showed as two identical-looking rows; the grain is unchanged and the
 * field lets the client key the rows.
 * currency_code: The ad group's currency label.
 * impressions: Impressions.
 * clicks: Clicks.
 * spend: Spend, as a decimal string.
 * units: Ordered units.
 * sales: Attributed sales, as a decimal string.
 * ctr: Clicks / impressions x100.
 * cpc: Spend / clicks.
 * conversion: Units / clicks x100.
 * acos: Spend / sales x100.
 */
export type AmsViewSearchTermRow = {
    ad_group_id: string;
    keyword: string;
    search_term: string;
    status: string;
    currency_code?: string | null;
    impressions: number;
    clicks: number;
    spend: string;
    units: number;
    sales: string;
    ctr: number;
    cpc: number;
    conversion: number;
    acos: number;
};

/**
 * An Amazon Ads store the caller can see (VentaHub ``SellerResponse``, SCH:401-407).
 *
 * The last five attributes are additive (BUILD-PLAN X18): VentaHub's page read
 * them from ``user.stores`` (``AmsViewPage.tsx:274-298, 522-551``), and VentaWiz
 * has no client-side store record, so this list is both pages' store-option source.
 *
 * Attributes:
 * store_id: ``com_store.pk_store_id``.
 * seller_id: ``com_store.store_key``.
 * seller_name: ``com_store.store_name``.
 * mp_name: ``com_marketplace.name``.
 * mp_id: ``com_marketplace.pk_mp_id``.
 * country_code: ``com_marketplace.country_code``.
 * short_code: ``com_marketplace.short_code`` (always ``AZ*`` here).
 * currency_code: The store's marketplace currency (VentaHub's per-store
 * ``store_currency``, S:4712-4735), not an ad-group label.
 * expected_margin_rate: ``com_store.expected_margin_rate``, a stored
 * fraction (the Analytics break-even line, the drilled margin rate).
 * profile_id: ``com_store.profile_id``, the Ads profile (option caption,
 * ``StoreDropdown.tsx:298-346``).
 * store_type: ``com_store.store_type`` (option caption and search).
 */
export type AmsViewSeller = {
    store_id: number;
    seller_id: string;
    seller_name: string;
    mp_name: string;
    mp_id?: number | null;
    country_code?: string | null;
    short_code: string;
    currency_code?: string | null;
    expected_margin_rate?: number | null;
    profile_id?: number | null;
    store_type?: string | null;
};

/**
 * The drilled Targets tab (SCH:219-229).
 *
 * Attributes:
 * items: Every target of the ad group, ENABLED / PAUSED / ARCHIVED first,
 * then by text.
 */
export type AmsViewTargetListResponse = {
    total_impressions?: number;
    total_clicks?: number;
    total_spend?: string;
    total_units?: number;
    total_sales?: string;
    avg_ctr?: number;
    avg_cpc?: number;
    avg_conversion?: number;
    avg_acos?: number;
    items?: Array<AmsViewTargetRow>;
};

/**
 * One target (``target_id``) of the drilled ad group (SCH:180-216).
 *
 * ``asp``, ``target_acos`` and ``target_conversion`` are the ad group's, carried
 * on every row so the bid column can show what an ASP-formula bid is built from.
 *
 * Attributes:
 * ad_group_id: Amazon's ad group id.
 * target_id: Amazon's keyword or target id, the row key. Always set: VentaHub's
 * hourly rows that lacked it are not ported.
 * target: Master ``target_text`` first, else the report text, else the id.
 * target_type: The report's match type first, else the master's family.
 * status: Master ``state`` first, else the report status.
 * bid: The live master bid, else the window's highest report bid, else the
 * sentinel ``-1`` (the page shows "N/A").
 * currency_code: The ad group's currency label.
 * asp: The ad group's configured ASP (a bid-formula input), not a measured one.
 * target_acos: The ad group's target ACOS, a stored fraction.
 * target_conversion: The ad group's target conversion, a stored fraction.
 * impressions: Impressions.
 * clicks: Clicks.
 * spend: Spend, as a decimal string.
 * units: Ordered units.
 * sales: Attributed sales, as a decimal string.
 * ctr: Clicks / impressions x100.
 * cpc: Spend / clicks.
 * conversion: Units / clicks x100.
 * acos: Spend / sales x100.
 * data_row_count: 0 means the target served nothing in the window.
 */
export type AmsViewTargetRow = {
    ad_group_id: string;
    target_id: string;
    target: string;
    target_type?: string | null;
    status: string;
    bid: string;
    currency_code?: string | null;
    asp?: string | null;
    target_acos?: number | null;
    target_conversion?: number | null;
    impressions: number;
    clicks: number;
    spend: string;
    units: number;
    sales: string;
    ctr: number;
    cpc: number;
    conversion: number;
    acos: number;
    data_row_count?: number;
};

/**
 * One ASIN's sales inside an ad group (``TopAsinItem``, SCH:99-103).
 *
 * Attributes:
 * asin: Nullable in VentaWiz's fact table (fix BF-03).
 * sales: Summed sales.
 * units: Summed units.
 * currency_code: VentaHub's ad-group label.
 */
export type AmsViewTopAsin = {
    asin?: string | null;
    sales: string;
    units: number;
    currency_code?: string | null;
};

/**
 * Top ASINs by sales, at most ``limit`` (``TopAsinResponse``, SCH:106-107).
 *
 * Attributes:
 * items: Highest sales first.
 */
export type AmsViewTopAsinResponse = {
    items?: Array<AmsViewTopAsin>;
};

/**
 * Enable or pause one ad (SCH:496-498).
 *
 * Attributes:
 * ad_id: Amazon ``adId`` as a numeric **string**. VentaHub typed it ``int``,
 * which a generated TypeScript client types ``number`` and which
 * corrupts any id above 2^53 in the browser (fix BF-23).
 * state: The state to set.
 */
export type AmsViewUpdateAdProductRequest = {
    /**
     * Amazon adId, digits only
     */
    ad_id: string;
    state: 'ENABLED' | 'PAUSED';
};

/**
 * Enable or pause an existing negative (SCH:484-487).
 *
 * Attributes:
 * id: The negative's Amazon id (``target_or_keyword_id``), sent explicitly
 * as the production modal does.
 * keyword: Ignored: the server reads the stored text (R:2153).
 * state: The state to set.
 */
export type AmsViewUpdateNegativeRequest = {
    id: string;
    keyword?: string | null;
    state: 'ENABLED' | 'PAUSED';
};

/**
 * What the caller may do with the analyst, for the UI to mirror.
 *
 * Hiding a control is UX, not authorization: every route checks again.
 *
 * Attributes:
 * can_use: ``ai_analyst:view`` (or ``edit``) is held.
 * can_edit_company_memory: ``ai_analyst:edit`` is held.
 * is_reviewer: The platform's own staff holding ``ai_analyst:admin``:
 * the Debug switch and the review console.
 */
export type AnalystAccessRead = {
    can_use: boolean;
    can_edit_company_memory: boolean;
    is_reviewer: boolean;
};

/**
 * Where a run is.
 */
export type AnalystRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/**
 * Where a run is.
 */
export const AnalystRunStatus = {
    QUEUED: 'queued',
    RUNNING: 'running',
    SUCCEEDED: 'succeeded',
    FAILED: 'failed',
    CANCELLED: 'cancelled'
} as const;

/**
 * Today's usage as shares (0-100) of the person's effective daily limits.
 */
export type AnalystUsageRead = {
    /**
     * The larger of runs_percent and spend_percent; 100 refuses the next question.
     */
    percent: number;
    runs_percent: number;
    spend_percent: number;
    runs_used: number;
    runs_limit: number;
    /**
     * The next midnight in the budget zone.
     */
    resets_at: string;
    /**
     * Short name of the budget zone, e.g. "IST".
     */
    reset_timezone: string;
    /**
     * About how many more questions fit today, from recent cost per question.
     */
    estimated_remaining_questions?: number | null;
};

/**
 * Which stores a rotated secret is written to.
 */
export type ApplyTo = 'this' | 'same_client';

/**
 * Which stores a rotated secret is written to.
 */
export const ApplyTo = {
    THIS: 'this',
    SAME_CLIENT: 'same_client'
} as const;

/**
 * Body of ``PUT /v1/purchase-orders/{id}/appointment``.
 *
 * Attributes:
 * appointment_date: The delivery appointment, or ``None`` to clear it.
 */
export type AppointmentWrite = {
    appointment_date: string | null;
};

/**
 * One scraper the agent should run now.
 *
 * Attributes:
 * scraper_key: Its stable name. Sent back on the run report.
 * kind: Ads or reviews.
 * marketplace: The value the scraper passes as ``marketplace=`` to its
 * queue, so it is handed only its own stores.
 * exe_name: The file to start, from the agent's folder.
 * query_endpoint: Where it reads its work, including the API prefix.
 * results_endpoint: Where it posts what it found.
 */
export type AssignmentRead = {
    scraper_key: ScraperKey;
    kind: ScraperKind;
    marketplace: ScraperMarketplace;
    exe_name: string;
    query_endpoint: string;
    results_endpoint: string;
};

/**
 * Context attached to a question.
 *
 * Attributes:
 * sku_codes: SKU or ASIN codes pasted or uploaded; the analyst filters to them.
 * page_path: "Ask AI about this page": the page the person came from.
 * page_title: Its title.
 * page_search: The page's search parameters (its filters, sort, period),
 * so the analyst answers about exactly what is on screen.
 * page_view: "Include what I'm looking at": a plain-text summary of the
 * page's main table or figures as rendered (headers and the first
 * rows), so "why is this row red?" needs no re-deriving. Data, never
 * instructions, and capped at 4 KB.
 */
export type Attachments = {
    sku_codes?: Array<string>;
    page_path?: string | null;
    page_title?: string | null;
    page_search?: {
        [key: string]: unknown;
    } | null;
    page_view?: string | null;
};

/**
 * Every auth surface a connector exists for: ``ingest_surface_map.auth_surface``.
 */
export type AuthSurface = 'sp_api' | 'ads' | 'walmart_marketplace' | 'bol_retailer' | 'otto_seller' | 'trendyol_seller' | 'noon_partner' | 'flipkart_seller' | 'shopify_admin' | 'tiktok_shop' | 'dhl';

/**
 * Every auth surface a connector exists for: ``ingest_surface_map.auth_surface``.
 */
export const AuthSurface = {
    SP_API: 'sp_api',
    ADS: 'ads',
    WALMART_MARKETPLACE: 'walmart_marketplace',
    BOL_RETAILER: 'bol_retailer',
    OTTO_SELLER: 'otto_seller',
    TRENDYOL_SELLER: 'trendyol_seller',
    NOON_PARTNER: 'noon_partner',
    FLIPKART_SELLER: 'flipkart_seller',
    SHOPIFY_ADMIN: 'shopify_admin',
    TIKTOK_SHOP: 'tiktok_shop',
    DHL: 'dhl'
} as const;

/**
 * What to check.
 *
 * Attributes:
 * warehouse_id: Where to look.
 * lines: The requirements, served in this order.
 */
export type AvailabilityCheck = {
    warehouse_id: number;
    lines: Array<AvailabilityLine>;
};

/**
 * One requirement to check stock for, naming the product one of two ways.
 *
 * Attributes:
 * po_code: The order it belongs to, carried through to the answer.
 * sku_code: Our SKU code.
 * retailer_product_code: The retailer's code for the product (an ASIN,
 * an article number), as VentaHub's check took it. Resolved at the
 * warehouse by :mod:`ventawiz.kit.retailer_codes`; a code no
 * retailer maps there is tried as our SKU code, as VentaHub did.
 * quantity: Units needed. Positive.
 *
 * Exactly one of ``sku_code`` and ``retailer_product_code`` is given.
 */
export type AvailabilityLine = {
    po_code: string;
    sku_code?: string | null;
    retailer_product_code?: string | null;
    quantity: number;
};

/**
 * Stock available for one PO line.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * retailer_product_code: The retailer's code.
 * quantity_ordered: Units ordered.
 * quantity_available: Free stock in the warehouse, capped at the order.
 * short: Whether the warehouse cannot cover the line.
 */
export type AvailabilityRead = {
    sku_code: string | null;
    retailer_product_code: string | null;
    quantity_ordered: number;
    quantity_available: number;
    short: boolean;
};

/**
 * Units a requirement could take from one batch of one box.
 *
 * Attributes:
 * po_code: The requirement's order.
 * sku_code: Our SKU code: the one given, or the one the retailer code
 * resolved to.
 * retailer_product_code: The retailer code the line gave, echoed;
 * ``None`` for a line that gave our SKU code.
 * sku_name: Its name.
 * box_code: The box to pick from.
 * expiry_date: The batch.
 * quantity: Units.
 */
export type AvailabilityRow = {
    po_code: string;
    sku_code: string;
    sku_name: string | null;
    box_code: string;
    expiry_date: string | null;
    quantity: number;
    retailer_product_code?: string | null;
};

/**
 * Body of ``POST /v1/stores/{id}/health/backfill``.
 *
 * Attributes:
 * entity: An entity the store is pulled for.
 * from_: The first day, sent as ``from``.
 * to: The last day, inclusive. Ingest checks the range against the
 * entity's backfill limit and today.
 */
export type BackfillRequest = {
    entity: string;
    from: string;
    to: string;
};

/**
 * How change-bid derives the new bid (RSC:257 ``BidMode``).
 */
export type BidMode = 'cpc' | 'asp' | 'manual';

/**
 * How change-bid derives the new bid (RSC:257 ``BidMode``).
 */
export const BidMode = {
    CPC: 'cpc',
    ASP: 'asp',
    MANUAL: 'manual'
} as const;

/**
 * The latest measured accuracy of one store; empty until a backtest has run.
 */
export type BidSimulatorAccuracy = {
    items?: Array<BidSimulatorAccuracyRow>;
};

/**
 * One scored cell of the latest backtest.
 */
export type BidSimulatorAccuracyRow = {
    metric: string;
    size_class: string;
    row_set: string;
    n: number;
    accuracy?: number | null;
    /**
     * The forecast this replaces
     */
    accuracy_naive?: number | null;
    /**
     * The best any model could do — counting noise alone
     */
    accuracy_ceiling?: number | null;
    bias?: number | null;
    param_level: string;
    measured_to: string;
    bid_changes_known?: boolean | null;
    /**
     * Share of the scored weeks the bid log could resolve
     */
    bid_changes_known_share?: number | null;
    /**
     * Which parts of the model this row measured
     */
    scored_components?: Array<string>;
};

/**
 * What the recommendation advises.
 */
export type BidSimulatorAction = 'change' | 'no_change';

/**
 * What the recommendation advises.
 */
export const BidSimulatorAction = {
    CHANGE: 'change',
    NO_CHANGE: 'no_change'
} as const;

/**
 * What capped the recommended bid.
 */
export type BidSimulatorClamp = 'step' | 'marketplace_min';

/**
 * What capped the recommended bid.
 */
export const BidSimulatorClamp = {
    STEP: 'step',
    MARKETPLACE_MIN: 'marketplace_min'
} as const;

/**
 * What a forecast could not measure, or why it should be read with care.
 */
export type BidSimulatorFlag = 'bid_unknown' | 'no_recent_clicks' | 'no_settled_orders' | 'no_recent_traffic' | 'history_short' | 'adgroup_dark_risk' | 'settings_unknown' | 'extrapolation' | 'orders_mostly_noise' | 'no_parameters';

/**
 * What a forecast could not measure, or why it should be read with care.
 */
export const BidSimulatorFlag = {
    BID_UNKNOWN: 'bid_unknown',
    NO_RECENT_CLICKS: 'no_recent_clicks',
    NO_SETTLED_ORDERS: 'no_settled_orders',
    NO_RECENT_TRAFFIC: 'no_recent_traffic',
    HISTORY_SHORT: 'history_short',
    ADGROUP_DARK_RISK: 'adgroup_dark_risk',
    SETTINGS_UNKNOWN: 'settings_unknown',
    EXTRAPOLATION: 'extrapolation',
    ORDERS_MOSTLY_NOISE: 'orders_mostly_noise',
    NO_PARAMETERS: 'no_parameters'
} as const;

/**
 * One metric at one bid: what to expect, and what it could be instead.
 */
export type BidSimulatorMetricForecast = {
    /**
     * Average across many keywords in this situation
     */
    expected: number;
    /**
     * What the middle keyword does — below expected wherever a few winners pull the average up
     */
    typical?: number | null;
    /**
     * None where no range has been measured for this metric
     */
    lo90?: number | null;
    hi90?: number | null;
};

/**
 * Why no change is advised; absent when the action is ``change``.
 */
export type BidSimulatorReason = 'target_not_enabled' | 'bid_unknown' | 'no_target_acos' | 'no_recent_traffic' | 'no_recent_clicks' | 'no_settled_orders' | 'history_short' | 'adgroup_dark_risk' | 'extrapolation' | 'thin_data' | 'inside_tolerance' | 'cool_down';

/**
 * Why no change is advised; absent when the action is ``change``.
 */
export const BidSimulatorReason = {
    TARGET_NOT_ENABLED: 'target_not_enabled',
    BID_UNKNOWN: 'bid_unknown',
    NO_TARGET_ACOS: 'no_target_acos',
    NO_RECENT_TRAFFIC: 'no_recent_traffic',
    NO_RECENT_CLICKS: 'no_recent_clicks',
    NO_SETTLED_ORDERS: 'no_settled_orders',
    HISTORY_SHORT: 'history_short',
    ADGROUP_DARK_RISK: 'adgroup_dark_risk',
    EXTRAPOLATION: 'extrapolation',
    THIN_DATA: 'thin_data',
    INSIDE_TOLERANCE: 'inside_tolerance',
    COOL_DOWN: 'cool_down'
} as const;

/**
 * What bid to set, or why no advice is being given.
 *
 * `action` is `no_change` far more often than `change`, and `reason` is the
 * point of the response when it is. Advice only: nothing is sent to Amazon.
 */
export type BidSimulatorRecommendation = {
    action: BidSimulatorAction;
    reason?: BidSimulatorReason | null;
    bid_now?: number | null;
    /**
     * The bid the ACOS goal implies, before any capping
     */
    bid_star?: number | null;
    /**
     * What to actually set — capped, floored and rounded
     */
    bid_rec?: number | null;
    clamped_by?: BidSimulatorClamp | null;
    /**
     * What a click is worth at the ACOS goal
     */
    cpc_star?: number | null;
    marketplace_min_bid?: number | null;
    /**
     * The forecast at `bid_rec`, priced before the advice is given
     */
    scenario?: BidSimulatorScenario | null;
    target?: BidSimulatorTargetSummary | null;
    steps?: Array<string>;
};

/**
 * What one bid is expected to do over the horizon.
 */
export type BidSimulatorScenario = {
    bid: number;
    delta_pct: number;
    cpc?: number | null;
    multiplier_expected: number;
    multiplier_typical?: number | null;
    clicks: BidSimulatorMetricForecast;
    impressions: BidSimulatorMetricForecast;
    spend?: BidSimulatorMetricForecast | null;
    units?: number | null;
    sales?: number | null;
    acos?: number | null;
    /**
     * Chance the period ends with no clicks at all
     */
    p_zero_clicks?: number | null;
    /**
     * Chance the period ends with no orders at all
     */
    p_no_order?: number | null;
    flags?: Array<BidSimulatorFlag>;
    /**
     * How the numbers were derived, in order
     */
    steps?: Array<string>;
};

/**
 * A what-if or a curve: the target, and one scenario per bid asked about.
 */
export type BidSimulatorSimulation = {
    target: BidSimulatorTargetSummary;
    scenarios: Array<BidSimulatorScenario>;
    forecast_available: boolean;
    flags?: Array<BidSimulatorFlag>;
    disclaimer?: string;
};

/**
 * The target and the measured inputs every answer about it rests on.
 */
export type BidSimulatorTargetSummary = {
    ad_group_id: string;
    target_id: string;
    target_text: string;
    /**
     * Last day of complete report data used
     */
    as_of: string;
    bid_now?: number | null;
    cpc_now?: number | null;
    clicks_per_day: number;
    impressions_per_day: number;
    /**
     * Clicks this target ACTUALLY took in the 7 days to as_of, so a 7-day forecast can be shown beside an actual measured over the same 7 days
     */
    clicks_last_7?: number;
    /**
     * Clicks actually taken in the whole rate window (rate_window_days)
     */
    clicks_window?: number;
    /**
     * Length in days of the window the rate was measured over
     */
    rate_window_days?: number;
    size_class: string;
    trend: BidSimulatorTrend;
    cvr?: number | null;
    asp?: number | null;
    acos_now?: number | null;
    /**
     * Share of days the whole ad group showed nothing, measured over the rate window. Context only: NOT applied as a discount, because those days are already inside clicks_per_day.
     */
    adgroup_dark_share: number;
    p_adgroup_dark_all_days: number;
    horizon: number;
    /**
     * Per piece: store (this account's own fit), seed (research), or none
     */
    parameter_levels: {
        [key: string]: string;
    };
    response_group: string;
};

/**
 * Whether the target is climbing, falling or flat over its rate window.
 */
export type BidSimulatorTrend = 'up' | 'down' | 'flat';

/**
 * Whether the target is climbing, falling or flat over its rate window.
 */
export const BidSimulatorTrend = {
    UP: 'up',
    DOWN: 'down',
    FLAT: 'flat'
} as const;

/**
 * Ask in whichever unit the decision is being made in.
 *
 * A CPC or an ACOS goal is converted to the bid that delivers it and then run
 * down exactly the same path, so the three modes cannot disagree.
 */
export type BidSimulatorWhatIfRequest = {
    ad_group_id: string;
    target_id: string;
    horizon?: 7 | 14;
    bids?: Array<number> | null;
    cpcs?: Array<number> | null;
    /**
     * As fractions: 0.30 is a 30 % ACOS goal
     */
    acos_targets?: Array<number> | null;
};

/**
 * A new block: stock reserved against a purchase order code.
 *
 * Attributes:
 * warehouse_id: Where the stock is.
 * po_code: The purchase order it is for. One live block per PO and warehouse.
 * lines: What to reserve.
 */
export type BlockCreate = {
    warehouse_id: number;
    po_code: string;
    lines: Array<BlockLineWrite>;
};

/**
 * A block and what it reserves.
 *
 * Attributes:
 * block: The header.
 * lines: Each reservation.
 */
export type BlockDetailRead = {
    block: BlockRead;
    lines: Array<BlockLineRead>;
};

/**
 * One reservation inside a block: a SKU in a box.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * sku_name: Its name.
 * box_code: The box it is reserved in.
 * quantity: Units reserved.
 * expiry_date: The batch's expiry, when it has one.
 */
export type BlockLineRead = {
    sku_code: string | null;
    sku_name: string | null;
    box_code: string | null;
    quantity: number;
    expiry_date: string | null;
};

/**
 * Units to reserve from one box.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * box_code: The box, in the block's warehouse. Upper-cased before lookup.
 * quantity: Units. Positive.
 * expiry_date: A specific batch. Leave out for "whichever batches the box
 * holds", drawn earliest expiry first.
 */
export type BlockLineWrite = {
    sku_code: string;
    box_code: string;
    quantity: number;
    expiry_date?: string | null;
};

/**
 * More units for a block that already exists.
 *
 * Attributes:
 * lines: The units to add, not the new total.
 */
export type BlockLinesAdd = {
    lines: Array<BlockLineWrite>;
};

/**
 * What the filters on this page can be chosen from.
 */
export type BlockLookupsRead = {
    warehouses: Array<NamedRead>;
};

/**
 * One block: stock reserved against a purchase order code.
 *
 * Attributes:
 * id: The block's key.
 * po_code: The purchase order it is reserved for.
 * warehouse_id: Where the stock is.
 * warehouse: Its name.
 * created_at: When it was made.
 * units: Units reserved across its lines.
 * sku_count: Distinct SKUs reserved.
 * valid: Whether the block is live (a voided block no longer reserves stock).
 */
export type BlockRead = {
    id: number;
    po_code: string | null;
    warehouse_id: number | null;
    warehouse: string | null;
    created_at: string;
    units: number;
    sku_count: number;
    valid: boolean;
};

export type BodyCheckUpload = {
    /**
     * The filled-in template or report
     */
    file: string;
    /**
     * The store the file is for
     */
    store_id: number;
    /**
     * The upload's key
     */
    upload: string;
    /**
     * The day the file covers, where it asks for one
     */
    report_date?: string | null;
};

export type BodyCommitCostUpload = {
    file: string;
    warehouse_id: number;
    party_id: number;
    currency: string;
};

export type BodyCommitFeeUpload = {
    file: string;
    store_id: number;
};

export type BodyCommitFocusedSkuUpload = {
    file: string;
    name: string;
    mode?: 'add' | 'replace';
    principal_id?: string | null;
};

export type BodyMarkSheetProblems = {
    /**
     * The sheet, .xlsx or .csv
     */
    file: string;
    /**
     * The check's problems, as a JSON list
     */
    problems: string;
};

export type BodyPreviewCostUpload = {
    file: string;
    warehouse_id?: number | null;
    party_id?: number | null;
    currency?: string | null;
};

export type BodyPreviewFeeUpload = {
    file: string;
    store_id: number;
};

export type BodyPreviewFocusedSkuUpload = {
    file: string;
    mode?: 'add' | 'replace';
    principal_id?: string | null;
};

export type BodyPreviewPerformanceMaster = {
    /**
     * The filled-in report
     */
    file: string;
    /**
     * The store the report is for
     */
    store_id: number;
};

export type BodyPreviewSheet = {
    /**
     * The sheet, .xlsx or .csv
     */
    file: string;
};

export type BodyPushPriceSheet = {
    /**
     * The template's columns; .xlsx or .csv
     */
    file: string;
    /**
     * Check and plan only. Upload once as a dry run to preview.
     */
    dry_run?: boolean;
};

export type BodyPushStockSheet = {
    /**
     * SKU Code and Quantity, .xlsx or .csv
     */
    file: string;
    /**
     * Plan only. Upload once as a dry run to preview.
     */
    dry_run?: boolean;
};

export type BodyRaiseUpload = {
    /**
     * The filled-in template or report
     */
    file: string;
    /**
     * The store the file is for
     */
    store_id: number;
    /**
     * The upload's key
     */
    upload: string;
    /**
     * The day the file covers, where it asks for one
     */
    report_date?: string | null;
};

export type BodyReadAutoOrderingPlanning = {
    /**
     * The filled-in template, .xlsx or .csv
     */
    file: string;
};

export type BodyReadTicketItemsSheet = {
    /**
     * The filled-in sheet, .xlsx or .csv
     */
    file: string;
};

export type BodyRunOptimizationUpload = {
    /**
     * The filled-in template
     */
    file: string;
};

export type BodyUploadBlockStockAvailability = {
    /**
     * Where to look
     */
    warehouse_id: number;
    /**
     * The template's columns: PO Code, Retailer SKU, Quantity
     */
    file: string;
};

/**
 * One box holding the SKU, and the receipt that dated it.
 *
 * Attributes:
 * received_date: ``None`` unless ``status`` is ``dated``; so are
 * ``age_days`` and ``vi_number``.
 * status: ``dated``; ``pre_migration`` (backfill only, age not known);
 * or ``no_receipt`` (no document: the one that can be fixed).
 */
export type BoxAgeingRead = {
    box_code: string;
    units: number;
    received_date: string | null;
    age_days: number | null;
    vi_number: string | null;
    status: 'dated' | 'pre_migration' | 'no_receipt';
};

/**
 * Whether one mapping can move, and what it would move.
 *
 * Attributes:
 * line: The 1-based line of the request.
 * from_box_code: The source box, normalised.
 * to_box_code: The destination code, normalised.
 * units: Units in the source box; 0 when it was not found.
 * sku_count: Distinct SKUs in it.
 * can_move: Whether this line passes every check.
 * problems: Why not, one sentence each; empty when it can.
 */
export type BoxCheck = {
    line: number;
    from_box_code: string;
    to_box_code: string;
    units: number;
    sku_count: number;
    can_move: boolean;
    problems: Array<string>;
};

/**
 * One SKU in one box.
 *
 * Attributes:
 * box_id: ``pk_box_id``.
 * box_code: The box's code.
 * sku_code: Our SKU code.
 * sku_name: Its name.
 * quantity: Units of it in the box.
 * blocked: Units of it blocked.
 * expiry_date: Its expiry, when it has one.
 */
export type BoxContentRead = {
    box_id: number;
    box_code: string;
    sku_code: string | null;
    sku_name: string | null;
    quantity: number;
    blocked: number;
    expiry_date: string | null;
};

/**
 * What the box list can be narrowed by, for a warehouse and window.
 *
 * Vendors and retailers are every one in service; the documents and
 * currencies are those on the window's boxes, so no choice can only ever
 * return nothing.
 *
 * Attributes:
 * vendors: Vendors to choose from.
 * retailers: Retailers to choose from.
 * sales_invoices: Valid sales invoices billing a box; ``name`` is the number.
 * vendor_invoices: Valid vendor invoices that received a box.
 * purchase_orders: POs a box is allotted to or was received against.
 * notes: Valid credit and debit notes on a box.
 * currencies: ISO 4217 codes on those documents, sorted.
 */
export type BoxFiltersRead = {
    vendors: Array<NamedRead>;
    retailers: Array<NamedRead>;
    sales_invoices: Array<NamedRead>;
    vendor_invoices: Array<NamedRead>;
    purchase_orders: Array<NamedRead>;
    notes: Array<NamedRead>;
    currencies: Array<string>;
};

/**
 * The ledger for a window.
 *
 * Attributes:
 * inward: Vendor invoices and credit notes that put stock in.
 * outward: Sales invoices and debit notes that took it out.
 * shift: Moves between boxes.
 * truncated: A section hit the row cap, so the window should be narrowed.
 */
export type BoxHistoryRead = {
    inward: Array<BoxHistoryRow>;
    outward: Array<BoxHistoryRow>;
    shift: Array<BoxShiftRow>;
    truncated: boolean;
};

/**
 * One line of stock moving in or out of a box.
 *
 * Attributes:
 * date: The document's date.
 * party: The vendor or retailer on the document.
 * number: The document's number.
 * type: ``Vendor invoice``, ``Credit note``, ``Sales invoice`` or
 * ``Debit note``.
 * box_code: The box it moved through.
 * sku_code: Our SKU code.
 * sku_name: Its name.
 * quantity: Units, summed per document, box and SKU.
 */
export type BoxHistoryRow = {
    date: string | null;
    party: string | null;
    number: string;
    type: string;
    box_code: string;
    sku_code: string | null;
    sku_name: string | null;
    quantity: number;
};

/**
 * Move one box: its contents go into a new box in the destination.
 *
 * Attributes:
 * from_box_code: The box in the source warehouse. Trimmed and upper-cased
 * before lookup.
 * to_box_code: The code the new box takes in the destination. Trimmed
 * and upper-cased; four of 0-9 and A-Z, and not already in use there.
 */
export type BoxMapping = {
    from_box_code: string;
    to_box_code: string;
};

/**
 * A box, with the documents that touch it.
 *
 * Attributes:
 * id: ``pk_box_id``.
 * box_code: The code printed on the box.
 * status: Where it has got to.
 * quantity: Units in it, all SKUs; asked to exclude blocked, the units no
 * valid block holds, each SKU's floored at 0.
 * blocked: Units of it blocked for a purchase order.
 * sku_count: How many SKUs it holds.
 * value: Cost of what it holds, from the vendor invoice that received it.
 * vendor_name: Who it came from.
 * vendor_invoice_number: The invoice that received it.
 * po_code: The purchase order it is allotted to, or that its vendor order
 * was raised for.
 * po_link: Which of the two ``po_code`` is; ``None`` without a PO.
 * note_number: The lowest valid credit or debit note on it.
 * invoice_number: The sales invoice it is billed on.
 * retailer_name: Who it is for.
 * received_at: When the box record was created.
 */
export type BoxRead = {
    id: number;
    box_code: string;
    status: BoxStatus;
    quantity: number;
    blocked: number;
    sku_count: number;
    value: MoneyRead | null;
    vendor_name: string | null;
    vendor_invoice_number: string | null;
    po_code: string | null;
    po_link?: PoLink | null;
    note_number?: string | null;
    invoice_number: string | null;
    retailer_name: string | null;
    received_at: string;
};

/**
 * Stock moved from one box and SKU to another.
 *
 * Attributes:
 * date: When it was shifted.
 * from_box: The box it left.
 * from_sku: The SKU it was.
 * from_sku_name: That SKU's name.
 * to_box: The box it went to.
 * to_sku: The SKU it became.
 * to_sku_name: That SKU's name.
 * quantity: Units.
 */
export type BoxShiftRow = {
    date: string | null;
    from_box: string;
    from_sku: string | null;
    from_sku_name?: string | null;
    to_box: string;
    to_sku: string | null;
    to_sku_name?: string | null;
    quantity: number;
};

/**
 * Where a box has got to.
 *
 * ``dispatched`` and ``delivered`` are not stored on a box: they are read from
 * the invoice a billed box is on, so they cannot drift from it.
 */
export type BoxStatus = 'inward' | 'allotted' | 'billed' | 'dispatched' | 'delivered';

/**
 * Where a box has got to.
 *
 * ``dispatched`` and ``delivered`` are not stored on a box: they are read from
 * the invoice a billed box is on, so they cannot drift from it.
 */
export const BoxStatus = {
    INWARD: 'inward',
    ALLOTTED: 'allotted',
    BILLED: 'billed',
    DISPATCHED: 'dispatched',
    DELIVERED: 'delivered'
} as const;

/**
 * One box, by code.
 */
export type BoxWrite = {
    box_code: string;
};

/**
 * Body of ``POST /v1/brands``.
 *
 * Attributes:
 * managed: Whether this organization manages the brand on the seller's
 * behalf. Off unless sent.
 */
export type BrandCreate = {
    code: string;
    name: string;
    managed?: boolean;
};

/**
 * What ``from-sku-name`` did.
 *
 * Attributes:
 * brand: The brand created.
 * linked_sku_count: SKUs that now carry it.
 */
export type BrandCreatedFromSkuName = {
    brand: BrandRead;
    linked_sku_count: number;
};

/**
 * Body of ``POST /v1/brands/from-sku-name``.
 *
 * Attributes:
 * sku_name: The SKU brand name to create the brand from and link.
 * code: The new brand's code.
 * name: Its name; the trimmed ``sku_name`` when left out.
 * managed: Whether this organization manages it.
 */
export type BrandFromSkuName = {
    sku_name: string;
    code: string;
    name?: string | null;
    managed?: boolean;
};

/**
 * One row of the brand list.
 *
 * Attributes:
 * tags: This organization's tags on it.
 * store_count: This organization's live stores naming it, retired ones
 * included.
 * active_store_count: Of those, the active ones.
 * sku_count: Live SKUs carrying it.
 */
export type BrandOverview = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    managed: boolean;
    tags: Array<string>;
    store_count: number;
    active_store_count: number;
    sku_count: number;
};

/**
 * A brand (``com_brand``).
 *
 * Attributes:
 * managed: v1's flag for a brand the tenant manages on the seller's
 * behalf, as opposed to one it only sells.
 */
export type BrandRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    managed: boolean;
};

/**
 * A brand by id and name.
 */
export type BrandRef = {
    id: number;
    name: string;
};

/**
 * Body of ``PATCH /v1/brands/{id}``. ``name`` and ``managed``.
 *
 * Attributes:
 * managed: Whether this organization manages the brand; ``null`` is
 * refused.
 */
export type BrandUpdate = {
    name?: string | null;
    managed?: boolean | null;
};

/**
 * How far a PO got into ops fulfilment.
 *
 * VentaHub's spelling, kept so a PO reads the same on both systems while
 * they run side by side.
 */
export type BridgeStatus = 'PENDING' | 'SKIPPED_NO_RETAILER' | 'SKIPPED_AMBIGUOUS_RETAILER' | 'SKIPPED_NO_WAREHOUSE' | 'CREATED_PENDING_EMAIL' | 'PARTIAL' | 'BRIDGED_NO_EMAIL' | 'BRIDGED';

/**
 * How far a PO got into ops fulfilment.
 *
 * VentaHub's spelling, kept so a PO reads the same on both systems while
 * they run side by side.
 */
export const BridgeStatus = {
    PENDING: 'PENDING',
    SKIPPED_NO_RETAILER: 'SKIPPED_NO_RETAILER',
    SKIPPED_AMBIGUOUS_RETAILER: 'SKIPPED_AMBIGUOUS_RETAILER',
    SKIPPED_NO_WAREHOUSE: 'SKIPPED_NO_WAREHOUSE',
    CREATED_PENDING_EMAIL: 'CREATED_PENDING_EMAIL',
    PARTIAL: 'PARTIAL',
    BRIDGED_NO_EMAIL: 'BRIDGED_NO_EMAIL',
    BRIDGED: 'BRIDGED'
} as const;

/**
 * The reporter's browser, as the report modal read it.
 */
export type BrowserInfo = {
    user_agent?: string | null;
    platform?: string | null;
    language?: string | null;
    viewport?: string | null;
    screen_resolution?: string | null;
    os?: string | null;
    browser_name?: string | null;
    browser_version?: string | null;
};

/**
 * The implicit signals only the browser sees.
 */
export type BrowserSignal = 'dig_deeper' | 'copied' | 'shared';

/**
 * The implicit signals only the browser sees.
 */
export const BrowserSignal = {
    DIG_DEEPER: 'dig_deeper',
    COPIED: 'copied',
    SHARED: 'shared'
} as const;

/**
 * Body of ``PUT /budgets/{principal_id}``; both limits null restores the defaults.
 */
export type BudgetUpdate = {
    daily_usd_limit?: number | string | null;
    daily_run_limit?: number | null;
    note?: string | null;
};

/**
 * Body of ``POST /conversations/bulk-delete``.
 */
export type BulkDelete = {
    ids: Array<string>;
};

/**
 * How many conversations were removed.
 */
export type BulkDeleteRead = {
    deleted: number;
};

/**
 * What happened to one invoice in a bulk move.
 */
export type BulkMoveOutcome = {
    invoice_id: number;
    number: string | null;
    ok: boolean;
    error: string | null;
};

/**
 * Each invoice stands alone: one that cannot move is reported, the rest move.
 */
export type BulkMoveResult = {
    succeeded: number;
    failed: number;
    outcomes: Array<BulkMoveOutcome>;
};

/**
 * Invoices to dispatch, or deliver, on one date.
 *
 * Attributes:
 * invoice_ids: The invoices.
 * date: The date.
 * tracking_number: For delivery only.
 */
export type BulkMoveWrite = {
    invoice_ids: Array<number>;
    date: string;
    tracking_number?: string | null;
};

/**
 * What was read, and how fresh it is.
 *
 * Attributes:
 * built_at: When the marts were last built (D14).
 * warehouse_stock_as_of: When warehouse stock was measured.
 * fba_stock_as_of: When marketplace stock was last observed.
 * sections: The sections read; one the caller may not read is left
 * out and its figures are ``null``.
 * days: Days in the range, the denominator of ``drr``.
 * grain: ``sku`` or ``listing``.
 */
export type BusinessViewMeta = {
    built_at: string | null;
    warehouse_stock_as_of: string | null;
    fba_stock_as_of: string | null;
    sections: Array<BusinessViewSection>;
    days: number;
    grain: string;
};

/**
 * A page of Business View rows, with totals over every row.
 *
 * Attributes:
 * totals: Over every matching row, not the page.
 * meta: What was read.
 */
export type BusinessViewReport = {
    items: Array<BusinessViewRow>;
    pagination: Pagination;
    totals: BusinessViewTotals;
    meta: BusinessViewMeta;
};

/**
 * One SKU, or one listing of a SKU.
 *
 * Attributes:
 * sku_id: The SKU; ``null`` for figures recorded without one.
 * sku_code: Its code.
 * sku_name: Its name.
 * brand_id: Its brand, from the SKU master (D10).
 * brand_name: The brand's name; ``Unassigned`` for a SKU without one.
 * category: Its category.
 * subcategory: Its sub-category.
 * product_ref: The listing's ASIN (listing rows); the SKU's first ASIN
 * in the selected places (SKU rows).
 * seller_sku: The listing's seller SKU (listing rows only).
 * listing_count: How many ASINs the SKU is listed under (SKU rows only).
 * title: The listing's title as the marketplace lists it; on a SKU
 * row, one of its listings'; the SKU's name when it has none.
 * data_source: ``sales`` when the row sold in the range, ``stock``
 * when it is shown for its stock alone.
 */
export type BusinessViewRow = {
    total_units: number;
    drr?: string | null;
    stock_cover_days?: string | null;
    seller_units?: number | null;
    returned_units?: number | null;
    seller_amount?: Array<MoneyRead> | null;
    df_units?: number | null;
    df_amount?: Array<MoneyRead> | null;
    of_units?: number | null;
    of_amount?: Array<MoneyRead> | null;
    vo_units?: number | null;
    vo_amount?: Array<MoneyRead> | null;
    po_units?: number | null;
    po_amount?: Array<MoneyRead> | null;
    total_amount: Array<MoneyRead>;
    average_price: Array<MoneyRead>;
    payment_amount?: Array<MoneyRead> | null;
    warehouse_units?: number | null;
    age_0_30_units?: number | null;
    age_31_60_units?: number | null;
    age_61_90_units?: number | null;
    age_91_180_units?: number | null;
    age_181_270_units?: number | null;
    age_over_270_units?: number | null;
    age_pre_migration_units?: number | null;
    age_unknown_units?: number | null;
    fba_units?: number | null;
    fba_aged_91_180_units?: number | null;
    fba_aged_181_270_units?: number | null;
    impressions?: number | null;
    clicks?: number | null;
    ad_units?: number | null;
    ad_spend?: Array<MoneyRead> | null;
    ad_sales?: Array<MoneyRead> | null;
    ctr?: string | null;
    cvr?: string | null;
    clicks_per_order?: string | null;
    cpc?: Array<MoneyRead> | null;
    roas?: Array<CurrencyRatio> | null;
    acos?: Array<CurrencyRatio> | null;
    ad_average_price?: Array<MoneyRead> | null;
    sessions?: number | null;
    page_views?: number | null;
    glance_views?: number | null;
    traffic_units_ordered?: number | null;
    buy_box_page_views?: string | null;
    buy_box_share?: string | null;
    unit_session_pct?: string | null;
    vendor_ordered_units?: number | null;
    vendor_customer_returns?: number | null;
    vendor_ordered_revenue?: Array<MoneyRead> | null;
    vendor_sellable_units?: number | null;
    vendor_unsellable_units?: number | null;
    vendor_aged_90_plus_units?: number | null;
    vendor_open_po_units?: number | null;
    vendor_sellable_cost?: Array<MoneyRead> | null;
    vendor_sell_through?: string | null;
    buybox_checks?: number | null;
    buybox_wins?: number | null;
    buybox_win_rate?: string | null;
    sales_rank?: number | null;
    rating_count?: number | null;
    rating?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    category?: string | null;
    subcategory?: string | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    listing_count?: number | null;
    title?: string | null;
    data_source: string;
};

/**
 * The parts of a row, each read from one report and needing its permission.
 */
export type BusinessViewSection = 'sales' | 'channels' | 'purchase_orders' | 'payments' | 'warehouse_stock' | 'fba_stock' | 'ads' | 'traffic' | 'vendor_sales' | 'vendor_inventory' | 'buybox' | 'listing_quality';

/**
 * The parts of a row, each read from one report and needing its permission.
 */
export const BusinessViewSection = {
    SALES: 'sales',
    CHANNELS: 'channels',
    PURCHASE_ORDERS: 'purchase_orders',
    PAYMENTS: 'payments',
    WAREHOUSE_STOCK: 'warehouse_stock',
    FBA_STOCK: 'fba_stock',
    ADS: 'ads',
    TRAFFIC: 'traffic',
    VENDOR_SALES: 'vendor_sales',
    VENDOR_INVENTORY: 'vendor_inventory',
    BUYBOX: 'buybox',
    LISTING_QUALITY: 'listing_quality'
} as const;

/**
 * The figures over every row the filters match, each SKU counted once.
 *
 * Attributes:
 * product_count: How many SKU rows match.
 */
export type BusinessViewTotals = {
    total_units: number;
    drr?: string | null;
    stock_cover_days?: string | null;
    seller_units?: number | null;
    returned_units?: number | null;
    seller_amount?: Array<MoneyRead> | null;
    df_units?: number | null;
    df_amount?: Array<MoneyRead> | null;
    of_units?: number | null;
    of_amount?: Array<MoneyRead> | null;
    vo_units?: number | null;
    vo_amount?: Array<MoneyRead> | null;
    po_units?: number | null;
    po_amount?: Array<MoneyRead> | null;
    total_amount: Array<MoneyRead>;
    average_price: Array<MoneyRead>;
    payment_amount?: Array<MoneyRead> | null;
    warehouse_units?: number | null;
    age_0_30_units?: number | null;
    age_31_60_units?: number | null;
    age_61_90_units?: number | null;
    age_91_180_units?: number | null;
    age_181_270_units?: number | null;
    age_over_270_units?: number | null;
    age_pre_migration_units?: number | null;
    age_unknown_units?: number | null;
    fba_units?: number | null;
    fba_aged_91_180_units?: number | null;
    fba_aged_181_270_units?: number | null;
    impressions?: number | null;
    clicks?: number | null;
    ad_units?: number | null;
    ad_spend?: Array<MoneyRead> | null;
    ad_sales?: Array<MoneyRead> | null;
    ctr?: string | null;
    cvr?: string | null;
    clicks_per_order?: string | null;
    cpc?: Array<MoneyRead> | null;
    roas?: Array<CurrencyRatio> | null;
    acos?: Array<CurrencyRatio> | null;
    ad_average_price?: Array<MoneyRead> | null;
    sessions?: number | null;
    page_views?: number | null;
    glance_views?: number | null;
    traffic_units_ordered?: number | null;
    buy_box_page_views?: string | null;
    buy_box_share?: string | null;
    unit_session_pct?: string | null;
    vendor_ordered_units?: number | null;
    vendor_customer_returns?: number | null;
    vendor_ordered_revenue?: Array<MoneyRead> | null;
    vendor_sellable_units?: number | null;
    vendor_unsellable_units?: number | null;
    vendor_aged_90_plus_units?: number | null;
    vendor_open_po_units?: number | null;
    vendor_sellable_cost?: Array<MoneyRead> | null;
    vendor_sell_through?: string | null;
    buybox_checks?: number | null;
    buybox_wins?: number | null;
    buybox_win_rate?: string | null;
    sales_rank?: number | null;
    rating_count?: number | null;
    rating?: string | null;
    product_count: number;
};

/**
 * One distinct value of a text field, and how many rows hold it.
 *
 * Attributes:
 * value: The value; ``""`` for a missing one.
 * count: Rows holding it.
 */
export type BusinessViewValue = {
    value: string;
    count: number;
};

/**
 * One page of a text field's distinct values, for its filter list.
 *
 * Attributes:
 * values: In value order.
 * next_cursor: Pass as ``cursor`` for the next page; null on the last.
 * has_more: More values follow this page.
 * total: Distinct values matching the search, across every page.
 */
export type BusinessViewValues = {
    values: Array<BusinessViewValue>;
    next_cursor?: string | null;
    has_more: boolean;
    total: number;
};

/**
 * What ``POST /runs/{id}/cancel`` answers: the run will stop at its next step.
 */
export type CancelRead = {
    status?: 'cancelling';
};

/**
 * What a store's carrier settings can prefill from its first warehouse.
 *
 * Attributes:
 * warehouse_id: The warehouse the values came from.
 * dhl: DHL's sender fields.
 */
export type CarrierDefaults = {
    warehouse_id: number;
    dhl: DhlSender;
};

/**
 * Response of ``GET /v1/carrier-specs``: every carrier, each with role ``carrier``.
 */
export type CarrierSpecList = {
    items: Array<SurfaceSpecRead>;
};

/**
 * One unsettled document behind a row's total.
 *
 * Attributes:
 * kind: Sales invoice (money in), vendor or service-provider invoice (out).
 * direction: ``in`` or ``out``.
 * number: The invoice number.
 * counterparty: The retailer, vendor or service provider.
 * issued: The date its credit runs from: delivery for a delivered sales
 * invoice, otherwise the invoice date.
 * credit_days: Days of credit on the invoice; none when it has none.
 * due: ``issued`` plus ``credit_days``.
 * currency: ISO 4217 code.
 * amount: The invoice amount.
 * settled: What no longer has to move: received or paid so far, in the
 * invoice's currency, plus credit notes less debit notes.
 * unsettled: ``amount`` less ``settled``: what the row counts.
 */
export type CashflowDocumentRead = {
    kind: 'sales_invoice' | 'vendor_invoice' | 'sp_invoice';
    direction: 'in' | 'out';
    number: string;
    counterparty: string | null;
    issued: string;
    credit_days: number;
    due: string;
    currency: string;
    amount: string;
    settled: string;
    unsettled: string;
};

/**
 * What the filters on this page can be chosen from.
 */
export type CashflowLookupsRead = {
    warehouses: Array<NamedRead>;
    retailers: Array<NamedRead>;
    vendors: Array<NamedRead>;
    service_providers: Array<NamedRead>;
};

/**
 * The projection for a window.
 *
 * Attributes:
 * date_from: First day of the window.
 * date_to: Day after the window's last day.
 * opening_balance: Cash in hand at the start, which each row's balance
 * builds on; zero when none was given.
 * opening_currency: Its currency; ``None`` when none was given.
 * rows: One row per week and currency, overdue first.
 */
export type CashflowRead = {
    date_from: string;
    date_to: string;
    opening_balance: string;
    opening_currency: string | null;
    rows: Array<CashflowRowRead>;
};

/**
 * Money expected to move in one week, in one currency.
 *
 * Attributes:
 * week_start: The Monday of the week it falls due, or ``None`` for money
 * already past due at the start of the window.
 * currency: ISO 4217 code. Currencies are never added together.
 * inflow: Unreceived sales invoices falling due.
 * outflow: Unpaid vendor and service-provider invoices falling due.
 * net: ``inflow`` less ``outflow``.
 * balance: The projected cash position at the end of this row: the
 * opening balance (in its currency) plus every net so far in the
 * same currency, overdue row first. Below zero is a cash gap.
 */
export type CashflowRowRead = {
    week_start: string | null;
    currency: string;
    inflow: string;
    outflow: string;
    net: string;
    balance: string;
};

/**
 * A store that can hold listings.
 *
 * Deliberately a narrow projection of ``com_store``: that row also carries
 * the store's marketplace credentials (``encrypted_info``), and a response
 * model listing every column is one refactor away from returning them.
 *
 * Attributes:
 * id: ``com_store.pk_store_id``. The id every listing points at.
 * key: The marketplace's own account id, e.g. an Amazon seller id.
 * name: For people.
 * store_type: e.g. ``seller``, ``vendor``.
 * marketplace_code: e.g. ``AZUK``.
 * marketplace_name: e.g. ``Amazon UK``.
 * marketplace_country: ISO 3166-1 alpha-2 of the marketplace, e.g. ``GB``.
 * marketplace_url: The marketplace's storefront, e.g.
 * ``https://amazon.co.uk``, for linking a listing to its live page.
 */
export type CatalogueStoreRead = {
    id: number;
    key: string;
    name: string;
    store_type: string;
    marketplace_code: string | null;
    marketplace_name: string | null;
    marketplace_country: string | null;
    marketplace_url: string | null;
};

/**
 * Cancelled orders in one category.
 */
export type CategoryCount = {
    category: string;
    orders: number;
};

export type CellValue = string | number | boolean | null;

/**
 * One day of the activity chart.
 *
 * Attributes:
 * day: The UTC day.
 * total: Changes that day.
 * executed: Of those, taken by Amazon.
 * failed: Of those, rejected.
 * other: The rest — pending, skipped and the like.
 */
export type ChangeDay = {
    day: string;
    total: number;
    executed: number;
    failed: number;
    other: number;
};

/**
 * One change VentaHub applied to Amazon Ads.
 *
 * Attributes:
 * id: The log row's id; 0 for a dayparting event, which has none.
 * event_id: Unique across the feed: ``<ledger>:<id>``.
 * occurred_at: When it was applied.
 * store_id: Which store.
 * store_name: Its name.
 * entity_level: What kind of object changed — target, negative,
 * ad_group, ad_product or campaign — or null for an action this
 * service has not been taught.
 * origin_level: What data triggered it, when recorded.
 * event_type: bid_change, state_change, budget_change or added.
 * action_taken: The raw action.
 * action_status: The raw outcome.
 * entity_id: Amazon's id for the object.
 * entity_text: Keyword, target expression or ASIN.
 * match_type: For keywords.
 * ad_type: SP, SB or SD.
 * old_value: Before, as text — a bid or a state.
 * new_value: After.
 * ad_group_id: Amazon's ad group id.
 * ad_group_name: Its name.
 * source_ad_group_id: The ad group whose data triggered it, when that
 * differs from the one changed.
 * campaign_id: Amazon's campaign id.
 * campaign_name: Its name.
 * asin: For product changes.
 * sku: For product changes.
 * rule_code: The rule that suggested it, when recorded.
 * rule_name: That rule's name, from the catalogue.
 * source: Canonical source: rules_engine, manual, tickets,
 * system-auto-accepted, new_targeting_page, dayparting or unknown.
 * source_detail: Which page, when the source is manual.
 * actor_id: The user id that applied it, or null for the system.
 * reasoning: Why, as recorded.
 * failure_reason: Amazon's reason, for a failed or skipped change.
 * failure_message: Amazon's message.
 * ticket_id: The ticket it came from, when recorded.
 * details: The recorded payload, verbatim.
 * actor_name: The person's name, when ``actor_id`` resolves to one of
 * this organization's users.
 * execution_id: The rules-engine run that produced it, when recorded.
 * ticket: The ticket's status and people, for a caller who may read
 * tickets; null otherwise, or when the ticket is not in this system.
 * ledger: Where the row comes from: ``v1`` (manual edits, v1 tickets and
 * executor, New Targeting), ``v2`` (the v2 rules engine and its
 * tickets) or ``dayparting`` (the dayparting event log).
 * engine_execution_id: The VentaWiz rules-engine run that produced it.
 * ``execution_id`` is VentaHub's integer run id, of the row's ledger.
 * actor_principal_id: The VentaWiz principal who made it, for a change
 * made here rather than in VentaHub.
 */
export type ChangeEvent = {
    id: number;
    event_id: string;
    occurred_at: string;
    store_id: number;
    store_name: string;
    entity_level: string | null;
    origin_level: string | null;
    event_type: string | null;
    action_taken: string | null;
    action_status: string | null;
    entity_id: string | null;
    entity_text: string | null;
    match_type: string | null;
    ad_type: string | null;
    old_value: string | null;
    new_value: string | null;
    ad_group_id: string | null;
    ad_group_name: string | null;
    source_ad_group_id: string | null;
    campaign_id: string | null;
    campaign_name: string | null;
    asin: string | null;
    sku: string | null;
    rule_code: string | null;
    rule_name: string | null;
    source: string;
    source_detail: string | null;
    actor_id: number | null;
    reasoning: string | null;
    failure_reason: string | null;
    failure_message: string | null;
    ticket_id: string | null;
    details: {
        [key: string]: unknown;
    };
    actor_name?: string | null;
    execution_id?: number | null;
    ticket?: ChangeTicket | null;
    ledger?: string;
    engine_execution_id?: string | null;
    actor_principal_id?: string | null;
};

/**
 * Which kind of change a history tab shows.
 */
export type ChangeGrain = 'ad_groups' | 'asins' | 'targets' | 'search_terms' | 'negatives';

/**
 * Which kind of change a history tab shows.
 */
export const ChangeGrain = {
    AD_GROUPS: 'ad_groups',
    ASINS: 'asins',
    TARGETS: 'targets',
    SEARCH_TERMS: 'search_terms',
    NEGATIVES: 'negatives'
} as const;

/**
 * Changes per day over the window, quiet days as zeros.
 *
 * Attributes:
 * days: Every day of the window, oldest first.
 * total: Changes in the selection.
 * failed: Of those, rejected by Amazon.
 */
export type ChangePulse = {
    days: Array<ChangeDay>;
    total: number;
    failed: number;
};

/**
 * What the change feed can be ordered by.
 */
export type ChangeSort = 'timestamp' | 'status' | 'ad_group' | 'entity' | 'rule';

/**
 * What the change feed can be ordered by.
 */
export const ChangeSort = {
    TIMESTAMP: 'timestamp',
    STATUS: 'status',
    AD_GROUP: 'ad_group',
    ENTITY: 'entity',
    RULE: 'rule'
} as const;

/**
 * The selection at a glance, and what can be filtered on.
 *
 * Two scopes, as on VentaHub's ``/feed/filters``. The counts honour every
 * filter passed except the tab, so a badge never disagrees with the table
 * and a tab never zeroes its siblings. The options come from the window and
 * stores alone: narrowing them by the other filters would empty the list a
 * value was just picked from.
 *
 * Attributes:
 * total: Changes matching the filters, across every tab.
 * failed: Of those, rejected by Amazon.
 * grain_counts: Changes per tab, keyed by :class:`ChangeGrain`. The
 * tabs overlap — a search term promoted into a target counts under
 * both — so these do not sum to ``total``.
 * action_types: Distinct actions in the window.
 * statuses: Distinct outcomes.
 * sources: Distinct canonical sources.
 * rules: Rule codes recorded.
 * actors: Who made the window's changes, most first.
 */
export type ChangeSummary = {
    total: number;
    failed: number;
    grain_counts: {
        [key: string]: number;
    };
    action_types: Array<CountedOption>;
    statuses: Array<CountedOption>;
    sources: Array<CountedOption>;
    rules: Array<CountedOption>;
    actors?: Array<ActorOption>;
};

/**
 * The ticket a change came from, as the tickets pages know it.
 *
 * Attributes:
 * ticket_id: Its number, as the API and the URL carry it.
 * ticket_type: The type's code, e.g. ``T22``.
 * ticket_type_name: The type's name.
 * status: PENDING, INPROGRESS, RESOLVED or REVIEWED.
 * creator: Who raised it, by name; null when the system did.
 * reviewer: Who reviewed it, by name.
 * created_at: When it was raised.
 * resolved_at: When its owner marked it resolved.
 * completed_at: When it was finished — VentaHub's "resolved" time.
 */
export type ChangeTicket = {
    ticket_id: string;
    ticket_type: string;
    ticket_type_name: string;
    status: string;
    creator: string | null;
    reviewer: string | null;
    created_at: string;
    resolved_at: string | null;
    completed_at: string | null;
};

/**
 * Which marketplace's report shape a store uses.
 */
export type Channel = 'otto' | 'noon';

/**
 * Which marketplace's report shape a store uses.
 */
export const Channel = {
    OTTO: 'otto',
    NOON: 'noon'
} as const;

/**
 * Body of ``POST /chat/stream``.
 *
 * ``conversation_id`` omitted starts a new conversation. ``mode="snapshot"``
 * asks for a business snapshot; ``message`` then carries the person's framing
 * (it may be empty, and a default is used). ``depth="deep"`` answers on the
 * deep tier (snapshots always do). ``debug`` streams the trace and is ignored
 * unless the caller is a reviewer.
 *
 * Attributes:
 * replace_run_id: Retry or edit: replace this run, the conversation's last.
 * notify: Tell the person when the answer is ready (snapshots).
 */
export type ChatRequest = {
    conversation_id?: string | null;
    message?: string;
    mode?: RunMode;
    scope_pins?: ScopePins | null;
    depth?: Depth;
    debug?: boolean;
    replace_run_id?: string | null;
    notify?: boolean;
    attachments?: Attachments | null;
};

/**
 * Result of ``POST /v1/purchase-orders/check``.
 *
 * Attributes:
 * ok: True when the same payload would be accepted by create or edit.
 * problems: Every problem found, not just the first.
 * units_total: What the lines add up to.
 * amount_total: What the lines add up to in money, when every line has a
 * price.
 */
export type CheckResult = {
    ok: boolean;
    problems: Array<LineProblem>;
    units_total: number;
    amount_total: MoneyRead | null;
};

/**
 * An agent announcing itself at the start of a cycle.
 *
 * Attributes:
 * device_id: The agent's own id, generated once per install and kept on
 * the machine. One per agent exe, so a laptop running the Amazon and
 * the noon agent is two devices.
 * agent_kind: Which marketplace's agent this is.
 * store_ids: The stores chosen at the agent's first run. Not stored:
 * they travel with every check-in and every queue request.
 * email: Who set the machine up. For the fleet view only.
 * hostname: The machine's name. For the fleet view only.
 * os: The machine's OS. For the fleet view only.
 * agent_version: The agent build. For the fleet view only.
 */
export type CheckinCreate = {
    device_id: string;
    agent_kind: ScraperMarketplace;
    store_ids: Array<number>;
    email?: string | null;
    hostname?: string | null;
    os?: string | null;
    agent_version?: string | null;
};

/**
 * What an agent should do this cycle.
 *
 * Attributes:
 * device_id: As sent.
 * status: The device's status. Anything but ``active`` comes with no
 * assignments.
 * assignments: The scrapers to run, one after another, in this order.
 * Empty means nothing to do this cycle.
 * stores: The agent's stores with their marketplace, in store order —
 * whatever the status. An agent sets up a sign-in for each distinct
 * site rather than for one hard-coded country.
 * server_time: The server's clock, which decided the assignments.
 */
export type CheckinRead = {
    device_id: string;
    status: DeviceStatus;
    assignments: Array<AssignmentRead>;
    stores: Array<CheckinStore>;
    server_time: string;
};

/**
 * One of the agent's stores, and the site its scrapers open.
 *
 * Attributes:
 * store_id: ``com_store.pk_store_id``, as the agent sent it.
 * short_code: Its marketplace's ``com_marketplace.short_code`` — ``AZAE``,
 * ``AZIN``, ``FKIN``, ``NNAE``. The agent turns it into the site its
 * employee must be signed in to, the same way the scrapers do.
 */
export type CheckinStore = {
    store_id: number;
    short_code: string;
};

/**
 * Where on the store page an item is put right: a tab and a section of it.
 */
export type ChecklistFix = {
    tab: StorePageTab;
    section: string;
};

/**
 * One step of setting a store up.
 *
 * Attributes:
 * key: Stable: ``credentials``, ``streams``, ``ads_profile``,
 * ``warehouses``, ``targets``, ``owner``, ``alert_route``,
 * ``carrier``.
 * label: What it is, in words.
 * done: Whether it is.
 * detail: What is missing, when it is not.
 * fix: Where to fix it; ``null`` when it is not on the store page.
 */
export type ChecklistItem = {
    key: string;
    label: string;
    done: boolean;
    detail: string | null;
    fix: ChecklistFix | null;
};

/**
 * One connector's circuit for the store.
 */
export type CircuitRead = {
    connector: string;
    state: CircuitState;
    failures: number;
    last_error?: string | null;
    opened_at?: string | null;
    rate_limit?: RateLimitHeadroom | null;
    account_ref?: string | null;
    auth_failures?: number | null;
    transient_failures?: number | null;
    half_open_at?: string | null;
    updated_at?: string | null;
    rate_limits?: Array<RateLimitBucket>;
};

/**
 * Response of ``POST /v1/stores/{id}/health/circuits/{connector}/reset``.
 */
export type CircuitReset = {
    connector: string;
    account_ref?: string | null;
    state: CircuitState;
    previous_state?: CircuitState | null;
};

/**
 * Whether ingest calls an account: ``open`` means it has stopped.
 */
export type CircuitState = 'closed' | 'open' | 'half_open';

/**
 * Whether ingest calls an account: ``open`` means it has stopped.
 */
export const CircuitState = {
    CLOSED: 'closed',
    OPEN: 'open',
    HALF_OPEN: 'half_open'
} as const;

/**
 * Orders shipped to one city.
 */
export type CityCount = {
    country_code: string;
    city: string;
    orders: number;
};

/**
 * An alert route scoped to the source store. Its Slack webhook is never returned.
 */
export type CloneAlertRoute = {
    id: number;
    min_severity: Severity;
    channel: AlertChannel;
    emails: Array<string>;
    slack_configured: boolean;
};

/**
 * One feature switch of the source store.
 */
export type CloneFeature = {
    feature: StoreFeatureName;
    enabled: boolean;
};

/**
 * One feature switch to copy. ``label`` and ``changed_*`` are accepted and ignored.
 */
export type CloneFeatureWrite = {
    feature: StoreFeatureName;
    enabled: boolean;
    label?: string | null;
    changed_by?: string | null;
    changed_at?: string | null;
};

/**
 * A group the source store is in, by kind and name.
 */
export type CloneGroup = {
    kind: StoreGroupKind;
    name: string;
};

/**
 * A group to put every created store in, by kind and name.
 *
 * ``id`` is accepted and ignored, so the template's or the settings' group
 * can be sent back as it came.
 */
export type CloneGroupWrite = {
    kind: StoreGroupKind;
    name: string;
    id?: number | null;
};

/**
 * The store a template is taken from.
 */
export type CloneSource = {
    id: number;
    code: string;
    name: string;
    marketplace_id: number;
};

/**
 * A real-time stream of the source store, switched on or off.
 */
export type CloneStream = {
    key: string;
    enabled: boolean;
};

/**
 * Response of ``GET /v1/stores/{id}/clone-template``: a new store's prefill. No secret.
 */
export type CloneTemplate = {
    source: CloneSource;
    store_type: string;
    is_retail: boolean;
    brand_ids: Array<number>;
    warehouses: Array<CloneWarehouse>;
    targets: SettingsTargets;
    features: Array<CloneFeature>;
    tags: Array<string>;
    groups: Array<CloneGroup>;
    alert_routes: Array<CloneAlertRoute>;
    streams: Array<CloneStream>;
    credential_reuse: Array<CredentialReuse>;
};

/**
 * A warehouse of the source store; ``priority`` 1 is filled first.
 */
export type CloneWarehouse = {
    id: number;
    name: string;
    priority: number;
};

/**
 * One distinct value of a column, and how many rows under the filters hold it.
 *
 * Attributes:
 * value: The value as text; ``""`` for a missing or empty cell.
 * count: Rows holding it.
 */
export type ColumnValue = {
    value: string;
    count: number;
};

/**
 * One page of a column's distinct values, for its filter list.
 *
 * Attributes:
 * values: In value order.
 * next_cursor: Pass as ``cursor`` for the next page; null on the last.
 * has_more: More values follow this page.
 * total: Distinct values matching the search, across every page — what
 * "Select all matching" selects.
 */
export type ColumnValuesRead = {
    values: Array<ColumnValue>;
    next_cursor?: string | null;
    has_more: boolean;
    total: number;
};

/**
 * What a merge does where both brands already have the row.
 */
export type ConflictResolution = 'keep_target';

/**
 * What a merge does where both brands already have the row.
 */
export const ConflictResolution = {
    KEEP_TARGET: 'keep_target'
} as const;

/**
 * Everything connecting a store on one channel family takes.
 *
 * ``online`` is false, with no surfaces, for a family no connector exists
 * for: such a store can only be created offline.
 */
export type ConnectionSpec = {
    family: string;
    platform: string;
    online: boolean;
    account_ref: AccountRefRead;
    surfaces: Array<SurfaceSpecRead>;
};

/**
 * Every family with a connector.
 */
export type ConnectionSpecList = {
    items: Array<ConnectionSpec>;
};

/**
 * Body of ``PATCH /v1/stores/{id}/connections/{surface}``. Every field optional.
 *
 * Attributes:
 * enabled: Switch the API on or off.
 * expires_at: Correct the expiry; ``null`` for none.
 * expiry_source: ``manual`` or ``none``. Inferred from ``expires_at``
 * when omitted.
 */
export type ConnectionUpdate = {
    enabled?: boolean | null;
    expires_at?: string | null;
    expiry_source?: CorrectedExpirySource | null;
};

/**
 * A person responsible for an entity.
 */
export type ContactRead = {
    kind: StoreContactKind;
    principal_id: string | null;
    name: string | null;
    email: string | null;
    phone: string | null;
    role: string | null;
};

/**
 * A person responsible for an entity.
 *
 * An owner or account manager is a dv-auth principal (``principal_id``
 * required, ``name`` their display name); an external contact needs a name.
 */
export type ContactWrite = {
    kind: StoreContactKind;
    principal_id?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    role?: string | null;
};

/**
 * An entity's contacts, in saved order.
 */
export type ContactsRead = {
    contacts: Array<ContactRead>;
};

/**
 * Body of ``PUT /v1/<kind>/{id}/contacts``: the whole list.
 *
 * At most one owner and one account manager; any number of external
 * contacts.
 */
export type ContactsWrite = {
    contacts: Array<ContactWrite>;
};

/**
 * Body of ``POST /conversations``.
 */
export type ConversationCreate = {
    scope_pins?: ScopePins;
};

/**
 * A conversation with its messages, for hydration and resume.
 *
 * Attributes:
 * read_only: The shared view of someone else's conversation.
 * owner_principal_id: Whose it is, on the shared view.
 * owner_name: Their display name on the shared view, when this service
 * has been told one (``PrincipalNames``); null otherwise, so a client
 * falls back to a neutral label rather than showing an id.
 */
export type ConversationDetail = {
    conversation: ConversationRead;
    messages: Array<MessageRead>;
    active_run_id?: string | null;
    read_only?: boolean;
    owner_principal_id?: string | null;
    owner_name?: string | null;
};

/**
 * How a whole conversation is exported.
 */
export type ConversationFormat = 'md' | 'docx';

/**
 * How a whole conversation is exported.
 */
export const ConversationFormat = {
    MD: 'md',
    DOCX: 'docx'
} as const;

/**
 * A conversation in the history sidebar.
 *
 * Attributes:
 * active_run_id: A queued or running run to re-attach to.
 * match_snippet: With a search: the matching passage, matched words in
 * ``<<...>>``.
 */
export type ConversationRead = {
    id: string;
    title: string | null;
    pinned: boolean;
    scope_pins: ScopePins;
    updated_at: string;
    created_at: string;
    active_run_id?: string | null;
    shared?: boolean;
    tags?: Array<string>;
    match_snippet?: string | null;
};

/**
 * Body of ``PATCH /conversations/{id}``; an omitted field is unchanged.
 */
export type ConversationUpdate = {
    title?: string | null;
    pinned?: boolean | null;
    scope_pins?: ScopePins | null;
    /**
     * Read-only link for people with AI Analyst access in the same organization.
     */
    shared?: boolean | null;
    tags?: Array<string> | null;
};

/**
 * Which conversion rate the ASP formula multiplied by (RSC:349).
 */
export type ConversionSource = 'actual' | 'target';

/**
 * Which conversion rate the ASP formula multiplied by (RSC:349).
 */
export const ConversionSource = {
    ACTUAL: 'actual',
    TARGET: 'target'
} as const;

/**
 * What a correction to an expiry is: a typed date, or "it does not expire".
 */
export type CorrectedExpirySource = 'manual' | 'none';

/**
 * What a correction to an expiry is: a typed date, or "it does not expire".
 */
export const CorrectedExpirySource = {
    MANUAL: 'manual',
    NONE: 'none'
} as const;

/**
 * What an upload saved.
 */
export type CostCommitResult = {
    inserted: number;
    updated: number;
};

/**
 * A change to one cost row.
 */
export type CostEdit = {
    cost: number | string;
    vat_rate?: number | string | null;
    rrp?: number | string | null;
};

/**
 * One choice in a picker.
 */
export type CostOption = {
    id: number;
    name: string;
    country_code?: string | null;
};

/**
 * Everything the costs page's pickers offer.
 */
export type CostOptions = {
    warehouses: Array<CostOption>;
    vendors: Array<CostOption>;
    retailers: Array<CostOption>;
    brands: Array<CostOption>;
};

/**
 * What an upload would do, before anything is saved.
 */
export type CostPreview = {
    rows: Array<CostPreviewRow>;
    new: number;
    updated: number;
    errors: number;
    writable: boolean;
};

/**
 * One sheet row as it would be saved.
 */
export type CostPreviewRow = {
    /**
     * The row number in the sheet, header excluded
     */
    row: number;
    sku_code: string;
    cost: string | null;
    vat_rate: string | null;
    rrp: string | null;
    /**
     * ready: valid, but where it applies is not chosen yet
     */
    action: 'new' | 'update' | 'ready' | 'error';
    error?: string | null;
    /**
     * Set when saving the row also retires a live cost row that is not valid
     */
    note?: string | null;
};

/**
 * One SKU cost record.
 */
export type CostRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    id: number;
    sku_id: number;
    sku_code: string;
    sku_name: string;
    warehouse_id: number | null;
    warehouse_name: string | null;
    vendor_id: number | null;
    vendor_name: string | null;
    currency_code: string;
    purchase_cost: string;
    shipping_cost: string;
    additional_cost: string;
    vendor_vat_rate: string;
    vendor_rrp: string;
    status: string | null;
    valid_from: string;
    valid_to: string | null;
    is_valid: boolean | null;
    updated_at: string | null;
};

/**
 * Whether costs can be changed here, and why not when they cannot.
 */
export type CostStatus = {
    writable: boolean;
    reason: string | null;
};

/**
 * A filter value and how many changes carry it.
 *
 * Attributes:
 * value: The value.
 * count: Changes in the window with it.
 */
export type CountedOption = {
    value: string;
    count: number;
};

/**
 * One ship-to country's seller orders over the window.
 *
 * Attributes:
 * country_code: ISO 3166 alpha-2.
 * orders: Orders shipping there, in every currency.
 * units: Their units.
 * sales: Their sales in the response's currency; None when none were in
 * it (or, converted, in any currency that converts).
 */
export type CountrySales = {
    country_code: string;
    orders: number;
    units: number;
    sales: string | null;
};

/**
 * How many focused SKUs fall in each band of cover; each counts once.
 */
export type CoverBands = {
    d0_30: number;
    d31_60: number;
    d61_90: number;
};

/**
 * A focused SKU's cover.
 *
 * Attributes:
 * marketplace_stock: Sellable at the marketplace, for the store.
 * warehouse_stock: Available in the store's warehouses.
 * velocity: Units sold a day, over the last 30 days.
 * days_of_cover: ``(marketplace + warehouse) / velocity``.
 */
export type CoverSku = {
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    asin: string | null;
    image_url: string | null;
    brand_name: string | null;
    store_id: number;
    store_name: string | null;
    marketplace_code: string | null;
    country_code: string | null;
    marketplace_stock: number;
    warehouse_stock: number;
    velocity: number;
    days_of_cover: number;
};

/**
 * What a warehouse can supply for the PO and what that is worth.
 *
 * The last row, with no warehouse, is the uncovered remainder, so the rows
 * add up to what is still outstanding and the gap shows in money.
 */
export type CoverageRead = {
    warehouse: PoWarehouseRead | null;
    units: number;
    worth: MoneyRead | null;
};

/**
 * Which application one of the source store's APIs is on. Never a credential.
 *
 * Attributes:
 * surface: The API.
 * label: Its name.
 * client_id_hint: The last four characters of the client id, if known.
 * sibling_store_ids: Every live store on the same application, the
 * source included.
 */
export type CredentialReuse = {
    surface: AuthSurface;
    label: string;
    client_id_hint: string | null;
    sibling_store_ids: Array<number>;
};

/**
 * A new credit or debit note.
 *
 * Attributes:
 * warehouse_id: Its warehouse.
 * type: Credit or debit.
 * number: Our note number. One live note per number and warehouse.
 * date: The note's date.
 * their_number: The retailer's, vendor's or provider's own number for it.
 * currency: ISO 4217. Must match the linked document's.
 * comment: Free text.
 * stock_mode: ``S`` amounts only; ``N`` priced lines; ``Y`` lines that move
 * stock (a credit brings units in, a debit takes them out).
 * link: The document it adjusts. Required when it moves stock.
 * amount_wo_vat: Before VAT. ``S`` only; otherwise summed from the lines.
 * amount: Including VAT. ``S`` only; otherwise summed from the lines.
 * lines: ``N`` and ``Y`` only.
 */
export type CreditDebitNoteCreate = {
    warehouse_id: number;
    type: 'C' | 'D';
    number: string;
    date: string;
    their_number?: string | null;
    currency: string;
    comment?: string | null;
    stock_mode: 'S' | 'N' | 'Y';
    link?: NoteLink | null;
    amount_wo_vat?: number | string | null;
    amount?: number | string | null;
    lines?: Array<NoteLineWrite>;
};

/**
 * One line of a credit / debit note.
 */
export type CreditDebitNoteLineRead = {
    id: number;
    sku_code: string | null;
    sku_name: string | null;
    box_code: string | null;
    currency: string | null;
    quantity: number | null;
    amount: string | null;
    vat_rate: string | null;
};

/**
 * What the filters on this page can be chosen from.
 */
export type CreditDebitNoteLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * One row of the credit / debit notes list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type CreditDebitNoteRead = {
    id: number;
    date: string | null;
    number: string | null;
    warehouse_id: number | null;
    warehouse: string | null;
    party_id: number | null;
    party: string | null;
    party_kind: string | null;
    currency: string | null;
    type: string | null;
    amount_wo_vat: string | null;
    amount: string | null;
    their_number: string | null;
    stock_update_type: string | null;
    vi_id: number | null;
    invoice_id: number | null;
    linked_kind?: ('sales_invoice' | 'vendor_invoice' | 'sp_invoice') | null;
    linked_number?: string | null;
    comment: string | null;
    valid: boolean;
};

/**
 * Revenue in one currency: order totals, never added across currencies.
 */
export type CurrencyMoney = {
    currency_code: string;
    orders: number;
    units: number;
    revenue: string;
    average_order: string | null;
    average_unit: string | null;
};

/**
 * A ratio of two amounts in one currency, such as ROAS.
 *
 * Attributes:
 * value: The ratio, to 6 places, as a string.
 * currency: The currency both amounts are in.
 */
export type CurrencyRatio = {
    value: string;
    currency: string;
};

/**
 * What was ordered and invoiced in one currency.
 */
export type CurrencyValueRead = {
    currency: string | null;
    value_ordered: string;
    value_invoiced: string;
};

/**
 * Dagster's own view of the entity's asset across every store.
 */
export type DagsterStatus = 'on_time' | 'overdue' | 'unknown';

/**
 * Dagster's own view of the entity's asset across every store.
 */
export const DagsterStatus = {
    ON_TIME: 'on_time',
    OVERDUE: 'overdue',
    UNKNOWN: 'unknown'
} as const;

/**
 * A part of the sales and marketing panel the caller may see.
 */
export type DashboardSection = 'seller' | 'vendor' | 'ads';

/**
 * A part of the sales and marketing panel the caller may see.
 */
export const DashboardSection = {
    SELLER: 'seller',
    VENDOR: 'vendor',
    ADS: 'ads'
} as const;

/**
 * The newest data per store and kind, with the time it was read.
 */
export type DataFreshness = {
    as_of: string;
    stores: Array<VentawizDashboardSchemasStoreFreshness>;
};

/**
 * The last day each source holds data for, in the sheet's stores.
 *
 * A period later than one of these is not loaded yet for that source: its
 * figures read low because the data has not arrived, not because nothing
 * sold.
 *
 * Attributes:
 * orders: Marketplace orders.
 * invoices: Invoices from the warehouses linked to the sheet's stores.
 * ads: Ads.
 * traffic: Amazon's weekly traffic report.
 * vendor_sales: Amazon's weekly vendor sales report.
 */
export type DataThrough = {
    orders: string | null;
    invoices: string | null;
    ads: string | null;
    traffic: string | null;
    vendor_sales: string | null;
};

/**
 * Which of a payment's two days a report counts it on (D27).
 *
 * ``payment`` is the day the money was paid: what came in, the cash view.
 * ``order`` is the day of the order it pays for: what a month's sales
 * earned, the cohort view the P&L uses, which restates past months as
 * refunds post.
 */
export type DateBasis = 'payment' | 'order';

/**
 * Which of a payment's two days a report counts it on (D27).
 *
 * ``payment`` is the day the money was paid: what came in, the cash view.
 * ``order`` is the day of the order it pays for: what a month's sales
 * earned, the cohort view the P&L uses, which restates past months as
 * refunds post.
 */
export const DateBasis = {
    PAYMENT: 'payment',
    ORDER: 'order'
} as const;

/**
 * One day of the window, for the cards' small charts.
 *
 * A figure the caller may not read is None.
 */
export type DayFigures = {
    day: string;
    total_sales: string | null;
    total_returns: string | null;
    ams_sales: string | null;
    ams_spend: string | null;
};

/**
 * One day's orders and units in one status group.
 */
export type DayStatus = {
    day: string;
    status: 'pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other';
    orders: number;
    units: number;
};

/**
 * What happens to the entities while a window is active.
 *
 * ``INCREASE``/``DECREASE`` scale a number by ``percent`` and pair with a
 * ``BID`` or ``BUDGET`` lever. ``PAUSE``/``ENABLE`` set a state and pair with
 * ``STATE``, where ``percent`` means nothing and is stored as 0.
 */
export type DaypartingAction = 'INCREASE' | 'DECREASE' | 'PAUSE' | 'ENABLE';

/**
 * What happens to the entities while a window is active.
 *
 * ``INCREASE``/``DECREASE`` scale a number by ``percent`` and pair with a
 * ``BID`` or ``BUDGET`` lever. ``PAUSE``/``ENABLE`` set a state and pair with
 * ``STATE``, where ``percent`` means nothing and is stored as 0.
 */
export const DaypartingAction = {
    INCREASE: 'INCREASE',
    DECREASE: 'DECREASE',
    PAUSE: 'PAUSE',
    ENABLE: 'ENABLE'
} as const;

/**
 * Sponsored Products, Brands or Display.
 */
export type DaypartingAdType = 'SP' | 'SB' | 'SD';

/**
 * Sponsored Products, Brands or Display.
 */
export const DaypartingAdType = {
    SP: 'SP',
    SB: 'SB',
    SD: 'SD'
} as const;

/**
 * Which lever a schedule pulls.
 *
 * ``BUDGET`` is campaign-only: Amazon holds a budget on the campaign, so an
 * ad-group or target budget schedule would have nothing to write to.
 */
export type DaypartingAdjustType = 'BID' | 'BUDGET' | 'STATE';

/**
 * Which lever a schedule pulls.
 *
 * ``BUDGET`` is campaign-only: Amazon holds a budget on the campaign, so an
 * ad-group or target budget schedule would have nothing to write to.
 */
export const DaypartingAdjustType = {
    BID: 'BID',
    BUDGET: 'BUDGET',
    STATE: 'STATE'
} as const;

/**
 * A campaign, ad group or target a schedule can govern.
 *
 * Attributes:
 * scope: Which of the three it is.
 * ad_type: SP, SB or SD.
 * entity_id: Its Amazon id — what goes in ``entity_ids``.
 * label: Its name.
 * campaign_name: The campaign it sits in, for ad groups and targets.
 * ad_group_name: The ad group it sits in, for targets.
 * state: ENABLED / PAUSED / ARCHIVED where the master records it.
 * current_bid: A target's bid in ``currency``.
 * currency: ISO code for ``current_bid``.
 */
export type DaypartingEntity = {
    scope: DaypartingScope;
    ad_type: DaypartingAdType;
    entity_id: string;
    label: string;
    campaign_name?: string | null;
    ad_group_name?: string | null;
    state?: string | null;
    current_bid?: string | null;
    currency?: string | null;
};

/**
 * One attempt to move one entity, as the event log shows it.
 *
 * Attributes:
 * schedule_id: The schedule, or null once it is gone.
 * store_id: The store.
 * target_id: Amazon id of what moved — a campaign id on a campaign row.
 * target_type: KEYWORD, PRODUCT_TARGET, CAMPAIGN_BUDGET, CAMPAIGN_STATE
 * or AD_GROUP_STATE.
 * ad_type: SP, SB or SD.
 * campaign_id: Amazon campaign id.
 * ad_group_id: Amazon ad group id, where there is one.
 * transition: APPLY or REVERT.
 * action: The schedule's action, on an APPLY.
 * percent: Its percent, on an APPLY.
 * base_bid: The captured base — a budget on a budget row.
 * old_bid: The value before.
 * new_bid: The value written, or that would have been.
 * base_state: State rows: the captured base state.
 * old_state: State rows: the state before.
 * new_state: State rows: the state written.
 * status: success, failed or skipped.
 * error: Why it failed or was skipped.
 * applied_at: When it was attempted.
 * window_info: The window that fired it, or why a revert ran.
 */
export type DaypartingEventRead = {
    id: string;
    schedule_id: string | null;
    store_id: number;
    target_id: string;
    target_type: string | null;
    ad_type: string;
    campaign_id: string | null;
    ad_group_id: string | null;
    transition: DaypartingTransition;
    action: DaypartingAction | null;
    percent: string | null;
    base_bid: string | null;
    old_bid: string | null;
    new_bid: string | null;
    base_state: string | null;
    old_state: string | null;
    new_state: string | null;
    status: DaypartingEventStatus;
    error: string | null;
    applied_at: string;
    window_info: {
        [key: string]: unknown;
    } | null;
};

/**
 * How an attempt ended. Lower case, as VentaHub stored it.
 *
 * ``skipped`` covers two cases, told apart by ``error``: a state change that
 * would have written the value already there, and a change refused by the
 * Amazon write lock — which leaves the row where it was, so the next hour
 * tries again.
 */
export type DaypartingEventStatus = 'success' | 'failed' | 'skipped';

/**
 * How an attempt ended. Lower case, as VentaHub stored it.
 *
 * ``skipped`` covers two cases, told apart by ``error``: a state change that
 * would have written the value already there, and a change refused by the
 * Amazon write lock — which leaves the row where it was, so the next hour
 * tries again.
 */
export const DaypartingEventStatus = {
    SUCCESS: 'success',
    FAILED: 'failed',
    SKIPPED: 'skipped'
} as const;

/**
 * What the weekday profile measures.
 *
 * The additive ones are averaged per occurrence of the weekday; the rates
 * are computed from the summed parts, never averaged.
 */
export type DaypartingMetric = 'impressions' | 'clicks' | 'spend' | 'sales' | 'units' | 'ctr' | 'cpc' | 'acos' | 'roas';

/**
 * What the weekday profile measures.
 *
 * The additive ones are averaged per occurrence of the weekday; the rates
 * are computed from the summed parts, never averaged.
 */
export const DaypartingMetric = {
    IMPRESSIONS: 'impressions',
    CLICKS: 'clicks',
    SPEND: 'spend',
    SALES: 'sales',
    UNITS: 'units',
    CTR: 'ctr',
    CPC: 'cpc',
    ACOS: 'acos',
    ROAS: 'roas'
} as const;

/**
 * A new schedule.
 *
 * One schedule governs every entity in ``entity_ids``. VentaHub accepted a
 * legacy single id beside three per-scope lists; here there is one list, read
 * according to ``scope``, so a payload cannot name ad groups on a campaign
 * schedule.
 *
 * Attributes:
 * store_id: The store the entities belong to.
 * name: What to call it.
 * scope: What ``entity_ids`` name.
 * ad_type: SP, SB or SD.
 * entity_ids: Amazon campaign, ad-group or target ids. Repeats are
 * dropped, first occurrence kept.
 * action: What happens while a window is active.
 * adjust_type: Which lever — immutable once created.
 * percent: The size of an INCREASE/DECREASE. Ignored and stored as 0 for
 * PAUSE/ENABLE.
 * windows: When it is active.
 * timezone: The IANA zone the hours are read in.
 */
export type DaypartingScheduleCreate = {
    store_id: number;
    name: string;
    scope: DaypartingScope;
    ad_type: DaypartingAdType;
    entity_ids: Array<string>;
    action: DaypartingAction;
    adjust_type: DaypartingAdjustType;
    percent?: number | string;
    windows: Array<DaypartingWindow>;
    timezone?: string;
};

/**
 * A schedule as the page shows it.
 *
 * Attributes:
 * store_id: The store.
 * name: Its name.
 * enabled: Whether it is meant to run.
 * scope: What ``entity_ids`` name.
 * ad_type: SP, SB or SD.
 * entity_ids: The governed entities' Amazon ids.
 * entity_labels: Their names, same order and length. An entity no
 * longer in the master falls back to its id.
 * action: What happens in a window.
 * adjust_type: Which lever.
 * percent: The size of an INCREASE/DECREASE; 0 for PAUSE/ENABLE.
 * windows: When it is active.
 * timezone: The zone the hours are read in.
 * created_by: The principal that created it.
 * state_summary: Its expanded rows by state.
 * can_delete: Whether this caller may delete it: its creator, or a
 * tenant-wide access administrator.
 */
export type DaypartingScheduleRead = {
    id: string;
    created_at: string;
    updated_at: string;
    store_id: number;
    name: string;
    enabled: boolean;
    scope: DaypartingScope;
    ad_type: DaypartingAdType;
    entity_ids: Array<string>;
    entity_labels: Array<string>;
    action: DaypartingAction;
    adjust_type: DaypartingAdjustType;
    percent: string;
    windows: Array<DaypartingWindow>;
    timezone: string;
    created_by: string;
    state_summary: DaypartingStateSummary;
    can_delete: boolean;
};

/**
 * Changes to a schedule. Only the fields sent are applied.
 *
 * The selection (store, scope, ad type, entities) and the lever are fixed at
 * creation, as in VentaHub: a different selection or lever is a different
 * schedule.
 *
 * Attributes:
 * name: New name.
 * enabled: Turn it on or off.
 * action: New action. Must still agree with the stored lever.
 * percent: New size.
 * windows: Replacement windows — the whole list.
 * timezone: New IANA zone.
 */
export type DaypartingScheduleUpdate = {
    name?: string | null;
    enabled?: boolean | null;
    action?: DaypartingAction | null;
    percent?: number | string | null;
    windows?: Array<DaypartingWindow> | null;
    timezone?: string | null;
};

/**
 * What a schedule's ``entity_ids`` name.
 */
export type DaypartingScope = 'CAMPAIGN' | 'AD_GROUP' | 'TARGET';

/**
 * What a schedule's ``entity_ids`` name.
 */
export const DaypartingScope = {
    CAMPAIGN: 'CAMPAIGN',
    AD_GROUP: 'AD_GROUP',
    TARGET: 'TARGET'
} as const;

/**
 * Where a schedule's governed entities stand right now.
 *
 * Attributes:
 * total: Every live row the schedule expanded to.
 * base: Rows at their captured base.
 * adjusted: Rows the worker has moved — the page's "Adjusted now".
 */
export type DaypartingStateSummary = {
    total: number;
    base: number;
    adjusted: number;
};

/**
 * Which way an event moved the entity.
 */
export type DaypartingTransition = 'APPLY' | 'REVERT';

/**
 * Which way an event moved the entity.
 */
export const DaypartingTransition = {
    APPLY: 'APPLY',
    REVERT: 'REVERT'
} as const;

/**
 * One day of the week in the profile.
 *
 * Attributes:
 * day_of_week: 0 = Monday … 6 = Sunday, as in a schedule's windows.
 * value: The metric for that weekday, or null where its denominator is
 * zero (a CTR with no impressions is unknown, not 0%).
 * occurrences: How many of that weekday the range holds — what the
 * additive metrics are averaged over.
 */
export type DaypartingWeekday = {
    day_of_week: number;
    value: number | null;
    occurrences: number;
};

/**
 * How the selection performs by day of week.
 *
 * VentaHub drew an hour-of-day by day-of-week grid from hourly Amazon
 * Marketing Stream data. VentaWiz ingests daily reports only, so this is the
 * day-of-week half of that grid; the hour half waits on hourly data.
 *
 * Attributes:
 * metric: What ``value`` measures.
 * start: First day, inclusive.
 * end: Last day, inclusive.
 * currency: The store's currency, for spend, sales and CPC.
 * days: Monday first, always seven.
 * max_value: The largest value, for a colour ramp; 0 when there is none.
 */
export type DaypartingWeekdayProfile = {
    metric: DaypartingMetric;
    start: string;
    end: string;
    currency: string | null;
    days: Array<DaypartingWeekday>;
    max_value: number;
};

/**
 * One active period.
 *
 * Attributes:
 * days_of_week: 0 = Monday … 6 = Sunday. At least one, no repeats.
 * start_hour: First hour, inclusive, 0 to 23.
 * end_hour: Last hour, exclusive, 1 to 24. A 9-to-17 window stops at 17:00.
 * date_start: Optional first day, inclusive.
 * date_end: Optional last day, inclusive.
 */
export type DaypartingWindow = {
    days_of_week: Array<number>;
    start_hour: number;
    end_hour: number;
    date_start?: string | null;
    date_end?: string | null;
};

/**
 * Body of ``POST /v1/stores/{id}/deactivate``.
 *
 * Attributes:
 * reason: Why. Required: a store that stopped syncing with no
 * explanation is the first thing somebody reactivates by mistake.
 */
export type DeactivateRequest = {
    reason: string;
};

/**
 * What a person may say an expiry came from. The platform-sourced values are not theirs.
 */
export type DeclaredExpirySource = 'none' | 'default_rule' | 'manual';

/**
 * What a person may say an expiry came from. The platform-sourced values are not theirs.
 */
export const DeclaredExpirySource = {
    NONE: 'none',
    DEFAULT_RULE: 'default_rule',
    MANUAL: 'manual'
} as const;

/**
 * Rows a deletion would reach that are another store's data too.
 *
 * Attributes:
 * table: The table.
 * rows: How many.
 * other_store_ids: The stores they belong to; empty for a row of
 * another tenant.
 * detail: What ties them to this store, in words.
 */
export type DeletionBlocking = {
    table: string;
    rows: number;
    other_store_ids: Array<number>;
    detail: string;
};

/**
 * A preview group: Orders, Finance, Ads, ...
 */
export type DeletionDomain = {
    domain: string;
    tables: Array<DeletionTable>;
};

/**
 * Response of ``GET /v1/stores/{id}/deletion-preview``: what deleting would remove.
 *
 * Attributes:
 * store: The store.
 * domains: Rows per table, grouped; every footprint table that exists,
 * zero counts included, so the list is the same shape for every
 * store.
 * streams: Streams to unsubscribe.
 * grants_to_revoke: How many grants dv-auth would revoke. ``None``:
 * dv-auth has no way to count them ahead of the revoke, which
 * reports its own count.
 * kept: What survives a deletion, in words.
 * blocking: Rows that are other stores' data too. While any exist the
 * store cannot be deleted (409 ``cross_store_references``); empty
 * when none.
 */
export type DeletionPreview = {
    store: DeletionStore;
    domains: Array<DeletionDomain>;
    streams: Array<DeletionStream>;
    grants_to_revoke: number | null;
    kept: Array<string>;
    blocking: Array<DeletionBlocking>;
};

/**
 * Body of ``POST /v1/stores/{id}/deletion``. The token is never logged.
 *
 * Attributes:
 * confirm_code: The store's code, typed by hand; compared exactly.
 * confirmation_token: dv-auth's confirmation from ``POST
 * /v1/session/reauth`` with ``action: store.delete`` and
 * ``resource: store:<id>``.
 */
export type DeletionRequest = {
    confirm_code: string;
    confirmation_token: string;
};

/**
 * Response of ``POST /v1/stores/{id}/deletion`` (202).
 */
export type DeletionStarted = {
    deletion_id: number;
    status: DeletionStatus;
};

/**
 * Where a deletion is. The last three are final.
 */
export type DeletionStatus = 'queued' | 'deactivating' | 'unsubscribing' | 'wiping' | 'purging' | 'tombstoning' | 'revoking' | 'done' | 'failed' | 'needs_manual_cleanup';

/**
 * Where a deletion is. The last three are final.
 */
export const DeletionStatus = {
    QUEUED: 'queued',
    DEACTIVATING: 'deactivating',
    UNSUBSCRIBING: 'unsubscribing',
    WIPING: 'wiping',
    PURGING: 'purging',
    TOMBSTONING: 'tombstoning',
    REVOKING: 'revoking',
    DONE: 'done',
    FAILED: 'failed',
    NEEDS_MANUAL_CLEANUP: 'needs_manual_cleanup'
} as const;

/**
 * One step of a deletion.
 *
 * Attributes:
 * name: ``deactivate``, ``unsubscribe``, ``wipe_credentials``,
 * ``purge``, ``tombstone``, ``publish``, ``revoke`` or ``notify``.
 * status: :class:`~ventawiz.models.StepStatus`.
 * detail: What happened, in words, when there is something to say.
 * counts: What the step counted: rows per table, grants revoked, ...
 */
export type DeletionStep = {
    name: string;
    status: StepStatus;
    detail?: string | null;
    counts?: {
        [key: string]: number;
    } | null;
};

/**
 * The store a preview is about.
 */
export type DeletionStore = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
};

/**
 * A real-time stream the deletion unsubscribes from the platform.
 */
export type DeletionStream = {
    key: string;
    label: string;
};

/**
 * One table of a store's footprint and how many rows the store has there.
 */
export type DeletionTable = {
    table: string;
    rows: number;
};

/**
 * When it arrived, and the carrier's tracking number.
 */
export type DeliverWrite = {
    date: string;
    tracking_number?: string | null;
};

/**
 * How the platform delivers a stream's events to VentaWiz.
 */
export type Delivery = 'sqs' | 'webhook';

/**
 * How the platform delivers a stream's events to VentaWiz.
 */
export const Delivery = {
    SQS: 'sqs',
    WEBHOOK: 'webhook'
} as const;

/**
 * ``Demand = Organic DRR * (days + price + event + ad)`` over one window.
 *
 * Attributes:
 * days: The window's days.
 * price: The price lever's extra days: ``Σ (Price(d) - 1)``.
 * event: The event lever's extra days.
 * ad: The AMS lever's extra days.
 */
export type DemandBracket = {
    days: number;
    price: number;
    event: number;
    ad: number;
};

/**
 * Which model tier answers: the fast one, or the deep one (snapshots always).
 */
export type Depth = 'fast' | 'deep';

/**
 * Which model tier answers: the fast one, or the deep one (snapshots always).
 */
export const Depth = {
    FAST: 'fast',
    DEEP: 'deep'
} as const;

/**
 * Orders shipped to one country.
 */
export type Destination = {
    country_code: string;
    orders: number;
    units: number;
};

/**
 * Everything the row-expand panel shows, and the one action it offers.
 */
export type DetailRead = {
    store_id: number;
    store_name: string;
    po_number: string;
    po_status: string | null;
    is_closed: boolean;
    ordered_at: string | null;
    /**
     * The store's retailer, by id and name.
     */
    retailer: PoWarehouseRead | null;
    skip_reason: string | null;
    warehouse_priority: Array<PoWarehouseRead>;
    existing_legs: Array<PoWarehouseRead>;
    lines: Array<LineRead>;
    plan: Array<PlanWarehouseRead>;
    coverage: Array<CoverageRead>;
    units_ordered: number;
    /**
     * Units the ops legs account for.
     */
    units_covered: number;
    status: BridgeStatus;
    stage: Stage;
    note: string | null;
    amazon_acknowledged_at: string | null;
    amazon_ack_status: AckStatus | null;
    amazon_ack_note: string | null;
    /**
     * Whether Create PO, Send to Warehouse and Acknowledge are switched on.
     */
    writes_enabled: boolean;
};

/**
 * ``details`` in a settings PATCH. Code and marketplace never change.
 *
 * Attributes:
 * brand_ids: Replaces the brands the store sells; ``[]`` for none.
 * Each must be one of this tenant's brands; a new one must be
 * active. (The deprecated ``brand_id`` is read as ``[brand_id]``.)
 */
export type DetailsUpdate = {
    /**
     * Deprecated, for one release: use brand_ids. A brand id is read as brand_ids [id], null as [].
     * @deprecated
     */
    brand_id?: number | null;
    name?: string | null;
    brand_ids?: Array<number> | null;
    store_type?: string | null;
    is_retail?: boolean | null;
    ads_timezone?: string | null;
    timezone?: string | null;
};

/**
 * Whether a device is given work. Only ``active`` is.
 */
export type DeviceStatus = 'active' | 'disabled' | 'retired';

/**
 * Whether a device is given work. Only ``active`` is.
 */
export const DeviceStatus = {
    ACTIVE: 'active',
    DISABLED: 'disabled',
    RETIRED: 'retired'
} as const;

/**
 * The DHL sender fields, named as the store's DHL credentials name them.
 *
 * Each is ``None`` when the warehouse does not have it.
 *
 * Attributes:
 * dhl_sender_country: ISO 3166 alpha-3, as DHL takes it.
 */
export type DhlSender = {
    dhl_sender_name: string | null;
    dhl_sender_email: string | null;
    dhl_sender_street: string | null;
    dhl_sender_house_no: string | null;
    dhl_sender_postal_code: string | null;
    dhl_sender_city: string | null;
    dhl_sender_country: string | null;
};

/**
 * A subscription the platform already has for the account.
 *
 * Attributes:
 * ours: Its destination is one of VentaWiz's.
 * owner_hint: A readable name for a known destination, e.g. v1
 * VentaHub's queue.
 */
export type DiscoveredSubscription = {
    stream_key: string;
    provider_subscription_id?: string | null;
    provider_destination?: string | null;
    ours?: boolean;
    owner_hint?: string | null;
    status?: string | null;
};

/**
 * When it left.
 */
export type DispatchWrite = {
    date: string;
};

/**
 * Why an ad group is distressed.
 */
export type DistressReason = 'conversion_drop' | 'high_acos';

/**
 * Why an ad group is distressed.
 */
export const DistressReason = {
    CONVERSION_DROP: 'conversion_drop',
    HIGH_ACOS: 'high_acos'
} as const;

/**
 * An ad group whose last 7 days went wrong.
 *
 * Attributes:
 * ad_group_id: Its id here.
 * ad_group_name: Its name.
 * campaign_name: Its campaign's.
 * store_id: Its store, so a link can open the ad group's own account.
 * store_name: The store.
 * marketplace_code: The marketplace's short code.
 * country_code: Its country.
 * brand_name: The brand.
 * clicks: The 7 days' clicks.
 * spend: The 7 days' spend, in the ad group's currency.
 * sales: The 7 days' sales.
 * conversion: Units over clicks, this week.
 * previous_conversion: The same, the week before; None without its clicks.
 * conversion_change: Against the week before, a fraction; None when
 * the week before converted nothing.
 * acos: Spend over sales; None without sales.
 * target_acos: The ad group's target.
 * acos_severity: ``acos / target_acos``; 0 without either.
 * budget_use: Spend over the daily budget times the days it ran.
 * reasons: What is wrong.
 */
export type DistressedAdGroup = {
    ad_group_id: number;
    ad_group_name: string | null;
    campaign_name: string | null;
    store_id?: number | null;
    store_name: string | null;
    marketplace_code: string | null;
    country_code: string | null;
    brand_name: string | null;
    clicks: number;
    spend: MoneyRead;
    sales: MoneyRead;
    conversion: number | null;
    previous_conversion: number | null;
    conversion_change: number | null;
    acos: number | null;
    target_acos: number | null;
    acos_severity: number;
    budget_use: number | null;
    reasons: Array<DistressReason>;
};

/**
 * Ad groups whose conversion fell by a quarter or whose ACOS ran past 1.5x target.
 *
 * Attributes:
 * this_week_from: The last 7 days' first day (to the latest ad data).
 * through: Their last day.
 * rows: Most severe ACOS first.
 * fx: How spend and sales were converted (``convert_to``), when they were.
 */
export type DistressedAdGroups = {
    this_week_from: string | null;
    through: string | null;
    rows: Array<DistressedAdGroup>;
    fx?: FxMeta | null;
};

/**
 * The groups the deletion preview shows tables under.
 */
export type Domain = 'Orders' | 'Fulfilment' | 'Finance' | 'Purchasing' | 'Ads' | 'Search and traffic' | 'Listings' | 'Inventory' | 'Focus SKUs' | 'AI listing' | 'Uploads' | 'Observations' | 'Ingest state' | 'Real-time streams' | 'Access' | 'Credentials' | 'Settings' | 'Tickets' | 'Exports';

/**
 * The groups the deletion preview shows tables under.
 */
export const Domain = {
    ORDERS: 'Orders',
    FULFILMENT: 'Fulfilment',
    FINANCE: 'Finance',
    PURCHASING: 'Purchasing',
    ADS: 'Ads',
    SEARCH_AND_TRAFFIC: 'Search and traffic',
    LISTINGS: 'Listings',
    INVENTORY: 'Inventory',
    FOCUS_SKUS: 'Focus SKUs',
    AI_LISTING: 'AI listing',
    UPLOADS: 'Uploads',
    OBSERVATIONS: 'Observations',
    INGEST_STATE: 'Ingest state',
    REAL_TIME_STREAMS: 'Real-time streams',
    ACCESS: 'Access',
    CREDENTIALS: 'Credentials',
    SETTINGS: 'Settings',
    TICKETS: 'Tickets',
    EXPORTS: 'Exports'
} as const;

/**
 * The latest scheduled sweep's pass rate in one domain (the question's first domain tag).
 */
export type DomainPassRate = {
    domain: string;
    passed: number;
    total: number;
    pass_pct: number | null;
};

/**
 * One day of the series.
 */
export type DoneDay = {
    day: string;
    count: number;
};

/**
 * What the done-per-day series counts.
 */
export type DoneMetric = 'solved' | 'reviewed' | 'assigned';

/**
 * What the done-per-day series counts.
 */
export const DoneMetric = {
    SOLVED: 'solved',
    REVIEWED: 'reviewed',
    ASSIGNED: 'assigned'
} as const;

/**
 * What the row's rate is made of.
 *
 * ``organic``: sales over the window. ``organic+channel``: with DF, vendor
 * or offline units in it. ``no_history``: nothing sold, so no demand.
 * ``thin_window``: too few sale days for a rate; the order is sized off sales.
 */
export type DrrSource = 'organic' | 'organic+channel' | 'no_history' | 'thin_window';

/**
 * What the row's rate is made of.
 *
 * ``organic``: sales over the window. ``organic+channel``: with DF, vendor
 * or offline units in it. ``no_history``: nothing sold, so no demand.
 * ``thin_window``: too few sale days for a rate; the order is sized off sales.
 */
export const DrrSource = {
    ORGANIC: 'organic',
    'ORGANIC+CHANNEL': 'organic+channel',
    NO_HISTORY: 'no_history',
    THIN_WINDOW: 'thin_window'
} as const;

/**
 * How an existing or deleted store collides with a new one.
 *
 * ``same_account_marketplace`` and ``tombstoned_code`` would be refused by
 * onboarding; the other two are warnings.
 */
export type DuplicateKind = 'same_account_marketplace' | 'same_seller_other_marketplace' | 'same_credentials' | 'tombstoned_code';

/**
 * How an existing or deleted store collides with a new one.
 *
 * ``same_account_marketplace`` and ``tombstoned_code`` would be refused by
 * onboarding; the other two are warnings.
 */
export const DuplicateKind = {
    SAME_ACCOUNT_MARKETPLACE: 'same_account_marketplace',
    SAME_SELLER_OTHER_MARKETPLACE: 'same_seller_other_marketplace',
    SAME_CREDENTIALS: 'same_credentials',
    TOMBSTONED_CODE: 'tombstoned_code'
} as const;

/**
 * One collision. ``store`` is ``null`` for a deleted store's code.
 */
export type DuplicateMatch = {
    kind: DuplicateKind;
    store: DuplicateStore | null;
    detail: string | null;
};

/**
 * Response of ``POST /v1/stores/onboarding/duplicates``.
 */
export type DuplicateMatches = {
    matches: Array<DuplicateMatch>;
};

/**
 * Body of ``POST /v1/stores/onboarding/duplicates``. Nothing is stored.
 *
 * Attributes:
 * marketplace_id: The new store's marketplace.
 * external_seller_id: Its account id.
 * code: Its code.
 * client_id_fingerprint: The lower-case hex SHA-256 of the trimmed
 * client id it will connect with - never the id itself.
 */
export type DuplicateQuery = {
    marketplace_id: number;
    external_seller_id?: string | null;
    code?: string | null;
    client_id_fingerprint?: string | null;
};

/**
 * The store a new one collides with.
 */
export type DuplicateStore = {
    id: number;
    code: string;
    name: string;
    marketplace: string;
    is_active: boolean;
};

/**
 * In-grid edits to one dataset, sent for approval rather than written.
 *
 * Attributes:
 * store_id: The store the rows belong to, where the dataset has stores;
 * every row must be in it.
 * rows: The edited rows.
 */
export type EditRequest = {
    store_id?: number | null;
    rows: Array<RowEdit>;
};

/**
 * The approval ticket an edit or a delete became.
 *
 * Attributes:
 * status: Always ``submitted``: nothing is written until it is approved.
 * changed_rows: Rows with at least one value that differs; 1 for a
 * delete.
 * ticket_id: The ticket raised.
 * ticket_number: Its number, for people.
 */
export type EditSubmitted = {
    status?: 'submitted';
    changed_rows: number;
    ticket_id: string;
    ticket_number: number;
};

/**
 * The columns an edit may change, in grid order.
 */
export type EditableColumns = {
    columns: Array<string>;
};

/**
 * One entity the store is pulled for.
 */
export type EntityFreshness = {
    connector: string;
    entity: string;
    cadence_minutes?: number | null;
    last_success_at?: string | null;
    state: FreshnessState;
    dagster_status?: DagsterStatus;
};

/**
 * Everything that happened to one target, ad group or campaign.
 *
 * Attributes:
 * entity: What it is.
 * first_seen: When it first appeared in any data.
 * last_active: The latest day it appeared.
 * change_count: Bid and state changes.
 * events: Oldest first.
 * note: A caveat about how this history was assembled, when there is one.
 */
export type EntityTimeline = {
    entity: TimelineEntity;
    first_seen: string | null;
    last_active: string | null;
    change_count: number;
    events: Array<TimelineEvent>;
    note?: string | null;
};

/**
 * The body of every error this service answers (``exception_handlers``).
 *
 * Attributes:
 * type: The code a client branches on, e.g. ``run_in_progress`` or
 * ``quota_exceeded``.
 * message: Safe to show a person.
 * request_id: The id the logs carry, for a support request.
 */
export type ErrorDetail = {
    type: string;
    message: string;
    request_id?: string | null;
};

/**
 * ``{"error": {...}}``: documented where a client acts on the error's ``type``.
 */
export type ErrorEnvelope = {
    error: ErrorDetail;
};

/**
 * What one sweep left behind: which questions failed, and which the spend cap skipped.
 */
export type EvalSummary = {
    failed?: Array<string>;
    skipped?: Array<string>;
};

/**
 * One entry in an order's timeline.
 *
 * Attributes:
 * id: ULID.
 * event_type: Dotted name, e.g. ``order.status_changed``.
 * occurred_at: When it happened.
 * actor: Who caused it.
 * payload: What changed.
 */
export type EventRead = {
    id: string;
    event_type: string;
    occurred_at: string;
    actor: string;
    payload: {
        [key: string]: unknown;
    };
};

/**
 * A store this tenant already has for the probed account.
 */
export type ExistingStore = {
    store_id: number;
    code: string;
    name: string;
    marketplace_id: number;
    external_seller_id: string;
    is_active: boolean;
};

/**
 * How long a surface's credentials live.
 */
export type ExpiryRuleRead = {
    kind: string;
    days: number;
    what: string;
    auto_source: string | null;
};

/**
 * Where ``expires_at`` came from — which decides how far to trust it.
 *
 * ``amazon_notification`` and ``api`` are the platform telling us; a
 * ``default_rule`` is our guess from the platform's published lifetime;
 * ``manual`` is somebody typing a date; ``none`` means it does not expire,
 * or nobody knows.
 */
export type ExpirySource = 'none' | 'default_rule' | 'manual' | 'amazon_notification' | 'api';

/**
 * Where ``expires_at`` came from — which decides how far to trust it.
 *
 * ``amazon_notification`` and ``api`` are the platform telling us; a
 * ``default_rule`` is our guess from the platform's published lifetime;
 * ``manual`` is somebody typing a date; ``none`` means it does not expire,
 * or nobody knows.
 */
export const ExpirySource = {
    NONE: 'none',
    DEFAULT_RULE: 'default_rule',
    MANUAL: 'manual',
    AMAZON_NOTIFICATION: 'amazon_notification',
    API: 'api'
} as const;

/**
 * A domain of the store footprint and its exportable tables.
 */
export type ExportDomain = {
    domain: string;
    tables: Array<ExportTable>;
};

/**
 * Response of ``GET /v1/stores/{id}/export-domains``: what can be exported, by domain.
 */
export type ExportDomains = {
    domains: Array<ExportDomain>;
};

/**
 * Body of ``POST /v1/stores/{id}/exports``. No ``domains``: every domain.
 *
 * Attributes:
 * domains: The footprint domains to export (``Orders``, ``Ads``, ...).
 */
export type ExportRequest = {
    domains?: Array<Domain> | null;
};

/**
 * Response of ``POST /v1/stores/{id}/exports`` (202).
 */
export type ExportStarted = {
    export_id: number;
    status: ExportStatus;
};

/**
 * Where an export is. ``ready`` becomes ``expired`` when its file is removed.
 */
export type ExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired';

/**
 * Where an export is. ``ready`` becomes ``expired`` when its file is removed.
 */
export const ExportStatus = {
    QUEUED: 'queued',
    RUNNING: 'running',
    READY: 'ready',
    FAILED: 'failed',
    EXPIRED: 'expired'
} as const;

/**
 * One table of an export: rows the store has there, or rows written so far.
 */
export type ExportTable = {
    table: string;
    rows: number;
};

/**
 * Failed steps whose errors read the same once names and numbers are taken out.
 *
 * Attributes:
 * pattern: The normalised error.
 * kind: ``query`` or ``endpoint_call``.
 * count: Failed steps.
 * runs: Distinct runs they failed in.
 * tables: The tables the errors name, most often first.
 */
export type FailureGroup = {
    pattern: string;
    kind: string;
    count: number;
    runs: number;
    tables: Array<string>;
    example: string;
    example_run_id: string;
};

/**
 * The most frequent failures, most frequent first.
 */
export type FailureGroups = {
    items: Array<FailureGroup>;
};

/**
 * A dataset free SQL kept reading although a query method covers it.
 */
export type FallbackTable = {
    table: string;
    flags: number;
};

/**
 * What marketplace stock can be grouped by.
 */
export type FbaStockGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'listing' | 'product' | 'sku' | 'brand' | 'category' | 'subcategory';

/**
 * What marketplace stock can be grouped by.
 */
export const FbaStockGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    LISTING: 'listing',
    PRODUCT: 'product',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory'
} as const;

/**
 * Marketplace stock, grouped.
 */
export type FbaStockReport = {
    items: Array<FbaStockRow>;
    pagination: Pagination;
    totals: Array<FbaStockTotals>;
    meta: ReportMeta;
};

/**
 * One group of marketplace stock pools.
 *
 * Grouped by product, ``product_ref`` is the ASIN (Amazon) or partner SKU
 * (Noon) the marketplace reported; grouped by listing, the listing's own.
 */
export type FbaStockRow = {
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    sellable_units?: number | null;
    unsellable_units?: number | null;
    fba_aged_91_180_units?: number | null;
    fba_aged_181_270_units?: number | null;
    stranded_count?: number | null;
    pool_count?: number | null;
    seller_flex_units?: number | null;
};

/**
 * Stock marketplaces hold for us (Amazon FBA, Noon FBN), over every matching pool.
 *
 * Current stock only: each pool once, as last reported, from stores whose
 * last report is at most two days older than the newest store's and than
 * the build that measured the stock, so a store whose feed stops drops out
 * (plan §9.4). Noon's echo of our own warehouse stock is never here.
 * Seller Flex units may also be in our own warehouses, so this is shown
 * beside warehouse stock, never added to it.
 *
 * Attributes:
 * sellable_units: Units the marketplace can sell.
 * unsellable_units: Units it holds and cannot sell; ``null`` when no
 * pool reported any.
 * fba_aged_91_180_units: Amazon's 91 to 180 day age bucket, reported for
 * some marketplaces only; ``null`` where none was. A partial view,
 * never a split of the sellable units.
 * fba_aged_181_270_units: Amazon's 181 to 270 day bucket, likewise.
 * stranded_count: Pools Amazon reported stranded.
 * pool_count: Pools of stock.
 * seller_flex_units: Sellable units on Seller Flex seller SKUs, known
 * by their ``-SF`` naming habit, so a lower bound.
 */
export type FbaStockTotals = {
    sellable_units?: number | null;
    unsellable_units?: number | null;
    fba_aged_91_180_units?: number | null;
    fba_aged_181_270_units?: number | null;
    stranded_count?: number | null;
    pool_count?: number | null;
    seller_flex_units?: number | null;
};

/**
 * Switch one feature on or off.
 */
export type FeatureWrite = {
    feature: StoreFeatureName;
    enabled: boolean;
};

/**
 * What an upload saved.
 */
export type FeeCommitResult = {
    inserted: number;
    updated: number;
};

/**
 * What an upload would do, before anything is saved.
 */
export type FeePreview = {
    columns: Array<string>;
    rows: Array<FeePreviewRow>;
    new: number;
    updated: number;
    errors: number;
    writable: boolean;
};

/**
 * One sheet row as it would be saved.
 */
export type FeePreviewRow = {
    row: number;
    product_ref: string;
    /**
     * Every other column, by header
     */
    values: {
        [key: string]: string | null;
    };
    action: 'new' | 'update' | 'error';
    error?: string | null;
};

/**
 * One marketplace fee estimate.
 */
export type FeeRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    asin: string | null;
    sku_code: string | null;
    sku_name: string | null;
    currency_code: string | null;
    selling_price: string | null;
    fee_fixed: string | null;
    fee_variable_frac: string | null;
    fee_return_frac: string | null;
    fee_shipping: string | null;
    total_fee: string;
    estimate_date: string | null;
};

/**
 * Whether fee uploads are saved here, and why not when they are not.
 */
export type FeeStatus = {
    writable: boolean;
    reason: string | null;
};

/**
 * A store fees can be loaded for, with its marketplace and template.
 */
export type FeeStore = {
    id: number;
    name: string;
    marketplace: string;
    marketplace_code: string;
    /**
     * False once deactivated: its fees are listed, new ones are refused
     */
    is_active: boolean;
    /**
     * The marketplace family the template follows
     */
    template: string;
    template_columns: Array<string>;
    /**
     * What the product key is called: ASIN, EAN, …
     */
    id_header: string;
};

/**
 * The viewer's rating of an answer.
 */
export type FeedbackRead = {
    rating: number;
    reason?: FeedbackReason | null;
    comment?: string | null;
};

/**
 * Why an answer was not helpful.
 */
export type FeedbackReason = 'wrong_number' | 'wrong_scope' | 'not_answered' | 'too_slow' | 'other';

/**
 * Why an answer was not helpful.
 */
export const FeedbackReason = {
    WRONG_NUMBER: 'wrong_number',
    WRONG_SCOPE: 'wrong_scope',
    NOT_ANSWERED: 'not_answered',
    TOO_SLOW: 'too_slow',
    OTHER: 'other'
} as const;

/**
 * Body of ``POST /runs/{id}/feedback``: 1 helpful, -1 not helpful, 0 clears the rating.
 */
export type FeedbackWrite = {
    rating: -1 | 0 | 1;
    reason?: FeedbackReason | null;
    comment?: string | null;
};

/**
 * One credential field, as the connect form renders it.
 */
export type FieldSpecRead = {
    key: string;
    label: string;
    secret: boolean;
    multiline: boolean;
    required: boolean;
    help: string;
    docs_url: string;
};

/**
 * Where a review flag is: open, fixed, or deliberately left.
 */
export type FlagStatus = 'open' | 'resolved' | 'wont_fix';

/**
 * Where a review flag is: open, fixed, or deliberately left.
 */
export const FlagStatus = {
    OPEN: 'open',
    RESOLVED: 'resolved',
    WONT_FIX: 'wont_fix'
} as const;

/**
 * Body of ``PATCH /flags/{id}``: resolve, reopen or annotate a flag.
 *
 * An omitted note or fix reference is unchanged; an empty fix reference
 * clears it.
 */
export type FlagUpdate = {
    status: FlagStatus;
    admin_note?: string | null;
    /**
     * The change that fixed it: PR, commit or catalog edit.
     */
    fix_ref?: string | null;
};

/**
 * Every scraper, and what the schedule says to run right now.
 *
 * Attributes:
 * scrapers: The catalogue, in a stable order.
 * current_kind: This two-hour block's kind, for every unpinned device.
 * block_ends_at: When it flips to the other kind.
 */
export type FleetCatalog = {
    scrapers: Array<FleetScraper>;
    current_kind: ScraperKind;
    block_ends_at: string;
};

/**
 * One installed agent, as the fleet page shows it.
 *
 * Attributes:
 * device_id: The agent's own id.
 * agent_kind: Which marketplace's agent; absent for one that never said.
 * email: Who set it up.
 * hostname: The machine.
 * os_info: The machine's OS.
 * agent_version: The agent build.
 * status: Whether it is given work.
 * notes: An administrator's notes.
 * last_seen: Its last check-in.
 * online: Whether it checked in within the online window.
 * pinned: The scrapers an administrator pinned, or None for the schedule.
 * runs_now: What a check-in now would hand it: nothing when out of
 * service, its pin when pinned, otherwise this block's scraper.
 * last_run: Its most recent run report.
 * created_at: When it first checked in.
 */
export type FleetDeviceRead = {
    device_id: string;
    agent_kind: ScraperMarketplace | null;
    email: string | null;
    hostname: string | null;
    os_info: string | null;
    agent_version: string | null;
    status: DeviceStatus;
    notes: string | null;
    last_seen: string | null;
    online: boolean;
    pinned: Array<ScraperKey> | null;
    runs_now: Array<ScraperKey>;
    last_run: FleetRunRead | null;
    created_at: string;
};

/**
 * What an administrator can change about a device.
 *
 * Only fields sent are applied. ``pinned: null`` clears a pin — the device
 * goes back to the schedule — which is different from not sending it.
 *
 * Attributes:
 * status: Take the device out of service, or back in.
 * notes: Free text for the fleet page. Empty clears it.
 * pinned: Scrapers to run every cycle instead of the schedule. Each must
 * be of the device's own marketplace.
 */
export type FleetDeviceUpdate = {
    status?: DeviceStatus | null;
    notes?: string | null;
    pinned?: Array<ScraperKey> | null;
};

/**
 * One scraper run an agent reported.
 *
 * ``scraper_key`` and ``status`` are plain strings rather than enums: older
 * agents reported values this service no longer hands out, and a history
 * page that refused to show them would hide exactly the rows worth looking
 * at.
 *
 * Attributes:
 * id: ``app_scraper_run.pk_run_id``.
 * device_id: The agent that ran it.
 * scraper_key: Which scraper.
 * cycle_id: The agent's id for the cycle.
 * started_at: When the exe started.
 * finished_at: When it exited.
 * status: How the agent judged it: success, partial, failed, crashed, skipped.
 * exit_code: The exe's exit code.
 * items_ok: Items done.
 * items_failed: Items failed.
 * items_total: Both together.
 * error_msg: Why it failed, when the agent knew.
 * log_tail: The exe's last output lines, oldest first.
 * created_at: When the report arrived.
 */
export type FleetRunRead = {
    id: number;
    device_id: string;
    scraper_key: string;
    cycle_id: string | null;
    started_at: string | null;
    finished_at: string | null;
    status: string;
    exit_code: number | null;
    items_ok: number;
    items_failed: number;
    items_total: number;
    error_msg: string | null;
    log_tail: Array<string>;
    created_at: string;
};

/**
 * One scraper the agents can run.
 *
 * Attributes:
 * key: Its stable name.
 * marketplace: The agent that carries it.
 * kind: Ads or reviews.
 * exe_name: The file the agent starts.
 * enabled: False for a scraper that does not exist yet.
 */
export type FleetScraper = {
    key: ScraperKey;
    marketplace: ScraperMarketplace;
    kind: ScraperKind;
    exe_name: string;
    enabled: boolean;
};

/**
 * How a metric's total splits, step by step.
 *
 * Attributes:
 * metric: Which figure.
 * currency: The values' currency; None for returns.
 * levels: The steps, left to right.
 * root: The total, split.
 * fx: How the amounts were converted; ``null`` without ``convert_to``.
 */
export type Flow = {
    metric: Metric;
    currency: string | null;
    levels: Array<FlowLevel>;
    root: FlowNode;
    fx?: FxMeta | null;
};

/**
 * A step of the breakdown, left to right.
 */
export type FlowLevel = 'category' | 'marketplace' | 'store' | 'brand' | 'product_category';

/**
 * A step of the breakdown, left to right.
 */
export const FlowLevel = {
    CATEGORY: 'category',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    BRAND: 'brand',
    PRODUCT_CATEGORY: 'product_category'
} as const;

/**
 * One node of the breakdown, with what it splits into.
 *
 * Attributes:
 * key: Stable within its parent: an id where there is one.
 * label: What it is called.
 * level: The step it is at; None at the root.
 * value: Its total: an amount, or units for returns.
 * children: The next step's nodes, largest first.
 */
export type FlowNode = {
    key: string;
    label: string;
    level: FlowLevel | null;
    value: string;
    children: Array<FlowNode>;
};

/**
 * Listings to add to a list.
 *
 * Attributes:
 * listed_product_ids: The listings.
 * principal_id: Whose list; omitted, the caller's own. Another person's
 * list needs ``catalogue:edit``.
 * name: That person's display name, shown in the sheet's "Assigned
 * User". The token carries no name, so the frontend sends it, as it
 * does when assigning a ticket.
 */
export type FocusAdd = {
    listed_product_ids: Array<number>;
    principal_id?: string | null;
    name: string;
};

/**
 * A brand with ASINs in the snapshot.
 *
 * Attributes:
 * brand_id: ``com_brand.pk_brand_id``.
 * name: Its name.
 */
export type FocusBrand = {
    brand_id: number;
    name: string;
};

/**
 * What an add or remove did, listing by listing.
 *
 * Attributes:
 * changed: Listings added (or removed).
 * unchanged: Already on the list (or already off it).
 * not_found: No live listing of this tenant within the caller's reach.
 */
export type FocusChange = {
    changed: Array<number>;
    unchanged: Array<number>;
    not_found: Array<number>;
};

/**
 * The two sheets: a week (Sunday to Saturday) or a calendar month.
 */
export type FocusGrain = 'weekly' | 'monthly';

/**
 * The two sheets: a week (Sunday to Saturday) or a calendar month.
 */
export const FocusGrain = {
    WEEKLY: 'weekly',
    MONTHLY: 'monthly'
} as const;

/**
 * A term with at least one frozen week.
 *
 * Attributes:
 * search_term: The term.
 * week_count: How many weeks were frozen for it.
 * total_spend: Spend across those weeks.
 */
export type FocusHistoryTerm = {
    search_term: string;
    week_count: number;
    total_spend: AdsMoney;
};

/**
 * The terms a store's history can be read for.
 *
 * Attributes:
 * terms: Most spend first.
 * brands: Brands across every week, for the filter.
 */
export type FocusHistoryTerms = {
    terms: Array<FocusHistoryTerm>;
    brands: Array<FocusBrand>;
};

/**
 * One listing on a person's list.
 *
 * Attributes:
 * id: The assignment.
 * listed_product_id: The listing.
 * store_id: Its store.
 * store_name: The store's name.
 * marketplace_code: e.g. ``AZUS``.
 * retailer_sku: The marketplace's product id (ASIN, EAN, ...).
 * seller_sku: Our seller SKU on the marketplace.
 * sku_id: The internal SKU the listing sells.
 * sku_code: Its code.
 * brand_name: Its brand.
 * title: The listing's title.
 * principal_id: Whose list it is on.
 * principal_name: Their name.
 * created_at: When it was added.
 * created_by: Who added it.
 */
export type FocusItem = {
    id: string;
    listed_product_id: number;
    store_id: number;
    store_name: string | null;
    marketplace_code: string | null;
    retailer_sku: string | null;
    seller_sku: string | null;
    sku_id: number | null;
    sku_code: string | null;
    brand_name: string | null;
    title: string | null;
    principal_id: string;
    principal_name: string;
    created_at: string;
    created_by: string;
};

/**
 * Listings to take off a list.
 *
 * Attributes:
 * listed_product_ids: The listings.
 * principal_id: Whose list; omitted, the caller's own.
 */
export type FocusRemove = {
    listed_product_ids: Array<number>;
    principal_id?: string | null;
};

/**
 * The top sponsored result on the term's latest scrape (SCH:87-118).
 *
 * Attributes:
 * asin: Sponsored slot 1's product id; None when unreadable.
 * price: Its listed price.
 * rating: Its star rating.
 * rating_count: How many ratings that is built on.
 * asp: Repeats ``price``: a competitor's average selling price is not
 * observable, we never see their units.
 * is_self: Slot 1 is one of our own advertised ASINs: there is no rival.
 */
export type FocusSearchTermCompetitor = {
    asin?: string | null;
    price?: string | null;
    rating?: string | null;
    rating_count?: number | null;
    asp?: string | null;
    is_self?: boolean;
};

/**
 * A term row with its ASIN rows, by spend descending (SCH:263-270).
 */
export type FocusSearchTermGroup = {
    search_term: string;
    asin?: string | null;
    version?: number;
    sku_id?: number | null;
    sku_code?: string | null;
    title?: string | null;
    rating?: string | null;
    rating_count?: number | null;
    selling_price?: string | null;
    stock?: number;
    warehouse_stock?: number;
    fba_stock?: number;
    intransit_quantity?: number;
    a_plus?: boolean;
    sas?: string | null;
    sas_samples?: number;
    has_scrape?: boolean;
    competitor?: FocusSearchTermCompetitor | null;
    manual?: FocusSearchTermManualEntry | null;
    asin_count?: number;
    asin_count_shown?: number;
    impressions?: number;
    clicks?: number;
    spend?: string;
    units?: number;
    sales?: string;
    ctr?: string | null;
    cvr?: string | null;
    asp?: string | null;
    acos?: string | null;
    target_acos?: string | null;
    target_conversion?: string | null;
    metrics_estimated?: boolean;
    ad_group_ids?: Array<string>;
    bid_action?: string | null;
    action_taken?: string | null;
    last_edited_at?: string | null;
    children?: Array<FocusSearchTermSheetRow>;
};

/**
 * The history's two pickers; brands span every frozen week (SCH:351-361).
 */
export type FocusSearchTermHistoryOptionsResponse = {
    terms: Array<FocusSearchTermHistoryTermOption>;
    brands: Array<FocusSearchTermSheetBrandOption>;
};

/**
 * One term's frozen weeks, newest first, unpaginated (SCH:364-384).
 */
export type FocusSearchTermHistoryResponse = {
    rows: Array<FocusSearchTermHistoryWeek>;
    search_term?: string | null;
    currency_code?: string | null;
    marketplace_timezone?: string;
    bid_action_labels?: {
        [key: string]: string;
    };
};

/**
 * One entry in the history's term picker, weighed by spend (SCH:337-348).
 */
export type FocusSearchTermHistoryTermOption = {
    search_term: string;
    week_count: number;
    total_spend: string;
};

/**
 * One frozen week of a single term, with that week's ASIN rows (SCH:324-334).
 */
export type FocusSearchTermHistoryWeek = {
    search_term: string;
    asin?: string | null;
    version?: number;
    sku_id?: number | null;
    sku_code?: string | null;
    title?: string | null;
    rating?: string | null;
    rating_count?: number | null;
    selling_price?: string | null;
    stock?: number;
    warehouse_stock?: number;
    fba_stock?: number;
    intransit_quantity?: number;
    a_plus?: boolean;
    sas?: string | null;
    sas_samples?: number;
    has_scrape?: boolean;
    competitor?: FocusSearchTermCompetitor | null;
    manual?: FocusSearchTermManualEntry | null;
    asin_count?: number;
    asin_count_shown?: number;
    impressions?: number;
    clicks?: number;
    spend?: string;
    units?: number;
    sales?: string;
    ctr?: string | null;
    cvr?: string | null;
    asp?: string | null;
    acos?: string | null;
    target_acos?: string | null;
    target_conversion?: string | null;
    metrics_estimated?: boolean;
    ad_group_ids?: Array<string>;
    bid_action?: string | null;
    action_taken?: string | null;
    last_edited_at?: string | null;
    children?: Array<FocusSearchTermSheetRow>;
    week_start: string;
    week_end: string;
};

/**
 * The Search Term Data form entry that put the term on the sheet (SCH:121-166).
 *
 * Typed by a person on ``recorded_at``: it describes that day's results page,
 * not the frozen week. ``bid_action`` is the form's and is read-only on the
 * sheet; the sheet's own is the term row's.
 */
export type FocusSearchTermManualEntry = {
    client_sku?: string | null;
    client_price?: string | null;
    client_delivery_days?: number | null;
    client_slot?: string | null;
    comp1_sku?: string | null;
    comp1_price?: string | null;
    comp1_delivery_days?: number | null;
    comp1_slot?: string | null;
    comp2_sku?: string | null;
    comp2_price?: string | null;
    comp2_delivery_days?: number | null;
    comp2_slot?: string | null;
    bid_action?: string | null;
    recorded_at?: string | null;
    recorded_by?: number | null;
};

/**
 * How many terms changed, and an id to find the save in the log.
 */
export type FocusSearchTermSheetBatchResult = {
    updated: number;
    batch_id: string;
};

/**
 * A save of several terms, all or nothing, on the latest week.
 */
export type FocusSearchTermSheetBatchUpdate = {
    rows: Array<FocusSearchTermSheetUpdateRow>;
};

/**
 * One brand among the week's ASIN rows.
 */
export type FocusSearchTermSheetBrandOption = {
    id: number;
    name: string;
};

/**
 * Distinct values of one column for its filter list (SCH:387-405).
 *
 * ``__blank__`` stands for "no value"; ``truncated`` says the list hit the cap.
 */
export type FocusSearchTermSheetColumnValuesResponse = {
    column: string;
    values: Array<string>;
    truncated: boolean;
};

/**
 * The stores with at least one frozen week that the caller reaches (SCH:291-294).
 */
export type FocusSearchTermSheetOptionsResponse = {
    stores: Array<FocusSearchTermSheetStoreOption>;
};

/**
 * One page of terms for one frozen week (SCH:297-321).
 *
 * ``total`` counts terms: their ASIN rows come with them, never paged apart.
 * Only the latest week is editable, and ``is_editable`` says so.
 */
export type FocusSearchTermSheetResponse = {
    rows: Array<FocusSearchTermGroup>;
    total: number;
    page: number;
    size: number;
    currency_code?: string | null;
    marketplace_timezone?: string;
    brand_options?: Array<FocusSearchTermSheetBrandOption>;
    bid_action_options?: Array<string>;
    bid_action_labels?: {
        [key: string]: string;
    };
    week_options?: Array<FocusSearchTermSheetWeekOption>;
    selected_week_start?: string | null;
    selected_week_end?: string | null;
    is_editable?: boolean;
};

/**
 * One stored row: a term row (``asin`` None) or one of its ASIN rows (SCH:169-260).
 *
 * See the module docstring for which fields belong to which grain.
 */
export type FocusSearchTermSheetRow = {
    search_term: string;
    asin?: string | null;
    version?: number;
    sku_id?: number | null;
    sku_code?: string | null;
    title?: string | null;
    rating?: string | null;
    rating_count?: number | null;
    selling_price?: string | null;
    stock?: number;
    warehouse_stock?: number;
    fba_stock?: number;
    intransit_quantity?: number;
    a_plus?: boolean;
    sas?: string | null;
    sas_samples?: number;
    has_scrape?: boolean;
    competitor?: FocusSearchTermCompetitor | null;
    manual?: FocusSearchTermManualEntry | null;
    asin_count?: number;
    asin_count_shown?: number;
    impressions?: number;
    clicks?: number;
    spend?: string;
    units?: number;
    sales?: string;
    ctr?: string | null;
    cvr?: string | null;
    asp?: string | null;
    acos?: string | null;
    target_acos?: string | null;
    target_conversion?: string | null;
    metrics_estimated?: boolean;
    ad_group_ids?: Array<string>;
    bid_action?: string | null;
    action_taken?: string | null;
    last_edited_at?: string | null;
};

/**
 * One store the caller may open the sheet for.
 */
export type FocusSearchTermSheetStoreOption = {
    id: number;
    name: string;
};

/**
 * One term's edit, guarded by the ``version`` it was served with (SCH:408-424).
 *
 * A field left out of the body is left alone; an explicit null clears it.
 */
export type FocusSearchTermSheetUpdateRow = {
    search_term: string;
    version: number;
    bid_action?: string | null;
    action_taken?: string | null;
};

/**
 * One frozen week in the period picker.
 */
export type FocusSearchTermSheetWeekOption = {
    week_start: string;
    week_end: string;
};

/**
 * A snapshot re-run (SCH:427-447). No week: the latest completed one.
 */
export type FocusSearchTermSnapshotRunRequest = {
    /**
     * Sunday of the week to freeze. Omit for the latest completed week.
     */
    week_start?: string | null;
    /**
     * Optional Saturday of the same week; must equal week_start + 6.
     */
    week_end?: string | null;
};

/**
 * What the re-run froze.
 *
 * VentaHub queued a Celery task and answered with its id; VentaWiz freezes
 * the week inside the request (a store-week takes well under a second), so
 * the sheet is current the moment this returns. ``task_id`` names the run
 * in the event log.
 */
export type FocusSearchTermSnapshotRunResponse = {
    task_id: string;
    store_id: number;
    week_start?: string | null;
    week_end?: string | null;
    rows_written?: number;
    detail: string;
};

/**
 * One store's focus search terms for one week.
 *
 * Attributes:
 * weeks: Every frozen week for the store, newest first.
 * selected_week: The week shown, or null when there are none.
 * brands: Brands with ASINs in that week, for the filter.
 * currency: The store's currency.
 * bid_action_labels: How to show each ``bid_action`` value.
 */
export type FocusSheetPage = {
    items: Array<FocusTermRow>;
    pagination: Pagination;
    weeks: Array<FocusWeek>;
    selected_week: FocusWeek | null;
    brands: Array<FocusBrand>;
    currency: string;
    bid_action_labels: {
        [key: string]: string;
    };
};

/**
 * One term across every frozen week.
 *
 * Attributes:
 * search_term: The term.
 * currency: The store's currency.
 * bid_action_labels: How to show each ``bid_action`` value.
 * weeks: Newest first.
 */
export type FocusTermHistory = {
    search_term: string;
    currency: string;
    bid_action_labels: {
        [key: string]: string;
    };
    weeks: Array<FocusTermRow>;
};

/**
 * One focus search term in one week, with its ASIN split.
 *
 * ``payload`` is the row exactly as the weekly snapshot froze it —
 * competitor, share-of-slot, rates, targets, the form's manual entries — so
 * the sheet shows what was true that week rather than a recomputation from
 * today's catalogue. ``children`` are its per-ASIN rows, frozen the same way.
 *
 * Attributes:
 * search_term: The term.
 * week_start: The week.
 * week_end: Its last day.
 * bid_action: The action Ops recorded for the week, if any.
 * action_taken: Their note on what was done.
 * payload: The frozen metrics.
 * children: The per-ASIN rows' frozen metrics, most spend first.
 */
export type FocusTermRow = {
    search_term: string;
    week_start: string;
    week_end: string;
    bid_action: string | null;
    action_taken: string | null;
    payload: {
        [key: string]: unknown;
    };
    children: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * A frozen week.
 *
 * Attributes:
 * week_start: Its first day.
 * week_end: Its last day.
 */
export type FocusWeek = {
    week_start: string;
    week_end: string;
};

/**
 * An ASIN the SKU sells as.
 *
 * Attributes:
 * asin: The ASIN.
 * marketplace_id: Where: its storefront is the marketplace's.
 * store_name: The store that lists it.
 */
export type ForecastAsin = {
    asin: string;
    marketplace_id: number;
    store_name: string | null;
};

/**
 * One day of the forecast chart.
 *
 * Attributes:
 * day: The day.
 * actual: What it was; None ahead of today.
 * forecast: What it is forecast to be; None behind today.
 * low: The band's low end, ahead only.
 * high: The band's high end, ahead only.
 * actual_trend: The trailing 7-day mean of the actuals.
 * baseline_trend: The trailing 7-day mean of what each day of the
 * month usually brings, over the last year.
 */
export type ForecastPoint = {
    day: string;
    actual?: string | null;
    forecast?: string | null;
    low?: string | null;
    high?: string | null;
    actual_trend?: string | null;
    baseline_trend?: string | null;
};

/**
 * A forecast run: its scope, its days and a row per SKU.
 *
 * Attributes:
 * warehouse_id: The warehouse.
 * warehouse_name: Its name.
 * currency: The warehouse's currency: its stores' most common.
 * anchor: Day 0: the 90 days of history end the day before.
 * forecast_days: FD, as run.
 * shipping_days: SD.
 * buffer_days: BD.
 * order_days: FD + BD - SD.
 * profit_visible: Whether the caller may see PPP, CP and profitability.
 * built_at: When the marts were last built.
 * rows: A row per SKU the warehouse sold, holds or awaits.
 */
export type ForecastRead = {
    warehouse_id: number;
    warehouse_name: string | null;
    currency: string | null;
    anchor: string;
    forecast_days: number;
    shipping_days: number;
    buffer_days: number;
    order_days: number;
    profit_visible: boolean;
    built_at: string | null;
    rows: Array<ForecastRow>;
};

/**
 * One forecast run.
 *
 * Attributes:
 * warehouse_id: The warehouse: its stock, its stores' sales.
 * brand_ids: Only these brands' SKUs; every brand when empty.
 * forecast_days: FD, how far ahead to look.
 * shipping_days: SD, the supplier's transit time. Not over FD.
 * buffer_days: BD, safety stock as days of demand.
 * planning: The uploaded events, price changes and marketing plans.
 */
export type ForecastRequest = {
    warehouse_id: number;
    brand_ids?: Array<number>;
    forecast_days: number;
    shipping_days: number;
    buffer_days: number;
    planning?: PlanningInput;
};

/**
 * One SKU's forecast and suggested order.
 *
 * Sales are units over the last 90 days. Amounts are in the warehouse's
 * currency.
 *
 * Attributes:
 * sku_id: The SKU.
 * sku_code: Its code.
 * sku_name: Its name.
 * brand_id: Its brand.
 * brand_name: The brand's name.
 * category: Its merchandising category.
 * subcategory: Its sub-category.
 * replen: Its replenishment class here: R, N or T.
 * asins: Its ASINs at the warehouse's stores, the best seller first.
 * sales_vendor: Units invoiced to the marketplace as a vendor (VO).
 * sales_seller: Units the stores sold on the marketplaces.
 * sales_df: Units invoiced on direct fulfilment.
 * sales_offline: Units invoiced to offline retailers (OF).
 * sales_total: The four together.
 * sale_days: The seller and DF sale days: the divisor for that half
 * of the rate.
 * drr_organic: The rate the forecast runs on; ``null`` on a thin window.
 * drr_ams: Ad-attributed units a day. Reported; not in the forecast.
 * drr_source: What the rate is made of.
 * event_factor: The largest event uplift in the forecast days.
 * ad_factor: The largest AMS multiplier in them.
 * price_factor: The price effect furthest from 1.0 in them.
 * demand_cover: Demand(FD + BD); ``null`` on a thin window.
 * demand_lead: Demand(SD); ``null`` on a thin window.
 * cover_bracket: Demand(FD + BD)'s days and levers.
 * lead_bracket: Demand(SD)'s.
 * on_hand_marketplace: Marketplace-held stock (FBA, FBN).
 * on_hand_warehouse: Our own warehouse's available stock.
 * on_hand: Both.
 * in_transit: Ordered from suppliers, on its way here.
 * cover_days: The day the shelf runs dry within the forecast days;
 * ``null`` while it lasts.
 * order_qty: The units to order now.
 * ppp_90d: Profit before ads over the 90 days.
 * cp_90d: PPP less ad spend.
 * profitability_90d: CP over the cost of the goods sold, a fraction
 * (0.25 is a 25% markup).
 * reorder_status: ``loss``, ``yes`` or ``no``.
 * pools: Each pool on its own.
 */
export type ForecastRow = {
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    replen: string | null;
    asins: Array<ForecastAsin>;
    sales_vendor: number;
    sales_seller: number;
    sales_df: number;
    sales_offline: number;
    sales_total: number;
    sale_days: number;
    drr_organic: number | null;
    drr_ams: number;
    drr_source: DrrSource;
    event_factor: number;
    ad_factor: number;
    price_factor: number;
    demand_cover: number | null;
    demand_lead: number | null;
    cover_bracket: DemandBracket;
    lead_bracket: DemandBracket;
    on_hand_marketplace: number;
    on_hand_warehouse: number;
    on_hand: number;
    in_transit: number;
    cover_days: number | null;
    order_qty: number;
    ppp_90d: MoneyRead | null;
    cp_90d: MoneyRead | null;
    profitability_90d: number | null;
    reorder_status: ReorderStatus;
    pools: Array<PoolForecastRead>;
};

/**
 * Whether there was enough history to forecast from.
 */
export type ForecastStatus = 'ok' | 'insufficient_data';

/**
 * Whether there was enough history to forecast from.
 */
export const ForecastStatus = {
    OK: 'ok',
    INSUFFICIENT_DATA: 'insufficient_data'
} as const;

/**
 * The community forum: its top threads and the month's most active members.
 *
 * Attributes:
 * connected: Whether the forum could be read; False when it is not
 * configured or did not answer, and the lists are then empty.
 * forum_url: Where the forum lives.
 * month: The contributors' month, ``YYYY-MM``.
 * threads: The top ten by the sort asked for.
 * contributors: The month's ten most active, most posts first.
 */
export type ForumActivity = {
    connected: boolean;
    forum_url: string;
    month: string;
    threads: Array<ForumThread>;
    contributors: Array<ForumContributor>;
};

/**
 * A member and how many posts they wrote in the month.
 */
export type ForumContributor = {
    username: string;
    contributions: number;
    avatar_url: string | null;
};

/**
 * What the forum's threads are ordered by.
 */
export type ForumSort = 'created' | 'comments' | 'likes';

/**
 * What the forum's threads are ordered by.
 */
export const ForumSort = {
    CREATED: 'created',
    COMMENTS: 'comments',
    LIKES: 'likes'
} as const;

/**
 * A thread on the community forum.
 */
export type ForumThread = {
    id: number;
    title: string;
    url: string;
    category: string | null;
    replies: number;
    likes: number;
    started_on: string;
    starter_name: string | null;
    starter_avatar_url: string | null;
};

/**
 * Where a freeze is in its life.
 */
export type FreezeState = 'scheduled' | 'active' | 'ended' | 'cancelled';

/**
 * Where a freeze is in its life.
 */
export const FreezeState = {
    SCHEDULED: 'scheduled',
    ACTIVE: 'active',
    ENDED: 'ended',
    CANCELLED: 'cancelled'
} as const;

/**
 * The newest data of one kind for one store.
 *
 * Attributes:
 * mode: ``not_applicable`` when the store has no such data at all.
 * last: The newest record's time.
 */
export type FreshnessCell = {
    mode: FreshnessMode;
    last: string | null;
};

/**
 * How a store's data of one kind arrives.
 */
export type FreshnessMode = 'synced' | 'not_applicable';

/**
 * How a store's data of one kind arrives.
 */
export const FreshnessMode = {
    SYNCED: 'synced',
    NOT_APPLICABLE: 'not_applicable'
} as const;

/**
 * How recent an entity's last success is against its cadence, as ingest decides it.
 */
export type FreshnessState = 'fresh' | 'late' | 'stale' | 'never';

/**
 * How recent an entity's last success is against its cadence, as ingest decides it.
 */
export const FreshnessState = {
    FRESH: 'fresh',
    LATE: 'late',
    STALE: 'stale',
    NEVER: 'never'
} as const;

/**
 * What the filters can be chosen from.
 */
export type FulfilmentLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
    marketplaces: Array<FulfilmentMarketplaceRead>;
};

/**
 * A marketplace some PO was ordered on, for the picker.
 */
export type FulfilmentMarketplaceRead = {
    id: number;
    name: string;
    code: string;
    country: string | null;
};

/**
 * Every retailer's row, busiest first, and their total.
 */
export type FulfilmentSummaryRead = {
    rows: Array<RetailerFulfilmentRead>;
    total: RetailerFulfilmentRead;
};

/**
 * ``fulfilment`` in a settings PATCH; each part optional.
 *
 * Attributes:
 * mcf_sources: Every store whose orders this store fulfils. Pairings
 * this store fulfils that are not listed are removed.
 * shopify: Replaces ``marketplace_meta.shopify``; every other key of
 * ``marketplace_meta`` (the Amazon write lock among them) is kept.
 */
export type FulfilmentUpdate = {
    mcf_sources?: Array<McfSourceWrite> | null;
    shopify?: {
        [key: string]: unknown;
    } | null;
};

/**
 * How a converted response was converted.
 *
 * Attributes:
 * currency: The currency every amount is in: the ``convert_to`` asked for.
 * base: The currency the rates are quoted against (``EUR``).
 * as_of: When the newest rate was fetched; ``null`` when no rate has
 * ever been fetched, and so nothing but ``currency`` itself counted.
 * converted_from: The other currencies read and converted, sorted.
 * unconverted: The currencies read but left out for want of a rate,
 * sorted. Every figure excludes their rows.
 */
export type FxMeta = {
    currency: string;
    base: string;
    as_of: string | null;
    converted_from: Array<string>;
    unconverted: Array<string>;
};

/**
 * What the filters and the form can be chosen from.
 */
export type GlobalShipmentLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
    shipping_types?: Array<string>;
};

/**
 * One shipment: the vendor order it carries to the purchase order it fills.
 *
 * Attributes:
 * id: The shipment.
 * date: Dispatched on, when it has been.
 * number: Its name.
 * po_id: The purchase order it fills, at the export warehouse.
 * po: That PO's code.
 * vo_id: The vendor order it carries, from the import warehouse.
 * vo: That VO's code.
 * from_warehouse: Where it leaves from: the vendor order's warehouse.
 * to_warehouse: Where it arrives: the purchase order's warehouse.
 * currency: The currency of ``shipping_amount``.
 * shipping_type: Air, ocean LCL or ocean FCL.
 * cbm: Volume in cubic metres.
 * shipping_amount: The freight charge.
 * delivery_date: Delivered on, when it has been.
 * tracking_number: The carrier's tracking number.
 * comment: Free text.
 * notification_emails: Who is told when it moves.
 * status: Planned, in transit or delivered, from the dates.
 * vo_amount: The vendor order's value, in ``vo_currency``.
 * vo_currency: Its currency.
 * vo_units: Its units.
 */
export type GlobalShipmentRead = {
    id: number;
    date: string | null;
    number: string | null;
    po_id: number | null;
    po: string | null;
    vo_id: number | null;
    vo: string | null;
    from_warehouse?: string | null;
    to_warehouse?: string | null;
    currency: string | null;
    shipping_type: string | null;
    cbm: string | null;
    shipping_amount: string | null;
    delivery_date: string | null;
    tracking_number: string | null;
    comment: string | null;
    notification_emails?: Array<string>;
    status?: 'planned' | 'in_transit' | 'delivered';
    vo_amount?: string | null;
    vo_currency?: string | null;
    vo_units?: number | null;
};

/**
 * How an eval question is graded beyond its numbers and phrases.
 */
export type Grader = 'numbers' | 'llm';

/**
 * How an eval question is graded beyond its numbers and phrases.
 */
export const Grader = {
    NUMBERS: 'numbers',
    LLM: 'llm'
} as const;

/**
 * What one row of the Business View is.
 */
export type Grain = 'sku' | 'listing';

/**
 * What one row of the Business View is.
 */
export const Grain = {
    SKU: 'sku',
    LISTING: 'listing'
} as const;

/**
 * A group to put the store in, by kind and name; created if the tenant has none.
 */
export type GroupWrite = {
    kind: StoreGroupKind;
    name: string;
};

/**
 * Month-over-month growth from January of last year to the financial year's end (March).
 *
 * Attributes:
 * metric: Which figure.
 * currency: The totals' currency; None for returns.
 * status: Whether there was enough history to project from.
 * months: Each month, actual then projected.
 * fx: How the amounts were converted; ``null`` without ``convert_to``.
 */
export type Growth = {
    metric: Metric;
    currency: string | null;
    status: ForecastStatus;
    months: Array<GrowthMonth>;
    fx?: FxMeta | null;
};

/**
 * One month of the growth chart.
 *
 * Attributes:
 * month: Its first day.
 * total: The month's total; for this month and later, projected.
 * growth: Against the month before, a fraction. None for the first, or
 * after a month of nothing.
 * trend: The straight line through the actual months' growth.
 * projected: Whether ``total`` is (partly) forecast.
 */
export type GrowthMonth = {
    month: string;
    total: string;
    growth: number | null;
    trend: number | null;
    projected: boolean;
};

/**
 * One surface's state, as the store table shows it.
 *
 * ``ok`` the last test passed; ``failing`` it did not, or ingest has
 * stopped calling the account; ``unknown`` never tested; ``missing`` switched
 * on with no credentials; ``off`` neither.
 */
export type Health = 'ok' | 'failing' | 'unknown' | 'missing' | 'off';

/**
 * One surface's state, as the store table shows it.
 *
 * ``ok`` the last test passed; ``failing`` it did not, or ingest has
 * stopped calling the account; ``unknown`` never tested; ``missing`` switched
 * on with no credentials; ``off`` neither.
 */
export const Health = {
    OK: 'ok',
    FAILING: 'failing',
    UNKNOWN: 'unknown',
    MISSING: 'missing',
    OFF: 'off'
} as const;

/**
 * One part of the score.
 *
 * Attributes:
 * name: Which part.
 * score: 0-100.
 * weight: How much it counts towards the total; 0 means shown but not
 * counted (freshness when ingest could not be asked).
 * detail: What the score is made of, in words.
 */
export type HealthPart = {
    name: HealthPartName;
    score: number;
    weight: number;
    detail: string | null;
};

/**
 * The parts a store's health score is made of.
 */
export type HealthPartName = 'credentials' | 'freshness' | 'streams' | 'circuits' | 'alerts' | 'write_locks' | 'onboarding';

/**
 * The parts a store's health score is made of.
 */
export const HealthPartName = {
    CREDENTIALS: 'credentials',
    FRESHNESS: 'freshness',
    STREAMS: 'streams',
    CIRCUITS: 'circuits',
    ALERTS: 'alerts',
    WRITE_LOCKS: 'write_locks',
    ONBOARDING: 'onboarding'
} as const;

/**
 * How far the forecast reaches: the end of this week, or of this month.
 */
export type Horizon = 'week' | 'month';

/**
 * How far the forecast reaches: the end of this week, or of this month.
 */
export const Horizon = {
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * The three image tabs, as sellers know them.
 *
 * ``PRODUCT`` images are stored with the type ``aplus``; the service maps
 * between the two so no client has to know the older name.
 */
export type ImageCategory = 'lifestyle' | 'product' | 'infographic';

/**
 * The three image tabs, as sellers know them.
 *
 * ``PRODUCT`` images are stored with the type ``aplus``; the service maps
 * between the two so no client has to know the older name.
 */
export const ImageCategory = {
    LIFESTYLE: 'lifestyle',
    PRODUCT: 'product',
    INFOGRAPHIC: 'infographic'
} as const;

/**
 * One generated image.
 *
 * Attributes:
 * id: What the regenerate endpoint takes.
 * category: Its tab.
 * slot_index: Its position in the tab.
 * url: A presigned URL, good for an hour.
 * version: Re-rolls completed so far. See the module docstring.
 * width: Pixels.
 * height: Pixels.
 * passes_amazon_check: Whether it met Amazon's image requirements.
 * issues: What the check found.
 * is_main: The white-background main image. A description sets only
 * its angle, framing and light; it stays on pure white.
 * direction: The seller's description it was last generated from, or
 * None.
 */
export type ImageRead = {
    id: string;
    category: ImageCategory;
    slot_index: number;
    url: string;
    version: number;
    width: number;
    height: number;
    passes_amazon_check: boolean;
    issues: Array<string>;
    is_main: boolean;
    direction: string | null;
};

/**
 * One consequence of retiring an entity.
 *
 * Attributes:
 * rule: Which.
 * message: A sentence a person can act on.
 * count: How many rows or stores.
 * store_ids: The stores concerned, when the rule is about stores.
 */
export type ImpactItem = {
    rule: ImpactRule;
    message: string;
    count: number;
    store_ids: Array<number>;
};

/**
 * Why retiring an entity would matter.
 *
 * A warehouse blocks on ``primary_for_active_store`` and warns on the
 * others of its own; a brand and a vendor only warn.
 */
export type ImpactRule = 'primary_for_active_store' | 'linked_to_active_stores' | 'has_stock_rows' | 'retailer_default_warehouse' | 'on_active_stores' | 'on_live_skus' | 'has_open_vendor_orders';

/**
 * Why retiring an entity would matter.
 *
 * A warehouse blocks on ``primary_for_active_store`` and warns on the
 * others of its own; a brand and a vendor only warn.
 */
export const ImpactRule = {
    PRIMARY_FOR_ACTIVE_STORE: 'primary_for_active_store',
    LINKED_TO_ACTIVE_STORES: 'linked_to_active_stores',
    HAS_STOCK_ROWS: 'has_stock_rows',
    RETAILER_DEFAULT_WAREHOUSE: 'retailer_default_warehouse',
    ON_ACTIVE_STORES: 'on_active_stores',
    ON_LIVE_SKUS: 'on_live_skus',
    HAS_OPEN_VENDOR_ORDERS: 'has_open_vendor_orders'
} as const;

/**
 * What stock in transit can be grouped by.
 */
export type InTransitGrouping = 'warehouse' | 'sku' | 'brand' | 'category' | 'subcategory' | 'vendor' | 'vendor_order' | 'line_status' | 'intercompany';

/**
 * What stock in transit can be grouped by.
 */
export const InTransitGrouping = {
    WAREHOUSE: 'warehouse',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    VENDOR: 'vendor',
    VENDOR_ORDER: 'vendor_order',
    LINE_STATUS: 'line_status',
    INTERCOMPANY: 'intercompany'
} as const;

/**
 * Stock in transit, grouped.
 */
export type InTransitReport = {
    items: Array<InTransitRow>;
    pagination: Pagination;
    totals: Array<InTransitTotals>;
    meta: ReportMeta;
};

/**
 * One group of vendor order lines; the warehouse is the one ordered for.
 *
 * Attributes:
 * vendor_id: Filled when grouped by vendor.
 * vendor_name: Its name.
 * vendor_order_id: Filled when grouped by vendor order.
 * vendor_order_code: Its code.
 * ordered_at: When it was placed; ``null`` for an undated order.
 * line_status: Filled when grouped by line status: ``in_transit``, or
 * why a line is not: ``received``, ``part_received`` (the rest is
 * closed), ``undated``, ``past_window`` (placed over 90 days
 * before ``as_of``) or ``nothing_ordered``.
 * is_intercompany: Filled when grouped by intercompany: whether the
 * order is on one of our group companies.
 */
export type InTransitRow = {
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    in_transit_units?: number | null;
    network_in_transit_units?: number | null;
    unreceived_units?: number | null;
    ordered_units?: number | null;
    received_units?: number | null;
    line_count?: number | null;
    order_count?: number | null;
    oldest_order_age_days?: number | null;
    vendor_id?: number | null;
    vendor_name?: string | null;
    vendor_order_id?: number | null;
    vendor_order_code?: string | null;
    ordered_at?: string | null;
    line_status?: string | null;
    is_intercompany?: boolean | null;
};

/**
 * Stock ordered for our warehouses, over every matching (vendor order, SKU) line.
 *
 * In transit follows the operators' rule (D19): a line counts its ordered
 * units while nothing has been received against it, on a dated order
 * placed at most 90 days before ``meta.as_of``. What the rule leaves out —
 * the rest of a short delivery, older and undated orders — stays in
 * ``unreceived_units``.
 *
 * Attributes:
 * in_transit_units: Units on their way to the warehouse, by the
 * operators' rule.
 * network_in_transit_units: The same, without orders on our group
 * companies, whose units may still be on the sending warehouse's
 * shelf: the figure to total across warehouses (plan §9.5).
 * unreceived_units: Ordered less received, on every line, whether or
 * not the rule counts it.
 * ordered_units: Units ordered.
 * received_units: Units received.
 * line_count: (vendor order, SKU) lines.
 * order_count: Vendor orders.
 * oldest_order_age_days: Days since the oldest order with units in
 * transit was placed; ``null`` when none has.
 */
export type InTransitTotals = {
    in_transit_units?: number | null;
    network_in_transit_units?: number | null;
    unreceived_units?: number | null;
    ordered_units?: number | null;
    received_units?: number | null;
    line_count?: number | null;
    order_count?: number | null;
    oldest_order_age_days?: number | null;
};

/**
 * One run step of the store's, as ingest lists it. ``error_detail`` is always null.
 */
export type IngestRunRead = {
    run_id: number;
    connector: string;
    entity: string;
    state: RunState;
    trigger?: string | null;
    window_start?: string | null;
    window_end?: string | null;
    rows?: number | null;
    started_at: string;
    finished_at?: string | null;
    error_class?: string | null;
    error_detail?: string | null;
    trace_id?: string | null;
    run_state?: string | null;
    partition_key?: string | null;
    variant?: string | null;
    pages?: number | null;
    rows_loaded?: number | null;
    rows_rejected?: number | null;
};

/**
 * One area of two compared reports: the most important insight on each side.
 *
 * Attributes:
 * area: The area both sides are matched on.
 * left: The first report's insight there, if it has one.
 * right: The second report's.
 */
export type InsightPair = {
    area: string;
    left?: SnapshotInsight | null;
    right?: SnapshotInsight | null;
};

/**
 * One internal SKU.
 */
export type InternalSkuRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    id: number;
    sku_code: string;
    sku_name: string;
    sku_model: string | null;
    ean: string | null;
    upc_code: string | null;
    isbn: string | null;
    hsn_code: string | null;
    manufacturer: string | null;
    is_exclusive: boolean;
    is_eol: boolean;
    length_cm: string | null;
    width_cm: string | null;
    height_cm: string | null;
    weight_g: string | null;
    color: string | null;
    size: string | null;
    material: string | null;
    is_fragile: boolean;
    is_hazardous: boolean;
    country_of_origin: string | null;
    stores_count: number;
    updated_at: string | null;
};

/**
 * What raising this invoice would refuse. Empty when it would go through.
 */
export type InvoiceCheckRead = {
    problems: Array<InvoiceProblem>;
};

/**
 * A new sales invoice billing a set of boxes.
 *
 * Attributes:
 * warehouse_id: Where the boxes are.
 * retailer_id: Who is billed.
 * number: The invoice number. One live invoice per number and warehouse.
 * date: The invoice date.
 * currency: ISO 4217. Prices and costs are read in it.
 * credit_period: Days the retailer has to pay; the warehouse mapping's when left out.
 * comment: Free text.
 * po_code: The purchase order it fills, in the same warehouse.
 * box_codes: The boxes to bill.
 * prices: Typed prices, for SKUs with no retailer price or to override it.
 */
export type InvoiceCreate = {
    warehouse_id: number;
    retailer_id: number;
    number: string;
    date: string;
    currency: string;
    credit_period?: number | null;
    comment?: string | null;
    po_code?: string | null;
    box_codes: Array<string>;
    prices?: Array<SkuPrice>;
};

/**
 * An invoice, its lines and its boxes.
 *
 * Attributes:
 * invoice: The header.
 * lines: Each billed batch.
 * boxes: The box codes billed on it.
 * reviewed: Whether it has been reviewed, which dispatch needs.
 */
export type InvoiceDetailRead = {
    invoice: SalesInvoiceRead;
    lines: Array<SalesInvoiceLineRead>;
    boxes: Array<string>;
    reviewed: boolean;
};

/**
 * A change to an invoice's header or prices. Fields left out keep their value.
 *
 * The warehouse, currency, PO and boxes are not here: boxes are added and
 * removed one at a time, and the rest are fixed once billed.
 *
 * Attributes:
 * number: The invoice number.
 * date: The invoice date.
 * retailer_id: Who is billed; only when no PO is linked.
 * credit_period: Days to pay.
 * comment: Free text.
 * clear_movement: Clear the dispatch and delivery dates and tracking.
 * prices: New prices.
 */
export type InvoiceEdit = {
    number?: string | null;
    date?: string | null;
    retailer_id?: number | null;
    credit_period?: number | null;
    comment?: string | null;
    clear_movement?: boolean;
    prices?: Array<LinePrice>;
};

/**
 * One reason an invoice cannot be raised or changed.
 *
 * Attributes:
 * field: Which input, when one.
 * message: What is wrong, in a sentence a person can act on.
 */
export type InvoiceProblem = {
    field: string | null;
    message: string;
};

/**
 * An uploaded item sheet, read.
 *
 * Attributes:
 * items: Its rows, in sheet order, blank rows skipped.
 */
export type ItemSheet = {
    items: Array<ItemSheetRow>;
};

/**
 * One row of an uploaded item sheet.
 *
 * Attributes:
 * row: Its line in the sheet, counting the header as 1.
 * retailer_sku: The SKU; empty when the row has none.
 * comments: The row's comment, if any.
 */
export type ItemSheetRow = {
    row: number;
    retailer_sku: string;
    comments: string | null;
};

/**
 * Body of ``POST /v1/ai-listing/jobs``.
 *
 * Attributes:
 * asin: The product. Ten letters and digits; case is normalised.
 * store_id: Whose SP-API credentials fetch the listing.
 */
export type JobCreate = {
    asin: string;
    store_id: number;
};

/**
 * A job, in full.
 *
 * Attributes:
 * completed_step: The last step that finished.
 * warnings: Things that did not fail the job but are worth knowing.
 * error: Why it failed, when it did. Safe to show.
 * original: The listing as Amazon has it, once fetched.
 * key_phrases: Generated search phrases.
 * bullet_points: Generated bullets.
 * descriptions: Generated descriptions.
 * listing_selections: What the seller last saved.
 * lifestyle_images: The lifestyle tab, by slot. Slot 0 is the main image.
 * product_images: The product tab, by slot.
 * infographic_images: The infographic tab, by slot.
 */
export type JobRead = {
    id: string;
    asin: string | null;
    marketplace_id: string | null;
    store_id: number;
    store_name: string;
    marketplace_name: string;
    country_code: string;
    product_name: string;
    brand: string;
    model_number: string;
    status: AiListingJobStatus;
    progress_pct: number;
    created_at: string;
    updated_at: string | null;
    completed_step: string | null;
    warnings: Array<string>;
    error: string | null;
    original: OriginalListingRead | null;
    key_phrases: Array<string>;
    bullet_points: Array<string>;
    descriptions: Array<string>;
    listing_selections: ListingSelections | null;
    lifestyle_images: Array<ImageRead>;
    product_images: Array<ImageRead>;
    infographic_images: Array<ImageRead>;
};

/**
 * A job, as a list shows it.
 *
 * Attributes:
 * id: The job.
 * asin: The product.
 * marketplace_id: Amazon's marketplace id.
 * store_id: The store it ran against.
 * store_name: The store's name, for people. Empty when the store is not
 * in this tenant's registry.
 * marketplace_name: The marketplace's name, e.g. ``Amazon UAE`` — what
 * to show instead of ``marketplace_id``.
 * country_code: ISO 3166-1 alpha-2 of the marketplace.
 * product_name: As the listing names it, once fetched.
 * brand: Once known.
 * model_number: Once known.
 * status: Where it is.
 * progress_pct: 0-100.
 * created_at: When it started.
 * updated_at: When it last changed.
 */
export type JobSummary = {
    id: string;
    asin: string | null;
    marketplace_id: string | null;
    store_id: number;
    store_name: string;
    marketplace_name: string;
    country_code: string;
    product_name: string;
    brand: string;
    model_number: string;
    status: AiListingJobStatus;
    progress_pct: number;
    created_at: string;
    updated_at: string | null;
};

/**
 * This organization's background jobs.
 *
 * Attributes:
 * pending: Waiting to run, including retries and delayed jobs.
 * running: Claimed by a worker now.
 * failed_last_day: Given up on in the last 24 hours, every attempt
 * spent; they stay on the queue for a person.
 * oldest_pending_at: When the longest-waiting job was due. Far in the
 * past means no worker is draining the queue.
 */
export type JobsStatus = {
    pending: number;
    running: number;
    failed_last_day: number;
    oldest_pending_at: string | null;
};

/**
 * One key phrase and whether the seller kept it.
 *
 * Attributes:
 * phrase: The phrase.
 * selected: Kept.
 */
export type KeyPhraseSelection = {
    phrase: string;
    selected: boolean;
};

/**
 * Whether we can still call the store's account.
 */
export type KeysStatus = 'working' | 'failing' | 'unknown';

/**
 * Whether we can still call the store's account.
 */
export const KeysStatus = {
    WORKING: 'working',
    FAILING: 'failing',
    UNKNOWN: 'unknown'
} as const;

/**
 * How many warehouses are of one kind.
 */
export type KindCount = {
    kind: WarehouseKind;
    count: number;
};

/**
 * The list's kind tabs: every kind, with how many, and the total.
 *
 * Attributes:
 * items: One per kind, in the enum's order, including empty kinds.
 * total: All of them.
 */
export type KindCounts = {
    items: Array<KindCount>;
    total: number;
};

/**
 * A sync or backfill ingest started.
 *
 * Attributes:
 * run_id: Dagster's run or backfill id, or a direct sync's id.
 * mode: ``dagster`` or ``direct``.
 */
export type LaunchedRun = {
    run_id: string;
    mode?: string | null;
};

/**
 * What an action did at one warehouse.
 */
export type LegRead = {
    warehouse: PoWarehouseRead;
    po_created: boolean;
    stock_blocked: boolean;
    topped_up: boolean;
    email_sent: boolean;
    note: string | null;
};

/**
 * Optional body of ``POST /v1/<kind>/{id}/retire`` and ``/reinstate``.
 *
 * Optional as a whole, so a caller that sends no body keeps working.
 *
 * Attributes:
 * reason: Why, in words. Recorded on the event as ``reason``.
 */
export type LifecycleRequest = {
    reason?: string | null;
};

/**
 * One SKU on one PO.
 */
export type LineFulfilmentRead = {
    ordered: number;
    confirmed: number;
    blocked: number;
    allotted: number;
    invoiced: number;
    dispatched: number;
    delivered: number;
    open: number;
    value_ordered: string | null;
    value_invoiced: string | null;
    fill_rate: number | null;
    confirm_rate: number | null;
    delivery_rate: number | null;
    currency: string | null;
    po_id: number;
    po_code: string;
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    price: string | null;
};

/**
 * A new price for one line, or every line of one SKU.
 *
 * Attributes:
 * line_id: One line; or
 * sku_code: Every line of this SKU.
 * price: Per unit.
 * vat_rate: A percentage; unchanged when left out.
 */
export type LinePrice = {
    line_id?: number | null;
    sku_code?: string | null;
    price?: number | string | null;
    vat_rate?: number | string | null;
};

/**
 * One thing wrong with a submitted PO.
 *
 * Attributes:
 * row: The 1-based line number, or ``None`` when it is about the PO.
 * field: Which input it concerns, when one.
 * message: What is wrong, in a sentence a person can act on.
 */
export type LineProblem = {
    row: number | null;
    field: string | null;
    message: string;
};

/**
 * One PO line.
 */
export type LineRead = {
    line_number: number;
    asin: string | null;
    /**
     * Null when the store lists no SKU for the ASIN.
     */
    sku_id: number | null;
    sku_code: string | null;
    sku_name: string | null;
    quantity_ordered: number;
    quantity_confirmed: number;
    quantity_delivered: number;
    /**
     * Units no candidate warehouse has in free stock: a stock gap, not the amount left to confirm. Null when none.
     */
    uncovered: number | null;
    item_price: MoneyRead | null;
};

/**
 * Body of ``PUT /v1/purchase-orders/{id}/lines`` — the PO's whole line set.
 *
 * Attributes:
 * lines: Replaces every existing line; at least one.
 */
export type LinesWrite = {
    lines: Array<PurchaseOrderLineWrite>;
};

/**
 * Body of ``POST /v1/brands/{id}/link-sku-names``.
 *
 * Attributes:
 * names: SKU brand names, matched ignoring case and surrounding spaces.
 */
export type LinkSkuNames = {
    names: Array<string>;
};

/**
 * One store a change left worth a look.
 */
export type LinkWarning = {
    store_id: number;
    rule: LinkWarningRule;
};

/**
 * What a change to a warehouse's stores left worth a second look.
 *
 * ``no_warehouse_left``: an unlinked store has no warehouse at all, so its
 * stock push has nothing to read. ``marketplace_fulfilled_first``: the
 * marketplace's own network is now a store's first warehouse.
 * ``retired_warehouse``: a store still ships from this retired warehouse.
 */
export type LinkWarningRule = 'no_warehouse_left' | 'marketplace_fulfilled_first' | 'retired_warehouse';

/**
 * What a change to a warehouse's stores left worth a second look.
 *
 * ``no_warehouse_left``: an unlinked store has no warehouse at all, so its
 * stock push has nothing to read. ``marketplace_fulfilled_first``: the
 * marketplace's own network is now a store's first warehouse.
 * ``retired_warehouse``: a store still ships from this retired warehouse.
 */
export const LinkWarningRule = {
    NO_WAREHOUSE_LEFT: 'no_warehouse_left',
    MARKETPLACE_FULFILLED_FIRST: 'marketplace_fulfilled_first',
    RETIRED_WAREHOUSE: 'retired_warehouse'
} as const;

/**
 * What a link did.
 *
 * Attributes:
 * linked_sku_count: SKUs that now carry the brand.
 */
export type LinkedSkus = {
    linked_sku_count: number;
};

export type ListResourceAdGroupMasterRead = {
    items: Array<AdGroupMasterRead>;
    pagination: Pagination;
};

export type ListResourceAdminEvalQuestionRead = {
    items: Array<AdminEvalQuestionRead>;
    pagination: Pagination;
};

export type ListResourceAdminEvalRunRead = {
    items: Array<AdminEvalRunRead>;
    pagination: Pagination;
};

export type ListResourceAdminQuestionRead = {
    items: Array<AdminQuestionRead>;
    pagination: Pagination;
};

export type ListResourceAdminUsageRead = {
    items: Array<AdminUsageRead>;
    pagination: Pagination;
};

export type ListResourceAdsStoreRead = {
    items: Array<AdsStoreRead>;
    pagination: Pagination;
};

export type ListResourceAliasRead = {
    items: Array<AliasRead>;
    pagination: Pagination;
};

export type ListResourceAmazonPoRead = {
    items: Array<AmazonPoRead>;
    pagination: Pagination;
};

export type ListResourceBlockRead = {
    items: Array<BlockRead>;
    pagination: Pagination;
};

export type ListResourceBoxRead = {
    items: Array<BoxRead>;
    pagination: Pagination;
};

export type ListResourceBrandOption = {
    items: Array<VentawizLookupsSchemasBrandOption>;
    pagination: Pagination;
};

export type ListResourceBrandOverview = {
    items: Array<BrandOverview>;
    pagination: Pagination;
};

export type ListResourceBrandRead = {
    items: Array<BrandRead>;
    pagination: Pagination;
};

export type ListResourceCashflowDocumentRead = {
    items: Array<CashflowDocumentRead>;
    pagination: Pagination;
};

export type ListResourceCatalogueStoreRead = {
    items: Array<CatalogueStoreRead>;
    pagination: Pagination;
};

export type ListResourceCategoryOption = {
    items: Array<VentawizLookupsSchemasCategoryOption>;
    pagination: Pagination;
};

export type ListResourceChangeEvent = {
    items: Array<ChangeEvent>;
    pagination: Pagination;
};

export type ListResourceConversationRead = {
    items: Array<ConversationRead>;
    pagination: Pagination;
};

export type ListResourceCostRow = {
    items: Array<CostRow>;
    pagination: Pagination;
};

export type ListResourceCreditDebitNoteRead = {
    items: Array<CreditDebitNoteRead>;
    pagination: Pagination;
};

export type ListResourceDaypartingEntity = {
    items: Array<DaypartingEntity>;
    pagination: Pagination;
};

export type ListResourceDaypartingEventRead = {
    items: Array<DaypartingEventRead>;
    pagination: Pagination;
};

export type ListResourceDaypartingScheduleRead = {
    items: Array<DaypartingScheduleRead>;
    pagination: Pagination;
};

export type ListResourceFeeRow = {
    items: Array<FeeRow>;
    pagination: Pagination;
};

export type ListResourceFleetDeviceRead = {
    items: Array<FleetDeviceRead>;
    pagination: Pagination;
};

export type ListResourceFleetRunRead = {
    items: Array<FleetRunRead>;
    pagination: Pagination;
};

export type ListResourceFocusItem = {
    items: Array<FocusItem>;
    pagination: Pagination;
};

export type ListResourceGlobalShipmentRead = {
    items: Array<GlobalShipmentRead>;
    pagination: Pagination;
};

export type ListResourceInternalSkuRow = {
    items: Array<InternalSkuRow>;
    pagination: Pagination;
};

export type ListResourceJobSummary = {
    items: Array<JobSummary>;
    pagination: Pagination;
};

export type ListResourceListingRead = {
    items: Array<ListingRead>;
    pagination: Pagination;
};

export type ListResourceListingRow = {
    items: Array<ListingRow>;
    pagination: Pagination;
};

export type ListResourceMailExceptionRow = {
    items: Array<MailExceptionRow>;
    pagination: Pagination;
};

export type ListResourceManagedSkuCostRow = {
    items: Array<ManagedSkuCostRow>;
    pagination: Pagination;
};

export type ListResourceMarketingAutomationDecisionRead = {
    items: Array<MarketingAutomationDecisionRead>;
    pagination: Pagination;
};

export type ListResourceMarketingRuleAutomationRead = {
    items: Array<MarketingRuleAutomationRead>;
    pagination: Pagination;
};

export type ListResourceMarketingRuleExclusionRead = {
    items: Array<MarketingRuleExclusionRead>;
    pagination: Pagination;
};

export type ListResourceMarketingRuleExecutionSummary = {
    items: Array<MarketingRuleExecutionSummary>;
    pagination: Pagination;
};

export type ListResourceMarketingRuleRead = {
    items: Array<MarketingRuleRead>;
    pagination: Pagination;
};

export type ListResourceMarketplaceCatalogueItem = {
    items: Array<MarketplaceCatalogueItem>;
    pagination: Pagination;
};

export type ListResourceMarketplaceFeeRow = {
    items: Array<MarketplaceFeeRow>;
    pagination: Pagination;
};

export type ListResourceMarketplaceOption = {
    items: Array<VentawizLookupsSchemasMarketplaceOption>;
    pagination: Pagination;
};

export type ListResourceMarketplaceRead = {
    items: Array<MarketplaceRead>;
    pagination: Pagination;
};

export type ListResourceMcfOrderRead = {
    items: Array<McfOrderRead>;
    pagination: Pagination;
};

export type ListResourceMcfPreviewOrderRead = {
    items: Array<McfPreviewOrderRead>;
    pagination: Pagination;
};

export type ListResourceMcfRunRead = {
    items: Array<McfRunRead>;
    pagination: Pagination;
};

export type ListResourceMcfStoreRead = {
    items: Array<McfStoreRead>;
    pagination: Pagination;
};

export type ListResourceMemoryRead = {
    items: Array<MemoryRead>;
    pagination: Pagination;
};

export type ListResourceNegativeRead = {
    items: Array<NegativeRead>;
    pagination: Pagination;
};

export type ListResourceNoteRead = {
    items: Array<NoteRead>;
    pagination: Pagination;
};

export type ListResourceOrderBookRow = {
    items: Array<OrderBookRow>;
    pagination: Pagination;
};

export type ListResourceOrderLineRow = {
    items: Array<OrderLineRow>;
    pagination: Pagination;
};

export type ListResourceOrderRead = {
    items: Array<OrderRead>;
    pagination: Pagination;
};

export type ListResourceOrderRow = {
    items: Array<OrderRow>;
    pagination: Pagination;
};

export type ListResourceOrganizationRead = {
    items: Array<OrganizationRead>;
    pagination: Pagination;
};

export type ListResourcePoFulfilmentRead = {
    items: Array<PoFulfilmentRead>;
    pagination: Pagination;
};

export type ListResourcePricePushRunRead = {
    items: Array<PricePushRunRead>;
    pagination: Pagination;
};

export type ListResourcePricePushStoreRead = {
    items: Array<PricePushStoreRead>;
    pagination: Pagination;
};

export type ListResourceProductRow = {
    items: Array<ProductRow>;
    pagination: Pagination;
};

export type ListResourcePurchaseOrderRead = {
    items: Array<PurchaseOrderRead>;
    pagination: Pagination;
};

export type ListResourceReplenSkuRead = {
    items: Array<ReplenSkuRead>;
    pagination: Pagination;
};

export type ListResourceRetailerCostRow = {
    items: Array<RetailerCostRow>;
    pagination: Pagination;
};

export type ListResourceRetailerOption = {
    items: Array<VentawizLookupsSchemasRetailerOption>;
    pagination: Pagination;
};

export type ListResourceRetailerRead = {
    items: Array<RetailerRead>;
    pagination: Pagination;
};

export type ListResourceRetailerReceiptRead = {
    items: Array<RetailerReceiptRead>;
    pagination: Pagination;
};

export type ListResourceReturnRow = {
    items: Array<ReturnRow>;
    pagination: Pagination;
};

export type ListResourceReviewQueryItem = {
    items: Array<ReviewQueryItem>;
    pagination: Pagination;
};

export type ListResourceReviewableDocumentRead = {
    items: Array<ReviewableDocumentRead>;
    pagination: Pagination;
};

export type ListResourceSalesInvoiceRead = {
    items: Array<SalesInvoiceRead>;
    pagination: Pagination;
};

export type ListResourceSavedReportRead = {
    items: Array<SavedReportRead>;
    pagination: Pagination;
};

export type ListResourceScrapedReviewRow = {
    items: Array<ScrapedReviewRow>;
    pagination: Pagination;
};

export type ListResourceScrapedSearchRow = {
    items: Array<ScrapedSearchRow>;
    pagination: Pagination;
};

export type ListResourceSearchTermQueryItem = {
    items: Array<SearchTermQueryItem>;
    pagination: Pagination;
};

export type ListResourceSellerFlexRowRead = {
    items: Array<SellerFlexRowRead>;
    pagination: Pagination;
};

export type ListResourceServiceProviderRead = {
    items: Array<ServiceProviderRead>;
    pagination: Pagination;
};

export type ListResourceSettlementLineRow = {
    items: Array<SettlementLineRow>;
    pagination: Pagination;
};

export type ListResourceSettlementRow = {
    items: Array<SettlementRow>;
    pagination: Pagination;
};

export type ListResourceShopifySyncRunRead = {
    items: Array<ShopifySyncRunRead>;
    pagination: Pagination;
};

export type ListResourceShopifySyncStoreRead = {
    items: Array<ShopifySyncStoreRead>;
    pagination: Pagination;
};

export type ListResourceSkuAgeingRead = {
    items: Array<SkuAgeingRead>;
    pagination: Pagination;
};

export type ListResourceSkuCostRow = {
    items: Array<SkuCostRow>;
    pagination: Pagination;
};

export type ListResourceSkuRow = {
    items: Array<SkuRow>;
    pagination: Pagination;
};

export type ListResourceSpInvoiceRead = {
    items: Array<SpInvoiceRead>;
    pagination: Pagination;
};

export type ListResourceSpPaymentRead = {
    items: Array<SpPaymentRead>;
    pagination: Pagination;
};

export type ListResourceStockPushRunRead = {
    items: Array<StockPushRunRead>;
    pagination: Pagination;
};

export type ListResourceStockPushStoreRead = {
    items: Array<StockPushStoreRead>;
    pagination: Pagination;
};

export type ListResourceStockRead = {
    items: Array<StockRead>;
    pagination: Pagination;
};

export type ListResourceStoreGroupRead = {
    items: Array<StoreGroupRead>;
    pagination: Pagination;
};

export type ListResourceStoreOption = {
    items: Array<VentawizLookupsSchemasStoreOption>;
    pagination: Pagination;
};

export type ListResourceStoreOverview = {
    items: Array<StoreOverview>;
    pagination: Pagination;
};

export type ListResourceTicketCommentRead = {
    items: Array<TicketCommentRead>;
    pagination: Pagination;
};

export type ListResourceTicketExceptionRow = {
    items: Array<TicketExceptionRow>;
    pagination: Pagination;
};

export type ListResourceTicketFormBrand = {
    items: Array<TicketFormBrand>;
    pagination: Pagination;
};

export type ListResourceTicketFormMarketplace = {
    items: Array<TicketFormMarketplace>;
    pagination: Pagination;
};

export type ListResourceTicketFormStore = {
    items: Array<TicketFormStore>;
    pagination: Pagination;
};

export type ListResourceTicketFormWarehouse = {
    items: Array<TicketFormWarehouse>;
    pagination: Pagination;
};

export type ListResourceTicketListItem = {
    items: Array<TicketListItem>;
    pagination: Pagination;
};

export type ListResourceTicketTypeRead = {
    items: Array<TicketTypeRead>;
    pagination: Pagination;
};

export type ListResourceTikTokRunRead = {
    items: Array<TikTokRunRead>;
    pagination: Pagination;
};

export type ListResourceTikTokStoreRead = {
    items: Array<TikTokStoreRead>;
    pagination: Pagination;
};

export type ListResourceUnmatchedSkuName = {
    items: Array<UnmatchedSkuName>;
    pagination: Pagination;
};

export type ListResourceVendorInvoiceRead = {
    items: Array<VendorInvoiceRead>;
    pagination: Pagination;
};

export type ListResourceVendorOption = {
    items: Array<VendorOption>;
    pagination: Pagination;
};

export type ListResourceVendorOrderRead = {
    items: Array<VendorOrderRead>;
    pagination: Pagination;
};

export type ListResourceVendorOverview = {
    items: Array<VendorOverview>;
    pagination: Pagination;
};

export type ListResourceVendorPaymentRead = {
    items: Array<VendorPaymentRead>;
    pagination: Pagination;
};

export type ListResourceVendorRead = {
    items: Array<VendorRead>;
    pagination: Pagination;
};

export type ListResourceVerifiedAnswerRead = {
    items: Array<VerifiedAnswerRead>;
    pagination: Pagination;
};

export type ListResourceWarehouseOption = {
    items: Array<VentawizLookupsSchemasWarehouseOption>;
    pagination: Pagination;
};

export type ListResourceWarehouseOverview = {
    items: Array<WarehouseOverview>;
    pagination: Pagination;
};

export type ListResourceWarehouseRead = {
    items: Array<WarehouseRead>;
    pagination: Pagination;
};

export type ListResourceWarehouseReturnRow = {
    items: Array<WarehouseReturnRow>;
    pagination: Pagination;
};

/**
 * One product as a marketplace presents it in one store.
 *
 * Attributes:
 * id: ``marketplace_listed_product.pk_id``.
 * store_id: The store it is listed in.
 * marketplace_product_id: The marketplace's product id — an ASIN on
 * Amazon, a barcode on Trendyol.
 * marketplace_sku: Our seller SKU as listed on the marketplace.
 * sku: Our own catalogue SKU this listing sells.
 * gtin: Barcode, where the marketplace has one.
 * condition: Normalised to lowercase, e.g. ``new``.
 * listing_status: Normalised state, where the marketplace reports one.
 * title: As listed.
 * description: As listed.
 * is_fulfilled_by_marketplace: FBA and its equivalents. ``None`` where the
 * marketplace does not say.
 * manufacturer: From our catalogue.
 * category: From our catalogue.
 * subcategory: From our catalogue.
 * is_focused: Whether the listing is a focused SKU (it has a
 * ``focused_sku`` row).
 * drr: Daily run rate the team plans with, units a day. A decimal
 * serialised as a string, like money, so 0.10 stays 0.10.
 */
export type ListingRead = {
    id: number;
    store_id: number;
    marketplace_product_id: string | null;
    marketplace_sku: string | null;
    sku: string | null;
    gtin: string | null;
    condition: string | null;
    listing_status: string | null;
    title: string | null;
    description: string | null;
    is_fulfilled_by_marketplace: boolean | null;
    manufacturer: string | null;
    category: string | null;
    subcategory: string | null;
    is_focused?: boolean;
    drr?: string | null;
};

/**
 * One listing, VentaHub's Product Data columns.
 */
export type ListingRow = {
    id: number;
    product_key: string | null;
    sku_code: string | null;
    mp_sku_code: string | null;
    condition: string | null;
    title: string | null;
    manufacturer: string | null;
    category: string | null;
    subcategory: string | null;
    description: string | null;
    is_fba: boolean | null;
    focused_sku: boolean;
    drr: string | null;
    currency_code: string | null;
    regular_price: string | null;
    sale_price: string | null;
    /**
     * Not held by this system yet
     */
    sale_start_date: string | null;
    /**
     * Not held by this system yet
     */
    sale_end_date: string | null;
    min_price: string | null;
    max_price: string | null;
    /**
     * Not held by this system yet
     */
    back_margin: string | null;
    cost_multiple: string | null;
    rrp_multiple: string | null;
    mfn_shipping_price: string | null;
    vat_rate: string | null;
    /**
     * The listing's main image, where the marketplace gives one
     */
    image_url?: string | null;
    /**
     * The SKU's length, in cm
     */
    length_cm?: string | null;
    /**
     * The SKU's width, in cm
     */
    width_cm?: string | null;
    /**
     * The SKU's height, in cm
     */
    height_cm?: string | null;
    /**
     * The SKU's weight, in grams
     */
    weight_g?: string | null;
};

/**
 * What the seller chose to keep, stored as sent.
 *
 * Every field is optional so a client can save as it goes. The bounds are
 * there so a selection is a selection and not a place to park a document.
 *
 * Attributes:
 * key_phrases: Each phrase and whether it was kept.
 * bullet_points: The kept bullets, possibly edited.
 * descriptions: The kept descriptions, possibly edited.
 * selected_description_index: Which description is the one to use.
 * selected_images: Ids of the kept images.
 * field_selection: Per field, whether the original or the AI version
 * wins — e.g. ``{"title": "ai", "description": "original"}``.
 * backend_keywords: Search terms for the listing's backend.
 * all_bullet_points: Every bullet, as edited, kept or not.
 * all_descriptions: Every description, as edited, kept or not.
 */
export type ListingSelections = {
    key_phrases?: Array<KeyPhraseSelection>;
    bullet_points?: Array<string>;
    descriptions?: Array<string>;
    selected_description_index?: number | null;
    selected_images?: Array<string>;
    field_selection?: {
        [key: string]: string;
    } | null;
    backend_keywords?: Array<string>;
    all_bullet_points?: Array<string>;
    all_descriptions?: Array<string>;
};

/**
 * What listings can be grouped by.
 */
export type ListingsGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'listing' | 'fulfilment' | 'status' | 'product' | 'sku' | 'brand' | 'category' | 'subcategory';

/**
 * What listings can be grouped by.
 */
export const ListingsGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    LISTING: 'listing',
    FULFILMENT: 'fulfilment',
    STATUS: 'status',
    PRODUCT: 'product',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory'
} as const;

/**
 * Listings, grouped.
 */
export type ListingsReport = {
    items: Array<ListingsRow>;
    pagination: Pagination;
    totals: Array<ListingsTotals>;
    meta: ReportMeta;
};

/**
 * One group of listings.
 *
 * ``product_ref`` is filled when grouped by product as well as by listing.
 *
 * Attributes:
 * listing_status: Filled when grouped by listing or by status: the
 * marketplace's own status, such as ``active`` or ``draft``. Only
 * Shopify and TikTok report one; ``null`` elsewhere.
 * regular_price: Filled when grouped by listing: its live price.
 * sale_price: Its sale price.
 * price_currency_code: The currency both prices were written in, not
 * always the marketplace's.
 * priced_at: When the price was written.
 * listed_at: When the listing was created.
 * is_fulfilled_by_marketplace: Filled when grouped by fulfilment;
 * ``null`` is unknown, not false.
 */
export type ListingsRow = {
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    listing_count?: number | null;
    fulfilled_by_marketplace_count?: number | null;
    priced_count?: number | null;
    listing_status?: string | null;
    regular_price?: MoneyRead | null;
    sale_price?: MoneyRead | null;
    price_currency_code?: string | null;
    priced_at?: string | null;
    listed_at?: string | null;
    is_fulfilled_by_marketplace?: boolean | null;
};

/**
 * Every live listing, as it stands; a deleted one is not counted.
 *
 * Attributes:
 * listing_count: Listings.
 * fulfilled_by_marketplace_count: Listings the marketplace fulfils; a
 * listing whose fulfilment is unknown is not counted.
 * priced_count: Listings with a regular price.
 */
export type ListingsTotals = {
    listing_count?: number | null;
    fulfilled_by_marketplace_count?: number | null;
    priced_count?: number | null;
};

/**
 * What the list filters and the create form choose from.
 *
 * One response rather than two endpoints, because every screen that needs one
 * needs both. Short lists: a tenant has tens of warehouses and retailers.
 *
 * Attributes:
 * warehouses: Where POs ship from.
 * retailers: Who orders.
 */
export type LookupsRead = {
    warehouses: Array<NamedRead>;
    retailers: Array<NamedRead>;
};

/**
 * Why a row is a loss-maker.
 *
 * ``loss``: its profit is below zero. ``under_margin``: it made a profit,
 * but its markup is more than five points under its store's expected one.
 */
export type LossReason = 'loss' | 'under_margin';

/**
 * Why a row is a loss-maker.
 *
 * ``loss``: its profit is below zero. ``under_margin``: it made a profit,
 * but its markup is more than five points under its store's expected one.
 */
export const LossReason = {
    LOSS: 'loss',
    UNDER_MARGIN: 'under_margin'
} as const;

/**
 * One ad group kept off the missing ad groups mail, for one store.
 */
export type MailExceptionRow = {
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    brand_id: number | null;
    brand_name: string | null;
    ad_group_id: string;
    ad_group_name: string | null;
    reason: string | null;
    /**
     * The user who added it, as VentaHub recorded them; null when unknown
     */
    added_by_user_id: number | null;
    created_at: string;
};

/**
 * One SKU's current cost at a warehouse.
 */
export type ManagedSkuCostRow = {
    id: number;
    sku_code: string;
    sku_name: string | null;
    brand_name: string | null;
    warehouse_name: string | null;
    vendor_name: string | null;
    currency_code: string;
    purchase_cost: string | null;
    shipping_cost: string | null;
    additional_cost: string | null;
    vendor_vat_rate: string | null;
    vendor_rrp: string | null;
    status: string | null;
    valid_from: string | null;
    valid_to: string | null;
};

/**
 * A box that could be billed onto the invoice.
 */
export type MappableBoxRead = {
    box_id: number;
    box_code: string;
    status: string | null;
};

/**
 * One suggestion the engine decided without a reviewer.
 *
 * Attributes:
 * ticket_number: The T22 it sits on.
 * decided_at: When the ticket was completed (or raised).
 * store_id: The row's store.
 * store_name: Its name.
 * ad_group_id: The row's Amazon ad group.
 * ad_group_name: Its name.
 * rule_code: The rule.
 * action_type: The plan.
 * mode: ``LIVE`` or ``SHADOW`` (an automation config), or ``BAND`` (the
 * ±band auto-accept).
 * reflected_on_ams: Whether it reached Amazon.
 * execution_status: ``EXECUTED``, ``FAILED`` or ``NOT_EXECUTED``.
 * error_message: Why not.
 * row_key: The row.
 */
export type MarketingAutomationDecisionRead = {
    ticket_number: number;
    decided_at: string;
    store_id: number | null;
    store_name: string | null;
    ad_group_id: string | null;
    ad_group_name: string | null;
    rule_code: string | null;
    action_type: string | null;
    mode: string;
    reflected_on_ams: boolean | null;
    execution_status: string | null;
    error_message: string | null;
    row_key: string | null;
};

/**
 * The engine's master switches.
 *
 * Attributes:
 * dry_run_only: The engine refuses to raise tickets or call Amazon.
 * reflect_on_ams: Accepted actions are written to the AMS view.
 * auto_accept_change_bid_enabled: Small bid changes are accepted with no
 * reviewer.
 * auto_accept_change_bid_pct: How small, in percent. A string, like
 * money: it is a decimal and a JSON number is a double.
 * updated_at: When a switch last changed. Null while every switch is
 * at its default.
 */
export type MarketingEngineConfigRead = {
    dry_run_only: boolean;
    reflect_on_ams: boolean;
    auto_accept_change_bid_enabled: boolean;
    auto_accept_change_bid_pct: string;
    updated_at: string | null;
};

/**
 * Body of ``PATCH /v1/marketing/engine-config``. Every field optional.
 *
 * Attributes:
 * dry_run_only: See :class:`MarketingEngineConfigRead`.
 * reflect_on_ams: As above.
 * auto_accept_change_bid_enabled: As above.
 * auto_accept_change_bid_pct: As above. Greater than 0, at most 50 — a
 * wider band is not "small changes" and should go through review.
 */
export type MarketingEngineConfigUpdate = {
    dry_run_only?: boolean | null;
    reflect_on_ams?: boolean | null;
    auto_accept_change_bid_enabled?: boolean | null;
    auto_accept_change_bid_pct?: number | string | null;
};

/**
 * Whether one learning job is in place and when it last ran.
 *
 * Attributes:
 * task: The job type.
 * purpose: What it moves.
 * registered: Whether a handler exists for it.
 * enabled: Whether the worker schedules it.
 * schedule: How often.
 * last_run: When it last finished for this organization; null if never.
 */
export type MarketingLearningJob = {
    task: string;
    purpose: string;
    registered: boolean;
    enabled: boolean;
    schedule: string;
    last_run: string | null;
};

/**
 * One rule's current learning state, as the T22 ticket reads it.
 *
 * Attributes:
 * coefficient: The rule's priority.
 * freeze_coefficient: Learning moves no further.
 * is_master: A master rule.
 * coefficient_explanation: VentaHub's plain-language reading.
 * freq_accepted: Lifetime accepts.
 * freq_shortlisted: Lifetime decisions.
 */
export type MarketingLiveCoefficient = {
    coefficient: number;
    freeze_coefficient: boolean;
    is_master: boolean;
    coefficient_explanation: string;
    freq_accepted: number;
    freq_shortlisted: number;
};

/**
 * Keyed by rule code; a rule with no state is absent.
 *
 * Attributes:
 * rules: ``{rule code: state}``.
 */
export type MarketingLiveCoefficients = {
    rules: {
        [key: string]: MarketingLiveCoefficient;
    };
};

/**
 * Body of ``POST /v1/marketing/live-coefficients``.
 *
 * Attributes:
 * rule_codes: The rules on a ticket.
 */
export type MarketingLiveCoefficientsRequest = {
    rule_codes: Array<string>;
};

/**
 * Body of ``POST /v1/marketing/rule-automations``.
 *
 * Sets the mode for these rules on this target. A rule already set to a
 * different mode on the same target moves to this one — it is never in two.
 *
 * Attributes:
 * scope: Whole profile, or one ad group.
 * mode: ``LIVE``, ``SHADOW`` or ``OFF``.
 * profile_id: Required for ``PROFILE``, and numeric — it is an Amazon
 * Ads profile id.
 * ad_group_id: Required for ``AD_GROUP``.
 * rule_codes: At least one. Case-insensitive.
 * reason: Why.
 * expires_at: When it lapses back to review.
 */
export type MarketingRuleAutomationCreate = {
    scope: MarketingRuleScope;
    profile_id?: string | null;
    ad_group_id?: string | null;
    reason?: string | null;
    expires_at?: string | null;
    mode: MarketingRuleAutomationMode;
    rule_codes: Array<string>;
};

/**
 * How far the engine may act on a rule's suggestion without a reviewer.
 *
 * ``LIVE`` accepts and pushes to Amazon. ``SHADOW`` only records what it would
 * have done, which is how a rule earns ``LIVE``. ``OFF`` pins a rule to human
 * review even where a broader config would automate it.
 */
export type MarketingRuleAutomationMode = 'LIVE' | 'SHADOW' | 'OFF';

/**
 * How far the engine may act on a rule's suggestion without a reviewer.
 *
 * ``LIVE`` accepts and pushes to Amazon. ``SHADOW`` only records what it would
 * have done, which is how a rule earns ``LIVE``. ``OFF`` pins a rule to human
 * review even where a broader config would automate it.
 */
export const MarketingRuleAutomationMode = {
    LIVE: 'LIVE',
    SHADOW: 'SHADOW',
    OFF: 'OFF'
} as const;

/**
 * An automation config.
 *
 * Attributes:
 * id: ULID.
 * scope: Whole profile, or one ad group.
 * mode: ``LIVE``, ``SHADOW`` or ``OFF``.
 * profile_id: The profile, for ``PROFILE``.
 * ad_group_id: The ad group, for ``AD_GROUP``.
 * rule_codes: The rules in this mode.
 * reason: Why.
 * expires_at: When it lapses.
 * is_expired: Whether it has.
 * created_by: Who last set it.
 * ad_group_name: The ad group's name, when it resolves.
 * store_name: The profile's store, when it resolves.
 */
export type MarketingRuleAutomationRead = {
    id: string;
    created_at: string;
    updated_at: string;
    scope: MarketingRuleScope;
    mode: MarketingRuleAutomationMode;
    profile_id: string | null;
    ad_group_id: string | null;
    rule_codes: Array<string>;
    reason: string | null;
    expires_at: string | null;
    is_expired: boolean;
    created_by: string | null;
    ad_group_name?: string | null;
    store_name?: string | null;
};

/**
 * How a proposed bid was computed, step by step.
 *
 * Attributes:
 * formula: The formula, as written.
 * inputs: Its inputs by name.
 * raw_bid: The formula's result before clamping.
 * min_bid: The marketplace minimum.
 * clamped: Whether the minimum was applied.
 * current_bid: The live bid.
 * fallback_applied: Whether a no-op nudge replaced an unchanged bid.
 * fallback_multiplier: The nudge.
 * final_bid: What is proposed.
 * steps: The calculation, one line per step.
 */
export type MarketingRuleBidTrace = {
    formula?: string | null;
    inputs?: {
        [key: string]: number | null;
    };
    raw_bid?: number | null;
    min_bid?: number | null;
    clamped?: boolean;
    current_bid?: number | null;
    fallback_applied?: boolean;
    fallback_multiplier?: number | null;
    final_bid?: number | null;
    steps?: Array<string>;
};

/**
 * A column a reviewer sees on a matched row.
 *
 * A list of these rather than a ``{label: column}`` map, because the order is
 * the point — a reviewer reads left to right — and JSON object order is not a
 * promise every client keeps.
 *
 * Attributes:
 * label: Column heading.
 * column: Key into the row's data.
 */
export type MarketingRuleDisplayColumn = {
    label: string;
    column: string;
};

/**
 * Body of ``POST /v1/marketing/rule-exclusions``.
 *
 * Attributes:
 * scope: Whole profile, or one ad group.
 * profile_id: Required for ``PROFILE``.
 * ad_group_id: Required for ``AD_GROUP``.
 * rule_codes: The rules to block. Omitted or empty blocks every rule.
 * Case-insensitive; stored in the catalogue's spelling.
 * reason: Why.
 * expires_at: When it stops applying. Omitted means never.
 */
export type MarketingRuleExclusionCreate = {
    scope: MarketingRuleScope;
    profile_id?: string | null;
    ad_group_id?: string | null;
    reason?: string | null;
    expires_at?: string | null;
    rule_codes?: Array<string> | null;
};

/**
 * An exclusion.
 *
 * Attributes:
 * id: ULID.
 * scope: Whole profile, or one ad group.
 * profile_id: The profile, for ``PROFILE``.
 * ad_group_id: The ad group, for ``AD_GROUP``.
 * rule_codes: The rules blocked. Null means every rule.
 * reason: Why.
 * expires_at: When it stops applying.
 * is_expired: Whether it has. Computed here rather than by each client
 * against its own clock.
 * created_by: Who added it.
 */
export type MarketingRuleExclusionRead = {
    id: string;
    created_at: string;
    updated_at: string;
    scope: MarketingRuleScope;
    profile_id: string | null;
    ad_group_id: string | null;
    rule_codes: Array<string> | null;
    reason: string | null;
    expires_at: string | null;
    is_expired: boolean;
    created_by: string | null;
};

/**
 * Body of ``POST /v1/marketing/execute``.
 *
 * Attributes:
 * rule_codes: Only these rules; omitted runs every active rule. A rule
 * named here runs even if inactive. Case-insensitive.
 * dry_run: Raise no tickets. The engine's master switch can force this
 * on whatever is sent.
 * store_ids: Keep only these stores' rows. A store-scoped run is always
 * a dry run: it exists to validate a rule change.
 */
export type MarketingRuleExecuteRequest = {
    rule_codes?: Array<string> | null;
    dry_run?: boolean;
    store_ids?: Array<number> | null;
};

/**
 * What a run did, with every matched row.
 *
 * Attributes:
 * execution_id: The run's ULID.
 * rows_evaluated: Rows the rules' queries returned, after processing.
 * rows_matched: Rows left with at least one suggestion.
 * actions_suggested: Suggestions across those rows.
 * duration_ms: Wall time.
 * dry_run: Whether it was barred from raising tickets — after the
 * master switch.
 * master_dry_run: Whether the master switch was on.
 * tickets_created: T22 tickets raised.
 * output_filename: The name the result downloads as.
 * rows: The matched rows.
 * diagnostics: Per-rule notes.
 */
export type MarketingRuleExecuteResult = {
    execution_id: string;
    rows_evaluated: number;
    rows_matched: number;
    actions_suggested: number;
    duration_ms: number;
    dry_run: boolean;
    master_dry_run: boolean;
    tickets_created: number;
    output_filename: string;
    rows: Array<MarketingRuleMatchedRow>;
    diagnostics: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * A run's result as VentaHub's ``execution_<id>.json``.
 *
 * Attributes:
 * execution_id: The run.
 * dry_run: As run.
 * generated_at: When it finished.
 * rows_evaluated: As VentaHub's file counted it — suggestions.
 * rows_matched: Matched rows.
 * actions_suggested: Suggestions.
 * rows: The matched rows, as stored.
 * diagnostics: Per-rule notes.
 * metadata: Rules run, exclusions in force, master switch, store scope.
 */
export type MarketingRuleExecutionFile = {
    execution_id: string;
    dry_run: boolean;
    generated_at: string;
    rows_evaluated: number;
    rows_matched: number;
    actions_suggested: number;
    rows: Array<{
        [key: string]: unknown;
    }>;
    diagnostics: Array<{
        [key: string]: unknown;
    }>;
    metadata: {
        [key: string]: unknown;
    };
};

/**
 * What started an execution.
 */
export type MarketingRuleExecutionMode = 'manual' | 'test' | 'scheduled';

/**
 * What started an execution.
 */
export const MarketingRuleExecutionMode = {
    MANUAL: 'manual',
    TEST: 'test',
    SCHEDULED: 'scheduled'
} as const;

/**
 * One run, with one page of its matched rows.
 *
 * Attributes:
 * rows: The requested page of matched rows, in the engine's order, after
 * the rule and search filters.
 * rows_pagination: The pager over the filtered rows.
 * rule_counts: Suggestions per rule over every row of the run the caller
 * reaches, unfiltered — the page's rule chips.
 * diagnostics: Per-rule notes — errors, validation warnings, skips.
 * Empty for a scoped caller, and the two below null: they count
 * the whole organization's rows.
 * errors_json: ``{"items": [...]}`` — the diagnostics that are errors.
 * warnings_json: ``{"items": [...]}`` — the failed validation reports.
 */
export type MarketingRuleExecutionRead = {
    id: string;
    created_at: string;
    updated_at: string;
    execution_mode: MarketingRuleExecutionMode;
    dry_run: boolean;
    rule_codes: Array<string>;
    store_ids: Array<number>;
    rows_evaluated: number | null;
    rows_matched: number;
    actions_suggested: number;
    duration_ms: number | null;
    executed_by: string | null;
    output_filename?: string | null;
    scope?: MarketingRuleExecutionScope;
    rows: Array<MarketingRuleMatchedRow>;
    rows_pagination: Pagination;
    rule_counts?: {
        [key: string]: number;
    };
    diagnostics: Array<{
        [key: string]: unknown;
    }>;
    errors_json?: {
        [key: string]: unknown;
    } | null;
    warnings_json?: {
        [key: string]: unknown;
    } | null;
};

/**
 * Whose view of a run this is.
 *
 * Attributes:
 * restricted: The caller reaches only some stores' rows. The counts are
 * then of those rows, and ``rows_evaluated`` - the rule queries' rows
 * before matching, which are not kept per store - is null.
 */
export type MarketingRuleExecutionScope = {
    restricted?: boolean;
};

/**
 * One run, without its result.
 *
 * Attributes:
 * id: ULID.
 * created_at: When it started.
 * updated_at: When it last changed — i.e. finished.
 * execution_mode: What started it.
 * dry_run: Whether it was barred from acting.
 * rule_codes: Which rules ran. Empty means the active catalogue.
 * store_ids: Which stores it was limited to. Empty means all.
 * rows_evaluated: Rows the queries returned. Null for a scoped caller.
 * rows_matched: Rows at least one rule matched, of those the caller reaches.
 * actions_suggested: Suggestions across those rows.
 * duration_ms: Wall time. Null if it never finished.
 * executed_by: Who started it. Null for the scheduler.
 * output_filename: The name its result downloads as. Null if it never
 * finished.
 * scope: Whether the counts are narrowed to the caller's stores.
 */
export type MarketingRuleExecutionSummary = {
    id: string;
    created_at: string;
    updated_at: string;
    execution_mode: MarketingRuleExecutionMode;
    dry_run: boolean;
    rule_codes: Array<string>;
    store_ids: Array<number>;
    rows_evaluated: number | null;
    rows_matched: number;
    actions_suggested: number;
    duration_ms: number | null;
    executed_by: string | null;
    output_filename?: string | null;
    scope?: MarketingRuleExecutionScope;
};

/**
 * One of the verdict's four requirements.
 *
 * Attributes:
 * name: ``reviewer consensus``, ``independent evidence``,
 * ``measured outcome`` or ``stable``.
 * passed: Whether the evidence meets it.
 * detail: The numbers behind it.
 */
export type MarketingRuleGate = {
    name: string;
    passed: boolean;
    detail: string;
};

/**
 * One rule's evidence.
 *
 * Attributes:
 * rule_code: The code.
 * rule_name: Its class name, or null for a code that has left the
 * catalogue but still has learned state.
 * in_catalogue: Whether the catalogue still has it.
 * has_run: Whether it has a state row at all.
 * is_active: Whether registry-wide runs include it.
 * is_master: Pinned by an operator.
 * freeze_coefficient: Learning does not move it.
 * coefficient: Its priority, or null if it has never run.
 * shortlisted: Suggestions reviewers were shown.
 * accepted: Of those, accepted.
 * accept_rate: ``accepted / shortlisted``, undecayed.
 * decayed_weight: Effective decisions after decay.
 * decayed_accept_rate: The posterior mean.
 * decayed_interval_low: 90% interval, low.
 * decayed_interval_high: 90% interval, high.
 * execution_count: Runs of any kind.
 * real_run_count: Runs that could act.
 * last_executed_at: When it last ran.
 * human_accept_rate: Accept rate over people's decisions only.
 * human_decisions: People's decisions.
 * human_interval_low: 90% credible interval of that rate, low.
 * human_interval_high: Likewise, high.
 * recent_accept_rate: People's accept rate over the last 14 days.
 * recent_decisions: Their decisions in that window.
 * self_accepted_actions: Accepts the engine made on its own judgement.
 * self_accepted_share: Their share of all accepts.
 * outcome_samples: Scored outcomes.
 * outcome_mean: Their mean score.
 * outcome_interval_low: 95% confidence interval of the mean, low.
 * outcome_interval_high: Likewise, high.
 * verdict: Should it act without a reviewer.
 */
export type MarketingRuleHealth = {
    rule_code: string;
    rule_name: string | null;
    in_catalogue: boolean;
    has_run: boolean;
    is_active: boolean;
    is_master: boolean;
    freeze_coefficient: boolean;
    coefficient: number | null;
    shortlisted: number;
    accepted: number;
    accept_rate: number | null;
    decayed_weight: number;
    decayed_accept_rate: number | null;
    decayed_interval_low: number | null;
    decayed_interval_high: number | null;
    execution_count: number;
    real_run_count: number;
    last_executed_at: string | null;
    human_accept_rate?: number | null;
    human_decisions?: number;
    human_interval_low?: number | null;
    human_interval_high?: number | null;
    recent_accept_rate?: number | null;
    recent_decisions?: number;
    self_accepted_actions?: number;
    self_accepted_share?: number | null;
    outcome_samples?: number;
    outcome_mean?: number | null;
    outcome_interval_low?: number | null;
    outcome_interval_high?: number | null;
    verdict?: MarketingRuleVerdict | null;
};

/**
 * A row at least one rule matched, with every suggestion on it.
 *
 * Attributes:
 * row_key: Identifies the row, e.g. ``ag_id=1|target_id=2``.
 * unique_key_columns: The columns ``row_key`` is built from.
 * row_data_summary: The row's values. Keys beginning ``_`` are engine
 * internals.
 * actions: The suggestions.
 * system_accept: Set when the engine decided the row itself.
 */
export type MarketingRuleMatchedRow = {
    row_key: string;
    unique_key_columns?: Array<string>;
    row_data_summary?: {
        [key: string]: unknown;
    };
    actions?: Array<MarketingRuleSuggestion>;
    system_accept?: {
        [key: string]: unknown;
    } | null;
};

/**
 * Two rules decided on the same rows by people.
 *
 * Attributes:
 * rule_a: The first rule.
 * rule_b: The second.
 * co_decided: Rows both were decided on.
 * both_accepted_pct: Rows both were accepted.
 * disagreement_pct: Rows one was accepted and the other not.
 * rule_a_accept_pct: How often the first was accepted there.
 * rule_b_accept_pct: How often the second was.
 */
export type MarketingRulePair = {
    rule_a: string;
    rule_b: string;
    co_decided: number;
    both_accepted_pct: number;
    disagreement_pct: number;
    rule_a_accept_pct: number;
    rule_b_accept_pct: number;
};

/**
 * One rule from the catalogue.
 *
 * Attributes:
 * rule_code: The catalogue code. Case-sensitive.
 * rule_name: The implementing class's name.
 * primary_dataset: The table its query is built on.
 * expression_english: What it does, as a reviewer reads it.
 * unique_key_columns: What identifies one matched row.
 * display_columns: What a reviewer sees, in order.
 * lookback_days: How much history it reads.
 * default_cron: Its default schedule.
 * supports_ad_group_scope: Whether an ad-group exclusion or automation
 * can address it.
 * emits_amazon_action: False for rules that only ask a human to check
 * something.
 */
export type MarketingRuleRead = {
    rule_code: string;
    rule_name: string;
    primary_dataset: string;
    expression_english: string;
    unique_key_columns: Array<string>;
    display_columns: Array<MarketingRuleDisplayColumn>;
    lookback_days: number;
    default_cron: string | null;
    supports_ad_group_scope: boolean;
    emits_amazon_action: boolean;
};

/**
 * What an exclusion or an automation covers.
 */
export type MarketingRuleScope = 'PROFILE' | 'AD_GROUP';

/**
 * What an exclusion or an automation covers.
 */
export const MarketingRuleScope = {
    PROFILE: 'PROFILE',
    AD_GROUP: 'AD_GROUP'
} as const;

/**
 * What the engine has learned about one rule, for this organization.
 *
 * Attributes:
 * rule_code: The catalogue code.
 * coefficient: The priority its suggestions are ranked by.
 * alpha_decayed: Decayed acceptances, plus the prior.
 * beta_decayed: Decayed rejections, plus the prior.
 * freq_shortlisted: Suggestions a reviewer was shown.
 * freq_accepted: Of those, how many were accepted.
 * execution_count: Runs of any kind.
 * real_run_count: Runs that could raise tickets.
 * last_executed_at: When it last ran.
 * next_run_at: When it next runs.
 * is_active: Whether registry-wide runs include it.
 * is_master: Pinned by an operator.
 * freeze_coefficient: Learning does not move the coefficient.
 * cron_schedule: Its schedule, when overriding the catalogue's.
 */
export type MarketingRuleStateRead = {
    rule_code: string;
    coefficient: number;
    alpha_decayed: number;
    beta_decayed: number;
    freq_shortlisted: number;
    freq_accepted: number;
    execution_count: number;
    real_run_count: number;
    last_executed_at: string | null;
    next_run_at: string | null;
    is_active: boolean;
    is_master: boolean;
    freeze_coefficient: boolean;
    cron_schedule: string | null;
};

/**
 * One rule's suggestion on one matched row.
 *
 * Attributes:
 * rule_code: Which rule.
 * rule_name: Its class name.
 * action_type: What it proposes, e.g. ``CHANGE_BID``.
 * action_payload: The proposal's parameters.
 * execution_trace: Why it matched, one line per step.
 * display_columns: ``{label: column}`` for this rule's view of the row.
 * expression_english: The rule's description.
 * context_columns: Extra ``{label: column}`` shown beside every rule.
 * context_data: Values for those.
 * bid_trace: How a proposed bid was computed, for bid actions.
 * automation: What an automation config decided or, in shadow or a dry
 * run, would have decided.
 * system_accepted: The engine accepted it itself (automation or band).
 * system_accept: How.
 * action_taken: ``accept`` once decided.
 * accepted_by: Who accepted it.
 * reflected_on_ams: Whether it reached Amazon.
 * execution_result: What became of the push.
 * memory_would: What raise memory would drop on a real run.
 * raised_before: How often and when it was raised before.
 * reraise_reason: Why it is back.
 */
export type MarketingRuleSuggestion = {
    rule_code: string;
    rule_name: string;
    action_type: string;
    action_payload?: {
        [key: string]: unknown;
    };
    execution_trace?: Array<string>;
    display_columns?: {
        [key: string]: string;
    };
    expression_english?: string;
    context_columns?: {
        [key: string]: string;
    };
    context_data?: {
        [key: string]: unknown;
    };
    bid_trace?: MarketingRuleBidTrace | null;
    automation?: {
        [key: string]: unknown;
    } | null;
    system_accepted?: boolean | null;
    system_accept?: {
        [key: string]: unknown;
    } | null;
    action_taken?: string | null;
    accepted_by?: string | null;
    reflected_on_ams?: boolean | null;
    execution_result?: {
        [key: string]: unknown;
    } | null;
    memory_would?: string | null;
    raised_before?: {
        [key: string]: unknown;
    } | null;
    reraise_reason?: string | null;
};

/**
 * Body of ``POST /v1/marketing/rules/{rule_code}/test``.
 *
 * Attributes:
 * sample_size: How many proposed actions to return.
 */
export type MarketingRuleTestRequest = {
    sample_size?: number;
};

/**
 * What a one-rule dry run found (VentaHub ``TestRuleResponse``).
 *
 * Attributes:
 * rule_code: The rule.
 * execution_id: The run, in the history like any other.
 * rows_evaluated: Rows its query returned.
 * rows_matched: Rows it matched.
 * match_rate: ``rows_matched / rows_evaluated``.
 * sample_actions: Up to ``sample_size`` proposals.
 * diagnostics: The run's notes.
 */
export type MarketingRuleTestResult = {
    rule_code: string;
    execution_id: string;
    rows_evaluated: number;
    rows_matched: number;
    match_rate: number;
    sample_actions: Array<MarketingRuleTestSample>;
    diagnostics: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * One proposed action of a test run.
 *
 * Attributes:
 * row_key: The row.
 * action_type: The plan.
 * action_payload: Its parameters.
 * display_columns: The rule's ``{label: column}`` view.
 */
export type MarketingRuleTestSample = {
    row_key: string;
    action_type: string | null;
    action_payload: {
        [key: string]: unknown;
    } | null;
    display_columns: {
        [key: string]: string;
    } | null;
};

/**
 * Should this rule act without a reviewer (VentaHub ``automation_verdict``).
 *
 * Attributes:
 * recommendation: ``automate``, ``suppress`` or ``review``.
 * confidence: The posterior confidence behind it.
 * gates: The four requirements.
 * rationale: One sentence naming what is missing.
 */
export type MarketingRuleVerdict = {
    recommendation: string;
    confidence: number;
    gates: Array<MarketingRuleGate>;
    rationale: string;
};

/**
 * A snapshot of the engine's learning state.
 *
 * Attributes:
 * generated_at: When this was computed.
 * loop: The engine as a whole.
 * rules: One entry per catalogue rule and per retired rule with state,
 * catalogue order first.
 * pairs: Rules people decide on the same rows, most disagreement first.
 */
export type MarketingRulesHealthRead = {
    generated_at: string;
    loop: MarketingRulesLoopHealth;
    rules: Array<MarketingRuleHealth>;
    pairs?: Array<MarketingRulePair>;
};

/**
 * The engine as a whole.
 *
 * Attributes:
 * dry_run_only: The master switch.
 * reflect_on_ams: As configured.
 * auto_accept_enabled: Whether the engine accepts its own bid changes.
 * auto_accept_pct: The band, as a decimal string.
 * rules_in_catalogue: Rules the catalogue defines.
 * rules_with_state: Rules that have run at least once.
 * rules_active: Of those, how many registry-wide runs include.
 * suggestions_reviewed: Suggestions reviewers were shown, all rules.
 * suggestions_accepted: Of those, accepted.
 * executions_last_30_days: Runs started in the last 30 days.
 * live_executions_last_30_days: Of those, runs that could act.
 * last_execution_at: When the engine last started a run.
 * decided_suggestions: Suggestions a person decided.
 * undecided_suggestions: Suggestions nobody has looked at yet.
 * outcomes_scored: Accepted actions scored.
 * outcomes_pending: Waiting to be scored.
 * self_accepted_actions: Accepts the engine made on its own judgement.
 * automated_actions: Accepts an automation config made.
 * cooccurrence_pairs: Rule pairs learned.
 * jobs: The learning jobs and when they last ran.
 */
export type MarketingRulesLoopHealth = {
    dry_run_only: boolean;
    reflect_on_ams: boolean;
    auto_accept_enabled: boolean;
    auto_accept_pct: string;
    rules_in_catalogue: number;
    rules_with_state: number;
    rules_active: number;
    suggestions_reviewed: number;
    suggestions_accepted: number;
    executions_last_30_days: number;
    live_executions_last_30_days: number;
    last_execution_at: string | null;
    decided_suggestions?: number;
    undecided_suggestions?: number;
    outcomes_scored?: number;
    outcomes_pending?: number;
    self_accepted_actions?: number;
    automated_actions?: number;
    cooccurrence_pairs?: number;
    jobs?: Array<MarketingLearningJob>;
};

/**
 * The shadow report.
 *
 * Attributes:
 * items: One row per (rule, ad group), most-watched first.
 * tickets_scanned: T22s with a shadow annotation in the window.
 * since_days: The window.
 */
export type MarketingShadowReportRead = {
    items: Array<MarketingShadowRowRead>;
    tickets_scanned: number;
    since_days: number;
};

/**
 * Shadow mode on one (rule, ad group), against what reviewers chose.
 *
 * Attributes:
 * rule_code: The rule.
 * ad_group_id: The ad group.
 * ad_group_name: Its name.
 * store_id: The store.
 * store_name: Its name.
 * would_accept: Suggestions shadow said it would accept.
 * agreed: Of those, accepted by a reviewer.
 * disagreed: Declined by a reviewer.
 * pending: Not decided yet.
 * agreement_pct: ``agreed / (agreed + disagreed)``; null while nobody decided.
 * first_seen: The first such ticket.
 * last_seen: The latest.
 */
export type MarketingShadowRowRead = {
    rule_code: string;
    ad_group_id: string | null;
    ad_group_name: string | null;
    store_id: number | null;
    store_name: string | null;
    would_accept: number;
    agreed: number;
    disagreed: number;
    pending: number;
    agreement_pct: number | null;
    first_seen: string | null;
    last_seen: string | null;
};

/**
 * Body of ``POST /v1/marketing/stats/explain``: the snapshot the page shows.
 *
 * Posted back rather than re-queried, so the prose can never describe numbers
 * the reader cannot see.
 *
 * Attributes:
 * stats: The health snapshot.
 */
export type MarketingStatsExplainRequest = {
    stats: {
        [key: string]: unknown;
    };
};

/**
 * The narrative and its provenance; on failure, an empty narrative and why.
 *
 * Attributes:
 * narrative: The prose.
 * provider: Which provider wrote it.
 * model: Which model.
 * error: Why there is none.
 */
export type MarketingStatsExplanation = {
    narrative?: string;
    provider?: string | null;
    model?: string | null;
    error?: string | null;
};

/**
 * A marketplace as the catalogue lists it, with this organization's view of it.
 *
 * Attributes:
 * family: The channel family, ``left(short_code, 2)`` - what the store
 * table's ``family`` filter takes too.
 * platform: The platform's name (``amazon``, ``walmart``); ``other`` for
 * a family no connector exists for.
 * store_count: This organization's stores on it, retired ones included,
 * deleted ones not.
 * active_store_count: Of those, the active ones.
 * tags: This organization's tags on it, alphabetically.
 */
export type MarketplaceCatalogueItem = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    short_code: string;
    country: string;
    currency: string;
    language_code: string;
    timezone: string;
    vat_rate: string;
    family: string;
    platform: string;
    store_count: number;
    active_store_count: number;
    tags: Array<string>;
};

/**
 * Body of ``POST /v1/marketplaces``.
 *
 * Every field a ``com_marketplace`` row cannot exist without is required.
 *
 * Attributes:
 * short_code: Upper case, at least four characters, unique.
 * country: ISO 3166-1 alpha-2, e.g. ``DE``.
 * currency: ISO 4217, e.g. ``EUR``.
 * language_code: e.g. ``de``.
 * timezone: An IANA zone, e.g. ``Europe/Berlin``.
 * vat_rate: A fraction between 0 and 1.
 */
export type MarketplaceCreate = {
    code: string;
    name: string;
    short_code: string;
    country: string;
    currency: string;
    language_code: string;
    timezone: string;
    vat_rate: number | string;
};

/**
 * Every channel family with a marketplace, most marketplaces first.
 *
 * Attributes:
 * items: One per family.
 * total: Marketplaces across every family - the "All" tab.
 * mine_total: Of those, the ones this organization has a store on.
 */
export type MarketplaceFamilies = {
    items: Array<MarketplaceFamily>;
    total: number;
    mine_total: number;
};

/**
 * One platform tab of the catalogue.
 *
 * Attributes:
 * family: The channel family.
 * platform: The platform's name; ``other`` without a connector.
 * count: Marketplaces in the family.
 * mine_count: Of those, the ones this organization has a store on.
 */
export type MarketplaceFamily = {
    family: string;
    platform: string;
    count: number;
    mine_count: number;
};

/**
 * One listing's current fee estimate.
 */
export type MarketplaceFeeRow = {
    id: number;
    /**
     * ASIN, EAN, FSN or SKU code, by marketplace
     */
    product_ref: string | null;
    selling_price: string | null;
    currency_code: string | null;
    fixed_fee: string | null;
    variable_fee_percent: string | null;
    return_fee_percent: string | null;
    shipping_fee: string | null;
    /**
     * The store's expected ACoS
     */
    acos_percent: string | null;
    /**
     * Fixed + variable x price + shipping
     */
    total_fee: string | null;
    estimate_date: string | null;
};

/**
 * A sales channel (``com_marketplace``).
 *
 * Attributes:
 * short_code: v1's channel handle, e.g. ``AZUK``. What v1 reports and
 * VentaWiz-Ingest's surface map key on.
 * country: ISO 3166-1 alpha-2.
 * currency: ISO 4217, what it settles in.
 * language_code: The storefront's language.
 * timezone: Where the channel's day boundaries fall.
 * vat_rate: The default VAT rate, as a fraction.
 */
export type MarketplaceRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    short_code: string;
    country: string;
    currency: string;
    language_code: string;
    timezone: string;
    vat_rate: string;
};

/**
 * The store's marketplace, with its channel family.
 */
export type MarketplaceSummary = {
    id: number;
    code: string;
    short_code: string;
    name: string;
    country: string;
    currency: string;
    family: string;
    platform: string;
};

/**
 * Body of ``PATCH /v1/marketplaces/{id}``.
 *
 * ``short_code`` is absent: like ``code`` it is a handle other systems hold.
 *
 * Attributes:
 * country: ISO 3166-1 alpha-2.
 * currency: ISO 4217.
 * language_code: The storefront's language.
 * timezone: An IANA zone.
 * vat_rate: A fraction between 0 and 1.
 */
export type MarketplaceUpdate = {
    name?: string | null;
    country?: string | null;
    currency?: string | null;
    language_code?: string | null;
    timezone?: string | null;
    vat_rate?: number | string | null;
};

/**
 * Where an MCF source store sells, as ``mcf_source_configs`` allows.
 */
export type McfChannel = 'walmart' | 'shopify' | 'tiktok';

/**
 * Where an MCF source store sells, as ``mcf_source_configs`` allows.
 */
export const McfChannel = {
    WALMART: 'walmart',
    SHOPIFY: 'shopify',
    TIKTOK: 'tiktok'
} as const;

/**
 * A queued create. Find its run with ``GET …/runs?request_id=``.
 */
export type McfCreateAccepted = {
    request_id: string;
    store_id: number;
    dry_run: boolean;
    orders: number;
};

/**
 * Source orders to turn into MCF orders.
 *
 * **A live create ships real parcels from FBA stock.** Nothing is sent
 * without ``confirm: true``, and nothing but a plan unless ``dry_run`` is
 * ``false`` too.
 */
export type McfCreateRequest = {
    source_order_ids: Array<string>;
    /**
     * Must be true: the caller confirms parcels will ship
     */
    confirm?: boolean;
    /**
     * Check and plan only; send nothing
     */
    dry_run?: boolean;
};

/**
 * One line of an MCF order.
 */
export type McfLineRead = {
    line_ref: string;
    source_sku: string | null;
    seller_sku: string;
    quantity: number;
    quantity_shipped: number;
};

/**
 * An MCF order this service requested, and what Amazon has said of it.
 */
export type McfOrderRead = {
    id: string;
    source_order_id: string;
    seller_fulfillment_order_id: string;
    fulfilling_store_id: number;
    /**
     * received, accepted, processing, complete, failed, …
     */
    status: string;
    amazon_status: string | null;
    shipping_speed: string;
    /**
     * When the customer ordered
     */
    order_date: string;
    created_at: string;
    submitted_at: string | null;
    completed_at: string | null;
    /**
     * When this row last changed
     */
    updated_at: string | null;
    last_error: string | null;
    lines: Array<McfLineRead>;
    packages: Array<McfPackageRead>;
};

/**
 * One package Amazon shipped.
 */
export type McfPackageRead = {
    package_number: number;
    amazon_shipment_id: string;
    carrier_code: string | null;
    tracking_number: string | null;
    tracking_url: string | null;
    status: string | null;
    shipped_at: string | null;
    estimated_arrival_at: string | null;
};

/**
 * One line of a previewed order.
 */
export type McfPreviewLineRead = {
    line_ref: string;
    quantity: number;
    source_sku: string | null;
    /**
     * The FBA seller SKU it ships as; null if skipped
     */
    seller_sku: string | null;
};

/**
 * One source order, and whether it would become an MCF order.
 */
export type McfPreviewOrderRead = {
    source_order_id: string;
    order_date: string | null;
    disposition: 'ready' | 'skip';
    /**
     * Why it is skipped; null when ready
     */
    reason: string | null;
    /**
     * An earlier MCF order of it failed; a create retries it
     */
    retry: boolean;
    /**
     * City and country only, when the address is good
     */
    destination: string | null;
    /**
     * The id the MCF order would carry
     */
    seller_fulfillment_order_id: string;
    lines: Array<McfPreviewLineRead>;
};

/**
 * A run's totals: a create's outcomes, or a status read's.
 */
export type McfRunCounts = {
    orders: number;
    created?: number;
    failed?: number;
    not_sent?: number;
    planned?: number;
    found?: number;
};

/**
 * One run with each order's outcome.
 */
export type McfRunDetail = {
    run_id: string;
    request_id: string | null;
    kind: 'create' | 'status';
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    dry_run: boolean;
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Null until the run has reported
     */
    counts: McfRunCounts | null;
    rows: Array<McfRunRowRead>;
    /**
     * Why the run failed, when it did
     */
    error: string | null;
};

/**
 * One create or status run, as listed.
 */
export type McfRunRead = {
    run_id: string;
    request_id: string | null;
    kind: 'create' | 'status';
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    dry_run: boolean;
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Null until the run has reported
     */
    counts: McfRunCounts | null;
};

/**
 * What became of one order in a run.
 */
export type McfRunRowRead = {
    seller_fulfillment_order_id: string;
    /**
     * created, failed, not_sent or planned (a create)
     */
    outcome: string | null;
    /**
     * Amazon's status (a status read)
     */
    amazon_status: string | null;
    error: string | null;
};

/**
 * Amazon MCF's shipping speeds.
 */
export type McfShippingSpeed = 'Standard' | 'Expedited' | 'Priority';

/**
 * Amazon MCF's shipping speeds.
 */
export const McfShippingSpeed = {
    STANDARD: 'Standard',
    EXPEDITED: 'Expedited',
    PRIORITY: 'Priority'
} as const;

/**
 * One pairing: the source store's orders, fulfilled from the fulfilling store's FBA stock.
 */
export type McfSourceRead = {
    id: string;
    channel: McfChannel;
    source_store_id: number;
    fulfilling_store_id: number;
    enabled: boolean;
    auto_create: boolean;
    shipping_speed: McfShippingSpeed;
    lookback_days: number;
};

/**
 * A store whose orders this store fulfils through MCF.
 */
export type McfSourceWrite = {
    channel: McfChannel;
    source_store_id: number;
    enabled?: boolean;
    auto_create?: boolean;
    shipping_speed?: McfShippingSpeed;
    lookback_days?: number;
};

/**
 * A queued read of the store's awaiting orders from Amazon.
 */
export type McfStatusSyncAccepted = {
    /**
     * Null when no order was awaiting
     */
    request_id: string | null;
    store_id: number;
    orders: number;
};

/**
 * A source store whose orders can be fulfilled through Amazon MCF.
 */
export type McfStoreRead = {
    /**
     * The source store, com_store.pk_store_id
     */
    store_id: number;
    name: string;
    /**
     * Its marketplace, e.g. SPUS
     */
    short_code: string;
    channel: 'shopify' | 'tiktok';
    /**
     * The Amazon store whose FBA stock ships
     */
    fulfilling_store_id: number;
    fulfilling_store_name: string;
    /**
     * Standard, Expedited or Priority
     */
    shipping_speed: string;
    /**
     * How far back source orders are read
     */
    lookback_days: number;
};

/**
 * A memory the analyst uses.
 */
export type MemoryRead = {
    id: string;
    text: string;
    scope: MemoryScope;
    source: string;
    created_at: string;
};

/**
 * Who a memory applies to: one person, or the whole organization (``client``).
 */
export type MemoryScope = 'user' | 'client';

/**
 * Who a memory applies to: one person, or the whole organization (``client``).
 */
export const MemoryScope = {
    USER: 'user',
    CLIENT: 'client'
} as const;

/**
 * Body of ``POST /memory``: remember something for me, or for everyone here.
 *
 * Attributes:
 * from_proposal: Confirming the analyst's "Remember this?". A company-wide
 * proposal from someone who may not edit the company's memory is
 * kept for them alone instead of refused.
 */
export type MemoryWrite = {
    text: string;
    scope?: MemoryScope;
    from_proposal?: boolean;
};

/**
 * Rows both brands have under one unique key.
 *
 * Attributes:
 * table: The table, without its schema.
 * label: What the "Used by" view calls it.
 * count: The source's rows that collide.
 * resolution: What happens to them: the target's row is kept and the
 * source's dropped.
 */
export type MergeConflict = {
    table: string;
    label: string;
    count: number;
    resolution: ConflictResolution;
};

/**
 * What merging one brand into another would do.
 *
 * Attributes:
 * source: The brand merged away (retired afterwards).
 * target: The brand it is merged into.
 * domains: What moves, by domain: the source's usage without its
 * notes, tags and contacts, which stay on it.
 * conflicts: Rows dropped instead of moved.
 */
export type MergePreview = {
    source: BrandRead;
    target: BrandRead;
    domains: Array<UsageDomainRead>;
    conflicts: Array<MergeConflict>;
};

/**
 * Body of ``POST /v1/brands/{id}/merge``.
 *
 * Attributes:
 * into_brand_id: The brand to merge into.
 * reason: Why; recorded on both brands' events.
 * confirm_code: The source brand's code, typed by the person to confirm.
 */
export type MergeRequest = {
    into_brand_id: number;
    reason: string;
    confirm_code: string;
};

/**
 * What a merge did.
 *
 * Attributes:
 * target: The brand merged into, afterwards.
 * moved: Rows moved, per table.
 * dropped: The source's rows the target already had, per table: the
 * conflicts the preview listed. A store selling both brands keeps
 * its link to the target (``rel_store_brand``).
 * retired_source: Always true: the source is retired.
 */
export type MergeResult = {
    target: BrandRead;
    moved: {
        [key: string]: number;
    };
    dropped: {
        [key: string]: number;
    };
    retired_source: boolean;
};

/**
 * Purchase orders to fold into this one.
 *
 * Attributes:
 * po_codes: Codes in the same warehouse, for the same retailer and in the
 * same currency, with nothing allotted, blocked, invoiced or on a
 * vendor order yet.
 */
export type MergeWrite = {
    po_codes: Array<string>;
};

/**
 * A stored message, in AI SDK UIMessage shape (``parts``).
 *
 * Attributes:
 * run_status: Assistant messages: the run's outcome.
 * feedback: Assistant messages: the viewer's rating, if any.
 */
export type MessageRead = {
    id: string;
    role: MessageRole;
    parts: Array<{
        [key: string]: unknown;
    }>;
    run_id?: string | null;
    created_at: string;
    run_status?: AnalystRunStatus | null;
    feedback?: FeedbackRead | null;
};

/**
 * Who wrote a message.
 */
export type MessageRole = 'user' | 'assistant';

/**
 * Who wrote a message.
 */
export const MessageRole = {
    USER: 'user',
    ASSISTANT: 'assistant'
} as const;

/**
 * A figure the sales and marketing panel shows.
 */
export type Metric = 'total_sales' | 'total_returns' | 'ams_sales' | 'ams_spend' | 'roas';

/**
 * A figure the sales and marketing panel shows.
 */
export const Metric = {
    TOTAL_SALES: 'total_sales',
    TOTAL_RETURNS: 'total_returns',
    AMS_SALES: 'ams_sales',
    AMS_SPEND: 'ams_spend',
    ROAS: 'roas'
} as const;

/**
 * One figure for the window, against the same window in the months before.
 *
 * Attributes:
 * metric: Which figure.
 * value: The window's value: an amount for sales and ads, a count of
 * units for returns, a ratio for ROAS. None when it cannot be told
 * (ROAS with no spend).
 * baseline: The mean of the same window one, two and three months
 * back, those that had any. None when none had.
 * change: ``value / baseline - 1``, a fraction. None without a baseline.
 */
export type MetricFigure = {
    metric: Metric;
    value: string | null;
    baseline: string | null;
    change: number | null;
};

/**
 * Where the week or month is heading.
 *
 * Attributes:
 * metric: Which figure.
 * horizon: To the week's end or the month's.
 * status: ``insufficient_data`` with fewer than 60 days of history,
 * or all of it zero; the points are then the actuals only.
 * currency: The amounts' currency; None for returns.
 * period_start: The week's Monday or the month's 1st.
 * horizon_end: The Sunday or the month's last day.
 * to_date: The period so far, through today.
 * window_from: The selected window's first day.
 * window_until: Its last day shown: its end, or today if that is sooner.
 * window_to_date: The window so far — what the reader picked dates for,
 * unlike ``to_date``, which is the forecast period's.
 * ahead: The forecast for the days left, summed.
 * projected: ``to_date`` plus ``ahead``.
 * projected_low: The same with the band's low ends.
 * projected_high: And with its high ends.
 * confidence: How right this forecast was recently, 0 to 0.99.
 * points: The window's days, then the days to the horizon.
 * fx: How the amounts were converted; ``null`` without ``convert_to``.
 * Converted, the forecast is fitted to the converted days.
 */
export type MetricForecast = {
    metric: Metric;
    horizon: Horizon;
    status: ForecastStatus;
    currency: string | null;
    period_start: string;
    horizon_end: string;
    to_date: string;
    window_from?: string | null;
    window_until?: string | null;
    window_to_date?: string | null;
    ahead: string | null;
    projected: string | null;
    projected_low: string | null;
    projected_high: string | null;
    confidence: number | null;
    points: Array<ForecastPoint>;
    fx?: FxMeta | null;
};

/**
 * An amount and its currency.
 *
 * ``amount`` is a **string**. JSON numbers are IEEE doubles in most clients,
 * so sending a Decimal as a number hands the precision straight back to the
 * floating-point problem the Decimal exists to avoid.
 *
 * Attributes:
 * amount: Decimal value, as a string.
 * currency: ISO 4217 code.
 */
export type MoneyRead = {
    amount: string;
    currency: string;
};

/**
 * An amount and its currency, inbound.
 *
 * Accepts a string or a number and normalises to Decimal. Pydantic parses a
 * JSON number into Decimal without going through float, so a client that
 * sends one is not silently punished — but a string is still the right thing
 * to send.
 *
 * Attributes:
 * amount: Decimal value.
 * currency: ISO 4217 code, three letters.
 */
export type MoneyWrite = {
    amount: number | string;
    currency: string;
};

/**
 * A focused SKU's week against the one before.
 *
 * Attributes:
 * this_week: The last 7 days' value (for rank, the latest in them).
 * last_week: The 7 days before.
 * change: ``this_week - last_week``.
 * worse: Whether the change is for the worse: a rank number grew, or
 * anything else fell.
 */
export type Mover = {
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    asin: string | null;
    image_url: string | null;
    brand_name: string | null;
    store_id: number;
    store_name: string | null;
    marketplace_code: string | null;
    country_code: string | null;
    this_week: string | null;
    last_week: string | null;
    change: string | null;
    worse: boolean;
};

/**
 * What a focused SKU's week is ranked on.
 */
export type MoverMetric = 'rank' | 'sales' | 'ams_spend' | 'ams_clicks' | 'ams_sales' | 'acos';

/**
 * What a focused SKU's week is ranked on.
 */
export const MoverMetric = {
    RANK: 'rank',
    SALES: 'sales',
    AMS_SPEND: 'ams_spend',
    AMS_CLICKS: 'ams_clicks',
    AMS_SALES: 'ams_sales',
    ACOS: 'acos'
} as const;

/**
 * The focused SKUs that moved most for the worse.
 *
 * Attributes:
 * metric: What they are ranked on.
 * this_week_from: The last 7 days' first day.
 * last_week_from: The 7 days before's first day.
 * rows: Worst first; only rows that moved for the worse.
 * currency: For ad spend and ad sales, what every row's amounts are in
 * once converted (``convert_to``); otherwise None, each row being in
 * its store's currency, or not money at all.
 * fx: How they were converted, when they were.
 */
export type Movers = {
    metric: MoverMetric;
    this_week_from: string;
    last_week_from: string;
    rows: Array<Mover>;
    currency?: string | null;
    fx?: FxMeta | null;
};

/**
 * What marketplace payments can be grouped by.
 */
export type MpPaymentsGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'transaction_class' | 'component' | 'settlement' | 'order' | 'day' | 'week' | 'month';

/**
 * What marketplace payments can be grouped by.
 */
export const MpPaymentsGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    TRANSACTION_CLASS: 'transaction_class',
    COMPONENT: 'component',
    SETTLEMENT: 'settlement',
    ORDER: 'order',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Marketplace payments, grouped.
 */
export type MpPaymentsReport = {
    items: Array<MpPaymentsRow>;
    pagination: Pagination;
    totals: Array<MpPaymentsTotals>;
    meta: ReportMeta;
};

/**
 * One group of settlement lines.
 *
 * Attributes:
 * transaction_class: Filled when grouped by transaction class:
 * ``sale``, ``refund``, ``adjustment`` or ``other``.
 * component: Filled when grouped by component: ``principal``,
 * ``shipping``, ``tax``, ``commission``, ``fee`` or ``other``.
 * settlement_id: Filled when grouped by settlement: the statement the
 * lines are on.
 * platform_settlement_id: The marketplace's id for the statement.
 * order_id: Filled when grouped by order: the order paid for.
 */
export type MpPaymentsRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    net_amount?: MoneyRead | null;
    principal_amount?: MoneyRead | null;
    shipping_amount?: MoneyRead | null;
    tax_amount?: MoneyRead | null;
    commission_amount?: MoneyRead | null;
    fee_amount?: MoneyRead | null;
    other_amount?: MoneyRead | null;
    memo_amount?: MoneyRead | null;
    sale_amount?: MoneyRead | null;
    refund_amount?: MoneyRead | null;
    line_count?: number | null;
    duplicate_line_count?: number | null;
    held_out_line_count?: number | null;
    order_line_count?: number | null;
    transaction_class?: string | null;
    component?: string | null;
    settlement_id?: number | null;
    platform_settlement_id?: string | null;
    order_id?: number | null;
};

/**
 * What marketplaces paid us over every matching settlement line, in one currency.
 *
 * A copy of a line another statement holds, a memo line and a line held
 * out as implausible hold no money (plan §11.1, D25, D26): they are counted,
 * and add nothing to the money paid. A memo line's amount is in
 * ``memo_amount`` alone.
 *
 * Attributes:
 * currency_code: The payout currency.
 * net_amount: Money paid to us, signed: sales and shipping in, refunds,
 * commissions and fees out. The six parts below add up to it.
 * principal_amount: The part that pays for the items.
 * shipping_amount: Shipping.
 * tax_amount: Tax.
 * commission_amount: The marketplace's commission. Amazon's is the
 * referral fee in report uploads and every fee through its API, so
 * compare commission and fee together.
 * fee_amount: Other fees.
 * other_amount: Everything else.
 * memo_amount: Memo lines, never money: a fee already netted from the
 * revenue beside it (Trendyol's commission, eBay's fee).
 * sale_amount: Money on sale lines.
 * refund_amount: Money on refund lines, a sale reversed by a line of its
 * own type included.
 * line_count: Settlement lines, copies and memos included.
 * duplicate_line_count: Copies of a line another statement holds.
 * held_out_line_count: Lines held out as implausible, such as an Otto
 * fee larger than its order's sale.
 * order_line_count: Order lines paid for. Otto names only the order of
 * a multi-line order, so its lines are not counted here.
 */
export type MpPaymentsTotals = {
    currency_code?: string | null;
    net_amount?: MoneyRead | null;
    principal_amount?: MoneyRead | null;
    shipping_amount?: MoneyRead | null;
    tax_amount?: MoneyRead | null;
    commission_amount?: MoneyRead | null;
    fee_amount?: MoneyRead | null;
    other_amount?: MoneyRead | null;
    memo_amount?: MoneyRead | null;
    sale_amount?: MoneyRead | null;
    refund_amount?: MoneyRead | null;
    line_count?: number | null;
    duplicate_line_count?: number | null;
    held_out_line_count?: number | null;
    order_line_count?: number | null;
};

/**
 * What marketplace payouts can be grouped by.
 */
export type MpPayoutsGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'payout' | 'total_basis' | 'day' | 'week' | 'month';

/**
 * What marketplace payouts can be grouped by.
 */
export const MpPayoutsGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    PAYOUT: 'payout',
    TOTAL_BASIS: 'total_basis',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Marketplace payouts, grouped.
 */
export type MpPayoutsReport = {
    items: Array<MpPayoutsRow>;
    pagination: Pagination;
    totals: Array<MpPayoutsTotals>;
    meta: ReportMeta;
};

/**
 * One group of payouts.
 *
 * Attributes:
 * settlement_id: Filled when grouped by payout: its lowest statement id.
 * payout_key: What makes several statements one payout, such as an
 * Amazon settlement id without its marketplace suffix.
 * platform_settlement_ids: The marketplace's ids of its statements,
 * comma-separated.
 * period_start: When the period it settles starts.
 * period_end: When that period ends.
 * deposit_at: When it was paid, where the marketplace says.
 * total_basis: Filled when grouped by payout or by total basis:
 * ``reported`` (the marketplace's own figure), ``computed`` (the
 * lines' total) or ``missing`` (no payout figure).
 */
export type MpPayoutsRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    payout_amount?: MoneyRead | null;
    reported_amount?: MoneyRead | null;
    lines_net_amount?: MoneyRead | null;
    unattributed_amount?: MoneyRead | null;
    payout_count?: number | null;
    line_count?: number | null;
    settlement_id?: number | null;
    payout_key?: string | null;
    platform_settlement_ids?: string | null;
    period_start?: string | null;
    period_end?: string | null;
    deposit_at?: string | null;
    total_basis?: string | null;
};

/**
 * What marketplaces paid out over every matching payout, in one currency.
 *
 * A payout all of whose lines are copies of another statement's holds no
 * money and is left out (D26).
 *
 * Attributes:
 * currency_code: The payout currency.
 * payout_amount: What was paid out: the marketplace's own figure where
 * it reports one, else the sum of the payout's lines.
 * reported_amount: The payouts' reported totals: the marketplace's own
 * figure (Amazon, Noon, Bol's API invoices), except on a Noon
 * statement re-uploaded with copies of its lines, whose total is its
 * lines that are not copies (``total_basis`` ``computed``);
 * ``null`` where none of the payouts has one.
 * lines_net_amount: The payouts' lines: money paid for order lines.
 * unattributed_amount: The reported totals less their lines: money for
 * no order line, such as advertising, storage and reserves;
 * ``null`` where none of the payouts has a reported total.
 * payout_count: Payouts.
 * line_count: Their settlement lines.
 */
export type MpPayoutsTotals = {
    currency_code?: string | null;
    payout_amount?: MoneyRead | null;
    reported_amount?: MoneyRead | null;
    lines_net_amount?: MoneyRead | null;
    unattributed_amount?: MoneyRead | null;
    payout_count?: number | null;
    line_count?: number | null;
};

/**
 * What marketplace sales can be grouped by.
 */
export type MpSalesGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'status' | 'order' | 'day' | 'week' | 'month';

/**
 * What marketplace sales can be grouped by.
 */
export const MpSalesGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    STATUS: 'status',
    ORDER: 'order',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Marketplace sales, grouped.
 */
export type MpSalesReport = {
    items: Array<MpSalesRow>;
    pagination: Pagination;
    totals: Array<MpSalesTotals>;
    meta: ReportMeta;
};

/**
 * One group of marketplace order lines.
 *
 * Attributes:
 * status_class: Filled when grouped by status: ``fulfilled``, ``open``,
 * ``returned``, ``undelivered`` or ``cancelled``.
 * order_id: Filled when grouped by order.
 * platform_order_id: The marketplace's order number.
 */
export type MpSalesRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    units?: number | null;
    sales_amount?: MoneyRead | null;
    cancelled_units?: number | null;
    cancelled_amount?: MoneyRead | null;
    returned_units?: number | null;
    returned_amount?: MoneyRead | null;
    order_count?: number | null;
    average_price?: MoneyRead | null;
    drr?: string | null;
    status_class?: string | null;
    order_id?: number | null;
    platform_order_id?: string | null;
};

/**
 * Marketplace sales over every matching order line, in one currency.
 *
 * A cancelled line, and a line stored twice, hold no units and no money
 * (plan §4.1, D15); a returned line is still a sale.
 *
 * Attributes:
 * currency_code: The order currency.
 * units: Units sold.
 * sales_amount: What customers were charged, by each marketplace's rule.
 * cancelled_units: Units on cancelled lines, shown beside the sales.
 * cancelled_amount: Their amount.
 * returned_units: Units on lines the marketplace reports returned.
 * returned_amount: Their sales.
 * order_count: Orders with at least one unit sold.
 * average_price: Sales per unit.
 * drr: Units a day (plan §9.5): units ÷ the days the row covers — one
 * for a day, the window's days in it for a week or a month, the
 * whole window otherwise.
 */
export type MpSalesTotals = {
    currency_code?: string | null;
    units?: number | null;
    sales_amount?: MoneyRead | null;
    cancelled_units?: number | null;
    cancelled_amount?: MoneyRead | null;
    returned_units?: number | null;
    returned_amount?: MoneyRead | null;
    order_count?: number | null;
    average_price?: MoneyRead | null;
    drr?: string | null;
};

/**
 * Everything the frontend needs to pick a zone for any page.
 *
 * The profile default is not here: it is the person's, held by dv-auth, and
 * the frontend reads it from there.
 *
 * Attributes:
 * overrides: The caller's narrowings.
 * stores: The zones of every store the caller may see.
 */
export type MyTimezonesRead = {
    overrides: Array<TimezoneOverrideRead>;
    stores: Array<StoreZoneRead>;
};

/**
 * An id and what a person calls it, for a picker.
 *
 * Attributes:
 * id: The row's key.
 * name: Its display name.
 * code: Its short code, when it has one.
 */
export type NamedRead = {
    id: number;
    name: string;
    code?: string | null;
};

/**
 * Negative match types: uppercase for SP, camelCase for SB (RSC:261-267, SCH:477-481).
 *
 * The add-negative modals offer ``NEGATIVE_*`` for SP ad groups and
 * ``negativeExact`` / ``negativePhrase`` for SB; which one an ad group accepts
 * is checked by the gate (AV11 / AV12), not here.
 */
export type NegativeMatchType = 'NEGATIVE_BROAD' | 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE' | 'negativeExact' | 'negativePhrase';

/**
 * Negative match types: uppercase for SP, camelCase for SB (RSC:261-267, SCH:477-481).
 *
 * The add-negative modals offer ``NEGATIVE_*`` for SP ad groups and
 * ``negativeExact`` / ``negativePhrase`` for SB; which one an ad group accepts
 * is checked by the gate (AV11 / AV12), not here.
 */
export const NegativeMatchType = {
    NEGATIVE_BROAD: 'NEGATIVE_BROAD',
    NEGATIVE_EXACT: 'NEGATIVE_EXACT',
    NEGATIVE_PHRASE: 'NEGATIVE_PHRASE',
    SB_NEGATIVE_EXACT: 'negativeExact',
    SB_NEGATIVE_PHRASE: 'negativePhrase'
} as const;

/**
 * A negative keyword or target, as currently configured.
 *
 * Not scoped to a date range: negatives are configuration, and this is the
 * latest snapshot of each one.
 *
 * Attributes:
 * key: Stable identity.
 * keyword_text: The negative.
 * match_type: NEGATIVE_EXACT, NEGATIVE_PHRASE, …
 * negative_type: keyword or product target.
 * status: ENABLED, PAUSED or ARCHIVED — the most alive across its ad
 * groups.
 * ad_group_count: How many ad groups carry it.
 * store_id: Which store.
 * store_name: Its name.
 * last_seen: The latest snapshot it appeared in.
 */
export type NegativeRead = {
    key: string;
    keyword_text: string;
    match_type: string | null;
    negative_type: string;
    status: string;
    ad_group_count: number;
    store_id: number;
    store_name: string;
    last_seen: string | null;
};

/**
 * Sort keys of the negatives listing and its exports (grain-checked by the service).
 *
 * ``ad_group_name`` / ``ad_group_id`` are legal only for the by-ad-group export,
 * ``ad_group_count`` only for the aggregated grain (RS:139, RS:1571-1584).
 */
export type NegativeSortKey = 'keyword_text' | 'status' | 'date' | 'ad_group_count' | 'ad_group_name' | 'ad_group_id';

/**
 * Sort keys of the negatives listing and its exports (grain-checked by the service).
 *
 * ``ad_group_name`` / ``ad_group_id`` are legal only for the by-ad-group export,
 * ``ad_group_count`` only for the aggregated grain (RS:139, RS:1571-1584).
 */
export const NegativeSortKey = {
    KEYWORD_TEXT: 'keyword_text',
    STATUS: 'status',
    DATE: 'date',
    AD_GROUP_COUNT: 'ad_group_count',
    AD_GROUP_NAME: 'ad_group_name',
    AD_GROUP_ID: 'ad_group_id'
} as const;

/**
 * The code a new vendor order from the forecast would take: ``AUTO-YYYYMMDD-N``.
 */
export type NextVendorOrderCode = {
    vo_code: string;
};

/**
 * Focused SKUs with no sale in the last 30 days, most clicked first.
 */
export type NoSale = {
    since: string;
    rows: Array<NoSaleSku>;
};

/**
 * A focused SKU that sold nothing in 30 days.
 *
 * Attributes:
 * marketplace_stock: Sellable at the marketplace, for the store.
 * warehouse_stock: Available in the store's warehouses.
 * clicks: Ad clicks on it over the 30 days.
 */
export type NoSaleSku = {
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    asin: string | null;
    image_url: string | null;
    brand_name: string | null;
    store_id: number;
    store_name: string | null;
    marketplace_code: string | null;
    country_code: string | null;
    marketplace_stock: number;
    warehouse_stock: number;
    clicks: number;
};

/**
 * What retailer receipts can be grouped by.
 */
export type NonMpReceiptsGrouping = 'warehouse' | 'retailer' | 'channel' | 'invoice' | 'day' | 'week' | 'month';

/**
 * What retailer receipts can be grouped by.
 */
export const NonMpReceiptsGrouping = {
    WAREHOUSE: 'warehouse',
    RETAILER: 'retailer',
    CHANNEL: 'channel',
    INVOICE: 'invoice',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Retailer receipts, grouped.
 */
export type NonMpReceiptsReport = {
    items: Array<NonMpReceiptsRow>;
    pagination: Pagination;
    totals: Array<NonMpReceiptsTotals>;
    meta: ReportMeta;
};

/**
 * One group of receipts.
 *
 * Attributes:
 * invoice_id: Filled when grouped by invoice: the invoice paid.
 * invoice_number: Its number.
 */
export type NonMpReceiptsRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    channel?: string | null;
    retailer_id?: number | null;
    retailer_name?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    receipt_amount?: MoneyRead | null;
    receipt_count?: number | null;
    invoice_count?: number | null;
    before_invoice_count?: number | null;
    future_dated_count?: number | null;
    invoice_id?: number | null;
    invoice_number?: string | null;
};

/**
 * Money retailers paid us over every matching receipt, in one currency.
 *
 * A receipt is dated by its own day, never by its invoice's (plan §11.2).
 *
 * Attributes:
 * currency_code: The receipt currency.
 * receipt_amount: Cash received, VAT included.
 * receipt_count: Receipts.
 * invoice_count: Invoices they pay.
 * before_invoice_count: Receipts dated before their invoice.
 * future_dated_count: Receipts dated after the day they were entered.
 */
export type NonMpReceiptsTotals = {
    currency_code?: string | null;
    receipt_amount?: MoneyRead | null;
    receipt_count?: number | null;
    invoice_count?: number | null;
    before_invoice_count?: number | null;
    future_dated_count?: number | null;
};

/**
 * What retailer sales can be grouped by.
 */
export type NonMpSalesGrouping = 'warehouse' | 'retailer' | 'channel' | 'invoice' | 'sku' | 'brand' | 'category' | 'subcategory' | 'day' | 'week' | 'month';

/**
 * What retailer sales can be grouped by.
 */
export const NonMpSalesGrouping = {
    WAREHOUSE: 'warehouse',
    RETAILER: 'retailer',
    CHANNEL: 'channel',
    INVOICE: 'invoice',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Retailer sales, grouped.
 */
export type NonMpSalesReport = {
    items: Array<NonMpSalesRow>;
    pagination: Pagination;
    totals: Array<NonMpSalesTotals>;
    meta: ReportMeta;
};

/**
 * One group of invoice lines.
 *
 * Attributes:
 * invoice_id: Filled when grouped by invoice.
 * invoice_number: Its number.
 */
export type NonMpSalesRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    channel?: string | null;
    retailer_id?: number | null;
    retailer_name?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    units?: number | null;
    sales_amount?: MoneyRead | null;
    line_count?: number | null;
    invoice_count?: number | null;
    average_price?: MoneyRead | null;
    drr?: string | null;
    invoice_id?: number | null;
    invoice_number?: string | null;
};

/**
 * Retailer sales (DF, OF, VO) over every matching invoice line, in one currency.
 *
 * Attributes:
 * currency_code: The invoice currency.
 * units: Units invoiced.
 * sales_amount: Quantity times price, without VAT (plan §4.6).
 * line_count: Invoice lines.
 * invoice_count: Invoices.
 * average_price: Sales per unit.
 * drr: Units a day: units ÷ the days the row covers, as for
 * marketplace sales.
 */
export type NonMpSalesTotals = {
    currency_code?: string | null;
    units?: number | null;
    sales_amount?: MoneyRead | null;
    line_count?: number | null;
    invoice_count?: number | null;
    average_price?: MoneyRead | null;
    drr?: string | null;
};

/**
 * One row of a Noon ads report, from whichever sheet it was on.
 *
 * Attributes:
 * sheet: The sheet's name.
 * row: Its row number on that sheet.
 */
export type NoonRow = {
    sheet: string;
    row: number;
    campaign_name?: string | null;
    sku?: string | null;
    target_value?: string | null;
    targeting_type?: string | null;
    bid?: string | null;
    strategy?: string | null;
    placement_type?: string | null;
    views?: string | null;
    clicks?: string | null;
    orders?: string | null;
    atc?: string | null;
    spends?: string | null;
    revenue?: string | null;
    ctr?: string | null;
    roas?: string | null;
    cpc?: string | null;
    cps?: string | null;
    cvr?: string | null;
};

/**
 * One row's notes, as edited.
 *
 * Send only the fields that changed; an omitted field keeps its value, and
 * ``null`` clears it.
 *
 * Attributes:
 * sku_id: The row.
 * note_versions: The ``note_versions`` the row was read with.
 * drr_miss_reason: One of the reasons, or null.
 * action: At most 500 characters; required with "Other".
 * stock_issue: One of the stock issues, or null.
 * stock_issue_action: At most 500 characters.
 */
export type NoteChange = {
    sku_id: number;
    note_versions: {
        [key: string]: number;
    };
    drr_miss_reason?: string | null;
    action?: string | null;
    stock_issue?: string | null;
    stock_issue_action?: string | null;
};

/**
 * What saving this note would refuse. Empty when it would save.
 *
 * Attributes:
 * problems: Every problem found.
 */
export type NoteCheckRead = {
    problems: Array<NoteProblem>;
};

/**
 * Body of ``POST .../notes``.
 *
 * Attributes:
 * body: The text.
 * author_name: The author's display name, shown beside the note.
 */
export type NoteCreate = {
    body: string;
    author_name?: string | null;
};

/**
 * A note and its lines, as a write returns them.
 */
export type NoteDetailRead = {
    note: CreditDebitNoteRead;
    lines: Array<CreditDebitNoteLineRead>;
};

/**
 * What may change on a saved note.
 *
 * Type, stock mode, link, quantities and boxes are fixed once saved: changing
 * them would mean moving stock again. Void the note and raise a new one.
 * Fields left out keep their value.
 *
 * Attributes:
 * number: Our note number.
 * date: The note's date.
 * their_number: Their number for it.
 * comment: Free text.
 * amount_wo_vat: ``S`` notes only.
 * amount: ``S`` notes only.
 * prices: New line prices; ``N`` and ``Y`` notes only.
 */
export type NoteEdit = {
    number?: string | null;
    date?: string | null;
    their_number?: string | null;
    comment?: string | null;
    amount_wo_vat?: number | string | null;
    amount?: number | string | null;
    prices?: Array<NoteLinePrice>;
};

/**
 * A new price for one line of a note.
 *
 * Attributes:
 * line_id: The line.
 * price: Price per unit, before VAT.
 * vat_rate: A fraction.
 */
export type NoteLinePrice = {
    line_id: number;
    price: number | string;
    vat_rate: number | string;
};

/**
 * One line of a new note.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * box_code: The box the units go into (credit) or come out of (debit).
 * Required when the note moves stock; ignored otherwise. A credit may
 * name a box that does not exist yet, and it is created on the shelf.
 * quantity: Units. Positive.
 * price: Price per unit, before VAT.
 * vat_rate: A fraction: ``0.18`` for 18%.
 */
export type NoteLineWrite = {
    sku_code: string;
    box_code?: string | null;
    quantity: number;
    price: number | string;
    vat_rate?: number | string;
};

/**
 * The document a note adjusts.
 *
 * Attributes:
 * kind: Sales invoice (a retailer), vendor invoice or service-provider invoice.
 * number: Its number, in the note's warehouse.
 */
export type NoteLink = {
    kind: 'sales_invoice' | 'vendor_invoice' | 'sp_invoice';
    number: string;
};

/**
 * One reason a note was refused.
 *
 * Attributes:
 * row: The 1-based line, or ``None`` when it is about the whole note.
 * field: Which input, when one.
 * message: What is wrong, in a sentence a person can act on.
 */
export type NoteProblem = {
    row: number | null;
    field: string | null;
    message: string;
};

/**
 * A note on a store, a warehouse, a brand or a marketplace.
 *
 * Attributes:
 * author: The author's display name as sent when the note was written,
 * else their principal id.
 * author_principal_id: The author.
 * at: When it was written.
 * edited_at: When its text last changed.
 * can_edit: Whether the caller may edit or delete it: its author, or
 * anyone holding ``registry:edit``.
 */
export type NoteRead = {
    id: number;
    body: string;
    author: string;
    author_principal_id: string;
    at: string;
    edited_at: string | null;
    can_edit: boolean;
};

/**
 * Notes to save on the latest finished period.
 *
 * Attributes:
 * grain: Which sheet.
 * period_start: The period the sheet showed, as it came: refused once
 * it is no longer the latest finished period, so notes written about
 * one week never land on the next.
 * warehouse_id: The warehouse the sheet was opened at.
 * store_id: The store, in store mode; omitted for all stores, where a
 * row's note is saved to every store it covers.
 * principal_id: Whose sheet it was; omitted, the caller's own. The notes
 * are shared, so this only says which rows the sheet had.
 * name: The caller's display name, kept beside the note.
 * rows: The edited rows.
 */
export type NoteSave = {
    grain?: FocusGrain;
    period_start: string;
    warehouse_id?: number | null;
    store_id?: number | null;
    principal_id?: string | null;
    name?: string | null;
    rows: Array<NoteChange>;
};

/**
 * What a save did.
 *
 * Attributes:
 * saved: Rows whose notes changed.
 * notes_written: Store notes written; above ``saved`` in all-stores mode.
 */
export type NoteSaveResult = {
    saved: number;
    notes_written: number;
};

/**
 * Body of ``PATCH .../notes/{note_id}``: the new text.
 */
export type NoteUpdate = {
    body: string;
};

/**
 * Why the caller is being told: VentaHub's ticket notifications, and the AI Analyst's.
 *
 * ``ticket_mention`` wins over ``ticket_comment`` when a comment does both:
 * one comment is one notification, and being named is the stronger reason.
 * ``ai_analyst_ready`` and ``ai_analyst_failed`` say how an answer (a
 * snapshot, usually) the caller asked to be notified about ended.
 * ``ai_analyst_alert`` tells a reviewer the analyst is failing, getting
 * expensive or passing fewer eval questions in this organization.
 */
export type NotificationKind = 'ticket_assigned' | 'ticket_comment' | 'ticket_mention' | 'ai_analyst_ready' | 'ai_analyst_failed' | 'ai_analyst_alert';

/**
 * Why the caller is being told: VentaHub's ticket notifications, and the AI Analyst's.
 *
 * ``ticket_mention`` wins over ``ticket_comment`` when a comment does both:
 * one comment is one notification, and being named is the stronger reason.
 * ``ai_analyst_ready`` and ``ai_analyst_failed`` say how an answer (a
 * snapshot, usually) the caller asked to be notified about ended.
 * ``ai_analyst_alert`` tells a reviewer the analyst is failing, getting
 * expensive or passing fewer eval questions in this organization.
 */
export const NotificationKind = {
    TICKET_ASSIGNED: 'ticket_assigned',
    TICKET_COMMENT: 'ticket_comment',
    TICKET_MENTION: 'ticket_mention',
    AI_ANALYST_READY: 'ai_analyst_ready',
    AI_ANALYST_FAILED: 'ai_analyst_failed',
    AI_ANALYST_ALERT: 'ai_analyst_alert'
} as const;

/**
 * One notification.
 *
 * The text is the frontend's to write from these fields, so it is
 * translated and never shows a raw key or an id as the message.
 *
 * Attributes:
 * id: The event's id — stable, so read state keyed on it survives a
 * refetch.
 * kind: Why the caller is told.
 * occurred_at: When it happened.
 * ticket_id: The ticket, as its URL carries it; null off a ticket.
 * ticket_number: The ``#1234`` people quote; null off a ticket.
 * ticket_type_id: e.g. ``T15``; null off a ticket.
 * ticket_type_name: The type's name.
 * actor_name: Who commented, when known. VentaHub named the sender by
 * email; no name is better than an id.
 * excerpt: The start of the comment, for a comment or a mention; the
 * question, for an AI Analyst answer; what happened, for an alert.
 * title: An AI Analyst alert's title; null otherwise.
 * conversation_id: The AI Analyst conversation the answer is in, which
 * its link opens (``/ai-analyst/c/{conversation_id}``).
 */
export type NotificationRead = {
    id: string;
    kind: NotificationKind;
    occurred_at: string;
    ticket_id?: string | null;
    ticket_number?: number | null;
    ticket_type_id?: string | null;
    ticket_type_name?: string | null;
    actor_name?: string | null;
    excerpt?: string | null;
    conversation_id?: string | null;
    title?: string | null;
};

/**
 * ``clone`` in ``POST /v1/stores/onboarding``: settings copied onto every created store.
 *
 * Attributes:
 * from_store_id: The source store, in this tenant.
 * tags: Its tags.
 * groups: Its groups; a group the tenant lacks is created.
 * features: Its switches; only those off are written.
 * target_tacos: Its TACoS target.
 * monthly_sales_goal: Its monthly goal.
 * currency: The goal's currency; the new store's marketplace's when
 * omitted with a goal.
 * alert_route_ids: Routes scoped to the source store, each copied and
 * scoped to the new store with the same Slack webhook.
 */
export type OnboardingClone = {
    from_store_id: number;
    tags?: Array<string>;
    groups?: Array<CloneGroupWrite>;
    features?: Array<CloneFeatureWrite>;
    target_tacos?: number | string | null;
    monthly_sales_goal?: number | string | null;
    currency?: string | null;
    alert_route_ids?: Array<number>;
};

/**
 * One API to connect on every store being created.
 *
 * Attributes:
 * surface: Which API.
 * credentials: The values, per the surface's spec.
 * enabled: Switch it on in ``enabled_apis``.
 * issued_at: When the secret was issued.
 * expires_at: When it expires; ``None`` for no expiry.
 * expiry_source: Where that date came from.
 */
export type OnboardingConnection = {
    surface: AuthSurface;
    credentials: {
        [key: string]: string;
    };
    enabled?: boolean;
    issued_at?: string | null;
    expires_at?: string | null;
    expiry_source?: DeclaredExpirySource;
};

/**
 * Body of ``POST /v1/stores/onboarding``: 1..N stores, created together or not at all.
 *
 * Attributes:
 * kind: ``offline`` stores carry no credentials; ``online`` ones are
 * re-verified before anything is written.
 * stores: The stores.
 * connections: The APIs to connect on every one of them.
 * write_locks: Whether they start locked.
 * streams: The real-time streams every store is to receive, from the
 * marketplace's stream spec; ``enabled: false`` rows are saved as
 * off. Saved as desired state only; the response says
 * ``streams_pending`` and the caller asks for each store's streams
 * to be reconciled. An Ads stream is saved off for a store with no
 * Ads profile.
 * clone: What else to copy from an existing store onto every store
 * created (``GET /v1/stores/{id}/clone-template``).
 */
export type OnboardingRequest = {
    kind: StoreKind;
    stores: Array<OnboardingStore>;
    connections?: Array<OnboardingConnection>;
    write_locks?: WriteLockChoice;
    streams?: Array<StreamChoice>;
    clone?: OnboardingClone | null;
};

/**
 * The stores created, as the table will show them.
 *
 * Attributes:
 * stores: The stores.
 * streams_pending: Some stream was switched on: ask for each store's
 * streams to be reconciled (``POST /v1/stores/{id}/streams/reconcile``)
 * now, rather than wait for the daily run.
 */
export type OnboardingResult = {
    stores: Array<StoreOverview>;
    streams_pending?: boolean;
};

/**
 * One store to create. The registry's store fields, plus what onboarding knows.
 *
 * Attributes:
 * external_seller_id: The platform's account id. May be omitted (an
 * offline channel often has none); the store's ``code`` is then
 * stored in its place, because ``com_store.store_key`` is NOT NULL
 * and is part of the per-marketplace natural key.
 * ads_profile_id: The Amazon Ads profile picked for it.
 * ads_timezone: That profile's timezone.
 * expected_margin_rate: Defaults to the table's 0.25 when omitted.
 * expected_acos: Target ACoS, if any.
 * warehouse_ids: Warehouses it ships from, in this tenant.
 */
export type OnboardingStore = {
    code: string;
    name: string;
    /**
     * Deprecated, for one release: use brand_ids. A brand id is read as brand_ids [id], null as [].
     * @deprecated
     */
    brand_id?: number | null;
    marketplace_id: number;
    /**
     * The brands it sells, a set: replaces the stored one. [] for none.
     */
    brand_ids?: Array<number>;
    external_seller_id?: string | null;
    store_type: string;
    is_retail: boolean;
    ads_profile_id?: number | null;
    ads_timezone?: string | null;
    expected_margin_rate?: number | string | null;
    expected_acos?: number | string | null;
    warehouse_ids?: Array<number>;
};

/**
 * What the filtered orders add up to, every status counted.
 *
 * Attributes:
 * orders: Orders.
 * units: Units ordered.
 * statuses: Orders per status group.
 * cancelled: Cancelled orders.
 * cancellation_rate: Cancelled over all, a fraction.
 * money: Revenue per currency, most orders first.
 * daily: Orders and units per day and status group.
 * timing: Purchase to last update, per status reached.
 * top_products: VentaHub's "80% of sales", by units.
 * top_categories: The same, by category.
 * cancelled_by_category: Cancelled orders per category, most first.
 * order_types: The order types in scope, most orders first: the filter's choices.
 * destinations: Orders per ship-to country, most first.
 * cities: Orders per ship-to city, where named, most first.
 */
export type OrderAnalytics = {
    orders: number;
    units: number;
    statuses: {
        [key: string]: number;
    };
    cancelled: number;
    cancellation_rate: string | null;
    money: Array<CurrencyMoney>;
    daily: Array<DayStatus>;
    timing: Array<StatusTiming>;
    top_products: ParetoProducts;
    top_categories: ParetoCategories;
    cancelled_by_category: Array<CategoryCount>;
    order_types: Array<OrderTypeCount>;
    destinations: Array<Destination>;
    cities: Array<CityCount>;
};

/**
 * One order, as the list shows it. No buyer details.
 */
export type OrderBookRow = {
    id: number;
    order_id: string;
    purchase_date: string | null;
    store_id: number;
    store_name: string | null;
    country_code: string | null;
    /**
     * The marketplace's own status
     */
    status: string | null;
    status_group: 'pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other';
    /**
     * FBA for marketplace-fulfilled, FBM for seller
     */
    fulfilment: string | null;
    currency_code: string | null;
    amount_total: string | null;
    lines: number;
    units: number;
    first_sku: string | null;
    first_title: string | null;
    /**
     * The marketplace's own last change to the order
     */
    last_update_date?: string | null;
    marketplace_name?: string | null;
    marketplace_code?: string | null;
    /**
     * Where the address names a city
     */
    ship_city?: string | null;
    /**
     * ISO alpha-2: the address's country, else the store's marketplace's
     */
    ship_country?: string | null;
};

/**
 * Body of ``POST /v1/orders``.
 *
 * Attributes:
 * external_id: The marketplace's identifier for this order.
 * marketplace: Where it came from.
 * total: Amount and currency.
 * occurred_at: When the buyer placed it, per the marketplace. Required,
 * not defaulted — defaulting to now would silently record ingestion
 * time as business time, which is the exact confusion the column
 * exists to prevent.
 * status: Initial status.
 * buyer_reference: Pseudonymous marketplace-side reference. Never a name,
 * address or email.
 */
export type OrderCreate = {
    external_id: string;
    marketplace: string;
    total: MoneyWrite;
    occurred_at: string;
    status?: OrderStatus;
    buyer_reference?: string | null;
};

/**
 * One order with its lines. No buyer name or address.
 */
export type OrderDetail = {
    order: OrderBookRow;
    secondary_ref: string | null;
    order_type: string | null;
    shipping_method: string | null;
    amount_shipping: string | null;
    amount_tax: string | null;
    amount_discount: string | null;
    last_update_date: string | null;
    lines: Array<OrderLine>;
};

/**
 * One line of an order.
 */
export type OrderLine = {
    id: number;
    sku_code: string | null;
    /**
     * ASIN or the marketplace's product id
     */
    product_ref: string | null;
    title: string | null;
    /**
     * The listing's main image
     */
    image_url?: string | null;
    quantity_ordered: number | null;
    quantity_shipped: number | null;
    quantity_cancelled: number | null;
    status: string | null;
    currency_code: string | null;
    item_price: string | null;
    amount_tax: string | null;
    amount_shipping: string | null;
    amount_discount: string | null;
};

/**
 * One order line, as the Orders table shows it. No buyer details.
 */
export type OrderLineRow = {
    id: number;
    /**
     * The order this line belongs to, for its panel
     */
    order_pk: number;
    order_id: string;
    order_item_id: string | null;
    /**
     * ASIN or the marketplace's product id
     */
    asin: string | null;
    sku_code: string | null;
    category: string | null;
    subcategory: string | null;
    brand: string | null;
    purchase_date: string | null;
    last_update_date: string | null;
    order_status: string | null;
    status_group: 'pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other';
    is_fba: boolean | null;
    order_type: string | null;
    quantity_ordered: number | null;
    quantity_shipped: number | null;
    currency_code: string | null;
    item_price: string | null;
    amount_tax: string | null;
    amount_shipping: string | null;
    amount_discount: string | null;
    buying_cost: string | null;
    store_id: number;
    store_name: string | null;
    marketplace_name: string | null;
    marketplace_code: string | null;
};

/**
 * An order as returned by the API.
 *
 * Attributes:
 * id: ULID.
 * created_at: When we ingested it.
 * updated_at: Last change here.
 * occurred_at: When it happened, per the marketplace. The field every
 * report keys off.
 * external_id: Marketplace identifier.
 * marketplace: Source.
 * status: Current status.
 * total: Amount and currency.
 * buyer_reference: Pseudonymous reference.
 */
export type OrderRead = {
    id: string;
    created_at: string;
    updated_at: string;
    occurred_at: string;
    external_id: string;
    marketplace: string;
    status: OrderStatus;
    total: MoneyRead;
    buyer_reference: string | null;
};

/**
 * One order line.
 */
export type OrderRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    order_id: string;
    order_item_id: string | null;
    asin: string | null;
    sku_code: string | null;
    purchase_date: string | null;
    last_update_date: string | null;
    order_status: string | null;
    order_type: string | null;
    is_fba: boolean | null;
    quantity_ordered: number | null;
    quantity_shipped: number | null;
    quantity_cancelled: number | null;
    item_price: string | null;
    buying_cost: string | null;
    import_cost: string | null;
    currency_code: string | null;
    amount_tax: string | null;
    amount_shipping: string | null;
    amount_discount: string | null;
};

/**
 * Where an order is in its life.
 */
export type OrderStatus = 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';

/**
 * Where an order is in its life.
 */
export const OrderStatus = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled',
    REFUNDED: 'refunded'
} as const;

/**
 * A store orders can be uploaded for, with its marketplace's template.
 */
export type OrderStore = {
    id: number;
    name: string;
    marketplace: string;
    country_code: string | null;
    template: string;
    template_columns: Array<string>;
    product_header: string;
};

/**
 * Orders of one type, as the marketplace names it (lower case).
 */
export type OrderTypeCount = {
    order_type: string;
    orders: number;
};

/**
 * Body of ``PATCH /v1/orders/{id}``.
 *
 * Only the status moves. Amounts and identifiers are facts from the
 * marketplace, and letting an API caller edit them would make the table
 * disagree with its source with no record of who changed what.
 *
 * Attributes:
 * status: New status, if changing.
 */
export type OrderUpdate = {
    status?: OrderStatus | null;
};

/**
 * A vendor, in one currency, that quotes every SKU asked about.
 *
 * Attributes:
 * vendor_id: The vendor.
 * vendor_name: Its name.
 * currency_code: The currency of its quotes.
 * items: Its cost for each SKU, in SKU order.
 */
export type OrderVendor = {
    vendor_id: number;
    vendor_name: string | null;
    currency_code: string;
    items: Array<OrderVendorItem>;
};

/**
 * What one vendor charges for one SKU.
 *
 * Attributes:
 * sku_id: The SKU.
 * sku_code: Its code.
 * item_cost: The vendor's unit cost for it at the warehouse.
 */
export type OrderVendorItem = {
    sku_id: number;
    sku_code: string | null;
    item_cost: MoneyRead;
};

/**
 * Who can supply the SKUs in one order, and which SKUs nobody prices.
 *
 * Attributes:
 * vendors: The vendors, by name, that quote all of them in one currency.
 * skus_without_cost: The SKUs without a valid vendor cost at the warehouse.
 */
export type OrderVendors = {
    vendors: Array<OrderVendor>;
    skus_without_cost: Array<UnpricedSku>;
};

/**
 * The SKUs a vendor order from the forecast would hold, at one warehouse.
 */
export type OrderVendorsRequest = {
    warehouse_id: number;
    sku_ids: Array<number>;
};

/**
 * Orders in the summary window, for one currency.
 */
export type OrdersSummary = {
    currency_code: string | null;
    orders: number;
    units: number;
    revenue: string;
};

/**
 * A window of TikTok order updates to pull.
 */
export type OrdersSyncRequest = {
    /**
     * Inclusive, with a UTC offset
     */
    start: string;
    /**
     * Exclusive, with a UTC offset; at most 31 days after start
     */
    end: string;
};

/**
 * An organization as returned by the API.
 *
 * Overrides ``id`` to an integer: the shared base assumes a ULID, and this is
 * the one resource whose key is a tenant id issued by another service.
 *
 * Attributes:
 * id: The tenant id.
 * created_at: Insert time, UTC.
 * updated_at: Last change, UTC.
 * name: Display name.
 * slug: URL-safe identifier.
 * is_active: Whether the organization is usable.
 * reporting_currency: The ISO 4217 currency its reports and dashboard
 * convert into: the one chosen, else ``USD``. Never null.
 */
export type OrganizationRead = {
    id: number;
    created_at: string;
    updated_at: string;
    name: string;
    slug: string;
    is_active: boolean;
    reporting_currency: string;
};

/**
 * What may be changed about an organization here.
 *
 * Attributes:
 * reporting_currency: The ISO 4217 currency to convert into, three
 * upper-case letters. ``null`` goes back to the default (``USD``);
 * leaving the field out leaves it as it is.
 */
export type OrganizationUpdate = {
    reporting_currency?: string | null;
};

/**
 * The listing as Amazon has it.
 *
 * Attributes:
 * asin: The product.
 * title: Its title.
 * images: Its image URLs, on Amazon's CDN.
 * bullet_points: Its bullets.
 * description: Its description.
 */
export type OriginalListingRead = {
    asin: string;
    title: string;
    images: Array<string>;
    bullet_points: Array<string>;
    description: string;
};

/**
 * One row of an Otto ads report, with VentaHub's column names.
 *
 * Attributes:
 * row: Its row number in the sheet, so it can be found in the file.
 */
export type OttoRow = {
    row: number;
    kampagne?: string | null;
    type?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    negative_keyword_typ?: string | null;
    normal_keyword_typ?: string | null;
    produkttitel?: string | null;
    keyword?: string | null;
    sku?: string | null;
    ausgaben?: string | null;
    cpc?: string | null;
    impressionen?: string | null;
    klicks?: string | null;
    ctr?: string | null;
    abgeschickte_warenkoerbe?: string | null;
    verkaeufe?: string | null;
    umsatz?: string | null;
    roas?: string | null;
    kur_acos?: string | null;
    cpo?: string | null;
    cvr?: string | null;
};

/**
 * Clarifying questions asked, and whether the person answered them.
 */
export type OverviewClarifying = {
    asked: number;
    answered: number;
    abandoned: number;
};

/**
 * One UTC day of runs.
 */
export type OverviewDay = {
    day: string;
    runs: number;
    succeeded: number;
    failed: number;
    cancelled: number;
    flagged: number;
    cost_usd: number;
    p50_seconds: number | null;
    p95_seconds: number | null;
};

/**
 * Deep analyses offered on fast answers, and how many were taken.
 */
export type OverviewDeepSuggestions = {
    shown: number;
    accepted: number;
};

/**
 * Succeeded runs at one depth: ``fast``, ``deep`` or ``snapshot``.
 */
export type OverviewDepth = {
    depth: string;
    runs: number;
    p50_seconds: number | null;
    p95_seconds: number | null;
    mean_cost_usd: number;
};

/**
 * Ratings and signals on the period's runs.
 */
export type OverviewFeedback = {
    thumbs_up: number;
    thumbs_down: number;
    signals: {
        [key: string]: number;
    };
};

/**
 * Runs sharing a prompt version or a variant, and how they went.
 */
export type OverviewGroup = {
    key: string;
    runs: number;
    success_pct: number | null;
    flag_pct: number | null;
    thumbs_down_pct: number | null;
    mean_cost_usd: number;
};

/**
 * Flags resolved in the period, and how many of their runs became eval questions.
 */
export type OverviewResolvedFlags = {
    resolved: number;
    with_eval: number;
    with_eval_pct: number | null;
};

/**
 * The period's totals, added up here so the browser never sums.
 */
export type OverviewTotals = {
    runs: number;
    succeeded_pct: number | null;
    cost_usd: number;
};

/**
 * The packing mail that was queued.
 *
 * Attributes:
 * block_id: The block whose lines are attached.
 * po_code: The reference code the mail names.
 * recipients: Who it goes to.
 * line_count: Lines on the attached sheet.
 */
export type PackingMailRead = {
    block_id: number;
    po_code: string | null;
    recipients: Array<string>;
    line_count: number;
};

/**
 * Who the packing team is, for one mail.
 *
 * Attributes:
 * recipients: The addresses, typed on the page as VentaHub's were.
 */
export type PackingMailWrite = {
    recipients: Array<string>;
};

/**
 * One SKU in one box of the invoice, for packing.
 */
export type PackingRowRead = {
    box_code: string;
    sku_code: string | null;
    sku_name: string | null;
    retailer_sku: string | null;
    quantity: number;
};

/**
 * What a client needs to render a pager.
 *
 * Attributes:
 * page: The page this response represents.
 * limit: Rows per page requested.
 * total: Total matching rows, ignoring pagination.
 * pages: Total number of pages, at least 1 so an empty list still
 * renders a pager rather than dividing by zero in the client.
 */
export type Pagination = {
    page: number;
    limit: number;
    total: number;
    pages: number;
};

/**
 * The JSON type of a stream parameter.
 */
export type ParamType = 'integer' | 'string';

/**
 * The JSON type of a stream parameter.
 */
export const ParamType = {
    INTEGER: 'integer',
    STRING: 'string'
} as const;

/**
 * The categories down to the one that brings the running share to 80%.
 */
export type ParetoCategories = {
    items: Array<ParetoCategory>;
    total_units: number;
    to_eighty: number | null;
};

/**
 * A category among the few bringing most of the units sold.
 */
export type ParetoCategory = {
    category: string;
    subcategory: string | null;
    units: number;
    orders: number;
    cumulative_share: string;
};

/**
 * A product among the few bringing most of the units sold.
 */
export type ParetoProduct = {
    /**
     * ASIN or the marketplace's product id
     */
    product_ref: string | null;
    sku_code: string | null;
    title: string | null;
    image_url: string | null;
    category: string | null;
    units: number;
    orders: number;
    /**
     * Its share and every larger one's, a fraction
     */
    cumulative_share: string;
};

/**
 * The products down to the one that brings the running share to 80%.
 */
export type ParetoProducts = {
    items: Array<ParetoProduct>;
    total_units: number;
    /**
     * How many of them make 80%; null under it
     */
    to_eighty: number | null;
};

/**
 * How fresh the report is and what it covers.
 *
 * Attributes:
 * built_at: When the marts were last built together; ``null`` if never.
 * days: How many days the range holds.
 */
export type PaymentReconciliationMeta = {
    built_at: string | null;
    days: number;
};

/**
 * A page of order lines, with the totals over every matching line.
 *
 * Attributes:
 * items: The lines of this page.
 * pagination: Page metadata; ``total`` counts lines.
 * totals: Over every line the filters match.
 * meta: How fresh the figures are.
 */
export type PaymentReconciliationReport = {
    items: Array<PaymentReconciliationRow>;
    pagination: Pagination;
    totals: PaymentReconciliationTotals;
    meta: PaymentReconciliationMeta;
};

/**
 * One order line and what the marketplace paid for it.
 *
 * Attributes:
 * order_item_id: The order line.
 * order_id: Its order.
 * platform_order_id: The marketplace's order number.
 * order_date: The day of the order, in the marketplace's zone.
 * order_status: The order's status class: fulfilled, returned, open, undelivered.
 * store_id: The store that took the order.
 * store_name: Its name.
 * marketplace_id: The marketplace.
 * marketplace_code: Its short code.
 * listed_product_id: The listing sold.
 * product_ref: The listing's ASIN or marketplace product id.
 * seller_sku: The listing's seller SKU.
 * sku_id: The SKU, when the listing is mapped to one.
 * sku_code: Its code.
 * sku_name: Its name.
 * brand_name: Its brand.
 * currency_code: The currency every amount of the row is in.
 * original_quantity: Units ordered.
 * returned_quantity: Units returned: every unit when the order was returned.
 * original_item_price: The line price the order was placed at.
 * original_tax: The tax on the line, where the order carries it.
 * settlement_id: The marketplace's id of the settlement that paid the
 * line; the latest, when several did.
 * settlement_date: The day it was paid, the latest.
 * settlement_lines: How many cash settlement lines are linked to the order line.
 * received_amount: Principal paid on the sale.
 * received_tax: Tax paid on the sale.
 * received_shipping: Shipping paid on the sale.
 * commission: The marketplace's commission on the sale, signed as taken.
 * fee: Its other fees on the sale, signed as taken.
 * refund_amount: Money moved on refund lines, signed.
 * actual_net_received: Every sale line's net, together.
 * expected_net_amount: The line price for the units kept.
 * discrepancy: Expected less the principal received: what the
 * marketplace has yet to pay for the line, or overpaid when negative.
 * reconciliation_status: Where the line stands.
 * buying_cost: Units times landed cost; ``null`` with no known cost.
 * margin: What the line earned less its buying cost — the net paid
 * once settled, the expected net until then; ``null`` with no cost.
 * margin_pct: The margin over the line price, a fraction (0.3009 is
 * 30.09%); ``null`` with no cost or no price.
 * replen: The SKU's replenishment class at the store's warehouses:
 * R (replenished), N (not) or T (trial); ``null`` with none.
 */
export type PaymentReconciliationRow = {
    order_item_id: number;
    order_id: number;
    platform_order_id: string | null;
    order_date: string | null;
    order_status: string;
    store_id: number;
    store_name: string | null;
    marketplace_id: number;
    marketplace_code: string;
    listed_product_id: number | null;
    product_ref: string | null;
    seller_sku: string | null;
    sku_id: number | null;
    sku_code: string | null;
    sku_name: string | null;
    brand_name: string | null;
    currency_code: string;
    original_quantity: number;
    returned_quantity: number;
    original_item_price: MoneyRead;
    original_tax: MoneyRead | null;
    settlement_id: string | null;
    settlement_date: string | null;
    settlement_lines: number;
    received_amount: MoneyRead;
    received_tax: MoneyRead;
    received_shipping: MoneyRead;
    commission: MoneyRead;
    fee: MoneyRead;
    refund_amount: MoneyRead;
    actual_net_received: MoneyRead;
    expected_net_amount: MoneyRead;
    discrepancy: MoneyRead;
    reconciliation_status: ReconciliationStatus;
    buying_cost: MoneyRead | null;
    margin: MoneyRead | null;
    margin_pct: string | null;
    replen: string | null;
};

/**
 * The figures over every order line the filters match, not the page.
 *
 * Attributes:
 * line_count: Order lines.
 * order_count: Distinct orders.
 * matched: Lines whose principal was paid in full.
 * unmatched: Lines no settlement line is linked to.
 * partially_matched: Lines paid, but not the line price.
 * returned: Lines returned or refunded.
 * original_amount: The line prices, per currency.
 * expected_amount: The line prices for the units kept, per currency.
 * received_amount: Principal paid, per currency.
 * discrepancy: Expected less received, per currency.
 * actual_net_received: Every settlement line's net, per currency.
 * buying_cost: The lines' known buying cost, per currency.
 * margin: The lines' margin, over those with a known cost, per currency.
 */
export type PaymentReconciliationTotals = {
    line_count: number;
    order_count: number;
    matched: number;
    unmatched: number;
    partially_matched: number;
    returned: number;
    original_amount: Array<MoneyRead>;
    expected_amount: Array<MoneyRead>;
    received_amount: Array<MoneyRead>;
    discrepancy: Array<MoneyRead>;
    actual_net_received: Array<MoneyRead>;
    buying_cost: Array<MoneyRead>;
    margin: Array<MoneyRead>;
};

/**
 * What a performance row is about.
 *
 * Brand, category and subcategory fold ad groups by what the master says
 * about them; SKU folds ads by the catalogue SKU behind them. An ad group
 * or ad with nothing recorded lands in one ``Unassigned`` row.
 */
export type PerformanceLevel = 'ad_group' | 'campaign' | 'brand' | 'category' | 'subcategory' | 'asin' | 'sku' | 'target' | 'search_term' | 'alt_product';

/**
 * What a performance row is about.
 *
 * Brand, category and subcategory fold ad groups by what the master says
 * about them; SKU folds ads by the catalogue SKU behind them. An ad group
 * or ad with nothing recorded lands in one ``Unassigned`` row.
 */
export const PerformanceLevel = {
    AD_GROUP: 'ad_group',
    CAMPAIGN: 'campaign',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    ASIN: 'asin',
    SKU: 'sku',
    TARGET: 'target',
    SEARCH_TERM: 'search_term',
    ALT_PRODUCT: 'alt_product'
} as const;

/**
 * What an uploaded report holds. Nothing is saved.
 *
 * Attributes:
 * channel: Which shape it was read as — the store's, not guessed.
 * currency: The store's currency, for the money columns.
 * otto: The rows, for an Otto store; empty otherwise.
 * noon: The rows, for a Noon store; empty otherwise.
 * truncated: More rows than the preview returns were in the file.
 * unrecognized_headers: Header cells that matched no known column, so
 * a misspelled header shows up rather than silently emptying a column.
 */
export type PerformanceMasterPreview = {
    channel: Channel;
    currency: string;
    otto: Array<OttoRow>;
    noon: Array<NoonRow>;
    truncated: boolean;
    unrecognized_headers: Array<string>;
};

/**
 * An Otto or Noon store.
 *
 * Attributes:
 * store_id: ``com_store.pk_store_id``.
 * store_name: Display name.
 * marketplace: The marketplace's name.
 * short_code: e.g. ``OTDE``, ``NNAE``.
 * country_code: The marketplace's country, ISO 3166-1 alpha-2.
 * currency: The marketplace's currency.
 * channel: Which template and columns the store uses.
 */
export type PerformanceMasterStore = {
    store_id: number;
    store_name: string;
    marketplace: string;
    short_code: string;
    country_code: string;
    currency: string;
    channel: Channel;
};

/**
 * One period's numbers for one row.
 *
 * Rates are computed from the period's sums, never by averaging daily rates.
 * Each is null when its denominator is zero, which is not the same as zero.
 *
 * Attributes:
 * impressions: Impressions.
 * clicks: Clicks.
 * spend: Spend, in the row's currency.
 * units: Units sold.
 * sales: Sales, in the row's currency.
 * ctr: clicks / impressions.
 * cpc: spend / clicks, as a decimal string.
 * conversion: units / clicks.
 * acos: spend / sales.
 * roas: sales / spend.
 * asp: sales / units, as a decimal string.
 * data_days: Days with any data — zero means the row has no data in the
 * period at all, which the page shows as dashes rather than zeros.
 */
export type PerformanceMetrics = {
    impressions: number;
    clicks: number;
    spend: string;
    units: number;
    sales: string;
    ctr: number | null;
    cpc: string | null;
    conversion: number | null;
    acos: number | null;
    roas: number | null;
    asp: string | null;
    data_days: number;
};

/**
 * A page of performance rows, with totals over all of them.
 *
 * Attributes:
 * level: What the rows are.
 * totals: One entry per currency — stores in different currencies are
 * never summed together.
 */
export type PerformancePage = {
    items: Array<PerformanceRow>;
    pagination: Pagination;
    level: PerformanceLevel;
    totals: Array<PerformanceTotals>;
};

/**
 * One entity's performance, in one or two periods.
 *
 * Attributes:
 * key: Stable identity within the level, for row keys and links.
 * name: What to call it — ad group, campaign, brand, category,
 * subcategory, ASIN, SKU, target, search term or purchased ASIN.
 * detail: The second line — the campaign, SKUs, ASINs, target type,
 * keywords or advertised ASINs.
 * store_id: Which store.
 * store_name: Its name.
 * currency: The store's currency; every money field on the row uses it.
 * ad_group_count: How many ad groups the row spans, in the current period.
 * ad_group_pk: For an ad-group row, its id in the master.
 * ad_group_id: For an ad-group row, Amazon's id.
 * campaign_id: Amazon's campaign id, for ad-group and campaign rows.
 * status: The ad group's or campaign's state.
 * campaign_status: For an ad-group row, its campaign's state.
 * ad_group_type: SP, SB or SD, for an ad-group row.
 * targeting_type: For an ad-group row.
 * budget: For an ad-group row, its daily budget.
 * target_acos: For an ad-group row, a fraction.
 * target_ctr: For an ad-group row, a fraction.
 * target_conversion: For an ad-group row, a fraction.
 * target_asp: For an ad-group row.
 * brand_name: For an ad-group row, the brand the master assigns it.
 * category: For an ad-group row, from the master.
 * subcategory: For an ad-group row, from the master.
 * bid_min: For a target row, the lowest bid seen in the period.
 * bid_max: The highest.
 * data_refreshed_at: When the row's data last changed.
 * current: The current period.
 * previous: The comparison period, when one was asked for.
 */
export type PerformanceRow = {
    key: string;
    name: string;
    detail: string | null;
    store_id: number;
    store_name: string;
    currency: string;
    ad_group_count: number;
    ad_group_pk?: number | null;
    ad_group_id?: string | null;
    campaign_id?: string | null;
    status?: string | null;
    campaign_status?: string | null;
    ad_group_type?: string | null;
    targeting_type?: string | null;
    budget?: string | null;
    target_acos?: number | null;
    target_ctr?: number | null;
    target_conversion?: number | null;
    target_asp?: string | null;
    brand_name?: string | null;
    category?: string | null;
    subcategory?: string | null;
    bid_min?: string | null;
    bid_max?: string | null;
    data_refreshed_at?: string | null;
    current: PerformanceMetrics;
    previous?: PerformanceMetrics | null;
};

/**
 * What performance rows can be ordered by — the current period's values.
 */
export type PerformanceSort = 'name' | 'impressions' | 'clicks' | 'spend' | 'units' | 'sales' | 'ctr' | 'cpc' | 'conversion' | 'acos';

/**
 * What performance rows can be ordered by — the current period's values.
 */
export const PerformanceSort = {
    NAME: 'name',
    IMPRESSIONS: 'impressions',
    CLICKS: 'clicks',
    SPEND: 'spend',
    UNITS: 'units',
    SALES: 'sales',
    CTR: 'ctr',
    CPC: 'cpc',
    CONVERSION: 'conversion',
    ACOS: 'acos'
} as const;

/**
 * Totals for one currency across every matching row.
 *
 * Attributes:
 * currency: The currency.
 * current: The current period.
 * previous: The comparison period, when one was asked for.
 */
export type PerformanceTotals = {
    currency: string;
    current: PerformanceMetrics;
    previous?: PerformanceMetrics | null;
};

/**
 * The period a saved report covers: the analyst's label and the pinned dates, if any.
 *
 * Attributes:
 * label: How the snapshot named it, e.g. "1-30 Sep 2026 vs Aug".
 * date_from: The pinned start (``from`` on the wire, as VentaHub stored it).
 * date_to: The pinned end (``to``).
 */
export type Period = {
    label?: string | null;
    from?: string | null;
    to?: string | null;
};

/**
 * A week or a month, both ends inclusive.
 *
 * Attributes:
 * start: The Sunday, or the 1st.
 * end: The Saturday, or the month's last day.
 */
export type PeriodRead = {
    start: string;
    end: string;
};

/**
 * Units of one SKU a warehouse would supply.
 */
export type PlanItemRead = {
    sku_id: number;
    sku_code: string;
    units: number;
};

/**
 * What Create PO would allocate and block at one warehouse.
 */
export type PlanWarehouseRead = {
    warehouse: PoWarehouseRead;
    items: Array<PlanItemRead>;
};

/**
 * An event calendar row: every SKU sells ``uplift`` times a normal day in it.
 *
 * Attributes:
 * event: Its name.
 * marketplace: The marketplace it is for, as written. Shown, not applied:
 * the uplift applies to every SKU, as VentaHub applies it.
 * region: Its region, as written; shown, not applied.
 * window_start: Its first day.
 * window_end: Its last day.
 * uplift: The multiplier, 1.8 for 80% more than a normal day.
 */
export type PlannedEvent = {
    event: string;
    marketplace?: string | null;
    region?: string | null;
    window_start: string;
    window_end: string;
    uplift: number;
};

/**
 * A change to one SKU's ad budget.
 *
 * Attributes:
 * sku_id: The SKU.
 * sku_code: Its code, as matched.
 * new_budget: The window's new budget.
 * old_budget: The window's budget before.
 * ams_share: The percent of the SKU's demand its ads drive, 0 to 100.
 * window_start: Its first day.
 * window_end: Its last day.
 */
export type PlannedMarketing = {
    sku_id: number;
    sku_code: string;
    new_budget: number;
    old_budget: number;
    ams_share: number;
    window_start: string;
    window_end: string;
};

/**
 * A planned price cut on one SKU.
 *
 * Attributes:
 * sku_id: The SKU.
 * sku_code: Its code, as matched.
 * discount: The cut, a percent from 1 to 99.
 * window_start: Its first day.
 * window_end: Its last day.
 */
export type PlannedPriceChange = {
    sku_id: number;
    sku_code: string;
    discount: number;
    window_start: string;
    window_end: string;
};

/**
 * Why a row of an upload was refused.
 *
 * Attributes:
 * row: Its row in the file, the header being row 1.
 * sku: The SKU it names, as written.
 * message: What is wrong with it.
 */
export type PlanningError = {
    row: number;
    sku: string | null;
    message: string;
};

/**
 * The caller's uploads, applied to this run only.
 */
export type PlanningInput = {
    events?: Array<PlannedEvent>;
    price_changes?: Array<PlannedPriceChange>;
    marketing_plans?: Array<PlannedMarketing>;
};

/**
 * The three uploads: the event calendar, price changes and the marketing plan.
 */
export type PlanningKind = 'event-calendar' | 'price-changes' | 'marketing-plan';

/**
 * The three uploads: the event calendar, price changes and the marketing plan.
 */
export const PlanningKind = {
    EVENT_CALENDAR: 'event-calendar',
    PRICE_CHANGES: 'price-changes',
    MARKETING_PLAN: 'marketing-plan'
} as const;

/**
 * An upload, read: its rows when every one is good, else why not.
 *
 * All or nothing: a file with any bad row loads nothing.
 *
 * Attributes:
 * kind: Which upload.
 * events: The event calendar's rows.
 * price_changes: The price changes' rows.
 * marketing_plans: The marketing plan's rows.
 * errors: The bad rows, the first 20.
 * error_count: How many rows were bad.
 */
export type PlanningUpload = {
    kind: PlanningKind;
    events?: Array<PlannedEvent>;
    price_changes?: Array<PlannedPriceChange>;
    marketing_plans?: Array<PlannedMarketing>;
    errors?: Array<PlanningError>;
    error_count?: number;
};

/**
 * One platform tab of the store table.
 *
 * Attributes:
 * family: The channel family, ``left(short_code, 2)`` - what the
 * overview's ``family`` filter takes.
 * platform: The platform's name (``amazon``, ``walmart``); ``offline``
 * for a family no connector exists for.
 * online: Whether stores on it can be connected to an API.
 * count: This organization's stores on it.
 */
export type PlatformCount = {
    family: string;
    platform: string;
    online: boolean;
    count: number;
};

/**
 * Every platform this organization has a store on, most stores first.
 *
 * Attributes:
 * items: One per channel family.
 * total: All stores, across every platform - the "All" tab.
 */
export type PlatformCounts = {
    items: Array<PlatformCount>;
    total: number;
};

/**
 * ``GET /v1/platform/status``.
 *
 * Attributes:
 * scope_graph: Publishing access scopes to VentaWiz-Auth.
 * jobs: The background job queue.
 * checked_at: When this was read.
 */
export type PlatformStatus = {
    scope_graph: ScopeGraphStatus;
    jobs: JobsStatus;
    checked_at: string;
};

/**
 * What the P&L can be grouped by.
 */
export type PnlGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'retailer' | 'channel' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'day' | 'week' | 'month';

/**
 * What the P&L can be grouped by.
 */
export const PnlGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    RETAILER: 'retailer',
    CHANNEL: 'channel',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * The P&L, grouped.
 */
export type PnlReport = {
    items: Array<PnlRow>;
    pagination: Pagination;
    totals: Array<PnlTotals>;
    meta: ReportMeta;
};

/**
 * One group of the P&L.
 */
export type PnlRow = {
    month?: string | null;
    week?: string | null;
    day?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    channel?: string | null;
    retailer_id?: number | null;
    retailer_name?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    line_count?: number | null;
    units?: number | null;
    sales_amount?: MoneyRead | null;
    returned_sales_amount?: MoneyRead | null;
    vat_amount?: MoneyRead | null;
    net_sales_amount?: MoneyRead | null;
    settled_amount?: MoneyRead | null;
    expected_amount?: MoneyRead | null;
    receipts_amount?: MoneyRead | null;
    marketplace_charges_amount?: MoneyRead | null;
    cogs_amount?: MoneyRead | null;
    ppp_amount?: MoneyRead | null;
    ad_spend_amount?: MoneyRead | null;
    cp_amount?: MoneyRead | null;
    margin_base_amount?: MoneyRead | null;
    cp_margin?: string | null;
    excluded_line_count?: number | null;
    excluded_sales_amount?: MoneyRead | null;
};

/**
 * The P&L over every matching line and ad, in one currency.
 *
 * A marketplace line is dated by its order, an invoice line by its invoice,
 * and ad spend by its report day. Lines without a known profit (no cost, a
 * cost that is an entry error, payments far above the sale, no VAT rate)
 * are counted beside the profit and left out of both its sums.
 *
 * Attributes:
 * currency_code: The order or invoice currency; ad spend is in its
 * store's marketplace currency.
 * line_count: Order and invoice lines.
 * units: Units sold.
 * sales_amount: Sales, as the sales reports count them.
 * returned_sales_amount: What customers were given back.
 * vat_amount: VAT inside marketplace sales; invoices exclude it.
 * net_sales_amount: Sales less returns and VAT.
 * settled_amount: What marketplaces have paid for these lines.
 * expected_amount: What they are expected to pay for the unpaid ones.
 * receipts_amount: Settled plus expected.
 * marketplace_charges_amount: Receipts less what customers paid: fees,
 * promotions, shipping credits (mostly negative).
 * cogs_amount: Units times landed cost.
 * ppp_amount: Profit before advertising, over the lines with a known
 * profit.
 * ad_spend_amount: Advertising, in its store's marketplace currency.
 * Spend not resolved to a SKU is a row of its own when grouped by
 * SKU, listing, brand or category: no SKU, no sales, a loss.
 * cp_amount: PPP less ad spend. ``null`` for a group whose lines all
 * lack a known profit, never 0; minus the spend for a group of ad
 * spend alone.
 * margin_base_amount: Net sales of the lines with a known profit — the
 * margin's denominator.
 * cp_margin: CP ÷ margin base, a fraction (0.3009 is 30.09%).
 * excluded_line_count: Lines without a known profit.
 * excluded_sales_amount: Their sales.
 */
export type PnlTotals = {
    currency_code?: string | null;
    line_count?: number | null;
    units?: number | null;
    sales_amount?: MoneyRead | null;
    returned_sales_amount?: MoneyRead | null;
    vat_amount?: MoneyRead | null;
    net_sales_amount?: MoneyRead | null;
    settled_amount?: MoneyRead | null;
    expected_amount?: MoneyRead | null;
    receipts_amount?: MoneyRead | null;
    marketplace_charges_amount?: MoneyRead | null;
    cogs_amount?: MoneyRead | null;
    ppp_amount?: MoneyRead | null;
    ad_spend_amount?: MoneyRead | null;
    cp_amount?: MoneyRead | null;
    margin_base_amount?: MoneyRead | null;
    cp_margin?: string | null;
    excluded_line_count?: number | null;
    excluded_sales_amount?: MoneyRead | null;
};

/**
 * One entry for a PO picker.
 *
 * Attributes:
 * id: ``pk_po_id``.
 * po_code: The retailer's PO number.
 * retailer_id: Who ordered.
 * currency_code: ISO 4217.
 */
export type PoCodeRead = {
    id: number;
    po_code: string;
    retailer_id: number | null;
    currency_code: string | null;
};

/**
 * One PO and where it stands.
 *
 * Attributes:
 * po_date: The PO date, or when the PO was recorded if it has none.
 */
export type PoFulfilmentRead = {
    ordered: number;
    confirmed: number;
    blocked: number;
    allotted: number;
    invoiced: number;
    dispatched: number;
    delivered: number;
    open: number;
    value_ordered: string | null;
    value_invoiced: string | null;
    fill_rate: number | null;
    confirm_rate: number | null;
    delivery_rate: number | null;
    currency: string | null;
    id: number;
    po_code: string;
    po_date: string | null;
    valid_till: string | null;
    warehouse_id: number | null;
    warehouse: string | null;
    retailer_id: number | null;
    retailer: string | null;
    lines: number;
    status: 'not_started' | 'in_progress' | 'invoiced' | 'delivered' | 'expired';
};

/**
 * How a box reached the PO the list names.
 *
 * The two look alike in a PO column and are not: only ``direct`` is the
 * mapping a PO's box mapping sets and clears; ``vendor_order`` lives on the
 * vendor order the box was received against, so unmapping the box from the
 * PO leaves it showing.
 */
export type PoLink = 'direct' | 'vendor_order';

/**
 * How a box reached the PO the list names.
 *
 * The two look alike in a PO column and are not: only ``direct`` is the
 * mapping a PO's box mapping sets and clears; ``vendor_order`` lives on the
 * vendor order the box was received against, so unmapping the box from the
 * PO leaves it showing.
 */
export const PoLink = {
    DIRECT: 'direct',
    VENDOR_ORDER: 'vendor_order'
} as const;

/**
 * A warehouse by id and name.
 */
export type PoWarehouseRead = {
    id: number;
    name: string;
};

/**
 * One pool on its own, for the breakdown.
 *
 * Attributes:
 * pool: ``marketplace`` (FBA, FBN) or ``warehouse`` (our own).
 * on_hand: Its stock now.
 * in_transit: On its way to it.
 * drr_organic: Its rate.
 * demand_cover: Its Demand(FD + BD).
 * demand_lead: Its Demand(SD).
 * cover_days: The day its shelf runs dry within the forecast days;
 * ``null`` while it lasts.
 * order_qty: Its own order, were it ordered for alone.
 */
export type PoolForecastRead = {
    pool: StockPool;
    on_hand: number;
    in_transit: number;
    drr_organic: number;
    demand_cover: number;
    demand_lead: number;
    cover_days: number | null;
    order_qty: number;
};

/**
 * One SKU the boxes would bill, with the price it would bill at.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * sku_name: Its name.
 * quantity: Units across the boxes.
 * price: The retailer's price, or ``None`` when one must be typed.
 * vat_rate: Its VAT %, or ``None``.
 */
export type PreviewLineRead = {
    sku_code: string;
    sku_name: string | null;
    quantity: number;
    price: string | null;
    vat_rate: string | null;
};

/**
 * Boxes to preview as an invoice's lines, writing nothing.
 *
 * Attributes:
 * warehouse_id: Where the boxes are.
 * retailer_id: Who will be billed.
 * currency: ISO 4217.
 * box_codes: The boxes.
 */
export type PreviewWrite = {
    warehouse_id: number;
    retailer_id: number;
    currency: string;
    box_codes: Array<string>;
};

/**
 * A queued push. Find its run with ``GET …/runs?request_id=``.
 */
export type PricePushAccepted = {
    request_id: string;
    store_id: number;
    dry_run: boolean;
    /**
     * SKUs in the push
     */
    rows: number;
};

/**
 * One update task: up to 500 SKUs sent in one call.
 */
export type PricePushBatchRead = {
    /**
     * The marketplace's id for the update task
     */
    process_uuid: string | null;
    /**
     * The task's state, e.g. pending or done
     */
    state: string;
    total: number;
    succeeded: number;
    failed: number;
    unchanged: number;
    message: string | null;
};

/**
 * A run's totals, in SKUs and update tasks.
 */
export type PricePushCounts = {
    skus: number;
    /**
     * Update tasks, of up to 500 SKUs each
     */
    batches: number;
    succeeded: number;
    failed: number;
    unchanged: number;
    /**
     * Not finished when the run stopped following them
     */
    pending: number;
    /**
     * A dry run's SKUs, which were not sent
     */
    planned: number;
};

/**
 * What became of one SKU.
 */
export type PricePushRowRead = {
    sku: string;
    /**
     * succeeded, failed, pending or planned
     */
    outcome: string;
    error: string | null;
};

/**
 * One run, with each update task and the SKUs worth showing.
 */
export type PricePushRunDetail = {
    run_id: string;
    request_id: string | null;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    dry_run: boolean;
    /**
     * When it was launched
     */
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Null until the push has reported — while queued or running
     */
    counts: PricePushCounts | null;
    batches: Array<PricePushBatchRead>;
    /**
     * Failed first, then pending, then succeeded or planned; capped by the run
     */
    rows: Array<PricePushRowRead>;
    rows_truncated: boolean;
    /**
     * Why the run failed, when it did
     */
    error: string | null;
};

/**
 * One run, as listed.
 */
export type PricePushRunRead = {
    run_id: string;
    request_id: string | null;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    dry_run: boolean;
    /**
     * When it was launched
     */
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Null until the push has reported — while queued or running
     */
    counts: PricePushCounts | null;
};

/**
 * A store whose prices can be pushed.
 */
export type PricePushStoreRead = {
    /**
     * com_store.pk_store_id
     */
    store_id: number;
    name: string;
    /**
     * Its marketplace, e.g. OTDE
     */
    short_code: string;
    /**
     * The marketplace's country
     */
    country_code: string;
    /**
     * The push it gets, e.g. otto_seller
     */
    connector: string;
};

/**
 * One cell, row or sheet that was not taken.
 */
export type PriceSheetProblemRead = {
    /**
     * Spreadsheet row number; the header is row 1
     */
    row: number;
    /**
     * The template's column at fault; null when the whole sheet is
     */
    column: string | null;
    message: string;
};

/**
 * An uploaded price sheet: queued, or refused with every problem it had.
 *
 * A sheet with any problem is not pushed at all — half a price list is a set
 * of prices nobody chose — so ``push`` is null whenever ``problems`` is not
 * empty.
 */
export type PriceSheetRead = {
    /**
     * SKUs taken from the sheet
     */
    rows: number;
    problems?: Array<PriceSheetProblemRead>;
    push?: PricePushAccepted | null;
};

/**
 * The account the credentials belong to, as the platform names it.
 */
export type ProbeAccount = {
    id?: string | null;
    name?: string | null;
    type?: string | null;
};

/**
 * One step of a probe: the token exchange, the account read.
 */
export type ProbeCheck = {
    name: string;
    ok: boolean;
    detail?: string | null;
    ms?: number | null;
};

/**
 * Why a probe failed, in words a person can act on.
 */
export type ProbeError = {
    kind: string;
    message: string;
};

/**
 * A marketplace the account participates in (Amazon).
 */
export type ProbeMarketplace = {
    marketplace_id?: string | null;
    country_code?: string | null;
    name?: string | null;
    currency_code?: string | null;
    language_code?: string | null;
    domain?: string | null;
    participating?: boolean | null;
};

/**
 * What testing a credential found. ``ok: false`` is a result, not an error.
 */
export type ProbeResult = {
    ok: boolean;
    surface: string;
    connector?: string | null;
    checks?: Array<ProbeCheck>;
    account?: ProbeAccount | null;
    marketplaces?: Array<ProbeMarketplace>;
    ads_profiles?: Array<AdsProfile>;
    vendor_accounts?: Array<{
        [key: string]: unknown;
    }>;
    expires_at?: string | null;
    error?: ProbeError | null;
};

/**
 * One marketplace listing with its latest price.
 *
 * ``marketplace_url`` is the marketplace's storefront (``com_marketplace``
 * ``urls.website_url``), so a page can label and link the product id on
 * any marketplace with ``marketplace_short_code``.
 *
 * ``sku_code`` and ``sku_name`` are null for a listing whose SKU is no
 * longer in the catalogue.
 *
 * ``units_trend`` is Total units for each of the last eight completed
 * weeks, oldest first, from the sales mart. ``stock`` is warehouse plus
 * marketplace stock now, ``None`` when neither mart has a reading.
 */
export type ProductRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    marketplace_url: string | null;
    asin: string | null;
    sku_code: string | null;
    sku_name: string | null;
    marketplace_sku: string | null;
    condition: string | null;
    listing_status: string | null;
    title: string | null;
    manufacturer: string | null;
    is_fba: boolean | null;
    is_focused: boolean;
    drr: string | null;
    units_trend: Array<number>;
    stock: number | null;
    currency_code: string | null;
    regular_price: string | null;
    sale_price: string | null;
    min_price: string | null;
    max_price: string | null;
    mfn_shipping_price: string | null;
    vat_rate: string | null;
    updated_at: string | null;
};

/**
 * A store, with what its marketplace calls things and its upload sheet.
 */
export type ProductStore = {
    id: number;
    name: string;
    marketplace: string;
    country_code: string | null;
    /**
     * ASIN, EAN, FSN, Item Number, …
     */
    key_header: string;
    vat_header: string;
    /**
     * Whether the marketplace has an Is FBA column
     */
    fba: boolean;
    template: string;
    template_columns: Array<string>;
};

/**
 * What the report holds, over every currency.
 *
 * Attributes:
 * row_count: Rows.
 * sku_count: Distinct SKUs.
 * store_count: Distinct stores.
 * loss_count: Rows whose profit is below zero.
 * under_margin_count: Rows in profit but under their store's expected markup.
 */
export type ProfitabilityCounts = {
    row_count: number;
    sku_count: number;
    store_count: number;
    loss_count: number;
    under_margin_count: number;
};

/**
 * What one row is: a marketplace, a store, or a SKU at a store — Partner View's drill.
 */
export type ProfitabilityLevel = 'marketplace' | 'store' | 'sku';

/**
 * What one row is: a marketplace, a store, or a SKU at a store — Partner View's drill.
 */
export const ProfitabilityLevel = {
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    SKU: 'sku'
} as const;

/**
 * One SKU at one store, one store, or one marketplace, in one currency.
 *
 * A store's row has no SKU fields; a marketplace's, no store fields either.
 * Stock, cover, replen and the loss reason are a SKU's alone.
 *
 * Attributes:
 * store_id: The store; ``null`` for invoices whose retailer sells
 * through no store linked to the invoice's warehouse.
 * store_name: Its name.
 * store_type: seller, vendor, offline or df.
 * marketplace_id: The store's marketplace.
 * marketplace_name: Its name.
 * marketplace_code: Its short code.
 * sku_id: The SKU; ``null`` for advertising not resolved to one.
 * sku_code: Its code.
 * sku_name: Its name.
 * brand_id: Its brand.
 * brand_name: The brand's name.
 * category: Its category.
 * subcategory: Its subcategory.
 * replen: Its replenishment class at the store's warehouses.
 * stock_units: What it holds now: our stock in the store's warehouses
 * (the picked ones, when warehouses are picked) and the
 * marketplace's at the store.
 * cover_days: How many days the stock lasts at the period's daily rate
 * of units; ``null`` when it sold none.
 * expected_margin: The store's expected markup, a fraction.
 * loss_reason: Why the row is a loss-maker; ``null`` when it is not.
 * sku_count: SKUs under a store's or a marketplace's row.
 * store_count: Stores under a marketplace's row.
 */
export type ProfitabilityRow = {
    currency_code: string;
    seller_units?: number;
    df_units?: number;
    of_units?: number;
    vo_units?: number;
    units?: number;
    returned_units?: number;
    seller_amount: MoneyRead;
    df_amount: MoneyRead;
    of_amount: MoneyRead;
    vo_amount: MoneyRead;
    sales_amount: MoneyRead;
    cogs_amount: MoneyRead;
    commission_amount: MoneyRead;
    settled_amount: MoneyRead;
    expected_amount: MoneyRead;
    ad_sales_amount: MoneyRead;
    ad_spend_amount: MoneyRead;
    profit_amount?: MoneyRead | null;
    margin?: string | null;
    sales_margin?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    store_type?: string | null;
    marketplace_id?: number | null;
    marketplace_name?: string | null;
    marketplace_code?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    category?: string | null;
    subcategory?: string | null;
    replen?: ReplenClass | null;
    stock_units?: number | null;
    cover_days?: string | null;
    expected_margin?: string | null;
    loss_reason?: LossReason | null;
    sku_count?: number | null;
    store_count?: number | null;
};

/**
 * Every matching row's figures in one currency.
 *
 * Attributes:
 * row_count: Rows in this currency.
 * sku_count: Distinct SKUs among them.
 * store_count: Distinct stores among them.
 */
export type ProfitabilityTotals = {
    currency_code: string;
    seller_units?: number;
    df_units?: number;
    of_units?: number;
    vo_units?: number;
    units?: number;
    returned_units?: number;
    seller_amount: MoneyRead;
    df_amount: MoneyRead;
    of_amount: MoneyRead;
    vo_amount: MoneyRead;
    sales_amount: MoneyRead;
    cogs_amount: MoneyRead;
    commission_amount: MoneyRead;
    settled_amount: MoneyRead;
    expected_amount: MoneyRead;
    ad_sales_amount: MoneyRead;
    ad_spend_amount: MoneyRead;
    profit_amount?: MoneyRead | null;
    margin?: string | null;
    sales_margin?: string | null;
    row_count?: number;
    sku_count?: number;
    store_count?: number;
};

/**
 * Which rows the report holds.
 *
 * ``loss``: the SKUs that lost money, or earned a markup more than five
 * points under their store's expected one — VentaHub's Loss making SKUs.
 * ``all``: every row — its Profitability tab.
 */
export type ProfitabilityView = 'loss' | 'all';

/**
 * Which rows the report holds.
 *
 * ``loss``: the SKUs that lost money, or earned a markup more than five
 * points under their store's expected one — VentaHub's Loss making SKUs.
 * ``all``: every row — its Profitability tab.
 */
export const ProfitabilityView = {
    LOSS: 'loss',
    ALL: 'all'
} as const;

/**
 * Body of ``POST /runs/{id}/promote``; omitted fields come from the run.
 *
 * It must give the question a way to be scored: a reference with check
 * columns, expected or forbidden text, a rubric for the judge, or a page
 * whose verify link the answer must carry.
 *
 * Attributes:
 * reference_method: A ``query_data`` request whose ``check_columns``
 * hold the key numbers; it must compile.
 * relative: A window computed on the day the eval runs.
 * page: A page (``/business/view``) the answer must link to with a
 * "Verify in VentaWiz" link.
 */
export type PromoteWrite = {
    slug?: string | null;
    question?: string | null;
    reference_sql?: string | null;
    reference_method?: {
        [key: string]: unknown;
    } | null;
    check_columns?: Array<string>;
    top?: number;
    date_from?: string | null;
    date_to?: string | null;
    relative?: ('yesterday' | 'last_7_days' | 'last_week' | 'last_month') | null;
    tags?: Array<string>;
    expect_any?: Array<string>;
    forbid?: Array<string>;
    grader?: Grader;
    rubric?: string | null;
    page?: string | null;
};

/**
 * The eval question a run became.
 */
export type PromotedRead = {
    id: string;
    slug: string;
};

/**
 * A model provider every run skips until ``until`` (``llm/cooldown.py``).
 *
 * Attributes:
 * reason: ``billing``, ``auth`` or ``not_configured``.
 * error: The failure that started it, trimmed.
 */
export type ProviderCooldownRead = {
    provider: string;
    model: string;
    reason: string;
    error: string;
    since: string;
    until: string;
};

/**
 * The providers on cool-down now; empty when every provider is in use.
 */
export type ProviderCooldowns = {
    items: Array<ProviderCooldownRead>;
};

/**
 * Body of ``POST /v1/purchase-orders``.
 *
 * ``amount_total`` and ``po_units_total`` are optional and **derived** from the
 * lines. If sent they must match, so a caller who typed a total finds out when
 * it is wrong rather than having it silently replaced.
 *
 * Attributes:
 * warehouse_id: The warehouse it ships from.
 * retailer_id: The retailer who ordered.
 * store_id: The store it belongs to. Optional when the retailer has exactly
 * one store.
 * po_code: The retailer's PO number; unique per warehouse among live POs.
 * po_date: When the PO was placed.
 * currency_code: ISO 4217. Every line price is in it.
 * po_valid_till: Last date the retailer will accept delivery.
 * shipping_address: Free-form address, as received.
 * comment: Free text.
 * amount_total: Optional check value; see above.
 * po_units_total: Optional check value; see above.
 * amount_confirmed: What the retailer confirmed, if known.
 * po_units_confirmed: As above, in units.
 * lines: At least one.
 */
export type PurchaseOrderCreate = {
    warehouse_id: number;
    retailer_id: number;
    store_id?: number | null;
    po_code: string;
    po_date?: string | null;
    currency_code: string;
    po_valid_till?: string | null;
    shipping_address?: {
        [key: string]: unknown;
    } | null;
    comment?: string | null;
    amount_total?: number | string | null;
    po_units_total?: number | null;
    amount_confirmed?: number | string | null;
    po_units_confirmed?: number | null;
    lines: Array<PurchaseOrderLineWrite>;
};

/**
 * A PO with its lines.
 *
 * Attributes:
 * shipping_address: Address as received.
 * lines: Every live line.
 */
export type PurchaseOrderDetail = {
    id: number;
    po_code: string;
    warehouse_id: number | null;
    retailer_id: number | null;
    store_id: number | null;
    po_date: string | null;
    currency_code: string | null;
    amount_total: MoneyRead | null;
    amount_confirmed: MoneyRead | null;
    po_units_total: number | null;
    po_units_confirmed: number | null;
    po_valid_till: string | null;
    appointment_date: string | null;
    comment: string | null;
    line_count: number;
    created_at: string;
    updated_at: string | null;
    shipping_address: {
        [key: string]: unknown;
    } | null;
    lines: Array<PurchaseOrderLineRead>;
};

/**
 * A line as stored, with what has been committed against it.
 *
 * Attributes:
 * id: Line id. Stable across edits: lines are matched on SKU and updated
 * in place.
 * sku_id: Our SKU's id.
 * sku_code: Our SKU code.
 * retailer_product_code: The retailer's code for it.
 * mp_product_code: The marketplace's numeric code.
 * item_price: Unit price, with the PO's currency.
 * quantity_ordered: Units ordered.
 * quantity_confirmed: Units the retailer confirmed.
 * quantity_committed: Units already allotted, blocked or invoiced.
 */
export type PurchaseOrderLineRead = {
    id: number;
    sku_id: number | null;
    sku_code: string | null;
    retailer_product_code: string | null;
    mp_product_code: number | null;
    item_price: MoneyRead | null;
    quantity_ordered: number | null;
    quantity_confirmed: number | null;
    quantity_committed: number;
};

/**
 * One line of a PO, as the caller states it.
 *
 * A line names its SKU one of two ways: the retailer's own code for it
 * (``retailer_product_code``, an ASIN for Amazon) or ours (``sku_code``). One is
 * enough; both must agree if both are sent.
 *
 * Attributes:
 * retailer_product_code: The retailer's code for the item.
 * sku_code: Our SKU code.
 * mp_product_code: The marketplace's numeric product code, if any.
 * item_price: Unit cost the retailer will pay, in the PO's currency.
 * quantity_ordered: Whole units, at least one.
 * quantity_confirmed: What the retailer confirmed, if known.
 */
export type PurchaseOrderLineWrite = {
    retailer_product_code?: string | null;
    sku_code?: string | null;
    mp_product_code?: number | null;
    item_price?: number | string | null;
    quantity_ordered: number;
    quantity_confirmed?: number | null;
};

/**
 * A PO in a list.
 *
 * Attributes:
 * id: ``pk_po_id``.
 * po_code: The retailer's PO number.
 * warehouse_id: Where it ships from.
 * retailer_id: Who ordered.
 * store_id: The store.
 * po_date: When it was placed.
 * currency_code: ISO 4217.
 * amount_total: Sum of line prices times quantities.
 * amount_confirmed: What the retailer confirmed.
 * po_units_total: Sum of line quantities.
 * po_units_confirmed: Units the retailer confirmed.
 * po_valid_till: Last acceptable delivery date.
 * appointment_date: The delivery appointment.
 * comment: Free text.
 * line_count: How many lines.
 * created_at: ``dt_create``.
 * updated_at: ``dt_update``, if it has ever changed.
 */
export type PurchaseOrderRead = {
    id: number;
    po_code: string;
    warehouse_id: number | null;
    retailer_id: number | null;
    store_id: number | null;
    po_date: string | null;
    currency_code: string | null;
    amount_total: MoneyRead | null;
    amount_confirmed: MoneyRead | null;
    po_units_total: number | null;
    po_units_confirmed: number | null;
    po_valid_till: string | null;
    appointment_date: string | null;
    comment: string | null;
    line_count: number;
    created_at: string;
    updated_at: string | null;
};

/**
 * ``-`` means descending. The primary key is always the final tiebreaker.
 */
export type PurchaseOrderSort = '-created' | 'created' | '-po_date' | 'po_date' | 'code' | '-code' | '-amount' | 'amount';

/**
 * ``-`` means descending. The primary key is always the final tiebreaker.
 */
export const PurchaseOrderSort = {
    '-CREATED': '-created',
    CREATED: 'created',
    '-PO_DATE': '-po_date',
    PO_DATE: 'po_date',
    CODE: 'code',
    '-CODE': '-code',
    '-AMOUNT': '-amount',
    AMOUNT: 'amount'
} as const;

/**
 * Body of ``PATCH /v1/purchase-orders/{id}``.
 *
 * Only the fields sent are changed. The warehouse and retailer cannot change:
 * every line was resolved against them, so moving a PO means creating a new one.
 *
 * Attributes:
 * po_code: A new code, still unique per warehouse.
 * po_date: When it was placed.
 * po_valid_till: Last acceptable delivery date.
 * shipping_address: Address as received.
 * comment: Free text.
 * amount_confirmed: What the retailer confirmed.
 * po_units_confirmed: As above, in units.
 */
export type PurchaseOrderUpdate = {
    po_code?: string | null;
    po_date?: string | null;
    po_valid_till?: string | null;
    shipping_address?: {
        [key: string]: unknown;
    } | null;
    comment?: string | null;
    amount_confirmed?: number | string | null;
    po_units_confirmed?: number | null;
};

/**
 * A queued push. Find its run with ``GET …/runs?request_id=``.
 */
export type PushAccepted = {
    request_id: string;
    store_id: number;
    connector: string;
    dry_run: boolean;
};

/**
 * Push a store's own stock now.
 */
export type PushRequestBody = {
    /**
     * Read and plan only: what would change, sending nothing
     */
    dry_run?: boolean;
};

/**
 * One rate bucket of a connector's account.
 */
export type RateLimitBucket = {
    group: string;
    rps?: number | null;
    burst?: number | null;
    available?: number | null;
    observed_rps?: number | null;
};

/**
 * The rate bucket with the least headroom.
 */
export type RateLimitHeadroom = {
    group?: string | null;
    available: number;
    capacity: number;
};

/**
 * The asker's rating, as the question log filters on it.
 */
export type RatingFilter = 'up' | 'down' | 'any' | 'none';

/**
 * The asker's rating, as the question log filters on it.
 */
export const RatingFilter = {
    UP: 'up',
    DOWN: 'down',
    ANY: 'any',
    NONE: 'none'
} as const;

/**
 * A product's latest scraped rating, as stored.
 *
 * Attributes:
 * product_id: ``marketplace_listed_product.pk_id``.
 * store_id: The store the listing belongs to.
 * rating: Average star rating.
 * rating_count: How many ratings it averages.
 * has_aplus_content: Whether the page has A+ content.
 * delivery_promise_days: Days until the promised delivery.
 * updated_at: When this result was written.
 */
export type RatingRead = {
    product_id: number;
    store_id: number;
    rating: string | null;
    rating_count: number | null;
    has_aplus_content: boolean | null;
    delivery_promise_days: number | null;
    updated_at: string | null;
};

/**
 * Body of ``POST /v1/stores/{id}/reactivate``.
 *
 * Attributes:
 * reason: Why, optionally. Recorded on the event when given.
 */
export type ReactivateRequest = {
    reason?: string | null;
};

/**
 * What receivables can be grouped by.
 */
export type ReceivablesGrouping = 'warehouse' | 'retailer' | 'channel' | 'ageing_bucket' | 'invoice';

/**
 * What receivables can be grouped by.
 */
export const ReceivablesGrouping = {
    WAREHOUSE: 'warehouse',
    RETAILER: 'retailer',
    CHANNEL: 'channel',
    AGEING_BUCKET: 'ageing_bucket',
    INVOICE: 'invoice'
} as const;

/**
 * Receivables, grouped.
 */
export type ReceivablesReport = {
    items: Array<ReceivablesRow>;
    pagination: Pagination;
    totals: Array<ReceivablesTotals>;
    meta: ReportMeta;
};

/**
 * One group of invoices.
 *
 * Attributes:
 * ageing_bucket: Filled when grouped by ageing bucket: ``paid``,
 * ``not_due``, ``1-30``, ``31-60``, ``61-90`` or ``90+`` days past
 * due.
 * invoice_id: Filled when grouped by invoice.
 * invoice_number: Its number.
 * invoice_date: Its date.
 * due_date: When it is due: its delivery day, or its own day without
 * one, plus its credit period.
 */
export type ReceivablesRow = {
    channel?: string | null;
    retailer_id?: number | null;
    retailer_name?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    billed_amount?: MoneyRead | null;
    billed_amount_ex_vat?: MoneyRead | null;
    received_amount?: MoneyRead | null;
    outstanding_amount?: MoneyRead | null;
    credit_note_amount?: MoneyRead | null;
    debit_note_amount?: MoneyRead | null;
    overdue_amount?: MoneyRead | null;
    invoice_count?: number | null;
    overdue_invoice_count?: number | null;
    max_days_past_due?: number | null;
    ageing_bucket?: string | null;
    invoice_id?: number | null;
    invoice_number?: string | null;
    invoice_date?: string | null;
    due_date?: string | null;
};

/**
 * What retailers owe us on every matching DF, OF and VO invoice, in one currency.
 *
 * Measured at ``meta.as_of``. Only receipts and notes in an invoice's own
 * currency count against it (plan §11.2).
 *
 * Attributes:
 * currency_code: The invoice currency.
 * billed_amount: What the retailers were billed, VAT included.
 * billed_amount_ex_vat: The same without VAT.
 * received_amount: What they have paid against it.
 * outstanding_amount: Billed less received. Most of it is receipts
 * nobody has entered yet.
 * credit_note_amount: Credit notes naming the invoices, shown beside
 * the balance and not netted from it (plan §8 question 2).
 * debit_note_amount: Debit notes naming them, likewise.
 * overdue_amount: What is outstanding past its due date.
 * invoice_count: Invoices.
 * overdue_invoice_count: Invoices with money outstanding past their due
 * date.
 * max_days_past_due: The most days an overdue invoice is past its due
 * date; 0 when none is overdue.
 */
export type ReceivablesTotals = {
    currency_code?: string | null;
    billed_amount?: MoneyRead | null;
    billed_amount_ex_vat?: MoneyRead | null;
    received_amount?: MoneyRead | null;
    outstanding_amount?: MoneyRead | null;
    credit_note_amount?: MoneyRead | null;
    debit_note_amount?: MoneyRead | null;
    overdue_amount?: MoneyRead | null;
    invoice_count?: number | null;
    overdue_invoice_count?: number | null;
    max_days_past_due?: number | null;
};

/**
 * Ingest could not be asked. The desired state is saved; the daily run converges it.
 *
 * Attributes:
 * error: Why, as an error type: ``probe_unavailable`` when ingest
 * could not be reached.
 */
export type ReconcileFailure = {
    error: string;
};

/**
 * What ingest did about one stream: subscribed, unsubscribed, adopted, ...
 */
export type ReconcileItem = {
    stream_key: string;
    action: string;
    status?: string | null;
    detail?: string | null;
};

/**
 * Ingest's answer to "make this store's streams match".
 */
export type ReconcileResult = {
    store_id: number;
    results?: Array<ReconcileItem>;
};

/**
 * Where an order line stands against its settlement.
 *
 * ``unmatched``: no cash settlement line is linked to it yet. ``returned``:
 * the order was returned, or a refund line is linked to it. ``matched``:
 * the principal paid equals the line price. ``partially_matched``: paid,
 * but not the line price.
 */
export type ReconciliationStatus = 'matched' | 'unmatched' | 'partially_matched' | 'returned';

/**
 * Where an order line stands against its settlement.
 *
 * ``unmatched``: no cash settlement line is linked to it yet. ``returned``:
 * the order was returned, or a refund line is linked to it. ``matched``:
 * the principal paid equals the line price. ``partially_matched``: paid,
 * but not the line price.
 */
export const ReconciliationStatus = {
    MATCHED: 'matched',
    UNMATCHED: 'unmatched',
    PARTIALLY_MATCHED: 'partially_matched',
    RETURNED: 'returned'
} as const;

/**
 * Body of the single-image re-roll.
 *
 * Attributes:
 * instructions: The seller's description of the image they want. Blank
 * or absent re-rolls from the image's existing prompt. On the main
 * image it sets only the angle, framing and light: the product stays
 * alone on pure white, as Amazon requires.
 */
export type RegenerateImage = {
    instructions?: string | null;
};

/**
 * A re-roll that has been queued.
 *
 * Attributes:
 * job_id: The job.
 * image_ids: Exactly the images being re-rolled — watch their versions.
 * directed: Whether a seller's description will be used. False when none
 * was given.
 * usage: The allowances after this request.
 */
export type RegenerationRead = {
    job_id: string;
    image_ids: Array<string>;
    directed: boolean;
    usage: UsageRead;
};

/**
 * The entities that carry notes, tags and contacts of their own.
 *
 * Stores have their own tables (``store_note`` and the rest), whose foreign
 * keys the store footprint and hard delete rely on. These share
 * ``registry_note``, ``registry_tag`` and ``registry_contact``, keyed by
 * kind and id - a marketplace is a shared row (a brand was until the brand
 * split), so the tenant is the annotation's own column, never the subject's.
 * A vendor joined them in revision ``7d3a5c9e1b42``; a retailer and a
 * service provider in ``b5e2c8a1d4f7``. The values are the scope types a
 * grant names, and fit ``subject_kind``'s 16 characters.
 */
export type RegistrySubjectKind = 'warehouse' | 'brand' | 'marketplace' | 'vendor' | 'retailer' | 'service_provider';

/**
 * The entities that carry notes, tags and contacts of their own.
 *
 * Stores have their own tables (``store_note`` and the rest), whose foreign
 * keys the store footprint and hard delete rely on. These share
 * ``registry_note``, ``registry_tag`` and ``registry_contact``, keyed by
 * kind and id - a marketplace is a shared row (a brand was until the brand
 * split), so the tenant is the annotation's own column, never the subject's.
 * A vendor joined them in revision ``7d3a5c9e1b42``; a retailer and a
 * service provider in ``b5e2c8a1d4f7``. The values are the scope types a
 * grant names, and fit ``subject_kind``'s 16 characters.
 */
export const RegistrySubjectKind = {
    WAREHOUSE: 'warehouse',
    BRAND: 'brand',
    MARKETPLACE: 'marketplace',
    VENDOR: 'vendor',
    RETAILER: 'retailer',
    SERVICE_PROVIDER: 'service_provider'
} as const;

/**
 * Whether to reorder.
 *
 * ``loss``: the SKU loses money, so the answer is not a purchase.
 * ``yes``: it makes money and is short. ``no``: its stock covers the window.
 */
export type ReorderStatus = 'loss' | 'yes' | 'no';

/**
 * Whether to reorder.
 *
 * ``loss``: the SKU loses money, so the answer is not a purchase.
 * ``yes``: it makes money and is short. ``no``: its stock covers the window.
 */
export const ReorderStatus = {
    LOSS: 'loss',
    YES: 'yes',
    NO: 'no'
} as const;

/**
 * A change to one SKU. A field left out is not changed.
 *
 * Attributes:
 * sku_id: The SKU.
 * replen_type: Its new class.
 * focus: Whether it should be a focus SKU.
 */
export type ReplenChange = {
    sku_id: number;
    replen_type?: ('R' | 'N' | 'T') | null;
    focus?: boolean | null;
};

/**
 * Changes to apply at one warehouse, all or none.
 *
 * Attributes:
 * warehouse_id: Where.
 * changes: One per SKU.
 */
export type ReplenChangesWrite = {
    warehouse_id: number;
    changes: Array<ReplenChange>;
};

/**
 * A SKU's replenishment class at the store's warehouses, the strongest of them.
 *
 * ``R``: replenished. ``N``: not replenished. ``T``: on trial.
 */
export type ReplenClass = 'R' | 'N' | 'T';

/**
 * A SKU's replenishment class at the store's warehouses, the strongest of them.
 *
 * ``R``: replenished. ``N``: not replenished. ``T``: on trial.
 */
export const ReplenClass = {
    R: 'R',
    N: 'N',
    T: 'T'
} as const;

/**
 * What the filters on this page can be chosen from.
 */
export type ReplenLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * What a save changed, and what it sent for approval.
 *
 * Attributes:
 * skus: SKUs with something changed now.
 * cost_rows: Vendor cost rows rewritten now.
 * applied: The SKUs whose changes were applied now, in the order sent.
 * pending_approval: The SKUs whose changes take them off ``N``: nothing
 * was written for them; they wait on the approval ticket.
 * ticket_id: That ticket, when there is one.
 * ticket_number: Its number, as the tickets pages show it.
 */
export type ReplenResultRead = {
    skus: number;
    cost_rows: number;
    applied?: Array<number>;
    pending_approval?: Array<number>;
    ticket_id?: string | null;
    ticket_number?: number | null;
};

/**
 * One SKU's replenishment set-up at a warehouse.
 *
 * Folded from its valid vendor cost rows, the way the vendor-order gate reads
 * them: replenished if any row is ``R``, else ``N`` if any is, else ``T``; in
 * focus if any row is.
 *
 * Attributes:
 * sku_id: The SKU.
 * sku_code: Its code.
 * sku_name: Its name.
 * brand: Its brand.
 * category: Its category and subcategory.
 * replen_type: The class in force, or ``None`` when no row has one.
 * mixed: Its rows disagree; a change sets them all to one class.
 * cost_rows: How many vendor cost rows a change rewrites.
 * focus: Whether it is a focus SKU here.
 * available: Units still here (inward and allotted, less blocked). Focus
 * cannot be turned off while this is above zero.
 */
export type ReplenSkuRead = {
    sku_id: number;
    sku_code: string;
    sku_name: string | null;
    brand: string | null;
    category: string | null;
    replen_type: ('R' | 'N' | 'T') | null;
    mixed: boolean;
    cost_rows: number;
    focus: boolean;
    available: number;
};

/**
 * What kind of problem a report from another ticket's page was.
 */
export type ReportCategory = 'Bug' | 'Data Issue' | 'UI Problem' | 'Performance' | 'Other';

/**
 * What kind of problem a report from another ticket's page was.
 */
export const ReportCategory = {
    BUG: 'Bug',
    DATA_ISSUE: 'Data Issue',
    UI_PROBLEM: 'UI Problem',
    PERFORMANCE: 'Performance',
    OTHER: 'Other'
} as const;

/**
 * Two saved reports side by side, their insights matched by area.
 *
 * Attributes:
 * left: The first report named, with its snapshot.
 * right: The second.
 * areas: Every area either side covers, the most important first
 * (VentaHub's ``matchByArea``).
 */
export type ReportComparison = {
    left: SavedReportRead;
    right: SavedReportRead;
    areas: Array<InsightPair>;
};

/**
 * Which date the report-sync window filters on.
 */
export type ReportDateField = 'created_at' | 'last_updated_at' | 'report_start_date' | 'report_end_date';

/**
 * Which date the report-sync window filters on.
 */
export const ReportDateField = {
    CREATED_AT: 'created_at',
    LAST_UPDATED_AT: 'last_updated_at',
    REPORT_START_DATE: 'report_start_date',
    REPORT_END_DATE: 'report_end_date'
} as const;

/**
 * How fresh a report is.
 *
 * Attributes:
 * built_at: When the marts were last built together (D14); ``null`` if
 * they never were. A report is no fresher than this.
 * as_of: When a snapshot report's figures were measured. ``null`` for a
 * report over a date range, and for a snapshot whose mart records
 * no measurement time: listings, and Amazon's vendor stock, whose
 * ``week`` grouping says which week each store's figure is from.
 * fx: How the amounts were converted; ``null`` when the request did not
 * ask (``convert_to``).
 */
export type ReportMeta = {
    built_at: string | null;
    as_of: string | null;
    fx?: FxMeta | null;
};

/**
 * Every filter's options, by name.
 *
 * Attributes:
 * warehouses: The tenant's live warehouses.
 * marketplaces: The marketplaces of its stores.
 * stores: Its live stores.
 * retailers: Its live retailers.
 * brands: The brands of its SKUs.
 * categories: The categories of its SKUs.
 * subcategories: Their sub-categories.
 */
export type ReportOptions = {
    warehouses: Array<VentawizReportsSchemasOptionsWarehouseOption>;
    marketplaces: Array<VentawizReportsSchemasOptionsMarketplaceOption>;
    stores: Array<VentawizReportsSchemasOptionsStoreOption>;
    retailers: Array<VentawizReportsSchemasOptionsRetailerOption>;
    brands: Array<VentawizReportsSchemasOptionsBrandOption>;
    categories: Array<VentawizReportsSchemasOptionsCategoryOption>;
    subcategories: Array<SubcategoryOption>;
};

/**
 * The latest attempt at one report for one store and period.
 *
 * Attributes:
 * id: The tracker row.
 * store_id: Which store.
 * store_name: Its name.
 * profile_id: Its Ads profile.
 * report_type: Which report.
 * status: SUCCESS, a FAILED_* stage, or a pending stage.
 * report_start_date: The data's first day.
 * report_end_date: Its last day.
 * requested_at: When it was requested.
 * last_updated_at: When its status last changed.
 * turnaround_minutes: From request to finish, for a finished report.
 * poll_count: Times Amazon was asked whether it was ready.
 * rows_in_report: Rows Amazon returned.
 * rows_inserted: Rows loaded.
 * last_error_message: Why it failed.
 */
export type ReportPullRead = {
    id: number;
    store_id: number;
    store_name: string;
    profile_id: string | null;
    report_type: string;
    status: string;
    report_start_date: string;
    report_end_date: string;
    requested_at: string | null;
    last_updated_at: string | null;
    turnaround_minutes: number | null;
    poll_count: number;
    rows_in_report: number | null;
    rows_inserted: number | null;
    last_error_message: string | null;
};

/**
 * How bad the reporter said it was.
 */
export type ReportSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

/**
 * How bad the reporter said it was.
 */
export const ReportSeverity = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    CRITICAL: 'Critical'
} as const;

/**
 * The report-sync tabs.
 */
export type ReportStatusGroup = 'successful' | 'failed' | 'pending';

/**
 * The report-sync tabs.
 */
export const ReportStatusGroup = {
    SUCCESSFUL: 'successful',
    FAILED: 'failed',
    PENDING: 'pending'
} as const;

/**
 * The selection at a glance.
 *
 * Attributes:
 * successful: Reports that loaded.
 * failed: Reports that failed at any stage.
 * pending: Reports still on their way.
 * total_polls: Readiness checks made.
 * average_polls: Per report.
 * average_turnaround_minutes: For successful reports.
 * reports_per_day: Average requests per day.
 */
export type ReportSyncCounts = {
    successful: number;
    failed: number;
    pending: number;
    total_polls: number;
    average_polls: number | null;
    average_turnaround_minutes: number | null;
    reports_per_day: number | null;
};

/**
 * A page of report pulls, with counts over the whole selection.
 *
 * Attributes:
 * counts: Across every tab, so the tab badges do not change as the
 * user switches between them.
 */
export type ReportSyncPage = {
    items: Array<ReportPullRead>;
    pagination: Pagination;
    counts: ReportSyncCounts;
};

/**
 * One column of a stored result: the key in its rows and the header people read.
 */
export type ResultColumn = {
    key: string;
    label: string;
};

/**
 * How one stored result is exported.
 */
export type ResultFormat = 'csv' | 'xlsx';

/**
 * How one stored result is exported.
 */
export const ResultFormat = {
    CSV: 'csv',
    XLSX: 'xlsx'
} as const;

/**
 * A stored result (the evidence behind an insight), capped for display.
 */
export type ResultRead = {
    id: string;
    title: string | null;
    columns: Array<ResultColumn>;
    rows: Array<Array<unknown>>;
    row_count: number;
    truncated: boolean;
};

/**
 * One retailer cost record.
 */
export type RetailerCostRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    id: number;
    sku_id: number | null;
    sku_code: string | null;
    sku_name: string | null;
    warehouse_id: number | null;
    warehouse_name: string | null;
    retailer_id: number | null;
    retailer_name: string | null;
    retailer_product_code: string | null;
    currency_code: string;
    nlc: string | null;
    rrp: string | null;
    vat_rate: string | null;
    is_valid: string | null;
    created_at: string;
    updated_at: string | null;
};

/**
 * Body of ``POST /v1/retailers``.
 *
 * Attributes:
 * country: ISO 3166-1 alpha-2.
 * group_warehouse_id: A warehouse in the same tenant.
 */
export type RetailerCreate = {
    code: string;
    name: string;
    country?: string | null;
    group_warehouse_id?: number | null;
};

/**
 * One retailer's POs in the window.
 *
 * Attributes:
 * values: The value ordered and invoiced per currency, so a retailer
 * ordering in two still has both totals.
 */
export type RetailerFulfilmentRead = {
    ordered: number;
    confirmed: number;
    blocked: number;
    allotted: number;
    invoiced: number;
    dispatched: number;
    delivered: number;
    open: number;
    value_ordered: string | null;
    value_invoiced: string | null;
    fill_rate: number | null;
    confirm_rate: number | null;
    delivery_rate: number | null;
    currency: string | null;
    retailer_id: number | null;
    retailer: string | null;
    pos: number;
    lines: number;
    skus: number;
    values: Array<CurrencyValueRead>;
};

/**
 * Somebody the tenant sells to in bulk (``com_retailer``).
 *
 * Attributes:
 * country: As stored; see :class:`WarehouseRead`.
 * group_warehouse_id: The warehouse their orders ship from by default.
 */
export type RetailerRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    country: string | null;
    group_warehouse_id: number | null;
};

/**
 * What the filters on this page can be chosen from.
 */
export type RetailerReceiptLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * One row of the retailer receipts list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type RetailerReceiptRead = {
    id: number;
    date: string | null;
    invoice_id: number | null;
    invoice: string | null;
    currency: string | null;
    amount: string | null;
    comment: string | null;
    valid: boolean;
};

/**
 * A Shift Stock request whose lines name retailer product codes.
 *
 * Attributes:
 * warehouse_id: Where the boxes are, and where the codes are read.
 * shift_date: As on :class:`ShiftCreate`.
 * lines: The moves. All are made, or none.
 */
export type RetailerShiftCreate = {
    warehouse_id: number;
    shift_date?: string | null;
    lines: Array<RetailerShiftLine>;
};

/**
 * Units of one SKU to move between boxes, named by a retailer's code.
 *
 * Attributes:
 * from_box_code: The box the units leave.
 * retailer_sku: A retailer product code at this warehouse, or our own SKU
 * code. Where it could be either and both are in the box, the line is
 * refused rather than guessed.
 * to_box_code: The box they arrive in, created when missing.
 * quantity: Units. Positive.
 * expiry_date: A specific batch, as on :class:`ShiftLine`.
 */
export type RetailerShiftLine = {
    from_box_code: string;
    retailer_sku: string;
    to_box_code: string;
    quantity: number;
    expiry_date?: string | null;
};

/**
 * Body of ``PATCH /v1/retailers/{id}``.
 *
 * Attributes:
 * country: ISO 3166-1 alpha-2.
 * group_warehouse_id: A warehouse in the same tenant.
 */
export type RetailerUpdate = {
    name?: string | null;
    country?: string | null;
    group_warehouse_id?: number | null;
};

/**
 * What retiring an entity would do.
 *
 * Attributes:
 * blocking: Each refuses the retire with a 409 until it is resolved.
 * warnings: To be acknowledged; the retire goes ahead.
 * usage: Everything referring to it, as ``/usage`` reports it.
 */
export type RetirePreview = {
    blocking: Array<ImpactItem>;
    warnings: Array<ImpactItem>;
    usage: UsageReport;
};

/**
 * One warehouse return.
 */
export type ReturnRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    order_id: string | null;
    return_order_id: string | null;
    sku_code: string | null;
    asin: string | null;
    return_date: string | null;
    return_status: string | null;
    return_reason: string | null;
    quantity: number | null;
    item_condition: string | null;
    box_id: string | null;
    claim_id: string | null;
    credit_note_num: string | null;
    item_price: string | null;
    refund_amount: string | null;
    currency_code: string | null;
};

/**
 * A store, with its marketplace's Return Data sheet.
 */
export type ReturnStore = {
    id: number;
    name: string;
    marketplace: string;
    country_code: string | null;
    template: string;
    template_columns: Array<string>;
};

/**
 * One ad group's share of an ASIN row (RSC:526-557).
 *
 * Null means "not measured at this grain", never zero: at ``alt_product`` the
 * impressions, clicks, CTR, CPC, conversion, spend, ACOS and SKUs are null.
 */
export type ReviewAmsAdProductInstance = {
    ag_id: number;
    ad_group_id: string;
    ad_group_name: string;
    ad_group_type?: string | null;
    /**
     * Upper-cased; null when unknown
     */
    state?: string | null;
    /**
     * Distinct SKUs, comma-joined, ordered
     */
    sku_codes?: string | null;
    impressions?: number | null;
    clicks?: number | null;
    ctr?: number | null;
    cpc?: number | null;
    units?: number;
    conversion?: number | null;
    sales?: string;
    /**
     * sales / units; null when no units sold
     */
    asp?: number | null;
    spend?: string | null;
    acos?: number | null;
};

/**
 * Resolve an ASIN or Alt Product row to the ad groups behind it (RSC:492-523). Read only.
 */
export type ReviewAmsAdProductInstancesRequest = {
    store_id: number;
    /**
     * Advertised ASIN (ad_product) or purchased ASIN (alt_product)
     */
    asin: string;
    /**
     * Which report to resolve against; alt_product has no spend
     */
    level?: AdProductInstanceLevel;
    current_start_date: string;
    current_end_date: string;
    /**
     * Scope to this Amazon ad group id (from a single-ad-group row)
     */
    ad_group_id?: string | null;
    /**
     * Brand filter from the page; 0 = ad groups with no brand
     */
    brand_ids?: Array<number> | null;
    /**
     * Ad-group type filter from the page
     */
    ad_group_type?: AmsAdGroupType | null;
    /**
     * Cross-drill filter from the page (pk_ag_id)
     */
    ad_group_ids?: Array<number> | null;
};

/**
 * The ad groups of one ASIN row, money column descending (RSC:560-562).
 */
export type ReviewAmsAdProductInstancesResponse = {
    instances?: Array<ReviewAmsAdProductInstance>;
    currency_code?: string | null;
};

/**
 * Add a negative keyword or negative ASIN target to one ad group (RSC:590-595).
 *
 * Attributes:
 * store_id: The store the ad group belongs to.
 * ad_group_id: Amazon ad group id.
 * keyword: The phrase, or an ASIN.
 * match_type: Either API generation's spelling.
 * source: Page attribution.
 */
export type ReviewAmsAddNegativeRequest = {
    store_id: number;
    ad_group_id: string;
    keyword: string;
    match_type: NegativeMatchType;
    source?: string | null;
};

/**
 * Add a search term as a target in a destination ad group (RSC:598-609).
 *
 * The bid is the CPC formula over the **origin** ad group's window CPC for the
 * term: it is the origin's traffic that justifies the new target.
 *
 * Attributes:
 * store_id: The store both ad groups belong to.
 * destination_ad_group_id: Where the target is created.
 * origin_ad_group_id: Where the search term was seen.
 * target: The search term (or an ASIN).
 * target_type: Keyword match type (EXACT / PHRASE / BROAD). Not validated,
 * as in VentaHub; ignored for an ASIN.
 * current_start_date: First day of the window the CPC is read over.
 * current_end_date: Last day of that window.
 * source: Page attribution.
 */
export type ReviewAmsAddTargetRequest = {
    store_id: number;
    destination_ad_group_id: string;
    origin_ad_group_id: string;
    target: string;
    /**
     * Keyword match type (EXACT / PHRASE / BROAD); ignored for ASINs
     */
    target_type: string;
    current_start_date: string;
    current_end_date: string;
    source?: string | null;
};

/**
 * One candidate destination; ineligible ones are returned with the reason (RSC:401-425).
 */
export type ReviewAmsBrandAdGroupOption = {
    ag_id: number;
    ad_group_id: string;
    ad_group_name: string;
    campaign_name?: string | null;
    ad_group_type: string;
    targeting_type?: string | null;
    ad_group_label?: string | null;
    /**
     * The master's raw state (not upper-cased)
     */
    state?: string | null;
    already_exists?: boolean;
    is_sister?: boolean;
    eligible?: boolean;
    ineligible_reason?: string | null;
};

/**
 * Brand-wide add-target destinations (RSC:428-446).
 *
 * No ``brand_id``: the brand is the origin ad group's, so a caller cannot read
 * back the ad groups of a brand it has no grant for.
 */
export type ReviewAmsBrandAdGroupsRequest = {
    store_id: number;
    /**
     * Ad group the search term appeared in; sets the brand
     */
    origin_ad_group_id: string;
    /**
     * Flags ad groups that already carry it as a target
     */
    search_term: string;
    /**
     * Filter on ad group / campaign name
     */
    search?: string | null;
    limit?: number;
};

/**
 * Every other ad group of the origin's brand on the store, or its unbranded ones (RSC:449-465).
 *
 * ``total_count`` is the unbounded count; ``truncated`` when ``limit`` clipped the list.
 */
export type ReviewAmsBrandAdGroupsResponse = {
    brand_id?: number | null;
    brand_name?: string | null;
    is_unbranded?: boolean;
    ad_groups?: Array<ReviewAmsBrandAdGroupOption>;
    total_count?: number;
    truncated?: boolean;
    currency_code?: string | null;
};

/**
 * Change one target's bid by the CPC formula, the ASP formula or a manual value (RSC:565-579).
 *
 * Attributes:
 * store_id: The store the ad group belongs to.
 * ad_group_id: Amazon ad group id.
 * target_id: Amazon ``keywordId`` / ``targetId``.
 * bid_mode: How the bid is derived.
 * manual_bid: Required when ``bid_mode`` is ``manual``.
 * current_start_date: First day of the window the formulas read.
 * current_end_date: Last day of that window.
 * target: Target text, for the log.
 * current_bid: The bid before the change, for the log.
 * source: Page attribution, e.g. ``AMS View Page``.
 */
export type ReviewAmsChangeBidRequest = {
    store_id: number;
    /**
     * Amazon ad group id
     */
    ad_group_id: string;
    /**
     * Amazon keywordId / targetId
     */
    target_id: string;
    bid_mode: BidMode;
    /**
     * Required when bid_mode is manual
     */
    manual_bid?: number | null;
    current_start_date: string;
    current_end_date: string;
    /**
     * Target text, for the log
     */
    target?: string | null;
    /**
     * Bid before the change, for the log
     */
    current_bid?: number | null;
    /**
     * Page attribution, e.g. 'AMS View Page'
     */
    source?: string | null;
};

/**
 * One negative, aggregated over each ad group's latest snapshot (RSC:201-218).
 */
export type ReviewAmsNegativeRow = {
    keyword_text: string;
    match_type?: string | null;
    negative_type?: string | null;
    status: string;
    ad_group_count?: number;
    latest_date?: string | null;
};

/**
 * Pagination envelope of the negatives listing (RSC:221-243); no money, so no currency.
 */
export type ReviewAmsNegativesPage = {
    data: Array<ReviewAmsNegativeRow>;
    total_count: number;
    page: number;
    page_size: number;
    total_pages: number;
};

/**
 * Pause one target (RSC:582-587).
 *
 * Attributes:
 * store_id: The store the ad group belongs to.
 * ad_group_id: Amazon ad group id.
 * target_id: Amazon ``keywordId`` / ``targetId``.
 * target: Target text, for the log.
 * source: Page attribution.
 */
export type ReviewAmsPauseTargetRequest = {
    store_id: number;
    ad_group_id: string;
    target_id: string;
    target?: string | null;
    source?: string | null;
};

/**
 * One period's KPI totals from the ad-group daily table (RSC:172-191).
 */
export type ReviewAmsPeriodTotals = {
    impressions?: number;
    clicks?: number;
    ctr?: number;
    cpc?: number;
    units?: number;
    conversion?: number;
    sales?: string;
    asp?: number | null;
    spend?: string;
    acos?: number;
};

/**
 * One aggregated row (RSC:73-160).
 *
 * Entity columns depend on the level (null when not applicable); the metric
 * block is the same on every level (alt_product fills units, sales and ASP only,
 * the others keep their defaults).
 */
export type ReviewAmsRow = {
    ag_id?: number | null;
    ad_group_id?: string | null;
    name?: string | null;
    campaign_name?: string | null;
    ad_group_type?: string | null;
    state?: string | null;
    /**
     * Configured fraction (0.30 = 30%)
     */
    target_acos?: number | null;
    /**
     * Configured fraction
     */
    target_conversion?: number | null;
    /**
     * Configured ASP (money), ad_group level only
     */
    asp?: string | null;
    asin?: string | null;
    sku_codes?: string | null;
    target?: string | null;
    target_type?: string | null;
    /**
     * Top of the bid range (= bid_max)
     */
    bid?: string | null;
    bid_min?: string | null;
    bid_max?: string | null;
    target_id?: string | null;
    search_term?: string | null;
    match_types?: string | null;
    keywords?: string | null;
    ad_group_name?: string | null;
    purchased_asin?: string | null;
    advertised_asins?: string | null;
    ad_group_count?: number | null;
    /**
     * target / search_term levels
     */
    enabled_ad_group_count?: number | null;
    curr_impressions?: number;
    curr_clicks?: number;
    curr_ctr?: number;
    curr_cpc?: number;
    curr_units?: number;
    curr_conversion?: number;
    curr_sales?: string;
    curr_asp?: number | null;
    curr_spend?: string;
    curr_acos?: number;
    prev_impressions?: number;
    prev_clicks?: number;
    prev_ctr?: number;
    prev_cpc?: number;
    prev_units?: number;
    prev_conversion?: number;
    prev_sales?: string;
    prev_asp?: number | null;
    prev_spend?: string;
    prev_acos?: number;
};

/**
 * VentaHub's pagination envelope (RSC:163-169), kept so the ported table reads it as is.
 *
 * A page past the end reports ``total_count = 0`` and ``total_pages = 0``
 * (VentaHub quirk, RR:247-252).
 */
export type ReviewAmsRowsPage = {
    data: Array<ReviewAmsRow>;
    total_count: number;
    page: number;
    page_size: number;
    total_pages: number;
    currency_code?: string | null;
};

/**
 * Body of ``POST /v1/review-ams-data``: one page of one level, two periods (RSC:44-72).
 *
 * VentaHub's ``*_dt`` UTC instants become plain calendar days (``*_date``);
 * ``client_id`` is gone (the tenant comes from the token). Ids are bounded to the
 * PostgreSQL ``integer`` range and ``search`` refuses NUL (``PgInt`` / ``PgText``):
 * asyncpg cannot bind either (a 500), where VentaHub compared the store in Python
 * (RR:101-107) and inlined the rest as literals.
 */
export type ReviewAmsRowsRequest = {
    level: ReviewLevel;
    store_id: number;
    /**
     * Brand pick; 0 = ad groups with no brand
     */
    brand_ids?: Array<number> | null;
    ad_group_type?: AmsAdGroupType | null;
    /**
     * Cross-drill: ad-group master ids (pk_ag_id); empty = no filter
     */
    ad_group_ids?: Array<number> | null;
    /**
     * Level-aware search text
     */
    search?: string | null;
    previous_start_date: string;
    previous_end_date: string;
    current_start_date: string;
    current_end_date: string;
    page?: number;
    page_size?: number;
    sort_by?: ReviewSortKey;
    sort_dir?: VentawizKitSortingSortOrder;
};

/**
 * An origin ad group with its sister destinations and CPC-formula bid (RSC:377-398).
 */
export type ReviewAmsSearchTermOrigin = {
    ag_id: number;
    ad_group_id: string;
    ad_group_name: string;
    ad_group_type: string;
    targeting_type?: string | null;
    /**
     * Upper-cased; null when unknown
     */
    state?: string | null;
    /**
     * The origin's brand; null when unbranded
     */
    brand_id?: number | null;
    /**
     * spend / clicks over the current period
     */
    cpc?: number;
    /**
     * Marketplace minimum bid for the ad type
     */
    min_bid?: string;
    cpc_formula_bid?: string | null;
    cpc_formula_reasoning?: string | null;
    sisters?: Array<ReviewAmsSisterAdGroupOption>;
};

/**
 * Resolve a Search Term row to the ad groups it appeared in (RSC:468-484).
 */
export type ReviewAmsSearchTermOriginsRequest = {
    store_id: number;
    /**
     * Search term, matched exactly (case-sensitive)
     */
    search_term: string;
    current_start_date: string;
    current_end_date: string;
    /**
     * Scope to this Amazon ad group id (from a single-ad-group row)
     */
    ad_group_id?: string | null;
    /**
     * Brand filter from the page; 0 = ad groups with no brand
     */
    brand_ids?: Array<number> | null;
    /**
     * Ad-group type filter from the page
     */
    ad_group_type?: AmsAdGroupType | null;
    /**
     * Cross-drill filter from the page (pk_ag_id)
     */
    ad_group_ids?: Array<number> | null;
};

/**
 * The origins of one Search Term row (RSC:487-489).
 */
export type ReviewAmsSearchTermOriginsResponse = {
    origins?: Array<ReviewAmsSearchTermOrigin>;
    currency_code?: string | null;
};

/**
 * One of an origin's six sister slots (RSC:357-374).
 *
 * ``ad_group_name`` and ``state`` are null when the slot is unset or names an ad
 * group outside the caller's scope; ``already_exists`` when the search term is
 * already a target there.
 */
export type ReviewAmsSisterAdGroupOption = {
    slot: string;
    label: string;
    ad_group_id?: string | null;
    ad_group_name?: string | null;
    /**
     * Upper-cased; null when unknown
     */
    state?: string | null;
    already_exists?: boolean;
};

/**
 * The KPI strip: both periods plus the ad groups with data in either (RSC:194-198).
 *
 * ``ad_group_count`` counts over the union window of both periods (and any gap
 * between them), while the tables count the current period only — VentaHub's
 * behaviour, kept.
 */
export type ReviewAmsSummary = {
    current: ReviewAmsPeriodTotals;
    previous: ReviewAmsPeriodTotals;
    ad_group_count?: number;
    currency_code?: string | null;
};

/**
 * One concrete (ad group, target) with its current bid and both formula previews.
 *
 * RSC:303-349. ``asp_formula_bid`` / ``asp_formula_reasoning`` are null when the
 * ad group lacks a configured ASP, target conversion or target ACOS; the modal
 * then disables that option.
 */
export type ReviewAmsTargetInstance = {
    ag_id: number;
    ad_group_id: string;
    ad_group_name: string;
    campaign_id?: string | null;
    target_id: string;
    target_type?: string | null;
    ad_group_type: string;
    targeting_type?: string | null;
    /**
     * The master's raw state (not upper-cased)
     */
    state?: string | null;
    /**
     * Live master bid, else the daily bid
     */
    current_bid?: string | null;
    /**
     * spend / clicks over the current period
     */
    cpc?: number;
    /**
     * Marketplace minimum bid for the ad type
     */
    min_bid?: string;
    cpc_formula_bid?: string | null;
    cpc_formula_reasoning?: string | null;
    asp_formula_bid?: string | null;
    asp_formula_reasoning?: string | null;
    /**
     * The ad group's configured ASP
     */
    asp?: string | null;
    /**
     * Configured fraction (0.20 = 20%)
     */
    target_acos?: number | null;
    /**
     * Configured fraction
     */
    target_conversion?: number | null;
    /**
     * units / clicks over the current period; null with no clicks or units
     */
    actual_conversion?: number | null;
    /**
     * The rate the ASP formula used: actual when measured, else target
     */
    effective_conversion?: number | null;
    conversion_source?: ConversionSource;
};

/**
 * Resolve a Target row to its per-ad-group instances over the current period (RSC:270-300).
 *
 * The filter fields carry the page's applied filters, so the list describes the
 * same report the row was read from.
 */
export type ReviewAmsTargetInstancesRequest = {
    store_id: number;
    /**
     * Target text, matched exactly (case-sensitive)
     */
    target: string;
    /**
     * Match type; None matches only rows with no target type
     */
    target_type?: string | null;
    current_start_date: string;
    current_end_date: string;
    /**
     * Scope to this Amazon ad group id (from a single-ad-group row)
     */
    ad_group_id?: string | null;
    /**
     * Brand filter from the page; 0 = ad groups with no brand
     */
    brand_ids?: Array<number> | null;
    /**
     * Ad-group type filter from the page
     */
    ad_group_type?: AmsAdGroupType | null;
    /**
     * Cross-drill filter from the page (pk_ag_id)
     */
    ad_group_ids?: Array<number> | null;
    /**
     * Scope to this Amazon target id (from a single-ad-group row)
     */
    target_id?: string | null;
};

/**
 * The instances of one Target row (RSC:352-354); empty when nothing ran in the period.
 */
export type ReviewAmsTargetInstancesResponse = {
    instances?: Array<ReviewAmsTargetInstance>;
    currency_code?: string | null;
};

/**
 * What was read.
 *
 * Attributes:
 * built_at: When the marts were last built together; ``null`` if never.
 * previous_from: The previous period's first day.
 * previous_to: Its last day.
 * current_from: The current period's first day.
 * current_to: Its last day.
 * sections: The Business View sections read.
 */
export type ReviewDataMeta = {
    built_at: string | null;
    previous_from: string;
    previous_to: string;
    current_from: string;
    current_to: string;
    sections: Array<BusinessViewSection>;
};

/**
 * A page of SKUs, the totals over every SKU, and what was read.
 */
export type ReviewDataReport = {
    items: Array<ReviewDataRow>;
    pagination: Pagination;
    totals: ReviewDataTotals;
    meta: ReviewDataMeta;
};

/**
 * One SKU, now and in both periods.
 *
 * Attributes:
 * sku_id: The SKU; ``null`` for figures not resolved to one.
 * sku_code: Its code.
 * sku_name: Its name.
 * brand_name: Its brand.
 * category: Its category.
 * subcategory: Its subcategory.
 * product_ref: Its ASIN, when it has one listing; else ``null``.
 * is_focus: It is a focused SKU at one of the stores in scope.
 * has_a_plus: One of its listings carried A+ content at its last check.
 * idq_score: Its listings' best item data quality score at their last check.
 * is_prime: One of its listings' Buy Box offers was Prime at its last check.
 * is_stranded: Amazon reports one of its listings stranded now.
 * p1_warehouse_units: Our stock now in the warehouses that are a store's
 * first choice (priority 1).
 * other_warehouse_units: Our stock now in the other linked warehouses.
 * overage_fee: Amazon's estimated aged-inventory surcharge now, per currency.
 * to_receive: What the marketplaces are expected to pay for the current
 * period's sales not yet paid, per currency.
 * loss: For the current period, what was received and is expected less
 * the cost of goods, where that is below zero; per currency.
 * previous: The previous period's figures; ``null`` when it had none.
 * current: The current period's figures; ``null`` when it had none.
 */
export type ReviewDataRow = {
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    brand_name?: string | null;
    category?: string | null;
    subcategory?: string | null;
    product_ref?: string | null;
    is_focus?: boolean;
    has_a_plus?: boolean | null;
    idq_score?: string | null;
    is_prime?: boolean | null;
    is_stranded?: boolean;
    p1_warehouse_units?: number | null;
    other_warehouse_units?: number | null;
    overage_fee?: Array<MoneyRead>;
    to_receive?: Array<MoneyRead>;
    loss?: Array<MoneyRead>;
    previous?: ReviewPeriodFigures | null;
    current?: ReviewPeriodFigures | null;
};

/**
 * Every SKU's figures, both periods, and what they hold now.
 *
 * Attributes:
 * previous: The previous period over every SKU shown.
 * current: The current period over every SKU shown.
 * growth: The current against the previous.
 * p1_warehouse_units: Stock now in priority-1 warehouses.
 * other_warehouse_units: Stock now in the other warehouses.
 * to_receive: Still expected for the current period's sales, per currency.
 * loss: The current period's losses, per currency.
 * average_fill_rate: The SKUs' fill rates averaged, per period.
 * stores: The stores the review covers.
 *
 * Every figure is over the rows' SKUs, so the filters narrow it as they
 * narrow the grid.
 */
export type ReviewDataTotals = {
    previous: BusinessViewTotals;
    current: BusinessViewTotals;
    growth: ReviewGrowth;
    p1_warehouse_units?: number;
    other_warehouse_units?: number;
    to_receive?: Array<MoneyRead>;
    loss?: Array<MoneyRead>;
    average_fill_rate?: ReviewFillRates;
    stores?: Array<ReviewStore>;
};

/**
 * What a decision did.
 *
 * Attributes:
 * entity_type: Which kind.
 * entity_id: Its key.
 * approved: The decision.
 * reviewed_by_principal_id: The approver; ``None`` on a rejection.
 * reviewed_date: When it was approved; ``None`` on a rejection.
 * comment: The comment sent.
 * notified: Who the outcome mail was queued to; ``None`` when nobody could
 * be resolved.
 * landed_cost_skus: Vendor invoice approvals: how many SKUs' landed cost
 * the approval set. ``0`` for an exempt vendor; ``None`` otherwise.
 */
export type ReviewDecisionRead = {
    entity_type: ReviewDocumentType;
    entity_id: number;
    approved: boolean;
    reviewed_by_principal_id: string | null;
    reviewed_date: string | null;
    comment: string | null;
    notified: string | null;
    landed_cost_skus?: number | null;
};

/**
 * Approve or reject one document.
 *
 * The reviewer is the caller, from the token: there is no field for it.
 *
 * Attributes:
 * entity_type: Which kind.
 * entity_id: Its key.
 * approved: ``True`` approves; ``False`` rejects, which changes nothing on
 * the document and sends it back with ``comment``.
 * comment: The reason, carried into the mail.
 * escalate_to_email: On a rejection, who to send it back to instead of the
 * document's creator.
 */
export type ReviewDecisionWrite = {
    entity_type: ReviewDocumentType;
    entity_id: number;
    approved: boolean;
    comment?: string | null;
    escalate_to_email?: string | null;
};

/**
 * One line of a document, in the one shape all four kinds share.
 *
 * ``None`` means unknown or not applicable to this kind, never zero: a SKU
 * with no agreed price is blank, not free.
 *
 * Attributes:
 * sku_code: Our code for the SKU.
 * sku_name: Its name.
 * category: The SKU master's category, free text.
 * subcategory: The SKU master's subcategory, free text.
 * box_code: The box the units sit in; ``None`` on a vendor order.
 * quantity: Units.
 * unit_amount: The price per unit (sales invoice, note) or the cost (vendor
 * order, vendor invoice).
 * vat_rate: As the document stores it: a percentage on a sales invoice
 * (``18`` for 18%), a fraction on a vendor invoice or note (``0.18``);
 * ``None`` on a vendor order, which carries no VAT.
 * line_value: Quantity times unit amount, VAT included, to two places, so a
 * reader never needs to know which VAT convention applies.
 * warehouse_stock: Vendor order: units available in the order's warehouse
 * now (the warehouse-stock mart), so "we already hold 400" is visible
 * before approving more. ``None`` when the mart has no row.
 * ordered_cost: Vendor invoice: the cost the backing vendor order agreed.
 * expiry_date: Vendor invoice: the batch's expiry.
 * expiry_is_near: Vendor invoice: the expiry is under nine months away.
 * retailer_nlc: Sales invoice: the retailer's agreed price for the SKU at
 * the warehouse, falling back to the catch-all retailer's.
 * retailer_sku_id: Sales invoice: the retailer's own code for the SKU.
 * buy_cost: Sales invoice: what these units cost us, as billing stored it on
 * the line (the landed cost, else the one vendor quote).
 * vendor_invoice_number: Sales invoice: the newest valid vendor invoice that
 * received this SKU into this box.
 * cndn_number: Sales invoice: the newest valid note raised against this
 * invoice for this box and SKU.
 * cndn_amount: Sales invoice: those notes' line amounts, summed.
 * cost_issue: Vendor invoice: billed above the ordered cost. Sales invoice:
 * sold below the buy cost. Compared at two places; ``None`` when either
 * side is unknown.
 */
export type ReviewDocumentLineRead = {
    sku_code?: string | null;
    sku_name?: string | null;
    category?: string | null;
    subcategory?: string | null;
    box_code?: string | null;
    quantity?: number | null;
    unit_amount?: string | null;
    vat_rate?: string | null;
    line_value?: string | null;
    warehouse_stock?: number | null;
    ordered_cost?: string | null;
    expiry_date?: string | null;
    expiry_is_near?: boolean | null;
    retailer_nlc?: string | null;
    retailer_sku_id?: string | null;
    buy_cost?: string | null;
    vendor_invoice_number?: string | null;
    cndn_number?: string | null;
    cndn_amount?: string | null;
    cost_issue?: boolean | null;
};

/**
 * A document as the review page shows it: header, review state and lines.
 *
 * Attributes:
 * entity_type: Which kind.
 * entity_id: Its key.
 * code: Its order code or number.
 * document_date: The date typed on the document.
 * currency_code: The currency every amount here is in.
 * units: Header units; on a note, the lines' sum.
 * value: The total including tax.
 * value_wo_tax: The total before tax, where the kind stores one.
 * counterparty_label: ``Vendor``, ``Retailer`` or ``Service Provider``.
 * counterparty_name: Who the document is with.
 * warehouse_id: The document's warehouse.
 * warehouse_name: Its name.
 * po_code: The retailer PO it was raised against, if any.
 * comment: The header note.
 * credit_period: Payment window in days (vendor and sales invoices).
 * valid_till: Vendor order: when its prices stop being valid.
 * cbm: Vendor invoice: declared volume.
 * shipping_cost: Vendor invoice: the freight on the header.
 * customs_duty: Vendor invoice: duty on the header.
 * other_cost: Vendor invoice: other costs on the header.
 * created_by: The VentaHub user who raised it, by name, else email.
 * created_by_principal_id: The VentaWiz principal who raised it, from the
 * event log; ``None`` for a document raised in VentaHub.
 * owner_email: The VentaHub creator's email: where a decision is mailed.
 * created_on: When the row was inserted.
 * updated_on: When it last changed.
 * reviewed: Approved, here or in VentaHub (:mod:`ventawiz.kit.review`).
 * reviewed_date: When it was approved.
 * reviewed_by_principal_id: The VentaWiz principal who approved it.
 * lines: Its lines.
 */
export type ReviewDocumentRead = {
    entity_type: ReviewDocumentType;
    entity_id: number;
    code: string;
    document_date?: string | null;
    currency_code?: string | null;
    units?: number | null;
    value?: string | null;
    value_wo_tax?: string | null;
    counterparty_label?: string | null;
    counterparty_name?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    po_code?: string | null;
    comment?: string | null;
    credit_period?: number | null;
    valid_till?: string | null;
    cbm?: string | null;
    shipping_cost?: string | null;
    customs_duty?: string | null;
    other_cost?: string | null;
    created_by?: string | null;
    created_by_principal_id?: string | null;
    owner_email?: string | null;
    created_on?: string | null;
    updated_on?: string | null;
    reviewed: boolean;
    reviewed_date?: string | null;
    reviewed_by_principal_id?: string | null;
    lines: Array<ReviewDocumentLineRead>;
};

/**
 * The kinds of document a review approves or rejects.
 *
 * The values are VentaHub's, so a link or a saved filter carries over.
 */
export type ReviewDocumentType = 'VO' | 'VI' | 'INVOICE' | 'CDN';

/**
 * The kinds of document a review approves or rejects.
 *
 * The values are VentaHub's, so a link or a saved filter carries over.
 */
export const ReviewDocumentType = {
    VO: 'VO',
    VI: 'VI',
    INVOICE: 'INVOICE',
    CDN: 'CDN'
} as const;

/**
 * The SKUs' fill rates averaged, each SKU once, per period; ``null`` with none.
 */
export type ReviewFillRates = {
    previous?: string | null;
    current?: string | null;
};

/**
 * The current period against the previous, over every SKU: (now - then) ÷ then.
 *
 * ``null`` (or no entry) where the previous period had none.
 */
export type ReviewGrowth = {
    total_units?: string | null;
    seller_units?: string | null;
    df_units?: string | null;
    po_units?: string | null;
    total_amount?: Array<CurrencyRatio>;
    seller_amount?: Array<CurrencyRatio>;
    df_amount?: Array<CurrencyRatio>;
    ad_spend?: Array<CurrencyRatio>;
    ad_sales?: Array<CurrencyRatio>;
};

/**
 * Aggregation level of a review table (RSC:22-24 ``ReviewAmsLevel``).
 *
 * ``placement`` is not offered: its only source, the Marketing Stream hourly
 * table, does not exist in VentaWiz (decision 2).
 */
export type ReviewLevel = 'ad_group' | 'ad_product' | 'target' | 'search_term' | 'alt_product';

/**
 * Aggregation level of a review table (RSC:22-24 ``ReviewAmsLevel``).
 *
 * ``placement`` is not offered: its only source, the Marketing Stream hourly
 * table, does not exist in VentaWiz (decision 2).
 */
export const ReviewLevel = {
    AD_GROUP: 'ad_group',
    AD_PRODUCT: 'ad_product',
    TARGET: 'target',
    SEARCH_TERM: 'search_term',
    ALT_PRODUCT: 'alt_product'
} as const;

/**
 * One period of a SKU: its Business View figures, and the period's own extras.
 *
 * Attributes:
 * fill_rate: Amazon's receive fill rate over the period's weeks, the
 * mean of the weekly rates, a fraction; ``null`` with none.
 * best_rank: The best (lowest) best-sellers rank any of the SKU's
 * listings showed in the period; ``null`` when none was read.
 */
export type ReviewPeriodFigures = {
    total_units: number;
    drr?: string | null;
    stock_cover_days?: string | null;
    seller_units?: number | null;
    returned_units?: number | null;
    seller_amount?: Array<MoneyRead> | null;
    df_units?: number | null;
    df_amount?: Array<MoneyRead> | null;
    of_units?: number | null;
    of_amount?: Array<MoneyRead> | null;
    vo_units?: number | null;
    vo_amount?: Array<MoneyRead> | null;
    po_units?: number | null;
    po_amount?: Array<MoneyRead> | null;
    total_amount: Array<MoneyRead>;
    average_price: Array<MoneyRead>;
    payment_amount?: Array<MoneyRead> | null;
    warehouse_units?: number | null;
    age_0_30_units?: number | null;
    age_31_60_units?: number | null;
    age_61_90_units?: number | null;
    age_91_180_units?: number | null;
    age_181_270_units?: number | null;
    age_over_270_units?: number | null;
    age_pre_migration_units?: number | null;
    age_unknown_units?: number | null;
    fba_units?: number | null;
    fba_aged_91_180_units?: number | null;
    fba_aged_181_270_units?: number | null;
    impressions?: number | null;
    clicks?: number | null;
    ad_units?: number | null;
    ad_spend?: Array<MoneyRead> | null;
    ad_sales?: Array<MoneyRead> | null;
    ctr?: string | null;
    cvr?: string | null;
    clicks_per_order?: string | null;
    cpc?: Array<MoneyRead> | null;
    roas?: Array<CurrencyRatio> | null;
    acos?: Array<CurrencyRatio> | null;
    ad_average_price?: Array<MoneyRead> | null;
    sessions?: number | null;
    page_views?: number | null;
    glance_views?: number | null;
    traffic_units_ordered?: number | null;
    buy_box_page_views?: string | null;
    buy_box_share?: string | null;
    unit_session_pct?: string | null;
    vendor_ordered_units?: number | null;
    vendor_customer_returns?: number | null;
    vendor_ordered_revenue?: Array<MoneyRead> | null;
    vendor_sellable_units?: number | null;
    vendor_unsellable_units?: number | null;
    vendor_aged_90_plus_units?: number | null;
    vendor_open_po_units?: number | null;
    vendor_sellable_cost?: Array<MoneyRead> | null;
    vendor_sell_through?: string | null;
    buybox_checks?: number | null;
    buybox_wins?: number | null;
    buybox_win_rate?: string | null;
    sales_rank?: number | null;
    rating_count?: number | null;
    rating?: string | null;
    fill_rate?: string | null;
    best_rank?: number | null;
};

/**
 * One product page to scrape.
 *
 * Attributes:
 * product_ids: Every focused listing this page answers for —
 * ``marketplace_listed_product.pk_id``. More than one when a store
 * lists the same product under several seller SKUs; the page is
 * scraped once and the result is posted back for all of them.
 * mp_product_id: The marketplace's id for the product: the ASIN on
 * Amazon, the FSN on Flipkart, the zsku on noon. Upper-cased for
 * Amazon, where it is case-insensitive and a few rows are stored
 * lower-case.
 * short_code: The marketplace country, e.g. ``AZAE``. Decides which
 * site the scraper opens.
 * store_id: The store the listings belong to.
 */
export type ReviewQueryItem = {
    product_ids: Array<number>;
    mp_product_id: string;
    short_code: string;
    store_id: number;
};

/**
 * The review request that was queued.
 *
 * Attributes:
 * entity_type: Which kind.
 * entity_id: Its key.
 * code: The document's code, as the mail names it.
 * recipients: Who it was queued to.
 */
export type ReviewRequestRead = {
    entity_type: ReviewDocumentType;
    entity_id: number;
    code: string;
    recipients: Array<string>;
};

/**
 * Ask the warehouse's reviewers to look at a document.
 *
 * Attributes:
 * entity_type: Which kind.
 * entity_id: Its key.
 * recipients: Who to ask instead of the warehouse's configured list.
 */
export type ReviewRequestWrite = {
    entity_type: ReviewDocumentType;
    entity_id: number;
    recipients?: Array<string> | null;
};

/**
 * What a scraper read off one product page.
 *
 * Unknown fields are ignored rather than rejected, unlike every other request
 * in this service: each scraper also sends what only its marketplace shows —
 * noon's price, Flipkart's applied pincode — and rejecting those would make
 * one scraper's extra field fail every result it posts.
 *
 * Attributes:
 * store_id: The store the listings belong to.
 * product_ids: The listings this result is for, as handed out by the
 * queue.
 * rating: Average star rating, 0 to 5.
 * rating_count: How many ratings it averages.
 * critical_reviews: The low-star reviews, as the scraper sends them —
 * ``{"reviews": [...]}``. Kept as sent because each marketplace
 * shows different fields per review.
 * aplus_content: Whether the page has A+ content. Amazon only.
 * delivery_promise_days: Days until the promised delivery.
 */
export type ReviewResultCreate = {
    store_id: number;
    product_ids: Array<number>;
    rating?: number | string | null;
    rating_count?: number | null;
    critical_reviews?: {
        [key: string]: unknown;
    } | null;
    aplus_content?: YesNo | null;
    delivery_promise_days?: number | null;
};

/**
 * Every key a review table or export can sort by.
 *
 * Which keys a level or export grain accepts is checked by the service
 * (422 ``Invalid sort_by: <key>``), as VentaHub's per-level ``sort_exprs``
 * allowlist did (RR:110-117, RS:662-671).
 */
export type ReviewSortKey = 'name' | 'curr_impressions' | 'curr_clicks' | 'curr_ctr' | 'curr_cpc' | 'curr_units' | 'curr_conversion' | 'curr_sales' | 'curr_asp' | 'curr_spend' | 'curr_acos' | 'prev_impressions' | 'prev_clicks' | 'prev_ctr' | 'prev_cpc' | 'prev_units' | 'prev_conversion' | 'prev_sales' | 'prev_asp' | 'prev_spend' | 'prev_acos' | 'campaign_name' | 'state' | 'target_acos' | 'bid' | 'bid_min' | 'bid_max' | 'ad_group_count' | 'ad_group_name' | 'ad_group_id';

/**
 * Every key a review table or export can sort by.
 *
 * Which keys a level or export grain accepts is checked by the service
 * (422 ``Invalid sort_by: <key>``), as VentaHub's per-level ``sort_exprs``
 * allowlist did (RR:110-117, RS:662-671).
 */
export const ReviewSortKey = {
    NAME: 'name',
    CURR_IMPRESSIONS: 'curr_impressions',
    CURR_CLICKS: 'curr_clicks',
    CURR_CTR: 'curr_ctr',
    CURR_CPC: 'curr_cpc',
    CURR_UNITS: 'curr_units',
    CURR_CONVERSION: 'curr_conversion',
    CURR_SALES: 'curr_sales',
    CURR_ASP: 'curr_asp',
    CURR_SPEND: 'curr_spend',
    CURR_ACOS: 'curr_acos',
    PREV_IMPRESSIONS: 'prev_impressions',
    PREV_CLICKS: 'prev_clicks',
    PREV_CTR: 'prev_ctr',
    PREV_CPC: 'prev_cpc',
    PREV_UNITS: 'prev_units',
    PREV_CONVERSION: 'prev_conversion',
    PREV_SALES: 'prev_sales',
    PREV_ASP: 'prev_asp',
    PREV_SPEND: 'prev_spend',
    PREV_ACOS: 'prev_acos',
    CAMPAIGN_NAME: 'campaign_name',
    STATE: 'state',
    TARGET_ACOS: 'target_acos',
    BID: 'bid',
    BID_MIN: 'bid_min',
    BID_MAX: 'bid_max',
    AD_GROUP_COUNT: 'ad_group_count',
    AD_GROUP_NAME: 'ad_group_name',
    AD_GROUP_ID: 'ad_group_id'
} as const;

/**
 * A store the review covers, as the summary lists it.
 */
export type ReviewStore = {
    store_id: number;
    store_name: string | null;
    marketplace_name: string | null;
};

/**
 * One document still waiting for a review, for the page's picker.
 *
 * Attributes:
 * entity_id: The document's key: what a decision names.
 * code: What people call it: the order code, invoice or note number.
 */
export type ReviewableDocumentRead = {
    entity_id: number;
    code: string;
};

/**
 * Body of ``PUT /v1/stores/{id}/connections/{surface}``: new credentials.
 *
 * Attributes:
 * credentials: The full set, per the surface's spec.
 * issued_at: When the new secret was issued.
 * expires_at: When it expires.
 * expiry_source: Where that date came from.
 * apply_to: ``same_client`` writes them to every store in the tenant
 * whose current credentials are for the same application.
 */
export type RotateRequest = {
    credentials: {
        [key: string]: string;
    };
    issued_at?: string | null;
    expires_at?: string | null;
    expiry_source?: DeclaredExpirySource;
    apply_to?: ApplyTo;
};

/**
 * Which stores now hold the new credentials.
 */
export type RotateResult = {
    updated_store_ids: Array<number>;
};

/**
 * Changes proposed to one row.
 *
 * Attributes:
 * id: The row's ``id`` as the dataset lists it.
 * changes: Column to its new value; every column must be editable.
 */
export type RowEdit = {
    id: number;
    changes: {
        [key: string]: CellValue;
    };
};

/**
 * How one scraper run went.
 *
 * Attributes:
 * device_id: The agent that ran it.
 * scraper_key: Which scraper, as assigned.
 * cycle_id: The agent's id for this cycle. A report repeated with the
 * same cycle and scraper is recorded once.
 * started_at: When the exe started.
 * finished_at: When it exited.
 * status: How it ended.
 * exit_code: The exe's exit code; absent when it was killed.
 * items_ok: Items it reported as done.
 * items_failed: Items it reported as failed.
 * items_total: Both together.
 * error_msg: Why it failed, when the agent knows.
 * log_tail: The exe's last output lines, oldest first.
 */
export type RunCreate = {
    device_id: string;
    scraper_key: ScraperKey;
    cycle_id?: string | null;
    started_at?: string | null;
    finished_at?: string | null;
    status: RunStatus;
    exit_code?: number | null;
    items_ok?: number;
    items_failed?: number;
    items_total?: number;
    error_msg?: string | null;
    log_tail?: Array<string>;
};

/**
 * What a run was asked for: an answer, or a business snapshot.
 */
export type RunMode = 'chat' | 'snapshot';

/**
 * What a run was asked for: an answer, or a business snapshot.
 */
export const RunMode = {
    CHAT: 'chat',
    SNAPSHOT: 'snapshot'
} as const;

/**
 * A run report, as stored.
 *
 * Attributes:
 * id: ``app_scraper_run.pk_run_id``.
 * recorded: False when this cycle's report for this scraper was already
 * stored and this one was a repeat.
 */
export type RunRead = {
    id: number;
    recorded: boolean;
};

/**
 * Where a run or one of its steps has got to.
 *
 * Stored as text rather than a PostgreSQL enum, matching this service's
 * existing choice: a new state is then an insert in the application and not
 * an `ALTER TYPE` that has to be sequenced against a deploy.
 */
export type RunState = 'pending' | 'running' | 'awaiting_external' | 'partial' | 'retrying' | 'blocked' | 'succeeded' | 'failed';

/**
 * Where a run or one of its steps has got to.
 *
 * Stored as text rather than a PostgreSQL enum, matching this service's
 * existing choice: a new state is then an insert in the application and not
 * an `ALTER TYPE` that has to be sequenced against a deploy.
 */
export const RunState = {
    PENDING: 'pending',
    RUNNING: 'running',
    AWAITING_EXTERNAL: 'awaiting_external',
    PARTIAL: 'partial',
    RETRYING: 'retrying',
    BLOCKED: 'blocked',
    SUCCEEDED: 'succeeded',
    FAILED: 'failed'
} as const;

/**
 * How one scraper run ended, as the agent judged it from the exe.
 */
export type RunStatus = 'success' | 'partial' | 'failed' | 'crashed' | 'skipped';

/**
 * How one scraper run ended, as the agent judged it from the exe.
 */
export const RunStatus = {
    SUCCESS: 'success',
    PARTIAL: 'partial',
    FAILED: 'failed',
    CRASHED: 'crashed',
    SKIPPED: 'skipped'
} as const;

/**
 * Where the window's marketplace orders ship to.
 *
 * Orders and units count every currency's stores; sales only the
 * currency's, unless converted, when they are every convertible
 * currency's, converted.
 *
 * Attributes:
 * currency: The sales' currency.
 * orders: Every order with a known country.
 * units: Their units.
 * rows: One per country, most orders first.
 * fx: How the sales were converted; ``null`` without ``convert_to``.
 */
export type SalesByCountry = {
    currency: string | null;
    orders: number;
    units: number;
    rows: Array<CountrySales>;
    fx?: FxMeta | null;
};

/**
 * One line of a sales invoice.
 */
export type SalesInvoiceLineRead = {
    id?: number | null;
    sku_code: string | null;
    sku_name: string | null;
    box_code: string | null;
    currency: string | null;
    quantity: number | null;
    price: string | null;
    cost: string | null;
    vat_rate: string | null;
    expiry_date: string | null;
};

/**
 * What the filters on this page can be chosen from.
 */
export type SalesInvoiceLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * One row of the sales invoices list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type SalesInvoiceRead = {
    id: number;
    date: string | null;
    number: string | null;
    warehouse_id: number | null;
    warehouse: string | null;
    party_id: number | null;
    party: string | null;
    currency: string | null;
    po_id: number | null;
    units: number | null;
    amount_wo_tax: string | null;
    amount: string | null;
    received: string | null;
    cndn_value: string | null;
    credit_period: number | null;
    dispatch_date: string | null;
    delivery_date: string | null;
    tracking_number: string | null;
    valid: boolean;
};

/**
 * The sales and marketing panel's figures.
 *
 * Attributes:
 * currency: Every amount's currency.
 * currencies: Every currency the caller's stores sell in, to pick from.
 * date_from: The window's first day.
 * date_to: Its last day.
 * sections: What the caller may see: seller sales, vendor and DF sales, ads.
 * figures: One per metric the sections allow.
 * days: Every day of the window (up to 400), for the cards' small charts.
 * built_at: When the marts were last built.
 * scope: How much of the organization the figures cover (:class:`AccessScope`).
 * fx: How the amounts were converted; ``null`` without ``convert_to``.
 */
export type SalesMarketing = {
    currency: string | null;
    currencies: Array<string>;
    date_from: string;
    date_to: string;
    sections: Array<DashboardSection>;
    figures: Array<MetricFigure>;
    days: Array<DayFigures>;
    built_at: string | null;
    scope?: AccessScope | null;
    fx?: FxMeta | null;
};

/**
 * A saved snapshot; ``data`` only when one report is read.
 */
export type SavedReportRead = {
    id: string;
    title: string;
    period: Period;
    created_at: string;
    conversation_id?: string | null;
    data?: SnapshotData | null;
};

/**
 * Body of ``POST /reports``: save a snapshot answer.
 */
export type SavedReportWrite = {
    run_id: string;
    title?: string | null;
};

/**
 * The one word the card leads with.
 *
 * ``healthy``: the last publish landed. ``failing``: the last one or more
 * failed and a retry is queued. ``not_configured``: this deployment has no
 * scope-graph credential, so nothing is published. ``never_published``: no
 * publish has landed yet for this organization.
 */
export type ScopeGraphHealth = 'healthy' | 'failing' | 'not_configured' | 'never_published';

/**
 * The one word the card leads with.
 *
 * ``healthy``: the last publish landed. ``failing``: the last one or more
 * failed and a retry is queued. ``not_configured``: this deployment has no
 * scope-graph credential, so nothing is published. ``never_published``: no
 * publish has landed yet for this organization.
 */
export const ScopeGraphHealth = {
    HEALTHY: 'healthy',
    FAILING: 'failing',
    NOT_CONFIGURED: 'not_configured',
    NEVER_PUBLISHED: 'never_published'
} as const;

/**
 * Where publishing this organization's access scopes to VentaWiz-Auth stands.
 *
 * Attributes:
 * health: See :class:`ScopeGraphHealth`.
 * last_attempt_at: The last attempt, whatever its outcome.
 * last_success_at: The last one that landed.
 * last_outcome: ``published``, ``failed`` or ``not_configured``.
 * node_count: Entities the last attempt sent.
 * graph_version: VentaWiz-Auth's version after the last success.
 * last_error: Why the last attempt failed; no URL, token or body.
 * consecutive_failures: Failures since the last success.
 * alerted_at: When the alert recipients were told of the current
 * failures; ``None`` until :data:`~ventawiz.scope_graph.service.ALERT_AFTER`.
 * queued: A publish is waiting to run.
 * next_attempt_at: When it is due.
 */
export type ScopeGraphStatus = {
    health: ScopeGraphHealth;
    last_attempt_at?: string | null;
    last_success_at?: string | null;
    last_outcome?: string | null;
    node_count?: number | null;
    graph_version?: number | null;
    last_error?: string | null;
    consecutive_failures?: number;
    alerted_at?: string | null;
    queued?: boolean;
    next_attempt_at?: string | null;
};

/**
 * One pickable store, brand or warehouse.
 */
export type ScopeOption = {
    id: number;
    name: string;
};

/**
 * What the person may pin: from their own access.
 *
 * Attributes:
 * marketplaces: Marketplace names of the stores, for tailored examples.
 */
export type ScopeOptions = {
    stores: Array<ScopeOption>;
    brands: Array<ScopeOption>;
    warehouses: Array<ScopeOption>;
    marketplaces?: Array<string>;
};

/**
 * Optional narrowing chosen in the UI. Pins only narrow what the person may see.
 *
 * Attributes:
 * store_ids: Only these stores.
 * brand_ids: Only these brands.
 * date_from: The period's start, as guidance to the analyst.
 * date_to: Its end.
 */
export type ScopePins = {
    store_ids?: Array<number> | null;
    brand_ids?: Array<number> | null;
    date_from?: string | null;
    date_to?: string | null;
};

/**
 * The rows one permission reaches when it is not tenant-wide.
 *
 * Attributes:
 * permission: The permission name, e.g. ``ads:view``.
 * store_ids: Stores granted directly.
 * brand_ids: Brands granted; they reach every store selling them.
 * marketplace_ids: Marketplaces granted; they reach every store on them.
 * warehouse_ids: Warehouses granted; they reach the vendors supplying them.
 * retailer_ids: Retailers granted.
 * service_provider_ids: Service providers granted.
 * vendor_ids: Vendors granted directly.
 * all_of_kinds: The kinds held on every entity - ``store`` for "All
 * stores" - each reaching what one id of that kind would, for every
 * entity of the kind, those created later included. That kind's ids
 * list is then empty.
 */
export type ScopedAccessRead = {
    permission: string;
    store_ids: Array<number>;
    brand_ids: Array<number>;
    marketplace_ids: Array<number>;
    warehouse_ids: Array<number>;
    retailer_ids: Array<number>;
    service_provider_ids: Array<number>;
    vendor_ids: Array<number>;
    all_of_kinds?: Array<string>;
};

/**
 * What the Scraped data page offers to filter by, for the caller's tenant.
 *
 * Attributes:
 * marketplaces: Every marketplace a scraper exists for or is planned.
 * stores: The tenant's live stores on those marketplaces, by name.
 */
export type ScrapedDataFilters = {
    marketplaces: Array<ScraperMarketplace>;
    stores: Array<ScrapedStoreOption>;
};

/**
 * A price read off a results page, in the store's marketplace currency.
 *
 * Attributes:
 * amount: A decimal string, so no client rounds it through a float.
 * currency: ISO 4217, from ``com_marketplace.currency_code``.
 */
export type ScrapedPrice = {
    amount: string;
    currency: string;
};

/**
 * One product card from a results page.
 *
 * Unknown fields are ignored for the same reason as on
 * :class:`ReviewResultCreate`: the scrapers also send a title and a
 * delivery text that nothing stores.
 *
 * Attributes:
 * position: Where the card sat on the page, 1 first. Orders the results
 * when given; otherwise the order sent is kept.
 * mp_product_id: The marketplace's id for the product. ``asin`` and
 * ``sku`` are accepted too, which is what the scrapers sent to
 * VentaHub. A card without one is dropped — it is a placeholder for
 * a card the scraper could not read.
 * is_sponsored: An ad rather than an organic result.
 * price: As printed, in any format :func:`parse_price` reads.
 * rating: Average star rating, 0 to 5.
 * review_count: How many ratings it averages. ``reviewCount`` is
 * accepted too.
 * delivery_days: Days until the promised delivery. Always absent on
 * Flipkart, whose results page shows none.
 */
export type ScrapedProduct = {
    position?: number | null;
    mp_product_id?: string | null;
    is_sponsored: boolean;
    price?: number | string | null;
    rating?: number | string | null;
    review_count?: number | null;
    delivery_days?: number | null;
};

/**
 * One sponsored or organic result of a search.
 *
 * Attributes:
 * slot: Its position among its kind, 1 to 5.
 * retailer_sku: The marketplace's id for the product.
 * price: Its price, when the page showed one.
 * rating: Its average star rating.
 * rating_count: How many ratings it averages.
 * delivery_days: Days until its promised delivery.
 */
export type ScrapedResultSlot = {
    slot: number;
    retailer_sku: string | null;
    price: ScrapedPrice | null;
    rating: string | null;
    rating_count: number | null;
    delivery_days: number | null;
};

/**
 * One focused listing's latest scraped product page.
 *
 * Attributes:
 * product_id: ``marketplace_listed_product.pk_id``.
 * mp_product_id: The ASIN, FSN or zsku.
 * title: The listing's title, when the catalogue has one.
 * store_id: The store the listing belongs to.
 * store_name: Its name.
 * short_code: Its marketplace country.
 * country_code: The marketplace's country, ISO 3166 — which storefront
 * a product id links to.
 * marketplace: Its scraper family.
 * rating: Average star rating, 0 to 5.
 * rating_count: How many ratings it averages.
 * has_aplus_content: Whether the page had A+ content.
 * delivery_promise_days: Days until the promised delivery.
 * critical_reviews: The low-star reviews read off the page, as the
 * scraper sent them.
 * updated_at: When this result was scraped.
 */
export type ScrapedReviewRow = {
    product_id: number;
    mp_product_id: string | null;
    title: string | null;
    store_id: number;
    store_name: string;
    short_code: string;
    country_code: string | null;
    marketplace: ScraperMarketplace;
    rating: string | null;
    rating_count: number | null;
    has_aplus_content: boolean | null;
    delivery_promise_days: number | null;
    critical_reviews: Array<{
        [key: string]: unknown;
    }>;
    updated_at: string | null;
};

/**
 * One stored search: what a tracked term showed on one day.
 *
 * Attributes:
 * id: ``marketing_focus_search_term_scrapper_data.pk_id``.
 * search_term: The term searched.
 * store_id: The store the term is tracked for.
 * store_name: Its name.
 * short_code: Its marketplace country.
 * country_code: The marketplace's country, ISO 3166 — which storefront
 * each result's product id links to.
 * marketplace: Its scraper family.
 * sponsored: The sponsored results, in page order. Only filled slots.
 * organic: The organic results, in page order. Only filled slots.
 * scraped_at: When the search ran.
 */
export type ScrapedSearchRow = {
    id: number;
    search_term: string;
    store_id: number;
    store_name: string;
    short_code: string;
    country_code: string | null;
    marketplace: ScraperMarketplace;
    sponsored: Array<ScrapedResultSlot>;
    organic: Array<ScrapedResultSlot>;
    scraped_at: string;
};

/**
 * A store the Scraped data page can filter by.
 *
 * The same shape the AI listing store picker reads, so the dashboard picks
 * stores with one component everywhere.
 *
 * Attributes:
 * id: ``com_store.pk_store_id``.
 * name: As the business calls it.
 * short_code: Its marketplace country, e.g. ``AZAE``.
 * marketplace: Which scraper family serves it.
 * marketplace_name: For people, e.g. ``Amazon UAE``.
 * country_code: ISO 3166-1 alpha-2, e.g. ``AE``.
 * marketplace_id: The marketplace's own id where it has one — Amazon's,
 * e.g. ``A2VIGQ35RCS4UG`` — and the short code where it has none.
 */
export type ScrapedStoreOption = {
    id: number;
    name: string;
    short_code: string;
    marketplace: ScraperMarketplace;
    marketplace_name: string;
    country_code: string;
    marketplace_id: string;
};

/**
 * Every scraper an agent can be told to run.
 */
export type ScraperKey = 'amazon-ads' | 'amazon-reviews' | 'flipkart-ads' | 'flipkart-reviews' | 'noon-ads' | 'noon-reviews' | 'otto-ads' | 'otto-reviews' | 'bol-ads' | 'bol-reviews' | 'walmart-ads' | 'walmart-reviews' | 'trendyol-ads' | 'trendyol-reviews' | 'shopify-reviews';

/**
 * Every scraper an agent can be told to run.
 */
export const ScraperKey = {
    AMAZON_ADS: 'amazon-ads',
    AMAZON_REVIEWS: 'amazon-reviews',
    FLIPKART_ADS: 'flipkart-ads',
    FLIPKART_REVIEWS: 'flipkart-reviews',
    NOON_ADS: 'noon-ads',
    NOON_REVIEWS: 'noon-reviews',
    OTTO_ADS: 'otto-ads',
    OTTO_REVIEWS: 'otto-reviews',
    BOL_ADS: 'bol-ads',
    BOL_REVIEWS: 'bol-reviews',
    WALMART_ADS: 'walmart-ads',
    WALMART_REVIEWS: 'walmart-reviews',
    TRENDYOL_ADS: 'trendyol-ads',
    TRENDYOL_REVIEWS: 'trendyol-reviews',
    SHOPIFY_REVIEWS: 'shopify-reviews'
} as const;

/**
 * What a scraper reads.
 */
export type ScraperKind = 'ads' | 'reviews';

/**
 * What a scraper reads.
 */
export const ScraperKind = {
    ADS: 'ads',
    REVIEWS: 'reviews'
} as const;

/**
 * A marketplace a scraper agent is built for.
 *
 * One agent exe per marketplace, each covering every country of it, so a
 * marketplace here is a family of ``com_marketplace`` rows rather than one.
 */
export type ScraperMarketplace = 'amazon' | 'flipkart' | 'noon' | 'otto' | 'bol' | 'walmart' | 'trendyol' | 'shopify';

/**
 * A marketplace a scraper agent is built for.
 *
 * One agent exe per marketplace, each covering every country of it, so a
 * marketplace here is a family of ``com_marketplace`` rows rather than one.
 */
export const ScraperMarketplace = {
    AMAZON: 'amazon',
    FLIPKART: 'flipkart',
    NOON: 'noon',
    OTTO: 'otto',
    BOL: 'bol',
    WALMART: 'walmart',
    TRENDYOL: 'trendyol',
    SHOPIFY: 'shopify'
} as const;

/**
 * Which queries to show, by whether our ads touched them.
 */
export type SearchAnalyticsFilter = 'all' | 'with_marketing' | 'without_marketing';

/**
 * Which queries to show, by whether our ads touched them.
 */
export const SearchAnalyticsFilter = {
    ALL: 'all',
    WITH_MARKETING: 'with_marketing',
    WITHOUT_MARKETING: 'without_marketing'
} as const;

/**
 * Which metric ranks the queries.
 */
export type SearchAnalyticsOrder = 'impressions' | 'clicks' | 'purchases';

/**
 * Which metric ranks the queries.
 */
export const SearchAnalyticsOrder = {
    IMPRESSIONS: 'impressions',
    CLICKS: 'clicks',
    PURCHASES: 'purchases'
} as const;

/**
 * A page of queries plus the summary over all of them.
 *
 * Attributes:
 * window_start: The first day counted — the requested start moved back
 * to its Sunday, because Brand Analytics reports whole weeks.
 * window_end: The last day counted.
 * summary: Totals across every matching query.
 */
export type SearchAnalyticsPage = {
    items: Array<SearchQueryRow>;
    pagination: Pagination;
    window_start: string;
    window_end: string;
    summary: SearchAnalyticsSummary;
};

/**
 * Whose numbers rank the queries: the market's, our ASINs', or our ads'.
 */
export type SearchAnalyticsSource = 'total' | 'our' | 'marketing';

/**
 * Whose numbers rank the queries: the market's, our ASINs', or our ads'.
 */
export const SearchAnalyticsSource = {
    TOTAL: 'total',
    OUR: 'our',
    MARKETING: 'marketing'
} as const;

/**
 * Sums over every query matching the filters, not just the page.
 *
 * Attributes:
 * total_impressions: Market impressions.
 * our_impressions: Our ASINs' impressions.
 * total_clicks: Market clicks.
 * our_clicks: Our ASINs' clicks.
 * total_purchases: Market purchases.
 * our_purchases: Our ASINs' purchases.
 * marketing_units: Units our ads sold.
 */
export type SearchAnalyticsSummary = {
    total_impressions: number;
    our_impressions: number;
    total_clicks: number;
    our_clicks: number;
    total_purchases: number;
    our_purchases: number;
    marketing_units: number;
};

/**
 * One of our ad keywords that served on a search query.
 *
 * Attributes:
 * keyword: The keyword or target text.
 * match_type: EXACT, PHRASE or BROAD, as reported.
 * impressions: Impressions on this query.
 * clicks: Clicks.
 * units: Units sold.
 * conversion: Units / clicks, or null with no clicks.
 * sales: Per currency.
 */
export type SearchQueryKeyword = {
    keyword: string;
    match_type: string;
    impressions: number;
    clicks: number;
    units: number;
    conversion: number | null;
    sales: Array<AdsMoney>;
};

/**
 * Which of our keywords served on one query.
 *
 * Attributes:
 * search_query: The query.
 * items: Keywords, most impressions first.
 */
export type SearchQueryKeywords = {
    search_query: string;
    items: Array<SearchQueryKeyword>;
};

/**
 * One customer search query over the window.
 *
 * "Total" is the whole market, from Brand Analytics; "our" is our ASINs'
 * share of it; "marketing" is what our Sponsored Ads recorded for the same
 * query text.
 *
 * Attributes:
 * search_query: The query, as Amazon reported it.
 * has_marketing: Whether our ads served on it at all.
 * total_impressions: Market impressions.
 * our_impressions: Our ASINs' impressions.
 * marketing_impressions: Our ads' impressions.
 * total_clicks: Market clicks.
 * our_clicks: Our ASINs' clicks.
 * marketing_clicks: Our ads' clicks.
 * total_purchases: Market purchases.
 * our_purchases: Our ASINs' purchases.
 * marketing_units: Units our ads sold.
 * total_ctr: Market clicks / impressions, or null with no impressions.
 * our_ctr: Ours, likewise.
 * marketing_ctr: Our ads', likewise.
 * marketing_conversion: Ads units / ads clicks, or null with no clicks.
 * marketing_sales: Ads sales, one entry per currency — a query sold in
 * two marketplaces is not summed across currencies.
 */
export type SearchQueryRow = {
    search_query: string;
    has_marketing: boolean;
    total_impressions: number;
    our_impressions: number;
    marketing_impressions: number;
    total_clicks: number;
    our_clicks: number;
    marketing_clicks: number;
    total_purchases: number;
    our_purchases: number;
    marketing_units: number;
    total_ctr: number | null;
    our_ctr: number | null;
    marketing_ctr: number | null;
    marketing_conversion: number | null;
    marketing_sales: Array<AdsMoney>;
};

/**
 * One Search Term Data form entry (CRS:46-62).
 *
 * The term is stored lower-cased and trimmed, as VentaHub stored it. Every
 * field is required here, as the form required it; the slot and bid-action
 * vocabularies are the table's check constraints.
 */
export type SearchTermDataCreate = {
    search_term: string;
    client_retailer_sku: string;
    comp1_retailer_sku: string;
    comp2_retailer_sku: string;
    client_price: number | string;
    comp1_price: number | string;
    comp2_price: number | string;
    client_delivery_days: number;
    comp1_delivery_days: number;
    comp2_delivery_days: number;
    client_slot: 'organic' | 'tos' | 'ros' | 'ba';
    comp1_slot: 'organic' | 'tos' | 'ros' | 'ba';
    comp2_slot: 'organic' | 'tos' | 'ros' | 'ba';
    bid_actions: 'bid_inc' | 'bid_dec' | 'pause' | 'no_action';
};

/**
 * The saved entry's id, and VentaHub's confirmation text.
 */
export type SearchTermDataCreated = {
    id: number;
    message: string;
};

/**
 * One of the caller's own form entries (CRS:137-163).
 */
export type SearchTermDataRead = {
    pk_id: number;
    fk_store_id: number;
    store_name?: string | null;
    search_term: string;
    client_retailer_sku: string;
    comp1_retailer_sku: string;
    comp2_retailer_sku: string;
    client_price: string;
    comp1_price: string;
    comp2_price: string;
    client_delivery_days: number;
    comp1_delivery_days: number;
    comp2_delivery_days: number;
    dt_create: string;
    bid_actions?: string | null;
    client_slot?: string | null;
    comp1_slot?: string | null;
    comp2_slot?: string | null;
};

/**
 * One search to run.
 *
 * Attributes:
 * store_id: The store the term is tracked for.
 * short_code: The marketplace country, e.g. ``AZAE``. Decides which
 * site the scraper searches.
 * search_term: What to type into the search box.
 */
export type SearchTermQueryItem = {
    store_id: number;
    short_code: string;
    search_term: string;
};

/**
 * What one search showed.
 *
 * Attributes:
 * store_id: The store the term is tracked for.
 * search_term: The term searched, as handed out by the queue.
 * products: The cards read, sponsored and organic together. Only the
 * first five of each kind are kept. Empty is valid: a search that
 * showed nothing is still a search that ran today.
 */
export type SearchTermResultCreate = {
    store_id: number;
    search_term: string;
    products?: Array<ScrapedProduct>;
};

/**
 * A stored search result.
 *
 * Attributes:
 * id: ``marketing_focus_search_term_scrapper_data.pk_id``.
 * store_id: The store the term is tracked for.
 * search_term: The term searched.
 * sponsored: How many sponsored results were stored, 0 to 5.
 * organic: How many organic results were stored, 0 to 5.
 * created_at: When it was stored. Also what "already scraped today" is
 * decided by.
 */
export type SearchTermResultRead = {
    id: number;
    store_id: number;
    search_term: string;
    sponsored: number;
    organic: number;
    created_at: string;
};

/**
 * What the filters can be chosen from: the warehouses. Stores follow the warehouse.
 */
export type SellerFlexLookupsRead = {
    warehouses: Array<NamedRead>;
};

/**
 * One live count: a SKU's units a store's marketplace holds in our warehouse.
 *
 * Attributes:
 * age_days: Days since the count. Worked out here so every page warns at
 * the same age: the number is only as good as the day it was true.
 */
export type SellerFlexRowRead = {
    id: number;
    store_id: number;
    store: string | null;
    warehouse_id: number;
    warehouse: string | null;
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    quantity: number;
    as_of_date: string;
    age_days: number;
};

/**
 * A store served from the chosen warehouse.
 *
 * Attributes:
 * id: The store.
 * name: Its name.
 * marketplace: The marketplace it sells on, when known.
 */
export type SellerFlexStoreRead = {
    id: number;
    name: string;
    marketplace?: string | null;
};

/**
 * The filtered register at a glance.
 *
 * Attributes:
 * rows: Live counts.
 * units: Their total: what comes off the marketplace's reading.
 * newest: The latest count date, or ``None`` with no counts.
 * oldest: The earliest, which is the one most likely to be stale.
 */
export type SellerFlexSummaryRead = {
    rows: number;
    units: number;
    newest: string | null;
    oldest: string | null;
};

/**
 * Body of ``POST /v1/service-providers``.
 *
 * Attributes:
 * kind: See :class:`~ventawiz.models.registry.ServiceProviderKind`.
 * contact_email: Where to reach them.
 * country: ISO 3166-1 alpha-2.
 */
export type ServiceProviderCreate = {
    code: string;
    name: string;
    kind?: ServiceProviderKind;
    contact_email?: string | null;
    country?: string | null;
};

/**
 * What a provider does, as the registry API offers it.
 *
 * ``ops_sp.service_type`` is free text in v1 and holds other values; these are
 * the ones a new provider is created with.
 */
export type ServiceProviderKind = 'agency' | 'logistics' | 'accounting' | 'other';

/**
 * What a provider does, as the registry API offers it.
 *
 * ``ops_sp.service_type`` is free text in v1 and holds other values; these are
 * the ones a new provider is created with.
 */
export const ServiceProviderKind = {
    AGENCY: 'agency',
    LOGISTICS: 'logistics',
    ACCOUNTING: 'accounting',
    OTHER: 'other'
} as const;

/**
 * An outside party doing work for the tenant (``ops_sp``).
 *
 * Attributes:
 * kind: What they do. Free text in v1 (``service_type``), so a v1 row may
 * hold a value outside :class:`ServiceProviderKind`.
 * contact_email: A business address, not an individual's.
 * country: As stored; see :class:`WarehouseRead`.
 */
export type ServiceProviderRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    kind: string | null;
    contact_email: string | null;
    country: string | null;
};

/**
 * The 3PL running a warehouse, as a list names it.
 */
export type ServiceProviderRef = {
    id: number;
    name: string;
};

/**
 * Body of ``PATCH /v1/service-providers/{id}``.
 *
 * Attributes:
 * kind: What they do.
 * contact_email: Where to reach them.
 * country: ISO 3166-1 alpha-2.
 */
export type ServiceProviderUpdate = {
    name?: string | null;
    kind?: ServiceProviderKind | null;
    contact_email?: string | null;
    country?: string | null;
};

/**
 * The store's Amazon Ads profile and the account behind it.
 */
export type SettingsAds = {
    profile_id: number | null;
    account_meta: AdsAccountMeta | null;
};

/**
 * A person responsible for the store.
 */
export type SettingsContact = {
    kind: StoreContactKind;
    principal_id: string | null;
    name: string | null;
    email: string | null;
    phone: string | null;
    role: string | null;
};

/**
 * What the store is. ``timezone`` overrides the marketplace's; ``null`` uses it.
 *
 * Attributes:
 * brand_ids: The brands it sells, ascending; ``[]`` for none. A set:
 * there is no primary brand.
 */
export type SettingsDetails = {
    name: string;
    brand_ids: Array<number>;
    store_type: string;
    is_retail: boolean;
    ads_timezone: string | null;
    timezone: string | null;
};

/**
 * One feature switch. ``changed_*`` are ``null`` for one never switched.
 */
export type SettingsFeature = {
    feature: StoreFeatureName;
    label: string;
    enabled: boolean;
    changed_by: string | null;
    changed_at: string | null;
};

/**
 * How the store's orders are fulfilled.
 *
 * Attributes:
 * mcf_sources: The stores whose orders this store fulfils through MCF.
 * shopify: ``marketplace_meta.shopify``, as stored; ``null`` when none.
 */
export type SettingsFulfilment = {
    mcf_sources: Array<McfSourceRead>;
    shopify: {
        [key: string]: unknown;
    } | null;
};

/**
 * A group the store is in.
 */
export type SettingsGroup = {
    id: number;
    kind: StoreGroupKind;
    name: string;
};

/**
 * What the store aims for. Rates are fractions: ``0.25`` is 25%.
 *
 * Attributes:
 * expected_margin_rate: ``com_store.expected_margin_rate``.
 * expected_acos: ``com_store.expected_acos``.
 * target_tacos: Ad spend over total sales.
 * monthly_sales_goal: Sales per calendar month, in ``currency``.
 * currency: The goal's currency; the marketplace's until one is saved.
 */
export type SettingsTargets = {
    expected_margin_rate: string;
    expected_acos: string | null;
    target_tacos: string | null;
    monthly_sales_goal: string | null;
    currency: string;
};

/**
 * A dv-auth principal assigned to the store.
 *
 * Attributes:
 * principal_id: Who.
 * display: Their name as the client sent it when assigning them; the
 * client resolves current names from dv-auth.
 */
export type SettingsUser = {
    principal_id: string;
    display: string | null;
};

/**
 * A warehouse the store ships from; lower ``priority`` is filled first.
 */
export type SettingsWarehouse = {
    id: number;
    name: string;
    priority: number | null;
};

/**
 * One settlement line, VentaHub's Settlement Data columns.
 */
export type SettlementLineRow = {
    id: number;
    settlement_id: string;
    start_date: string | null;
    end_date: string | null;
    deposit_date: string | null;
    amount_total: string | null;
    order_id: string | null;
    sku_code: string | null;
    order_item_id: string | null;
    product_key: string | null;
    transaction_type: string | null;
    shipment_id: string | null;
    amount_type: string | null;
    amount: string | null;
    currency_code: string | null;
};

/**
 * A recorded settlement and where its invoice now stands.
 *
 * Attributes:
 * id: The settlement.
 * invoice_number: The invoice it settles.
 * amount: How much.
 * currency: In what.
 * invoice_amount: The invoice's amount.
 * invoice_settled: Received or paid against it so far, this included.
 */
export type SettlementRead = {
    id: number;
    invoice_number: string;
    amount: string;
    currency: string | null;
    invoice_amount: string | null;
    invoice_settled: string;
};

/**
 * One settlement transaction with its settlement.
 */
export type SettlementRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    settlement_id: string;
    start_date: string | null;
    end_date: string | null;
    deposit_date: string | null;
    settlement_total: string | null;
    order_id: string | null;
    order_item_id: string | null;
    asin: string | null;
    sku_code: string | null;
    transaction_type: string | null;
    shipment_id: string | null;
    amount_type: string | null;
    amount: string | null;
    posted_date: string | null;
    currency_code: string | null;
};

/**
 * A store, with what its marketplace calls its product and its upload sheet.
 */
export type SettlementStore = {
    id: number;
    name: string;
    marketplace: string;
    country_code: string | null;
    /**
     * ASIN, EAN, FSN, Item Number, …
     */
    key_header: string;
    template: string;
    template_columns: Array<string>;
};

/**
 * Money received or paid against one invoice.
 *
 * Attributes:
 * warehouse_id: The invoice's warehouse.
 * invoice_number: Its number there.
 * amount: How much. Positive.
 * date: When; today when left out.
 * currency: ISO 4217; must be the invoice's. Taken from it when left out.
 * comment: Free text.
 */
export type SettlementWrite = {
    warehouse_id: number;
    invoice_number: string;
    amount: number | string;
    date?: string | null;
    currency?: string | null;
    comment?: string | null;
};

/**
 * How loudly an alert asks for attention.
 */
export type Severity = 'critical' | 'warning' | 'info';

/**
 * How loudly an alert asks for attention.
 */
export const Severity = {
    CRITICAL: 'critical',
    WARNING: 'warning',
    INFO: 'info'
} as const;

/**
 * The focused-SKU sheet for one period.
 *
 * Every row is here: the list is one person's and small enough for the
 * frontend to search, filter, sort, page and total, as VentaHub's did.
 *
 * Attributes:
 * rows: One per SKU.
 * total: How many rows.
 * principal_id: Whose list.
 * grain: Weekly or monthly.
 * period: T.
 * previous: T-1.
 * trailing: The 90 days "3M Sales" covers.
 * period_days: Days in T.
 * is_editable: Whether T is the latest finished period, the only one whose
 * notes may be saved.
 * can_edit: Whether the caller may save notes at all.
 * warehouse_id: The warehouse stock and in transit are for.
 * warehouse_name: Its name, for the stock column's header.
 * store_id: The store, in store mode; ``null`` for all stores.
 * account_type: ``seller``, ``vendor`` or ``mixed`` across the rows.
 * retailer_sku_type: What the first column's id is, when every row agrees.
 * levels_as_of: When warehouse stock, FBA, in transit and price were
 * measured. Computed from the marts they are shown on the latest
 * period only, as they are now; a frozen period carries its own,
 * as of ``frozen_at``.
 * frozen_at: When the period was frozen, for a period read from its
 * frozen rows (store mode, a finished period): its figures, stock
 * included, are as they were then. ``null`` when computed from the
 * marts.
 * built_at: When the marts were last built.
 * data_through: The last day each source holds data for.
 * pending_listings: The person's listings the sheet cannot show yet:
 * added after the last build, or no longer live.
 * weeks: The weeks the picker offers.
 * months: The months the picker offers.
 * drr_miss_reasons: What a DRR Miss Reason may be.
 * stock_issues: What a Stock Issue may be.
 */
export type Sheet = {
    rows: Array<SheetRow>;
    total: number;
    principal_id: string;
    grain: FocusGrain;
    period: PeriodRead;
    previous: PeriodRead;
    trailing: PeriodRead;
    period_days: number;
    is_editable: boolean;
    can_edit: boolean;
    warehouse_id: number | null;
    warehouse_name: string | null;
    store_id: number | null;
    account_type: string | null;
    retailer_sku_type: string | null;
    levels_as_of: string | null;
    frozen_at?: string | null;
    built_at: string | null;
    data_through: DataThrough;
    pending_listings: number;
    weeks: Array<PeriodRead>;
    months: Array<PeriodRead>;
    drr_miss_reasons: Array<string>;
    stock_issues: Array<string>;
};

/**
 * A brand of a SKU on the caller's list.
 *
 * Attributes:
 * brand_id: The brand; ``null`` for SKUs with none.
 * brand_name: Its name.
 */
export type SheetBrand = {
    brand_id: number | null;
    brand_name: string;
};

/**
 * One column of an upload sheet, as the upload dialog explains it.
 *
 * Attributes:
 * name: The header as the template writes it.
 * requirement: ``required`` on every row, ``optional``, or ``either``: this
 * column or the one its description names.
 * description: What to put in it.
 * example: A value that would be accepted.
 * accepted: Every header the upload takes for this column, folded as
 * :func:`~ventawiz.kit.spreadsheets.normalise` folds them, so a page
 * can say a column is missing before the file is sent.
 */
export type SheetColumnRead = {
    name: string;
    requirement: 'required' | 'optional' | 'either';
    description: string;
    example: string;
    accepted: Array<string>;
};

/**
 * What the sheet's pickers can be set to, from the person's own list.
 *
 * Attributes:
 * warehouses: Warehouses linked to a store on the list.
 * stores: Stores on the list; one with no warehouse has none listed,
 * and its sheet is opened without a warehouse.
 * brands: Brands on the list.
 * weeks: Weeks to pick, newest first; the first is the editable one.
 * months: Months to pick, likewise.
 * drr_miss_reasons: What a DRR Miss Reason may be.
 * stock_issues: What a Stock Issue may be.
 */
export type SheetOptions = {
    warehouses: Array<SheetWarehouse>;
    stores: Array<SheetStore>;
    brands: Array<SheetBrand>;
    weeks: Array<PeriodRead>;
    months: Array<PeriodRead>;
    drr_miss_reasons: Array<string>;
    stock_issues: Array<string>;
};

/**
 * The start of a chosen sheet, read the way its upload will read it.
 *
 * Attributes:
 * headers: The header row as written.
 * keys: Each header folded the way the upload matches columns.
 * rows: The first rows, every cell as text (empty cells are ``None``).
 * total: Rows with anything in them, header excluded.
 * picked: The rows asked for by number, those that exist.
 */
export type SheetPreviewRead = {
    headers: Array<string>;
    keys: Array<string>;
    rows: Array<Array<string | null>>;
    total: number;
    picked?: Array<SheetRowRead>;
};

/**
 * One row of an uploaded sheet that was not taken.
 */
export type SheetProblemRead = {
    /**
     * Spreadsheet row number; the header is row 1
     */
    row: number;
    message: string;
};

/**
 * An uploaded stock sheet: queued, or refused with every problem it had.
 *
 * A sheet with any problem is not pushed at all — half a sheet is a stock
 * level nobody chose — so ``push`` is null whenever ``problems`` is not empty.
 */
export type SheetRead = {
    /**
     * SKU codes taken from the sheet
     */
    rows: number;
    problems?: Array<SheetProblemRead>;
    push?: PushAccepted | null;
};

/**
 * One SKU: in one store (store mode), or across the warehouse's stores.
 *
 * ``_t1`` fields are the period before. Counts are ``null`` where the source
 * has nothing for the SKU (no vendor report, no reading), and 0 where it
 * reported none. Fields under "Not loaded yet" in docs/focused-skus/plan.md
 * §6 are always ``null`` until VentaWiz loads their data.
 *
 * Attributes:
 * sku_id: The internal SKU: the row's identity.
 * sku_code: Its code ("SKU Code (OC)").
 * brand_id: Its brand.
 * brand_name: Its brand's name.
 * store_ids: The stores the row covers: one in store mode.
 * listed_product_id: The representative listing: the lowest id among
 * the person's listings of this SKU in the row's stores.
 * retailer_sku: Its marketplace product id ("ASIN").
 * retailer_sku_type: What that id is: ``asin``, ``ean``, ``zsku``, ...
 * seller_sku: Its seller SKU.
 * title: Its title.
 * account_type: ``seller``, ``vendor`` or ``mixed`` across the stores.
 * assigned_users: Everyone whose list holds the SKU in these stores.
 * a_plus_updated: Not loaded yet.
 * parent_asin: The parent product, from Amazon's traffic report.
 * sales_90d: "3M Sales": Total units over the 90 days ending on T's last day.
 * replen_status: The SKU's replenishment class at the warehouse: R, N or T.
 * warehouse_stock: Available units in the warehouse.
 * fba_stock: Sellable units marketplaces hold, each pool once, Seller
 * Flex apart.
 * seller_flex_stock: Seller Flex units marketplaces report: stock in our
 * own warehouse, so already in warehouse stock and not in Stock.
 * secondary_stock: Sellable units Amazon holds on our vendor accounts,
 * from its weekly inventory report.
 * unsellable: Unsellable units marketplaces hold; ``null`` when none reported.
 * stock: Warehouse stock + FBA stock + Secondary Stock.
 * cover_days: Days Stock lasts at T and T-1's rate of Total and Other
 * Seller Units; In transit is not counted.
 * stock_cover_healthy: Cover is 60 to 120 days: no stock issue to
 * report, so Stock Issue and its Action cannot be set.
 * intransit_quantity: Units in transit to the warehouse.
 * intransit_eta_days: Days until the earliest expected delivery still ahead.
 * stock_issue: The shared note's Stock Issue.
 * stock_issue_action: Its Action.
 * seller_units: Marketplace order units.
 * other_seller_units: Units the warehouse's Shopify and TikTok shops
 * sold; in cover, not in Total. ``null`` where it does not apply.
 * df_units: Direct-fulfilment invoice units.
 * of_units: Offline invoice units.
 * vo_units: Vendor-order invoice units.
 * po_units: Retailer purchase-order units; never in the total.
 * total_units: Seller + DF + OF + VO.
 * drr_planned: Planned units a day: the lowest listing plan per store,
 * summed across stores.
 * drr_target_met: The plan was met — every planned store's own T Total,
 * or its Amazon sell-out (T-1 on a weekly sheet, T on a monthly
 * one), reached plan x days — so there is no DRR Miss Reason to give.
 * sales_value: T's sales value, per currency.
 * drr_miss_reason: The shared note's reason.
 * action: Its Action.
 * note_versions: Each store's note version, keyed by store id; send them
 * back to save.
 * last_edited_at: When the note was last saved.
 * last_edited_by: By whom.
 * ams_clicks: Ad clicks.
 * ams_spend: Ad spend, per currency.
 * ams_attributed_sales: Ad-attributed sales, per currency.
 * ams_acos: Spend / ad sales x 100; ``null`` without ad sales or across
 * currencies.
 * ams_shared_asin: An advertised ASIN is listed under another SKU too,
 * whose row shows the same ads: do not add the two rows' ads up.
 * secondary_ordered_units: Amazon's vendor report: units ordered.
 * secondary_ordered_revenue: Its revenue, per currency.
 * secondary_customer_returns: Its customer returns.
 * secondary_glance_views: Glance views from the traffic report.
 * secondary_unit_session_pct: Units ordered / sessions x 100.
 * secondary_buy_box_pct: Buy-box page views / page views x 100.
 * secondary_shared_asin: One of the row's ASINs is listed under another
 * SKU in the same store, whose row shows the same Amazon reports: do not
 * add the two rows' Secondary figures up.
 * competitor_asin: Not loaded yet.
 * competitor_url: Not loaded yet.
 * competitor_price: Not loaded yet.
 * competitor_mrr: Not loaded yet.
 * current_price: The lowest price among the person's listings, per currency.
 * buy_box: Not loaded yet.
 * delivery_promise_days: Not loaded yet.
 * rating: Not loaded yet.
 * ranking: Not loaded yet.
 * ranking_t1: Not loaded yet.
 * ranking_as_of: Not loaded yet.
 * critical_reviews: Not loaded yet.
 * form_updates_30d: Not loaded yet.
 */
export type SheetRow = {
    sku_id: number;
    sku_code: string | null;
    brand_id: number | null;
    brand_name: string | null;
    store_ids: Array<number>;
    listed_product_id: number;
    retailer_sku: string | null;
    retailer_sku_type: string | null;
    seller_sku: string | null;
    title: string | null;
    account_type: string | null;
    assigned_users: Array<string>;
    a_plus_updated?: boolean | null;
    parent_asin: string | null;
    sales_90d: number;
    replen_status: string | null;
    warehouse_stock: number | null;
    fba_stock: number | null;
    seller_flex_stock: number | null;
    secondary_stock: number | null;
    unsellable: number | null;
    stock: number | null;
    cover_days: string | null;
    stock_cover_healthy: boolean;
    intransit_quantity: number | null;
    intransit_eta_days: number | null;
    stock_issue: string | null;
    stock_issue_action: string | null;
    seller_units: number;
    seller_units_t1: number;
    other_seller_units: number | null;
    other_seller_units_t1: number | null;
    df_units: number;
    df_units_t1: number;
    of_units: number;
    of_units_t1: number;
    vo_units: number;
    vo_units_t1: number;
    po_units: number;
    po_units_t1: number;
    total_units: number;
    total_units_t1: number;
    drr_planned: string | null;
    drr_target_met: boolean;
    sales_value: Array<MoneyRead>;
    drr_miss_reason: string | null;
    action: string | null;
    note_versions: {
        [key: string]: number;
    };
    last_edited_at: string | null;
    last_edited_by: string | null;
    ams_clicks: number;
    ams_clicks_t1: number;
    ams_spend: Array<MoneyRead>;
    ams_spend_t1: Array<MoneyRead>;
    ams_attributed_sales: Array<MoneyRead>;
    ams_attributed_sales_t1: Array<MoneyRead>;
    ams_acos: string | null;
    ams_acos_t1: string | null;
    ams_shared_asin: boolean;
    secondary_ordered_units: number | null;
    secondary_ordered_units_t1: number | null;
    secondary_ordered_revenue: Array<MoneyRead>;
    secondary_ordered_revenue_t1: Array<MoneyRead>;
    secondary_customer_returns: number | null;
    secondary_customer_returns_t1: number | null;
    secondary_glance_views: number | null;
    secondary_glance_views_t1: number | null;
    secondary_unit_session_pct: string | null;
    secondary_unit_session_pct_t1: string | null;
    secondary_buy_box_pct: string | null;
    secondary_buy_box_pct_t1: string | null;
    secondary_shared_asin: boolean;
    competitor_asin?: string | null;
    competitor_url?: string | null;
    competitor_price?: MoneyRead | null;
    competitor_mrr?: number | null;
    current_price: Array<MoneyRead>;
    buy_box?: boolean | null;
    delivery_promise_days?: number | null;
    rating?: string | null;
    ranking?: number | null;
    ranking_t1?: number | null;
    ranking_as_of?: string | null;
    critical_reviews?: number | null;
    form_updates_30d?: number | null;
};

/**
 * One row asked for by its number in the margin, header being 1.
 */
export type SheetRowRead = {
    row: number;
    cells: Array<string | null>;
};

/**
 * A store on the caller's list, for the store picker.
 *
 * Attributes:
 * store_id: The store.
 * store_name: Its name.
 * marketplace_code: Its marketplace.
 * account_type: ``seller``, ``vendor``, ...
 * warehouse_ids: The warehouses linked to it.
 */
export type SheetStore = {
    store_id: number;
    store_name: string | null;
    marketplace_code: string | null;
    account_type: string | null;
    warehouse_ids: Array<number>;
};

/**
 * A warehouse linked to a store on the caller's list.
 *
 * Attributes:
 * warehouse_id: The warehouse.
 * warehouse_name: Its name.
 */
export type SheetWarehouse = {
    warehouse_id: number;
    warehouse_name: string | null;
};

/**
 * A Shift Stock (or Transfer SKU Stock) request.
 *
 * Attributes:
 * warehouse_id: Where the boxes are.
 * shift_date: When the move happened, as Box History shows it. Now when
 * left out.
 * lines: The moves. All are made, or none.
 */
export type ShiftCreate = {
    warehouse_id: number;
    shift_date?: string | null;
    lines: Array<ShiftLine>;
};

/**
 * Units to move from one box and SKU to another box and SKU, in one warehouse.
 *
 * The same SKU on both sides is a Shift; a different one is VentaHub's Transfer
 * SKU Stock. Both are this line.
 *
 * Attributes:
 * from_box_code: The box the units leave. Upper-cased before lookup.
 * from_sku_code: Our SKU code, as it is in that box.
 * to_box_code: The box they arrive in. Created on the shelf when the
 * warehouse has no box with this code.
 * to_sku_code: Our SKU code they are held under there.
 * quantity: Units. Positive.
 * expiry_date: A specific batch. Leave out for "whichever batches the box
 * holds", drawn earliest expiry first; the batch travels with the units.
 */
export type ShiftLine = {
    from_box_code: string;
    from_sku_code: string;
    to_box_code: string;
    to_sku_code: string;
    quantity: number;
    expiry_date?: string | null;
};

/**
 * What a shift did.
 *
 * Attributes:
 * shift_count: Records written. A line drawing from two batches writes
 * two, so this can exceed the lines sent.
 * created_box_codes: Boxes that did not exist and were made, on the shelf.
 * shifts: Each record.
 */
export type ShiftRead = {
    shift_count: number;
    created_box_codes: Array<string>;
    shifts: Array<ShiftRecordRead>;
};

/**
 * One move as Box History's shift section shows it: one batch of one line.
 *
 * Attributes:
 * id: The shift record's key.
 * shift_date: When it happened.
 * from_box_code: The box the units left.
 * from_sku_code: The SKU they left as.
 * to_box_code: The box they arrived in.
 * to_sku_code: The SKU they arrived as.
 * quantity: Units.
 * expiry_date: Their batch.
 */
export type ShiftRecordRead = {
    id: number;
    shift_date: string;
    from_box_code: string;
    from_sku_code: string;
    to_box_code: string;
    to_sku_code: string;
    quantity: number;
    expiry_date: string | null;
};

/**
 * A new shipment between a vendor order and a purchase order.
 *
 * Attributes:
 * name: What people call it.
 * from_warehouse_id: The import warehouse: where the vendor order is.
 * vo_code: The vendor order it carries.
 * to_warehouse_id: The export warehouse: where the purchase order is.
 * po_code: The purchase order it fills.
 */
export type ShipmentCreate = {
    shipping_type?: ('Air' | 'Ocean_LCL' | 'Ocean_FCL') | null;
    cbm?: number | string | null;
    shipping_amount?: number | string | null;
    currency?: string | null;
    dispatch_date?: string | null;
    delivery_date?: string | null;
    tracking_number?: string | null;
    comment?: string | null;
    notification_emails?: Array<string> | null;
    name: string;
    from_warehouse_id: number;
    vo_code: string;
    to_warehouse_id: number;
    po_code: string;
};

/**
 * A shipment and how far it has got.
 */
export type ShipmentDetailRead = {
    shipment: GlobalShipmentRead;
    progress: ShipmentProgressRead;
};

/**
 * A change to a shipment. Fields left out keep their value.
 *
 * The two ends are fixed: a shipment carrying a different order is a
 * different shipment.
 *
 * Attributes:
 * name: What people call it.
 */
export type ShipmentEdit = {
    shipping_type?: ('Air' | 'Ocean_LCL' | 'Ocean_FCL') | null;
    cbm?: number | string | null;
    shipping_amount?: number | string | null;
    currency?: string | null;
    dispatch_date?: string | null;
    delivery_date?: string | null;
    tracking_number?: string | null;
    comment?: string | null;
    notification_emails?: Array<string> | null;
    name?: string | null;
};

/**
 * How far the shipment's vendor order / purchase order pair has got.
 *
 * Every figure counts only the SKUs on the shipment's own vendor order, so a
 * PO fed by several shipments shows each one its own slice.
 *
 * Attributes:
 * ordered_units: On this shipment's vendor order.
 * ordered_value: Their cost, in ``vo_currency``.
 * po_units: Of those SKUs, on the purchase order.
 * po_value: Their price, in ``po_currency``.
 * vo_units: On every vendor order backing the PO.
 * last_vo_date: The latest of those orders.
 * vi_units: Invoiced by vendors against those orders: received.
 * last_vi_date: The latest vendor invoice.
 * allotted_units: In boxes packed for the PO.
 * invoiced_units: Invoiced to the retailer against the PO.
 * invoiced_value: Their value, in ``po_currency``.
 * last_invoiced_date: The latest sales invoice.
 * vo_currency: The vendor order's currency.
 * po_currency: The purchase order's currency.
 */
export type ShipmentProgressRead = {
    ordered_units: number;
    ordered_value: string;
    po_units: number;
    po_value: string;
    vo_units: number;
    last_vo_date: string | null;
    vi_units: number;
    last_vi_date: string | null;
    allotted_units: number;
    invoiced_units: number;
    invoiced_value: string;
    last_invoiced_date: string | null;
    vo_currency: string | null;
    po_currency: string | null;
};

/**
 * A queued sync. Find its run with ``GET …/runs?request_id=``.
 */
export type ShopifySyncAccepted = {
    request_id: string;
    store_id: number;
    kind: ShopifySyncKind;
    start_date: string | null;
    end_date: string | null;
};

/**
 * What the run read from Shopify.
 */
export type ShopifySyncCounts = {
    pages: number;
    /**
     * Variants or orders read
     */
    rows: number;
    /**
     * Rows Shopify sent that could not be read
     */
    rejected: number;
    /**
     * New observations; zero when nothing changed since the last sync
     */
    observations_written: number;
};

/**
 * A sync to start: every variant, or the orders created in a range.
 */
export type ShopifySyncCreate = {
    kind: ShopifySyncKind;
    /**
     * Orders only: the first day, inclusive (UTC)
     */
    start_date?: string | null;
    /**
     * Orders only: the last day, inclusive (UTC)
     */
    end_date?: string | null;
};

/**
 * What a sync reads.
 */
export type ShopifySyncKind = 'products' | 'orders';

/**
 * What a sync reads.
 */
export const ShopifySyncKind = {
    PRODUCTS: 'products',
    ORDERS: 'orders'
} as const;

/**
 * What became of the rows in VentaWiz's tables.
 */
export type ShopifySyncLoadRead = {
    /**
     * running, succeeded or failed
     */
    state: string;
    /**
     * Rows considered
     */
    parsed: number;
    /**
     * Rows written to the listing or order tables
     */
    loaded: number;
    /**
     * Rows kept aside, each with its reason
     */
    rejected: number;
    /**
     * Why the load failed, when it did
     */
    error: string | null;
    finished_at: string | null;
};

/**
 * A row Shopify sent that could not be read.
 */
export type ShopifySyncParseRejectRead = {
    code: string;
    message: string;
    value: string | null;
};

/**
 * A row read from Shopify that was not loaded.
 */
export type ShopifySyncRejectRead = {
    /**
     * listings, listing_prices, orders or order_items
     */
    entity: string;
    /**
     * e.g. unresolved_reference: the SKU is not in the catalogue
     */
    reason: string;
    detail: string | null;
    /**
     * The SKU, or the order, the row was about
     */
    key: string | null;
};

/**
 * One sync, with what it read and what was kept aside.
 */
export type ShopifySyncRunDetail = {
    run_id: string;
    request_id: string | null;
    kind: ShopifySyncKind | null;
    start_date: string | null;
    end_date: string | null;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    /**
     * When it was launched
     */
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Null until the run has succeeded and its rows were loaded
     */
    load: ShopifySyncLoadRead | null;
    /**
     * Null until the run has reported
     */
    counts: ShopifySyncCounts | null;
    /**
     * Why the run failed, when it did
     */
    error: string | null;
    parse_rejects: Array<ShopifySyncParseRejectRead>;
    /**
     * The first 200
     */
    load_rejects: Array<ShopifySyncRejectRead>;
};

/**
 * One sync, as listed.
 */
export type ShopifySyncRunRead = {
    run_id: string;
    request_id: string | null;
    kind: ShopifySyncKind | null;
    start_date: string | null;
    end_date: string | null;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    /**
     * When it was launched
     */
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Null until the run has succeeded and its rows were loaded
     */
    load: ShopifySyncLoadRead | null;
};

/**
 * A Shopify store that can be synced.
 */
export type ShopifySyncStoreRead = {
    /**
     * com_store.pk_store_id
     */
    store_id: number;
    name: string;
    /**
     * Its marketplace, e.g. SPUS
     */
    short_code: string;
    /**
     * The marketplace's country
     */
    country_code: string;
};

/**
 * The *other* stores in this tenant using the same application for this API.
 *
 * The store asked about is not in ``store_ids``; ``count`` is its length.
 */
export type Siblings = {
    store_ids: Array<number>;
    count: number;
};

/**
 * Body of ``POST /runs/{id}/signal``.
 */
export type SignalWrite = {
    signal: BrowserSignal;
};

/**
 * One SKU's stock in the warehouse, split by age.
 *
 * The eight buckets add up to exactly ``total_units``, and none is ever
 * missing: a SKU with nothing in a band has a measured 0 there.
 *
 * Attributes:
 * age_pre_migration: Units whose only receipt is the cut-over backfill:
 * old, but how old is not known. Not 0-30 days.
 * age_unknown: Units with no receipt at all. Entering the missing vendor
 * invoice moves them into a real bucket.
 */
export type SkuAgeingRead = {
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    total_units: number;
    age_0_30: number;
    age_31_60: number;
    age_61_90: number;
    age_91_180: number;
    age_181_270: number;
    age_270_plus: number;
    age_pre_migration: number;
    age_unknown: number;
};

/**
 * Why searched codes are not on the grid.
 *
 * Attributes:
 * missing_cost: Our SKUs with no valid vendor cost at the warehouse, so no
 * row to hold a class: upload a vendor cost first.
 * unknown: Codes that are not our SKUs at all.
 */
export type SkuCheckRead = {
    missing_cost: Array<NamedRead>;
    unknown: Array<string>;
};

/**
 * SKU codes searched for that the grid did not show.
 *
 * Attributes:
 * warehouse_id: The grid's warehouse.
 * sku_codes: The codes.
 */
export type SkuCheckWrite = {
    warehouse_id: number;
    sku_codes: Array<string>;
};

/**
 * One current cost. Amounts are strings: a JSON number is a float.
 */
export type SkuCostRow = {
    id: number;
    sku_code: string;
    sku_name: string | null;
    brand_name: string | null;
    currency_code: string;
    cost: string | null;
    vat_rate: string | null;
    rrp: string | null;
    /**
     * The vendor or the retailer
     */
    party_name: string | null;
    warehouse_name: string | null;
    /**
     * The retailer's own SKU
     */
    retailer_product_code: string | null;
    /**
     * The vendor the retailer's cost is sourced from
     */
    cost_reference_vendor_name: string | null;
    /**
     * The warehouse the retailer's cost is sourced from
     */
    cost_reference_warehouse_name: string | null;
    /**
     * The currency of the cost the NLC is sourced from
     */
    cost_reference_currency_code: string | null;
};

/**
 * The SKU row a list belongs to.
 *
 * Attributes:
 * store_id: The store.
 * sku_id: The SKU.
 * currency_code: The currency.
 * date_from: The range's first day.
 * date_to: Its last day.
 * sku_code: The SKU's code.
 * sku_name: Its name.
 * store_name: The store's name.
 * marketplace_name: The store's marketplace.
 */
export type SkuDocumentSubject = {
    store_id: number;
    sku_id: number;
    currency_code: string;
    date_from: string;
    date_to: string;
    sku_code: string | null;
    sku_name: string | null;
    store_name: string | null;
    marketplace_name: string | null;
};

/**
 * One DF/OF/VO sales-invoice line of the SKU counted at the store.
 *
 * Attributes:
 * invoice_line_id: The invoice line; unique per row.
 * invoice_id: Its invoice.
 * invoice_number: The invoice's number.
 * invoice_date: The invoice's day.
 * channel: ``DF``, ``OF`` or ``VO``.
 * retailer_name: Who it was billed to.
 * units: Units on the line.
 * sales_amount: The line, before VAT.
 * cogs_amount: Units times landed cost, where the cost is known.
 * invoice_billed: The whole invoice billed, other SKUs' lines included.
 * invoice_received: What has been received against the whole invoice.
 * invoice_outstanding: What the whole invoice still owes.
 */
export type SkuInvoiceLine = {
    invoice_line_id: number;
    invoice_id: number | null;
    invoice_number: string | null;
    invoice_date: string;
    channel: string;
    retailer_name: string | null;
    units: number;
    sales_amount: MoneyRead;
    cogs_amount: MoneyRead;
    invoice_billed: MoneyRead | null;
    invoice_received: MoneyRead | null;
    invoice_outstanding: MoneyRead | null;
};

/**
 * A page of a SKU row's invoice lines.
 *
 * Attributes:
 * subject: The SKU row.
 * totals: Over every line, not the page.
 * meta: How fresh the figures are.
 */
export type SkuInvoiceLinesReport = {
    items: Array<SkuInvoiceLine>;
    pagination: Pagination;
    subject: SkuDocumentSubject;
    totals: SkuInvoiceTotals;
    meta: ReportMeta;
};

/**
 * The sums over every invoice line of the row. Invoice balances are not summed.
 *
 * Attributes:
 * units: Units.
 * sales_amount: Invoiced, before VAT.
 * cogs_amount: Cost of goods.
 */
export type SkuInvoiceTotals = {
    units: number;
    sales_amount: MoneyRead;
    cogs_amount: MoneyRead;
};

/**
 * One marketplace order line of the SKU at the store, and what has been paid on it.
 *
 * Attributes:
 * order_item_id: The order line; unique per row.
 * order_id: The marketplace's order id.
 * product_ref: What the marketplace calls the product (ASIN, EAN, FSN …).
 * ordered_at: When it was bought, if the marketplace says.
 * order_date: The marketplace day it counts on.
 * status: ``shipped``, ``pending``, ``returned``, ``cancelled`` …
 * units: Units sold.
 * returned_units: Units of the line that came back.
 * sales_amount: What it sold for.
 * cogs_amount: Units times landed cost, where the cost is known.
 * commission_amount: What the marketplace kept.
 * settled_amount: What the marketplace has paid on it.
 * expected_amount: What it is still expected to pay.
 * settlement_refs: The payouts that settled it; empty while awaiting payout.
 * settled_on: The last day one of them was paid.
 */
export type SkuOrderLine = {
    order_item_id: number;
    order_id: string | null;
    product_ref: string | null;
    ordered_at: string | null;
    order_date: string;
    status: string | null;
    units: number;
    returned_units: number;
    sales_amount: MoneyRead;
    cogs_amount: MoneyRead;
    commission_amount: MoneyRead;
    settled_amount: MoneyRead;
    expected_amount: MoneyRead;
    settlement_refs: Array<string>;
    settled_on: string | null;
};

/**
 * The sums over every order line of the row — the row's own figures.
 *
 * Attributes:
 * units: Units sold.
 * returned_units: Units returned.
 * sales_amount: Sales.
 * cogs_amount: Cost of goods.
 * commission_amount: Commission.
 * settled_amount: Paid.
 * expected_amount: Still to be paid.
 */
export type SkuOrderTotals = {
    units: number;
    returned_units: number;
    sales_amount: MoneyRead;
    cogs_amount: MoneyRead;
    commission_amount: MoneyRead;
    settled_amount: MoneyRead;
    expected_amount: MoneyRead;
};

/**
 * A page of a SKU row's order lines.
 *
 * Attributes:
 * subject: The SKU row.
 * totals: Over every line, not the page.
 * ad_spend: The row's advertising, which belongs to no order line.
 * meta: How fresh the figures are.
 */
export type SkuOrdersReport = {
    items: Array<SkuOrderLine>;
    pagination: Pagination;
    subject: SkuDocumentSubject;
    totals: SkuOrderTotals;
    ad_spend: MoneyRead;
    meta: ReportMeta;
};

/**
 * A price typed for one SKU, instead of the retailer's.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * price: Per unit. Negative only on the DISCOUNT line.
 * vat_rate: A percentage (``18`` for 18%); the retailer's, else zero, when left out.
 */
export type SkuPrice = {
    sku_code: string;
    price: number | string;
    vat_rate?: number | string | null;
};

/**
 * A page of rows, the totals per currency and the counts over every row.
 *
 * Attributes:
 * counts: The rows, SKUs and stores, and the loss-makers among them.
 */
export type SkuProfitabilityReport = {
    items: Array<ProfitabilityRow>;
    pagination: Pagination;
    totals: Array<ProfitabilityTotals>;
    meta: ReportMeta;
    counts: ProfitabilityCounts;
};

/**
 * One SKU, every field the detail panel shows.
 */
export type SkuRow = {
    id: number;
    sku_code: string;
    sku_name: string | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    sku_model: string | null;
    ean: string | null;
    upc_code: string | null;
    isbn: string | null;
    hsn_code: string | null;
    manufacturer: string | null;
    product_description: string | null;
    bullet_points: string | null;
    keywords: string | null;
    color: string | null;
    size: string | null;
    material: string | null;
    country_of_origin: string | null;
    length_cm: string | null;
    width_cm: string | null;
    height_cm: string | null;
    weight_g: string | null;
    is_exclusive: boolean | null;
    is_eol: boolean | null;
    is_fragile: boolean | null;
    is_hazardous: boolean | null;
    stores_count: number;
    image_url?: string | null;
};

/**
 * A business snapshot as it was streamed (``data-snapshot``) and saved.
 *
 * Attributes:
 * result_id: The stored insights table, for export.
 * summary: The markdown summary.
 * period: How the snapshot named its period.
 */
export type SnapshotData = {
    result_id?: string | null;
    summary?: string;
    insights?: Array<SnapshotInsight>;
    period?: string | null;
};

/**
 * One prioritised insight of a business snapshot (``present_snapshot``).
 *
 * Stored as the model delivered it, so text fields a model left out read as
 * empty rather than failing the read.
 *
 * Attributes:
 * area: The part of the business, e.g. "Advertising"; compare matches on it.
 * confidence: 1-5.
 * priority: 1-10; the snapshot lists the highest first.
 * evidence_result_id: The stored result behind it.
 */
export type SnapshotInsight = {
    area?: string;
    finding?: string;
    metric?: string | null;
    impact?: string;
    recommended_action?: string;
    confidence?: number;
    priority?: number;
    evidence_result_id?: string | null;
};

/**
 * What the filters on this page can be chosen from.
 */
export type SpInvoiceLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
    types: Array<'Transportation' | 'Shipping' | 'Duty' | 'Vat' | 'Bank_Charges' | 'Salaries' | 'Fees' | 'Packaging' | 'Labour' | 'Warehousing' | 'Rent' | 'Travel' | 'Others'>;
};

/**
 * One row of the sp invoices list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type SpInvoiceRead = {
    id: number;
    date: string | null;
    number: string | null;
    warehouse_id: number | null;
    warehouse: string | null;
    party_id: number | null;
    party: string | null;
    currency: string | null;
    type: string | null;
    amount_wo_tax: string | null;
    amount: string | null;
    paid: string | null;
    cndn_value: string | null;
    credit_period: number | null;
    order_detail: string | null;
    vi_id: number | null;
    invoice_id: number | null;
    comment: string | null;
    valid: boolean;
    vendor_invoice_number?: string | null;
    invoice_number?: string | null;
};

/**
 * A service provider's bill, as typed in: new, or the whole of an edit.
 *
 * Attributes:
 * number: The provider's invoice number; unique among the warehouse's
 * live SP invoices.
 * currency: ISO 4217, e.g. ``INR``.
 * vendor_invoice_number: A vendor invoice in the same warehouse this
 * cost belongs to, e.g. the freight on a shipment.
 * invoice_number: A sales invoice in the same warehouse this cost
 * belongs to, e.g. the delivery charge on it.
 */
export type SpInvoiceWrite = {
    warehouse_id: number;
    sp_id: number;
    number: string;
    date?: string | null;
    currency: string;
    type: 'Transportation' | 'Shipping' | 'Duty' | 'Vat' | 'Bank_Charges' | 'Salaries' | 'Fees' | 'Packaging' | 'Labour' | 'Warehousing' | 'Rent' | 'Travel' | 'Others';
    amount_wo_tax: number | string;
    amount: number | string;
    credit_period?: number | null;
    order_detail?: string | null;
    comment?: string | null;
    vendor_invoice_number?: string | null;
    invoice_number?: string | null;
};

/**
 * What the filters on this page can be chosen from.
 */
export type SpPaymentLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * One row of the sp payments list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type SpPaymentRead = {
    id: number;
    date: string | null;
    invoice_id: number | null;
    invoice: string | null;
    currency: string | null;
    amount: string | null;
    comment: string | null;
    valid: boolean;
};

/**
 * Open ``sql_fallback`` flags: measures the query methods lack, by dataset.
 */
export type SqlFallbackBacklog = {
    open: number;
    tables: Array<FallbackTable>;
};

/**
 * A dataset free SQL read, and whether a query method already covers it.
 */
export type SqlTopic = {
    table: string;
    queries: number;
    covered_by_methods: boolean;
    example_purpose: string;
};

/**
 * Datasets free SQL read, most used first.
 */
export type SqlTopics = {
    items: Array<SqlTopic>;
};

/**
 * The one action a PO offers next. Derived, never stored.
 */
export type Stage = 'preview' | 'ready-to-acknowledge' | 'ready-to-send' | 'done' | 'blocked';

/**
 * The one action a PO offers next. Derived, never stored.
 */
export const Stage = {
    PREVIEW: 'preview',
    READY_TO_ACKNOWLEDGE: 'ready-to-acknowledge',
    READY_TO_SEND: 'ready-to-send',
    DONE: 'done',
    BLOCKED: 'blocked'
} as const;

/**
 * How long orders took to reach a status: purchase to the marketplace's last update.
 */
export type StatusTiming = {
    status: 'pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other';
    orders: number;
    average_hours: string | null;
    median_hours: string | null;
};

/**
 * Where one step of a deletion is.
 */
export type StepStatus = 'pending' | 'running' | 'done' | 'failed' | 'needs_manual_cleanup' | 'skipped';

/**
 * Where one step of a deletion is.
 */
export const StepStatus = {
    PENDING: 'pending',
    RUNNING: 'running',
    DONE: 'done',
    FAILED: 'failed',
    NEEDS_MANUAL_CLEANUP: 'needs_manual_cleanup',
    SKIPPED: 'skipped'
} as const;

/**
 * What the filters on this page can be chosen from.
 */
export type StockAgeingLookupsRead = {
    warehouses: Array<NamedRead>;
};

/**
 * A SKU's landed cost at the warehouse, in one currency.
 *
 * The three parts are returned separately so a reader can see *why* a cost
 * moved. Their sum is the landed ("buying") cost.
 *
 * Attributes:
 * currency: ISO 4217 code the three parts are in.
 * purchase: What the vendor charged, per unit.
 * shipping: Freight in, per unit.
 * additional: Duty, handling and anything else capitalised, per unit.
 */
export type StockCostRead = {
    currency: string;
    purchase: string;
    shipping: string;
    additional: string;
};

/**
 * Focused SKUs by days of cover, the most urgent first.
 */
export type StockCover = {
    bands: CoverBands;
    rows: Array<CoverSku>;
};

/**
 * What a warehouse's position can be narrowed by.
 *
 * Only parties that price a SKU at the warehouse are offered, so no choice can
 * only ever return nothing.
 *
 * Attributes:
 * vendors: Vendors with a valid cost for a SKU here.
 * retailers: Retailers with a valid price for a SKU here.
 * brands: Brands with stock here.
 */
export type StockFiltersRead = {
    vendors: Array<NamedRead>;
    retailers: Array<NamedRead>;
    brands: Array<string>;
};

/**
 * Where stock sits: the marketplace's (FBA, FBN) or our own warehouse's.
 */
export type StockPool = 'marketplace' | 'warehouse';

/**
 * Where stock sits: the marketplace's (FBA, FBN) or our own warehouse's.
 */
export const StockPool = {
    MARKETPLACE: 'marketplace',
    WAREHOUSE: 'warehouse'
} as const;

/**
 * One run, with what became of this store's listings.
 */
export type StockPushRunDetail = {
    run_id: string;
    /**
     * stock_push_store for a requested push; else the schedule's
     */
    job_name: string;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Whether someone asked for it, rather than the schedule
     */
    requested: boolean;
    /**
     * For a requested push, whether it sent anything
     */
    dry_run: boolean | null;
    request_id: string | null;
    /**
     * This store's counts; null for a scheduled run, whose totals span stores
     */
    counts: {
        [key: string]: number;
    } | null;
    /**
     * Listings worth showing: failed, not attempted, unmatched, ambiguous or skipped — and every planned change on a dry run
     */
    rows: Array<{
        [key: string]: unknown;
    }>;
    rows_truncated: boolean;
    /**
     * Why the run failed, when it did
     */
    error: string | null;
};

/**
 * One run, as listed.
 */
export type StockPushRunRead = {
    run_id: string;
    /**
     * stock_push_store for a requested push; else the schedule's
     */
    job_name: string;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Whether someone asked for it, rather than the schedule
     */
    requested: boolean;
    /**
     * For a requested push, whether it sent anything
     */
    dry_run: boolean | null;
    request_id: string | null;
};

/**
 * A store whose stock can be pushed.
 */
export type StockPushStoreRead = {
    /**
     * com_store.pk_store_id
     */
    store_id: number;
    name: string;
    /**
     * Its marketplace, e.g. OTDE
     */
    short_code: string;
    /**
     * The push it gets, e.g. otto_seller
     */
    connector: string;
};

/**
 * One SKU's position at a warehouse.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * retailer_skus: The chosen retailer's product code (ASIN) for it, or every
 * retailer's when none is chosen. Empty when it has none here.
 * sku_name: Its name.
 * brand_name: Its brand.
 * category: Its category.
 * subcategory: Its subcategory.
 * box_qty: Units in inward boxes, plus allotted boxes when asked.
 * blocked_qty: Units valid blocks hold on inward boxes, always.
 * available_qty: ``box_qty``, or with blocked taken out, each box less its
 * own blocks and floored at zero; allotted units, when counted, are
 * not reduced by their order's own block.
 * cost: The cost in force now, or ``None`` when the SKU was never costed here.
 */
export type StockRead = {
    sku_code: string;
    retailer_skus: Array<string>;
    sku_name: string | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    box_qty: number;
    blocked_qty: number;
    available_qty: number;
    cost: StockCostRead | null;
};

/**
 * Response of ``GET /v1/stores/{id}/checklist``: only the items that apply.
 */
export type StoreChecklist = {
    done: number;
    total: number;
    items: Array<ChecklistItem>;
};

/**
 * Response of ``GET /v1/stores/{id}/health/circuits``.
 */
export type StoreCircuits = {
    items: Array<CircuitRead>;
};

/**
 * Who a contact is to the store.
 */
export type StoreContactKind = 'owner' | 'account_manager' | 'external';

/**
 * Who a contact is to the store.
 */
export const StoreContactKind = {
    OWNER: 'owner',
    ACCOUNT_MANAGER: 'account_manager',
    EXTERNAL: 'external'
} as const;

/**
 * Body of ``POST /v1/stores``.
 *
 * Attributes:
 * marketplace_id: Required. A store with no marketplace cannot receive an
 * order, and this is the edge the scope graph publishes.
 * brand_ids: Optional, any number: a store with a mixed catalogue sells
 * several, and one with none is not made to name a "various" row
 * that then appears in every report. Each must be one of this
 * tenant's brands, and active.
 * external_seller_id: The marketplace's own identifier for the account.
 * Unique per marketplace among live stores.
 * store_type: e.g. ``seller`` or ``vendor``; decides which API grants
 * the account resolves to.
 * is_retail: Whether it sells to consumers.
 */
export type StoreCreate = {
    code: string;
    name: string;
    /**
     * Deprecated, for one release: use brand_ids. A brand id is read as brand_ids [id], null as [].
     * @deprecated
     */
    brand_id?: number | null;
    marketplace_id: number;
    /**
     * The brands it sells, a set: replaces the stored one. [] for none.
     */
    brand_ids?: Array<number>;
    external_seller_id: string;
    store_type: string;
    is_retail: boolean;
};

/**
 * Response of ``GET /v1/stores/deletions/{id}``. Readable after the store is gone.
 */
export type StoreDeletionRead = {
    status: DeletionStatus;
    steps: Array<DeletionStep>;
    store_snapshot: StoreSnapshot | null;
};

/**
 * Response of ``GET /v1/stores/{id}/exports``, newest first.
 */
export type StoreExportList = {
    items: Array<StoreExportRead>;
};

/**
 * One export, as the Data & Compliance tab shows it.
 *
 * Attributes:
 * export_id: Its id.
 * status: ``queued``, ``running``, ``ready``, ``failed`` or ``expired``.
 * domains: The domains asked for, with the rows written per table.
 * size_bytes: The zip's size, once ready.
 * download_url: A link that works for ten minutes, while ready; fetch
 * the export again just before downloading.
 * expires_at: When the file is removed.
 * error: Why it failed.
 * requested_by: Who asked for it, as a name where one is known.
 * created_at: When it was asked for.
 */
export type StoreExportRead = {
    export_id: number;
    status: ExportStatus;
    domains: Array<ExportDomain>;
    size_bytes: number | null;
    download_url: string | null;
    expires_at: string | null;
    error: string | null;
    requested_by: string | null;
    created_at: string;
};

/**
 * What can be switched off per store. Stored as text; the CHECK lists these.
 */
export type StoreFeatureName = 'ads_automation' | 'rules_engine' | 'dayparting' | 'repricing' | 'stock_push' | 'ai_listing' | 'real_time_streams';

/**
 * What can be switched off per store. Stored as text; the CHECK lists these.
 */
export const StoreFeatureName = {
    ADS_AUTOMATION: 'ads_automation',
    RULES_ENGINE: 'rules_engine',
    DAYPARTING: 'dayparting',
    REPRICING: 'repricing',
    STOCK_PUSH: 'stock_push',
    AI_LISTING: 'ai_listing',
    REAL_TIME_STREAMS: 'real_time_streams'
} as const;

/**
 * Body of ``POST /v1/store-groups``.
 */
export type StoreGroupCreate = {
    kind: StoreGroupKind;
    name: string;
};

/**
 * The kinds of group. A store is in at most one of each.
 */
export type StoreGroupKind = 'region' | 'business_unit';

/**
 * The kinds of group. A store is in at most one of each.
 */
export const StoreGroupKind = {
    REGION: 'region',
    BUSINESS_UNIT: 'business_unit'
} as const;

/**
 * One of the tenant's groups, with how many stores it has.
 */
export type StoreGroupRead = {
    id: number;
    kind: StoreGroupKind;
    name: string;
    store_count: number;
};

/**
 * Response of ``GET /v1/stores/{id}/health``.
 *
 * Attributes:
 * score: 0-100, the parts' scores weighted by their weights.
 * parts: Every part, in a fixed order, counted or not.
 */
export type StoreHealth = {
    score: number;
    parts: Array<HealthPart>;
};

/**
 * Whether a store is connected to its platform's API.
 */
export type StoreKind = 'online' | 'offline';

/**
 * Whether a store is connected to its platform's API.
 */
export const StoreKind = {
    ONLINE: 'online',
    OFFLINE: 'offline'
} as const;

/**
 * A store after deactivating or reactivating it.
 *
 * Attributes:
 * streams: What ingest did to the store's real-time streams - removed
 * them on deactivation, restored them on reactivation - or why it
 * could not be asked. The store's state is saved either way.
 */
export type StoreLifecycleResult = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    deactivated_at?: string | null;
    deactivated_reason?: string | null;
    external_seller_id: string;
    store_type: string;
    is_retail: boolean;
    marketplace: MarketplaceSummary;
    brands: Array<VentawizCredentialsSchemasBrandSummary>;
    kind: StoreKind;
    ads_profile_id: number | null;
    surfaces: Array<SurfaceStatus>;
    next_expiry_at: string | null;
    warehouses: Array<WarehouseSummary>;
    created_at: string;
    updated_at: string | null;
    carriers?: Array<SurfaceStatus>;
    streams?: ReconcileResult | ReconcileFailure | null;
};

/**
 * One store to link.
 *
 * Attributes:
 * store_id: A store of this organization.
 * position: The warehouse's 1-based place in that store's list.
 * Omitted: its current place, or last for a new link.
 */
export type StoreLinkWrite = {
    store_id: number;
    position?: number | null;
};

/**
 * A store, with the state of every API it could connect. No secret, ever.
 *
 * Attributes:
 * deactivated_at: When the store was deactivated, from the latest
 * ``store.deactivated`` (or ``store.retired``) event. ``None`` while
 * it is active, and for a store deactivated outside this service
 * (v1 set the flag with no event).
 * deactivated_reason: The reason given then. ``None`` likewise, and for
 * a store retired through the generic registry route, which takes
 * no reason.
 * carriers: Every shipping carrier's API (DHL), connected or not -
 * apart from ``surfaces``, which are the marketplace's own.
 * brands: Every brand it sells, by name; ``[]`` for none. There is no
 * primary brand. (``brand``, one or none, until stores could sell
 * several.)
 */
export type StoreOverview = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    deactivated_at?: string | null;
    deactivated_reason?: string | null;
    external_seller_id: string;
    store_type: string;
    is_retail: boolean;
    marketplace: MarketplaceSummary;
    brands: Array<VentawizCredentialsSchemasBrandSummary>;
    kind: StoreKind;
    ads_profile_id: number | null;
    surfaces: Array<SurfaceStatus>;
    next_expiry_at: string | null;
    warehouses: Array<WarehouseSummary>;
    created_at: string;
    updated_at: string | null;
    carriers?: Array<SurfaceStatus>;
};

/**
 * The tabs of the store page, where a checklist item is fixed.
 */
export type StorePageTab = 'overview' | 'settings' | 'connections' | 'health' | 'activity' | 'data' | 'danger';

/**
 * The tabs of the store page, where a checklist item is fixed.
 */
export const StorePageTab = {
    OVERVIEW: 'overview',
    SETTINGS: 'settings',
    CONNECTIONS: 'connections',
    HEALTH: 'health',
    ACTIVITY: 'activity',
    DATA: 'data',
    DANGER: 'danger'
} as const;

/**
 * Response of ``GET /v1/stores/{id}/health/runs``, newest first.
 */
export type StoreRuns = {
    items: Array<IngestRunRead>;
};

/**
 * Everything on a store's Settings tab. No secret, ever.
 */
export type StoreSettingsRead = {
    details: SettingsDetails;
    ads: SettingsAds;
    warehouses: Array<SettingsWarehouse>;
    users: Array<SettingsUser>;
    targets: SettingsTargets;
    fulfilment: SettingsFulfilment;
    tags: Array<string>;
    groups: Array<SettingsGroup>;
    contacts: Array<SettingsContact>;
    features: Array<SettingsFeature>;
};

/**
 * Body of ``PATCH /v1/stores/{id}/settings``. Every section optional.
 *
 * A section sent is applied; one left out is untouched. Within a section,
 * ``details`` and ``targets`` apply only the fields sent; the list sections
 * (``warehouses``, ``users``, ``tags``, ``groups``, ``contacts``) replace
 * the store's list with the one sent; ``features`` switches only the
 * features sent.
 */
export type StoreSettingsUpdate = {
    details?: DetailsUpdate | null;
    ads?: AdsUpdate | null;
    warehouses?: Array<WarehouseLinkWrite> | null;
    users?: Array<UserWrite> | null;
    targets?: TargetsUpdate | null;
    fulfilment?: FulfilmentUpdate | null;
    tags?: Array<string> | null;
    groups?: Array<GroupWrite> | null;
    contacts?: Array<ContactWrite> | null;
    features?: Array<FeatureWrite> | null;
};

/**
 * The settings after a change.
 *
 * Attributes:
 * streams: What ingest did to the store's real-time streams when
 * ``real_time_streams`` was switched, or why it could not be asked;
 * ``null`` when it was not switched or the store has no streams.
 */
export type StoreSettingsUpdated = {
    details: SettingsDetails;
    ads: SettingsAds;
    warehouses: Array<SettingsWarehouse>;
    users: Array<SettingsUser>;
    targets: SettingsTargets;
    fulfilment: SettingsFulfilment;
    tags: Array<string>;
    groups: Array<SettingsGroup>;
    contacts: Array<SettingsContact>;
    features: Array<SettingsFeature>;
    streams?: ReconcileResult | ReconcileFailure | null;
};

/**
 * What a deleted store was.
 */
export type StoreSnapshot = {
    id: number;
    code: string;
    name: string;
};

/**
 * One stream on one store: what was asked for, and what the platform has.
 *
 * Attributes:
 * enabled: Switched on for this store.
 * desired: Switched on *and* the store is active - what ingest acts on.
 * status: What ingest last found on the platform; ``not_subscribed``
 * when it has recorded nothing.
 * owner: Who made the subscription; ``external`` for one found on the
 * platform pointing elsewhere (v1 VentaHub). ``None`` with no
 * subscription.
 * events_24h: Events received in the last day.
 * dropped_24h: Events dropped in the last day because the store is
 * deactivated.
 */
export type StoreStreamRead = {
    key: string;
    label: string;
    description: string;
    surface: AuthSurface;
    enabled: boolean;
    desired: boolean;
    status: StreamStatus | 'not_subscribed';
    owner: StreamOwner | null;
    last_event_at: string | null;
    events_24h: number;
    dropped_24h: number;
    last_error: string | null;
    last_reconciled_at: string | null;
    provider_subscription_id: string | null;
};

/**
 * Every stream the store's platform offers, in catalogue order.
 */
export type StoreStreams = {
    items: Array<StoreStreamRead>;
};

/**
 * The store's streams after a change, and what ingest did about it.
 */
export type StoreStreamsUpdated = {
    items: Array<StoreStreamRead>;
    reconcile: ReconcileResult | ReconcileFailure;
};

/**
 * Per-store totals for one marketplace and window.
 */
export type StoreSummaryRead = {
    start_date: string;
    end_date: string;
    rows: Array<StoreSummaryRowRead>;
};

/**
 * One store's POs over the window.
 */
export type StoreSummaryRowRead = {
    store_id: number;
    store_name: string;
    store_key: string;
    po_count: number;
    line_count: number;
    /**
     * Distinct ASINs.
     */
    sku_count: number;
    /**
     * What Amazon first ordered, cancellations included.
     */
    units_ordered: number;
    units_confirmed: number;
    /**
     * Units Amazon has receipted.
     */
    units_delivered: number;
    amount_ordered: MoneyRead | null;
    amount_confirmed: MoneyRead | null;
    amount_delivered: MoneyRead | null;
    /**
     * Confirmed over ordered; null when none ordered.
     */
    confirm_rate: number | null;
    /**
     * Delivered over ordered; null when none ordered.
     */
    delivery_rate: number | null;
    first_po_at: string | null;
    last_po_at: string | null;
};

/**
 * Body of ``PATCH /v1/stores/{id}``.
 *
 * ``marketplace_id`` is absent: moving a store between channels is not a
 * rename, it changes what the historical orders mean and rewrites a
 * scope-graph edge that grants already depend on.
 *
 * Attributes:
 * brand_ids: Replace the brands it sells; ``[]`` for none. A brand
 * already linked may stay after it is retired; a new one must be
 * active.
 * external_seller_id: Correct the account identifier.
 * store_type: Correct the kind of account.
 * is_retail: Correct whether it sells to consumers.
 */
export type StoreUpdate = {
    name?: string | null;
    /**
     * Deprecated, for one release: use brand_ids. A brand id is read as brand_ids [id], null as [].
     * @deprecated
     */
    brand_id?: number | null;
    brand_ids?: Array<number> | null;
    external_seller_id?: string | null;
    store_type?: string | null;
    is_retail?: boolean | null;
};

/**
 * A store the caller may see, with the zones its data is kept in.
 *
 * Attributes:
 * store_id: The store.
 * name: Its name.
 * timezone: The store's own zone — its settings override, else its
 * marketplace's. What a page shows a store's instants in when
 * nobody has said otherwise.
 * ads_timezone: The ads profile's zone, which the ads reports' hours
 * are bucketed in; null when the store has no profile.
 */
export type StoreZoneRead = {
    store_id: number;
    name: string;
    timezone: string;
    ads_timezone: string | null;
};

/**
 * One real-time stream switched on or off.
 *
 * Attributes:
 * key: The catalogue's key.
 * enabled: Receive it or not.
 * params: What subscribing needs beyond the grant. Omitted, the stored
 * ones are kept, and a parameter the store already holds (the Ads
 * profile) is taken from the store.
 */
export type StreamChoice = {
    key: string;
    enabled: boolean;
    params?: {
        [key: string]: number | string;
    } | null;
};

/**
 * Body of ``POST /v1/stores/onboarding/streams/discover``. Never stored, never logged.
 *
 * Attributes:
 * marketplace_id: Which marketplace; its ids and country go to ingest.
 * surface: Which API's subscriptions to look for.
 * store_type: ``seller``, ``vendor``, ...
 * account_ref: The platform's account id, where it is needed.
 * credentials: ``{field key: value}`` per the surface's spec.
 * params: E.g. the Ads ``profile_id`` to look under.
 */
export type StreamDiscoverRequest = {
    marketplace_id: number;
    surface: AuthSurface;
    store_type?: string;
    account_ref?: string | null;
    credentials: {
        [key: string]: string;
    };
    params?: {
        [key: string]: number | string;
    } | null;
};

/**
 * What the platform already has, and which streams can be subscribed.
 *
 * Attributes:
 * error: Why the platform could not be asked, when it could not; the
 * lists are then empty.
 */
export type StreamDiscovery = {
    surface: string;
    subscriptions?: Array<DiscoveredSubscription>;
    available?: Array<string>;
    error?: ProbeError | null;
};

/**
 * Who made a subscription: VentaWiz, or something else (v1 VentaHub) found on the platform.
 */
export type StreamOwner = 'ventawiz' | 'external';

/**
 * Who made a subscription: VentaWiz, or something else (v1 VentaHub) found on the platform.
 */
export const StreamOwner = {
    VENTAWIZ: 'ventawiz',
    EXTERNAL: 'external'
} as const;

/**
 * One value subscribing to a stream needs beyond the store's grant.
 */
export type StreamParamRead = {
    key: string;
    label: string;
    type: ParamType;
    required: boolean;
    help: string;
    store_field: string | null;
};

/**
 * The streams a marketplace's platform offers; empty for one with none.
 */
export type StreamSpecList = {
    family: string;
    streams: Array<StreamSpecRead>;
};

/**
 * One stream a store on this platform can receive.
 */
export type StreamSpecRead = {
    key: string;
    label: string;
    description: string;
    surface: AuthSurface;
    default: boolean;
    params: Array<StreamParamRead>;
    delivery: Delivery;
    notes: string;
};

/**
 * What the platform actually has for one (store, stream), as ingest last found it.
 */
export type StreamStatus = 'active' | 'pending_confirmation' | 'failed' | 'removed' | 'external' | 'unsupported';

/**
 * What the platform actually has for one (store, stream), as ingest last found it.
 */
export const StreamStatus = {
    ACTIVE: 'active',
    PENDING_CONFIRMATION: 'pending_confirmation',
    FAILED: 'failed',
    REMOVED: 'removed',
    EXTERNAL: 'external',
    UNSUPPORTED: 'unsupported'
} as const;

/**
 * Body of ``PUT /v1/stores/{id}/streams``: switch some streams; the rest stay as they are.
 */
export type StreamsUpdate = {
    streams: Array<StreamChoice>;
};

/**
 * A sub-category, within its category.
 *
 * Attributes:
 * id: What ``subcategory_ids`` takes.
 * category_key: Its category.
 * name: Its name.
 */
export type SubcategoryOption = {
    id: string;
    category_key: string;
    name: string;
};

/**
 * A drafted fix for a run, to review before applying anything.
 *
 * Attributes:
 * suggestion: Markdown: the diagnosis, the change, a regression question.
 * cost_usd: What drafting it cost.
 */
export type SuggestedFix = {
    suggestion: string;
    cost_usd: number | null;
};

/**
 * The Data Vault landing numbers under the caller's filters.
 */
export type SummaryRead = {
    listings: number;
    skus: number;
    orders_window_days: number;
    orders: Array<OrdersSummary>;
    returns: number;
    settlements: number;
};

/**
 * Stock in our warehouses that no marketplace lists.
 *
 * Attributes:
 * count: How many SKUs.
 * units: Their units together.
 * rows: The SKUs, most units first.
 */
export type SuppressedInventory = {
    count: number;
    units: number;
    rows: Array<SuppressedSku>;
};

/**
 * A SKU with stock and no live listing anywhere.
 */
export type SuppressedSku = {
    sku_id: number;
    sku_code: string | null;
    sku_name: string | null;
    brand_name: string | null;
    units: number;
    warehouses: Array<WarehouseUnits>;
};

/**
 * Where a surface sits in the onboarding wizard.
 *
 * ``primary`` is the store's own grant, asked for first. ``linked_ads`` is
 * the "is there an advertising account too?" step after it.
 */
export type SurfaceRole = 'primary' | 'linked_ads' | 'carrier';

/**
 * Where a surface sits in the onboarding wizard.
 *
 * ``primary`` is the store's own grant, asked for first. ``linked_ads`` is
 * the "is there an advertising account too?" step after it.
 */
export const SurfaceRole = {
    PRIMARY: 'primary',
    LINKED_ADS: 'linked_ads',
    CARRIER: 'carrier'
} as const;

/**
 * One auth surface: what connecting it takes.
 */
export type SurfaceSpecRead = {
    surface: AuthSurface;
    label: string;
    role: SurfaceRole;
    store_types: Array<string>;
    fields: Array<FieldSpecRead>;
    expiry_rule: ExpiryRuleRead | null;
    supports_oauth: boolean;
};

/**
 * One API on one store: switched on, connected, healthy, expiring, locked.
 *
 * Every surface the marketplace's family supports is listed, connected or
 * not, so the table has a stable set of columns.
 */
export type SurfaceStatus = {
    surface: AuthSurface;
    label: string;
    enabled: boolean;
    has_credentials: boolean;
    health: Health;
    last_verified_at: string | null;
    last_error: string | null;
    expires_at: string | null;
    days_left: number | null;
    expiry_source: ExpirySource;
    issued_at: string | null;
    source: string | null;
    client_id_hint: string | null;
    write_locked: boolean;
    write_lock_reason: string | null;
    circuit_open: boolean;
};

/**
 * Body of ``POST /v1/stores/{id}/health/sync``: every entity, or the one named.
 */
export type SyncRequest = {
    entity?: string | null;
};

/**
 * One tag and how many of this organization's entities of a kind carry it.
 */
export type TagCount = {
    tag: string;
    count: number;
};

/**
 * Every tag this organization uses on one kind of entity, most used first.
 */
export type TagCountList = {
    items: Array<TagCount>;
};

/**
 * An entity's tags, alphabetically, ignoring case.
 */
export type TagsRead = {
    tags: Array<string>;
};

/**
 * Body of ``PUT /v1/<kind>/{id}/tags``: the whole set.
 *
 * Trimmed, inner whitespace collapsed and de-duplicated ignoring case on
 * the way in; the first spelling of a repeated tag is kept.
 */
export type TagsWrite = {
    tags: Array<string>;
};

/**
 * One proposed target, checked.
 *
 * Attributes:
 * target: As submitted, trimmed.
 * kind: ``product`` for something shaped like an ASIN, else ``keyword``.
 * duplicate_of: The existing target in the same campaign it matches.
 */
export type TargetCheckItem = {
    target: string;
    kind: string;
    duplicate_of: string | null;
};

/**
 * Body of ``POST /v1/ams/target-check``.
 *
 * Attributes:
 * store_id: The store.
 * ad_group_id: The ad group the targets would go into.
 * targets: Keywords or ASINs to check, one per entry.
 */
export type TargetCheckRequest = {
    store_id: number;
    ad_group_id: string;
    targets: Array<string>;
};

/**
 * What the master says the proposed targets should bid, and which exist.
 *
 * Attributes:
 * ad_group_id: The ad group.
 * ad_group_name: Its name.
 * campaign_name: Its campaign.
 * calculated_bid: target ACOS times target conversion times ASP, or null when the
 * master lacks any of the three.
 * items: One per unique submitted target.
 */
export type TargetCheckResult = {
    ad_group_id: string;
    ad_group_name: string;
    campaign_name: string | null;
    calculated_bid: AdsMoney | null;
    items: Array<TargetCheckItem>;
};

/**
 * The kinds of identifier a write's ``targets`` holds (ingest's ADR 0043).
 */
export type TargetKind = 'skus' | 'offer_ids' | 'campaign_ids' | 'ad_group_ids' | 'keyword_ids' | 'target_ids' | 'ad_ids' | 'order_ids' | 'order_item_ids' | 'shipment_ids' | 'subscription_ids' | 'stream_keys';

/**
 * The kinds of identifier a write's ``targets`` holds (ingest's ADR 0043).
 */
export const TargetKind = {
    SKUS: 'skus',
    OFFER_IDS: 'offer_ids',
    CAMPAIGN_IDS: 'campaign_ids',
    AD_GROUP_IDS: 'ad_group_ids',
    KEYWORD_IDS: 'keyword_ids',
    TARGET_IDS: 'target_ids',
    AD_IDS: 'ad_ids',
    ORDER_IDS: 'order_ids',
    ORDER_ITEM_IDS: 'order_item_ids',
    SHIPMENT_IDS: 'shipment_ids',
    SUBSCRIPTION_IDS: 'subscription_ids',
    STREAM_KEYS: 'stream_keys'
} as const;

/**
 * Every target a text or id names in a store, one timeline each.
 *
 * The same keyword in one ad group can carry several Amazon ids — a re-add
 * gets a new one — so a lookup by text answers with all of them.
 *
 * Attributes:
 * timelines: One per distinct target id, live targets first.
 */
export type TargetTimelines = {
    timelines: Array<EntityTimeline>;
};

/**
 * ``targets`` in a settings PATCH. ``currency`` is the goal's.
 */
export type TargetsUpdate = {
    expected_margin_rate?: number | string | null;
    expected_acos?: number | string | null;
    target_tacos?: number | string | null;
    monthly_sales_goal?: number | string | null;
    currency?: string | null;
};

/**
 * One of a vendor's tax numbers.
 */
export type TaxIdRead = {
    id: number;
    kind: VendorTaxIdKind;
    value: string;
    country: string | null;
};

/**
 * One tax number, as sent.
 *
 * Attributes:
 * kind: Which register.
 * value: As on the vendor's invoices; trimmed.
 * country: ISO 3166-1 alpha-2 of the register.
 */
export type TaxIdWrite = {
    kind: VendorTaxIdKind;
    value: string;
    country?: string | null;
};

/**
 * Who the caller is to the tickets pages.
 *
 * The frontend picks tabs and controls from this rather than from the
 * token, which it cannot read.
 *
 * Attributes:
 * principal_id: The caller, as tickets record owners and creators.
 * can_edit: Holds ``tickets:edit``: assigns to others, reviews, deletes
 * anyone's ticket, edits ETAs and raises detected types.
 */
export type TicketAccess = {
    principal_id: string;
    can_edit: boolean;
};

/**
 * Item-level decisions, in the type's own shape.
 *
 * Attributes:
 * updates: VentaHub's ``{identifiers, action, ...}`` entries.
 * actor_name: The caller's display name.
 */
export type TicketActionRequest = {
    updates: Array<{
        [key: string]: unknown;
    }>;
    actor_name?: string | null;
};

/**
 * The ticket after the decisions, and what the type reported.
 *
 * Attributes:
 * ticket: The ticket now.
 * result: Whatever the type returned, e.g. per-item outcomes.
 */
export type TicketActionResult = {
    ticket: TicketRead;
    result: {
        [key: string]: unknown;
    };
};

/**
 * Give a ticket an owner.
 *
 * Attributes:
 * principal_id: Who. Omitted means the caller: a self-assign.
 * name: Their display name, for the snapshot.
 */
export type TicketAssign = {
    principal_id?: string | null;
    name: string;
};

/**
 * A comment.
 *
 * Attributes:
 * body: The text. Mentions are written into it as ``@Name``.
 * author_name: The caller's display name.
 * mentions: Principal ids mentioned.
 */
export type TicketCommentCreate = {
    body: string;
    author_name?: string | null;
    mentions?: Array<string>;
};

/**
 * A comment as it was written.
 *
 * Attributes:
 * ticket_id: The ticket.
 * author_principal_id: Who; null for the system.
 * author_name: Their name then.
 * body: The text.
 * is_master: Whether it is the description.
 * mentions: Principal ids mentioned.
 */
export type TicketCommentRead = {
    id: string;
    created_at: string;
    updated_at: string;
    ticket_id: string;
    author_principal_id: string | null;
    author_name: string | null;
    body: string;
    is_master: boolean;
    mentions: Array<string>;
};

/**
 * How many tickets each view holds, under the same filters.
 *
 * One field per :class:`~ventawiz.tickets.views.TicketView`, plus the unread
 * notifications that badge the Notifications tab.
 */
export type TicketCounts = {
    unassigned: number;
    assigned: number;
    review_pending: number;
    reviewed_all: number;
    auto_solved: number;
    my_pending: number;
    my_inprogress: number;
    my_resolved: number;
    my_reviewed: number;
    raised_pending_inprogress: number;
    raised_open_to_review: number;
    raised_reviewed: number;
    notifications: number;
    notifications_unread: number;
};

/**
 * Raise tickets of one type.
 *
 * The payload may raise several tickets: a type groups and chunks its items.
 *
 * Attributes:
 * ticket_type_id: The type.
 * data: ``{"items": [...], ...}`` in the type's shape. ``client_id`` on
 * each item is stamped from the token.
 * description: The master comment, copied onto every ticket raised.
 * creator_name: The caller's display name, for the snapshot. Tokens
 * carry no name.
 */
export type TicketCreate = {
    ticket_type_id: string;
    data: {
        [key: string]: unknown;
    };
    description?: string | null;
    creator_name?: string | null;
};

/**
 * What a raise produced. Empty when every item was already open.
 *
 * Attributes:
 * ids: The new tickets' ULIDs, in the order they were raised.
 * numbers: Their ``#`` numbers, in the same order.
 */
export type TicketCreateResult = {
    ids: Array<string>;
    numbers: Array<number>;
};

/**
 * The caller's tickets solved, reviewed or assigned, per day, every day of the range.
 *
 * Attributes:
 * metric: What was counted.
 * days: Every day from the first to the last, zeros included.
 * total: The days summed.
 */
export type TicketDoneSeries = {
    metric: DoneMetric;
    days: Array<DoneDay>;
    total: number;
};

/**
 * One SKU excused from a ticket type.
 */
export type TicketExceptionRow = {
    brand_id: number | null;
    brand_name: string | null;
    category: string | null;
    subcategory: string | null;
    store_id: number;
    store_name: string;
    marketplace_id: number;
    marketplace_short_code: string;
    country_code: string;
    id: number;
    sku_id: number;
    sku_code: string;
    sku_name: string;
    ticket_type_id: string;
    ticket_type_name: string | null;
    retailer_sku_code: string;
    mp_sku_code: string;
    comments: string;
    created_at: string;
};

/**
 * A brand an item may name, as T1's picker lists it.
 *
 * Attributes:
 * brand_id: The ``com_brand`` key — what an item's ``brand_id`` holds.
 * name: For people.
 */
export type TicketFormBrand = {
    brand_id: number;
    name: string;
};

/**
 * A marketplace an item may name, as the picker lists it.
 *
 * Attributes:
 * mp_id: The ``com_marketplace`` key — what an item's ``mp_id`` holds.
 * name: For people, e.g. ``Amazon DE``.
 * short_code: e.g. ``AZDE``.
 * country_code: Its country, ISO 3166-1 alpha-2 as the registry spells
 * it (``UK``, ``KSA`` included) — for the picker's flag.
 */
export type TicketFormMarketplace = {
    mp_id: number;
    name: string;
    short_code: string;
    country_code: string;
};

/**
 * A store a ticket may name, as the list's store filter lists it.
 *
 * Attributes:
 * store_id: The ``com_store`` key — what a ticket's ``store_id`` holds.
 * name: For people.
 * mp_id: Its marketplace.
 * marketplace_name: Its marketplace's name; null if that was deleted.
 * country_code: Its marketplace's country; null if that was deleted.
 */
export type TicketFormStore = {
    store_id: number;
    name: string;
    mp_id: number;
    marketplace_name: string | null;
    country_code: string | null;
};

/**
 * A warehouse a ticket may name, as the list's warehouse filter lists it.
 *
 * Attributes:
 * warehouse_id: The ``com_warehouse`` key — what a ticket's ``warehouse_id`` holds.
 * name: For people.
 */
export type TicketFormWarehouse = {
    warehouse_id: number;
    name: string;
};

/**
 * One row of the tickets list.
 *
 * Attributes:
 * id: ULID.
 * number: The ``#`` people quote.
 * ticket_type_id: The type's code.
 * ticket_type_name: The type's name.
 * section: The page it is listed on.
 * data_type: The sub-type, ``items[0].data_type``.
 * status: Its status.
 * percent_completion: 0-100.
 * num_items: How many items it carries.
 * owner_principal_id: Who is working it.
 * owner_name: Their name when assigned.
 * creator_principal_id: Who raised it; null for the system.
 * creator_name: Their name then.
 * reviewer_principal_id: Who reviewed it.
 * reviewer_name: Their name then.
 * brand_id: Its brand.
 * brand_name: Its brand's name.
 * marketplace_id: Its marketplace.
 * marketplace_name: Its marketplace's name.
 * marketplace_country_code: Its marketplace's country, ISO 3166-1
 * alpha-2 as the registry spells it (``UK``, ``KSA`` included).
 * store_id: Its store.
 * store_name: Its store's name.
 * warehouse_id: Its warehouse.
 * warehouse_name: Its warehouse's name.
 * created_at: When it was raised.
 * updated_at: When it last changed.
 * assigned_at: When it was last assigned.
 * resolved_at: When it entered RESOLVED.
 * completed_at: When its owner finished it.
 * is_read: Read flag, for notifications.
 * decided_by_system: Closed by the engine with nobody's review.
 * eta: The type's ETA in days; 0 means no SLA.
 * is_overdue: Open and past its ETA.
 */
export type TicketListItem = {
    id: string;
    number: number;
    ticket_type_id: string;
    ticket_type_name: string;
    section: TicketSection | null;
    data_type: string | null;
    status: TicketStatus;
    percent_completion: number;
    num_items: number;
    owner_principal_id: string | null;
    owner_name: string | null;
    creator_principal_id: string | null;
    creator_name: string | null;
    reviewer_principal_id: string | null;
    reviewer_name: string | null;
    brand_id: number | null;
    brand_name: string | null;
    marketplace_id: number | null;
    marketplace_name: string | null;
    marketplace_country_code: string | null;
    store_id: number | null;
    store_name: string | null;
    warehouse_id: number | null;
    warehouse_name: string | null;
    created_at: string;
    updated_at: string;
    assigned_at: string | null;
    resolved_at: string | null;
    completed_at: string | null;
    is_read: boolean;
    decided_by_system: boolean;
    eta: number;
    is_overdue: boolean;
};

/**
 * Dismiss notification tickets: they leave the tab as RESOLVED.
 *
 * Attributes:
 * ticket_ids: Which. Tickets of other types are ignored.
 */
export type TicketNotificationsDismiss = {
    ticket_ids: Array<string>;
};

/**
 * Mark notification tickets read or unread.
 *
 * Attributes:
 * ticket_ids: Which. Tickets of other types are ignored.
 * is_read: Read, or back to unread.
 */
export type TicketNotificationsRead = {
    ticket_ids: Array<string>;
    is_read?: boolean;
};

/**
 * How many tickets a bulk notification change touched.
 *
 * Attributes:
 * updated: Tickets changed. Ids of other types, other tenants or none at
 * all are not counted.
 */
export type TicketNotificationsResult = {
    updated: number;
};

/**
 * One ticket with its payload and description.
 *
 * Attributes:
 * data: ``{"items": [...], ...}`` — the type's payload, VentaHub's shape.
 * description: The master comment, if it has one.
 * ticket_type_description: What the type asks of its resolver.
 * allows_manual_resolve: Whether a person may set RESOLVED.
 */
export type TicketRead = {
    id: string;
    number: number;
    ticket_type_id: string;
    ticket_type_name: string;
    section: TicketSection | null;
    data_type: string | null;
    status: TicketStatus;
    percent_completion: number;
    num_items: number;
    owner_principal_id: string | null;
    owner_name: string | null;
    creator_principal_id: string | null;
    creator_name: string | null;
    reviewer_principal_id: string | null;
    reviewer_name: string | null;
    brand_id: number | null;
    brand_name: string | null;
    marketplace_id: number | null;
    marketplace_name: string | null;
    marketplace_country_code: string | null;
    store_id: number | null;
    store_name: string | null;
    warehouse_id: number | null;
    warehouse_name: string | null;
    created_at: string;
    updated_at: string;
    assigned_at: string | null;
    resolved_at: string | null;
    completed_at: string | null;
    is_read: boolean;
    decided_by_system: boolean;
    eta: number;
    is_overdue: boolean;
    data: {
        [key: string]: unknown;
    };
    description: string | null;
    ticket_type_description: string | null;
    allows_manual_resolve: boolean;
};

/**
 * Report a problem with a ticket — VentaHub's ``POST /tickets/report``.
 *
 * Raises a T8 for the technical team carrying the reported ticket's state
 * and the reporter's browser. The reporter is the caller; nothing here can
 * name somebody else.
 *
 * Attributes:
 * source_ticket_id: The ticket being reported.
 * reporter_name: The caller's display name.
 * reason: What is wrong.
 * category: What kind of problem.
 * severity: How bad.
 * steps_to_reproduce: How to see it.
 * expected_behavior: What should have happened.
 * actual_behavior: What did.
 * browser_info: The reporter's browser, as the page read it.
 * console_errors: Errors the page logged, newest last.
 * source_url: The page the report was made from.
 */
export type TicketReportCreate = {
    source_ticket_id: string;
    reporter_name?: string | null;
    reason: string;
    category: ReportCategory;
    severity?: ReportSeverity | null;
    steps_to_reproduce?: string | null;
    expected_behavior?: string | null;
    actual_behavior?: string | null;
    browser_info?: BrowserInfo | null;
    console_errors?: Array<string>;
    source_url?: string | null;
};

/**
 * Which dashboard page a type's tickets are listed on.
 */
export type TicketSection = 'business' | 'ads';

/**
 * Which dashboard page a type's tickets are listed on.
 */
export const TicketSection = {
    BUSINESS: 'business',
    ADS: 'ads'
} as const;

/**
 * What the list can be ordered by — VentaHub's ``_SORTABLE_COLUMNS``.
 *
 * ``age`` is creation time read the other way round: the oldest ticket has
 * the greatest age.
 */
export type TicketSort = 'number' | 'status' | 'type' | 'owner_name' | 'creator_name' | 'brand_name' | 'store_name' | 'marketplace_name' | 'percent_completion' | 'created_at' | 'assigned_at' | 'completed_at' | 'num_items' | 'data_type' | 'age';

/**
 * What the list can be ordered by — VentaHub's ``_SORTABLE_COLUMNS``.
 *
 * ``age`` is creation time read the other way round: the oldest ticket has
 * the greatest age.
 */
export const TicketSort = {
    NUMBER: 'number',
    STATUS: 'status',
    TYPE: 'type',
    OWNER_NAME: 'owner_name',
    CREATOR_NAME: 'creator_name',
    BRAND_NAME: 'brand_name',
    STORE_NAME: 'store_name',
    MARKETPLACE_NAME: 'marketplace_name',
    PERCENT_COMPLETION: 'percent_completion',
    CREATED_AT: 'created_at',
    ASSIGNED_AT: 'assigned_at',
    COMPLETED_AT: 'completed_at',
    NUM_ITEMS: 'num_items',
    DATA_TYPE: 'data_type',
    AGE: 'age'
} as const;

/**
 * Ascending or descending.
 */
export type TicketSortOrder = 'asc' | 'desc';

/**
 * Ascending or descending.
 */
export const TicketSortOrder = {
    ASC: 'asc',
    DESC: 'desc'
} as const;

/**
 * Where a ticket is in its life.
 *
 * VentaHub enforced no transition table and neither does this enum; the
 * service decides who may move a ticket where. ``RESOLVED`` is the owner
 * saying "done"; ``REVIEWED`` is somebody else agreeing, or the engine having
 * decided it alone (see :attr:`Ticket.decided_by_system`).
 */
export type TicketStatus = 'PENDING' | 'INPROGRESS' | 'RESOLVED' | 'REVIEWED';

/**
 * Where a ticket is in its life.
 *
 * VentaHub enforced no transition table and neither does this enum; the
 * service decides who may move a ticket where. ``RESOLVED`` is the owner
 * saying "done"; ``REVIEWED`` is somebody else agreeing, or the engine having
 * decided it alone (see :attr:`Ticket.decided_by_system`).
 */
export const TicketStatus = {
    PENDING: 'PENDING',
    INPROGRESS: 'INPROGRESS',
    RESOLVED: 'RESOLVED',
    REVIEWED: 'REVIEWED'
} as const;

/**
 * Move a ticket, change its progress, or both.
 *
 * Progress alone is a valid update — VentaHub's PATCH took
 * ``percent_completion`` by itself — so ``status`` may be left out; one of
 * the two must be given.
 *
 * Attributes:
 * status: Where to; None to leave it where it is.
 * percent_completion: New progress, 0-100.
 * actor_name: The caller's display name, kept as the reviewer's when the
 * ticket becomes REVIEWED.
 */
export type TicketStatusUpdate = {
    status?: TicketStatus | null;
    percent_completion?: number | null;
    actor_name?: string | null;
};

/**
 * One ticket type: the ``app_ticket_type`` row plus the catalogue's facts.
 *
 * Attributes:
 * ticket_type_id: ``T1`` … ``T32``.
 * ticket_type_name: Display name.
 * ticket_type_description: What it asks of its resolver.
 * eta: Days from creation the resolver has; 0 means no SLA. Editable.
 * resolver_expected_role: VentaHub's informational role.
 * section: The page its tickets are listed on.
 * is_notification: Listed only in the Notifications tab.
 * manual_create: May be raised from the create page.
 * has_detector: May be raised by ``POST /v1/tickets/raise/{code}``.
 */
export type TicketTypeRead = {
    ticket_type_id: string;
    ticket_type_name: string;
    ticket_type_description: string | null;
    eta: number;
    resolver_expected_role: string;
    section: TicketSection;
    is_notification: boolean;
    manual_create: boolean;
    has_detector: boolean;
};

/**
 * Change a type's ETA.
 *
 * Attributes:
 * eta: Days. 0 means no SLA.
 */
export type TicketTypeUpdate = {
    eta?: number | null;
};

/**
 * Edit a ticket its creator raised, while nobody has picked it up.
 *
 * Attributes:
 * data: The new payload, validated by the type.
 * description: The new master comment. An empty string removes it.
 */
export type TicketUpdate = {
    data?: {
        [key: string]: unknown;
    } | null;
    description?: string | null;
};

/**
 * One tab or sub-tab of the tickets list — VentaHub's ``TicketView``.
 *
 * Which tabs a person *sees* is the frontend's call (by whether they hold
 * ``tickets:edit``); which rows a view returns is the same for everyone and
 * still passes through the visibility rules.
 */
export type TicketView = 'unassigned' | 'assigned' | 'review_pending' | 'reviewed_all' | 'auto_solved' | 'my_pending' | 'my_inprogress' | 'my_resolved' | 'my_reviewed' | 'raised_pending_inprogress' | 'raised_open_to_review' | 'raised_reviewed' | 'notifications';

/**
 * One tab or sub-tab of the tickets list — VentaHub's ``TicketView``.
 *
 * Which tabs a person *sees* is the frontend's call (by whether they hold
 * ``tickets:edit``); which rows a view returns is the same for everyone and
 * still passes through the visibility rules.
 */
export const TicketView = {
    UNASSIGNED: 'unassigned',
    ASSIGNED: 'assigned',
    REVIEW_PENDING: 'review_pending',
    REVIEWED_ALL: 'reviewed_all',
    AUTO_SOLVED: 'auto_solved',
    MY_PENDING: 'my_pending',
    MY_INPROGRESS: 'my_inprogress',
    MY_RESOLVED: 'my_resolved',
    MY_REVIEWED: 'my_reviewed',
    RAISED_PENDING_INPROGRESS: 'raised_pending_inprogress',
    RAISED_OPEN_TO_REVIEW: 'raised_open_to_review',
    RAISED_REVIEWED: 'raised_reviewed',
    NOTIFICATIONS: 'notifications'
} as const;

/**
 * The dashboard's ticket matrix for one week.
 *
 * Attributes:
 * week_from: The week's first day.
 * week_to: Its last day.
 * rows: One per type with tickets, by code (T2 before T10).
 * totals: The columns summed.
 * today: What the caller resolved and reviewed today.
 */
export type TicketWorklist = {
    week_from: string;
    week_to: string;
    rows: Array<WorklistRow>;
    totals: WorklistCounts;
    today: WorklistToday;
};

/**
 * Typical duration and cost of recent answers at one depth.
 */
export type TierTiming = {
    p50_seconds?: number | null;
    p50_cost_usd?: number | null;
    runs?: number;
};

/**
 * A queued run. Find it with ``GET …/runs?request_id=``.
 */
export type TikTokAccepted = {
    request_id: string;
    store_id: number;
    action: 'products' | 'orders' | 'tracking';
    dry_run: boolean;
};

/**
 * What a products or orders pull read.
 */
export type TikTokPullCounts = {
    pages: number;
    /**
     * Products' SKUs, or orders
     */
    rows: number;
    /**
     * Records TikTok sent that could not be read
     */
    rejects: number;
    /**
     * Rows not seen before
     */
    new_observations: number;
    /**
     * The page ceiling stopped the pull early
     */
    truncated: boolean;
};

/**
 * One run, newest first in a list.
 */
export type TikTokRunRead = {
    run_id: string;
    request_id: string | null;
    action: ('products' | 'orders' | 'tracking') | null;
    /**
     * Dagster's status: QUEUED, STARTED, SUCCESS, FAILURE, …
     */
    status: string;
    dry_run: boolean;
    created_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    /**
     * A pull's counts, once it has reported
     */
    pull: TikTokPullCounts | null;
    /**
     * A correction's outcome, once it has reported
     */
    tracking: TikTokTrackingResult | null;
    /**
     * Why the run failed, when it did
     */
    error: string | null;
};

/**
 * A TikTok Shop store the caller may sync.
 */
export type TikTokStoreRead = {
    /**
     * com_store.pk_store_id
     */
    store_id: number;
    name: string;
    /**
     * Its marketplace, e.g. TTUS
     */
    short_code: string;
    /**
     * The marketplace's country
     */
    country_code: string;
};

/**
 * What became of a tracking correction.
 */
export type TikTokTrackingResult = {
    /**
     * planned, unchanged, updated, refused or refused_locked
     */
    outcome: string;
    order_id: string;
    tracking_number: string;
    shipping_provider_id: string;
    /**
     * The order's status on TikTok when read
     */
    order_status: string | null;
    /**
     * The numbers TikTok carried when the order was read
     */
    previous_tracking_numbers: Array<string>;
    message: string | null;
    /**
     * TikTok's code, when it refused
     */
    tiktok_code: number | null;
};

/**
 * What a timeline is about.
 *
 * Attributes:
 * level: target, ad_group or campaign.
 * entity_id: Amazon's id.
 * text: Keyword, target, ad group or campaign name.
 * match_type: For keywords.
 * ad_type: SP, SB or SD.
 * state: Its current state.
 * current_bid: Its current bid, for a target.
 * ad_group_name: The ad group it belongs to.
 * campaign_name: The campaign it belongs to.
 */
export type TimelineEntity = {
    level: string;
    entity_id: string;
    text: string | null;
    match_type: string | null;
    ad_type: string | null;
    state: string | null;
    current_bid: string | null;
    ad_group_name: string | null;
    campaign_name: string | null;
};

/**
 * One point in an entity's history.
 *
 * Attributes:
 * occurred_at: When.
 * event_type: first_appeared, added, bid_change, state_change or
 * budget_change.
 * old_value: Before.
 * new_value: After.
 * source: ``observed`` for a change seen in Amazon's own data, else the
 * canonical source of the action that made it.
 * result: success, error or skipped, for an action.
 * result_message: Amazon's message, for a failed action.
 * actor_id: Who applied it, when it was a person.
 * ticket_id: The ticket it came from, when recorded.
 * actor_name: Their name, when it resolves.
 * ticket: The ticket's status and people, when the caller may read it.
 */
export type TimelineEvent = {
    occurred_at: string;
    event_type: string;
    old_value: string | null;
    new_value: string | null;
    source: string;
    result: string | null;
    result_message: string | null;
    actor_id: number | null;
    ticket_id: string | null;
    actor_name?: string | null;
    ticket?: ChangeTicket | null;
};

/**
 * One narrowing the caller has made.
 *
 * Attributes:
 * store_id: The store it applies to, or null for every store.
 * page: The page it applies to, or null for every page.
 * timezone: The IANA zone.
 */
export type TimezoneOverrideRead = {
    store_id: number | null;
    page: string | null;
    timezone: string;
};

/**
 * A narrowing to set. The same store and page again replaces the zone.
 *
 * Attributes:
 * store_id: The store, or null for every store.
 * page: The page, or null for every page.
 * timezone: The IANA zone to read times in there.
 */
export type TimezoneOverrideWrite = {
    store_id?: number | null;
    page?: string | null;
    timezone: string;
};

/**
 * What to expect from a fast answer, a deep one and a snapshot.
 */
export type TimingRead = {
    fast: TierTiming;
    deep: TierTiming;
    snapshot: TierTiming;
    /**
     * Deep answers' typical cost as a multiple of fast ones.
     */
    deep_cost_multiple?: number | null;
};

/**
 * A run's tokens, by kind.
 */
export type TokenCounts = {
    input: number;
    output: number;
    cache_read: number;
    cache_write: number;
};

/**
 * One entry of a run's trace.
 *
 * Attributes:
 * kind: ``query``, ``endpoint_call``, ``knowledge_lookup``, ``subagent``,
 * ``finding``, ``narration``, ``reasoning``, ``compaction``,
 * ``model_fallback`` or ``tool``.
 * detail: For a query: ``sql`` as the model wrote it and
 * ``rewritten_sql`` as it ran, ``tables``, ``result_id``, and the
 * ``method`` of a ``query_data`` call. The shapes are the tools';
 * the trace shows them as recorded.
 */
export type TraceStep = {
    seq: number;
    agent: string;
    kind: string;
    label: string | null;
    detail: {
        [key: string]: unknown;
    };
    row_count: number | null;
    duration_ms: number | null;
    error: string | null;
};

/**
 * Replace the tracking number TikTok carries for one order.
 *
 * Without ``confirm`` it is a dry run: the order is read and what would be
 * sent is reported, and nothing is written to TikTok.
 */
export type TrackingCorrectionRequest = {
    /**
     * The TikTok order id
     */
    order_id: string;
    tracking_number: string;
    /**
     * TikTok's id for the carrier the number is submitted under
     */
    shipping_provider_id: string;
    /**
     * Send the correction. False (the default) is a dry run.
     */
    confirm?: boolean;
};

/**
 * What Amazon's traffic can be grouped by.
 */
export type TrafficGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'product' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'week' | 'month';

/**
 * What Amazon's traffic can be grouped by.
 */
export const TrafficGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    PRODUCT: 'product',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Amazon's traffic, grouped.
 */
export type TrafficReport = {
    items: Array<TrafficRow>;
    pagination: Pagination;
    totals: Array<TrafficTotals>;
    meta: ReportMeta;
};

/**
 * One group of products and weeks.
 *
 * ``product_ref`` is filled when grouped by product, the child ASIN, as
 * well as by listing. Amazon reports by ASIN, so a listing is the one the
 * ASIN's owner SKU is first listed under, for drill-down: the figure is
 * the ASIN's.
 */
export type TrafficRow = {
    month?: string | null;
    week?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    sessions?: number | null;
    page_views?: number | null;
    glance_views?: number | null;
    units_ordered?: number | null;
    buy_box_page_views?: string | null;
    buy_box_share?: string | null;
    unit_session_pct?: string | null;
};

/**
 * Amazon's traffic over every matching product and week.
 *
 * Seller accounts report sessions and page views, vendor accounts glance
 * views, and a figure no row reported is ``null``. A week counts in the
 * date range, and the month, that holds its Sunday (D23). The ratios are
 * computed from the sums, never averaged. Where two of our seller stores in
 * one marketplace report the same product page in a week, a total across
 * them counts that page in each (plan §8, question 17).
 *
 * Attributes:
 * sessions: Sessions.
 * page_views: Page views.
 * glance_views: Glance views.
 * units_ordered: Units ordered, as Amazon's traffic report counts them.
 * buy_box_page_views: Page views on which the buy box appeared: an
 * estimate where Amazon rounded its percentage.
 * buy_box_share: Buy-box page views ÷ page views, a fraction.
 * unit_session_pct: Units ordered ÷ sessions, a fraction (0.1 is 10%):
 * Amazon's unit session percentage.
 */
export type TrafficTotals = {
    sessions?: number | null;
    page_views?: number | null;
    glance_views?: number | null;
    units_ordered?: number | null;
    buy_box_page_views?: string | null;
    buy_box_share?: string | null;
    unit_session_pct?: string | null;
};

/**
 * What a transfer moved.
 *
 * Attributes:
 * moved_boxes: Boxes moved.
 * moved_units: Units moved across them.
 * boxes: Each box, in the request's order.
 */
export type TransferRead = {
    moved_boxes: number;
    moved_units: number;
    boxes: Array<TransferredBox>;
};

/**
 * Boxes to move from one warehouse to another.
 *
 * Attributes:
 * from_warehouse_id: Where the boxes are.
 * to_warehouse_id: Where they go. Must differ from the source.
 * mappings: One line per box, in the order they are reported.
 */
export type TransferRequest = {
    from_warehouse_id: number;
    to_warehouse_id: number;
    mappings: Array<BoxMapping>;
};

/**
 * One box moved.
 *
 * Attributes:
 * from_box_code: The source box, as stored.
 * to_box_code: The new box in the destination, as stored.
 * units: Units moved, every SKU and batch together.
 * sku_count: Distinct SKUs moved.
 */
export type TransferredBox = {
    from_box_code: string;
    to_box_code: string;
    units: number;
    sku_count: number;
};

/**
 * One currency's points. Counts have a single series with no currency.
 *
 * Attributes:
 * currency: For a money metric; null for a count.
 * points: Oldest first, gaps omitted.
 */
export type TrendSeries = {
    currency: string | null;
    points: Array<VentawizAdsSchemasTrendPoint>;
};

/**
 * An Unblock & Shift request.
 *
 * The lines must account for everything the block holds, no more and no less:
 * the block is released whole, and its units moved.
 *
 * Attributes:
 * warehouse_id: Where the block and boxes are.
 * block_id: The block to release. Left out, it is the one valid block the
 * lines match in full; two blocks holding identical stock cannot be told
 * apart that way, so send it whenever it is known.
 * shift_date: As on :class:`ShiftCreate`.
 * lines: Where its units go.
 */
export type UnblockShiftCreate = {
    warehouse_id: number;
    block_id?: number | null;
    shift_date?: string | null;
    lines: Array<UnblockShiftLine>;
};

/**
 * Blocked units of one SKU to release and move to another box.
 *
 * Attributes:
 * from_box_code: The box the block holds them in.
 * sku_code: Our SKU code, the same on both sides.
 * to_box_code: The box they move to, created when missing.
 * quantity: Units. Positive.
 * expiry_date: The blocked batch. Leave out to match whatever the block
 * holds of this SKU in this box.
 */
export type UnblockShiftLine = {
    from_box_code: string;
    sku_code: string;
    to_box_code: string;
    quantity: number;
    expiry_date?: string | null;
};

/**
 * What an Unblock & Shift did: the release, then the move.
 *
 * Attributes:
 * unblocked: The block released.
 * shift: The move of its units.
 */
export type UnblockShiftRead = {
    unblocked: UnblockedRead;
    shift: ShiftRead;
};

/**
 * The block an Unblock & Shift released.
 *
 * Attributes:
 * block_id: The block.
 * po_code: Its PO code.
 * units: Units it held, now free and moved.
 */
export type UnblockedRead = {
    block_id: number;
    po_code: string | null;
    units: number;
};

/**
 * A brand name SKUs carry that some of them are not linked by.
 *
 * Attributes:
 * name: As SKUs spell it most often, trimmed.
 * sku_count: Live SKUs carrying it, ignoring case.
 * unlinked_sku_count: Of those, the ones linked to no brand.
 * sample_sku_codes: A few of the unlinked ones.
 * suggestion: The brand it most likely is, or ``None``.
 */
export type UnmatchedSkuName = {
    name: string;
    sku_count: number;
    unlinked_sku_count: number;
    sample_sku_codes: Array<string>;
    suggestion: BrandRef | null;
};

/**
 * A word people ask with that no glossary entry, alias, dataset or measure knows.
 */
export type UnmatchedTerm = {
    term: string;
    questions: number;
    example: string;
};

/**
 * Unknown words, most frequent first.
 */
export type UnmatchedTerms = {
    items: Array<UnmatchedTerm>;
};

/**
 * A SKU no vendor prices at the warehouse: it cannot go on an order.
 */
export type UnpricedSku = {
    sku_id: number;
    sku_code: string | null;
};

/**
 * What committing a file would do.
 *
 * Attributes:
 * mode: ``add`` keeps the rest of the list; ``replace`` removes the
 * person's other listings in the stores the file names.
 * rows: Row by row.
 * adding: Listings it would add.
 * already: Listings already on the list.
 * removing: Listings ``replace`` would take off.
 * errors: Rows with an error; any error and nothing is saved.
 */
export type UploadPreview = {
    mode: 'add' | 'replace';
    rows: Array<UploadPreviewRow>;
    adding: number;
    already: number;
    removing: number;
    errors: number;
};

/**
 * One row of an upload, as read and matched.
 *
 * Attributes:
 * row: Its line in the file, counting the first row under the header as 1.
 * store: The Store cell.
 * retailer_sku: The Retailer SKU cell.
 * seller_sku: The Seller SKU cell.
 * sku_code: The SKU Code cell.
 * drr: The DRR cell, as a decimal string.
 * listed_product_ids: The listings it matched.
 * action: ``add`` (puts at least one listing on the list), ``already``
 * (every match is on it already) or ``error``.
 * error: What is wrong, for an error.
 */
export type UploadPreviewRow = {
    row: number;
    store: string | null;
    retailer_sku: string | null;
    seller_sku: string | null;
    sku_code: string | null;
    drr: string | null;
    listed_product_ids: Array<number>;
    action: 'add' | 'already' | 'error';
    error?: string | null;
};

/**
 * What a committed upload did.
 *
 * Attributes:
 * added: Listings put on the list.
 * removed: Listings ``replace`` took off.
 * drr_set: Listings whose DRR the file set.
 */
export type UploadResult = {
    added: number;
    removed: number;
    drr_set: number;
};

/**
 * The groups the "Used by" view shows tables under, in its order.
 */
export type UsageDomain = 'stores' | 'stock' | 'purchasing' | 'finance' | 'costs' | 'links' | 'catalogue' | 'ads' | 'snapshots' | 'tickets' | 'access' | 'annotations';

/**
 * The groups the "Used by" view shows tables under, in its order.
 */
export const UsageDomain = {
    STORES: 'stores',
    STOCK: 'stock',
    PURCHASING: 'purchasing',
    FINANCE: 'finance',
    COSTS: 'costs',
    LINKS: 'links',
    CATALOGUE: 'catalogue',
    ADS: 'ads',
    SNAPSHOTS: 'snapshots',
    TICKETS: 'tickets',
    ACCESS: 'access',
    ANNOTATIONS: 'annotations'
} as const;

/**
 * The tables of one domain that refer to the entity.
 */
export type UsageDomainRead = {
    domain: UsageDomain;
    label: string;
    total: number;
    tables: Array<UsageTable>;
};

/**
 * Today's allowances.
 *
 * Display only: the actions that spend them can still answer 429, because
 * another tab may have spent the last unit in the meantime.
 *
 * Attributes:
 * runs: Pipeline starts.
 * regenerations: Regenerate requests — one image or one tab each.
 * resets_at: The next 00:00 UTC.
 */
export type UsageRead = {
    runs: AllowanceRead;
    regenerations: AllowanceRead;
    resets_at: string;
};

/**
 * What refers to an entity, in this tenant, by domain.
 *
 * Only tables with at least one row, and domains with at least one table,
 * are listed; ``total`` is their sum. A marketplace's or a brand's report
 * counts this tenant's rows only.
 */
export type UsageReport = {
    total: number;
    domains: Array<UsageDomainRead>;
};

/**
 * One table's references to the entity, in this tenant.
 *
 * Attributes:
 * table: The table, without its schema, e.g. ``com_stock``.
 * label: What the view calls it.
 * count: Live rows referring to the entity.
 */
export type UsageTable = {
    table: string;
    label: string;
    count: number;
};

/**
 * A principal to assign. ``display`` is kept for showing without a lookup.
 */
export type UserWrite = {
    principal_id: string;
    display?: string | null;
};

/**
 * The answer to Validate. Nothing is written.
 *
 * Attributes:
 * can_transfer: Whether every line can move: what Transfer would do now.
 * moved_boxes: Lines that can move. Transfer moves them only when
 * ``can_transfer``: it is all or nothing.
 * moved_units: Units across the lines that can move.
 * boxes: Each line's verdict, in the request's order.
 */
export type ValidationRead = {
    can_transfer: boolean;
    moved_boxes: number;
    moved_units: number;
    boxes: Array<BoxCheck>;
};

/**
 * The prompt variant a run was answered with (A/B).
 */
export type Variant = 'a' | 'b';

/**
 * The prompt variant a run was answered with (A/B).
 */
export const Variant = {
    A: 'a',
    B: 'b'
} as const;

/**
 * Body of ``POST /v1/vendors``.
 *
 * Attributes:
 * code: Lowercase, hyphenated, unique in the organization and permanent.
 * Left out, the vendor is given ``vnd-<id>``.
 * name: Unique among the organization's active vendors.
 */
export type VendorCreate = {
    kind?: VendorKind | null;
    country?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    address?: string | null;
    email?: string | null;
    poc_name?: string | null;
    poc_phone?: string | null;
    website?: string | null;
    default_currency?: string | null;
    lead_time_days?: number | null;
    moq_units?: number | null;
    payment_terms_days?: number | null;
    group_warehouse_id?: number | null;
    code?: string | null;
    name: string;
};

/**
 * What Amazon's stock can be grouped by.
 */
export type VendorInventoryGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'product' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'week';

/**
 * What Amazon's stock can be grouped by.
 */
export const VendorInventoryGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    PRODUCT: 'product',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    WEEK: 'week'
} as const;

/**
 * Amazon's stock, grouped.
 */
export type VendorInventoryReport = {
    items: Array<VendorInventoryRow>;
    pagination: Pagination;
    totals: Array<VendorInventoryTotals>;
    meta: ReportMeta;
};

/**
 * One group of Amazon's stock.
 *
 * ``product_ref`` is filled when grouped by product, the ASIN, as well as
 * by listing: the listing the ASIN's owner SKU is first listed under.
 *
 * Attributes:
 * week_start_date: Filled when grouped by week: the Sunday of the week
 * the figures are from, each store's newest.
 */
export type VendorInventoryRow = {
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    sellable_units?: number | null;
    unsellable_units?: number | null;
    aged_90_plus_units?: number | null;
    unhealthy_units?: number | null;
    open_po_units?: number | null;
    unfilled_customer_ordered_units?: number | null;
    sellable_cost?: MoneyRead | null;
    unsellable_cost?: MoneyRead | null;
    aged_90_plus_cost?: MoneyRead | null;
    unhealthy_cost?: MoneyRead | null;
    sell_through?: string | null;
    week_start_date?: string | null;
};

/**
 * Amazon's stock of what it bought from us, in one currency.
 *
 * Amazon's stock, never ours and never added to it (D24). Each store
 * counts its newest reported week only, and a store whose feed stopped
 * shows its last one: grouping by week says which. ``null`` is not
 * reported, and Amazon leaves on-hand stock empty on many rows, so a total
 * is a lower bound.
 *
 * Attributes:
 * currency_code: The store's currency.
 * sellable_units: Units Amazon holds and can sell.
 * unsellable_units: Units it holds and cannot sell.
 * aged_90_plus_units: Sellable units it has held for over 90 days.
 * unhealthy_units: Units Amazon counts as unhealthy stock.
 * open_po_units: Units on Amazon's open purchase orders.
 * unfilled_customer_ordered_units: Units customers ordered that Amazon
 * has not shipped.
 * sellable_cost: What the sellable units cost Amazon.
 * unsellable_cost: What the unsellable units cost it.
 * aged_90_plus_cost: What the aged units cost it.
 * unhealthy_cost: What the unhealthy units cost it.
 * sell_through: Amazon's sell-through: net shipped units over what
 * there was to ship (opening sellable plus net received); ``null``
 * when there was nothing to ship.
 */
export type VendorInventoryTotals = {
    currency_code?: string | null;
    sellable_units?: number | null;
    unsellable_units?: number | null;
    aged_90_plus_units?: number | null;
    unhealthy_units?: number | null;
    open_po_units?: number | null;
    unfilled_customer_ordered_units?: number | null;
    sellable_cost?: MoneyRead | null;
    unsellable_cost?: MoneyRead | null;
    aged_90_plus_cost?: MoneyRead | null;
    unhealthy_cost?: MoneyRead | null;
    sell_through?: string | null;
};

/**
 * What saving would refuse, and what it would only warn about.
 */
export type VendorInvoiceCheckRead = {
    problems: Array<VendorInvoiceProblem>;
    warnings: Array<string>;
};

/**
 * One vendor invoice, its lines, the order it fills, and warnings worth a look.
 */
export type VendorInvoiceDetailRead = {
    invoice: VendorInvoiceRead;
    vo_code: string | null;
    lines: Array<VendorInvoiceLineRead>;
    warnings: Array<string>;
};

/**
 * The header fields of a vendor invoice that can change once it is saved.
 *
 * The warehouse, vendor and vendor order stay: the lines were checked
 * against them. Totals come from the lines.
 */
export type VendorInvoiceEdit = {
    number: string;
    date?: string | null;
    received_date?: string | null;
    currency: string;
    amount?: number | string | null;
    credit_period?: number | null;
    cbm?: number | string | null;
    shipping_cost?: number | string | null;
    customs_duty?: number | string | null;
    other_cost?: number | string | null;
    comment?: string | null;
};

/**
 * One line of a vendor invoice. ``vat_rate`` is a fraction: ``0.05`` is 5%.
 */
export type VendorInvoiceLineRead = {
    sku_code: string | null;
    sku_name: string | null;
    box_code: string | null;
    currency: string | null;
    quantity: number | null;
    cost: string | null;
    vat_rate: string | null;
    expiry_date: string | null;
};

/**
 * One line as typed: units of a SKU received into a box.
 *
 * Attributes:
 * sku_code: Our SKU code, or the retailer's code for it at the warehouse.
 * box_code: The box it went into. A code the warehouse does not have yet
 * opens a new box, which needs four of 0-9 and A-Z.
 * quantity: Units received.
 * cost: Per unit, before tax.
 * vat_rate: A fraction: ``0.05`` is 5%.
 * expiry_date: When the batch expires, if it does.
 */
export type VendorInvoiceLineWrite = {
    sku_code: string;
    box_code: string;
    quantity: number;
    cost?: number | string | null;
    vat_rate?: number | string | null;
    expiry_date?: string | null;
};

/**
 * The whole line set, replacing what the invoice held.
 */
export type VendorInvoiceLinesWrite = {
    lines: Array<VendorInvoiceLineWrite>;
};

/**
 * What the filters on this page can be chosen from.
 */
export type VendorInvoiceLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * Something that stops the invoice being saved.
 *
 * Attributes:
 * row: The line's position, from 1, when the problem is one line's.
 */
export type VendorInvoiceProblem = {
    row?: number | null;
    sku_code?: string | null;
    box_code?: string | null;
    message: string;
};

/**
 * One row of the vendor invoices list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type VendorInvoiceRead = {
    id: number;
    date: string | null;
    number: string | null;
    warehouse_id: number | null;
    warehouse: string | null;
    party_id: number | null;
    party: string | null;
    currency: string | null;
    received_date: string | null;
    units: number | null;
    amount_wo_tax: string | null;
    amount: string | null;
    paid: string | null;
    cndn_value: string | null;
    shipping_cost: string | null;
    customs_duty: string | null;
    other_cost: string | null;
    credit_period: number | null;
    vo_id: number | null;
    valid: boolean;
    cbm?: string | null;
    comment?: string | null;
};

/**
 * A new vendor invoice: the header, the vendor order it fills, and its lines.
 *
 * Attributes:
 * vo_code: The vendor order it delivers, in the same warehouse and for
 * the same vendor.
 * amount: The total with tax, as the vendor's invoice states it. Units
 * and the total before tax are worked out from the lines.
 */
export type VendorInvoiceWrite = {
    number: string;
    date?: string | null;
    received_date?: string | null;
    currency: string;
    amount?: number | string | null;
    credit_period?: number | null;
    cbm?: number | string | null;
    shipping_cost?: number | string | null;
    customs_duty?: number | string | null;
    other_cost?: number | string | null;
    comment?: string | null;
    warehouse_id: number;
    vendor_id: number;
    vo_code: string;
    lines: Array<VendorInvoiceLineWrite>;
};

/**
 * What a supplier is to us, by ``ops_vendor.kind``'s CHECK.
 *
 * ``internal`` is a company of the group selling to another; NULL on a
 * vendor nobody has classified, which every v1-era row starts as.
 */
export type VendorKind = 'manufacturer' | 'distributor' | 'agent' | 'internal';

/**
 * What a supplier is to us, by ``ops_vendor.kind``'s CHECK.
 *
 * ``internal`` is a company of the group selling to another; NULL on a
 * vendor nobody has classified, which every v1-era row starts as.
 */
export const VendorKind = {
    MANUFACTURER: 'manufacturer',
    DISTRIBUTOR: 'distributor',
    AGENT: 'agent',
    INTERNAL: 'internal'
} as const;

/**
 * One vendor.
 */
export type VendorOption = {
    id: number;
    name: string;
};

/**
 * Result of ``POST /v1/vendor-orders/check``.
 *
 * Attributes:
 * ok: Whether the same body would be accepted by create.
 * problems: Every problem found, not only the first.
 * warnings: Non-blocking observations.
 * units_total: What the lines add up to.
 * amount_total: What the priced lines add up to, if any are priced.
 */
export type VendorOrderCheckResult = {
    ok: boolean;
    problems: Array<VendorOrderProblem>;
    warnings: Array<string>;
    units_total: number;
    amount_total: MoneyRead | null;
};

/**
 * One entry for a picker.
 *
 * Attributes:
 * id: ``pk_vo_id``.
 * vo_code: Our code for it.
 * vendor_id: Who it is with.
 * currency_code: ISO 4217.
 */
export type VendorOrderCodeRead = {
    id: number;
    vo_code: string;
    vendor_id: number | null;
    currency_code: string | null;
};

/**
 * Body of ``POST /v1/vendor-orders``.
 *
 * Totals are derived from the lines and are not accepted.
 *
 * Attributes:
 * warehouse_id: Where the goods will arrive.
 * vendor_id: Who we are ordering from.
 * vo_code: Our code for the order; unique per warehouse among live orders.
 * vo_date: When it was placed.
 * po_code: The purchase order it fulfils, in the same warehouse; this makes
 * the order *PO-backed*.
 * currency_code: ISO 4217; every cost is in it.
 * vo_valid_till: Last date the vendor may fulfil it.
 * comment: Free text.
 * lines: At least one.
 */
export type VendorOrderCreate = {
    warehouse_id: number;
    vendor_id: number;
    vo_code: string;
    vo_date?: string | null;
    po_code?: string | null;
    currency_code: string;
    vo_valid_till?: string | null;
    comment?: string | null;
    lines: Array<VendorOrderLineWrite>;
};

/**
 * A vendor order with its lines and what is worth a second look.
 *
 * Attributes:
 * lines: Every live line.
 * warnings: Things that do not block saving but deserve attention, such as
 * a cost that differs from the vendor's recorded cost. Part of the read,
 * so they are there every time and not only on the save that made them.
 */
export type VendorOrderDetail = {
    id: number;
    vo_code: string;
    warehouse_id: number | null;
    vendor_id: number | null;
    vo_date: string | null;
    po_id: number | null;
    po_code: string | null;
    currency_code: string | null;
    amount: MoneyRead | null;
    units: number | null;
    valid_till: string | null;
    comment: string | null;
    approved: boolean;
    line_count: number;
    created_at: string;
    updated_at: string | null;
    lines: Array<VendorOrderLineRead>;
    warnings: Array<string>;
};

/**
 * A line as stored.
 *
 * Attributes:
 * id: Line id; stable across edits.
 * sku_id: Our SKU's id.
 * sku_code: Our code for it.
 * sku_name: Its name.
 * quantity: Units ordered.
 * cost: Unit cost, with the order's currency.
 * amount: ``quantity * cost``.
 * recorded_cost: What the vendor's cost sheet says it should be, when it
 * differs from ``cost``; ``None`` when they agree or none is recorded.
 */
export type VendorOrderLineRead = {
    id: number;
    sku_id: number | null;
    sku_code: string | null;
    sku_name: string | null;
    quantity: number | null;
    cost: MoneyRead | null;
    amount: MoneyRead | null;
    recorded_cost: MoneyRead | null;
};

/**
 * One line of a vendor order.
 *
 * Attributes:
 * sku_code: Our SKU code.
 * quantity: Whole units, at least one.
 * cost: Unit cost in the order's currency, if known.
 */
export type VendorOrderLineWrite = {
    sku_code: string;
    quantity: number;
    cost?: number | string | null;
};

/**
 * Body of ``PUT /v1/vendor-orders/{id}/lines`` — the whole line set.
 *
 * Attributes:
 * lines: Replaces every existing line; at least one.
 */
export type VendorOrderLinesWrite = {
    lines: Array<VendorOrderLineWrite>;
};

/**
 * What the list filters and the create form choose from.
 *
 * Attributes:
 * warehouses: Where orders arrive.
 * vendors: Who we order from.
 */
export type VendorOrderLookupsRead = {
    warehouses: Array<NamedRead>;
    vendors: Array<NamedRead>;
};

/**
 * Body of ``POST /v1/vendor-orders/{id}/merge``: the orders folded into this one.
 *
 * Attributes:
 * vo_codes: Codes of orders in the same warehouse, for the same vendor and
 * currency. Each is deleted once its lines are folded in.
 */
export type VendorOrderMerge = {
    vo_codes: Array<string>;
};

/**
 * One thing wrong with a submitted vendor order.
 *
 * Attributes:
 * row: The 1-based line number, or ``None`` when it is about the order.
 * field: Which input it concerns, when one.
 * message: What is wrong, in a sentence a person can act on.
 */
export type VendorOrderProblem = {
    row: number | null;
    field: string | null;
    message: string;
};

/**
 * A vendor order in a list.
 *
 * Attributes:
 * id: ``pk_vo_id``.
 * vo_code: Our code for it.
 * warehouse_id: Where it arrives.
 * vendor_id: Who it is with.
 * vo_date: When it was placed.
 * po_id: The PO it fulfils, if any.
 * po_code: That PO's code.
 * currency_code: ISO 4217.
 * amount: Sum of ``quantity * cost`` over priced lines.
 * units: Sum of quantities.
 * valid_till: Last date the vendor may fulfil it.
 * comment: Free text.
 * approved: Whether a reviewer has approved it.
 * line_count: How many lines.
 * created_at: ``dt_create``.
 * updated_at: ``dt_update``, if it has ever changed.
 */
export type VendorOrderRead = {
    id: number;
    vo_code: string;
    warehouse_id: number | null;
    vendor_id: number | null;
    vo_date: string | null;
    po_id: number | null;
    po_code: string | null;
    currency_code: string | null;
    amount: MoneyRead | null;
    units: number | null;
    valid_till: string | null;
    comment: string | null;
    approved: boolean;
    line_count: number;
    created_at: string;
    updated_at: string | null;
};

/**
 * Body of ``POST /v1/vendor-orders/{id}/shift``: where the order moves to.
 *
 * Attributes:
 * warehouse_id: The warehouse the goods will now arrive at.
 * vendor_id: The vendor now fulfilling it; the same one is fine.
 */
export type VendorOrderShift = {
    warehouse_id: number;
    vendor_id: number;
};

/**
 * ``-`` means descending. The primary key is always the final tiebreaker.
 */
export type VendorOrderSort = '-created' | 'created' | '-vo_date' | 'vo_date' | 'code' | '-code' | '-amount' | 'amount';

/**
 * ``-`` means descending. The primary key is always the final tiebreaker.
 */
export const VendorOrderSort = {
    '-CREATED': '-created',
    CREATED: 'created',
    '-VO_DATE': '-vo_date',
    VO_DATE: 'vo_date',
    CODE: 'code',
    '-CODE': '-code',
    '-AMOUNT': '-amount',
    AMOUNT: 'amount'
} as const;

/**
 * Body of ``PATCH /v1/vendor-orders/{id}``.
 *
 * Only the fields sent change. ``po_code`` may be sent as ``null`` to unlink the
 * PO. The warehouse cannot change: moving an order is *shift*, which carries its
 * own guards.
 *
 * Attributes:
 * vo_code: A new code, still unique per warehouse.
 * vendor_id: A different vendor; refused once a vendor invoice is mapped.
 * vo_date: When it was placed.
 * vo_valid_till: Last date the vendor may fulfil it.
 * po_code: The PO it fulfils, or ``null`` for none.
 * currency_code: ISO 4217.
 * comment: Free text.
 */
export type VendorOrderUpdate = {
    vo_code?: string | null;
    vendor_id?: number | null;
    vo_date?: string | null;
    vo_valid_till?: string | null;
    po_code?: string | null;
    currency_code?: string | null;
    comment?: string | null;
};

/**
 * One row of the vendor list.
 *
 * Attributes:
 * tags: This organization's tags on it.
 * contact_count: Its contacts.
 * warehouse_count: Warehouses it has live terms at - of those the
 * caller can see.
 * open_order_count: Its vendor orders with stock still in transit.
 * last_order_at: When its newest order was placed; ``None`` if never.
 */
export type VendorOverview = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    kind: VendorKind | null;
    country: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    address: string | null;
    email: string | null;
    poc_name: string | null;
    poc_phone: string | null;
    website: string | null;
    default_currency: string | null;
    lead_time_days: number | null;
    moq_units: number | null;
    payment_terms_days: number | null;
    group_warehouse_id: number | null;
    tags: Array<string>;
    contact_count: number;
    warehouse_count: number;
    open_order_count: number;
    last_order_at: string | null;
};

/**
 * What the filters on this page can be chosen from.
 */
export type VendorPaymentLookupsRead = {
    warehouses: Array<NamedRead>;
    parties: Array<NamedRead>;
};

/**
 * One row of the vendor payments list.
 *
 * Money fields are decimals that serialise as strings, in ``currency``.
 */
export type VendorPaymentRead = {
    id: number;
    date: string | null;
    invoice_id: number | null;
    invoice: string | null;
    currency: string | null;
    amount: string | null;
    comment: string | null;
    valid: boolean;
};

/**
 * A supplier (``ops_vendor``).
 *
 * Attributes:
 * kind: What it is to us; ``None`` until somebody says.
 * country: As stored. v1 rows may hold a name rather than an ISO code;
 * new ones are ISO 3166-1 alpha-2.
 * city: Free text.
 * state: Region or state.
 * zip_code: Postal code.
 * address: Free text.
 * email: Where to reach it - v1's field, which may list several,
 * comma-separated; the contacts list is the structured one.
 * poc_name: Its point of contact.
 * poc_phone: Their phone.
 * website: Its site.
 * default_currency: ISO 4217, what it invoices in.
 * lead_time_days: Days from order to delivery, typically.
 * moq_units: Minimum order quantity, in units.
 * payment_terms_days: Days after invoice that payment is due.
 * group_warehouse_id: The group warehouse it supplies through.
 */
export type VendorRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    kind: VendorKind | null;
    country: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    address: string | null;
    email: string | null;
    poc_name: string | null;
    poc_phone: string | null;
    website: string | null;
    default_currency: string | null;
    lead_time_days: number | null;
    moq_units: number | null;
    payment_terms_days: number | null;
    group_warehouse_id: number | null;
};

/**
 * What Amazon's sell-out can be grouped by.
 */
export type VendorSalesGrouping = 'warehouse' | 'marketplace' | 'store' | 'country' | 'family' | 'product' | 'listing' | 'sku' | 'brand' | 'category' | 'subcategory' | 'week' | 'month';

/**
 * What Amazon's sell-out can be grouped by.
 */
export const VendorSalesGrouping = {
    WAREHOUSE: 'warehouse',
    MARKETPLACE: 'marketplace',
    STORE: 'store',
    COUNTRY: 'country',
    FAMILY: 'family',
    PRODUCT: 'product',
    LISTING: 'listing',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * Amazon's sell-out, grouped.
 */
export type VendorSalesReport = {
    items: Array<VendorSalesRow>;
    pagination: Pagination;
    totals: Array<VendorSalesTotals>;
    meta: ReportMeta;
};

/**
 * One group of products and weeks.
 *
 * ``product_ref`` is filled when grouped by product, the ASIN, as well as
 * by listing: the listing the ASIN's owner SKU is first listed under.
 */
export type VendorSalesRow = {
    month?: string | null;
    week?: string | null;
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    listed_product_id?: number | null;
    product_ref?: string | null;
    seller_sku?: string | null;
    marketplace_family?: string | null;
    country_code?: string | null;
    store_id?: number | null;
    store_name?: string | null;
    marketplace_id?: number | null;
    marketplace_code?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    currency_code?: string | null;
    ordered_units?: number | null;
    shipped_units?: number | null;
    customer_returns?: number | null;
    ordered_revenue?: MoneyRead | null;
    shipped_revenue?: MoneyRead | null;
    shipped_cogs?: MoneyRead | null;
    return_rate?: string | null;
};

/**
 * Amazon's sell-out of what it bought from us, over every matching product and week.
 *
 * Amazon's figures: never added to our sales, which already hold our sale
 * to Amazon (D24). A week counts in the date range, and the month, that
 * holds its Sunday, and a figure no row reported is ``null``.
 *
 * Attributes:
 * currency_code: The store's currency.
 * ordered_units: Units Amazon's customers ordered.
 * shipped_units: Units Amazon shipped to them.
 * customer_returns: Units they returned.
 * ordered_revenue: Amazon's retail revenue on the units ordered.
 * shipped_revenue: Its revenue on the units shipped.
 * shipped_cogs: What Amazon pays us for the units it shipped.
 * return_rate: Customer returns ÷ shipped units, a fraction.
 */
export type VendorSalesTotals = {
    currency_code?: string | null;
    ordered_units?: number | null;
    shipped_units?: number | null;
    customer_returns?: number | null;
    ordered_revenue?: MoneyRead | null;
    shipped_revenue?: MoneyRead | null;
    shipped_cogs?: MoneyRead | null;
    return_rate?: string | null;
};

/**
 * Which register a number is from, by ``ops_vendor_tax_id.kind``'s CHECK.
 */
export type VendorTaxIdKind = 'gst' | 'vat' | 'ein' | 'other';

/**
 * Which register a number is from, by ``ops_vendor_tax_id.kind``'s CHECK.
 */
export const VendorTaxIdKind = {
    GST: 'gst',
    VAT: 'vat',
    EIN: 'ein',
    OTHER: 'other'
} as const;

/**
 * A vendor's tax numbers, in saved order.
 */
export type VendorTaxIds = {
    items: Array<TaxIdRead>;
};

/**
 * Body of ``PUT /v1/vendors/{id}/tax-ids``: the whole list.
 */
export type VendorTaxIdsWrite = {
    items: Array<TaxIdWrite>;
};

/**
 * Body of ``PATCH /v1/vendors/{id}``: only what is sent changes.
 *
 * ``code`` is not a field, so sending one is a 422 naming it.
 *
 * Attributes:
 * name: A new name; it cannot be cleared.
 */
export type VendorUpdate = {
    kind?: VendorKind | null;
    country?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    address?: string | null;
    email?: string | null;
    poc_name?: string | null;
    poc_phone?: string | null;
    website?: string | null;
    default_currency?: string | null;
    lead_time_days?: number | null;
    moq_units?: number | null;
    payment_terms_days?: number | null;
    group_warehouse_id?: number | null;
    name?: string | null;
};

/**
 * A vendor's terms, one per warehouse, by warehouse name.
 */
export type VendorWarehouses = {
    items: Array<WarehouseTermsRead>;
};

/**
 * Body of ``PUT /v1/vendors/{id}/warehouses``: the warehouses and their terms, exactly.
 *
 * A warehouse left out has its terms removed.
 */
export type VendorWarehousesWrite = {
    items: Array<WarehouseTermsWrite>;
};

/**
 * An admin-approved way to answer a common question.
 */
export type VerifiedAnswerRead = {
    id: string;
    title: string;
    examples: Array<string>;
    method: {
        [key: string]: unknown;
    };
    presentation: string | null;
    active: boolean;
    page_path?: string | null;
    updated_at: string;
};

/**
 * A verified answer: a ``query_data`` request without dates, with example phrasings.
 *
 * Attributes:
 * method: The request. It must compile; its period comes from each question.
 * page_path: The app page whose own numbers it reproduces (a UI Directory
 * path such as ``/business/view``); asked from that page, it comes
 * first (plan §6.10). None for any page.
 */
export type VerifiedAnswerWrite = {
    title: string;
    examples: Array<string>;
    method: {
        [key: string]: unknown;
    };
    presentation?: string | null;
    active?: boolean;
    page_path?: string | null;
};

/**
 * Body of ``POST /v1/stores/onboarding/verify``. Never stored, never logged.
 *
 * Attributes:
 * marketplace_id: Which marketplace; its ids and country go to the probe.
 * surface: Which API the credentials are for.
 * store_type: ``seller``, ``vendor``, ``hybrid`` or ``df``.
 * account_ref: The platform's account id, where the probe needs it.
 * credentials: ``{field key: value}`` per the surface's spec.
 */
export type VerifyRequest = {
    marketplace_id: number;
    surface: AuthSurface;
    store_type?: string;
    account_ref?: string | null;
    credentials: {
        [key: string]: string;
    };
};

/**
 * A probe result, plus the stores already onboarded for that account.
 */
export type VerifyResult = {
    ok: boolean;
    surface: string;
    connector?: string | null;
    checks?: Array<ProbeCheck>;
    account?: ProbeAccount | null;
    marketplaces?: Array<ProbeMarketplace>;
    ads_profiles?: Array<AdsProfile>;
    vendor_accounts?: Array<{
        [key: string]: unknown;
    }>;
    expires_at?: string | null;
    error?: ProbeError | null;
    existing: Array<ExistingStore>;
};

/**
 * Body of ``POST /v1/warehouses``. ``kind`` defaults to ``own``.
 */
export type WarehouseCreate = {
    kind?: WarehouseKind;
    service_provider_id?: number | null;
    timezone?: string | null;
    currency?: string | null;
    country?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    address?: string | null;
    street?: string | null;
    house_number?: string | null;
    address_line2?: string | null;
    email?: string | null;
    poc_name?: string | null;
    poc_phone?: string | null;
    code: string;
    name: string;
};

/**
 * Who runs a warehouse, by ``com_warehouse.kind``'s CHECK.
 *
 * ``third_party`` is a 3PL's, and the only kind that names one
 * (``fk_sp_id``). ``marketplace_fulfilled`` is the marketplace's own
 * network (FBA and the like), which a store's stock push must not fill
 * first; ``seller_flex`` is the seller's building run on the marketplace's
 * software.
 */
export type WarehouseKind = 'own' | 'third_party' | 'marketplace_fulfilled' | 'seller_flex';

/**
 * Who runs a warehouse, by ``com_warehouse.kind``'s CHECK.
 *
 * ``third_party`` is a 3PL's, and the only kind that names one
 * (``fk_sp_id``). ``marketplace_fulfilled`` is the marketplace's own
 * network (FBA and the like), which a store's stock push must not fill
 * first; ``seller_flex`` is the seller's building run on the marketplace's
 * software.
 */
export const WarehouseKind = {
    OWN: 'own',
    THIRD_PARTY: 'third_party',
    MARKETPLACE_FULFILLED: 'marketplace_fulfilled',
    SELLER_FLEX: 'seller_flex'
} as const;

/**
 * One warehouse the store ships from. ``priority`` defaults to the list position.
 */
export type WarehouseLinkWrite = {
    id: number;
    priority?: number | null;
};

/**
 * One row of the warehouse list.
 *
 * Attributes:
 * service_provider: The 3PL, for a ``third_party`` warehouse.
 * tags: This organization's tags on it.
 * store_count: Stores linked to it, retired ones included.
 * active_store_count: Active stores linked to it.
 * primary_store_count: Active stores it is the first warehouse of: the
 * ones the stock push fills from it, and the ones that block
 * retiring it.
 */
export type WarehouseOverview = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    kind: WarehouseKind;
    service_provider_id: number | null;
    timezone: string | null;
    currency: string | null;
    country: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    address: string | null;
    street: string | null;
    house_number: string | null;
    address_line2: string | null;
    email: string | null;
    poc_name: string | null;
    poc_phone: string | null;
    service_provider: ServiceProviderRef | null;
    tags: Array<string>;
    store_count: number;
    active_store_count: number;
    primary_store_count: number;
};

/**
 * Somewhere stock is held (``com_warehouse``).
 *
 * Attributes:
 * kind: Who runs it.
 * service_provider_id: The 3PL running a ``third_party`` warehouse.
 * timezone: An IANA zone, where its working day falls.
 * currency: ISO 4217, what it is invoiced in.
 * country: As stored. v1 rows may hold a country name rather than an
 * ISO code; new ones are ISO 3166-1 alpha-2.
 * city: For humans reading a stock report.
 * state: Region or state.
 * zip_code: Postal code.
 * address: v1's free-text address, shown when the structured fields
 * are empty.
 * street: Street, without the house number.
 * house_number: The house number.
 * address_line2: Building, floor, unit.
 * email: Where to reach the warehouse.
 * poc_name: Its point of contact.
 * poc_phone: Their phone.
 */
export type WarehouseRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    kind: WarehouseKind;
    service_provider_id: number | null;
    timezone: string | null;
    currency: string | null;
    country: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    address: string | null;
    street: string | null;
    house_number: string | null;
    address_line2: string | null;
    email: string | null;
    poc_name: string | null;
    poc_phone: string | null;
};

/**
 * One warehouse return, VentaHub's Return Data columns.
 */
export type WarehouseReturnRow = {
    id: number;
    order_id: string | null;
    order_item_id: string | null;
    sku_code: string | null;
    mp_sku_code: string | null;
    box_id: string | null;
    quantity: number | null;
    item_condition: string | null;
    claim_id: string | null;
    /**
     * Not held by this system yet
     */
    claim_result: string | null;
    credit_note_num: string | null;
    price: string | null;
    currency_code: string | null;
    return_order_id: string | null;
    /**
     * ISO 8601
     */
    return_date: string | null;
    return_status: string | null;
    return_reason: string | null;
    refund_amount: string | null;
};

/**
 * A store linked to the warehouse.
 *
 * Attributes:
 * store_id: The store.
 * code: Its code.
 * name: Its name.
 * marketplace_id: Its marketplace.
 * short_code: The marketplace's handle, e.g. ``AZUK``.
 * family: The marketplace family, e.g. ``AZ``.
 * is_active: False once the store is retired.
 * priority: This warehouse's place in the store's list; 1 is filled
 * first. ``None`` on a v1 link never given one.
 * link_count: How many warehouses the store has.
 */
export type WarehouseStoreLink = {
    store_id: number;
    code: string;
    name: string;
    marketplace_id: number;
    short_code: string;
    family: string;
    is_active: boolean;
    priority: number | null;
    link_count: number;
};

/**
 * The stores linked to a warehouse, by priority and then code.
 */
export type WarehouseStores = {
    items: Array<WarehouseStoreLink>;
};

/**
 * The warehouse's stores after a PUT, and what to look at.
 *
 * Attributes:
 * warnings: Per store; the change has been saved regardless.
 */
export type WarehouseStoresUpdated = {
    items: Array<WarehouseStoreLink>;
    warnings: Array<LinkWarning>;
};

/**
 * Body of ``PUT /v1/warehouses/{id}/stores``: the stores linked, exactly.
 *
 * A store left out is unlinked. Each store's list is renumbered 1..n.
 */
export type WarehouseStoresWrite = {
    links: Array<StoreLinkWrite>;
};

/**
 * A warehouse the store ships from.
 */
export type WarehouseSummary = {
    id: number;
    name: string;
};

/**
 * A vendor's terms at one warehouse, with the warehouse.
 *
 * Attributes:
 * warehouse_id: The warehouse.
 * warehouse_code: Its code.
 * warehouse_name: Its name.
 * warehouse_is_active: False once it is retired.
 * updated_at: When these terms last changed; ``None`` if never since made.
 */
export type WarehouseTermsRead = {
    credit_period_days?: number | null;
    turnover_discount_pct?: string | null;
    promotion_support_pct?: string | null;
    damage_allowance_pct?: string | null;
    sale_or_return?: boolean | null;
    damage_returns_allowed?: boolean | null;
    damage_returns_rate_pct?: string | null;
    returns_allowed?: boolean | null;
    returns_rate_pct?: string | null;
    import_rate_pct?: string | null;
    warehouse_id: number;
    warehouse_code: string;
    warehouse_name: string;
    warehouse_is_active: boolean;
    updated_at: string | null;
};

/**
 * One warehouse's terms, as sent.
 *
 * Attributes:
 * warehouse_id: A live warehouse of this organization.
 */
export type WarehouseTermsWrite = {
    credit_period_days?: number | null;
    turnover_discount_pct?: (number | string) | null;
    promotion_support_pct?: (number | string) | null;
    damage_allowance_pct?: (number | string) | null;
    sale_or_return?: boolean | null;
    damage_returns_allowed?: boolean | null;
    damage_returns_rate_pct?: (number | string) | null;
    returns_allowed?: boolean | null;
    returns_rate_pct?: (number | string) | null;
    import_rate_pct?: (number | string) | null;
    warehouse_id: number;
};

/**
 * One warehouse's available units of a SKU.
 */
export type WarehouseUnits = {
    warehouse_id: number;
    warehouse_name: string | null;
    units: number;
};

/**
 * Body of ``PATCH /v1/warehouses/{id}``. Every field but ``code``; send ``null`` to clear one.
 *
 * ``kind`` cannot be cleared: ``null`` is refused.
 */
export type WarehouseUpdate = {
    kind?: WarehouseKind | null;
    service_provider_id?: number | null;
    timezone?: string | null;
    currency?: string | null;
    country?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    address?: string | null;
    street?: string | null;
    house_number?: string | null;
    address_line2?: string | null;
    email?: string | null;
    poc_name?: string | null;
    poc_phone?: string | null;
    name?: string | null;
};

/**
 * What warehouse stock can be grouped by.
 */
export type WhStockGrouping = 'warehouse' | 'sku' | 'brand' | 'category' | 'subcategory' | 'box' | 'box_status' | 'age_bucket' | 'receipt_basis';

/**
 * What warehouse stock can be grouped by.
 */
export const WhStockGrouping = {
    WAREHOUSE: 'warehouse',
    SKU: 'sku',
    BRAND: 'brand',
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory',
    BOX: 'box',
    BOX_STATUS: 'box_status',
    AGE_BUCKET: 'age_bucket',
    RECEIPT_BASIS: 'receipt_basis'
} as const;

/**
 * Warehouse stock, grouped.
 */
export type WhStockReport = {
    items: Array<WhStockRow>;
    pagination: Pagination;
    totals: Array<WhStockTotals>;
    meta: ReportMeta;
};

/**
 * One group of warehouse stock positions.
 *
 * Attributes:
 * box_id: Filled when grouped by box.
 * box_code: Its code.
 * box_status: Filled when grouped by box status: ``I`` in stock, ``A``
 * allotted to an order.
 * age_bucket: Filled when grouped by age bucket: ``0-30``, ``31-60``,
 * ``61-90``, ``91-180``, ``181-270``, ``270+``, ``pre_migration``
 * or ``unknown``.
 * receipt_basis: Filled when grouped by what dates the stock:
 * ``vendor_receipt``, ``moved_from_receipt`` (a receipt into the
 * position the units were moved from), ``opening_stock`` or
 * ``none``.
 */
export type WhStockRow = {
    subcategory_key?: string | null;
    subcategory_id?: string | null;
    subcategory?: string | null;
    category_key?: string | null;
    category?: string | null;
    brand_id?: number | null;
    brand_name?: string | null;
    sku_id?: number | null;
    sku_code?: string | null;
    sku_name?: string | null;
    warehouse_id?: number | null;
    warehouse_name?: string | null;
    available_units?: number | null;
    inward_units?: number | null;
    allotted_units?: number | null;
    blocked_units?: number | null;
    age_0_30_units?: number | null;
    age_31_60_units?: number | null;
    age_61_90_units?: number | null;
    age_91_180_units?: number | null;
    age_181_270_units?: number | null;
    age_over_270_units?: number | null;
    age_pre_migration_units?: number | null;
    age_unknown_units?: number | null;
    position_count?: number | null;
    box_count?: number | null;
    sku_count?: number | null;
    box_id?: number | null;
    box_code?: string | null;
    box_status?: string | null;
    age_bucket?: string | null;
    receipt_basis?: string | null;
};

/**
 * Stock in our warehouses over every matching (box, SKU) position.
 *
 * A position is aged from the latest receipt booked into it, or into a
 * position its units were moved from before they moved, in whole days to
 * ``meta.as_of`` (plan §9.3). Every position is in one age bucket, so the
 * buckets add up to the available units.
 *
 * Attributes:
 * available_units: The stock figure: inward less blocked, never below
 * 0 — VentaHub's com_stock.
 * inward_units: Units in boxes that are in stock (status ``I``).
 * allotted_units: Units in boxes allotted to an order (status ``A``),
 * which the stock figure leaves out.
 * blocked_units: Units reserved against a purchase order.
 * age_0_30_units: Available units received 0 to 30 days ago.
 * age_31_60_units: Received 31 to 60 days ago.
 * age_61_90_units: Received 61 to 90 days ago.
 * age_91_180_units: Received 91 to 180 days ago.
 * age_181_270_units: Received 181 to 270 days ago.
 * age_over_270_units: Received more than 270 days ago (VentaHub's
 * ``270+``).
 * age_pre_migration_units: Opening stock booked by the 31 July 2026
 * backfill, which has no real arrival date.
 * age_unknown_units: Stock with neither a receipt nor opening stock to
 * date it.
 * position_count: (box, SKU) positions, including those with nothing
 * available: an allotted box, or one wholly blocked.
 * box_count: Boxes.
 * sku_count: SKUs.
 */
export type WhStockTotals = {
    available_units?: number | null;
    inward_units?: number | null;
    allotted_units?: number | null;
    blocked_units?: number | null;
    age_0_30_units?: number | null;
    age_31_60_units?: number | null;
    age_61_90_units?: number | null;
    age_91_180_units?: number | null;
    age_181_270_units?: number | null;
    age_over_270_units?: number | null;
    age_pre_migration_units?: number | null;
    age_unknown_units?: number | null;
    position_count?: number | null;
    box_count?: number | null;
    sku_count?: number | null;
};

/**
 * One row of the caller's worklist: a ticket type, or the total.
 *
 * Attributes:
 * unassigned: In the pool, nobody's yet. Live.
 * assigned_to_me: The caller's, in progress. Live.
 * pending: The caller's, not started. Live.
 * resolved: The caller resolved, within the week.
 * reviewed: The caller's, reviewed within the week.
 * overdue: Of the unassigned, in progress and pending, past the type's ETA.
 */
export type WorklistCounts = {
    unassigned: number;
    assigned_to_me: number;
    pending: number;
    resolved: number;
    reviewed: number;
    overdue: number;
};

/**
 * One ticket type's counts.
 *
 * Attributes:
 * ticket_type_id: The type's code.
 * ticket_type_name: Its name.
 */
export type WorklistRow = {
    unassigned: number;
    assigned_to_me: number;
    pending: number;
    resolved: number;
    reviewed: number;
    overdue: number;
    ticket_type_id: string;
    ticket_type_name: string;
};

/**
 * What the caller finished today, in their zone.
 */
export type WorklistToday = {
    resolved: number;
    reviewed: number;
};

/**
 * Body of ``POST /v1/stores/{id}/write-freezes``.
 *
 * Attributes:
 * surfaces: The APIs to freeze; each must be one the store's platform has.
 * starts_at: When; a moment already past means now.
 * ends_at: When it ends; after ``starts_at`` and in the future.
 * reason: Why. Shown to whoever the lock refuses.
 */
export type WriteFreezeCreate = {
    surfaces: Array<AuthSurface>;
    starts_at: string;
    ends_at: string;
    reason: string;
};

/**
 * Response of ``GET /v1/stores/{id}/write-freezes``: latest start first.
 */
export type WriteFreezeList = {
    items: Array<WriteFreezeRead>;
};

/**
 * One write freeze.
 *
 * Attributes:
 * surfaces: The APIs frozen.
 * starts_at: When the lock goes on.
 * ends_at: When it comes off.
 * reason: Why.
 * created_by: Who scheduled it: a display name where known, else
 * their principal id.
 * applied_at: When the lock went on; ``null`` until it has.
 * lifted_at: When it came off; ``null`` until it has.
 * state: ``scheduled``, ``active``, ``ended`` or ``cancelled``.
 */
export type WriteFreezeRead = {
    id: number;
    surfaces: Array<AuthSurface>;
    starts_at: string;
    ends_at: string;
    reason: string;
    created_by: string;
    applied_at: string | null;
    lifted_at: string | null;
    state: FreezeState;
};

/**
 * Response of ``GET /v1/stores/{id}/writes``: newest first.
 */
export type WriteList = {
    items: Array<WriteRead>;
};

/**
 * Whether the new stores start with writes refused.
 */
export type WriteLockChoice = {
    locked?: boolean;
    reason?: string | null;
};

/**
 * A store's write lock on one API.
 */
export type WriteLockRead = {
    surface: AuthSurface;
    locked: boolean;
    reason: string | null;
    updated_by: string;
    updated_at: string;
};

/**
 * Body of ``PUT /v1/stores/{id}/write-locks/{surface}``.
 */
export type WriteLockUpdate = {
    locked: boolean;
    reason?: string | null;
};

/**
 * ``ingest.write_log.outcome``: how one write attempt went (ingest's ADR 0043).
 */
export type WriteOutcome = 'ok' | 'refused_locked' | 'failed' | 'pending';

/**
 * ``ingest.write_log.outcome``: how one write attempt went (ingest's ADR 0043).
 */
export const WriteOutcome = {
    OK: 'ok',
    REFUSED_LOCKED: 'refused_locked',
    FAILED: 'failed',
    PENDING: 'pending'
} as const;

/**
 * One write attempt to the store's platform account.
 *
 * Attributes:
 * id: The row's id.
 * connector: The connector it went through, e.g. ``bol_retailer``.
 * operation_id: The operation, e.g. ``seller.listings.patchListingsItem``.
 * actor: The person's display name where this service knows one, else
 * their principal id; ``system:<job>`` for a scheduled write.
 * targets: The identifiers it addressed, by kind.
 * summary: What it sent, redacted by ingest - never a secret or PII.
 * outcome: ``ok``, ``refused_locked``, ``failed`` or ``pending``.
 * platform_status: The platform's HTTP status, when one came back.
 * receipt_id: The platform's receipt for an asynchronous write.
 * trace_id: What joins the row to ingest's logs.
 * at: When it was attempted.
 */
export type WriteRead = {
    id: number;
    connector: string;
    operation_id: string;
    actor: string;
    targets: {
        [key: string]: Array<string>;
    };
    summary: {
        [key: string]: unknown;
    } | null;
    outcome: WriteOutcome;
    platform_status: number | null;
    receipt_id: string | null;
    trace_id: string | null;
    at: string;
};

/**
 * How the scrapers report a yes-or-no page feature.
 */
export type YesNo = 'yes' | 'no';

/**
 * How the scrapers report a yes-or-no page feature.
 */
export const YesNo = {
    YES: 'yes',
    NO: 'no'
} as const;

/**
 * Direction.
 */
export type VentawizAdsSchemasSortOrder = 'asc' | 'desc';

/**
 * Direction.
 */
export const VentawizAdsSchemasSortOrder = {
    ASC: 'asc',
    DESC: 'desc'
} as const;

/**
 * How a trend's points are bucketed.
 */
export type VentawizAdsSchemasTrendGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

/**
 * How a trend's points are bucketed.
 */
export const VentawizAdsSchemasTrendGranularity = {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    QUARTER: 'quarter',
    YEAR: 'year'
} as const;

/**
 * What a trend chart plots.
 */
export type VentawizAdsSchemasTrendMetric = 'impressions' | 'clicks' | 'spend' | 'units' | 'sales';

/**
 * What a trend chart plots.
 */
export const VentawizAdsSchemasTrendMetric = {
    IMPRESSIONS: 'impressions',
    CLICKS: 'clicks',
    SPEND: 'spend',
    UNITS: 'units',
    SALES: 'sales'
} as const;

/**
 * One bucket.
 *
 * Attributes:
 * period: The bucket's first day.
 * value: The sum, as a string so money keeps its precision.
 */
export type VentawizAdsSchemasTrendPoint = {
    period: string;
    value: string;
};

/**
 * A metric over time.
 *
 * Attributes:
 * metric: What is plotted.
 * granularity: The bucket size.
 * series: One per currency for money, one in total for a count.
 */
export type VentawizAdsSchemasTrendRead = {
    metric: VentawizAdsSchemasTrendMetric;
    granularity: VentawizAdsSchemasTrendGranularity;
    series: Array<TrendSeries>;
};

/**
 * A store that can fetch a listing from Amazon.
 *
 * Attributes:
 * id: The store id to start a job with.
 * name: For people.
 * marketplace_name: For people, e.g. ``Amazon DE``.
 * country_code: ISO 3166-1 alpha-2.
 * marketplace_id: Amazon's id for the marketplace.
 */
export type VentawizAiListingSchemasStoreRead = {
    id: number;
    name: string;
    marketplace_name: string;
    country_code: string;
    marketplace_id: string;
};

/**
 * The brand list's tab counts and the cleanup entry's.
 *
 * Attributes:
 * total: Brands, all of them.
 * managed: Those this organization manages.
 * unmanaged: The others.
 * unmatched_sku_names: SKU brand names the cleanup page lists.
 */
export type VentawizBrandsSchemasBrandSummary = {
    total: number;
    managed: number;
    unmanaged: number;
    unmatched_sku_names: number;
};

/**
 * One of the brands a store sells.
 */
export type VentawizCredentialsSchemasBrandSummary = {
    id: number;
    name: string;
};

/**
 * Response of ``GET /v1/stores/{id}/health/freshness``.
 */
export type VentawizCredentialsSchemasStoreFreshness = {
    entities: Array<EntityFreshness>;
};

/**
 * One store's row of the freshness matrix.
 */
export type VentawizDashboardSchemasStoreFreshness = {
    store_id: number;
    store_name: string | null;
    store_type: string | null;
    marketplace_code: string | null;
    country_code: string | null;
    keys: KeysStatus;
    orders: FreshnessCell;
    inventory: FreshnessCell;
    ads: FreshnessCell;
    settlements: FreshnessCell;
    listings: FreshnessCell;
};

/**
 * How a trend's points are bucketed.
 */
export type VentawizDataVaultSchemasTrendGranularity = 'day' | 'week' | 'month';

/**
 * How a trend's points are bucketed.
 */
export const VentawizDataVaultSchemasTrendGranularity = {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month'
} as const;

/**
 * What the orders trend plots.
 */
export type VentawizDataVaultSchemasTrendMetric = 'units' | 'revenue' | 'orders';

/**
 * What the orders trend plots.
 */
export const VentawizDataVaultSchemasTrendMetric = {
    UNITS: 'units',
    REVENUE: 'revenue',
    ORDERS: 'orders'
} as const;

/**
 * One bucket of the orders trend.
 */
export type VentawizDataVaultSchemasTrendPoint = {
    bucket: string;
    value: string | number;
    currency_code: string | null;
};

/**
 * The orders metric over time, one point per bucket and currency.
 */
export type VentawizDataVaultSchemasTrendRead = {
    metric: VentawizDataVaultSchemasTrendMetric;
    granularity: VentawizDataVaultSchemasTrendGranularity;
    points: Array<VentawizDataVaultSchemasTrendPoint>;
};

export type VentawizKitPaginationListResourceStoreRead1 = {
    items: Array<VentawizAiListingSchemasStoreRead>;
    pagination: Pagination;
};

export type VentawizKitPaginationListResourceStoreRead2 = {
    items: Array<VentawizRegistrySchemasStoreRead>;
    pagination: Pagination;
};

/**
 * Direction.
 */
export type VentawizKitSortingSortOrder = 'asc' | 'desc';

/**
 * Direction.
 */
export const VentawizKitSortingSortOrder = {
    ASC: 'asc',
    DESC: 'desc'
} as const;

/**
 * One brand.
 */
export type VentawizLookupsSchemasBrandOption = {
    id: number;
    name: string;
};

/**
 * One category with the subcategories seen under it.
 */
export type VentawizLookupsSchemasCategoryOption = {
    category: string;
    subcategories: Array<string>;
};

/**
 * One marketplace.
 */
export type VentawizLookupsSchemasMarketplaceOption = {
    id: number;
    short_code: string;
    name: string;
    country_code: string;
    currency_code: string;
};

/**
 * One retailer.
 */
export type VentawizLookupsSchemasRetailerOption = {
    id: number;
    name: string;
};

/**
 * One store, with the marketplace it sells on.
 *
 * Several stores can share a name — the same brand's account on two Amazon
 * regions, or two accounts on one — so a picker shows what tells them apart:
 * the store's ``code`` and its seller account id on the marketplace.
 */
export type VentawizLookupsSchemasStoreOption = {
    id: number;
    name: string;
    code?: string | null;
    external_seller_id?: string | null;
    store_type: string;
    marketplace_id: number;
    marketplace_short_code: string | null;
    marketplace_name?: string | null;
    country_code: string | null;
    currency_code: string | null;
};

/**
 * One warehouse.
 */
export type VentawizLookupsSchemasWarehouseOption = {
    id: number;
    code: string;
    name: string;
    country_code: string | null;
};

/**
 * A selling account on a marketplace (``com_store``).
 *
 * Attributes:
 * marketplace_id: Which channel. Also a scope-graph parent, so a grant
 * on the marketplace reaches this store.
 * brand_ids: The brands it sells (``rel_store_brand``), ascending; each
 * is a scope-graph parent too. No primary brand.
 * external_seller_id: The marketplace's own account identifier
 * (``store_key``) — what VentaWiz-Ingest calls the account ref.
 * store_type: v1's kind of account, e.g. ``seller`` or ``vendor``.
 * is_retail: Whether it sells to consumers.
 */
export type VentawizRegistrySchemasStoreRead = {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    marketplace_id: number;
    brand_ids: Array<number>;
    external_seller_id: string;
    store_type: string;
    is_retail: boolean;
};

/**
 * Which way a sorted column runs.
 */
export type VentawizReportsQuerySortOrder = 'asc' | 'desc';

/**
 * Which way a sorted column runs.
 */
export const VentawizReportsQuerySortOrder = {
    ASC: 'asc',
    DESC: 'desc'
} as const;

/**
 * A brand of one of the tenant's SKUs.
 *
 * Attributes:
 * id: What ``brand_ids`` takes.
 * name: Its name.
 */
export type VentawizReportsSchemasOptionsBrandOption = {
    id: number;
    name: string;
};

/**
 * A category of the tenant's SKUs.
 *
 * Attributes:
 * key: What ``category_keys`` takes.
 * name: Its name.
 */
export type VentawizReportsSchemasOptionsCategoryOption = {
    key: string;
    name: string;
};

/**
 * A marketplace one of the tenant's stores sells on.
 *
 * Attributes:
 * id: What ``marketplace_ids`` takes.
 * code: Its short code, e.g. ``AZAE``.
 * name: Its name.
 */
export type VentawizReportsSchemasOptionsMarketplaceOption = {
    id: number;
    code: string;
    name: string | null;
};

/**
 * A live retailer.
 *
 * Attributes:
 * id: What ``retailer_ids`` takes.
 * name: Its name.
 */
export type VentawizReportsSchemasOptionsRetailerOption = {
    id: number;
    name: string | null;
};

/**
 * A store.
 *
 * Attributes:
 * id: What ``store_ids`` takes.
 * name: Its name.
 * marketplace_id: The marketplace it sells on.
 * warehouse_ids: The warehouses it is linked to: a warehouse filter
 * covers it when any of them is chosen (D9).
 */
export type VentawizReportsSchemasOptionsStoreOption = {
    id: number;
    name: string | null;
    marketplace_id: number;
    warehouse_ids: Array<number>;
};

/**
 * A warehouse.
 *
 * Attributes:
 * id: What ``warehouse_ids`` takes.
 * code: Its code.
 * name: Its name.
 */
export type VentawizReportsSchemasOptionsWarehouseOption = {
    id: number;
    code: string | null;
    name: string | null;
};

export type GetAccessData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/access';
};

export type GetAccessErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetAccessResponses = {
    /**
     * Successful Response
     */
    200: AccessRead;
};

export type GetAccessResponse = GetAccessResponses[keyof GetAccessResponses];

export type GetPlatformStatusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/platform/status';
};

export type GetPlatformStatusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetPlatformStatusResponses = {
    /**
     * Successful Response
     */
    200: PlatformStatus;
};

export type GetPlatformStatusResponse = GetPlatformStatusResponses[keyof GetPlatformStatusResponses];

export type RepublishScopeGraphData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/platform/scope-graph/republish';
};

export type RepublishScopeGraphErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RepublishScopeGraphResponses = {
    /**
     * Successful Response
     */
    202: ScopeGraphStatus;
};

export type RepublishScopeGraphResponse = RepublishScopeGraphResponses[keyof RepublishScopeGraphResponses];

export type ListOrganizationsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only active organizations
         */
        active_only?: boolean;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/organizations';
};

export type ListOrganizationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListOrganizationsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceOrganizationRead;
};

export type ListOrganizationsResponse = ListOrganizationsResponses[keyof ListOrganizationsResponses];

export type GetOrganizationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        organization_id: number;
    };
    query?: never;
    url: '/v1/organizations/{organization_id}';
};

export type GetOrganizationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetOrganizationResponses = {
    /**
     * Successful Response
     */
    200: OrganizationRead;
};

export type GetOrganizationResponse = GetOrganizationResponses[keyof GetOrganizationResponses];

export type UpdateOrganizationData = {
    body: OrganizationUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        organization_id: number;
    };
    query?: never;
    url: '/v1/organizations/{organization_id}';
};

export type UpdateOrganizationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateOrganizationResponses = {
    /**
     * Successful Response
     */
    200: OrganizationRead;
};

export type UpdateOrganizationResponse = UpdateOrganizationResponses[keyof UpdateOrganizationResponses];

export type ListOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one status
         */
        status?: OrderStatus | null;
        /**
         * Restrict to one source
         */
        marketplace?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/orders';
};

export type ListOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceOrderRead;
};

export type ListOrdersResponse = ListOrdersResponses[keyof ListOrdersResponses];

export type CreateOrderData = {
    body: OrderCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/orders';
};

export type CreateOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateOrderResponses = {
    /**
     * Successful Response
     */
    201: OrderRead;
};

export type CreateOrderResponse = CreateOrderResponses[keyof CreateOrderResponses];

export type DeleteOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        order_id: string;
    };
    query?: never;
    url: '/v1/orders/{order_id}';
};

export type DeleteOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteOrderResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteOrderResponse = DeleteOrderResponses[keyof DeleteOrderResponses];

export type GetOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        order_id: string;
    };
    query?: never;
    url: '/v1/orders/{order_id}';
};

export type GetOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetOrderResponses = {
    /**
     * Successful Response
     */
    200: OrderRead;
};

export type GetOrderResponse = GetOrderResponses[keyof GetOrderResponses];

export type UpdateOrderData = {
    body: OrderUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        order_id: string;
    };
    query?: never;
    url: '/v1/orders/{order_id}';
};

export type UpdateOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateOrderResponses = {
    /**
     * Successful Response
     */
    200: OrderRead;
};

export type UpdateOrderResponse = UpdateOrderResponses[keyof UpdateOrderResponses];

export type OrderTimelineData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        order_id: string;
    };
    query?: never;
    url: '/v1/orders/{order_id}/timeline';
};

export type OrderTimelineErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type OrderTimelineResponses = {
    /**
     * Successful Response
     */
    200: Array<EventRead>;
};

export type OrderTimelineResponse = OrderTimelineResponses[keyof OrderTimelineResponses];

export type ListMarketingRulesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketing/rules';
};

export type ListMarketingRulesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketingRulesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketingRuleRead;
};

export type ListMarketingRulesResponse = ListMarketingRulesResponses[keyof ListMarketingRulesResponses];

export type GetMarketingRuleData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        rule_code: string;
    };
    query?: never;
    url: '/v1/marketing/rules/{rule_code}';
};

export type GetMarketingRuleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingRuleResponses = {
    /**
     * Successful Response
     */
    200: MarketingRuleRead;
};

export type GetMarketingRuleResponse = GetMarketingRuleResponses[keyof GetMarketingRuleResponses];

export type GetMarketingRuleStateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        rule_code: string;
    };
    query?: never;
    url: '/v1/marketing/rules/{rule_code}/state';
};

export type GetMarketingRuleStateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingRuleStateResponses = {
    /**
     * Successful Response
     */
    200: MarketingRuleStateRead;
};

export type GetMarketingRuleStateResponse = GetMarketingRuleStateResponses[keyof GetMarketingRuleStateResponses];

export type GetMarketingLiveCoefficientsData = {
    body: MarketingLiveCoefficientsRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/live-coefficients';
};

export type GetMarketingLiveCoefficientsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingLiveCoefficientsResponses = {
    /**
     * Successful Response
     */
    200: MarketingLiveCoefficients;
};

export type GetMarketingLiveCoefficientsResponse = GetMarketingLiveCoefficientsResponses[keyof GetMarketingLiveCoefficientsResponses];

export type ListMarketingRuleExecutionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only dry runs (true) or only runs that could act (false)
         */
        dry_run?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketing/rule-executions';
};

export type ListMarketingRuleExecutionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketingRuleExecutionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketingRuleExecutionSummary;
};

export type ListMarketingRuleExecutionsResponse = ListMarketingRuleExecutionsResponses[keyof ListMarketingRuleExecutionsResponses];

export type GetMarketingRuleExecutionData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        execution_id: string;
    };
    query?: {
        /**
         * Comma-separated rule codes: rows any of them matched
         */
        rules?: string | null;
        /**
         * Search the rows
         */
        q?: string | null;
        /**
         * The one row with this key
         */
        row?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketing/rule-executions/{execution_id}';
};

export type GetMarketingRuleExecutionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingRuleExecutionResponses = {
    /**
     * Successful Response
     */
    200: MarketingRuleExecutionRead;
};

export type GetMarketingRuleExecutionResponse = GetMarketingRuleExecutionResponses[keyof GetMarketingRuleExecutionResponses];

export type DownloadMarketingRuleExecutionData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        execution_id: string;
    };
    query?: never;
    url: '/v1/marketing/rule-executions/{execution_id}/download';
};

export type DownloadMarketingRuleExecutionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadMarketingRuleExecutionResponses = {
    /**
     * VentaHub's file
     */
    200: MarketingRuleExecutionFile;
};

export type DownloadMarketingRuleExecutionResponse = DownloadMarketingRuleExecutionResponses[keyof DownloadMarketingRuleExecutionResponses];

export type DownloadMarketingRuleExecutionExcelData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        execution_id: string;
    };
    query?: never;
    url: '/v1/marketing/rule-executions/{execution_id}/excel';
};

export type DownloadMarketingRuleExecutionExcelErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadMarketingRuleExecutionExcelResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DownloadMarketingRuleAuditData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        rule_code: string;
    };
    query: {
        /**
         * The store to audit
         */
        store_id: number;
    };
    url: '/v1/marketing/rules/{rule_code}/audit-excel';
};

export type DownloadMarketingRuleAuditErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadMarketingRuleAuditResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type TestMarketingRuleData = {
    body: MarketingRuleTestRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        rule_code: string;
    };
    query?: never;
    url: '/v1/marketing/rules/{rule_code}/test';
};

export type TestMarketingRuleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type TestMarketingRuleResponses = {
    /**
     * Successful Response
     */
    200: MarketingRuleTestResult;
};

export type TestMarketingRuleResponse = TestMarketingRuleResponses[keyof TestMarketingRuleResponses];

export type ExecuteMarketingRulesData = {
    body: MarketingRuleExecuteRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/execute';
};

export type ExecuteMarketingRulesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExecuteMarketingRulesResponses = {
    /**
     * Successful Response
     */
    200: MarketingRuleExecuteResult;
};

export type ExecuteMarketingRulesResponse = ExecuteMarketingRulesResponses[keyof ExecuteMarketingRulesResponses];

export type StreamMarketingRulesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Raise no tickets
         */
        dry_run?: boolean;
        /**
         * Comma-separated rule codes; omit to run every active rule
         */
        rule_codes?: string | null;
        /**
         * Comma-separated store ids; a store-scoped run is always a dry run
         */
        store_ids?: string | null;
    };
    url: '/v1/marketing/execute/stream';
};

export type StreamMarketingRulesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StreamMarketingRulesResponses = {
    /**
     * Progress events
     */
    200: unknown;
};

export type ListMarketingRuleExclusionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only profile or only ad-group exclusions
         */
        scope?: MarketingRuleScope | null;
        /**
         * Comma-separated Amazon ad group ids
         */
        ad_group_ids?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketing/rule-exclusions';
};

export type ListMarketingRuleExclusionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketingRuleExclusionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketingRuleExclusionRead;
};

export type ListMarketingRuleExclusionsResponse = ListMarketingRuleExclusionsResponses[keyof ListMarketingRuleExclusionsResponses];

export type CreateMarketingRuleExclusionData = {
    body: MarketingRuleExclusionCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/rule-exclusions';
};

export type CreateMarketingRuleExclusionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateMarketingRuleExclusionResponses = {
    /**
     * Successful Response
     */
    201: MarketingRuleExclusionRead;
};

export type CreateMarketingRuleExclusionResponse = CreateMarketingRuleExclusionResponses[keyof CreateMarketingRuleExclusionResponses];

export type DeleteMarketingRuleExclusionData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        exclusion_id: string;
    };
    query?: never;
    url: '/v1/marketing/rule-exclusions/{exclusion_id}';
};

export type DeleteMarketingRuleExclusionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteMarketingRuleExclusionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteMarketingRuleExclusionResponse = DeleteMarketingRuleExclusionResponses[keyof DeleteMarketingRuleExclusionResponses];

export type ListMarketingRuleAutomationsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only one mode
         */
        mode?: MarketingRuleAutomationMode | null;
        /**
         * Comma-separated Amazon ad group ids
         */
        ad_group_ids?: string | null;
        /**
         * Comma-separated profile ids
         */
        profile_ids?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketing/rule-automations';
};

export type ListMarketingRuleAutomationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketingRuleAutomationsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketingRuleAutomationRead;
};

export type ListMarketingRuleAutomationsResponse = ListMarketingRuleAutomationsResponses[keyof ListMarketingRuleAutomationsResponses];

export type SetMarketingRuleAutomationData = {
    body: MarketingRuleAutomationCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/rule-automations';
};

export type SetMarketingRuleAutomationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SetMarketingRuleAutomationResponses = {
    /**
     * Successful Response
     */
    201: MarketingRuleAutomationRead;
};

export type SetMarketingRuleAutomationResponse = SetMarketingRuleAutomationResponses[keyof SetMarketingRuleAutomationResponses];

export type ListMarketingAutomationDecisionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Comma-separated LIVE, SHADOW, BAND; all when absent
         */
        modes?: string | null;
        rule_code?: string | null;
        store_id?: number | null;
        /**
         * Search the rows
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketing/automation/decisions';
};

export type ListMarketingAutomationDecisionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketingAutomationDecisionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketingAutomationDecisionRead;
};

export type ListMarketingAutomationDecisionsResponse = ListMarketingAutomationDecisionsResponses[keyof ListMarketingAutomationDecisionsResponses];

export type GetMarketingAutomationShadowData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * How far back
         */
        since_days?: number;
    };
    url: '/v1/marketing/automation/shadow';
};

export type GetMarketingAutomationShadowErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingAutomationShadowResponses = {
    /**
     * Successful Response
     */
    200: MarketingShadowReportRead;
};

export type GetMarketingAutomationShadowResponse = GetMarketingAutomationShadowResponses[keyof GetMarketingAutomationShadowResponses];

export type DeleteMarketingRuleAutomationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        automation_id: string;
    };
    query?: never;
    url: '/v1/marketing/rule-automations/{automation_id}';
};

export type DeleteMarketingRuleAutomationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteMarketingRuleAutomationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteMarketingRuleAutomationResponse = DeleteMarketingRuleAutomationResponses[keyof DeleteMarketingRuleAutomationResponses];

export type GetMarketingEngineConfigData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/engine-config';
};

export type GetMarketingEngineConfigErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingEngineConfigResponses = {
    /**
     * Successful Response
     */
    200: MarketingEngineConfigRead;
};

export type GetMarketingEngineConfigResponse = GetMarketingEngineConfigResponses[keyof GetMarketingEngineConfigResponses];

export type UpdateMarketingEngineConfigData = {
    body: MarketingEngineConfigUpdate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/engine-config';
};

export type UpdateMarketingEngineConfigErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateMarketingEngineConfigResponses = {
    /**
     * Successful Response
     */
    200: MarketingEngineConfigRead;
};

export type UpdateMarketingEngineConfigResponse = UpdateMarketingEngineConfigResponses[keyof UpdateMarketingEngineConfigResponses];

export type GetMarketingRulesHealthData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/rules-health';
};

export type GetMarketingRulesHealthErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketingRulesHealthResponses = {
    /**
     * Successful Response
     */
    200: MarketingRulesHealthRead;
};

export type GetMarketingRulesHealthResponse = GetMarketingRulesHealthResponses[keyof GetMarketingRulesHealthResponses];

export type ExplainMarketingRulesHealthData = {
    body: MarketingStatsExplainRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/stats/explain';
};

export type ExplainMarketingRulesHealthErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExplainMarketingRulesHealthResponses = {
    /**
     * Successful Response
     */
    200: MarketingStatsExplanation;
};

export type ExplainMarketingRulesHealthResponse = ExplainMarketingRulesHealthResponses[keyof ExplainMarketingRulesHealthResponses];

export type ListAdsStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/stores';
};

export type ListAdsStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListAdsStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAdsStoreRead;
};

export type ListAdsStoresResponse = ListAdsStoresResponses[keyof ListAdsStoresResponses];

export type ListAdsBrandsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/brands';
};

export type ListAdsBrandsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListAdsBrandsResponses = {
    /**
     * Successful Response
     */
    200: Array<FocusBrand>;
};

export type ListAdsBrandsResponse = ListAdsBrandsResponses[keyof ListAdsBrandsResponses];

export type GetAdsPerformanceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: PerformanceLevel;
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        /**
         * Limit to these stores; none means all
         */
        store_ids?: Array<number>;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
        /**
         * Also include ad groups with no brand
         */
        include_unbranded?: boolean;
        /**
         * Comparison period start
         */
        compare_start?: string | null;
        /**
         * Comparison period end
         */
        compare_end?: string | null;
        ad_group_type?: string | null;
        /**
         * Limit to these ad groups (master ids)
         */
        ad_group_ids?: Array<number>;
        /**
         * Ad-group states, for level=ad_group
         */
        statuses?: Array<string>;
        /**
         * Include ad groups with no data, for level=ad_group
         */
        include_idle?: boolean;
        search?: string | null;
        sort?: PerformanceSort;
        order?: VentawizAdsSchemasSortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/performance';
};

export type GetAdsPerformanceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetAdsPerformanceResponses = {
    /**
     * Successful Response
     */
    200: PerformancePage;
};

export type GetAdsPerformanceResponse = GetAdsPerformanceResponses[keyof GetAdsPerformanceResponses];

export type ListAdsNegativesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these stores; none means all
         */
        store_ids?: Array<number>;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
        /**
         * Also include ad groups with no brand
         */
        include_unbranded?: boolean;
        /**
         * Limit to these ad groups
         */
        ad_group_ids?: Array<number>;
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/negatives';
};

export type ListAdsNegativesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListAdsNegativesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNegativeRead;
};

export type ListAdsNegativesResponse = ListAdsNegativesResponses[keyof ListAdsNegativesResponses];

export type GetAdsTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        metric: VentawizAdsSchemasTrendMetric;
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        granularity?: VentawizAdsSchemasTrendGranularity;
        /**
         * Limit to these stores; none means all
         */
        store_ids?: Array<number>;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
        /**
         * Also include ad groups with no brand
         */
        include_unbranded?: boolean;
        /**
         * Limit to these ad groups
         */
        ad_group_ids?: Array<number>;
        campaign_id?: string | null;
    };
    url: '/v1/ams/trend';
};

export type GetAdsTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetAdsTrendResponses = {
    /**
     * Successful Response
     */
    200: VentawizAdsSchemasTrendRead;
};

export type GetAdsTrendResponse = GetAdsTrendResponses[keyof GetAdsTrendResponses];

export type GetSearchAnalyticsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        store_id?: number | null;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
        /**
         * Also include ad groups with no brand
         */
        include_unbranded?: boolean;
        show?: SearchAnalyticsFilter;
        order_by?: SearchAnalyticsOrder;
        order_source?: SearchAnalyticsSource;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/search-analytics';
};

export type GetSearchAnalyticsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetSearchAnalyticsResponses = {
    /**
     * Successful Response
     */
    200: SearchAnalyticsPage;
};

export type GetSearchAnalyticsResponse = GetSearchAnalyticsResponses[keyof GetSearchAnalyticsResponses];

export type GetSearchQueryKeywordsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        search_query: string;
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        store_id?: number | null;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
        /**
         * Also include ad groups with no brand
         */
        include_unbranded?: boolean;
    };
    url: '/v1/ams/search-analytics/keywords';
};

export type GetSearchQueryKeywordsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetSearchQueryKeywordsResponses = {
    /**
     * Successful Response
     */
    200: SearchQueryKeywords;
};

export type GetSearchQueryKeywordsResponse = GetSearchQueryKeywordsResponses[keyof GetSearchQueryKeywordsResponses];

export type GetFocusSearchTermSheetData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        store_id: number;
        week_start?: string | null;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
        search?: string | null;
        sort?: string;
        order?: VentawizAdsSchemasSortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/focus-search-terms';
};

export type GetFocusSearchTermSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusSearchTermSheetResponses = {
    /**
     * Successful Response
     */
    200: FocusSheetPage;
};

export type GetFocusSearchTermSheetResponse = GetFocusSearchTermSheetResponses[keyof GetFocusSearchTermSheetResponses];

export type ListFocusSearchTermHistoryTermsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        store_id: number;
        /**
         * Limit to ad groups of these brands
         */
        brand_ids?: Array<number>;
    };
    url: '/v1/ams/focus-search-terms/terms';
};

export type ListFocusSearchTermHistoryTermsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListFocusSearchTermHistoryTermsResponses = {
    /**
     * Successful Response
     */
    200: FocusHistoryTerms;
};

export type ListFocusSearchTermHistoryTermsResponse = ListFocusSearchTermHistoryTermsResponses[keyof ListFocusSearchTermHistoryTermsResponses];

export type GetFocusSearchTermHistoryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        store_id: number;
        search_term: string;
    };
    url: '/v1/ams/focus-search-terms/history';
};

export type GetFocusSearchTermHistoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusSearchTermHistoryResponses = {
    /**
     * Successful Response
     */
    200: FocusTermHistory;
};

export type GetFocusSearchTermHistoryResponse = GetFocusSearchTermHistoryResponses[keyof GetFocusSearchTermHistoryResponses];

export type ListAdsChangesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        grain?: ChangeGrain | null;
        sort?: ChangeSort;
        order?: VentawizAdsSchemasSortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        /**
         * Limit to these stores; none means all
         */
        store_ids?: Array<number>;
        /**
         * Raw actions
         */
        action_types?: Array<string>;
        /**
         * Outcomes
         */
        statuses?: Array<string>;
        /**
         * Rule codes
         */
        rule_codes?: Array<string>;
        search?: string | null;
        /**
         * Canonical sources: rules_engine, manual, tickets, ...
         */
        sources?: Array<string>;
        /**
         * Changes these users made
         */
        actor_ids?: Array<number>;
        /**
         * Keep changes no person made; with actor_ids, beside theirs
         */
        include_system?: boolean;
        /**
         * Amazon ad group ids
         */
        ad_group_ids?: Array<string>;
        /**
         * Amazon campaign ids
         */
        campaign_ids?: Array<string>;
    };
    url: '/v1/ams/changes';
};

export type ListAdsChangesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListAdsChangesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceChangeEvent;
};

export type ListAdsChangesResponse = ListAdsChangesResponses[keyof ListAdsChangesResponses];

export type GetAdsChangeSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        /**
         * Limit to these stores; none means all
         */
        store_ids?: Array<number>;
        /**
         * Raw actions
         */
        action_types?: Array<string>;
        /**
         * Outcomes
         */
        statuses?: Array<string>;
        /**
         * Rule codes
         */
        rule_codes?: Array<string>;
        search?: string | null;
        /**
         * Canonical sources: rules_engine, manual, tickets, ...
         */
        sources?: Array<string>;
        /**
         * Changes these users made
         */
        actor_ids?: Array<number>;
        /**
         * Keep changes no person made; with actor_ids, beside theirs
         */
        include_system?: boolean;
        /**
         * Amazon ad group ids
         */
        ad_group_ids?: Array<string>;
        /**
         * Amazon campaign ids
         */
        campaign_ids?: Array<string>;
    };
    url: '/v1/ams/changes/summary';
};

export type GetAdsChangeSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetAdsChangeSummaryResponses = {
    /**
     * Successful Response
     */
    200: ChangeSummary;
};

export type GetAdsChangeSummaryResponse = GetAdsChangeSummaryResponses[keyof GetAdsChangeSummaryResponses];

export type GetAdsChangePulseData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        grain?: ChangeGrain | null;
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        /**
         * Limit to these stores; none means all
         */
        store_ids?: Array<number>;
        /**
         * Raw actions
         */
        action_types?: Array<string>;
        /**
         * Outcomes
         */
        statuses?: Array<string>;
        /**
         * Rule codes
         */
        rule_codes?: Array<string>;
        search?: string | null;
        /**
         * Canonical sources: rules_engine, manual, tickets, ...
         */
        sources?: Array<string>;
        /**
         * Changes these users made
         */
        actor_ids?: Array<number>;
        /**
         * Keep changes no person made; with actor_ids, beside theirs
         */
        include_system?: boolean;
        /**
         * Amazon ad group ids
         */
        ad_group_ids?: Array<string>;
        /**
         * Amazon campaign ids
         */
        campaign_ids?: Array<string>;
    };
    url: '/v1/ams/changes/pulse';
};

export type GetAdsChangePulseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetAdsChangePulseResponses = {
    /**
     * Successful Response
     */
    200: ChangePulse;
};

export type GetAdsChangePulseResponse = GetAdsChangePulseResponses[keyof GetAdsChangePulseResponses];

export type GetTargetTimelineData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        target_id: string;
    };
    query: {
        store_id: number;
    };
    url: '/v1/ams/changes/targets/{target_id}';
};

export type GetTargetTimelineErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetTargetTimelineResponses = {
    /**
     * Successful Response
     */
    200: EntityTimeline;
};

export type GetTargetTimelineResponse = GetTargetTimelineResponses[keyof GetTargetTimelineResponses];

export type ListTargetTimelinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        target_id: string;
    };
    query: {
        store_id: number;
        ad_group_id?: string | null;
        ad_type?: string | null;
    };
    url: '/v1/ams/changes/targets/{target_id}/timelines';
};

export type ListTargetTimelinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTargetTimelinesResponses = {
    /**
     * Successful Response
     */
    200: TargetTimelines;
};

export type ListTargetTimelinesResponse = ListTargetTimelinesResponses[keyof ListTargetTimelinesResponses];

export type GetSearchTermTimelineData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        store_id: number;
        ad_group_id: string;
        search_term: string;
        keyword_id?: string | null;
        match_type?: string | null;
        keyword?: string | null;
    };
    url: '/v1/ams/changes/search-terms';
};

export type GetSearchTermTimelineErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetSearchTermTimelineResponses = {
    /**
     * Successful Response
     */
    200: EntityTimeline;
};

export type GetSearchTermTimelineResponse = GetSearchTermTimelineResponses[keyof GetSearchTermTimelineResponses];

export type GetAdGroupTimelineData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ad_group_id: string;
    };
    query: {
        store_id: number;
    };
    url: '/v1/ams/changes/ad-groups/{ad_group_id}';
};

export type GetAdGroupTimelineErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetAdGroupTimelineResponses = {
    /**
     * Successful Response
     */
    200: EntityTimeline;
};

export type GetAdGroupTimelineResponse = GetAdGroupTimelineResponses[keyof GetAdGroupTimelineResponses];

export type GetCampaignTimelineData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        campaign_id: string;
    };
    query: {
        store_id: number;
    };
    url: '/v1/ams/changes/campaigns/{campaign_id}';
};

export type GetCampaignTimelineErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetCampaignTimelineResponses = {
    /**
     * Successful Response
     */
    200: EntityTimeline;
};

export type GetCampaignTimelineResponse = GetCampaignTimelineResponses[keyof GetCampaignTimelineResponses];

export type GetReportSyncData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        date_field?: ReportDateField;
        store_id?: number | null;
        /**
         * Report types
         */
        report_types?: Array<string>;
        status?: ReportStatusGroup | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/report-sync';
};

export type GetReportSyncErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetReportSyncResponses = {
    /**
     * Successful Response
     */
    200: ReportSyncPage;
};

export type GetReportSyncResponse = GetReportSyncResponses[keyof GetReportSyncResponses];

export type ListReportTypesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/report-sync/types';
};

export type ListReportTypesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReportTypesResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type ListReportTypesResponse = ListReportTypesResponses[keyof ListReportTypesResponses];

export type ListAdGroupMasterData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_id?: number | null;
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ams/ad-groups';
};

export type ListAdGroupMasterErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListAdGroupMasterResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAdGroupMasterRead;
};

export type ListAdGroupMasterResponse = ListAdGroupMasterResponses[keyof ListAdGroupMasterResponses];

export type DownloadAdGroupMasterTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/ad-groups/template';
};

export type DownloadAdGroupMasterTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadAdGroupMasterTemplateResponses = {
    /**
     * The template workbook
     */
    200: unknown;
};

export type ListUploadStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/uploads/stores';
};

export type ListUploadStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListUploadStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<AdUploadStoreRead>;
};

export type ListUploadStoresResponse = ListUploadStoresResponses[keyof ListUploadStoresResponses];

export type DownloadUploadTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store the sheet is for
         */
        store_id: number;
        /**
         * The upload's key
         */
        upload: string;
    };
    url: '/v1/ams/uploads/template';
};

export type DownloadUploadTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadUploadTemplateResponses = {
    /**
     * The template workbook
     */
    200: unknown;
};

export type CheckUploadData = {
    body: BodyCheckUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/uploads/check';
};

export type CheckUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckUploadResponses = {
    /**
     * Successful Response
     */
    200: AdUploadCheck;
};

export type CheckUploadResponse = CheckUploadResponses[keyof CheckUploadResponses];

export type ListUploadsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_id?: number | null;
    };
    url: '/v1/ams/uploads';
};

export type ListUploadsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListUploadsResponses = {
    /**
     * Successful Response
     */
    200: Array<AdUploadRead>;
};

export type ListUploadsResponse = ListUploadsResponses[keyof ListUploadsResponses];

export type RaiseUploadData = {
    body: BodyRaiseUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/uploads';
};

export type RaiseUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RaiseUploadResponses = {
    /**
     * Successful Response
     */
    201: AdUploadResult;
};

export type RaiseUploadResponse = RaiseUploadResponses[keyof RaiseUploadResponses];

export type CheckNewTargetsData = {
    body: TargetCheckRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams/target-check';
};

export type CheckNewTargetsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckNewTargetsResponses = {
    /**
     * Successful Response
     */
    200: TargetCheckResult;
};

export type CheckNewTargetsResponse = CheckNewTargetsResponses[keyof CheckNewTargetsResponses];

export type AmsViewListSellersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams-view/sellers';
};

export type AmsViewListSellersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListSellersResponses = {
    /**
     * Successful Response
     */
    200: Array<AmsViewSeller>;
};

export type AmsViewListSellersResponse = AmsViewListSellersResponses[keyof AmsViewListSellersResponses];

export type AmsViewListBrandsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams-view/brands';
};

export type AmsViewListBrandsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListBrandsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewBrands;
};

export type AmsViewListBrandsResponse = AmsViewListBrandsResponses[keyof AmsViewListBrandsResponses];

export type AmsViewGetAdGroupCountsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams-view/ad-group-counts';
};

export type AmsViewGetAdGroupCountsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetAdGroupCountsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewAdGroupCountsResponse;
};

export type AmsViewGetAdGroupCountsResponse = AmsViewGetAdGroupCountsResponses[keyof AmsViewGetAdGroupCountsResponses];

export type AmsViewListAdGroupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * First picked day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last picked day, inclusive (YYYY-MM-DD)
         */
        end_date: string;
        /**
         * Stores to read, in the order they are answered; none = nothing
         */
        store_ids?: Array<number>;
        /**
         * Brands to keep; 0 = unbranded ad groups; none = the default
         */
        brand_ids?: Array<number>;
        /**
         * Ad group / campaign name, target, negative, ASIN, SKU or search term
         */
        search?: string | null;
    };
    url: '/v1/ams-view/ad-groups-multi';
};

export type AmsViewListAdGroupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListAdGroupsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewAdGroupMultiResponse;
};

export type AmsViewListAdGroupsResponse = AmsViewListAdGroupsResponses[keyof AmsViewListAdGroupsResponses];

export type AmsViewGetTopAsinsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group or campaign belongs to
         */
        store_id: number;
        /**
         * First picked day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last picked day, inclusive (YYYY-MM-DD)
         */
        end_date: string;
        /**
         * Most ASINs to return
         */
        limit?: number;
    };
    url: '/v1/ams-view/ad-groups/{ad_group_id}/top-asins';
};

export type AmsViewGetTopAsinsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetTopAsinsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewTopAsinResponse;
};

export type AmsViewGetTopAsinsResponse = AmsViewGetTopAsinsResponses[keyof AmsViewGetTopAsinsResponses];

export type AmsViewGetCampaignTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's campaign id
         */
        campaign_id: string;
    };
    query: {
        /**
         * The store the ad group or campaign belongs to
         */
        store_id: number;
        /**
         * The measure to plot
         */
        metric: AmsTrendMetric;
        /**
         * Bucket size
         */
        granularity: AmsTrendGranularity;
        /**
         * First picked day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last picked day, inclusive (YYYY-MM-DD)
         */
        end_date: string;
    };
    url: '/v1/ams-view/campaigns/{campaign_id}/trend';
};

export type AmsViewGetCampaignTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetCampaignTrendResponses = {
    /**
     * Successful Response
     */
    200: Array<AmsTrendPoint>;
};

export type AmsViewGetCampaignTrendResponse = AmsViewGetCampaignTrendResponses[keyof AmsViewGetCampaignTrendResponses];

export type AmsViewGetAdGroupTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group or campaign belongs to
         */
        store_id: number;
        /**
         * The measure to plot
         */
        metric: AmsTrendMetric;
        /**
         * Bucket size
         */
        granularity: AmsTrendGranularity;
        /**
         * First picked day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last picked day, inclusive (YYYY-MM-DD)
         */
        end_date: string;
    };
    url: '/v1/ams-view/ad-groups/{ad_group_id}/trend';
};

export type AmsViewGetAdGroupTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetAdGroupTrendResponses = {
    /**
     * Successful Response
     */
    200: Array<AmsTrendPoint>;
};

export type AmsViewGetAdGroupTrendResponse = AmsViewGetAdGroupTrendResponses[keyof AmsViewGetAdGroupTrendResponses];

export type AmsViewGetOverallTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The measure to plot
         */
        metric: AmsTrendMetric;
        /**
         * Bucket size
         */
        granularity: AmsTrendGranularity;
        /**
         * First picked day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last picked day, inclusive (YYYY-MM-DD)
         */
        end_date: string;
        /**
         * Stores to read, in the order they are answered; none = nothing
         */
        store_ids?: Array<number>;
        /**
         * Brands to keep; 0 = unbranded ad groups; none = the default
         */
        brand_ids?: Array<number>;
        /**
         * Ad group / campaign name, target, negative, ASIN, SKU or search term
         */
        search?: string | null;
        /**
         * Campaign names, exact; any character but NUL
         */
        campaigns?: Array<string>;
        /**
         * Ad group states, exact
         */
        statuses?: Array<string>;
        /**
         * Ad group types, exact (an unknown one matches nothing)
         */
        ad_group_types?: Array<string>;
        /**
         * Targeting types, exact
         */
        targeting_types?: Array<string>;
    };
    url: '/v1/ams-view/trends/overall';
};

export type AmsViewGetOverallTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetOverallTrendResponses = {
    /**
     * Successful Response
     */
    200: Array<AmsViewOverallTrendPoint>;
};

export type AmsViewGetOverallTrendResponse = AmsViewGetOverallTrendResponses[keyof AmsViewGetOverallTrendResponses];

export type AmsViewExportWorkbookData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Inclusive; the end at most tomorrow (UTC)
         */
        start_date: string;
        /**
         * Inclusive; the end at most tomorrow (UTC)
         */
        end_date: string;
        /**
         * The stores to export; at least one
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands; 0 = no brand
         */
        brand_ids?: Array<number>;
        /**
         * Ad group, campaign, target, negative, ASIN or term
         */
        search?: string | null;
        /**
         * Exact values; none means no filter
         */
        campaigns?: Array<string>;
        /**
         * Exact values; none means no filter
         */
        statuses?: Array<string>;
        /**
         * Exact values; none means no filter
         */
        ad_group_types?: Array<string>;
        /**
         * Exact values; none means no filter
         */
        targeting_types?: Array<string>;
        /**
         * One row per entity on ASINs, Targets, Search Terms, Negatives
         */
        aggregate_sheets?: boolean;
    };
    url: '/v1/ams-view/export';
};

export type AmsViewExportWorkbookErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewExportWorkbookResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type AmsViewListAdProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group belongs to
         */
        store_id: number;
        /**
         * First day, inclusive
         */
        start_date: string;
        /**
         * Last day, inclusive
         */
        end_date: string;
        /**
         * Limit to ad groups of these brands; 0 means unbranded
         */
        brand_ids?: Array<number>;
    };
    url: '/v1/ams-view/ad-products/{ad_group_id}';
};

export type AmsViewListAdProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListAdProductsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewAdProductListResponse;
};

export type AmsViewListAdProductsResponse = AmsViewListAdProductsResponses[keyof AmsViewListAdProductsResponses];

export type AmsViewListTargetsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group belongs to
         */
        store_id: number;
        /**
         * First day, inclusive
         */
        start_date: string;
        /**
         * Last day, inclusive
         */
        end_date: string;
        /**
         * Limit to ad groups of these brands; 0 means unbranded
         */
        brand_ids?: Array<number>;
    };
    url: '/v1/ams-view/targets/{ad_group_id}';
};

export type AmsViewListTargetsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListTargetsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewTargetListResponse;
};

export type AmsViewListTargetsResponse = AmsViewListTargetsResponses[keyof AmsViewListTargetsResponses];

export type AmsViewListSearchTermsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group belongs to
         */
        store_id: number;
        /**
         * First day, inclusive
         */
        start_date: string;
        /**
         * Last day, inclusive
         */
        end_date: string;
        /**
         * Limit to ad groups of these brands; 0 means unbranded
         */
        brand_ids?: Array<number>;
        /**
         * Only terms matched through this keyword
         */
        target?: string | null;
    };
    url: '/v1/ams-view/search-terms/{ad_group_id}';
};

export type AmsViewListSearchTermsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListSearchTermsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewSearchTermListResponse;
};

export type AmsViewListSearchTermsResponse = AmsViewListSearchTermsResponses[keyof AmsViewListSearchTermsResponses];

export type AmsViewListNegativesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group belongs to
         */
        store_id: number;
        /**
         * Limit to ad groups of these brands; 0 means unbranded
         */
        brand_ids?: Array<number>;
    };
    url: '/v1/ams-view/negatives/{ad_group_id}';
};

export type AmsViewListNegativesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListNegativesResponses = {
    /**
     * Successful Response
     */
    200: AmsViewNegativeListResponse;
};

export type AmsViewListNegativesResponse = AmsViewListNegativesResponses[keyof AmsViewListNegativesResponses];

export type AmsViewListAltProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group belongs to
         */
        store_id: number;
        /**
         * First day, inclusive
         */
        start_date: string;
        /**
         * Last day, inclusive
         */
        end_date: string;
        /**
         * Limit to ad groups of these brands; 0 means unbranded
         */
        brand_ids?: Array<number>;
    };
    url: '/v1/ams-view/alt-products/{ad_group_id}';
};

export type AmsViewListAltProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewListAltProductsResponses = {
    /**
     * Successful Response
     */
    200: AmsViewAltProductListResponse;
};

export type AmsViewListAltProductsResponse = AmsViewListAltProductsResponses[keyof AmsViewListAltProductsResponses];

export type AmsViewGetEntityTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
    };
    query: {
        /**
         * The store the ad group belongs to
         */
        store_id: number;
        /**
         * Which tab the row belongs to
         */
        level: AmsViewEntityLevel;
        /**
         * The row's id: ad_id (asin), target_id (target), the term (search_term)
         */
        entity_id: string;
        /**
         * The measure plotted
         */
        metric: AmsTrendMetric;
        /**
         * Bucket size
         */
        granularity: AmsTrendGranularity;
        /**
         * First day, inclusive
         */
        start_date: string;
        /**
         * Last day, inclusive
         */
        end_date: string;
        /**
         * search_term only: the keyword the term matched
         */
        keyword?: string | null;
        /**
         * Limit to ad groups of these brands; 0 means unbranded
         */
        brand_ids?: Array<number>;
    };
    url: '/v1/ams-view/ad-groups/{ad_group_id}/entity-trend';
};

export type AmsViewGetEntityTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetEntityTrendResponses = {
    /**
     * Successful Response
     */
    200: Array<AmsTrendPoint>;
};

export type AmsViewGetEntityTrendResponse = AmsViewGetEntityTrendResponses[keyof AmsViewGetEntityTrendResponses];

export type AmsViewGetAggregateTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        level: AmsViewAggregateTrendLevel;
    };
    query: {
        /**
         * The row's ASIN, target text or search term, by level
         */
        entity_id: string;
        /**
         * The row's store
         */
        store_id: number;
        metric: AmsTrendMetric;
        granularity: AmsTrendGranularity;
        /**
         * First day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last day, inclusive; at most tomorrow (UTC)
         */
        end_date: string;
        /**
         * Targets only; omitted matches untyped targets
         */
        target_type?: string | null;
        /**
         * Limit to ad groups of these brands; 0 = ad groups with no brand
         */
        brand_ids?: Array<number>;
        /**
         * SP, SB or SD
         */
        ad_group_type?: AmsAdGroupType | null;
        /**
         * The page's search box
         */
        search?: string | null;
        /**
         * Comma-separated pk_ag_id to narrow to; empty means no filter
         */
        ad_group_ids?: string | null;
    };
    url: '/v1/ams-view/aggregate/{level}/trend';
};

export type AmsViewGetAggregateTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetAggregateTrendResponses = {
    /**
     * Successful Response
     */
    200: Array<AmsTrendPoint>;
};

export type AmsViewGetAggregateTrendResponse = AmsViewGetAggregateTrendResponses[keyof AmsViewGetAggregateTrendResponses];

export type AmsViewGetAggregateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        level: AmsViewAggregateLevel;
    };
    query: {
        /**
         * First day, inclusive (YYYY-MM-DD)
         */
        start_date: string;
        /**
         * Last day, inclusive; at most tomorrow (UTC)
         */
        end_date: string;
        /**
         * The page's stores; none gives an empty result
         */
        store_ids?: Array<number>;
        /**
         * Limit to ad groups of these brands; 0 = ad groups with no brand
         */
        brand_ids?: Array<number>;
        /**
         * SP, SB or SD
         */
        ad_group_type?: AmsAdGroupType | null;
        /**
         * The page's search box
         */
        search?: string | null;
        /**
         * search-terms only: the terms one target's keyword surfaced
         */
        keyword?: string | null;
        /**
         * Comma-separated pk_ag_id to narrow to; empty means no rows
         */
        ad_group_ids?: string | null;
        /**
         * A column of the level; omitted, the level's default
         */
        sort_by?: string | null;
        sort_dir?: VentawizKitSortingSortOrder;
    };
    url: '/v1/ams-view/aggregate/{level}';
};

export type AmsViewGetAggregateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetAggregateResponses = {
    /**
     * Successful Response
     */
    200: AmsViewAggregateResponse;
};

export type AmsViewGetAggregateResponse = AmsViewGetAggregateResponses[keyof AmsViewGetAggregateResponses];

export type ReviewAmsListRowsData = {
    body: ReviewAmsRowsRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data';
};

export type ReviewAmsListRowsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsListRowsResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsRowsPage;
};

export type ReviewAmsListRowsResponse = ReviewAmsListRowsResponses[keyof ReviewAmsListRowsResponses];

export type ReviewAmsGetSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (Review AMS Data is store-scoped)
         */
        store_id: number;
        /**
         * Previous period, first day
         */
        previous_start_date: string;
        /**
         * Previous period, last day
         */
        previous_end_date: string;
        /**
         * Current period, first day
         */
        current_start_date: string;
        /**
         * Current period, last day
         */
        current_end_date: string;
        /**
         * Brand pick; 0 = no brand
         */
        brand_ids?: Array<number> | null;
        ad_group_type?: AmsAdGroupType | null;
    };
    url: '/v1/review-ams-data/summary';
};

export type ReviewAmsGetSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsGetSummaryResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsSummary;
};

export type ReviewAmsGetSummaryResponse = ReviewAmsGetSummaryResponses[keyof ReviewAmsGetSummaryResponses];

export type ReviewAmsExportRowsCsvData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: ReviewLevel;
        /**
         * The store (Review AMS Data is store-scoped)
         */
        store_id: number;
        /**
         * Previous period, first day
         */
        previous_start_date: string;
        /**
         * Previous period, last day
         */
        previous_end_date: string;
        /**
         * Current period, first day
         */
        current_start_date: string;
        /**
         * Current period, last day
         */
        current_end_date: string;
        /**
         * Brand pick; 0 = no brand
         */
        brand_ids?: Array<number> | null;
        ad_group_type?: AmsAdGroupType | null;
        /**
         * Cross-drill master ids (pk_ag_id)
         */
        ad_group_ids?: Array<number> | null;
        /**
         * Level-aware search text
         */
        search?: string | null;
        sort_by?: ReviewSortKey;
        sort_dir?: VentawizKitSortingSortOrder;
        /**
         * One row per (ad group, entity) instead of the tab's rows
         */
        by_ad_group?: boolean;
    };
    url: '/v1/review-ams-data/export.csv';
};

export type ReviewAmsExportRowsCsvErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsExportRowsCsvResponses = {
    /**
     * The tab as CSV
     */
    200: unknown;
};

export type ReviewAmsListNegativesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (Review AMS Data is store-scoped)
         */
        store_id: number;
        /**
         * Brand pick; 0 = no brand
         */
        brand_ids?: Array<number> | null;
        ad_group_type?: AmsAdGroupType | null;
        /**
         * Cross-drill master ids (pk_ag_id)
         */
        ad_group_ids?: Array<number> | null;
        /**
         * Filter on keyword text
         */
        search?: string | null;
        page?: number;
        page_size?: number;
        sort_by?: NegativeSortKey;
        sort_dir?: VentawizKitSortingSortOrder;
    };
    url: '/v1/review-ams-data/negatives';
};

export type ReviewAmsListNegativesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsListNegativesResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsNegativesPage;
};

export type ReviewAmsListNegativesResponse = ReviewAmsListNegativesResponses[keyof ReviewAmsListNegativesResponses];

export type ReviewAmsExportNegativesCsvData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (Review AMS Data is store-scoped)
         */
        store_id: number;
        /**
         * Brand pick; 0 = no brand
         */
        brand_ids?: Array<number> | null;
        ad_group_type?: AmsAdGroupType | null;
        /**
         * Cross-drill master ids (pk_ag_id)
         */
        ad_group_ids?: Array<number> | null;
        /**
         * Filter on keyword text
         */
        search?: string | null;
        sort_by?: NegativeSortKey;
        sort_dir?: VentawizKitSortingSortOrder;
        /**
         * One row per (ad group, entity) instead of the tab's rows
         */
        by_ad_group?: boolean;
    };
    url: '/v1/review-ams-data/negatives/export.csv';
};

export type ReviewAmsExportNegativesCsvErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsExportNegativesCsvResponses = {
    /**
     * Negatives as CSV
     */
    200: unknown;
};

export type ReviewAmsResolveTargetInstancesData = {
    body: ReviewAmsTargetInstancesRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/target-instances';
};

export type ReviewAmsResolveTargetInstancesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsResolveTargetInstancesResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsTargetInstancesResponse;
};

export type ReviewAmsResolveTargetInstancesResponse = ReviewAmsResolveTargetInstancesResponses[keyof ReviewAmsResolveTargetInstancesResponses];

export type ReviewAmsResolveSearchTermOriginsData = {
    body: ReviewAmsSearchTermOriginsRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/search-term-origins';
};

export type ReviewAmsResolveSearchTermOriginsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsResolveSearchTermOriginsResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsSearchTermOriginsResponse;
};

export type ReviewAmsResolveSearchTermOriginsResponse = ReviewAmsResolveSearchTermOriginsResponses[keyof ReviewAmsResolveSearchTermOriginsResponses];

export type ReviewAmsResolveAdProductInstancesData = {
    body: ReviewAmsAdProductInstancesRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/ad-product-instances';
};

export type ReviewAmsResolveAdProductInstancesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsResolveAdProductInstancesResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsAdProductInstancesResponse;
};

export type ReviewAmsResolveAdProductInstancesResponse = ReviewAmsResolveAdProductInstancesResponses[keyof ReviewAmsResolveAdProductInstancesResponses];

export type ReviewAmsResolveBrandAdGroupsData = {
    body: ReviewAmsBrandAdGroupsRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/brand-ad-groups';
};

export type ReviewAmsResolveBrandAdGroupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReviewAmsResolveBrandAdGroupsResponses = {
    /**
     * Successful Response
     */
    200: ReviewAmsBrandAdGroupsResponse;
};

export type ReviewAmsResolveBrandAdGroupsResponse = ReviewAmsResolveBrandAdGroupsResponses[keyof ReviewAmsResolveBrandAdGroupsResponses];

export type AmsViewGetAmazonWriteLockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ams-view/amazon-write-lock';
};

export type AmsViewGetAmazonWriteLockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AmsViewGetAmazonWriteLockResponses = {
    /**
     * Successful Response
     */
    200: AmsViewAmazonWriteLock;
};

export type AmsViewGetAmazonWriteLockResponse = AmsViewGetAmazonWriteLockResponses[keyof AmsViewGetAmazonWriteLockResponses];

export type AmsViewAddTargetData = {
    body: AmsViewAddTargetRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon ad group id
         */
        ad_group_id: string;
    };
    query?: never;
    url: '/v1/ams-view/target/{ad_group_id}/add';
};

export type AmsViewAddTargetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type AmsViewAddTargetResponses = {
    /**
     * Successful Response
     */
    202: unknown;
};

export type AmsViewAddNegativeData = {
    body: AmsViewAddNegativeRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon ad group id
         */
        ad_group_id: string;
    };
    query?: never;
    url: '/v1/ams-view/negatives/{ad_group_id}/add';
};

export type AmsViewAddNegativeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type AmsViewAddNegativeResponses = {
    /**
     * Successful Response
     */
    202: unknown;
};

export type AmsViewUpdateNegativeData = {
    body: AmsViewUpdateNegativeRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon ad group id
         */
        ad_group_id: string;
    };
    query?: never;
    url: '/v1/ams-view/negatives/{ad_group_id}/update';
};

export type AmsViewUpdateNegativeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type AmsViewUpdateNegativeResponses = {
    /**
     * Successful Response
     */
    202: unknown;
};

export type AmsViewAddAdProductData = {
    body: AmsViewAddAdProductRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon ad group id
         */
        ad_group_id: string;
    };
    query?: never;
    url: '/v1/ams-view/ad-products/{ad_group_id}/add';
};

export type AmsViewAddAdProductErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type AmsViewAddAdProductResponses = {
    /**
     * Successful Response
     */
    202: unknown;
};

export type AmsViewUpdateAdProductData = {
    body: AmsViewUpdateAdProductRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon ad group id
         */
        ad_group_id: string;
    };
    query?: never;
    url: '/v1/ams-view/ad-products/{ad_group_id}/update';
};

export type AmsViewUpdateAdProductErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type AmsViewUpdateAdProductResponses = {
    /**
     * Successful Response
     */
    202: unknown;
};

export type ReviewAmsChangeBidData = {
    body: ReviewAmsChangeBidRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/actions/change-bid';
};

export type ReviewAmsChangeBidErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type ReviewAmsChangeBidResponses = {
    /**
     * Successful Response
     */
    200: AmsActionResponse;
};

export type ReviewAmsChangeBidResponse = ReviewAmsChangeBidResponses[keyof ReviewAmsChangeBidResponses];

export type ReviewAmsPauseTargetData = {
    body: ReviewAmsPauseTargetRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/actions/pause-target';
};

export type ReviewAmsPauseTargetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type ReviewAmsPauseTargetResponses = {
    /**
     * Successful Response
     */
    200: AmsActionResponse;
};

export type ReviewAmsPauseTargetResponse = ReviewAmsPauseTargetResponses[keyof ReviewAmsPauseTargetResponses];

export type ReviewAmsAddNegativeData = {
    body: ReviewAmsAddNegativeRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/actions/add-negative';
};

export type ReviewAmsAddNegativeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type ReviewAmsAddNegativeResponses = {
    /**
     * Successful Response
     */
    200: AmsActionResponse;
};

export type ReviewAmsAddNegativeResponse = ReviewAmsAddNegativeResponses[keyof ReviewAmsAddNegativeResponses];

export type ReviewAmsAddTargetData = {
    body: ReviewAmsAddTargetRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review-ams-data/actions/add-target';
};

export type ReviewAmsAddTargetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Writes to Amazon Ads are disabled (error.type amazon_writes_disabled)
     */
    423: unknown;
    /**
     * Writes are enabled but no Amazon Ads client exists
     */
    502: unknown;
};

export type ReviewAmsAddTargetResponses = {
    /**
     * Successful Response
     */
    200: AmsActionResponse;
};

export type ReviewAmsAddTargetResponse = ReviewAmsAddTargetResponses[keyof ReviewAmsAddTargetResponses];

export type BidSimulatorWhatIfData = {
    body: BidSimulatorWhatIfRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store the target belongs to
         */
        store_id: number;
    };
    url: '/v1/bid-simulator/what-if';
};

export type BidSimulatorWhatIfErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BidSimulatorWhatIfResponses = {
    /**
     * Successful Response
     */
    200: BidSimulatorSimulation;
};

export type BidSimulatorWhatIfResponse = BidSimulatorWhatIfResponses[keyof BidSimulatorWhatIfResponses];

export type BidSimulatorCurveData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
        /**
         * Amazon's target id
         */
        target_id: string;
    };
    query: {
        /**
         * The store the target belongs to
         */
        store_id: number;
        /**
         * Forecast days
         */
        horizon?: 7 | 14;
    };
    url: '/v1/bid-simulator/targets/{ad_group_id}/{target_id}/curve';
};

export type BidSimulatorCurveErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BidSimulatorCurveResponses = {
    /**
     * Successful Response
     */
    200: BidSimulatorSimulation;
};

export type BidSimulatorCurveResponse = BidSimulatorCurveResponses[keyof BidSimulatorCurveResponses];

export type BidSimulatorRecommendationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Amazon's ad group id
         */
        ad_group_id: string;
        /**
         * Amazon's target id
         */
        target_id: string;
    };
    query: {
        /**
         * The store the target belongs to
         */
        store_id: number;
        /**
         * Forecast days
         */
        horizon?: 7 | 14;
    };
    url: '/v1/bid-simulator/targets/{ad_group_id}/{target_id}/recommendation';
};

export type BidSimulatorRecommendationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BidSimulatorRecommendationResponses = {
    /**
     * Successful Response
     */
    200: BidSimulatorRecommendation;
};

export type BidSimulatorRecommendationResponse = BidSimulatorRecommendationResponses[keyof BidSimulatorRecommendationResponses];

export type BidSimulatorAccuracyData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store the target belongs to
         */
        store_id: number;
        /**
         * Forecast days
         */
        horizon?: 7 | 14;
    };
    url: '/v1/bid-simulator/accuracy';
};

export type BidSimulatorAccuracyErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BidSimulatorAccuracyResponses = {
    /**
     * Successful Response
     */
    200: BidSimulatorAccuracy;
};

export type BidSimulatorAccuracyResponse = BidSimulatorAccuracyResponses[keyof BidSimulatorAccuracyResponses];

export type DownloadOptimizationTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/optimization/template';
};

export type DownloadOptimizationTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadOptimizationTemplateResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type RunOptimizationUploadData = {
    body: BodyRunOptimizationUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketing/optimization/run';
};

export type RunOptimizationUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RunOptimizationUploadResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type GetCostStatusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/costs/status';
};

export type GetCostStatusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetCostStatusResponses = {
    /**
     * Successful Response
     */
    200: CostStatus;
};

export type GetCostStatusResponse = GetCostStatusResponses[keyof GetCostStatusResponses];

export type GetCostOptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/costs/options';
};

export type GetCostOptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetCostOptionsResponses = {
    /**
     * Successful Response
     */
    200: CostOptions;
};

export type GetCostOptionsResponse = GetCostOptionsResponses[keyof GetCostOptionsResponses];

export type ListCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
    };
    query?: {
        warehouse_id?: number | null;
        /**
         * The vendor or retailer
         */
        party_id?: number | null;
        brand_ids?: Array<number> | null;
        currency?: string | null;
        /**
         * SKU code contains
         */
        sku?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/costs/{kind}';
};

export type ListCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListCostsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSkuCostRow;
};

export type ListCostsResponse = ListCostsResponses[keyof ListCostsResponses];

export type ExportCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
    };
    query?: {
        warehouse_id?: number | null;
        /**
         * The vendor or retailer
         */
        party_id?: number | null;
        brand_ids?: Array<number> | null;
        currency?: string | null;
        /**
         * SKU code contains
         */
        sku?: string | null;
    };
    url: '/v1/costs/{kind}/export';
};

export type ExportCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportCostsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type DownloadCostTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
    };
    query?: never;
    url: '/v1/costs/{kind}/template';
};

export type DownloadCostTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadCostTemplateResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PreviewCostUploadData = {
    body: BodyPreviewCostUpload;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
    };
    query?: never;
    url: '/v1/costs/{kind}/preview';
};

export type PreviewCostUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewCostUploadResponses = {
    /**
     * Successful Response
     */
    200: CostPreview;
};

export type PreviewCostUploadResponse = PreviewCostUploadResponses[keyof PreviewCostUploadResponses];

export type CommitCostUploadData = {
    body: BodyCommitCostUpload;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
    };
    query?: never;
    url: '/v1/costs/{kind}/commit';
};

export type CommitCostUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CommitCostUploadResponses = {
    /**
     * Successful Response
     */
    200: CostCommitResult;
};

export type CommitCostUploadResponse = CommitCostUploadResponses[keyof CommitCostUploadResponses];

export type DeleteCostData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
        cost_id: number;
    };
    query?: never;
    url: '/v1/costs/{kind}/{cost_id}';
};

export type DeleteCostErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteCostResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteCostResponse = DeleteCostResponses[keyof DeleteCostResponses];

export type EditCostData = {
    body: CostEdit;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * vendor or retailer
         */
        kind: 'vendor' | 'retailer';
        cost_id: number;
    };
    query?: never;
    url: '/v1/costs/{kind}/{cost_id}';
};

export type EditCostErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type EditCostResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type EditCostResponse = EditCostResponses[keyof EditCostResponses];

export type GetFeeStatusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/fees/status';
};

export type GetFeeStatusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFeeStatusResponses = {
    /**
     * Successful Response
     */
    200: FeeStatus;
};

export type GetFeeStatusResponse = GetFeeStatusResponses[keyof GetFeeStatusResponses];

export type ListFeeStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/fees/stores';
};

export type ListFeeStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListFeeStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<FeeStore>;
};

export type ListFeeStoresResponse = ListFeeStoresResponses[keyof ListFeeStoresResponses];

export type ListFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose fees these are
         */
        store_id: number;
        /**
         * Product key, ASIN, seller SKU or SKU code contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/fees';
};

export type ListFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListFeesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketplaceFeeRow;
};

export type ListFeesResponse = ListFeesResponses[keyof ListFeesResponses];

export type ExportFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose fees these are
         */
        store_id: number;
        /**
         * Product key, ASIN, seller SKU or SKU code contains
         */
        q?: string | null;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/fees/export';
};

export type ExportFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportFeesResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type DownloadFeeTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose fees these are
         */
        store_id: number;
    };
    url: '/v1/fees/template';
};

export type DownloadFeeTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadFeeTemplateResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PreviewFeeUploadData = {
    body: BodyPreviewFeeUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/fees/preview';
};

export type PreviewFeeUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewFeeUploadResponses = {
    /**
     * Successful Response
     */
    200: FeePreview;
};

export type PreviewFeeUploadResponse = PreviewFeeUploadResponses[keyof PreviewFeeUploadResponses];

export type CommitFeeUploadData = {
    body: BodyCommitFeeUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/fees/commit';
};

export type CommitFeeUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CommitFeeUploadResponses = {
    /**
     * Successful Response
     */
    200: FeeCommitResult;
};

export type CommitFeeUploadResponse = CommitFeeUploadResponses[keyof CommitFeeUploadResponses];

export type ListSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        warehouse_id?: number | null;
        store_id?: number | null;
        vendor_id?: number | null;
        brand_ids?: Array<number> | null;
        category?: string | null;
        currency?: string | null;
        /**
         * SKU code, name, EAN, UPC or model number contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/skus';
};

export type ListSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSkusResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSkuRow;
};

export type ListSkusResponse = ListSkusResponses[keyof ListSkusResponses];

export type ListSkuCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        warehouse_id?: number | null;
        store_id?: number | null;
        vendor_id?: number | null;
        brand_ids?: Array<number> | null;
        category?: string | null;
        currency?: string | null;
        /**
         * SKU code, name, EAN, UPC or model number contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/skus/costs';
};

export type ListSkuCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSkuCostsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceManagedSkuCostRow;
};

export type ListSkuCostsResponse = ListSkuCostsResponses[keyof ListSkuCostsResponses];

export type ExportSheetData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * skus, dimensions or costs
         */
        sheet: 'skus' | 'dimensions' | 'costs';
    };
    query?: {
        warehouse_id?: number | null;
        store_id?: number | null;
        vendor_id?: number | null;
        brand_ids?: Array<number> | null;
        category?: string | null;
        currency?: string | null;
        /**
         * SKU code, name, EAN, UPC or model number contains
         */
        q?: string | null;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/skus/sheets/{sheet}/export';
};

export type ExportSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportSheetResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ListOrderBookData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_ids?: Array<number> | null;
        /**
         * The first UTC day, inclusive
         */
        date_from?: string | null;
        /**
         * The last UTC day, inclusive
         */
        date_to?: string | null;
        status?: ('pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other') | null;
        fulfilment?: string | null;
        /**
         * Order ID or SKU contains
         */
        q?: string | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Only the stores linked to these warehouses.
         */
        warehouse_ids?: Array<number> | null;
        /**
         * Only orders with a line of these brands' SKUs.
         */
        brand_ids?: Array<number> | null;
        /**
         * ISO alpha-2: the address's country, else the store's marketplace's.
         */
        ship_country?: string | null;
        /**
         * Which day the range reads: the purchase, or the last update.
         */
        dated_by?: string;
        /**
         * Text the order id contains.
         */
        order_ref?: string | null;
        /**
         * Text a line's ASIN, seller SKU or SKU code contains.
         */
        product?: string | null;
        /**
         * Only these order types, as the marketplace names them.
         */
        order_types?: Array<string> | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/order-book';
};

export type ListOrderBookErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListOrderBookResponses = {
    /**
     * Successful Response
     */
    200: ListResourceOrderLineRow;
};

export type ListOrderBookResponse = ListOrderBookResponses[keyof ListOrderBookResponses];

export type ListOrderBookOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_ids?: Array<number> | null;
        /**
         * The first UTC day, inclusive
         */
        date_from?: string | null;
        /**
         * The last UTC day, inclusive
         */
        date_to?: string | null;
        status?: ('pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other') | null;
        fulfilment?: string | null;
        /**
         * Order ID or SKU contains
         */
        q?: string | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Only the stores linked to these warehouses.
         */
        warehouse_ids?: Array<number> | null;
        /**
         * Only orders with a line of these brands' SKUs.
         */
        brand_ids?: Array<number> | null;
        /**
         * ISO alpha-2: the address's country, else the store's marketplace's.
         */
        ship_country?: string | null;
        /**
         * Which day the range reads: the purchase, or the last update.
         */
        dated_by?: string;
        /**
         * Text the order id contains.
         */
        order_ref?: string | null;
        /**
         * Text a line's ASIN, seller SKU or SKU code contains.
         */
        product?: string | null;
        /**
         * Only these order types, as the marketplace names them.
         */
        order_types?: Array<string> | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/order-book/orders';
};

export type ListOrderBookOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListOrderBookOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceOrderBookRow;
};

export type ListOrderBookOrdersResponse = ListOrderBookOrdersResponses[keyof ListOrderBookOrdersResponses];

export type CountOrderBookStatusesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_ids?: Array<number> | null;
        /**
         * The first UTC day, inclusive
         */
        date_from?: string | null;
        /**
         * The last UTC day, inclusive
         */
        date_to?: string | null;
        status?: ('pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other') | null;
        fulfilment?: string | null;
        /**
         * Order ID or SKU contains
         */
        q?: string | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Only the stores linked to these warehouses.
         */
        warehouse_ids?: Array<number> | null;
        /**
         * Only orders with a line of these brands' SKUs.
         */
        brand_ids?: Array<number> | null;
        /**
         * ISO alpha-2: the address's country, else the store's marketplace's.
         */
        ship_country?: string | null;
        /**
         * Which day the range reads: the purchase, or the last update.
         */
        dated_by?: string;
        /**
         * Text the order id contains.
         */
        order_ref?: string | null;
        /**
         * Text a line's ASIN, seller SKU or SKU code contains.
         */
        product?: string | null;
        /**
         * Only these order types, as the marketplace names them.
         */
        order_types?: Array<string> | null;
    };
    url: '/v1/order-book/statuses';
};

export type CountOrderBookStatusesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CountOrderBookStatusesResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: number;
    };
};

export type CountOrderBookStatusesResponse = CountOrderBookStatusesResponses[keyof CountOrderBookStatusesResponses];

export type GetOrderBookAnalyticsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_ids?: Array<number> | null;
        /**
         * The first UTC day, inclusive
         */
        date_from?: string | null;
        /**
         * The last UTC day, inclusive
         */
        date_to?: string | null;
        status?: ('pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other') | null;
        fulfilment?: string | null;
        /**
         * Order ID or SKU contains
         */
        q?: string | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Only the stores linked to these warehouses.
         */
        warehouse_ids?: Array<number> | null;
        /**
         * Only orders with a line of these brands' SKUs.
         */
        brand_ids?: Array<number> | null;
        /**
         * ISO alpha-2: the address's country, else the store's marketplace's.
         */
        ship_country?: string | null;
        /**
         * Which day the range reads: the purchase, or the last update.
         */
        dated_by?: string;
        /**
         * Text the order id contains.
         */
        order_ref?: string | null;
        /**
         * Text a line's ASIN, seller SKU or SKU code contains.
         */
        product?: string | null;
        /**
         * Only these order types, as the marketplace names them.
         */
        order_types?: Array<string> | null;
    };
    url: '/v1/order-book/analytics';
};

export type GetOrderBookAnalyticsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetOrderBookAnalyticsResponses = {
    /**
     * Successful Response
     */
    200: OrderAnalytics;
};

export type GetOrderBookAnalyticsResponse = GetOrderBookAnalyticsResponses[keyof GetOrderBookAnalyticsResponses];

export type ExportOrderBookData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        store_ids?: Array<number> | null;
        /**
         * The first UTC day, inclusive
         */
        date_from?: string | null;
        /**
         * The last UTC day, inclusive
         */
        date_to?: string | null;
        status?: ('pending' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'other') | null;
        fulfilment?: string | null;
        /**
         * Order ID or SKU contains
         */
        q?: string | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Only the stores linked to these warehouses.
         */
        warehouse_ids?: Array<number> | null;
        /**
         * Only orders with a line of these brands' SKUs.
         */
        brand_ids?: Array<number> | null;
        /**
         * ISO alpha-2: the address's country, else the store's marketplace's.
         */
        ship_country?: string | null;
        /**
         * Which day the range reads: the purchase, or the last update.
         */
        dated_by?: string;
        /**
         * Text the order id contains.
         */
        order_ref?: string | null;
        /**
         * Text a line's ASIN, seller SKU or SKU code contains.
         */
        product?: string | null;
        /**
         * Only these order types, as the marketplace names them.
         */
        order_types?: Array<string> | null;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/order-book/export';
};

export type ExportOrderBookErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportOrderBookResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ListOrderUploadStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/order-book/upload/stores';
};

export type ListOrderUploadStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListOrderUploadStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<OrderStore>;
};

export type ListOrderUploadStoresResponse = ListOrderUploadStoresResponses[keyof ListOrderUploadStoresResponses];

export type GetOrderBookOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        order_id: number;
    };
    query?: never;
    url: '/v1/order-book/{order_id}';
};

export type GetOrderBookOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetOrderBookOrderResponses = {
    /**
     * Successful Response
     */
    200: OrderDetail;
};

export type GetOrderBookOrderResponse = GetOrderBookOrderResponses[keyof GetOrderBookOrderResponses];

export type ListProductStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/product-book/stores';
};

export type ListProductStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListProductStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<ProductStore>;
};

export type ListProductStoresResponse = ListProductStoresResponses[keyof ListProductStoresResponses];

export type ListProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose listings these are
         */
        store_id: number;
        /**
         * Product key, SKU or title contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/product-book';
};

export type ListProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListProductsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceListingRow;
};

export type ListProductsResponse = ListProductsResponses[keyof ListProductsResponses];

export type ExportProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose listings these are
         */
        store_id: number;
        /**
         * Product key, SKU or title contains
         */
        q?: string | null;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/product-book/export';
};

export type ExportProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportProductsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ListReturnStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/return-book/stores';
};

export type ListReturnStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReturnStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<ReturnStore>;
};

export type ListReturnStoresResponse = ListReturnStoresResponses[keyof ListReturnStoresResponses];

export type ListReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose returns these are
         */
        store_id: number;
        /**
         * Order ID, SKU, box, claim or credit note contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/return-book';
};

export type ListReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReturnsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceWarehouseReturnRow;
};

export type ListReturnsResponse = ListReturnsResponses[keyof ListReturnsResponses];

export type ExportReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose returns these are
         */
        store_id: number;
        /**
         * Order ID, SKU, box, claim or credit note contains
         */
        q?: string | null;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/return-book/export';
};

export type ExportReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportReturnsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ListSettlementStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/settlement-book/stores';
};

export type ListSettlementStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSettlementStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<SettlementStore>;
};

export type ListSettlementStoresResponse = ListSettlementStoresResponses[keyof ListSettlementStoresResponses];

export type ListSettlementLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose settlements these are
         */
        store_id: number;
        /**
         * Deposited on or after
         */
        date_from?: string | null;
        /**
         * Deposited on or before
         */
        date_to?: string | null;
        /**
         * Settlement, order, SKU, product, shipment or type contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * A column of the response rows to order by
         */
        sort?: string | null;
        /**
         * Direction
         */
        order?: VentawizKitSortingSortOrder;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/settlement-book';
};

export type ListSettlementLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSettlementLinesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSettlementLineRow;
};

export type ListSettlementLinesResponse = ListSettlementLinesResponses[keyof ListSettlementLinesResponses];

export type ExportSettlementLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store whose settlements these are
         */
        store_id: number;
        /**
         * Deposited on or after
         */
        date_from?: string | null;
        /**
         * Deposited on or before
         */
        date_to?: string | null;
        /**
         * Settlement, order, SKU, product, shipment or type contains
         */
        q?: string | null;
        /**
         * column:op:value, repeated; all must hold. Text: contains, eq, ne, starts, empty, notempty. Number and date (ISO): eq, ne, gt, gte, lt, lte, empty, notempty. Yes/no: eq, ne with true or false.
         */
        filter?: Array<string> | null;
    };
    url: '/v1/settlement-book/export';
};

export type ExportSettlementLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportSettlementLinesResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ListPerformanceMasterStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/performance-master/stores';
};

export type ListPerformanceMasterStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListPerformanceMasterStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<PerformanceMasterStore>;
};

export type ListPerformanceMasterStoresResponse = ListPerformanceMasterStoresResponses[keyof ListPerformanceMasterStoresResponses];

export type DownloadPerformanceMasterTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        store_id: number;
    };
    url: '/v1/performance-master/template';
};

export type DownloadPerformanceMasterTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadPerformanceMasterTemplateResponses = {
    /**
     * The template workbook
     */
    200: unknown;
};

export type PreviewPerformanceMasterData = {
    body: BodyPreviewPerformanceMaster;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/performance-master/preview';
};

export type PreviewPerformanceMasterErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewPerformanceMasterResponses = {
    /**
     * Successful Response
     */
    200: PerformanceMasterPreview;
};

export type PreviewPerformanceMasterResponse = PreviewPerformanceMasterResponses[keyof PreviewPerformanceMasterResponses];

export type ListDaypartingEntitiesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * An Amazon store with an Ads profile
         */
        store_id: number;
        scope: DaypartingScope;
        ad_type: DaypartingAdType;
        /**
         * Name or Amazon id
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/dayparting/entities';
};

export type ListDaypartingEntitiesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListDaypartingEntitiesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceDaypartingEntity;
};

export type ListDaypartingEntitiesResponse = ListDaypartingEntitiesResponses[keyof ListDaypartingEntitiesResponses];

export type GetDaypartingWeekdayProfileData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * An Amazon store with an Ads profile
         */
        store_id: number;
        scope: DaypartingScope;
        ad_type: DaypartingAdType;
        /**
         * First day, inclusive
         */
        start: string;
        /**
         * Last day, inclusive
         */
        end: string;
        /**
         * Amazon ids at the scope; repeat for several
         */
        entity_ids?: Array<string>;
        metric?: DaypartingMetric;
    };
    url: '/v1/dayparting/weekday-profile';
};

export type GetDaypartingWeekdayProfileErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetDaypartingWeekdayProfileResponses = {
    /**
     * Successful Response
     */
    200: DaypartingWeekdayProfile;
};

export type GetDaypartingWeekdayProfileResponse = GetDaypartingWeekdayProfileResponses[keyof GetDaypartingWeekdayProfileResponses];

export type ListDaypartingSchedulesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this store's
         */
        store_id?: number | null;
        /**
         * Only enabled, or only disabled
         */
        enabled?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/dayparting/schedules';
};

export type ListDaypartingSchedulesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListDaypartingSchedulesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceDaypartingScheduleRead;
};

export type ListDaypartingSchedulesResponse = ListDaypartingSchedulesResponses[keyof ListDaypartingSchedulesResponses];

export type CreateDaypartingScheduleData = {
    body: DaypartingScheduleCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/dayparting/schedules';
};

export type CreateDaypartingScheduleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Store or entity not found
     */
    404: unknown;
    /**
     * Overlap
     */
    409: unknown;
    /**
     * Nothing the lever can move
     */
    422: unknown;
};

export type CreateDaypartingScheduleResponses = {
    /**
     * Successful Response
     */
    201: DaypartingScheduleRead;
};

export type CreateDaypartingScheduleResponse = CreateDaypartingScheduleResponses[keyof CreateDaypartingScheduleResponses];

export type DeleteDaypartingScheduleData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        schedule_id: string;
    };
    query?: never;
    url: '/v1/dayparting/schedules/{schedule_id}';
};

export type DeleteDaypartingScheduleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Not its creator
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteDaypartingScheduleResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteDaypartingScheduleResponse = DeleteDaypartingScheduleResponses[keyof DeleteDaypartingScheduleResponses];

export type GetDaypartingScheduleData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        schedule_id: string;
    };
    query?: never;
    url: '/v1/dayparting/schedules/{schedule_id}';
};

export type GetDaypartingScheduleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetDaypartingScheduleResponses = {
    /**
     * Successful Response
     */
    200: DaypartingScheduleRead;
};

export type GetDaypartingScheduleResponse = GetDaypartingScheduleResponses[keyof GetDaypartingScheduleResponses];

export type UpdateDaypartingScheduleData = {
    body: DaypartingScheduleUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        schedule_id: string;
    };
    query?: never;
    url: '/v1/dayparting/schedules/{schedule_id}';
};

export type UpdateDaypartingScheduleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Overlap
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateDaypartingScheduleResponses = {
    /**
     * Successful Response
     */
    200: DaypartingScheduleRead;
};

export type UpdateDaypartingScheduleResponse = UpdateDaypartingScheduleResponses[keyof UpdateDaypartingScheduleResponses];

export type ListDaypartingEventsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this store's
         */
        store_id?: number | null;
        /**
         * Only this schedule's
         */
        schedule_id?: string | null;
        /**
         * Only this outcome
         */
        status?: DaypartingEventStatus | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/dayparting/event-log';
};

export type ListDaypartingEventsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListDaypartingEventsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceDaypartingEventRead;
};

export type ListDaypartingEventsResponse = ListDaypartingEventsResponses[keyof ListDaypartingEventsResponses];

export type ListFocusedSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Whose list; omitted, your own
         */
        principal_id?: string | null;
        store_id?: number | null;
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/focused-skus';
};

export type ListFocusedSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListFocusedSkusResponses = {
    /**
     * Successful Response
     */
    200: ListResourceFocusItem;
};

export type ListFocusedSkusResponse = ListFocusedSkusResponses[keyof ListFocusedSkusResponses];

export type AddFocusedSkusData = {
    body: FocusAdd;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focused-skus';
};

export type AddFocusedSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AddFocusedSkusResponses = {
    /**
     * Successful Response
     */
    200: FocusChange;
};

export type AddFocusedSkusResponse = AddFocusedSkusResponses[keyof AddFocusedSkusResponses];

export type RemoveFocusedSkusData = {
    body: FocusRemove;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focused-skus/remove';
};

export type RemoveFocusedSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RemoveFocusedSkusResponses = {
    /**
     * Successful Response
     */
    200: FocusChange;
};

export type RemoveFocusedSkusResponse = RemoveFocusedSkusResponses[keyof RemoveFocusedSkusResponses];

export type DownloadFocusedSkuTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focused-skus/template';
};

export type DownloadFocusedSkuTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadFocusedSkuTemplateResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PreviewFocusedSkuUploadData = {
    body: BodyPreviewFocusedSkuUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focused-skus/upload/preview';
};

export type PreviewFocusedSkuUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewFocusedSkuUploadResponses = {
    /**
     * Successful Response
     */
    200: UploadPreview;
};

export type PreviewFocusedSkuUploadResponse = PreviewFocusedSkuUploadResponses[keyof PreviewFocusedSkuUploadResponses];

export type CommitFocusedSkuUploadData = {
    body: BodyCommitFocusedSkuUpload;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focused-skus/upload/commit';
};

export type CommitFocusedSkuUploadErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CommitFocusedSkuUploadResponses = {
    /**
     * Successful Response
     */
    200: UploadResult;
};

export type CommitFocusedSkuUploadResponse = CommitFocusedSkuUploadResponses[keyof CommitFocusedSkuUploadResponses];

export type GetFocusedSkuSheetOptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Whose list; omitted, your own
         */
        principal_id?: string | null;
    };
    url: '/v1/focused-skus/sheet/options';
};

export type GetFocusedSkuSheetOptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusedSkuSheetOptionsResponses = {
    /**
     * Successful Response
     */
    200: SheetOptions;
};

export type GetFocusedSkuSheetOptionsResponse = GetFocusedSkuSheetOptionsResponses[keyof GetFocusedSkuSheetOptionsResponses];

export type GetFocusedSkuSheetData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        warehouse_id?: number | null;
        /**
         * One store; omitted, all stores
         */
        store_id?: number | null;
        brand_ids?: Array<number> | null;
        grain?: FocusGrain;
        /**
         * A Sunday, or the 1st; omitted, the latest finished
         */
        period_start?: string | null;
        search?: string | null;
        /**
         * Whose list; omitted, your own
         */
        principal_id?: string | null;
    };
    url: '/v1/focused-skus/sheet';
};

export type GetFocusedSkuSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusedSkuSheetResponses = {
    /**
     * Successful Response
     */
    200: Sheet;
};

export type GetFocusedSkuSheetResponse = GetFocusedSkuSheetResponses[keyof GetFocusedSkuSheetResponses];

export type SaveFocusedSkuNotesData = {
    body: NoteSave;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focused-skus/sheet';
};

export type SaveFocusedSkuNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SaveFocusedSkuNotesResponses = {
    /**
     * Successful Response
     */
    200: NoteSaveResult;
};

export type SaveFocusedSkuNotesResponse = SaveFocusedSkuNotesResponses[keyof SaveFocusedSkuNotesResponses];

export type RunAutoOrderingForecastData = {
    body: ForecastRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/operations/auto-ordering/forecast';
};

export type RunAutoOrderingForecastErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RunAutoOrderingForecastResponses = {
    /**
     * Successful Response
     */
    200: ForecastRead;
};

export type RunAutoOrderingForecastResponse = RunAutoOrderingForecastResponses[keyof RunAutoOrderingForecastResponses];

export type ReadAutoOrderingPlanningData = {
    body: BodyReadAutoOrderingPlanning;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Which upload
         */
        kind: PlanningKind;
    };
    query?: never;
    url: '/v1/operations/auto-ordering/planning/{kind}';
};

export type ReadAutoOrderingPlanningErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadAutoOrderingPlanningResponses = {
    /**
     * Successful Response
     */
    200: PlanningUpload;
};

export type ReadAutoOrderingPlanningResponse = ReadAutoOrderingPlanningResponses[keyof ReadAutoOrderingPlanningResponses];

export type DownloadAutoOrderingTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * Which upload
         */
        kind: PlanningKind;
    };
    query?: never;
    url: '/v1/operations/auto-ordering/planning/{kind}/template';
};

export type DownloadAutoOrderingTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadAutoOrderingTemplateResponses = {
    /**
     * The template
     */
    200: unknown;
};

export type AutoOrderingVendorOptionsData = {
    body: OrderVendorsRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/operations/auto-ordering/vendor-options';
};

export type AutoOrderingVendorOptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AutoOrderingVendorOptionsResponses = {
    /**
     * Successful Response
     */
    200: OrderVendors;
};

export type AutoOrderingVendorOptionsResponse = AutoOrderingVendorOptionsResponses[keyof AutoOrderingVendorOptionsResponses];

export type AutoOrderingNextVoCodeData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
    };
    url: '/v1/operations/auto-ordering/next-vo-code';
};

export type AutoOrderingNextVoCodeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AutoOrderingNextVoCodeResponses = {
    /**
     * Successful Response
     */
    200: NextVendorOrderCode;
};

export type AutoOrderingNextVoCodeResponse = AutoOrderingNextVoCodeResponses[keyof AutoOrderingNextVoCodeResponses];

export type PoFulfilmentSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The marketplace, com_marketplace.pk_mp_id
         */
        marketplace_id: number;
        /**
         * Earliest PO date, inclusive. Default: 30 days before end.
         */
        start_date?: string | null;
        /**
         * Latest PO date, inclusive. Default: today.
         */
        end_date?: string | null;
    };
    url: '/v1/operations/po-fulfilment/summary';
};

export type PoFulfilmentSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PoFulfilmentSummaryResponses = {
    /**
     * Successful Response
     */
    200: StoreSummaryRead;
};

export type PoFulfilmentSummaryResponse = PoFulfilmentSummaryResponses[keyof PoFulfilmentSummaryResponses];

export type ListPoFulfilmentPurchaseOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The marketplace, com_marketplace.pk_mp_id
         */
        marketplace_id: number;
        /**
         * Earliest PO date, inclusive. Default: 30 days before end.
         */
        start_date?: string | null;
        /**
         * Latest PO date, inclusive. Default: today.
         */
        end_date?: string | null;
        /**
         * Only this status
         */
        status?: BridgeStatus | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/operations/po-fulfilment/purchase-orders';
};

export type ListPoFulfilmentPurchaseOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListPoFulfilmentPurchaseOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAmazonPoRead;
};

export type ListPoFulfilmentPurchaseOrdersResponse = ListPoFulfilmentPurchaseOrdersResponses[keyof ListPoFulfilmentPurchaseOrdersResponses];

export type ReadPoFulfilmentPurchaseOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor store
         */
        store_id: number;
        /**
         * Amazon's PO number
         */
        po_number: string;
    };
    query?: never;
    url: '/v1/operations/po-fulfilment/purchase-orders/{store_id}/{po_number}';
};

export type ReadPoFulfilmentPurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadPoFulfilmentPurchaseOrderResponses = {
    /**
     * Successful Response
     */
    200: DetailRead;
};

export type ReadPoFulfilmentPurchaseOrderResponse = ReadPoFulfilmentPurchaseOrderResponses[keyof ReadPoFulfilmentPurchaseOrderResponses];

export type CreatePoFulfilmentLegsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor store
         */
        store_id: number;
        /**
         * Amazon's PO number
         */
        po_number: string;
    };
    query?: never;
    url: '/v1/operations/po-fulfilment/purchase-orders/{store_id}/{po_number}/create';
};

export type CreatePoFulfilmentLegsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreatePoFulfilmentLegsResponses = {
    /**
     * Successful Response
     */
    200: ActionRead;
};

export type CreatePoFulfilmentLegsResponse = CreatePoFulfilmentLegsResponses[keyof CreatePoFulfilmentLegsResponses];

export type SendPoFulfilmentToWarehouseData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor store
         */
        store_id: number;
        /**
         * Amazon's PO number
         */
        po_number: string;
    };
    query?: never;
    url: '/v1/operations/po-fulfilment/purchase-orders/{store_id}/{po_number}/send';
};

export type SendPoFulfilmentToWarehouseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SendPoFulfilmentToWarehouseResponses = {
    /**
     * Successful Response
     */
    200: ActionRead;
};

export type SendPoFulfilmentToWarehouseResponse = SendPoFulfilmentToWarehouseResponses[keyof SendPoFulfilmentToWarehouseResponses];

export type AcknowledgePoFulfilmentPurchaseOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor store
         */
        store_id: number;
        /**
         * Amazon's PO number
         */
        po_number: string;
    };
    query?: never;
    url: '/v1/operations/po-fulfilment/purchase-orders/{store_id}/{po_number}/acknowledge';
};

export type AcknowledgePoFulfilmentPurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AcknowledgePoFulfilmentPurchaseOrderResponses = {
    /**
     * Successful Response
     */
    200: ActionRead;
};

export type AcknowledgePoFulfilmentPurchaseOrderResponse = AcknowledgePoFulfilmentPurchaseOrderResponses[keyof AcknowledgePoFulfilmentPurchaseOrderResponses];

export type ExportPoFulfilmentReportData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The marketplace, com_marketplace.pk_mp_id
         */
        marketplace_id: number;
        /**
         * Earliest PO date, inclusive. Default: 30 days before end.
         */
        start_date?: string | null;
        /**
         * Latest PO date, inclusive. Default: today.
         */
        end_date?: string | null;
    };
    url: '/v1/operations/po-fulfilment/export';
};

export type ExportPoFulfilmentReportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportPoFulfilmentReportResponses = {
    /**
     * The fill-rate workbook
     */
    200: unknown;
};

export type RetailerPoFulfilmentLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/operations/po-fulfilment/retailer/lookups';
};

export type RetailerPoFulfilmentLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetailerPoFulfilmentLookupsResponses = {
    /**
     * Successful Response
     */
    200: FulfilmentLookupsRead;
};

export type RetailerPoFulfilmentLookupsResponse = RetailerPoFulfilmentLookupsResponses[keyof RetailerPoFulfilmentLookupsResponses];

export type RetailerPoFulfilmentSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this retailer
         */
        party_id?: number | null;
        /**
         * Only POs from retailers on this marketplace
         */
        marketplace_id?: number | null;
        /**
         * PO code contains
         */
        q?: string | null;
        /**
         * PO date on or after
         */
        date_from?: string | null;
        /**
         * PO date before
         */
        date_to?: string | null;
        /**
         * Only POs in this status
         */
        status?: ('not_started' | 'in_progress' | 'invoiced' | 'delivered' | 'expired') | null;
    };
    url: '/v1/operations/po-fulfilment/retailer/summary';
};

export type RetailerPoFulfilmentSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetailerPoFulfilmentSummaryResponses = {
    /**
     * Successful Response
     */
    200: FulfilmentSummaryRead;
};

export type RetailerPoFulfilmentSummaryResponse = RetailerPoFulfilmentSummaryResponses[keyof RetailerPoFulfilmentSummaryResponses];

export type ListRetailerPoFulfilmentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this retailer
         */
        party_id?: number | null;
        /**
         * Only POs from retailers on this marketplace
         */
        marketplace_id?: number | null;
        /**
         * PO code contains
         */
        q?: string | null;
        /**
         * PO date on or after
         */
        date_from?: string | null;
        /**
         * PO date before
         */
        date_to?: string | null;
        /**
         * Only POs in this status
         */
        status?: ('not_started' | 'in_progress' | 'invoiced' | 'delivered' | 'expired') | null;
    };
    url: '/v1/operations/po-fulfilment/retailer';
};

export type ListRetailerPoFulfilmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListRetailerPoFulfilmentResponses = {
    /**
     * Successful Response
     */
    200: ListResourcePoFulfilmentRead;
};

export type ListRetailerPoFulfilmentResponse = ListRetailerPoFulfilmentResponses[keyof ListRetailerPoFulfilmentResponses];

export type ExportRetailerPoFulfilmentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this retailer
         */
        party_id?: number | null;
        /**
         * Only POs from retailers on this marketplace
         */
        marketplace_id?: number | null;
        /**
         * PO code contains
         */
        q?: string | null;
        /**
         * PO date on or after
         */
        date_from?: string | null;
        /**
         * PO date before
         */
        date_to?: string | null;
        /**
         * Only POs in this status
         */
        status?: ('not_started' | 'in_progress' | 'invoiced' | 'delivered' | 'expired') | null;
    };
    url: '/v1/operations/po-fulfilment/retailer/export';
};

export type ExportRetailerPoFulfilmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportRetailerPoFulfilmentResponses = {
    /**
     * By retailer, by PO, by line
     */
    200: unknown;
};

export type RetailerPoFulfilmentLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/operations/po-fulfilment/retailer/{po_id}/lines';
};

export type RetailerPoFulfilmentLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetailerPoFulfilmentLinesResponses = {
    /**
     * Successful Response
     */
    200: Array<LineFulfilmentRead>;
};

export type RetailerPoFulfilmentLinesResponse = RetailerPoFulfilmentLinesResponses[keyof RetailerPoFulfilmentLinesResponses];

export type DashboardSalesMarketingData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/sales-marketing';
};

export type DashboardSalesMarketingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardSalesMarketingResponses = {
    /**
     * Successful Response
     */
    200: SalesMarketing;
};

export type DashboardSalesMarketingResponse = DashboardSalesMarketingResponses[keyof DashboardSalesMarketingResponses];

export type DashboardFlowData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The figure
         */
        metric: Metric;
        /**
         * The steps, left to right
         */
        levels: Array<FlowLevel>;
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/flow';
};

export type DashboardFlowErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardFlowResponses = {
    /**
     * Successful Response
     */
    200: Flow;
};

export type DashboardFlowResponse = DashboardFlowResponses[keyof DashboardFlowResponses];

export type DashboardForecastData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The figure; not ROAS
         */
        metric: Metric;
        /**
         * To the week's end or the month's
         */
        horizon?: Horizon;
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/forecast';
};

export type DashboardForecastErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardForecastResponses = {
    /**
     * Successful Response
     */
    200: MetricForecast;
};

export type DashboardForecastResponse = DashboardForecastResponses[keyof DashboardForecastResponses];

export type DashboardGrowthData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The figure; not ROAS
         */
        metric?: Metric;
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/growth';
};

export type DashboardGrowthErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardGrowthResponses = {
    /**
     * Successful Response
     */
    200: Growth;
};

export type DashboardGrowthResponse = DashboardGrowthResponses[keyof DashboardGrowthResponses];

export type DashboardMoversData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * What to rank on
         */
        metric?: MoverMetric;
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/focused/movers';
};

export type DashboardMoversErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardMoversResponses = {
    /**
     * Successful Response
     */
    200: Movers;
};

export type DashboardMoversResponse = DashboardMoversResponses[keyof DashboardMoversResponses];

export type DashboardNoSaleData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/focused/no-sale';
};

export type DashboardNoSaleErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardNoSaleResponses = {
    /**
     * Successful Response
     */
    200: NoSale;
};

export type DashboardNoSaleResponse = DashboardNoSaleResponses[keyof DashboardNoSaleResponses];

export type DashboardStockCoverData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/stock-cover';
};

export type DashboardStockCoverErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardStockCoverResponses = {
    /**
     * Successful Response
     */
    200: StockCover;
};

export type DashboardStockCoverResponse = DashboardStockCoverResponses[keyof DashboardStockCoverResponses];

export type DashboardSuppressedData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/suppressed-inventory';
};

export type DashboardSuppressedErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardSuppressedResponses = {
    /**
     * Successful Response
     */
    200: SuppressedInventory;
};

export type DashboardSuppressedResponse = DashboardSuppressedResponses[keyof DashboardSuppressedResponses];

export type DashboardAdSlotsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/ad-slots';
};

export type DashboardAdSlotsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardAdSlotsResponses = {
    /**
     * Successful Response
     */
    200: AdSlots;
};

export type DashboardAdSlotsResponse = DashboardAdSlotsResponses[keyof DashboardAdSlotsResponses];

export type DashboardDistressedData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/distressed-ad-groups';
};

export type DashboardDistressedErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardDistressedResponses = {
    /**
     * Successful Response
     */
    200: DistressedAdGroups;
};

export type DashboardDistressedResponse = DashboardDistressedResponses[keyof DashboardDistressedResponses];

export type DashboardFreshnessData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/data-freshness';
};

export type DashboardFreshnessErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardFreshnessResponses = {
    /**
     * Successful Response
     */
    200: DataFreshness;
};

export type DashboardFreshnessResponse = DashboardFreshnessResponses[keyof DashboardFreshnessResponses];

export type DashboardCountriesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * The window's first day
         */
        date_from?: string | null;
        /**
         * Its last day; today by default
         */
        date_to?: string | null;
        /**
         * Read only the stores that sell in this currency. By default, the one most of the window's orders were in
         */
        currency?: string | null;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Only these stores
         */
        store_ids?: Array<number> | null;
        /**
         * Only these brands
         */
        brand_ids?: Array<number> | null;
        /**
         * Only these SKU codes
         */
        sku_codes?: Array<string> | null;
    };
    url: '/v1/dashboard/countries';
};

export type DashboardCountriesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardCountriesResponses = {
    /**
     * Successful Response
     */
    200: SalesByCountry;
};

export type DashboardCountriesResponse = DashboardCountriesResponses[keyof DashboardCountriesResponses];

export type DashboardForumData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * How the threads are ordered
         */
        sort?: ForumSort;
        /**
         * The contributors' month, YYYY-MM
         */
        month?: string | null;
    };
    url: '/v1/dashboard/forum';
};

export type DashboardForumErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DashboardForumResponses = {
    /**
     * Successful Response
     */
    200: ForumActivity;
};

export type DashboardForumResponse = DashboardForumResponses[keyof DashboardForumResponses];

export type GetFocusTermSheetOptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/focus-search-term/sheet/options';
};

export type GetFocusTermSheetOptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusTermSheetOptionsResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermSheetOptionsResponse;
};

export type GetFocusTermSheetOptionsResponse = GetFocusTermSheetOptionsResponses[keyof GetFocusTermSheetOptionsResponses];

export type GetFocusTermSheetData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
        /**
         * Frozen week to serve; omitted, the latest
         */
        week_start?: string | null;
        /**
         * Brand IDs to filter
         */
        brand_id?: Array<number> | null;
        page?: number;
        size?: number;
        /**
         * Term, ASIN or title
         */
        search?: string | null;
        sort_by?: string;
        sort_order?: VentawizKitSortingSortOrder;
        /**
         * Per-column filters, e.g. {"bid_action": ["pause"]}
         */
        filters?: string | null;
    };
    url: '/v1/focus-search-term/sheet';
};

export type GetFocusTermSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusTermSheetResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermSheetResponse;
};

export type GetFocusTermSheetResponse = GetFocusTermSheetResponses[keyof GetFocusTermSheetResponses];

export type SaveFocusTermBidActionsData = {
    body: FocusSearchTermSheetBatchUpdate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
    };
    url: '/v1/focus-search-term/sheet';
};

export type SaveFocusTermBidActionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SaveFocusTermBidActionsResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermSheetBatchResult;
};

export type SaveFocusTermBidActionsResponse = SaveFocusTermBidActionsResponses[keyof SaveFocusTermBidActionsResponses];

export type GetFocusTermColumnValuesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
        /**
         * Sheet column to list values for
         */
        column: string;
        /**
         * Frozen week to serve; omitted, the latest
         */
        week_start?: string | null;
        /**
         * Brand IDs to filter
         */
        brand_id?: Array<number> | null;
        /**
         * Term, ASIN or title
         */
        search?: string | null;
        /**
         * Per-column filters, e.g. {"bid_action": ["pause"]}
         */
        filters?: string | null;
        value_search?: string | null;
    };
    url: '/v1/focus-search-term/sheet/column-values';
};

export type GetFocusTermColumnValuesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusTermColumnValuesResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermSheetColumnValuesResponse;
};

export type GetFocusTermColumnValuesResponse = GetFocusTermColumnValuesResponses[keyof GetFocusTermColumnValuesResponses];

export type RunFocusTermSnapshotData = {
    body: FocusSearchTermSnapshotRunRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
    };
    url: '/v1/focus-search-term/sheet/snapshot';
};

export type RunFocusTermSnapshotErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RunFocusTermSnapshotResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermSnapshotRunResponse;
};

export type RunFocusTermSnapshotResponse = RunFocusTermSnapshotResponses[keyof RunFocusTermSnapshotResponses];

export type GetFocusTermHistoryTermsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
        /**
         * Brand IDs to filter
         */
        brand_id?: Array<number> | null;
    };
    url: '/v1/focus-search-term/history/terms';
};

export type GetFocusTermHistoryTermsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusTermHistoryTermsResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermHistoryOptionsResponse;
};

export type GetFocusTermHistoryTermsResponse = GetFocusTermHistoryTermsResponses[keyof GetFocusTermHistoryTermsResponses];

export type GetFocusTermHistoryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
        search_term: string;
    };
    url: '/v1/focus-search-term/history';
};

export type GetFocusTermHistoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFocusTermHistoryResponses = {
    /**
     * Successful Response
     */
    200: FocusSearchTermHistoryResponse;
};

export type GetFocusTermHistoryResponse = GetFocusTermHistoryResponses[keyof GetFocusTermHistoryResponses];

export type CreateSearchTermDataData = {
    body: SearchTermDataCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store (the sheet is store-scoped)
         */
        store_id: number;
    };
    url: '/v1/focus-search-term/search-term-data';
};

export type CreateSearchTermDataErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateSearchTermDataResponses = {
    /**
     * Successful Response
     */
    200: SearchTermDataCreated;
};

export type CreateSearchTermDataResponse = CreateSearchTermDataResponses[keyof CreateSearchTermDataResponses];

export type ListMySearchTermDataData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * From this day; default 30 days ago
         */
        start_date?: string | null;
        /**
         * Up to this day; default now
         */
        end_date?: string | null;
    };
    url: '/v1/focus-search-term/search-term-data/user-history';
};

export type ListMySearchTermDataErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMySearchTermDataResponses = {
    /**
     * Successful Response
     */
    200: Array<SearchTermDataRead>;
};

export type ListMySearchTermDataResponse = ListMySearchTermDataResponses[keyof ListMySearchTermDataResponses];

export type ListTicketsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The tab
         */
        view?: TicketView;
        sort?: TicketSort;
        order?: TicketSortOrder;
        /**
         * Pinned tickets, shown above; left out here
         */
        pinned_ids?: Array<string>;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Only this page's types
         */
        section?: TicketSection | null;
        /**
         * Only these types
         */
        ticket_type_ids?: Array<string>;
        /**
         * Only these statuses
         */
        statuses?: Array<TicketStatus>;
        /**
         * Only these brands
         */
        brand_ids?: Array<number>;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Only these stores
         */
        store_ids?: Array<number>;
        /**
         * Only these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Only these owners
         */
        owner_ids?: Array<string>;
        /**
         * Only these creators
         */
        creator_ids?: Array<string>;
        /**
         * Raised at or after
         */
        created_from?: string | null;
        /**
         * Raised at or before
         */
        created_to?: string | null;
        /**
         * Only (true) or never (false) past the type's ETA
         */
        overdue?: boolean | null;
        /**
         * An item's SKU or ASIN contains this, any case
         */
        sku?: string | null;
        /**
         * Comma is OR, space is AND
         */
        q?: string | null;
    };
    url: '/v1/tickets';
};

export type ListTicketsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketListItem;
};

export type ListTicketsResponse = ListTicketsResponses[keyof ListTicketsResponses];

export type CreateTicketsData = {
    body: TicketCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/tickets';
};

export type CreateTicketsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * No such ticket type
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateTicketsResponses = {
    /**
     * Successful Response
     */
    201: TicketCreateResult;
};

export type CreateTicketsResponse = CreateTicketsResponses[keyof CreateTicketsResponses];

export type TicketsByIdsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Ticket ids
         */
        ids: Array<string>;
    };
    url: '/v1/tickets/by-ids';
};

export type TicketsByIdsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type TicketsByIdsResponses = {
    /**
     * Successful Response
     */
    200: Array<TicketListItem>;
};

export type TicketsByIdsResponse = TicketsByIdsResponses[keyof TicketsByIdsResponses];

export type CountTicketsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this page's types
         */
        section?: TicketSection | null;
        /**
         * Only these types
         */
        ticket_type_ids?: Array<string>;
        /**
         * Only these statuses
         */
        statuses?: Array<TicketStatus>;
        /**
         * Only these brands
         */
        brand_ids?: Array<number>;
        /**
         * Only these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Only these stores
         */
        store_ids?: Array<number>;
        /**
         * Only these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Only these owners
         */
        owner_ids?: Array<string>;
        /**
         * Only these creators
         */
        creator_ids?: Array<string>;
        /**
         * Raised at or after
         */
        created_from?: string | null;
        /**
         * Raised at or before
         */
        created_to?: string | null;
        /**
         * Only (true) or never (false) past the type's ETA
         */
        overdue?: boolean | null;
        /**
         * An item's SKU or ASIN contains this, any case
         */
        sku?: string | null;
        /**
         * Comma is OR, space is AND
         */
        q?: string | null;
    };
    url: '/v1/tickets/counts';
};

export type CountTicketsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CountTicketsResponses = {
    /**
     * Successful Response
     */
    200: TicketCounts;
};

export type CountTicketsResponse = CountTicketsResponses[keyof CountTicketsResponses];

export type TicketWorklistData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The week's first day
         */
        week_from: string;
        /**
         * The week's last day
         */
        week_to: string;
        /**
         * The caller's IANA time zone
         */
        tz?: string;
    };
    url: '/v1/tickets/worklist';
};

export type TicketWorklistErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type TicketWorklistResponses = {
    /**
     * Successful Response
     */
    200: TicketWorklist;
};

export type TicketWorklistResponse = TicketWorklistResponses[keyof TicketWorklistResponses];

export type TicketDoneSeriesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The first day
         */
        date_from: string;
        /**
         * The last day
         */
        date_to: string;
        /**
         * What to count
         */
        metric?: DoneMetric;
        /**
         * The caller's IANA time zone
         */
        tz?: string;
    };
    url: '/v1/tickets/done-series';
};

export type TicketDoneSeriesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type TicketDoneSeriesResponses = {
    /**
     * Successful Response
     */
    200: TicketDoneSeries;
};

export type TicketDoneSeriesResponse = TicketDoneSeriesResponses[keyof TicketDoneSeriesResponses];

export type GetTicketAccessData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/tickets/access';
};

export type GetTicketAccessErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetTicketAccessResponses = {
    /**
     * Successful Response
     */
    200: TicketAccess;
};

export type GetTicketAccessResponse = GetTicketAccessResponses[keyof GetTicketAccessResponses];

export type ListTicketTypesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tickets/types';
};

export type ListTicketTypesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketTypesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketTypeRead;
};

export type ListTicketTypesResponse = ListTicketTypesResponses[keyof ListTicketTypesResponses];

export type UpdateTicketTypeData = {
    body: TicketTypeUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * e.g. T15
         */
        ticket_type_id: string;
    };
    query?: never;
    url: '/v1/tickets/types/{ticket_type_id}';
};

export type UpdateTicketTypeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateTicketTypeResponses = {
    /**
     * Successful Response
     */
    200: TicketTypeRead;
};

export type UpdateTicketTypeResponse = UpdateTicketTypeResponses[keyof UpdateTicketTypeResponses];

export type ReadTicketNotificationsData = {
    body: TicketNotificationsRead;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/tickets/notifications/read';
};

export type ReadTicketNotificationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadTicketNotificationsResponses = {
    /**
     * Successful Response
     */
    200: TicketNotificationsResult;
};

export type ReadTicketNotificationsResponse = ReadTicketNotificationsResponses[keyof ReadTicketNotificationsResponses];

export type DismissTicketNotificationsData = {
    body: TicketNotificationsDismiss;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/tickets/notifications/dismiss';
};

export type DismissTicketNotificationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DismissTicketNotificationsResponses = {
    /**
     * Successful Response
     */
    200: TicketNotificationsResult;
};

export type DismissTicketNotificationsResponse = DismissTicketNotificationsResponses[keyof DismissTicketNotificationsResponses];

export type RaiseDetectedTicketsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * e.g. T15
         */
        ticket_type_id: string;
    };
    query?: never;
    url: '/v1/tickets/raise/{ticket_type_id}';
};

export type RaiseDetectedTicketsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * The type has no detector yet
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RaiseDetectedTicketsResponses = {
    /**
     * Successful Response
     */
    201: TicketCreateResult;
};

export type RaiseDetectedTicketsResponse = RaiseDetectedTicketsResponses[keyof RaiseDetectedTicketsResponses];

export type ReportTicketData = {
    body: TicketReportCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/tickets/report';
};

export type ReportTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportTicketResponses = {
    /**
     * Successful Response
     */
    201: TicketCreateResult;
};

export type ReportTicketResponse = ReportTicketResponses[keyof ReportTicketResponses];

export type ReportedSourceIdsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/tickets/reported-source-ids';
};

export type ReportedSourceIdsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportedSourceIdsResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type ReportedSourceIdsResponse = ReportedSourceIdsResponses[keyof ReportedSourceIdsResponses];

export type DeleteTicketData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}';
};

export type DeleteTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteTicketResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteTicketResponse = DeleteTicketResponses[keyof DeleteTicketResponses];

export type GetTicketData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}';
};

export type GetTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetTicketResponses = {
    /**
     * Successful Response
     */
    200: TicketRead;
};

export type GetTicketResponse = GetTicketResponses[keyof GetTicketResponses];

export type UpdateTicketData = {
    body: TicketUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}';
};

export type UpdateTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateTicketResponses = {
    /**
     * Successful Response
     */
    200: TicketRead;
};

export type UpdateTicketResponse = UpdateTicketResponses[keyof UpdateTicketResponses];

export type AssignTicketData = {
    body: TicketAssign;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}/assign';
};

export type AssignTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AssignTicketResponses = {
    /**
     * Successful Response
     */
    200: TicketRead;
};

export type AssignTicketResponse = AssignTicketResponses[keyof AssignTicketResponses];

export type UnassignTicketData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}/unassign';
};

export type UnassignTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UnassignTicketResponses = {
    /**
     * Successful Response
     */
    200: TicketRead;
};

export type UnassignTicketResponse = UnassignTicketResponses[keyof UnassignTicketResponses];

export type ChangeTicketStatusData = {
    body: TicketStatusUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}/status';
};

export type ChangeTicketStatusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ChangeTicketStatusResponses = {
    /**
     * Successful Response
     */
    200: TicketRead;
};

export type ChangeTicketStatusResponse = ChangeTicketStatusResponses[keyof ChangeTicketStatusResponses];

export type ActOnTicketData = {
    body: TicketActionRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}/actions';
};

export type ActOnTicketErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ActOnTicketResponses = {
    /**
     * Successful Response
     */
    200: TicketActionResult;
};

export type ActOnTicketResponse = ActOnTicketResponses[keyof ActOnTicketResponses];

export type ListTicketReportsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}/reports';
};

export type ListTicketReportsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketReportsResponses = {
    /**
     * Successful Response
     */
    200: Array<TicketListItem>;
};

export type ListTicketReportsResponse = ListTicketReportsResponses[keyof ListTicketReportsResponses];

export type ListTicketCommentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tickets/{ticket_id}/comments';
};

export type ListTicketCommentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketCommentsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketCommentRead;
};

export type ListTicketCommentsResponse = ListTicketCommentsResponses[keyof ListTicketCommentsResponses];

export type CreateTicketCommentData = {
    body: TicketCommentCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        ticket_id: string;
    };
    query?: never;
    url: '/v1/tickets/{ticket_id}/comments';
};

export type CreateTicketCommentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateTicketCommentResponses = {
    /**
     * Successful Response
     */
    201: TicketCommentRead;
};

export type CreateTicketCommentResponse = CreateTicketCommentResponses[keyof CreateTicketCommentResponses];

export type ListNotificationsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        limit?: number;
    };
    url: '/v1/notifications';
};

export type ListNotificationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListNotificationsResponses = {
    /**
     * Successful Response
     */
    200: Array<NotificationRead>;
};

export type ListNotificationsResponse = ListNotificationsResponses[keyof ListNotificationsResponses];

export type ClearMyTimezoneData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The store, or none for every store
         */
        store_id?: number | null;
        /**
         * The page key, or none for every page
         */
        page?: string | null;
    };
    url: '/v1/me/timezones';
};

export type ClearMyTimezoneErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ClearMyTimezoneResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type ClearMyTimezoneResponse = ClearMyTimezoneResponses[keyof ClearMyTimezoneResponses];

export type ReadMyTimezonesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/me/timezones';
};

export type ReadMyTimezonesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadMyTimezonesResponses = {
    /**
     * Successful Response
     */
    200: MyTimezonesRead;
};

export type ReadMyTimezonesResponse = ReadMyTimezonesResponses[keyof ReadMyTimezonesResponses];

export type SetMyTimezoneData = {
    body: TimezoneOverrideWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/me/timezones';
};

export type SetMyTimezoneErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SetMyTimezoneResponses = {
    /**
     * Successful Response
     */
    200: TimezoneOverrideRead;
};

export type SetMyTimezoneResponse = SetMyTimezoneResponses[keyof SetMyTimezoneResponses];

export type ListTicketFormMarketplacesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tickets/forms/marketplaces';
};

export type ListTicketFormMarketplacesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketFormMarketplacesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketFormMarketplace;
};

export type ListTicketFormMarketplacesResponse = ListTicketFormMarketplacesResponses[keyof ListTicketFormMarketplacesResponses];

export type ListTicketFormBrandsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tickets/forms/brands';
};

export type ListTicketFormBrandsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketFormBrandsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketFormBrand;
};

export type ListTicketFormBrandsResponse = ListTicketFormBrandsResponses[keyof ListTicketFormBrandsResponses];

export type ListTicketFormStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tickets/forms/stores';
};

export type ListTicketFormStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketFormStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketFormStore;
};

export type ListTicketFormStoresResponse = ListTicketFormStoresResponses[keyof ListTicketFormStoresResponses];

export type ListTicketFormWarehousesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tickets/forms/warehouses';
};

export type ListTicketFormWarehousesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTicketFormWarehousesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketFormWarehouse;
};

export type ListTicketFormWarehousesResponse = ListTicketFormWarehousesResponses[keyof ListTicketFormWarehousesResponses];

export type DownloadTicketItemsTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * e.g. T9
         */
        ticket_type_id: string;
    };
    query?: never;
    url: '/v1/tickets/forms/{ticket_type_id}/items-sheet';
};

export type DownloadTicketItemsTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * The type has no item sheet
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadTicketItemsTemplateResponses = {
    /**
     * The blank item sheet
     */
    200: unknown;
};

export type ReadTicketItemsSheetData = {
    body: BodyReadTicketItemsSheet;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * e.g. T9
         */
        ticket_type_id: string;
    };
    query?: never;
    url: '/v1/tickets/forms/{ticket_type_id}/items-sheet';
};

export type ReadTicketItemsSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * The type has no item sheet
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadTicketItemsSheetResponses = {
    /**
     * Successful Response
     */
    200: ItemSheet;
};

export type ReadTicketItemsSheetResponse = ReadTicketItemsSheetResponses[keyof ReadTicketItemsSheetResponses];

export type DataVaultSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/summary';
};

export type DataVaultSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultSummaryResponses = {
    /**
     * Successful Response
     */
    200: SummaryRead;
};

export type DataVaultSummaryResponse = DataVaultSummaryResponses[keyof DataVaultSummaryResponses];

export type DataVaultOrdersTrendData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        metric?: VentawizDataVaultSchemasTrendMetric;
        granularity?: VentawizDataVaultSchemasTrendGranularity;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/orders/trend';
};

export type DataVaultOrdersTrendErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultOrdersTrendResponses = {
    /**
     * Successful Response
     */
    200: VentawizDataVaultSchemasTrendRead;
};

export type DataVaultOrdersTrendResponse = DataVaultOrdersTrendResponses[keyof DataVaultOrdersTrendResponses];

export type DataVaultListProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/products';
};

export type DataVaultListProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListProductsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceProductRow;
};

export type DataVaultListProductsResponse = DataVaultListProductsResponses[keyof DataVaultListProductsResponses];

export type DataVaultEditProductsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/products';
};

export type DataVaultEditProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditProductsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditProductsResponse = DataVaultEditProductsResponses[keyof DataVaultEditProductsResponses];

export type DataVaultExportProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/products/export';
};

export type DataVaultExportProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportProductsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/products/values';
};

export type DataVaultValuesProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesProductsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesProductsResponse = DataVaultValuesProductsResponses[keyof DataVaultValuesProductsResponses];

export type DataVaultAggregateProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: AggregateLevel;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/products/aggregate';
};

export type DataVaultAggregateProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultAggregateProductsResponses = {
    /**
     * Successful Response
     */
    200: AggregateRead;
};

export type DataVaultAggregateProductsResponse = DataVaultAggregateProductsResponses[keyof DataVaultAggregateProductsResponses];

export type DataVaultListInternalSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/internal-skus';
};

export type DataVaultListInternalSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListInternalSkusResponses = {
    /**
     * Successful Response
     */
    200: ListResourceInternalSkuRow;
};

export type DataVaultListInternalSkusResponse = DataVaultListInternalSkusResponses[keyof DataVaultListInternalSkusResponses];

export type DataVaultEditInternalSkusData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/internal-skus';
};

export type DataVaultEditInternalSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditInternalSkusResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditInternalSkusResponse = DataVaultEditInternalSkusResponses[keyof DataVaultEditInternalSkusResponses];

export type DataVaultExportInternalSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/internal-skus/export';
};

export type DataVaultExportInternalSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportInternalSkusResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesInternalSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/internal-skus/values';
};

export type DataVaultValuesInternalSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesInternalSkusResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesInternalSkusResponse = DataVaultValuesInternalSkusResponses[keyof DataVaultValuesInternalSkusResponses];

export type DataVaultListCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/costs';
};

export type DataVaultListCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListCostsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceCostRow;
};

export type DataVaultListCostsResponse = DataVaultListCostsResponses[keyof DataVaultListCostsResponses];

export type DataVaultEditCostsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/costs';
};

export type DataVaultEditCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditCostsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditCostsResponse = DataVaultEditCostsResponses[keyof DataVaultEditCostsResponses];

export type DataVaultExportCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/costs/export';
};

export type DataVaultExportCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportCostsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/costs/values';
};

export type DataVaultValuesCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesCostsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesCostsResponse = DataVaultValuesCostsResponses[keyof DataVaultValuesCostsResponses];

export type DataVaultAggregateCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: AggregateLevel;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/costs/aggregate';
};

export type DataVaultAggregateCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultAggregateCostsResponses = {
    /**
     * Successful Response
     */
    200: AggregateRead;
};

export type DataVaultAggregateCostsResponse = DataVaultAggregateCostsResponses[keyof DataVaultAggregateCostsResponses];

export type DataVaultListRetailerCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/retailer-costs';
};

export type DataVaultListRetailerCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListRetailerCostsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceRetailerCostRow;
};

export type DataVaultListRetailerCostsResponse = DataVaultListRetailerCostsResponses[keyof DataVaultListRetailerCostsResponses];

export type DataVaultEditRetailerCostsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/retailer-costs';
};

export type DataVaultEditRetailerCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditRetailerCostsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditRetailerCostsResponse = DataVaultEditRetailerCostsResponses[keyof DataVaultEditRetailerCostsResponses];

export type DataVaultExportRetailerCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/retailer-costs/export';
};

export type DataVaultExportRetailerCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportRetailerCostsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesRetailerCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/retailer-costs/values';
};

export type DataVaultValuesRetailerCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesRetailerCostsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesRetailerCostsResponse = DataVaultValuesRetailerCostsResponses[keyof DataVaultValuesRetailerCostsResponses];

export type DataVaultListFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/fees';
};

export type DataVaultListFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListFeesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceFeeRow;
};

export type DataVaultListFeesResponse = DataVaultListFeesResponses[keyof DataVaultListFeesResponses];

export type DataVaultEditFeesData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/fees';
};

export type DataVaultEditFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditFeesResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditFeesResponse = DataVaultEditFeesResponses[keyof DataVaultEditFeesResponses];

export type DataVaultExportFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/fees/export';
};

export type DataVaultExportFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportFeesResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/fees/values';
};

export type DataVaultValuesFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesFeesResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesFeesResponse = DataVaultValuesFeesResponses[keyof DataVaultValuesFeesResponses];

export type DataVaultAggregateFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: AggregateLevel;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/fees/aggregate';
};

export type DataVaultAggregateFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultAggregateFeesResponses = {
    /**
     * Successful Response
     */
    200: AggregateRead;
};

export type DataVaultAggregateFeesResponse = DataVaultAggregateFeesResponses[keyof DataVaultAggregateFeesResponses];

export type DataVaultListOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/orders';
};

export type DataVaultListOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceOrderRow;
};

export type DataVaultListOrdersResponse = DataVaultListOrdersResponses[keyof DataVaultListOrdersResponses];

export type DataVaultEditOrdersData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/orders';
};

export type DataVaultEditOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditOrdersResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditOrdersResponse = DataVaultEditOrdersResponses[keyof DataVaultEditOrdersResponses];

export type DataVaultExportOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/orders/export';
};

export type DataVaultExportOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportOrdersResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/orders/values';
};

export type DataVaultValuesOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesOrdersResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesOrdersResponse = DataVaultValuesOrdersResponses[keyof DataVaultValuesOrdersResponses];

export type DataVaultAggregateOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: AggregateLevel;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/orders/aggregate';
};

export type DataVaultAggregateOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultAggregateOrdersResponses = {
    /**
     * Successful Response
     */
    200: AggregateRead;
};

export type DataVaultAggregateOrdersResponse = DataVaultAggregateOrdersResponses[keyof DataVaultAggregateOrdersResponses];

export type DataVaultListReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/returns';
};

export type DataVaultListReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListReturnsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceReturnRow;
};

export type DataVaultListReturnsResponse = DataVaultListReturnsResponses[keyof DataVaultListReturnsResponses];

export type DataVaultEditReturnsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/returns';
};

export type DataVaultEditReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditReturnsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditReturnsResponse = DataVaultEditReturnsResponses[keyof DataVaultEditReturnsResponses];

export type DataVaultExportReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/returns/export';
};

export type DataVaultExportReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportReturnsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/returns/values';
};

export type DataVaultValuesReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesReturnsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesReturnsResponse = DataVaultValuesReturnsResponses[keyof DataVaultValuesReturnsResponses];

export type DataVaultAggregateReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: AggregateLevel;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/returns/aggregate';
};

export type DataVaultAggregateReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultAggregateReturnsResponses = {
    /**
     * Successful Response
     */
    200: AggregateRead;
};

export type DataVaultAggregateReturnsResponse = DataVaultAggregateReturnsResponses[keyof DataVaultAggregateReturnsResponses];

export type DataVaultListSettlementsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/settlements';
};

export type DataVaultListSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListSettlementsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSettlementRow;
};

export type DataVaultListSettlementsResponse = DataVaultListSettlementsResponses[keyof DataVaultListSettlementsResponses];

export type DataVaultEditSettlementsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/settlements';
};

export type DataVaultEditSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditSettlementsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditSettlementsResponse = DataVaultEditSettlementsResponses[keyof DataVaultEditSettlementsResponses];

export type DataVaultExportSettlementsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/settlements/export';
};

export type DataVaultExportSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportSettlementsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesSettlementsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/settlements/values';
};

export type DataVaultValuesSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesSettlementsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesSettlementsResponse = DataVaultValuesSettlementsResponses[keyof DataVaultValuesSettlementsResponses];

export type DataVaultAggregateSettlementsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        level: AggregateLevel;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/settlements/aggregate';
};

export type DataVaultAggregateSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultAggregateSettlementsResponses = {
    /**
     * Successful Response
     */
    200: AggregateRead;
};

export type DataVaultAggregateSettlementsResponse = DataVaultAggregateSettlementsResponses[keyof DataVaultAggregateSettlementsResponses];

export type DataVaultListTicketExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/ticket-exceptions';
};

export type DataVaultListTicketExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListTicketExceptionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTicketExceptionRow;
};

export type DataVaultListTicketExceptionsResponse = DataVaultListTicketExceptionsResponses[keyof DataVaultListTicketExceptionsResponses];

export type DataVaultEditTicketExceptionsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/ticket-exceptions';
};

export type DataVaultEditTicketExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditTicketExceptionsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditTicketExceptionsResponse = DataVaultEditTicketExceptionsResponses[keyof DataVaultEditTicketExceptionsResponses];

export type DataVaultExportTicketExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/ticket-exceptions/export';
};

export type DataVaultExportTicketExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportTicketExceptionsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesTicketExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/ticket-exceptions/values';
};

export type DataVaultValuesTicketExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesTicketExceptionsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesTicketExceptionsResponse = DataVaultValuesTicketExceptionsResponses[keyof DataVaultValuesTicketExceptionsResponses];

export type DataVaultListMailExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/mail-exceptions';
};

export type DataVaultListMailExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultListMailExceptionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMailExceptionRow;
};

export type DataVaultListMailExceptionsResponse = DataVaultListMailExceptionsResponses[keyof DataVaultListMailExceptionsResponses];

export type DataVaultEditMailExceptionsData = {
    body: EditRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/mail-exceptions';
};

export type DataVaultEditMailExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditMailExceptionsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultEditMailExceptionsResponse = DataVaultEditMailExceptionsResponses[keyof DataVaultEditMailExceptionsResponses];

export type DataVaultExportMailExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/mail-exceptions/export';
};

export type DataVaultExportMailExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultExportMailExceptionsResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type DataVaultValuesMailExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text column of the dataset
         */
        column: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Limit to these warehouses
         */
        warehouse_ids?: Array<number>;
        /**
         * Limit to these marketplaces
         */
        marketplace_ids?: Array<number>;
        /**
         * Limit to these stores
         */
        store_ids?: Array<number>;
        /**
         * Limit to these brands
         */
        brand_ids?: Array<number>;
        /**
         * Limit to these vendors (costs)
         */
        vendor_ids?: Array<number>;
        /**
         * Limit to these retailers (retailer costs)
         */
        retailer_ids?: Array<number>;
        /**
         * Limit to these currencies (costs and retailer costs)
         */
        currency_codes?: Array<string>;
        /**
         * First day, inclusive
         */
        date_from?: string | null;
        /**
         * Last day, inclusive
         */
        date_to?: string | null;
        /**
         * SKU contains
         */
        sku?: string | null;
        /**
         * Product id contains
         */
        asin?: string | null;
        /**
         * Any text column contains
         */
        search?: string | null;
        /**
         * column:op:value; op in eq, ne, gt, gte, lt, lte for a number, in, nin, contains for a text column (__blank__ for no value); repeatable
         */
        filter?: Array<string>;
        /**
         * A column of the dataset
         */
        sort?: string | null;
        order?: VentawizKitSortingSortOrder;
    };
    url: '/v1/data-vault/mail-exceptions/values';
};

export type DataVaultValuesMailExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultValuesMailExceptionsResponses = {
    /**
     * Successful Response
     */
    200: ColumnValuesRead;
};

export type DataVaultValuesMailExceptionsResponse = DataVaultValuesMailExceptionsResponses[keyof DataVaultValuesMailExceptionsResponses];

export type DataVaultEditableProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/products/editable';
};

export type DataVaultEditableProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableProductsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableProductsResponse = DataVaultEditableProductsResponses[keyof DataVaultEditableProductsResponses];

export type DataVaultDeleteProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/products/{row_id}';
};

export type DataVaultDeleteProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteProductsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteProductsResponse = DataVaultDeleteProductsResponses[keyof DataVaultDeleteProductsResponses];

export type DataVaultEditableInternalSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/internal-skus/editable';
};

export type DataVaultEditableInternalSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableInternalSkusResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableInternalSkusResponse = DataVaultEditableInternalSkusResponses[keyof DataVaultEditableInternalSkusResponses];

export type DataVaultDeleteInternalSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/internal-skus/{row_id}';
};

export type DataVaultDeleteInternalSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteInternalSkusResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteInternalSkusResponse = DataVaultDeleteInternalSkusResponses[keyof DataVaultDeleteInternalSkusResponses];

export type DataVaultEditableCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/costs/editable';
};

export type DataVaultEditableCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableCostsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableCostsResponse = DataVaultEditableCostsResponses[keyof DataVaultEditableCostsResponses];

export type DataVaultDeleteCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/costs/{row_id}';
};

export type DataVaultDeleteCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteCostsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteCostsResponse = DataVaultDeleteCostsResponses[keyof DataVaultDeleteCostsResponses];

export type DataVaultEditableRetailerCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/retailer-costs/editable';
};

export type DataVaultEditableRetailerCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableRetailerCostsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableRetailerCostsResponse = DataVaultEditableRetailerCostsResponses[keyof DataVaultEditableRetailerCostsResponses];

export type DataVaultDeleteRetailerCostsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/retailer-costs/{row_id}';
};

export type DataVaultDeleteRetailerCostsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteRetailerCostsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteRetailerCostsResponse = DataVaultDeleteRetailerCostsResponses[keyof DataVaultDeleteRetailerCostsResponses];

export type DataVaultEditableOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/orders/editable';
};

export type DataVaultEditableOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableOrdersResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableOrdersResponse = DataVaultEditableOrdersResponses[keyof DataVaultEditableOrdersResponses];

export type DataVaultDeleteOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/orders/{row_id}';
};

export type DataVaultDeleteOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteOrdersResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteOrdersResponse = DataVaultDeleteOrdersResponses[keyof DataVaultDeleteOrdersResponses];

export type DataVaultEditableReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/returns/editable';
};

export type DataVaultEditableReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableReturnsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableReturnsResponse = DataVaultEditableReturnsResponses[keyof DataVaultEditableReturnsResponses];

export type DataVaultDeleteReturnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/returns/{row_id}';
};

export type DataVaultDeleteReturnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteReturnsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteReturnsResponse = DataVaultDeleteReturnsResponses[keyof DataVaultDeleteReturnsResponses];

export type DataVaultEditableSettlementsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/settlements/editable';
};

export type DataVaultEditableSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableSettlementsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableSettlementsResponse = DataVaultEditableSettlementsResponses[keyof DataVaultEditableSettlementsResponses];

export type DataVaultDeleteSettlementsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/settlements/{row_id}';
};

export type DataVaultDeleteSettlementsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteSettlementsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteSettlementsResponse = DataVaultDeleteSettlementsResponses[keyof DataVaultDeleteSettlementsResponses];

export type DataVaultEditableFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/fees/editable';
};

export type DataVaultEditableFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableFeesResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableFeesResponse = DataVaultEditableFeesResponses[keyof DataVaultEditableFeesResponses];

export type DataVaultDeleteFeesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/fees/{row_id}';
};

export type DataVaultDeleteFeesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteFeesResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteFeesResponse = DataVaultDeleteFeesResponses[keyof DataVaultDeleteFeesResponses];

export type DataVaultEditableTicketExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/ticket-exceptions/editable';
};

export type DataVaultEditableTicketExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableTicketExceptionsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableTicketExceptionsResponse = DataVaultEditableTicketExceptionsResponses[keyof DataVaultEditableTicketExceptionsResponses];

export type DataVaultDeleteTicketExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/ticket-exceptions/{row_id}';
};

export type DataVaultDeleteTicketExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteTicketExceptionsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteTicketExceptionsResponse = DataVaultDeleteTicketExceptionsResponses[keyof DataVaultDeleteTicketExceptionsResponses];

export type DataVaultEditableMailExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/data-vault/mail-exceptions/editable';
};

export type DataVaultEditableMailExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultEditableMailExceptionsResponses = {
    /**
     * Successful Response
     */
    200: EditableColumns;
};

export type DataVaultEditableMailExceptionsResponse = DataVaultEditableMailExceptionsResponses[keyof DataVaultEditableMailExceptionsResponses];

export type DataVaultDeleteMailExceptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The row's id
         */
        row_id: number;
    };
    query?: never;
    url: '/v1/data-vault/mail-exceptions/{row_id}';
};

export type DataVaultDeleteMailExceptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DataVaultDeleteMailExceptionsResponses = {
    /**
     * Successful Response
     */
    202: EditSubmitted;
};

export type DataVaultDeleteMailExceptionsResponse = DataVaultDeleteMailExceptionsResponses[keyof DataVaultDeleteMailExceptionsResponses];

export type LookupWarehousesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/warehouses';
};

export type LookupWarehousesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupWarehousesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceWarehouseOption;
};

export type LookupWarehousesResponse = LookupWarehousesResponses[keyof LookupWarehousesResponses];

export type LookupMarketplacesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/marketplaces';
};

export type LookupMarketplacesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupMarketplacesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketplaceOption;
};

export type LookupMarketplacesResponse = LookupMarketplacesResponses[keyof LookupMarketplacesResponses];

export type LookupStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/stores';
};

export type LookupStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceStoreOption;
};

export type LookupStoresResponse = LookupStoresResponses[keyof LookupStoresResponses];

export type LookupBrandsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/brands';
};

export type LookupBrandsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupBrandsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceBrandOption;
};

export type LookupBrandsResponse = LookupBrandsResponses[keyof LookupBrandsResponses];

export type LookupCategoriesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/categories';
};

export type LookupCategoriesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupCategoriesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceCategoryOption;
};

export type LookupCategoriesResponse = LookupCategoriesResponses[keyof LookupCategoriesResponses];

export type LookupVendorsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/vendors';
};

export type LookupVendorsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupVendorsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVendorOption;
};

export type LookupVendorsResponse = LookupVendorsResponses[keyof LookupVendorsResponses];

export type LookupRetailersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/lookups/retailers';
};

export type LookupRetailersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LookupRetailersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceRetailerOption;
};

export type LookupRetailersResponse = LookupRetailersResponses[keyof LookupRetailersResponses];

export type ListCatalogueStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Name or account id contains
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/catalogue/stores';
};

export type ListCatalogueStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListCatalogueStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceCatalogueStoreRead;
};

export type ListCatalogueStoresResponse = ListCatalogueStoresResponses[keyof ListCatalogueStoresResponses];

export type ListCatalogueListingsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store
         */
        store_id: number;
        /**
         * Title, seller SKU or marketplace product id contains
         */
        search?: string | null;
        /**
         * Column to order by
         */
        sort?: 'title' | 'marketplace_sku' | 'marketplace_product_id' | 'condition';
        /**
         * Sort direction
         */
        direction?: 'asc' | 'desc';
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/catalogue/listings';
};

export type ListCatalogueListingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListCatalogueListingsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceListingRead;
};

export type ListCatalogueListingsResponse = ListCatalogueListingsResponses[keyof ListCatalogueListingsResponses];

export type ExportCatalogueListingsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The store
         */
        store_id: number;
        /**
         * Title, seller SKU or marketplace product id contains
         */
        search?: string | null;
        /**
         * Column to order by
         */
        sort?: 'title' | 'marketplace_sku' | 'marketplace_product_id' | 'condition';
        /**
         * Sort direction
         */
        direction?: 'asc' | 'desc';
    };
    url: '/v1/catalogue/listings/export';
};

export type ExportCatalogueListingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportCatalogueListingsResponses = {
    /**
     * Successful Response
     */
    200: Blob | File;
};

export type ExportCatalogueListingsResponse = ExportCatalogueListingsResponses[keyof ExportCatalogueListingsResponses];

export type ListAiListingStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-listing/stores';
};

export type ListAiListingStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type ListAiListingStoresResponses = {
    /**
     * Successful Response
     */
    200: VentawizKitPaginationListResourceStoreRead1;
};

export type ListAiListingStoresResponse = ListAiListingStoresResponses[keyof ListAiListingStoresResponses];

export type ListAiListingJobsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one status
         */
        status?: AiListingJobStatus | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-listing/jobs';
};

export type ListAiListingJobsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type ListAiListingJobsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceJobSummary;
};

export type ListAiListingJobsResponse = ListAiListingJobsResponses[keyof ListAiListingJobsResponses];

export type CreateAiListingJobData = {
    body: JobCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-listing/jobs';
};

export type CreateAiListingJobErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type CreateAiListingJobResponses = {
    /**
     * Successful Response
     */
    201: JobRead;
};

export type CreateAiListingJobResponse = CreateAiListingJobResponses[keyof CreateAiListingJobResponses];

export type GetAiListingJobData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The job
         */
        job_id: string;
    };
    query?: never;
    url: '/v1/ai-listing/jobs/{job_id}';
};

export type GetAiListingJobErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type GetAiListingJobResponses = {
    /**
     * Successful Response
     */
    200: JobRead;
};

export type GetAiListingJobResponse = GetAiListingJobResponses[keyof GetAiListingJobResponses];

export type SaveAiListingSelectionsData = {
    body: ListingSelections;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The job
         */
        job_id: string;
    };
    query?: never;
    url: '/v1/ai-listing/jobs/{job_id}/selections';
};

export type SaveAiListingSelectionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type SaveAiListingSelectionsResponses = {
    /**
     * Successful Response
     */
    200: JobRead;
};

export type SaveAiListingSelectionsResponse = SaveAiListingSelectionsResponses[keyof SaveAiListingSelectionsResponses];

export type RegenerateAiListingImageData = {
    body?: RegenerateImage | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The job
         */
        job_id: string;
        /**
         * The image
         */
        image_id: string;
    };
    query?: never;
    url: '/v1/ai-listing/jobs/{job_id}/images/{image_id}/regenerate';
};

export type RegenerateAiListingImageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type RegenerateAiListingImageResponses = {
    /**
     * Successful Response
     */
    202: RegenerationRead;
};

export type RegenerateAiListingImageResponse = RegenerateAiListingImageResponses[keyof RegenerateAiListingImageResponses];

export type RegenerateAiListingCategoryData = {
    body?: RegenerateImage | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The job
         */
        job_id: string;
        /**
         * The tab
         */
        category: ImageCategory;
    };
    query?: never;
    url: '/v1/ai-listing/jobs/{job_id}/categories/{category}/regenerate';
};

export type RegenerateAiListingCategoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type RegenerateAiListingCategoryResponses = {
    /**
     * Successful Response
     */
    202: RegenerationRead;
};

export type RegenerateAiListingCategoryResponse = RegenerateAiListingCategoryResponses[keyof RegenerateAiListingCategoryResponses];

export type GetAiListingUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-listing/usage';
};

export type GetAiListingUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type GetAiListingUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageRead;
};

export type GetAiListingUsageResponse = GetAiListingUsageResponses[keyof GetAiListingUsageResponses];

export type ProxyAiListingImageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * An Amazon CDN or S3 image URL
         */
        url: string;
    };
    url: '/v1/ai-listing/image-proxy';
};

export type ProxyAiListingImageErrors = {
    /**
     * The URL is not on an allowed host
     */
    400: unknown;
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Today's allowance for this action is used up
     */
    429: unknown;
    /**
     * A third party this request depends on failed
     */
    502: unknown;
};

export type ProxyAiListingImageResponses = {
    /**
     * The image bytes
     */
    200: unknown;
};

export type ReadMyAnalystAccessData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/me';
};

export type ReadMyAnalystAccessErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadMyAnalystAccessResponses = {
    /**
     * Successful Response
     */
    200: AnalystAccessRead;
};

export type ReadMyAnalystAccessResponse = ReadMyAnalystAccessResponses[keyof ReadMyAnalystAccessResponses];

export type ListConversationsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Matches titles and message text
         */
        search?: string | null;
        tag?: string | null;
        /**
         * Only conversations pinned to it
         */
        store_id?: number | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/conversations';
};

export type ListConversationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListConversationsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceConversationRead;
};

export type ListConversationsResponse = ListConversationsResponses[keyof ListConversationsResponses];

export type CreateConversationData = {
    body: ConversationCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/conversations';
};

export type CreateConversationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateConversationResponses = {
    /**
     * Successful Response
     */
    201: ConversationRead;
};

export type CreateConversationResponse = CreateConversationResponses[keyof CreateConversationResponses];

export type BulkDeleteConversationsData = {
    body: BulkDelete;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/conversations/bulk-delete';
};

export type BulkDeleteConversationsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BulkDeleteConversationsResponses = {
    /**
     * Successful Response
     */
    200: BulkDeleteRead;
};

export type BulkDeleteConversationsResponse = BulkDeleteConversationsResponses[keyof BulkDeleteConversationsResponses];

export type DeleteConversationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        conversation_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/conversations/{conversation_id}';
};

export type DeleteConversationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteConversationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteConversationResponse = DeleteConversationResponses[keyof DeleteConversationResponses];

export type UpdateConversationData = {
    body: ConversationUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        conversation_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/conversations/{conversation_id}';
};

export type UpdateConversationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateConversationResponses = {
    /**
     * Successful Response
     */
    200: ConversationRead;
};

export type UpdateConversationResponse = UpdateConversationResponses[keyof UpdateConversationResponses];

export type ReadConversationMessagesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        conversation_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/conversations/{conversation_id}/messages';
};

export type ReadConversationMessagesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadConversationMessagesResponses = {
    /**
     * Successful Response
     */
    200: ConversationDetail;
};

export type ReadConversationMessagesResponse = ReadConversationMessagesResponses[keyof ReadConversationMessagesResponses];

export type ExportConversationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        conversation_id: string;
    };
    query?: {
        format?: ConversationFormat;
    };
    url: '/v1/ai-analyst/conversations/{conversation_id}/export';
};

export type ExportConversationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportConversationResponses = {
    /**
     * The conversation as a document
     */
    200: unknown;
};

export type ResumeConversationStreamData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        conversation_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/conversations/{conversation_id}/stream';
};

export type ResumeConversationStreamErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ResumeConversationStreamResponses = {
    /**
     * AI SDK UI message stream (v1) chunks as server-sent events
     */
    200: unknown;
    /**
     * Nothing in progress
     */
    204: void;
};

export type ResumeConversationStreamResponse = ResumeConversationStreamResponses[keyof ResumeConversationStreamResponses];

export type ReadSharedConversationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        conversation_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/shared/{conversation_id}';
};

export type ReadSharedConversationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadSharedConversationResponses = {
    /**
     * Successful Response
     */
    200: ConversationDetail;
};

export type ReadSharedConversationResponse = ReadSharedConversationResponses[keyof ReadSharedConversationResponses];

export type ChatData = {
    body: ChatRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/chat/stream';
};

export type ChatErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * ``run_in_progress``: an answer is being written in this conversation; ``run_not_replaceable``: the run named to retry is not its latest finished one
     */
    409: ErrorEnvelope;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * ``quota_exceeded``: today's allowance is used up
     */
    429: ErrorEnvelope;
};

export type ChatError = ChatErrors[keyof ChatErrors];

export type ChatResponses = {
    /**
     * AI SDK UI message stream (v1) chunks as server-sent events
     */
    200: unknown;
};

export type ResumeRunStreamData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/runs/{run_id}/stream';
};

export type ResumeRunStreamErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ResumeRunStreamResponses = {
    /**
     * AI SDK UI message stream (v1) chunks as server-sent events
     */
    200: unknown;
    /**
     * Nothing left to replay
     */
    204: void;
};

export type ResumeRunStreamResponse = ResumeRunStreamResponses[keyof ResumeRunStreamResponses];

export type CancelRunData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/runs/{run_id}/cancel';
};

export type CancelRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CancelRunResponses = {
    /**
     * Successful Response
     */
    202: CancelRead;
};

export type CancelRunResponse = CancelRunResponses[keyof CancelRunResponses];

export type RateAnswerData = {
    body: FeedbackWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/runs/{run_id}/feedback';
};

export type RateAnswerErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RateAnswerResponses = {
    /**
     * Successful Response
     */
    200: FeedbackRead | null;
};

export type RateAnswerResponse = RateAnswerResponses[keyof RateAnswerResponses];

export type RecordAnswerSignalData = {
    body: SignalWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/runs/{run_id}/signal';
};

export type RecordAnswerSignalErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordAnswerSignalResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type RecordAnswerSignalResponse = RecordAnswerSignalResponses[keyof RecordAnswerSignalResponses];

export type ReadResultData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        result_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/results/{result_id}';
};

export type ReadResultErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadResultResponses = {
    /**
     * Successful Response
     */
    200: ResultRead;
};

export type ReadResultResponse = ReadResultResponses[keyof ReadResultResponses];

export type ExportResultData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        result_id: string;
    };
    query?: {
        format?: ResultFormat;
    };
    url: '/v1/ai-analyst/results/{result_id}/export';
};

export type ExportResultErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportResultResponses = {
    /**
     * The result with readable headers
     */
    200: unknown;
};

export type ReadTimingData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/timing';
};

export type ReadTimingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadTimingResponses = {
    /**
     * Successful Response
     */
    200: TimingRead;
};

export type ReadTimingResponse = ReadTimingResponses[keyof ReadTimingResponses];

export type ReadScopeOptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/scope-options';
};

export type ReadScopeOptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadScopeOptionsResponses = {
    /**
     * Successful Response
     */
    200: ScopeOptions;
};

export type ReadScopeOptionsResponse = ReadScopeOptionsResponses[keyof ReadScopeOptionsResponses];

export type ReadUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/usage';
};

export type ReadUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadUsageResponses = {
    /**
     * Successful Response
     */
    200: AnalystUsageRead;
};

export type ReadUsageResponse = ReadUsageResponses[keyof ReadUsageResponses];

export type ListMemoryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/memory';
};

export type ListMemoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMemoryResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMemoryRead;
};

export type ListMemoryResponse = ListMemoryResponses[keyof ListMemoryResponses];

export type AddMemoryData = {
    body: MemoryWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/memory';
};

export type AddMemoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AddMemoryResponses = {
    /**
     * Successful Response
     */
    201: MemoryRead;
};

export type AddMemoryResponse = AddMemoryResponses[keyof AddMemoryResponses];

export type DeleteMemoryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        memory_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/memory/{memory_id}';
};

export type DeleteMemoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteMemoryResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteMemoryResponse = DeleteMemoryResponses[keyof DeleteMemoryResponses];

export type ListReportsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/reports';
};

export type ListReportsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReportsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSavedReportRead;
};

export type ListReportsResponse = ListReportsResponses[keyof ListReportsResponses];

export type SaveReportData = {
    body: SavedReportWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/reports';
};

export type SaveReportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SaveReportResponses = {
    /**
     * Successful Response
     */
    201: SavedReportRead;
};

export type SaveReportResponse = SaveReportResponses[keyof SaveReportResponses];

export type CompareReportsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Two of the caller's report ids, comma-separated
         */
        ids: string;
    };
    url: '/v1/ai-analyst/reports/compare';
};

export type CompareReportsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CompareReportsResponses = {
    /**
     * Successful Response
     */
    200: ReportComparison;
};

export type CompareReportsResponse = CompareReportsResponses[keyof CompareReportsResponses];

export type DeleteReportData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        report_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/reports/{report_id}';
};

export type DeleteReportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteReportResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteReportResponse = DeleteReportResponses[keyof DeleteReportResponses];

export type ReadReportData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        report_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/reports/{report_id}';
};

export type ReadReportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReadReportResponses = {
    /**
     * Successful Response
     */
    200: SavedReportRead;
};

export type ReadReportResponse = ReadReportResponses[keyof ReadReportResponses];

export type AdminListQuestionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Runs with at least one flag
         */
        flagged_only?: boolean;
        category?: string | null;
        flag_status?: FlagStatus | null;
        run_status?: AnalystRunStatus | null;
        /**
         * In question or answer
         */
        search?: string | null;
        /**
         * The asker's rating
         */
        rating?: RatingFilter | null;
        /**
         * An implicit signal
         */
        signal?: string | null;
        prompt_version?: string | null;
        variant?: Variant | null;
        clarifying?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/questions';
};

export type AdminListQuestionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminListQuestionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAdminQuestionRead;
};

export type AdminListQuestionsResponse = AdminListQuestionsResponses[keyof AdminListQuestionsResponses];

export type AdminReadRunTraceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/runs/{run_id}';
};

export type AdminReadRunTraceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminReadRunTraceResponses = {
    /**
     * Successful Response
     */
    200: AdminRunTrace;
};

export type AdminReadRunTraceResponse = AdminReadRunTraceResponses[keyof AdminReadRunTraceResponses];

export type AdminUpdateFlagData = {
    body: FlagUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        flag_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/flags/{flag_id}';
};

export type AdminUpdateFlagErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminUpdateFlagResponses = {
    /**
     * Successful Response
     */
    200: AdminFlagRead;
};

export type AdminUpdateFlagResponse = AdminUpdateFlagResponses[keyof AdminUpdateFlagResponses];

export type AdminListUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * How many days back
         */
        days?: number;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/usage';
};

export type AdminListUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminListUsageResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAdminUsageRead;
};

export type AdminListUsageResponse = AdminListUsageResponses[keyof AdminListUsageResponses];

export type AdminResetBudgetData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        principal_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/budgets/{principal_id}';
};

export type AdminResetBudgetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminResetBudgetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminResetBudgetResponse = AdminResetBudgetResponses[keyof AdminResetBudgetResponses];

export type AdminReadBudgetData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        principal_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/budgets/{principal_id}';
};

export type AdminReadBudgetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminReadBudgetResponses = {
    /**
     * Successful Response
     */
    200: AdminBudgetRead;
};

export type AdminReadBudgetResponse = AdminReadBudgetResponses[keyof AdminReadBudgetResponses];

export type AdminSetBudgetData = {
    body: BudgetUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        principal_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/budgets/{principal_id}';
};

export type AdminSetBudgetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminSetBudgetResponses = {
    /**
     * Successful Response
     */
    200: AdminBudgetRead;
};

export type AdminSetBudgetResponse = AdminSetBudgetResponses[keyof AdminSetBudgetResponses];

export type AdminListProviderCooldownsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/admin/providers/cooldowns';
};

export type AdminListProviderCooldownsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Redis could not be read
     */
    502: unknown;
};

export type AdminListProviderCooldownsResponses = {
    /**
     * Successful Response
     */
    200: ProviderCooldowns;
};

export type AdminListProviderCooldownsResponse = AdminListProviderCooldownsResponses[keyof AdminListProviderCooldownsResponses];

export type AdminEndProviderCooldownData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        provider: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/providers/{provider}/cooldown';
};

export type AdminEndProviderCooldownErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * Redis could not be reached
     */
    502: unknown;
};

export type AdminEndProviderCooldownResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminEndProviderCooldownResponse = AdminEndProviderCooldownResponses[keyof AdminEndProviderCooldownResponses];

export type AdminReadOverviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        days?: number;
    };
    url: '/v1/ai-analyst/admin/overview';
};

export type AdminReadOverviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminReadOverviewResponses = {
    /**
     * Successful Response
     */
    200: AdminOverview;
};

export type AdminReadOverviewResponse = AdminReadOverviewResponses[keyof AdminReadOverviewResponses];

export type AdminMineFailuresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * How many days back
         */
        days?: number;
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/mining/failures';
};

export type AdminMineFailuresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminMineFailuresResponses = {
    /**
     * Successful Response
     */
    200: FailureGroups;
};

export type AdminMineFailuresResponse = AdminMineFailuresResponses[keyof AdminMineFailuresResponses];

export type AdminMineSqlTopicsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * How many days back
         */
        days?: number;
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/mining/sql-topics';
};

export type AdminMineSqlTopicsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminMineSqlTopicsResponses = {
    /**
     * Successful Response
     */
    200: SqlTopics;
};

export type AdminMineSqlTopicsResponse = AdminMineSqlTopicsResponses[keyof AdminMineSqlTopicsResponses];

export type AdminMineUnmatchedTermsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * How many days back
         */
        days?: number;
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/mining/unmatched-terms';
};

export type AdminMineUnmatchedTermsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminMineUnmatchedTermsResponses = {
    /**
     * Successful Response
     */
    200: UnmatchedTerms;
};

export type AdminMineUnmatchedTermsResponse = AdminMineUnmatchedTermsResponses[keyof AdminMineUnmatchedTermsResponses];

export type AdminPromoteRunData = {
    body: PromoteWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/runs/{run_id}/promote';
};

export type AdminPromoteRunErrors = {
    /**
     * ``query_method_invalid``: the reference does not compile
     */
    400: unknown;
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminPromoteRunResponses = {
    /**
     * Successful Response
     */
    201: PromotedRead;
};

export type AdminPromoteRunResponse = AdminPromoteRunResponses[keyof AdminPromoteRunResponses];

export type AdminSuggestFixData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        run_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/runs/{run_id}/suggest-fix';
};

export type AdminSuggestFixErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * The model could not draft one
     */
    502: unknown;
    /**
     * ``suggestion_timed_out``: no draft in time
     */
    504: unknown;
};

export type AdminSuggestFixResponses = {
    /**
     * Successful Response
     */
    200: SuggestedFix;
};

export type AdminSuggestFixResponse = AdminSuggestFixResponses[keyof AdminSuggestFixResponses];

export type AdminListEvalQuestionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/evals/questions';
};

export type AdminListEvalQuestionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminListEvalQuestionsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAdminEvalQuestionRead;
};

export type AdminListEvalQuestionsResponse = AdminListEvalQuestionsResponses[keyof AdminListEvalQuestionsResponses];

export type AdminDeleteEvalQuestionData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        question_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/evals/questions/{question_id}';
};

export type AdminDeleteEvalQuestionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminDeleteEvalQuestionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteEvalQuestionResponse = AdminDeleteEvalQuestionResponses[keyof AdminDeleteEvalQuestionResponses];

export type AdminSetEvalQuestionActiveData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        question_id: string;
    };
    query: {
        active: boolean;
    };
    url: '/v1/ai-analyst/admin/evals/questions/{question_id}';
};

export type AdminSetEvalQuestionActiveErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminSetEvalQuestionActiveResponses = {
    /**
     * Successful Response
     */
    200: AdminEvalQuestionRead;
};

export type AdminSetEvalQuestionActiveResponse = AdminSetEvalQuestionActiveResponses[keyof AdminSetEvalQuestionActiveResponses];

export type AdminListEvalRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/evals/runs';
};

export type AdminListEvalRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminListEvalRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAdminEvalRunRead;
};

export type AdminListEvalRunsResponse = AdminListEvalRunsResponses[keyof AdminListEvalRunsResponses];

export type AdminListVerifiedData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/verified';
};

export type AdminListVerifiedErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminListVerifiedResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVerifiedAnswerRead;
};

export type AdminListVerifiedResponse = AdminListVerifiedResponses[keyof AdminListVerifiedResponses];

export type AdminCreateVerifiedData = {
    body: VerifiedAnswerWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/admin/verified';
};

export type AdminCreateVerifiedErrors = {
    /**
     * ``query_method_invalid``: the method does not compile
     */
    400: unknown;
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminCreateVerifiedResponses = {
    /**
     * Successful Response
     */
    201: VerifiedAnswerRead;
};

export type AdminCreateVerifiedResponse = AdminCreateVerifiedResponses[keyof AdminCreateVerifiedResponses];

export type AdminDeleteVerifiedData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        verified_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/verified/{verified_id}';
};

export type AdminDeleteVerifiedErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminDeleteVerifiedResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteVerifiedResponse = AdminDeleteVerifiedResponses[keyof AdminDeleteVerifiedResponses];

export type AdminUpdateVerifiedData = {
    body: VerifiedAnswerWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        verified_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/verified/{verified_id}';
};

export type AdminUpdateVerifiedErrors = {
    /**
     * ``query_method_invalid``: the method does not compile
     */
    400: unknown;
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminUpdateVerifiedResponses = {
    /**
     * Successful Response
     */
    200: VerifiedAnswerRead;
};

export type AdminUpdateVerifiedResponse = AdminUpdateVerifiedResponses[keyof AdminUpdateVerifiedResponses];

export type AdminListAliasesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/ai-analyst/admin/aliases';
};

export type AdminListAliasesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminListAliasesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceAliasRead;
};

export type AdminListAliasesResponse = AdminListAliasesResponses[keyof AdminListAliasesResponses];

export type AdminCreateAliasData = {
    body: AliasWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/ai-analyst/admin/aliases';
};

export type AdminCreateAliasErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminCreateAliasResponses = {
    /**
     * Successful Response
     */
    201: AliasRead;
};

export type AdminCreateAliasResponse = AdminCreateAliasResponses[keyof AdminCreateAliasResponses];

export type AdminDeleteAliasData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        alias_id: string;
    };
    query?: never;
    url: '/v1/ai-analyst/admin/aliases/{alias_id}';
};

export type AdminDeleteAliasErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AdminDeleteAliasResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteAliasResponse = AdminDeleteAliasResponses[keyof AdminDeleteAliasResponses];

export type ListStockPushStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/stock-push/stores';
};

export type ListStockPushStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListStockPushStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceStockPushStoreRead;
};

export type ListStockPushStoresResponse = ListStockPushStoresResponses[keyof ListStockPushStoresResponses];

export type PushStoreStockData = {
    body: PushRequestBody;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/stock-push/stores/{store_id}/push';
};

export type PushStoreStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PushStoreStockResponses = {
    /**
     * Successful Response
     */
    202: PushAccepted;
};

export type PushStoreStockResponse = PushStoreStockResponses[keyof PushStoreStockResponses];

export type PushStockSheetData = {
    body: BodyPushStockSheet;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/stock-push/stores/{store_id}/sheet';
};

export type PushStockSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PushStockSheetResponses = {
    /**
     * Successful Response
     */
    202: SheetRead;
};

export type PushStockSheetResponse = PushStockSheetResponses[keyof PushStockSheetResponses];

export type DownloadStockSheetTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stock-push/sheet-template';
};

export type DownloadStockSheetTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadStockSheetTemplateResponses = {
    /**
     * An .xlsx with the headers
     */
    200: unknown;
};

export type ListStockPushRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        request_id?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/stock-push/stores/{store_id}/runs';
};

export type ListStockPushRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListStockPushRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceStockPushRunRead;
};

export type ListStockPushRunsResponse = ListStockPushRunsResponses[keyof ListStockPushRunsResponses];

export type GetStockPushRunData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
        run_id: string;
    };
    query?: never;
    url: '/v1/stock-push/stores/{store_id}/runs/{run_id}';
};

export type GetStockPushRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetStockPushRunResponses = {
    /**
     * Successful Response
     */
    200: StockPushRunDetail;
};

export type GetStockPushRunResponse = GetStockPushRunResponses[keyof GetStockPushRunResponses];

export type StockSheetTemplateColumnsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stock-push/sheet-template/columns';
};

export type StockSheetTemplateColumnsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StockSheetTemplateColumnsResponses = {
    /**
     * Successful Response
     */
    200: Array<SheetColumnRead>;
};

export type StockSheetTemplateColumnsResponse = StockSheetTemplateColumnsResponses[keyof StockSheetTemplateColumnsResponses];

export type ListPricePushStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/price-push/stores';
};

export type ListPricePushStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListPricePushStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourcePricePushStoreRead;
};

export type ListPricePushStoresResponse = ListPricePushStoresResponses[keyof ListPricePushStoresResponses];

export type DownloadPriceSheetTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/price-push/sheet-template';
};

export type DownloadPriceSheetTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DownloadPriceSheetTemplateResponses = {
    /**
     * An .xlsx with the headers
     */
    200: unknown;
};

export type PushPriceSheetData = {
    body: BodyPushPriceSheet;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/price-push/stores/{store_id}/sheet';
};

export type PushPriceSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PushPriceSheetResponses = {
    /**
     * Successful Response
     */
    202: PriceSheetRead;
};

export type PushPriceSheetResponse = PushPriceSheetResponses[keyof PushPriceSheetResponses];

export type ListPricePushRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        request_id?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/price-push/stores/{store_id}/runs';
};

export type ListPricePushRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListPricePushRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourcePricePushRunRead;
};

export type ListPricePushRunsResponse = ListPricePushRunsResponses[keyof ListPricePushRunsResponses];

export type GetPricePushRunData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
        run_id: string;
    };
    query?: never;
    url: '/v1/price-push/stores/{store_id}/runs/{run_id}';
};

export type GetPricePushRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetPricePushRunResponses = {
    /**
     * Successful Response
     */
    200: PricePushRunDetail;
};

export type GetPricePushRunResponse = GetPricePushRunResponses[keyof GetPricePushRunResponses];

export type ListShopifySyncStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/shopify-sync/stores';
};

export type ListShopifySyncStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListShopifySyncStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceShopifySyncStoreRead;
};

export type ListShopifySyncStoresResponse = ListShopifySyncStoresResponses[keyof ListShopifySyncStoresResponses];

export type StartShopifySyncData = {
    body: ShopifySyncCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/shopify-sync/stores/{store_id}/syncs';
};

export type StartShopifySyncErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StartShopifySyncResponses = {
    /**
     * Successful Response
     */
    202: ShopifySyncAccepted;
};

export type StartShopifySyncResponse = StartShopifySyncResponses[keyof StartShopifySyncResponses];

export type ListShopifySyncRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        request_id?: string | null;
        kind?: ShopifySyncKind | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/shopify-sync/stores/{store_id}/runs';
};

export type ListShopifySyncRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListShopifySyncRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceShopifySyncRunRead;
};

export type ListShopifySyncRunsResponse = ListShopifySyncRunsResponses[keyof ListShopifySyncRunsResponses];

export type GetShopifySyncRunData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
        run_id: string;
    };
    query?: never;
    url: '/v1/shopify-sync/stores/{store_id}/runs/{run_id}';
};

export type GetShopifySyncRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetShopifySyncRunResponses = {
    /**
     * Successful Response
     */
    200: ShopifySyncRunDetail;
};

export type GetShopifySyncRunResponse = GetShopifySyncRunResponses[keyof GetShopifySyncRunResponses];

export type ListMcfStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/mcf/stores';
};

export type ListMcfStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMcfStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMcfStoreRead;
};

export type ListMcfStoresResponse = ListMcfStoresResponses[keyof ListMcfStoresResponses];

export type PreviewMcfOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/mcf/stores/{store_id}/preview';
};

export type PreviewMcfOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewMcfOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMcfPreviewOrderRead;
};

export type PreviewMcfOrdersResponse = PreviewMcfOrdersResponses[keyof PreviewMcfOrdersResponses];

export type ListMcfOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        status?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/mcf/stores/{store_id}/orders';
};

export type ListMcfOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMcfOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMcfOrderRead;
};

export type ListMcfOrdersResponse = ListMcfOrdersResponses[keyof ListMcfOrdersResponses];

export type CreateMcfOrdersData = {
    body: McfCreateRequest;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/mcf/stores/{store_id}/orders';
};

export type CreateMcfOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * confirm was not true
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateMcfOrdersResponses = {
    /**
     * Successful Response
     */
    202: McfCreateAccepted;
};

export type CreateMcfOrdersResponse = CreateMcfOrdersResponses[keyof CreateMcfOrdersResponses];

export type SyncMcfStatusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/mcf/stores/{store_id}/status-sync';
};

export type SyncMcfStatusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SyncMcfStatusResponses = {
    /**
     * Successful Response
     */
    202: McfStatusSyncAccepted;
};

export type SyncMcfStatusResponse = SyncMcfStatusResponses[keyof SyncMcfStatusResponses];

export type ExportMcfOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/mcf/stores/{store_id}/orders/export';
};

export type ExportMcfOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportMcfOrdersResponses = {
    /**
     * One row per order line
     */
    200: unknown;
};

export type ListMcfRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        request_id?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/mcf/stores/{store_id}/runs';
};

export type ListMcfRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMcfRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMcfRunRead;
};

export type ListMcfRunsResponse = ListMcfRunsResponses[keyof ListMcfRunsResponses];

export type GetMcfRunData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
        run_id: string;
    };
    query?: never;
    url: '/v1/mcf/stores/{store_id}/runs/{run_id}';
};

export type GetMcfRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMcfRunResponses = {
    /**
     * Successful Response
     */
    200: McfRunDetail;
};

export type GetMcfRunResponse = GetMcfRunResponses[keyof GetMcfRunResponses];

export type ListTiktokStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tiktok-sync/stores';
};

export type ListTiktokStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTiktokStoresResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTikTokStoreRead;
};

export type ListTiktokStoresResponse = ListTiktokStoresResponses[keyof ListTiktokStoresResponses];

export type SyncTiktokProductsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/tiktok-sync/stores/{store_id}/products-sync';
};

export type SyncTiktokProductsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SyncTiktokProductsResponses = {
    /**
     * Successful Response
     */
    202: TikTokAccepted;
};

export type SyncTiktokProductsResponse = SyncTiktokProductsResponses[keyof SyncTiktokProductsResponses];

export type SyncTiktokOrdersData = {
    body: OrdersSyncRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/tiktok-sync/stores/{store_id}/orders-sync';
};

export type SyncTiktokOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SyncTiktokOrdersResponses = {
    /**
     * Successful Response
     */
    202: TikTokAccepted;
};

export type SyncTiktokOrdersResponse = SyncTiktokOrdersResponses[keyof SyncTiktokOrdersResponses];

export type CorrectTiktokTrackingData = {
    body: TrackingCorrectionRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: never;
    url: '/v1/tiktok-sync/stores/{store_id}/tracking-corrections';
};

export type CorrectTiktokTrackingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CorrectTiktokTrackingResponses = {
    /**
     * Successful Response
     */
    202: TikTokAccepted;
};

export type CorrectTiktokTrackingResponse = CorrectTiktokTrackingResponses[keyof CorrectTiktokTrackingResponses];

export type ListTiktokRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
    };
    query?: {
        request_id?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/tiktok-sync/stores/{store_id}/runs';
};

export type ListTiktokRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListTiktokRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceTikTokRunRead;
};

export type ListTiktokRunsResponse = ListTiktokRunsResponses[keyof ListTiktokRunsResponses];

export type GetTiktokRunData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        store_id: number;
        run_id: string;
    };
    query?: never;
    url: '/v1/tiktok-sync/stores/{store_id}/runs/{run_id}';
};

export type GetTiktokRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetTiktokRunResponses = {
    /**
     * Successful Response
     */
    200: TikTokRunRead;
};

export type GetTiktokRunResponse = GetTiktokRunResponses[keyof GetTiktokRunResponses];

export type ListConnectionSpecsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/connection-specs';
};

export type ListConnectionSpecsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListConnectionSpecsResponses = {
    /**
     * Successful Response
     */
    200: ConnectionSpecList;
};

export type ListConnectionSpecsResponse = ListConnectionSpecsResponses[keyof ListConnectionSpecsResponses];

export type ListCarrierSpecsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/carrier-specs';
};

export type ListCarrierSpecsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListCarrierSpecsResponses = {
    /**
     * Successful Response
     */
    200: CarrierSpecList;
};

export type ListCarrierSpecsResponse = ListCarrierSpecsResponses[keyof ListCarrierSpecsResponses];

export type GetMarketplaceConnectionSpecData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        marketplace_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{marketplace_id}/connection-spec';
};

export type GetMarketplaceConnectionSpecErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetMarketplaceConnectionSpecResponses = {
    /**
     * Successful Response
     */
    200: ConnectionSpec;
};

export type GetMarketplaceConnectionSpecResponse = GetMarketplaceConnectionSpecResponses[keyof GetMarketplaceConnectionSpecResponses];

export type GetMarketplaceStreamSpecData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        marketplace_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{marketplace_id}/stream-spec';
};

export type GetMarketplaceStreamSpecErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetMarketplaceStreamSpecResponses = {
    /**
     * Successful Response
     */
    200: StreamSpecList;
};

export type GetMarketplaceStreamSpecResponse = GetMarketplaceStreamSpecResponses[keyof GetMarketplaceStreamSpecResponses];

export type DiscoverOnboardingStreamsData = {
    body: StreamDiscoverRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stores/onboarding/streams/discover';
};

export type DiscoverOnboardingStreamsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type DiscoverOnboardingStreamsResponses = {
    /**
     * Successful Response
     */
    200: StreamDiscovery;
};

export type DiscoverOnboardingStreamsResponse = DiscoverOnboardingStreamsResponses[keyof DiscoverOnboardingStreamsResponses];

export type VerifyOnboardingCredentialsData = {
    body: VerifyRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stores/onboarding/verify';
};

export type VerifyOnboardingCredentialsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type VerifyOnboardingCredentialsResponses = {
    /**
     * Successful Response
     */
    200: VerifyResult;
};

export type VerifyOnboardingCredentialsResponse = VerifyOnboardingCredentialsResponses[keyof VerifyOnboardingCredentialsResponses];

export type OnboardStoresData = {
    body: OnboardingRequest;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stores/onboarding';
};

export type OnboardStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type OnboardStoresResponses = {
    /**
     * Successful Response
     */
    201: OnboardingResult;
};

export type OnboardStoresResponse = OnboardStoresResponses[keyof OnboardStoresResponses];

export type FindOnboardingDuplicatesData = {
    body: DuplicateQuery;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stores/onboarding/duplicates';
};

export type FindOnboardingDuplicatesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type FindOnboardingDuplicatesResponses = {
    /**
     * Successful Response
     */
    200: DuplicateMatches;
};

export type FindOnboardingDuplicatesResponse = FindOnboardingDuplicatesResponses[keyof FindOnboardingDuplicatesResponses];

export type ListStoreOverviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Substring of code, name or account id
         */
        search?: string | null;
        marketplace_id?: number | null;
        kind?: StoreKind | null;
        /**
         * Holding a credential expiring this soon
         */
        expiring_within_days?: number | null;
        write_locked?: boolean | null;
        /**
         * Some API failing or missing
         */
        unhealthy?: boolean | null;
        is_active?: boolean | null;
        /**
         * Channel family, e.g. AZ: one platform's tab
         */
        family?: string | null;
        /**
         * Carrying this tag
         */
        tag?: string | null;
        /**
         * In this store group (its id, from /v1/store-groups)
         */
        group?: number | null;
        /**
         * Selling this brand
         */
        brand_id?: number | null;
        /**
         * Linked to this warehouse, at any priority
         */
        warehouse_id?: number | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/store-overview';
};

export type ListStoreOverviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreOverviewResponses = {
    /**
     * Successful Response
     */
    200: ListResourceStoreOverview;
};

export type ListStoreOverviewResponse = ListStoreOverviewResponses[keyof ListStoreOverviewResponses];

export type ListStorePlatformsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        is_active?: boolean | null;
        /**
         * Selling this brand
         */
        brand_id?: number | null;
        /**
         * Linked to this warehouse, at any priority
         */
        warehouse_id?: number | null;
    };
    url: '/v1/store-platforms';
};

export type ListStorePlatformsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStorePlatformsResponses = {
    /**
     * Successful Response
     */
    200: PlatformCounts;
};

export type ListStorePlatformsResponse = ListStorePlatformsResponses[keyof ListStorePlatformsResponses];

export type GetStoreOverviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/store-overview/{store_id}';
};

export type GetStoreOverviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreOverviewResponses = {
    /**
     * Successful Response
     */
    200: StoreOverview;
};

export type GetStoreOverviewResponse = GetStoreOverviewResponses[keyof GetStoreOverviewResponses];

export type DeactivateStoreData = {
    body: DeactivateRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/deactivate';
};

export type DeactivateStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type DeactivateStoreResponses = {
    /**
     * Successful Response
     */
    200: StoreLifecycleResult;
};

export type DeactivateStoreResponse = DeactivateStoreResponses[keyof DeactivateStoreResponses];

export type ReactivateStoreData = {
    body: ReactivateRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/reactivate';
};

export type ReactivateStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ReactivateStoreResponses = {
    /**
     * Successful Response
     */
    200: StoreLifecycleResult;
};

export type ReactivateStoreResponse = ReactivateStoreResponses[keyof ReactivateStoreResponses];

export type GetStoreDeletionPreviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/deletion-preview';
};

export type GetStoreDeletionPreviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * registry:delete is not held tenant-wide
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreDeletionPreviewResponses = {
    /**
     * Successful Response
     */
    200: DeletionPreview;
};

export type GetStoreDeletionPreviewResponse = GetStoreDeletionPreviewResponses[keyof GetStoreDeletionPreviewResponses];

export type StartStoreDeletionData = {
    body: DeletionRequest;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/deletion';
};

export type StartStoreDeletionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * registry:delete missing, or the confirmation was refused
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * The store is already being deleted
     */
    409: unknown;
    /**
     * The typed code is not the store's
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type StartStoreDeletionResponses = {
    /**
     * Successful Response
     */
    202: DeletionStarted;
};

export type StartStoreDeletionResponse = StartStoreDeletionResponses[keyof StartStoreDeletionResponses];

export type GetStoreDeletionData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The deletion's id, from the 202
         */
        deletion_id: number;
    };
    query?: never;
    url: '/v1/stores/deletions/{deletion_id}';
};

export type GetStoreDeletionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * registry:delete is not held tenant-wide
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreDeletionResponses = {
    /**
     * Successful Response
     */
    200: StoreDeletionRead;
};

export type GetStoreDeletionResponse = GetStoreDeletionResponses[keyof GetStoreDeletionResponses];

export type ListStoreStreamsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/streams';
};

export type ListStoreStreamsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreStreamsResponses = {
    /**
     * Successful Response
     */
    200: StoreStreams;
};

export type ListStoreStreamsResponse = ListStoreStreamsResponses[keyof ListStoreStreamsResponses];

export type UpdateStoreStreamsData = {
    body: StreamsUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/streams';
};

export type UpdateStoreStreamsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type UpdateStoreStreamsResponses = {
    /**
     * Successful Response
     */
    200: StoreStreamsUpdated;
};

export type UpdateStoreStreamsResponse = UpdateStoreStreamsResponses[keyof UpdateStoreStreamsResponses];

export type ReconcileStoreStreamsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/streams/reconcile';
};

export type ReconcileStoreStreamsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ReconcileStoreStreamsResponses = {
    /**
     * Successful Response
     */
    200: ReconcileResult;
};

export type ReconcileStoreStreamsResponse = ReconcileStoreStreamsResponses[keyof ReconcileStoreStreamsResponses];

export type UpdateStoreConnectionData = {
    body: ConnectionUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The auth surface, e.g. sp_api
         */
        surface: AuthSurface;
    };
    query?: never;
    url: '/v1/stores/{store_id}/connections/{surface}';
};

export type UpdateStoreConnectionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type UpdateStoreConnectionResponses = {
    /**
     * Successful Response
     */
    200: StoreOverview;
};

export type UpdateStoreConnectionResponse = UpdateStoreConnectionResponses[keyof UpdateStoreConnectionResponses];

export type RotateStoreConnectionData = {
    body: RotateRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The auth surface, e.g. sp_api
         */
        surface: AuthSurface;
    };
    query?: never;
    url: '/v1/stores/{store_id}/connections/{surface}';
};

export type RotateStoreConnectionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type RotateStoreConnectionResponses = {
    /**
     * Successful Response
     */
    200: RotateResult;
};

export type RotateStoreConnectionResponse = RotateStoreConnectionResponses[keyof RotateStoreConnectionResponses];

export type ListConnectionSiblingsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The auth surface, e.g. sp_api
         */
        surface: AuthSurface;
    };
    query?: never;
    url: '/v1/stores/{store_id}/connections/{surface}/siblings';
};

export type ListConnectionSiblingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListConnectionSiblingsResponses = {
    /**
     * Successful Response
     */
    200: Siblings;
};

export type ListConnectionSiblingsResponse = ListConnectionSiblingsResponses[keyof ListConnectionSiblingsResponses];

export type VerifyStoreConnectionData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The auth surface, e.g. sp_api
         */
        surface: AuthSurface;
    };
    query?: never;
    url: '/v1/stores/{store_id}/connections/{surface}/verify';
};

export type VerifyStoreConnectionErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type VerifyStoreConnectionResponses = {
    /**
     * Successful Response
     */
    200: ProbeResult;
};

export type VerifyStoreConnectionResponse = VerifyStoreConnectionResponses[keyof VerifyStoreConnectionResponses];

export type SetStoreWriteLockData = {
    body: WriteLockUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The auth surface, e.g. sp_api
         */
        surface: AuthSurface;
    };
    query?: never;
    url: '/v1/stores/{store_id}/write-locks/{surface}';
};

export type SetStoreWriteLockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type SetStoreWriteLockResponses = {
    /**
     * Successful Response
     */
    200: WriteLockRead;
};

export type SetStoreWriteLockResponse = SetStoreWriteLockResponses[keyof SetStoreWriteLockResponses];

export type ListStoreActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/stores/{store_id}/activity';
};

export type ListStoreActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListStoreActivityResponse = ListStoreActivityResponses[keyof ListStoreActivityResponses];

export type ListStoreWritesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: {
        limit?: number;
        outcome?: WriteOutcome | null;
        connector?: string | null;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
        /**
         * With target_id: writes that addressed it
         */
        target_kind?: TargetKind | null;
        target_id?: string | null;
    };
    url: '/v1/stores/{store_id}/writes';
};

export type ListStoreWritesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreWritesResponses = {
    /**
     * Successful Response
     */
    200: WriteList;
};

export type ListStoreWritesResponse = ListStoreWritesResponses[keyof ListStoreWritesResponses];

export type ListStoreWriteFreezesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/write-freezes';
};

export type ListStoreWriteFreezesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreWriteFreezesResponses = {
    /**
     * Successful Response
     */
    200: WriteFreezeList;
};

export type ListStoreWriteFreezesResponse = ListStoreWriteFreezesResponses[keyof ListStoreWriteFreezesResponses];

export type CreateStoreWriteFreezeData = {
    body: WriteFreezeCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/write-freezes';
};

export type CreateStoreWriteFreezeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Another freeze shares a surface and a moment with it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type CreateStoreWriteFreezeResponses = {
    /**
     * Successful Response
     */
    201: WriteFreezeRead;
};

export type CreateStoreWriteFreezeResponse = CreateStoreWriteFreezeResponses[keyof CreateStoreWriteFreezeResponses];

export type CancelStoreWriteFreezeData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The write freeze's id
         */
        freeze_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/write-freezes/{freeze_id}';
};

export type CancelStoreWriteFreezeErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * The freeze has already ended
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type CancelStoreWriteFreezeResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type CancelStoreWriteFreezeResponse = CancelStoreWriteFreezeResponses[keyof CancelStoreWriteFreezeResponses];

export type ListAlertsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/alerts';
};

export type ListAlertsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListAlertsResponses = {
    /**
     * Successful Response
     */
    200: AlertList;
};

export type ListAlertsResponse = ListAlertsResponses[keyof ListAlertsResponses];

export type ListAlertRoutesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/alert-routes';
};

export type ListAlertRoutesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListAlertRoutesResponses = {
    /**
     * Successful Response
     */
    200: AlertRouteList;
};

export type ListAlertRoutesResponse = ListAlertRoutesResponses[keyof ListAlertRoutesResponses];

export type ReplaceAlertRoutesData = {
    body: AlertRoutesUpdate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/alert-routes';
};

export type ReplaceAlertRoutesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ReplaceAlertRoutesResponses = {
    /**
     * Successful Response
     */
    200: AlertRouteList;
};

export type ReplaceAlertRoutesResponse = ReplaceAlertRoutesResponses[keyof ReplaceAlertRoutesResponses];

export type GetAlertSettingsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/settings/alerts';
};

export type GetAlertSettingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetAlertSettingsResponses = {
    /**
     * Successful Response
     */
    200: AlertSettingsRead;
};

export type GetAlertSettingsResponse = GetAlertSettingsResponses[keyof GetAlertSettingsResponses];

export type UpdateAlertSettingsData = {
    body: AlertSettingsWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/settings/alerts';
};

export type UpdateAlertSettingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type UpdateAlertSettingsResponses = {
    /**
     * Successful Response
     */
    200: AlertSettingsRead;
};

export type UpdateAlertSettingsResponse = UpdateAlertSettingsResponses[keyof UpdateAlertSettingsResponses];

export type GetStoreSettingsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/settings';
};

export type GetStoreSettingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreSettingsResponses = {
    /**
     * Successful Response
     */
    200: StoreSettingsRead;
};

export type GetStoreSettingsResponse = GetStoreSettingsResponses[keyof GetStoreSettingsResponses];

export type UpdateStoreSettingsData = {
    body: StoreSettingsUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/settings';
};

export type UpdateStoreSettingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type UpdateStoreSettingsResponses = {
    /**
     * Successful Response
     */
    200: StoreSettingsUpdated;
};

export type UpdateStoreSettingsResponse = UpdateStoreSettingsResponses[keyof UpdateStoreSettingsResponses];

export type ListStoreAdsProfilesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/ads/profiles';
};

export type ListStoreAdsProfilesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreAdsProfilesResponses = {
    /**
     * Successful Response
     */
    200: AdsProfiles;
};

export type ListStoreAdsProfilesResponse = ListStoreAdsProfilesResponses[keyof ListStoreAdsProfilesResponses];

export type RefreshStoreAccountMetadataData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/account-metadata/refresh';
};

export type RefreshStoreAccountMetadataErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type RefreshStoreAccountMetadataResponses = {
    /**
     * Successful Response
     */
    200: AdsAccountMeta;
};

export type RefreshStoreAccountMetadataResponse = RefreshStoreAccountMetadataResponses[keyof RefreshStoreAccountMetadataResponses];

export type ListStoreNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/stores/{store_id}/notes';
};

export type ListStoreNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListStoreNotesResponse = ListStoreNotesResponses[keyof ListStoreNotesResponses];

export type CreateStoreNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/notes';
};

export type CreateStoreNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type CreateStoreNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateStoreNoteResponse = CreateStoreNoteResponses[keyof CreateStoreNoteResponses];

export type DeleteStoreNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/notes/{note_id}';
};

export type DeleteStoreNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type DeleteStoreNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteStoreNoteResponse = DeleteStoreNoteResponses[keyof DeleteStoreNoteResponses];

export type UpdateStoreNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/notes/{note_id}';
};

export type UpdateStoreNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type UpdateStoreNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateStoreNoteResponse = UpdateStoreNoteResponses[keyof UpdateStoreNoteResponses];

export type ListStoreGroupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        kind?: StoreGroupKind | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/store-groups';
};

export type ListStoreGroupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreGroupsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceStoreGroupRead;
};

export type ListStoreGroupsResponse = ListStoreGroupsResponses[keyof ListStoreGroupsResponses];

export type CreateStoreGroupData = {
    body: StoreGroupCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/store-groups';
};

export type CreateStoreGroupErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type CreateStoreGroupResponses = {
    /**
     * Successful Response
     */
    201: StoreGroupRead;
};

export type CreateStoreGroupResponse = CreateStoreGroupResponses[keyof CreateStoreGroupResponses];

export type GetStoreHealthData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/health';
};

export type GetStoreHealthErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreHealthResponses = {
    /**
     * Successful Response
     */
    200: StoreHealth;
};

export type GetStoreHealthResponse = GetStoreHealthResponses[keyof GetStoreHealthResponses];

export type GetStoreFreshnessData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/health/freshness';
};

export type GetStoreFreshnessErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreFreshnessResponses = {
    /**
     * Successful Response
     */
    200: VentawizCredentialsSchemasStoreFreshness;
};

export type GetStoreFreshnessResponse = GetStoreFreshnessResponses[keyof GetStoreFreshnessResponses];

export type ListStoreRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The store's step state
         */
        state?: RunState | null;
        entity?: string | null;
    };
    url: '/v1/stores/{store_id}/health/runs';
};

export type ListStoreRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreRunsResponses = {
    /**
     * Successful Response
     */
    200: StoreRuns;
};

export type ListStoreRunsResponse = ListStoreRunsResponses[keyof ListStoreRunsResponses];

export type SyncStoreData = {
    body: SyncRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/health/sync';
};

export type SyncStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * A direct sync was refused for the store's state
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * dagster_unavailable: nothing was started
     */
    503: unknown;
};

export type SyncStoreResponses = {
    /**
     * Successful Response
     */
    200: LaunchedRun;
};

export type SyncStoreResponse = SyncStoreResponses[keyof SyncStoreResponses];

export type BackfillStoreData = {
    body: BackfillRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/health/backfill';
};

export type BackfillStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * dagster_unavailable: nothing was started
     */
    503: unknown;
};

export type BackfillStoreResponses = {
    /**
     * Successful Response
     */
    200: LaunchedRun;
};

export type BackfillStoreResponse = BackfillStoreResponses[keyof BackfillStoreResponses];

export type ListStoreCircuitsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/health/circuits';
};

export type ListStoreCircuitsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreCircuitsResponses = {
    /**
     * Successful Response
     */
    200: StoreCircuits;
};

export type ListStoreCircuitsResponse = ListStoreCircuitsResponses[keyof ListStoreCircuitsResponses];

export type ResetStoreCircuitData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
        /**
         * The connector, e.g. amazon_seller
         */
        connector: string;
    };
    query?: never;
    url: '/v1/stores/{store_id}/health/circuits/{connector}/reset';
};

export type ResetStoreCircuitErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ResetStoreCircuitResponses = {
    /**
     * Successful Response
     */
    200: CircuitReset;
};

export type ResetStoreCircuitResponse = ResetStoreCircuitResponses[keyof ResetStoreCircuitResponses];

export type GetStoreExportDomainsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/export-domains';
};

export type GetStoreExportDomainsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreExportDomainsResponses = {
    /**
     * Successful Response
     */
    200: ExportDomains;
};

export type GetStoreExportDomainsResponse = GetStoreExportDomainsResponses[keyof GetStoreExportDomainsResponses];

export type ListStoreExportsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/exports';
};

export type ListStoreExportsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ListStoreExportsResponses = {
    /**
     * Successful Response
     */
    200: StoreExportList;
};

export type ListStoreExportsResponse = ListStoreExportsResponses[keyof ListStoreExportsResponses];

export type StartStoreExportData = {
    body: ExportRequest;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/exports';
};

export type StartStoreExportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * A domain with nothing to export
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type StartStoreExportResponses = {
    /**
     * Successful Response
     */
    202: ExportStarted;
};

export type StartStoreExportResponse = StartStoreExportResponses[keyof StartStoreExportResponses];

export type GetStoreExportData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The export's id, from the 202
         */
        export_id: number;
    };
    query?: never;
    url: '/v1/exports/{export_id}';
};

export type GetStoreExportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreExportResponses = {
    /**
     * Successful Response
     */
    200: StoreExportRead;
};

export type GetStoreExportResponse = GetStoreExportResponses[keyof GetStoreExportResponses];

export type DownloadStoreExportData = {
    body?: never;
    path: {
        /**
         * The export's id, from the 202
         */
        export_id: number;
    };
    query: {
        /**
         * From download_url
         */
        token: string;
    };
    url: '/v1/exports/{export_id}/download';
};

export type DownloadStoreExportErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * download_link_invalid: forged, expired or no longer ready
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type DownloadStoreExportResponses = {
    /**
     * The zip
     */
    200: Blob | File;
};

export type DownloadStoreExportResponse = DownloadStoreExportResponses[keyof DownloadStoreExportResponses];

export type ExportStoreAuditData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query: {
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from: string;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to: string;
    };
    url: '/v1/stores/{store_id}/audit-export';
};

export type ExportStoreAuditErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type ExportStoreAuditResponses = {
    /**
     * The CSV
     */
    200: unknown;
};

export type GetStoreCloneTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/clone-template';
};

export type GetStoreCloneTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreCloneTemplateResponses = {
    /**
     * Successful Response
     */
    200: CloneTemplate;
};

export type GetStoreCloneTemplateResponse = GetStoreCloneTemplateResponses[keyof GetStoreCloneTemplateResponses];

export type GetStoreChecklistData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/checklist';
};

export type GetStoreChecklistErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
    /**
     * VentaWiz-Ingest could not be reached to test credentials
     */
    502: unknown;
    /**
     * No credential key is configured
     */
    503: unknown;
};

export type GetStoreChecklistResponses = {
    /**
     * Successful Response
     */
    200: StoreChecklist;
};

export type GetStoreChecklistResponse = GetStoreChecklistResponses[keyof GetStoreChecklistResponses];

export type ListBrandOverviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Substring, ignoring case
         */
        search?: string | null;
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Managed or not managed only
         */
        managed?: boolean | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/brand-overview';
};

export type ListBrandOverviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListBrandOverviewResponses = {
    /**
     * Successful Response
     */
    200: ListResourceBrandOverview;
};

export type ListBrandOverviewResponse = ListBrandOverviewResponses[keyof ListBrandOverviewResponses];

export type GetBrandOverviewSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
    };
    url: '/v1/brand-overview/summary';
};

export type GetBrandOverviewSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandOverviewSummaryResponses = {
    /**
     * Successful Response
     */
    200: VentawizBrandsSchemasBrandSummary;
};

export type GetBrandOverviewSummaryResponse = GetBrandOverviewSummaryResponses[keyof GetBrandOverviewSummaryResponses];

export type GetBrandOverviewItemData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        brand_id: number;
    };
    query?: never;
    url: '/v1/brand-overview/{brand_id}';
};

export type GetBrandOverviewItemErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandOverviewItemResponses = {
    /**
     * Successful Response
     */
    200: BrandOverview;
};

export type GetBrandOverviewItemResponse = GetBrandOverviewItemResponses[keyof GetBrandOverviewItemResponses];

export type ListUnmatchedSkuNamesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Substring, ignoring case
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/brands/unmatched-sku-names';
};

export type ListUnmatchedSkuNamesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListUnmatchedSkuNamesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceUnmatchedSkuName;
};

export type ListUnmatchedSkuNamesResponse = ListUnmatchedSkuNamesResponses[keyof ListUnmatchedSkuNamesResponses];

export type CreateBrandFromSkuNameData = {
    body: BrandFromSkuName;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/brands/from-sku-name';
};

export type CreateBrandFromSkuNameErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateBrandFromSkuNameResponses = {
    /**
     * Successful Response
     */
    201: BrandCreatedFromSkuName;
};

export type CreateBrandFromSkuNameResponse = CreateBrandFromSkuNameResponses[keyof CreateBrandFromSkuNameResponses];

export type LinkBrandSkuNamesData = {
    body: LinkSkuNames;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        brand_id: number;
    };
    query?: never;
    url: '/v1/brands/{brand_id}/link-sku-names';
};

export type LinkBrandSkuNamesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type LinkBrandSkuNamesResponses = {
    /**
     * Successful Response
     */
    200: LinkedSkus;
};

export type LinkBrandSkuNamesResponse = LinkBrandSkuNamesResponses[keyof LinkBrandSkuNamesResponses];

export type GetBrandMergePreviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        brand_id: number;
    };
    query: {
        /**
         * The brand to merge into
         */
        into: number;
    };
    url: '/v1/brands/{brand_id}/merge-preview';
};

export type GetBrandMergePreviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandMergePreviewResponses = {
    /**
     * Successful Response
     */
    200: MergePreview;
};

export type GetBrandMergePreviewResponse = GetBrandMergePreviewResponses[keyof GetBrandMergePreviewResponses];

export type MergeBrandData = {
    body: MergeRequest;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        brand_id: number;
    };
    query?: never;
    url: '/v1/brands/{brand_id}/merge';
};

export type MergeBrandErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type MergeBrandResponses = {
    /**
     * Successful Response
     */
    200: MergeResult;
};

export type MergeBrandResponse = MergeBrandResponses[keyof MergeBrandResponses];

export type ListReviewQueriesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A store the agent was set up with. Repeat for each.
         */
        store_id: Array<number>;
        /**
         * The agent's marketplace. Refuses stores of any other.
         */
        marketplace?: ScraperMarketplace | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/scrapper/review-queries';
};

export type ListReviewQueriesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReviewQueriesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceReviewQueryItem;
};

export type ListReviewQueriesResponse = ListReviewQueriesResponses[keyof ListReviewQueriesResponses];

export type RecordReviewData = {
    body: ReviewResultCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/scrapper/reviews';
};

export type RecordReviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordReviewResponses = {
    /**
     * Successful Response
     */
    200: Array<RatingRead>;
};

export type RecordReviewResponse = RecordReviewResponses[keyof RecordReviewResponses];

export type ListSearchTermQueriesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A store the agent was set up with. Repeat for each.
         */
        store_id: Array<number>;
        /**
         * The agent's marketplace. Refuses stores of any other.
         */
        marketplace?: ScraperMarketplace | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/scrapper/search-term-queries';
};

export type ListSearchTermQueriesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSearchTermQueriesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSearchTermQueryItem;
};

export type ListSearchTermQueriesResponse = ListSearchTermQueriesResponses[keyof ListSearchTermQueriesResponses];

export type RecordSearchTermResultData = {
    body: SearchTermResultCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/scrapper/search-term-results';
};

export type RecordSearchTermResultErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordSearchTermResultResponses = {
    /**
     * Successful Response
     */
    201: SearchTermResultRead;
};

export type RecordSearchTermResultResponse = RecordSearchTermResultResponses[keyof RecordSearchTermResultResponses];

export type CheckInData = {
    body: CheckinCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/scrapper/checkin';
};

export type CheckInErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckInResponses = {
    /**
     * Successful Response
     */
    200: CheckinRead;
};

export type CheckInResponse = CheckInResponses[keyof CheckInResponses];

export type RecordRunData = {
    body: RunCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/scrapper/runs';
};

export type RecordRunErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordRunResponses = {
    /**
     * Successful Response
     */
    201: RunRead;
};

export type RecordRunResponse = RecordRunResponses[keyof RecordRunResponses];

export type GetScrapedDataFiltersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/scraped-data/filters';
};

export type GetScrapedDataFiltersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetScrapedDataFiltersResponses = {
    /**
     * Successful Response
     */
    200: ScrapedDataFilters;
};

export type GetScrapedDataFiltersResponse = GetScrapedDataFiltersResponses[keyof GetScrapedDataFiltersResponses];

export type ListScrapedReviewsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one marketplace
         */
        marketplace?: ScraperMarketplace | null;
        /**
         * Restrict to a store. Repeatable.
         */
        store_id?: Array<number>;
        /**
         * First UTC day included
         */
        updated_from?: string | null;
        /**
         * Last UTC day included
         */
        updated_to?: string | null;
        /**
         * Case-insensitive part match
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/scraped-data/reviews';
};

export type ListScrapedReviewsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListScrapedReviewsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceScrapedReviewRow;
};

export type ListScrapedReviewsResponse = ListScrapedReviewsResponses[keyof ListScrapedReviewsResponses];

export type ListScrapedSearchesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one marketplace
         */
        marketplace?: ScraperMarketplace | null;
        /**
         * Restrict to a store. Repeatable.
         */
        store_id?: Array<number>;
        /**
         * First UTC day included
         */
        scraped_from?: string | null;
        /**
         * Last UTC day included
         */
        scraped_to?: string | null;
        /**
         * Case-insensitive part match
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/scraped-data/ads';
};

export type ListScrapedSearchesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListScrapedSearchesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceScrapedSearchRow;
};

export type ListScrapedSearchesResponse = ListScrapedSearchesResponses[keyof ListScrapedSearchesResponses];

export type ExportScrapedReviewsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one marketplace
         */
        marketplace?: ScraperMarketplace | null;
        /**
         * Restrict to a store. Repeatable.
         */
        store_id?: Array<number>;
        /**
         * First UTC day included
         */
        updated_from?: string | null;
        /**
         * Last UTC day included
         */
        updated_to?: string | null;
        /**
         * Case-insensitive part match
         */
        search?: string | null;
    };
    url: '/v1/scraped-data/reviews/export';
};

export type ExportScrapedReviewsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportScrapedReviewsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ExportScrapedSearchesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one marketplace
         */
        marketplace?: ScraperMarketplace | null;
        /**
         * Restrict to a store. Repeatable.
         */
        store_id?: Array<number>;
        /**
         * First UTC day included
         */
        scraped_from?: string | null;
        /**
         * Last UTC day included
         */
        scraped_to?: string | null;
        /**
         * Case-insensitive part match
         */
        search?: string | null;
    };
    url: '/v1/scraped-data/ads/export';
};

export type ExportScrapedSearchesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportScrapedSearchesResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetFleetCatalogData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/scrapper/admin/catalog';
};

export type GetFleetCatalogErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFleetCatalogResponses = {
    /**
     * Successful Response
     */
    200: FleetCatalog;
};

export type GetFleetCatalogResponse = GetFleetCatalogResponses[keyof GetFleetCatalogResponses];

export type ListFleetDevicesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * One marketplace's agents
         */
        agent_kind?: ScraperMarketplace | null;
        /**
         * One status
         */
        status?: DeviceStatus | null;
        /**
         * Only devices that checked in recently, or only not
         */
        online?: boolean | null;
        /**
         * Case-insensitive part match
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/scrapper/admin/devices';
};

export type ListFleetDevicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListFleetDevicesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceFleetDeviceRead;
};

export type ListFleetDevicesResponse = ListFleetDevicesResponses[keyof ListFleetDevicesResponses];

export type GetFleetDeviceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The agent's own id
         */
        device_id: string;
    };
    query?: never;
    url: '/v1/scrapper/admin/devices/{device_id}';
};

export type GetFleetDeviceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetFleetDeviceResponses = {
    /**
     * Successful Response
     */
    200: FleetDeviceRead;
};

export type GetFleetDeviceResponse = GetFleetDeviceResponses[keyof GetFleetDeviceResponses];

export type UpdateFleetDeviceData = {
    body: FleetDeviceUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The agent's own id
         */
        device_id: string;
    };
    query?: never;
    url: '/v1/scrapper/admin/devices/{device_id}';
};

export type UpdateFleetDeviceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateFleetDeviceResponses = {
    /**
     * Successful Response
     */
    200: FleetDeviceRead;
};

export type UpdateFleetDeviceResponse = UpdateFleetDeviceResponses[keyof UpdateFleetDeviceResponses];

export type ListFleetRunsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * One agent
         */
        device_id?: string | null;
        /**
         * One scraper
         */
        scraper_key?: string | null;
        /**
         * One outcome
         */
        status?: string | null;
        /**
         * First UTC day included
         */
        reported_from?: string | null;
        /**
         * Last UTC day included
         */
        reported_to?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/scrapper/admin/runs';
};

export type ListFleetRunsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListFleetRunsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceFleetRunRead;
};

export type ListFleetRunsResponse = ListFleetRunsResponses[keyof ListFleetRunsResponses];

export type ListMarketplacesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Substring of the code or the name
         */
        search?: string | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketplaces';
};

export type ListMarketplacesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketplacesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketplaceRead;
};

export type ListMarketplacesResponse = ListMarketplacesResponses[keyof ListMarketplacesResponses];

export type CreateMarketplaceData = {
    body: MarketplaceCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/marketplaces';
};

export type CreateMarketplaceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateMarketplaceResponses = {
    /**
     * Successful Response
     */
    201: MarketplaceRead;
};

export type CreateMarketplaceResponse = CreateMarketplaceResponses[keyof CreateMarketplaceResponses];

export type GetMarketplaceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}';
};

export type GetMarketplaceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketplaceResponses = {
    /**
     * Successful Response
     */
    200: MarketplaceRead;
};

export type GetMarketplaceResponse = GetMarketplaceResponses[keyof GetMarketplaceResponses];

export type UpdateMarketplaceData = {
    body: MarketplaceUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}';
};

export type UpdateMarketplaceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateMarketplaceResponses = {
    /**
     * Successful Response
     */
    200: MarketplaceRead;
};

export type UpdateMarketplaceResponse = UpdateMarketplaceResponses[keyof UpdateMarketplaceResponses];

export type RetireMarketplaceData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/retire';
};

export type RetireMarketplaceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireMarketplaceResponses = {
    /**
     * Successful Response
     */
    200: MarketplaceRead;
};

export type RetireMarketplaceResponse = RetireMarketplaceResponses[keyof RetireMarketplaceResponses];

export type ReinstateMarketplaceData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/reinstate';
};

export type ReinstateMarketplaceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateMarketplaceResponses = {
    /**
     * Successful Response
     */
    200: MarketplaceRead;
};

export type ReinstateMarketplaceResponse = ReinstateMarketplaceResponses[keyof ReinstateMarketplaceResponses];

export type ListMarketplaceActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/marketplaces/{entity_id}/activity';
};

export type ListMarketplaceActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketplaceActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListMarketplaceActivityResponse = ListMarketplaceActivityResponses[keyof ListMarketplaceActivityResponses];

export type GetMarketplaceUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/usage';
};

export type GetMarketplaceUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketplaceUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageReport;
};

export type GetMarketplaceUsageResponse = GetMarketplaceUsageResponses[keyof GetMarketplaceUsageResponses];

export type ListBrandsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Substring of the code or the name
         */
        search?: string | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/brands';
};

export type ListBrandsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListBrandsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceBrandRead;
};

export type ListBrandsResponse = ListBrandsResponses[keyof ListBrandsResponses];

export type CreateBrandData = {
    body: BrandCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/brands';
};

export type CreateBrandErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateBrandResponses = {
    /**
     * Successful Response
     */
    201: BrandRead;
};

export type CreateBrandResponse = CreateBrandResponses[keyof CreateBrandResponses];

export type GetBrandData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}';
};

export type GetBrandErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandResponses = {
    /**
     * Successful Response
     */
    200: BrandRead;
};

export type GetBrandResponse = GetBrandResponses[keyof GetBrandResponses];

export type UpdateBrandData = {
    body: BrandUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}';
};

export type UpdateBrandErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateBrandResponses = {
    /**
     * Successful Response
     */
    200: BrandRead;
};

export type UpdateBrandResponse = UpdateBrandResponses[keyof UpdateBrandResponses];

export type RetireBrandData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/retire';
};

export type RetireBrandErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireBrandResponses = {
    /**
     * Successful Response
     */
    200: BrandRead;
};

export type RetireBrandResponse = RetireBrandResponses[keyof RetireBrandResponses];

export type ReinstateBrandData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/reinstate';
};

export type ReinstateBrandErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateBrandResponses = {
    /**
     * Successful Response
     */
    200: BrandRead;
};

export type ReinstateBrandResponse = ReinstateBrandResponses[keyof ReinstateBrandResponses];

export type ListBrandActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/brands/{entity_id}/activity';
};

export type ListBrandActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListBrandActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListBrandActivityResponse = ListBrandActivityResponses[keyof ListBrandActivityResponses];

export type GetBrandUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/usage';
};

export type GetBrandUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageReport;
};

export type GetBrandUsageResponse = GetBrandUsageResponses[keyof GetBrandUsageResponses];

export type GetBrandRetirePreviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/retire-preview';
};

export type GetBrandRetirePreviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandRetirePreviewResponses = {
    /**
     * Successful Response
     */
    200: RetirePreview;
};

export type GetBrandRetirePreviewResponse = GetBrandRetirePreviewResponses[keyof GetBrandRetirePreviewResponses];

export type ListStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Substring of the code or the name
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/stores';
};

export type ListStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListStoresResponses = {
    /**
     * Successful Response
     */
    200: VentawizKitPaginationListResourceStoreRead2;
};

export type ListStoresResponse = ListStoresResponses[keyof ListStoresResponses];

export type CreateStoreData = {
    body: StoreCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stores';
};

export type CreateStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateStoreResponses = {
    /**
     * Successful Response
     */
    201: VentawizRegistrySchemasStoreRead;
};

export type CreateStoreResponse = CreateStoreResponses[keyof CreateStoreResponses];

export type GetStoreData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/stores/{entity_id}';
};

export type GetStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetStoreResponses = {
    /**
     * Successful Response
     */
    200: VentawizRegistrySchemasStoreRead;
};

export type GetStoreResponse = GetStoreResponses[keyof GetStoreResponses];

export type UpdateStoreData = {
    body: StoreUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/stores/{entity_id}';
};

export type UpdateStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateStoreResponses = {
    /**
     * Successful Response
     */
    200: VentawizRegistrySchemasStoreRead;
};

export type UpdateStoreResponse = UpdateStoreResponses[keyof UpdateStoreResponses];

export type RetireStoreData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/stores/{entity_id}/retire';
};

export type RetireStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireStoreResponses = {
    /**
     * Successful Response
     */
    200: VentawizRegistrySchemasStoreRead;
};

export type RetireStoreResponse = RetireStoreResponses[keyof RetireStoreResponses];

export type ReinstateStoreData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/stores/{entity_id}/reinstate';
};

export type ReinstateStoreErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateStoreResponses = {
    /**
     * Successful Response
     */
    200: VentawizRegistrySchemasStoreRead;
};

export type ReinstateStoreResponse = ReinstateStoreResponses[keyof ReinstateStoreResponses];

export type ListWarehousesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Substring of the code or the name
         */
        search?: string | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/warehouses';
};

export type ListWarehousesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListWarehousesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceWarehouseRead;
};

export type ListWarehousesResponse = ListWarehousesResponses[keyof ListWarehousesResponses];

export type CreateWarehouseData = {
    body: WarehouseCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/warehouses';
};

export type CreateWarehouseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateWarehouseResponses = {
    /**
     * Successful Response
     */
    201: WarehouseRead;
};

export type CreateWarehouseResponse = CreateWarehouseResponses[keyof CreateWarehouseResponses];

export type GetWarehouseData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}';
};

export type GetWarehouseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetWarehouseResponses = {
    /**
     * Successful Response
     */
    200: WarehouseRead;
};

export type GetWarehouseResponse = GetWarehouseResponses[keyof GetWarehouseResponses];

export type UpdateWarehouseData = {
    body: WarehouseUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}';
};

export type UpdateWarehouseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateWarehouseResponses = {
    /**
     * Successful Response
     */
    200: WarehouseRead;
};

export type UpdateWarehouseResponse = UpdateWarehouseResponses[keyof UpdateWarehouseResponses];

export type RetireWarehouseData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/retire';
};

export type RetireWarehouseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * retire_blocked: see /retire-preview
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireWarehouseResponses = {
    /**
     * Successful Response
     */
    200: WarehouseRead;
};

export type RetireWarehouseResponse = RetireWarehouseResponses[keyof RetireWarehouseResponses];

export type ReinstateWarehouseData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/reinstate';
};

export type ReinstateWarehouseErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateWarehouseResponses = {
    /**
     * Successful Response
     */
    200: WarehouseRead;
};

export type ReinstateWarehouseResponse = ReinstateWarehouseResponses[keyof ReinstateWarehouseResponses];

export type ListWarehouseActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/warehouses/{entity_id}/activity';
};

export type ListWarehouseActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListWarehouseActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListWarehouseActivityResponse = ListWarehouseActivityResponses[keyof ListWarehouseActivityResponses];

export type GetWarehouseUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/usage';
};

export type GetWarehouseUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetWarehouseUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageReport;
};

export type GetWarehouseUsageResponse = GetWarehouseUsageResponses[keyof GetWarehouseUsageResponses];

export type GetWarehouseRetirePreviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/retire-preview';
};

export type GetWarehouseRetirePreviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetWarehouseRetirePreviewResponses = {
    /**
     * Successful Response
     */
    200: RetirePreview;
};

export type GetWarehouseRetirePreviewResponse = GetWarehouseRetirePreviewResponses[keyof GetWarehouseRetirePreviewResponses];

export type ListRetailersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Substring of the code or the name
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/retailers';
};

export type ListRetailersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListRetailersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceRetailerRead;
};

export type ListRetailersResponse = ListRetailersResponses[keyof ListRetailersResponses];

export type CreateRetailerData = {
    body: RetailerCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/retailers';
};

export type CreateRetailerErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateRetailerResponses = {
    /**
     * Successful Response
     */
    201: RetailerRead;
};

export type CreateRetailerResponse = CreateRetailerResponses[keyof CreateRetailerResponses];

export type GetRetailerData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}';
};

export type GetRetailerErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetRetailerResponses = {
    /**
     * Successful Response
     */
    200: RetailerRead;
};

export type GetRetailerResponse = GetRetailerResponses[keyof GetRetailerResponses];

export type UpdateRetailerData = {
    body: RetailerUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}';
};

export type UpdateRetailerErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateRetailerResponses = {
    /**
     * Successful Response
     */
    200: RetailerRead;
};

export type UpdateRetailerResponse = UpdateRetailerResponses[keyof UpdateRetailerResponses];

export type RetireRetailerData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/retire';
};

export type RetireRetailerErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireRetailerResponses = {
    /**
     * Successful Response
     */
    200: RetailerRead;
};

export type RetireRetailerResponse = RetireRetailerResponses[keyof RetireRetailerResponses];

export type ReinstateRetailerData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/reinstate';
};

export type ReinstateRetailerErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateRetailerResponses = {
    /**
     * Successful Response
     */
    200: RetailerRead;
};

export type ReinstateRetailerResponse = ReinstateRetailerResponses[keyof ReinstateRetailerResponses];

export type ListRetailerActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/retailers/{entity_id}/activity';
};

export type ListRetailerActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListRetailerActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListRetailerActivityResponse = ListRetailerActivityResponses[keyof ListRetailerActivityResponses];

export type GetRetailerUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/usage';
};

export type GetRetailerUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetRetailerUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageReport;
};

export type GetRetailerUsageResponse = GetRetailerUsageResponses[keyof GetRetailerUsageResponses];

export type ListServiceProvidersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Substring of the code or the name
         */
        search?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/service-providers';
};

export type ListServiceProvidersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListServiceProvidersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceServiceProviderRead;
};

export type ListServiceProvidersResponse = ListServiceProvidersResponses[keyof ListServiceProvidersResponses];

export type CreateServiceProviderData = {
    body: ServiceProviderCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/service-providers';
};

export type CreateServiceProviderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateServiceProviderResponses = {
    /**
     * Successful Response
     */
    201: ServiceProviderRead;
};

export type CreateServiceProviderResponse = CreateServiceProviderResponses[keyof CreateServiceProviderResponses];

export type GetServiceProviderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}';
};

export type GetServiceProviderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetServiceProviderResponses = {
    /**
     * Successful Response
     */
    200: ServiceProviderRead;
};

export type GetServiceProviderResponse = GetServiceProviderResponses[keyof GetServiceProviderResponses];

export type UpdateServiceProviderData = {
    body: ServiceProviderUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}';
};

export type UpdateServiceProviderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateServiceProviderResponses = {
    /**
     * Successful Response
     */
    200: ServiceProviderRead;
};

export type UpdateServiceProviderResponse = UpdateServiceProviderResponses[keyof UpdateServiceProviderResponses];

export type RetireServiceProviderData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/retire';
};

export type RetireServiceProviderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireServiceProviderResponses = {
    /**
     * Successful Response
     */
    200: ServiceProviderRead;
};

export type RetireServiceProviderResponse = RetireServiceProviderResponses[keyof RetireServiceProviderResponses];

export type ReinstateServiceProviderData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/reinstate';
};

export type ReinstateServiceProviderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateServiceProviderResponses = {
    /**
     * Successful Response
     */
    200: ServiceProviderRead;
};

export type ReinstateServiceProviderResponse = ReinstateServiceProviderResponses[keyof ReinstateServiceProviderResponses];

export type ListServiceProviderActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/service-providers/{entity_id}/activity';
};

export type ListServiceProviderActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListServiceProviderActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListServiceProviderActivityResponse = ListServiceProviderActivityResponses[keyof ListServiceProviderActivityResponses];

export type GetServiceProviderUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/usage';
};

export type GetServiceProviderUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetServiceProviderUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageReport;
};

export type GetServiceProviderUsageResponse = GetServiceProviderUsageResponses[keyof GetServiceProviderUsageResponses];

export type ListWarehouseNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/warehouses/{entity_id}/notes';
};

export type ListWarehouseNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListWarehouseNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListWarehouseNotesResponse = ListWarehouseNotesResponses[keyof ListWarehouseNotesResponses];

export type CreateWarehouseNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/notes';
};

export type CreateWarehouseNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateWarehouseNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateWarehouseNoteResponse = CreateWarehouseNoteResponses[keyof CreateWarehouseNoteResponses];

export type DeleteWarehouseNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/notes/{note_id}';
};

export type DeleteWarehouseNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteWarehouseNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteWarehouseNoteResponse = DeleteWarehouseNoteResponses[keyof DeleteWarehouseNoteResponses];

export type UpdateWarehouseNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/notes/{note_id}';
};

export type UpdateWarehouseNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateWarehouseNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateWarehouseNoteResponse = UpdateWarehouseNoteResponses[keyof UpdateWarehouseNoteResponses];

export type GetWarehouseTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/tags';
};

export type GetWarehouseTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetWarehouseTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type GetWarehouseTagsResponse = GetWarehouseTagsResponses[keyof GetWarehouseTagsResponses];

export type ReplaceWarehouseTagsData = {
    body: TagsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/tags';
};

export type ReplaceWarehouseTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceWarehouseTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type ReplaceWarehouseTagsResponse = ReplaceWarehouseTagsResponses[keyof ReplaceWarehouseTagsResponses];

export type GetWarehouseContactsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/contacts';
};

export type GetWarehouseContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetWarehouseContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type GetWarehouseContactsResponse = GetWarehouseContactsResponses[keyof GetWarehouseContactsResponses];

export type ReplaceWarehouseContactsData = {
    body: ContactsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/contacts';
};

export type ReplaceWarehouseContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceWarehouseContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type ReplaceWarehouseContactsResponse = ReplaceWarehouseContactsResponses[keyof ReplaceWarehouseContactsResponses];

export type ListBrandNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/brands/{entity_id}/notes';
};

export type ListBrandNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListBrandNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListBrandNotesResponse = ListBrandNotesResponses[keyof ListBrandNotesResponses];

export type CreateBrandNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/notes';
};

export type CreateBrandNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateBrandNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateBrandNoteResponse = CreateBrandNoteResponses[keyof CreateBrandNoteResponses];

export type DeleteBrandNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/notes/{note_id}';
};

export type DeleteBrandNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteBrandNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteBrandNoteResponse = DeleteBrandNoteResponses[keyof DeleteBrandNoteResponses];

export type UpdateBrandNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/notes/{note_id}';
};

export type UpdateBrandNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateBrandNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateBrandNoteResponse = UpdateBrandNoteResponses[keyof UpdateBrandNoteResponses];

export type GetBrandTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/tags';
};

export type GetBrandTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type GetBrandTagsResponse = GetBrandTagsResponses[keyof GetBrandTagsResponses];

export type ReplaceBrandTagsData = {
    body: TagsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/tags';
};

export type ReplaceBrandTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceBrandTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type ReplaceBrandTagsResponse = ReplaceBrandTagsResponses[keyof ReplaceBrandTagsResponses];

export type GetBrandContactsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/contacts';
};

export type GetBrandContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetBrandContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type GetBrandContactsResponse = GetBrandContactsResponses[keyof GetBrandContactsResponses];

export type ReplaceBrandContactsData = {
    body: ContactsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The brand id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/brands/{entity_id}/contacts';
};

export type ReplaceBrandContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceBrandContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type ReplaceBrandContactsResponse = ReplaceBrandContactsResponses[keyof ReplaceBrandContactsResponses];

export type ListMarketplaceNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketplaces/{entity_id}/notes';
};

export type ListMarketplaceNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketplaceNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListMarketplaceNotesResponse = ListMarketplaceNotesResponses[keyof ListMarketplaceNotesResponses];

export type CreateMarketplaceNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/notes';
};

export type CreateMarketplaceNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateMarketplaceNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateMarketplaceNoteResponse = CreateMarketplaceNoteResponses[keyof CreateMarketplaceNoteResponses];

export type DeleteMarketplaceNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/notes/{note_id}';
};

export type DeleteMarketplaceNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteMarketplaceNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteMarketplaceNoteResponse = DeleteMarketplaceNoteResponses[keyof DeleteMarketplaceNoteResponses];

export type UpdateMarketplaceNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/notes/{note_id}';
};

export type UpdateMarketplaceNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateMarketplaceNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateMarketplaceNoteResponse = UpdateMarketplaceNoteResponses[keyof UpdateMarketplaceNoteResponses];

export type GetMarketplaceTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/tags';
};

export type GetMarketplaceTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketplaceTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type GetMarketplaceTagsResponse = GetMarketplaceTagsResponses[keyof GetMarketplaceTagsResponses];

export type ReplaceMarketplaceTagsData = {
    body: TagsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/tags';
};

export type ReplaceMarketplaceTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceMarketplaceTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type ReplaceMarketplaceTagsResponse = ReplaceMarketplaceTagsResponses[keyof ReplaceMarketplaceTagsResponses];

export type GetMarketplaceContactsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/contacts';
};

export type GetMarketplaceContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketplaceContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type GetMarketplaceContactsResponse = GetMarketplaceContactsResponses[keyof GetMarketplaceContactsResponses];

export type ReplaceMarketplaceContactsData = {
    body: ContactsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/marketplaces/{entity_id}/contacts';
};

export type ReplaceMarketplaceContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceMarketplaceContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type ReplaceMarketplaceContactsResponse = ReplaceMarketplaceContactsResponses[keyof ReplaceMarketplaceContactsResponses];

export type ListVendorNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/vendors/{entity_id}/notes';
};

export type ListVendorNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListVendorNotesResponse = ListVendorNotesResponses[keyof ListVendorNotesResponses];

export type CreateVendorNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/notes';
};

export type CreateVendorNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateVendorNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateVendorNoteResponse = CreateVendorNoteResponses[keyof CreateVendorNoteResponses];

export type DeleteVendorNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/notes/{note_id}';
};

export type DeleteVendorNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteVendorNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteVendorNoteResponse = DeleteVendorNoteResponses[keyof DeleteVendorNoteResponses];

export type UpdateVendorNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/notes/{note_id}';
};

export type UpdateVendorNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateVendorNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateVendorNoteResponse = UpdateVendorNoteResponses[keyof UpdateVendorNoteResponses];

export type GetVendorTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/tags';
};

export type GetVendorTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type GetVendorTagsResponse = GetVendorTagsResponses[keyof GetVendorTagsResponses];

export type ReplaceVendorTagsData = {
    body: TagsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/tags';
};

export type ReplaceVendorTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceVendorTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type ReplaceVendorTagsResponse = ReplaceVendorTagsResponses[keyof ReplaceVendorTagsResponses];

export type GetVendorContactsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/contacts';
};

export type GetVendorContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type GetVendorContactsResponse = GetVendorContactsResponses[keyof GetVendorContactsResponses];

export type ReplaceVendorContactsData = {
    body: ContactsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/vendors/{entity_id}/contacts';
};

export type ReplaceVendorContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceVendorContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type ReplaceVendorContactsResponse = ReplaceVendorContactsResponses[keyof ReplaceVendorContactsResponses];

export type ListRetailerNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/retailers/{entity_id}/notes';
};

export type ListRetailerNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListRetailerNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListRetailerNotesResponse = ListRetailerNotesResponses[keyof ListRetailerNotesResponses];

export type CreateRetailerNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/notes';
};

export type CreateRetailerNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateRetailerNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateRetailerNoteResponse = CreateRetailerNoteResponses[keyof CreateRetailerNoteResponses];

export type DeleteRetailerNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/notes/{note_id}';
};

export type DeleteRetailerNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteRetailerNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteRetailerNoteResponse = DeleteRetailerNoteResponses[keyof DeleteRetailerNoteResponses];

export type UpdateRetailerNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/notes/{note_id}';
};

export type UpdateRetailerNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateRetailerNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateRetailerNoteResponse = UpdateRetailerNoteResponses[keyof UpdateRetailerNoteResponses];

export type GetRetailerTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/tags';
};

export type GetRetailerTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetRetailerTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type GetRetailerTagsResponse = GetRetailerTagsResponses[keyof GetRetailerTagsResponses];

export type ReplaceRetailerTagsData = {
    body: TagsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/tags';
};

export type ReplaceRetailerTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceRetailerTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type ReplaceRetailerTagsResponse = ReplaceRetailerTagsResponses[keyof ReplaceRetailerTagsResponses];

export type GetRetailerContactsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/contacts';
};

export type GetRetailerContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetRetailerContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type GetRetailerContactsResponse = GetRetailerContactsResponses[keyof GetRetailerContactsResponses];

export type ReplaceRetailerContactsData = {
    body: ContactsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The retailer id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/retailers/{entity_id}/contacts';
};

export type ReplaceRetailerContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceRetailerContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type ReplaceRetailerContactsResponse = ReplaceRetailerContactsResponses[keyof ReplaceRetailerContactsResponses];

export type ListServiceProviderNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/service-providers/{entity_id}/notes';
};

export type ListServiceProviderNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListServiceProviderNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceNoteRead;
};

export type ListServiceProviderNotesResponse = ListServiceProviderNotesResponses[keyof ListServiceProviderNotesResponses];

export type CreateServiceProviderNoteData = {
    body: NoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/notes';
};

export type CreateServiceProviderNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateServiceProviderNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteRead;
};

export type CreateServiceProviderNoteResponse = CreateServiceProviderNoteResponses[keyof CreateServiceProviderNoteResponses];

export type DeleteServiceProviderNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/notes/{note_id}';
};

export type DeleteServiceProviderNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteServiceProviderNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteServiceProviderNoteResponse = DeleteServiceProviderNoteResponses[keyof DeleteServiceProviderNoteResponses];

export type UpdateServiceProviderNoteData = {
    body: NoteUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
        /**
         * The note's id
         */
        note_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/notes/{note_id}';
};

export type UpdateServiceProviderNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateServiceProviderNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteRead;
};

export type UpdateServiceProviderNoteResponse = UpdateServiceProviderNoteResponses[keyof UpdateServiceProviderNoteResponses];

export type GetServiceProviderTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/tags';
};

export type GetServiceProviderTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetServiceProviderTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type GetServiceProviderTagsResponse = GetServiceProviderTagsResponses[keyof GetServiceProviderTagsResponses];

export type ReplaceServiceProviderTagsData = {
    body: TagsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/tags';
};

export type ReplaceServiceProviderTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceServiceProviderTagsResponses = {
    /**
     * Successful Response
     */
    200: TagsRead;
};

export type ReplaceServiceProviderTagsResponse = ReplaceServiceProviderTagsResponses[keyof ReplaceServiceProviderTagsResponses];

export type GetServiceProviderContactsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/contacts';
};

export type GetServiceProviderContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetServiceProviderContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type GetServiceProviderContactsResponse = GetServiceProviderContactsResponses[keyof GetServiceProviderContactsResponses];

export type ReplaceServiceProviderContactsData = {
    body: ContactsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The service provider id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/service-providers/{entity_id}/contacts';
};

export type ReplaceServiceProviderContactsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceServiceProviderContactsResponses = {
    /**
     * Successful Response
     */
    200: ContactsRead;
};

export type ReplaceServiceProviderContactsResponse = ReplaceServiceProviderContactsResponses[keyof ReplaceServiceProviderContactsResponses];

export type ListRegistryTagsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Which kind of entity
         */
        kind: RegistrySubjectKind;
    };
    url: '/v1/registry-tags';
};

export type ListRegistryTagsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListRegistryTagsResponses = {
    /**
     * Successful Response
     */
    200: TagCountList;
};

export type ListRegistryTagsResponse = ListRegistryTagsResponses[keyof ListRegistryTagsResponses];

export type ListMarketplaceCatalogueData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Substring of the code, short code or name
         */
        search?: string | null;
        /**
         * Channel family, e.g. AZ: one platform's tab
         */
        family?: string | null;
        /**
         * ISO 3166-1 alpha-2, e.g. DE
         */
        country?: string | null;
        /**
         * Only where this organization has a store (true), or has none (false)
         */
        mine?: boolean | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * Live or retired marketplaces only
         */
        is_active?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/marketplace-catalogue';
};

export type ListMarketplaceCatalogueErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketplaceCatalogueResponses = {
    /**
     * Successful Response
     */
    200: ListResourceMarketplaceCatalogueItem;
};

export type ListMarketplaceCatalogueResponse = ListMarketplaceCatalogueResponses[keyof ListMarketplaceCatalogueResponses];

export type GetMarketplaceCatalogueItemData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The marketplace id
         */
        marketplace_id: number;
    };
    query?: never;
    url: '/v1/marketplace-catalogue/{marketplace_id}';
};

export type GetMarketplaceCatalogueItemErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetMarketplaceCatalogueItemResponses = {
    /**
     * Successful Response
     */
    200: MarketplaceCatalogueItem;
};

export type GetMarketplaceCatalogueItemResponse = GetMarketplaceCatalogueItemResponses[keyof GetMarketplaceCatalogueItemResponses];

export type ListMarketplaceFamiliesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Live or retired marketplaces only
         */
        is_active?: boolean | null;
    };
    url: '/v1/marketplace-families';
};

export type ListMarketplaceFamiliesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListMarketplaceFamiliesResponses = {
    /**
     * Successful Response
     */
    200: MarketplaceFamilies;
};

export type ListMarketplaceFamiliesResponse = ListMarketplaceFamiliesResponses[keyof ListMarketplaceFamiliesResponses];

export type ListWarehouseOverviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * Only this kind
         */
        kind?: WarehouseKind | null;
        /**
         * ISO 3166-1 alpha-2
         */
        country?: string | null;
        /**
         * Substring of the code, name, city or postcode
         */
        search?: string | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/warehouse-overview';
};

export type ListWarehouseOverviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListWarehouseOverviewResponses = {
    /**
     * Successful Response
     */
    200: ListResourceWarehouseOverview;
};

export type ListWarehouseOverviewResponse = ListWarehouseOverviewResponses[keyof ListWarehouseOverviewResponses];

export type ListWarehouseKindsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
    };
    url: '/v1/warehouse-kinds';
};

export type ListWarehouseKindsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListWarehouseKindsResponses = {
    /**
     * Successful Response
     */
    200: KindCounts;
};

export type ListWarehouseKindsResponse = ListWarehouseKindsResponses[keyof ListWarehouseKindsResponses];

export type ListWarehouseStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/stores';
};

export type ListWarehouseStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListWarehouseStoresResponses = {
    /**
     * Successful Response
     */
    200: WarehouseStores;
};

export type ListWarehouseStoresResponse = ListWarehouseStoresResponses[keyof ListWarehouseStoresResponses];

export type ReplaceWarehouseStoresData = {
    body: WarehouseStoresWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The warehouse id, as it appears in a grant scope
         */
        entity_id: number;
    };
    query?: never;
    url: '/v1/warehouses/{entity_id}/stores';
};

export type ReplaceWarehouseStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceWarehouseStoresResponses = {
    /**
     * Successful Response
     */
    200: WarehouseStoresUpdated;
};

export type ReplaceWarehouseStoresResponse = ReplaceWarehouseStoresResponses[keyof ReplaceWarehouseStoresResponses];

export type GetStoreCarrierDefaultsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The store id
         */
        store_id: number;
    };
    query?: never;
    url: '/v1/stores/{store_id}/carrier-defaults';
};

export type GetStoreCarrierDefaultsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * no_warehouse: the store has no warehouse linked
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetStoreCarrierDefaultsResponses = {
    /**
     * Successful Response
     */
    200: CarrierDefaults;
};

export type GetStoreCarrierDefaultsResponse = GetStoreCarrierDefaultsResponses[keyof GetStoreCarrierDefaultsResponses];

export type ListVendorOverviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Substring of the code, the name or the city
         */
        search?: string | null;
        /**
         * ISO 3166-1 alpha-2
         */
        country?: string | null;
        /**
         * Only this kind
         */
        kind?: VendorKind | null;
        /**
         * Carrying this tag, ignoring case
         */
        tag?: string | null;
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/vendor-overview';
};

export type ListVendorOverviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorOverviewResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVendorOverview;
};

export type ListVendorOverviewResponse = ListVendorOverviewResponses[keyof ListVendorOverviewResponses];

export type GetVendorOverviewItemData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendor-overview/{vendor_id}';
};

export type GetVendorOverviewItemErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorOverviewItemResponses = {
    /**
     * Successful Response
     */
    200: VendorOverview;
};

export type GetVendorOverviewItemResponse = GetVendorOverviewItemResponses[keyof GetVendorOverviewItemResponses];

export type ListVendorsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Substring of the code, the name or the city
         */
        search?: string | null;
        /**
         * Restrict to live or retired rows
         */
        is_active?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/vendors';
};

export type ListVendorsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVendorRead;
};

export type ListVendorsResponse = ListVendorsResponses[keyof ListVendorsResponses];

export type CreateVendorData = {
    body: VendorCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendors';
};

export type CreateVendorErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * resource_conflict: the code is spent, or the name is taken
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateVendorResponses = {
    /**
     * Successful Response
     */
    201: VendorRead;
};

export type CreateVendorResponse = CreateVendorResponses[keyof CreateVendorResponses];

export type GetVendorData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}';
};

export type GetVendorErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorResponses = {
    /**
     * Successful Response
     */
    200: VendorRead;
};

export type GetVendorResponse = GetVendorResponses[keyof GetVendorResponses];

export type UpdateVendorData = {
    body: VendorUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}';
};

export type UpdateVendorErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * resource_conflict: the code is spent, or the name is taken
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateVendorResponses = {
    /**
     * Successful Response
     */
    200: VendorRead;
};

export type UpdateVendorResponse = UpdateVendorResponses[keyof UpdateVendorResponses];

export type RetireVendorData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/retire';
};

export type RetireVendorErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetireVendorResponses = {
    /**
     * Successful Response
     */
    200: VendorRead;
};

export type RetireVendorResponse = RetireVendorResponses[keyof RetireVendorResponses];

export type ReinstateVendorData = {
    body?: LifecycleRequest | null;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/reinstate';
};

export type ReinstateVendorErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * resource_conflict: the code is spent, or the name is taken
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReinstateVendorResponses = {
    /**
     * Successful Response
     */
    200: VendorRead;
};

export type ReinstateVendorResponse = ReinstateVendorResponses[keyof ReinstateVendorResponses];

export type GetVendorRetirePreviewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/retire-preview';
};

export type GetVendorRetirePreviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorRetirePreviewResponses = {
    /**
     * Successful Response
     */
    200: RetirePreview;
};

export type GetVendorRetirePreviewResponse = GetVendorRetirePreviewResponses[keyof GetVendorRetirePreviewResponses];

export type GetVendorUsageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/usage';
};

export type GetVendorUsageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageReport;
};

export type GetVendorUsageResponse = GetVendorUsageResponses[keyof GetVendorUsageResponses];

export type ListVendorActivityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: {
        limit?: number;
        /**
         * The first UTC day, inclusive (YYYY-MM-DD)
         */
        from?: string | null;
        /**
         * The last UTC day, inclusive (YYYY-MM-DD)
         */
        to?: string | null;
    };
    url: '/v1/vendors/{vendor_id}/activity';
};

export type ListVendorActivityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorActivityResponses = {
    /**
     * Successful Response
     */
    200: ActivityList;
};

export type ListVendorActivityResponse = ListVendorActivityResponses[keyof ListVendorActivityResponses];

export type GetVendorWarehousesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/warehouses';
};

export type GetVendorWarehousesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorWarehousesResponses = {
    /**
     * Successful Response
     */
    200: VendorWarehouses;
};

export type GetVendorWarehousesResponse = GetVendorWarehousesResponses[keyof GetVendorWarehousesResponses];

export type ReplaceVendorWarehousesData = {
    body: VendorWarehousesWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/warehouses';
};

export type ReplaceVendorWarehousesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceVendorWarehousesResponses = {
    /**
     * Successful Response
     */
    200: VendorWarehouses;
};

export type ReplaceVendorWarehousesResponse = ReplaceVendorWarehousesResponses[keyof ReplaceVendorWarehousesResponses];

export type GetVendorTaxIdsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/tax-ids';
};

export type GetVendorTaxIdsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorTaxIdsResponses = {
    /**
     * Successful Response
     */
    200: VendorTaxIds;
};

export type GetVendorTaxIdsResponse = GetVendorTaxIdsResponses[keyof GetVendorTaxIdsResponses];

export type ReplaceVendorTaxIdsData = {
    body: VendorTaxIdsWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        /**
         * The vendor id (ops_vendor.pk_vendor_id)
         */
        vendor_id: number;
    };
    query?: never;
    url: '/v1/vendors/{vendor_id}/tax-ids';
};

export type ReplaceVendorTaxIdsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceVendorTaxIdsResponses = {
    /**
     * Successful Response
     */
    200: VendorTaxIds;
};

export type ReplaceVendorTaxIdsResponse = ReplaceVendorTaxIdsResponses[keyof ReplaceVendorTaxIdsResponses];

export type BlockStockLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/lookups';
};

export type BlockStockLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BlockStockLookupsResponses = {
    /**
     * Successful Response
     */
    200: BlockLookupsRead;
};

export type BlockStockLookupsResponse = BlockStockLookupsResponses[keyof BlockStockLookupsResponses];

export type ListBlockStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * PO code contains
         */
        q?: string | null;
        /**
         * Made on or after
         */
        date_from?: string | null;
        /**
         * Made before
         */
        date_to?: string | null;
        /**
         * Include voided blocks
         */
        include_void?: boolean;
        /**
         * Only blocks holding one of these SKU codes (exact)
         */
        sku_code?: Array<string> | null;
        /**
         * Only blocks holding a SKU one of these retailer product codes names at the block's warehouse (exact)
         */
        retailer_sku?: Array<string> | null;
    };
    url: '/v1/block-stock';
};

export type ListBlockStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListBlockStockResponses = {
    /**
     * Successful Response
     */
    200: ListResourceBlockRead;
};

export type ListBlockStockResponse = ListBlockStockResponses[keyof ListBlockStockResponses];

export type CreateBlockStockData = {
    body: BlockCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock';
};

export type CreateBlockStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateBlockStockResponses = {
    /**
     * Successful Response
     */
    201: BlockDetailRead;
};

export type CreateBlockStockResponse = CreateBlockStockResponses[keyof CreateBlockStockResponses];

export type ExportBlockStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * PO code contains
         */
        q?: string | null;
        /**
         * Made on or after
         */
        date_from?: string | null;
        /**
         * Made before
         */
        date_to?: string | null;
        /**
         * Include voided blocks
         */
        include_void?: boolean;
        /**
         * Only blocks holding one of these SKU codes (exact)
         */
        sku_code?: Array<string> | null;
        /**
         * Only blocks holding a SKU one of these retailer product codes names at the block's warehouse (exact)
         */
        retailer_sku?: Array<string> | null;
    };
    url: '/v1/block-stock/export';
};

export type ExportBlockStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBlockStockResponses = {
    /**
     * The filtered blocks as .xlsx
     */
    200: unknown;
};

export type BlockStockLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        block_id: number;
    };
    query?: never;
    url: '/v1/block-stock/{block_id}/lines';
};

export type BlockStockLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BlockStockLinesResponses = {
    /**
     * Successful Response
     */
    200: Array<BlockLineRead>;
};

export type BlockStockLinesResponse = BlockStockLinesResponses[keyof BlockStockLinesResponses];

export type AddBlockStockLinesData = {
    body: BlockLinesAdd;
    headers?: {
        authorization?: string | null;
    };
    path: {
        block_id: number;
    };
    query?: never;
    url: '/v1/block-stock/{block_id}/lines';
};

export type AddBlockStockLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AddBlockStockLinesResponses = {
    /**
     * Successful Response
     */
    200: BlockDetailRead;
};

export type AddBlockStockLinesResponse = AddBlockStockLinesResponses[keyof AddBlockStockLinesResponses];

export type ExportBlockStockLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        block_id: number;
    };
    query?: never;
    url: '/v1/block-stock/{block_id}/lines/export';
};

export type ExportBlockStockLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBlockStockLinesResponses = {
    /**
     * The block's lines as .xlsx
     */
    200: unknown;
};

export type BlockStockAvailabilityData = {
    body: AvailabilityCheck;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/availability';
};

export type BlockStockAvailabilityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BlockStockAvailabilityResponses = {
    /**
     * Successful Response
     */
    200: Array<AvailabilityRow>;
};

export type BlockStockAvailabilityResponse = BlockStockAvailabilityResponses[keyof BlockStockAvailabilityResponses];

export type UploadBlockStockAvailabilityData = {
    body: BodyUploadBlockStockAvailability;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/availability/upload';
};

export type UploadBlockStockAvailabilityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UploadBlockStockAvailabilityResponses = {
    /**
     * Successful Response
     */
    200: Array<AvailabilityRow>;
};

export type UploadBlockStockAvailabilityResponse = UploadBlockStockAvailabilityResponses[keyof UploadBlockStockAvailabilityResponses];

export type BlockStockAvailabilityTemplateData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/availability/template';
};

export type BlockStockAvailabilityTemplateErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BlockStockAvailabilityTemplateResponses = {
    /**
     * An empty template as .xlsx
     */
    200: unknown;
};

export type ExportBlockStockAvailabilityData = {
    body: AvailabilityCheck;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/availability/export';
};

export type ExportBlockStockAvailabilityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBlockStockAvailabilityResponses = {
    /**
     * The check's answer as .xlsx
     */
    200: unknown;
};

export type MailBlockStockPackingData = {
    body: PackingMailWrite;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path: {
        block_id: number;
    };
    query?: never;
    url: '/v1/block-stock/{block_id}/mail-packing';
};

export type MailBlockStockPackingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type MailBlockStockPackingResponses = {
    /**
     * Successful Response
     */
    200: PackingMailRead;
};

export type MailBlockStockPackingResponse = MailBlockStockPackingResponses[keyof MailBlockStockPackingResponses];

export type UnblockBlockStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        block_id: number;
    };
    query?: never;
    url: '/v1/block-stock/{block_id}/unblock';
};

export type UnblockBlockStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UnblockBlockStockResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UnblockBlockStockResponse = UnblockBlockStockResponses[keyof UnblockBlockStockResponses];

export type ShiftStockData = {
    body: ShiftCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/shift';
};

export type ShiftStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ShiftStockResponses = {
    /**
     * Successful Response
     */
    201: ShiftRead;
};

export type ShiftStockResponse = ShiftStockResponses[keyof ShiftStockResponses];

export type ShiftStockByRetailerSkuData = {
    body: RetailerShiftCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/shift-by-retailer-sku';
};

export type ShiftStockByRetailerSkuErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ShiftStockByRetailerSkuResponses = {
    /**
     * Successful Response
     */
    201: ShiftRead;
};

export type ShiftStockByRetailerSkuResponse = ShiftStockByRetailerSkuResponses[keyof ShiftStockByRetailerSkuResponses];

export type UnblockAndShiftStockData = {
    body: UnblockShiftCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/block-stock/unblock-and-shift';
};

export type UnblockAndShiftStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UnblockAndShiftStockResponses = {
    /**
     * Successful Response
     */
    201: UnblockShiftRead;
};

export type UnblockAndShiftStockResponse = UnblockAndShiftStockResponses[keyof UnblockAndShiftStockResponses];

export type BoxHistoryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
        /**
         * Window start, inclusive
         */
        date_from: string;
        /**
         * Window end, exclusive
         */
        date_to: string;
        /**
         * Only these boxes
         */
        box_code?: Array<string> | null;
        /**
         * Only these SKUs
         */
        sku_code?: Array<string> | null;
    };
    url: '/v1/box-history';
};

export type BoxHistoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BoxHistoryResponses = {
    /**
     * Successful Response
     */
    200: BoxHistoryRead;
};

export type BoxHistoryResponse = BoxHistoryResponses[keyof BoxHistoryResponses];

export type ExportBoxHistoryInwardData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
        /**
         * Window start, inclusive
         */
        date_from: string;
        /**
         * Window end, exclusive
         */
        date_to: string;
        /**
         * Only these boxes
         */
        box_code?: Array<string> | null;
        /**
         * Only these SKUs
         */
        sku_code?: Array<string> | null;
    };
    url: '/v1/box-history/export/inward';
};

export type ExportBoxHistoryInwardErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBoxHistoryInwardResponses = {
    /**
     * The section, as .xlsx
     */
    200: unknown;
};

export type ExportBoxHistoryOutwardData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
        /**
         * Window start, inclusive
         */
        date_from: string;
        /**
         * Window end, exclusive
         */
        date_to: string;
        /**
         * Only these boxes
         */
        box_code?: Array<string> | null;
        /**
         * Only these SKUs
         */
        sku_code?: Array<string> | null;
    };
    url: '/v1/box-history/export/outward';
};

export type ExportBoxHistoryOutwardErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBoxHistoryOutwardResponses = {
    /**
     * The section, as .xlsx
     */
    200: unknown;
};

export type ExportBoxHistoryShiftData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
        /**
         * Window start, inclusive
         */
        date_from: string;
        /**
         * Window end, exclusive
         */
        date_to: string;
        /**
         * Only these boxes
         */
        box_code?: Array<string> | null;
        /**
         * Only these SKUs
         */
        sku_code?: Array<string> | null;
    };
    url: '/v1/box-history/export/shift';
};

export type ExportBoxHistoryShiftErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBoxHistoryShiftResponses = {
    /**
     * The section, as .xlsx
     */
    200: unknown;
};

export type ValidateWarehouseTransferData = {
    body: TransferRequest;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/warehouse-transfer/validate';
};

export type ValidateWarehouseTransferErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ValidateWarehouseTransferResponses = {
    /**
     * Successful Response
     */
    200: ValidationRead;
};

export type ValidateWarehouseTransferResponse = ValidateWarehouseTransferResponses[keyof ValidateWarehouseTransferResponses];

export type TransferWarehouseStockData = {
    body: TransferRequest;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/warehouse-transfer';
};

export type TransferWarehouseStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type TransferWarehouseStockResponses = {
    /**
     * Successful Response
     */
    200: TransferRead;
};

export type TransferWarehouseStockResponse = TransferWarehouseStockResponses[keyof TransferWarehouseStockResponses];

export type BoxLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/boxes/lookups';
};

export type BoxLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BoxLookupsResponses = {
    /**
     * Successful Response
     */
    200: Array<NamedRead>;
};

export type BoxLookupsResponse = BoxLookupsResponses[keyof BoxLookupsResponses];

export type BoxFilterChoicesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse to list
         */
        warehouse_id: number;
        /**
         * Created at or after
         */
        created_from?: string | null;
        /**
         * Created before
         */
        created_to?: string | null;
    };
    url: '/v1/boxes/filters';
};

export type BoxFilterChoicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BoxFilterChoicesResponses = {
    /**
     * Successful Response
     */
    200: BoxFiltersRead;
};

export type BoxFilterChoicesResponse = BoxFilterChoicesResponses[keyof BoxFilterChoicesResponses];

export type ListBoxesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * The warehouse to list
         */
        warehouse_id: number;
        /**
         * Only these box codes
         */
        box_code?: Array<string> | null;
        /**
         * Only boxes holding these SKUs
         */
        sku_code?: Array<string> | null;
        /**
         * Only these statuses
         */
        status?: Array<BoxStatus> | null;
        /**
         * Created at or after
         */
        created_from?: string | null;
        /**
         * Created before
         */
        created_to?: string | null;
        /**
         * Leave out empty boxes
         */
        exclude_zero?: boolean;
        /**
         * Count units less what valid blocks hold
         */
        exclude_blocked?: boolean;
        /**
         * Only boxes from this vendor
         */
        vendor_id?: number | null;
        /**
         * Only boxes for this retailer
         */
        retailer_id?: number | null;
        /**
         * Only boxes billed on this sales invoice
         */
        invoice_id?: number | null;
        /**
         * Only boxes received on this vendor invoice
         */
        vendor_invoice_id?: number | null;
        /**
         * Only boxes allotted to, or received for, this PO
         */
        po_id?: number | null;
        /**
         * Only boxes on this credit or debit note
         */
        note_id?: number | null;
        /**
         * Only boxes in this currency (ISO 4217)
         */
        currency?: string | null;
    };
    url: '/v1/boxes';
};

export type ListBoxesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListBoxesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceBoxRead;
};

export type ListBoxesResponse = ListBoxesResponses[keyof ListBoxesResponses];

export type ExportBoxesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse to list
         */
        warehouse_id: number;
        /**
         * Only these box codes
         */
        box_code?: Array<string> | null;
        /**
         * Only boxes holding these SKUs
         */
        sku_code?: Array<string> | null;
        /**
         * Only these statuses
         */
        status?: Array<BoxStatus> | null;
        /**
         * Created at or after
         */
        created_from?: string | null;
        /**
         * Created before
         */
        created_to?: string | null;
        /**
         * Leave out empty boxes
         */
        exclude_zero?: boolean;
        /**
         * Count units less what valid blocks hold
         */
        exclude_blocked?: boolean;
        /**
         * Only boxes from this vendor
         */
        vendor_id?: number | null;
        /**
         * Only boxes for this retailer
         */
        retailer_id?: number | null;
        /**
         * Only boxes billed on this sales invoice
         */
        invoice_id?: number | null;
        /**
         * Only boxes received on this vendor invoice
         */
        vendor_invoice_id?: number | null;
        /**
         * Only boxes allotted to, or received for, this PO
         */
        po_id?: number | null;
        /**
         * Only boxes on this credit or debit note
         */
        note_id?: number | null;
        /**
         * Only boxes in this currency (ISO 4217)
         */
        currency?: string | null;
    };
    url: '/v1/boxes/export';
};

export type ExportBoxesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBoxesResponses = {
    /**
     * Every listed box, as .xlsx
     */
    200: unknown;
};

export type BoxContentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse the boxes are in
         */
        warehouse_id: number;
        /**
         * The boxes to open
         */
        box_id: Array<number>;
    };
    url: '/v1/boxes/contents';
};

export type BoxContentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BoxContentsResponses = {
    /**
     * Successful Response
     */
    200: Array<BoxContentRead>;
};

export type BoxContentsResponse = BoxContentsResponses[keyof BoxContentsResponses];

export type CashflowLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/cashflow/lookups';
};

export type CashflowLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CashflowLookupsResponses = {
    /**
     * Successful Response
     */
    200: CashflowLookupsRead;
};

export type CashflowLookupsResponse = CashflowLookupsResponses[keyof CashflowLookupsResponses];

export type GetCashflowData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Cash in hand at the start of the window
         */
        opening_balance?: number | string | null;
        /**
         * The opening balance's currency
         */
        opening_currency?: string | null;
        /**
         * Start of the window
         */
        date_from?: string | null;
        /**
         * Day after the window's last day
         */
        date_to?: string | null;
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only these retailers' sales invoices
         */
        retailer_id?: Array<number> | null;
        /**
         * Only these vendors' bills
         */
        vendor_id?: Array<number> | null;
        /**
         * Only these service providers' bills
         */
        sp_id?: Array<number> | null;
    };
    url: '/v1/cashflow';
};

export type GetCashflowErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetCashflowResponses = {
    /**
     * Successful Response
     */
    200: CashflowRead;
};

export type GetCashflowResponse = GetCashflowResponses[keyof GetCashflowResponses];

export type ListCashflowDocumentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The row's currency
         */
        currency: string;
        /**
         * The row's week (a Monday); omit for the overdue row
         */
        week_start?: string | null;
        /**
         * Start of the window
         */
        date_from?: string | null;
        /**
         * Day after the window's last day
         */
        date_to?: string | null;
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only these retailers' sales invoices
         */
        retailer_id?: Array<number> | null;
        /**
         * Only these vendors' bills
         */
        vendor_id?: Array<number> | null;
        /**
         * Only these service providers' bills
         */
        sp_id?: Array<number> | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/cashflow/documents';
};

export type ListCashflowDocumentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListCashflowDocumentsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceCashflowDocumentRead;
};

export type ListCashflowDocumentsResponse = ListCashflowDocumentsResponses[keyof ListCashflowDocumentsResponses];

export type CreditDebitNotesLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/credit-debit-notes/lookups';
};

export type CreditDebitNotesLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreditDebitNotesLookupsResponses = {
    /**
     * Successful Response
     */
    200: CreditDebitNoteLookupsRead;
};

export type CreditDebitNotesLookupsResponse = CreditDebitNotesLookupsResponses[keyof CreditDebitNotesLookupsResponses];

export type ListCreditDebitNotesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Note number contains
         */
        q?: string | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * Type equals
         */
        kind?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/credit-debit-notes';
};

export type ListCreditDebitNotesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListCreditDebitNotesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceCreditDebitNoteRead;
};

export type ListCreditDebitNotesResponse = ListCreditDebitNotesResponses[keyof ListCreditDebitNotesResponses];

export type CreateCreditDebitNoteData = {
    body: CreditDebitNoteCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/credit-debit-notes';
};

export type CreateCreditDebitNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateCreditDebitNoteResponses = {
    /**
     * Successful Response
     */
    201: NoteDetailRead;
};

export type CreateCreditDebitNoteResponse = CreateCreditDebitNoteResponses[keyof CreateCreditDebitNoteResponses];

export type CreditDebitNoteLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        header_id: number;
    };
    query?: never;
    url: '/v1/credit-debit-notes/{header_id}/lines';
};

export type CreditDebitNoteLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreditDebitNoteLinesResponses = {
    /**
     * Successful Response
     */
    200: Array<CreditDebitNoteLineRead>;
};

export type CreditDebitNoteLinesResponse = CreditDebitNoteLinesResponses[keyof CreditDebitNoteLinesResponses];

export type CheckCreditDebitNoteData = {
    body: CreditDebitNoteCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/credit-debit-notes/check';
};

export type CheckCreditDebitNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckCreditDebitNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteCheckRead;
};

export type CheckCreditDebitNoteResponse = CheckCreditDebitNoteResponses[keyof CheckCreditDebitNoteResponses];

export type GetCreditDebitNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        note_id: number;
    };
    query?: never;
    url: '/v1/credit-debit-notes/{note_id}';
};

export type GetCreditDebitNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetCreditDebitNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteDetailRead;
};

export type GetCreditDebitNoteResponse = GetCreditDebitNoteResponses[keyof GetCreditDebitNoteResponses];

export type EditCreditDebitNoteData = {
    body: NoteEdit;
    headers?: {
        authorization?: string | null;
    };
    path: {
        note_id: number;
    };
    query?: never;
    url: '/v1/credit-debit-notes/{note_id}';
};

export type EditCreditDebitNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type EditCreditDebitNoteResponses = {
    /**
     * Successful Response
     */
    200: NoteDetailRead;
};

export type EditCreditDebitNoteResponse = EditCreditDebitNoteResponses[keyof EditCreditDebitNoteResponses];

export type ExportCreditDebitNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        note_id: number;
    };
    query?: never;
    url: '/v1/credit-debit-notes/{note_id}/export';
};

export type ExportCreditDebitNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportCreditDebitNoteResponses = {
    /**
     * The note's lines and total, as .xlsx
     */
    200: unknown;
};

export type VoidCreditDebitNoteData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        note_id: number;
    };
    query?: never;
    url: '/v1/credit-debit-notes/{note_id}/void';
};

export type VoidCreditDebitNoteErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VoidCreditDebitNoteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VoidCreditDebitNoteResponse = VoidCreditDebitNoteResponses[keyof VoidCreditDebitNoteResponses];

export type GlobalShipmentsLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/global-shipments/lookups';
};

export type GlobalShipmentsLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GlobalShipmentsLookupsResponses = {
    /**
     * Successful Response
     */
    200: GlobalShipmentLookupsRead;
};

export type GlobalShipmentsLookupsResponse = GlobalShipmentsLookupsResponses[keyof GlobalShipmentsLookupsResponses];

export type ListGlobalShipmentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Shipment contains
         */
        q?: string | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Shipping type equals
         */
        kind?: string | null;
        /**
         * Leaving this warehouse (the vendor order's)
         */
        from_warehouse_id?: number | null;
        /**
         * Arriving at this warehouse (the purchase order's)
         */
        to_warehouse_id?: number | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/global-shipments';
};

export type ListGlobalShipmentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListGlobalShipmentsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceGlobalShipmentRead;
};

export type ListGlobalShipmentsResponse = ListGlobalShipmentsResponses[keyof ListGlobalShipmentsResponses];

export type CreateGlobalShipmentData = {
    body: ShipmentCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/global-shipments';
};

export type CreateGlobalShipmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateGlobalShipmentResponses = {
    /**
     * Successful Response
     */
    201: ShipmentDetailRead;
};

export type CreateGlobalShipmentResponse = CreateGlobalShipmentResponses[keyof CreateGlobalShipmentResponses];

export type GetGlobalShipmentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        shipment_id: number;
    };
    query?: never;
    url: '/v1/global-shipments/{shipment_id}';
};

export type GetGlobalShipmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetGlobalShipmentResponses = {
    /**
     * Successful Response
     */
    200: ShipmentDetailRead;
};

export type GetGlobalShipmentResponse = GetGlobalShipmentResponses[keyof GetGlobalShipmentResponses];

export type EditGlobalShipmentData = {
    body: ShipmentEdit;
    headers?: {
        authorization?: string | null;
    };
    path: {
        shipment_id: number;
    };
    query?: never;
    url: '/v1/global-shipments/{shipment_id}';
};

export type EditGlobalShipmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type EditGlobalShipmentResponses = {
    /**
     * Successful Response
     */
    200: ShipmentDetailRead;
};

export type EditGlobalShipmentResponse = EditGlobalShipmentResponses[keyof EditGlobalShipmentResponses];

export type DeleteGlobalShipmentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        shipment_id: number;
    };
    query?: never;
    url: '/v1/global-shipments/{shipment_id}/delete';
};

export type DeleteGlobalShipmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteGlobalShipmentResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteGlobalShipmentResponse = DeleteGlobalShipmentResponses[keyof DeleteGlobalShipmentResponses];

export type ReplenLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/replen/lookups';
};

export type ReplenLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplenLookupsResponses = {
    /**
     * Successful Response
     */
    200: ReplenLookupsRead;
};

export type ReplenLookupsResponse = ReplenLookupsResponses[keyof ReplenLookupsResponses];

export type ListReplenSkusData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
        /**
         * Only these SKU codes
         */
        sku_code?: Array<string> | null;
        /**
         * SKU code or name contains
         */
        q?: string | null;
        /**
         * Brand contains
         */
        brand?: string | null;
        /**
         * Category or subcategory contains
         */
        category?: string | null;
        /**
         * Only SKUs with these retailer product codes
         */
        retailer_sku?: Array<string> | null;
        /**
         * Class in force; NONE for SKUs with none
         */
        replen_type?: ('R' | 'N' | 'T' | 'NONE') | null;
        /**
         * In focus or not
         */
        focus?: boolean | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/replen/skus';
};

export type ListReplenSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReplenSkusResponses = {
    /**
     * Successful Response
     */
    200: ListResourceReplenSkuRead;
};

export type ListReplenSkusResponse = ListReplenSkusResponses[keyof ListReplenSkusResponses];

export type CheckReplenSkusData = {
    body: SkuCheckWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/replen/sku-check';
};

export type CheckReplenSkusErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckReplenSkusResponses = {
    /**
     * Successful Response
     */
    200: SkuCheckRead;
};

export type CheckReplenSkusResponse = CheckReplenSkusResponses[keyof CheckReplenSkusResponses];

export type ApplyReplenChangesData = {
    body: ReplenChangesWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/replen/changes';
};

export type ApplyReplenChangesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ApplyReplenChangesResponses = {
    /**
     * Successful Response
     */
    200: ReplenResultRead;
};

export type ApplyReplenChangesResponse = ApplyReplenChangesResponses[keyof ApplyReplenChangesResponses];

export type ListPurchaseOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one warehouse
         */
        warehouse_id?: number | null;
        /**
         * Restrict to one retailer
         */
        retailer_id?: number | null;
        /**
         * Exact PO code
         */
        po_code?: string | null;
        /**
         * PO code contains
         */
        q?: string | null;
        /**
         * Has a line whose SKU code contains
         */
        sku_code?: string | null;
        /**
         * Has a line for this brand
         */
        brand_id?: number | null;
        /**
         * Created at or after
         */
        created_from?: string | null;
        /**
         * Created before
         */
        created_to?: string | null;
        /**
         * Ordering
         */
        sort?: PurchaseOrderSort;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/purchase-orders';
};

export type ListPurchaseOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListPurchaseOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourcePurchaseOrderRead;
};

export type ListPurchaseOrdersResponse = ListPurchaseOrdersResponses[keyof ListPurchaseOrdersResponses];

export type CreatePurchaseOrderData = {
    body: PurchaseOrderCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/purchase-orders';
};

export type CreatePurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreatePurchaseOrderResponses = {
    /**
     * Successful Response
     */
    201: PurchaseOrderDetail;
};

export type CreatePurchaseOrderResponse = CreatePurchaseOrderResponses[keyof CreatePurchaseOrderResponses];

export type PurchaseOrderLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/purchase-orders/lookups';
};

export type PurchaseOrderLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PurchaseOrderLookupsResponses = {
    /**
     * Successful Response
     */
    200: LookupsRead;
};

export type PurchaseOrderLookupsResponse = PurchaseOrderLookupsResponses[keyof PurchaseOrderLookupsResponses];

export type ListPurchaseOrderCodesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse to list
         */
        warehouse_id: number;
    };
    url: '/v1/purchase-orders/codes';
};

export type ListPurchaseOrderCodesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListPurchaseOrderCodesResponses = {
    /**
     * Successful Response
     */
    200: Array<PoCodeRead>;
};

export type ListPurchaseOrderCodesResponse = ListPurchaseOrderCodesResponses[keyof ListPurchaseOrderCodesResponses];

export type CheckPurchaseOrderData = {
    body: PurchaseOrderCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The PO being edited, if any
         */
        po_id?: number | null;
    };
    url: '/v1/purchase-orders/check';
};

export type CheckPurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckPurchaseOrderResponses = {
    /**
     * Successful Response
     */
    200: CheckResult;
};

export type CheckPurchaseOrderResponse = CheckPurchaseOrderResponses[keyof CheckPurchaseOrderResponses];

export type DeletePurchaseOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}';
};

export type DeletePurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeletePurchaseOrderResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeletePurchaseOrderResponse = DeletePurchaseOrderResponses[keyof DeletePurchaseOrderResponses];

export type GetPurchaseOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}';
};

export type GetPurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetPurchaseOrderResponses = {
    /**
     * Successful Response
     */
    200: PurchaseOrderDetail;
};

export type GetPurchaseOrderResponse = GetPurchaseOrderResponses[keyof GetPurchaseOrderResponses];

export type UpdatePurchaseOrderData = {
    body: PurchaseOrderUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}';
};

export type UpdatePurchaseOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdatePurchaseOrderResponses = {
    /**
     * Successful Response
     */
    200: PurchaseOrderDetail;
};

export type UpdatePurchaseOrderResponse = UpdatePurchaseOrderResponses[keyof UpdatePurchaseOrderResponses];

export type ExportPurchaseOrderLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/export';
};

export type ExportPurchaseOrderLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportPurchaseOrderLinesResponses = {
    /**
     * The lines as a workbook
     */
    200: unknown;
};

export type ReplacePurchaseOrderLinesData = {
    body: LinesWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/lines';
};

export type ReplacePurchaseOrderLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplacePurchaseOrderLinesResponses = {
    /**
     * Successful Response
     */
    200: PurchaseOrderDetail;
};

export type ReplacePurchaseOrderLinesResponse = ReplacePurchaseOrderLinesResponses[keyof ReplacePurchaseOrderLinesResponses];

export type SetPurchaseOrderAppointmentData = {
    body: AppointmentWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/appointment';
};

export type SetPurchaseOrderAppointmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SetPurchaseOrderAppointmentResponses = {
    /**
     * Successful Response
     */
    200: PurchaseOrderDetail;
};

export type SetPurchaseOrderAppointmentResponse = SetPurchaseOrderAppointmentResponses[keyof SetPurchaseOrderAppointmentResponses];

export type PurchaseOrderStockAvailabilityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/stock-availability';
};

export type PurchaseOrderStockAvailabilityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PurchaseOrderStockAvailabilityResponses = {
    /**
     * Successful Response
     */
    200: Array<AvailabilityRead>;
};

export type PurchaseOrderStockAvailabilityResponse = PurchaseOrderStockAvailabilityResponses[keyof PurchaseOrderStockAvailabilityResponses];

export type PurchaseOrderAllotmentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/allotment';
};

export type PurchaseOrderAllotmentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PurchaseOrderAllotmentResponses = {
    /**
     * Successful Response
     */
    200: AllotmentRead;
};

export type PurchaseOrderAllotmentResponse = PurchaseOrderAllotmentResponses[keyof PurchaseOrderAllotmentResponses];

export type AllotPurchaseOrderBoxesData = {
    body: AllotmentWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/allotment';
};

export type AllotPurchaseOrderBoxesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AllotPurchaseOrderBoxesResponses = {
    /**
     * Successful Response
     */
    200: AllotmentRead;
};

export type AllotPurchaseOrderBoxesResponse = AllotPurchaseOrderBoxesResponses[keyof AllotPurchaseOrderBoxesResponses];

export type MergePurchaseOrdersData = {
    body: MergeWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        po_id: number;
    };
    query?: never;
    url: '/v1/purchase-orders/{po_id}/merge';
};

export type MergePurchaseOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type MergePurchaseOrdersResponses = {
    /**
     * Successful Response
     */
    200: PurchaseOrderDetail;
};

export type MergePurchaseOrdersResponse = MergePurchaseOrdersResponses[keyof MergePurchaseOrdersResponses];

export type RetailerReceiptsLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/retailer-receipts/lookups';
};

export type RetailerReceiptsLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RetailerReceiptsLookupsResponses = {
    /**
     * Successful Response
     */
    200: RetailerReceiptLookupsRead;
};

export type RetailerReceiptsLookupsResponse = RetailerReceiptsLookupsResponses[keyof RetailerReceiptsLookupsResponses];

export type ListRetailerReceiptsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only receipts against sales invoices in this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only receipts against sales invoices with this retailer
         */
        party_id?: number | null;
        /**
         * Only receipts against this sales invoice
         */
        invoice_id?: number | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/retailer-receipts';
};

export type ListRetailerReceiptsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListRetailerReceiptsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceRetailerReceiptRead;
};

export type ListRetailerReceiptsResponse = ListRetailerReceiptsResponses[keyof ListRetailerReceiptsResponses];

export type RecordRetailerReceiptData = {
    body: SettlementWrite;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/retailer-receipts';
};

export type RecordRetailerReceiptErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordRetailerReceiptResponses = {
    /**
     * Successful Response
     */
    201: SettlementRead;
};

export type RecordRetailerReceiptResponse = RecordRetailerReceiptResponses[keyof RecordRetailerReceiptResponses];

export type VoidRetailerReceiptData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        retailer_receipt_id: number;
    };
    query?: never;
    url: '/v1/retailer-receipts/{retailer_receipt_id}/void';
};

export type VoidRetailerReceiptErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VoidRetailerReceiptResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VoidRetailerReceiptResponse = VoidRetailerReceiptResponses[keyof VoidRetailerReceiptResponses];

export type ListReviewDocumentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Which kind to list
         */
        entity_type: ReviewDocumentType;
        /**
         * Whose documents
         */
        warehouse_id: number;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/review/documents';
};

export type ListReviewDocumentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListReviewDocumentsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceReviewableDocumentRead;
};

export type ListReviewDocumentsResponse = ListReviewDocumentsResponses[keyof ListReviewDocumentsResponses];

export type GetReviewDocumentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        entity_type: ReviewDocumentType;
        entity_id: number;
    };
    query?: never;
    url: '/v1/review/documents/{entity_type}/{entity_id}';
};

export type GetReviewDocumentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetReviewDocumentResponses = {
    /**
     * Successful Response
     */
    200: ReviewDocumentRead;
};

export type GetReviewDocumentResponse = GetReviewDocumentResponses[keyof GetReviewDocumentResponses];

export type ExportReviewDocumentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        entity_type: ReviewDocumentType;
        entity_id: number;
    };
    query?: never;
    url: '/v1/review/documents/{entity_type}/{entity_id}/export';
};

export type ExportReviewDocumentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportReviewDocumentResponses = {
    /**
     * The lines as a workbook
     */
    200: unknown;
};

export type DecideReviewData = {
    body: ReviewDecisionWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review';
};

export type DecideReviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DecideReviewResponses = {
    /**
     * Successful Response
     */
    200: ReviewDecisionRead;
};

export type DecideReviewResponse = DecideReviewResponses[keyof DecideReviewResponses];

export type RequestReviewData = {
    body: ReviewRequestWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/review/request';
};

export type RequestReviewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RequestReviewResponses = {
    /**
     * Successful Response
     */
    200: ReviewRequestRead;
};

export type RequestReviewResponse = RequestReviewResponses[keyof RequestReviewResponses];

export type SalesInvoicesLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sales-invoices/lookups';
};

export type SalesInvoicesLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SalesInvoicesLookupsResponses = {
    /**
     * Successful Response
     */
    200: SalesInvoiceLookupsRead;
};

export type SalesInvoicesLookupsResponse = SalesInvoicesLookupsResponses[keyof SalesInvoicesLookupsResponses];

export type ListSalesInvoicesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this retailer
         */
        party_id?: number | null;
        /**
         * Invoice number contains
         */
        q?: string | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/sales-invoices';
};

export type ListSalesInvoicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSalesInvoicesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSalesInvoiceRead;
};

export type ListSalesInvoicesResponse = ListSalesInvoicesResponses[keyof ListSalesInvoicesResponses];

export type CreateSalesInvoiceData = {
    body: InvoiceCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sales-invoices';
};

export type CreateSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    201: InvoiceDetailRead;
};

export type CreateSalesInvoiceResponse = CreateSalesInvoiceResponses[keyof CreateSalesInvoiceResponses];

export type SalesInvoiceLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        header_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{header_id}/lines';
};

export type SalesInvoiceLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SalesInvoiceLinesResponses = {
    /**
     * Successful Response
     */
    200: Array<SalesInvoiceLineRead>;
};

export type SalesInvoiceLinesResponse = SalesInvoiceLinesResponses[keyof SalesInvoiceLinesResponses];

export type PreviewSalesInvoiceData = {
    body: PreviewWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sales-invoices/preview';
};

export type PreviewSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    200: Array<PreviewLineRead>;
};

export type PreviewSalesInvoiceResponse = PreviewSalesInvoiceResponses[keyof PreviewSalesInvoiceResponses];

export type CheckSalesInvoiceData = {
    body: InvoiceCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sales-invoices/check';
};

export type CheckSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    200: InvoiceCheckRead;
};

export type CheckSalesInvoiceResponse = CheckSalesInvoiceResponses[keyof CheckSalesInvoiceResponses];

export type DispatchSalesInvoicesData = {
    body: BulkMoveWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sales-invoices/dispatch';
};

export type DispatchSalesInvoicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DispatchSalesInvoicesResponses = {
    /**
     * Successful Response
     */
    200: BulkMoveResult;
};

export type DispatchSalesInvoicesResponse = DispatchSalesInvoicesResponses[keyof DispatchSalesInvoicesResponses];

export type DeliverSalesInvoicesData = {
    body: BulkMoveWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sales-invoices/deliver';
};

export type DeliverSalesInvoicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeliverSalesInvoicesResponses = {
    /**
     * Successful Response
     */
    200: BulkMoveResult;
};

export type DeliverSalesInvoicesResponse = DeliverSalesInvoicesResponses[keyof DeliverSalesInvoicesResponses];

export type GetSalesInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}';
};

export type GetSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    200: InvoiceDetailRead;
};

export type GetSalesInvoiceResponse = GetSalesInvoiceResponses[keyof GetSalesInvoiceResponses];

export type EditSalesInvoiceData = {
    body: InvoiceEdit;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}';
};

export type EditSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type EditSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    200: InvoiceDetailRead;
};

export type EditSalesInvoiceResponse = EditSalesInvoiceResponses[keyof EditSalesInvoiceResponses];

export type DeleteSalesInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/delete';
};

export type DeleteSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteSalesInvoiceResponse = DeleteSalesInvoiceResponses[keyof DeleteSalesInvoiceResponses];

export type AddSalesInvoiceBoxData = {
    body: BoxWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/boxes';
};

export type AddSalesInvoiceBoxErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type AddSalesInvoiceBoxResponses = {
    /**
     * Successful Response
     */
    200: InvoiceDetailRead;
};

export type AddSalesInvoiceBoxResponse = AddSalesInvoiceBoxResponses[keyof AddSalesInvoiceBoxResponses];

export type RemoveSalesInvoiceBoxData = {
    body: BoxWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/boxes/remove';
};

export type RemoveSalesInvoiceBoxErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RemoveSalesInvoiceBoxResponses = {
    /**
     * Successful Response
     */
    200: InvoiceDetailRead;
};

export type RemoveSalesInvoiceBoxResponse = RemoveSalesInvoiceBoxResponses[keyof RemoveSalesInvoiceBoxResponses];

export type SalesInvoiceMappableBoxesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/mappable-boxes';
};

export type SalesInvoiceMappableBoxesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SalesInvoiceMappableBoxesResponses = {
    /**
     * Successful Response
     */
    200: Array<MappableBoxRead>;
};

export type SalesInvoiceMappableBoxesResponse = SalesInvoiceMappableBoxesResponses[keyof SalesInvoiceMappableBoxesResponses];

export type SalesInvoicePackingData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/packing';
};

export type SalesInvoicePackingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SalesInvoicePackingResponses = {
    /**
     * Successful Response
     */
    200: Array<PackingRowRead>;
};

export type SalesInvoicePackingResponse = SalesInvoicePackingResponses[keyof SalesInvoicePackingResponses];

export type ExportSalesInvoicePackingData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/packing/export';
};

export type ExportSalesInvoicePackingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportSalesInvoicePackingResponses = {
    /**
     * Each SKU in each box, as .xlsx
     */
    200: unknown;
};

export type ExportSalesInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/export';
};

export type ExportSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportSalesInvoiceResponses = {
    /**
     * The invoice document, as .xlsx
     */
    200: unknown;
};

export type DispatchSalesInvoiceData = {
    body: DispatchWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/dispatch';
};

export type DispatchSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DispatchSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    200: InvoiceDetailRead;
};

export type DispatchSalesInvoiceResponse = DispatchSalesInvoiceResponses[keyof DispatchSalesInvoiceResponses];

export type DeliverSalesInvoiceData = {
    body: DeliverWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sales-invoices/{invoice_id}/deliver';
};

export type DeliverSalesInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeliverSalesInvoiceResponses = {
    /**
     * Successful Response
     */
    200: InvoiceDetailRead;
};

export type DeliverSalesInvoiceResponse = DeliverSalesInvoiceResponses[keyof DeliverSalesInvoiceResponses];

export type SellerFlexLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/seller-flex/lookups';
};

export type SellerFlexLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SellerFlexLookupsResponses = {
    /**
     * Successful Response
     */
    200: SellerFlexLookupsRead;
};

export type SellerFlexLookupsResponse = SellerFlexLookupsResponses[keyof SellerFlexLookupsResponses];

export type SellerFlexStoresData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse
         */
        warehouse_id: number;
    };
    url: '/v1/seller-flex/stores';
};

export type SellerFlexStoresErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SellerFlexStoresResponses = {
    /**
     * Successful Response
     */
    200: Array<SellerFlexStoreRead>;
};

export type SellerFlexStoresResponse = SellerFlexStoresResponses[keyof SellerFlexStoresResponses];

export type ListSellerFlexCountsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this store
         */
        store_id?: number | null;
        /**
         * SKU code or name contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/seller-flex';
};

export type ListSellerFlexCountsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSellerFlexCountsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSellerFlexRowRead;
};

export type ListSellerFlexCountsResponse = ListSellerFlexCountsResponses[keyof ListSellerFlexCountsResponses];

export type ExportSellerFlexCountsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this store
         */
        store_id?: number | null;
        /**
         * SKU code or name contains
         */
        q?: string | null;
    };
    url: '/v1/seller-flex/export';
};

export type ExportSellerFlexCountsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportSellerFlexCountsResponses = {
    /**
     * The register as a workbook
     */
    200: unknown;
};

export type SellerFlexSummaryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this store
         */
        store_id?: number | null;
        /**
         * SKU code or name contains
         */
        q?: string | null;
    };
    url: '/v1/seller-flex/summary';
};

export type SellerFlexSummaryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SellerFlexSummaryResponses = {
    /**
     * Successful Response
     */
    200: SellerFlexSummaryRead;
};

export type SellerFlexSummaryResponse = SellerFlexSummaryResponses[keyof SellerFlexSummaryResponses];

export type DeleteSellerFlexCountData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        count_id: number;
    };
    query?: never;
    url: '/v1/seller-flex/{count_id}/delete';
};

export type DeleteSellerFlexCountErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteSellerFlexCountResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteSellerFlexCountResponse = DeleteSellerFlexCountResponses[keyof DeleteSellerFlexCountResponses];

export type PreviewSheetData = {
    body: BodyPreviewSheet;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Rows to return, by number
         */
        rows?: Array<number> | null;
    };
    url: '/v1/sheets/preview';
};

export type PreviewSheetErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type PreviewSheetResponses = {
    /**
     * Successful Response
     */
    200: SheetPreviewRead;
};

export type PreviewSheetResponse = PreviewSheetResponses[keyof PreviewSheetResponses];

export type MarkSheetProblemsData = {
    body: BodyMarkSheetProblems;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sheets/marked';
};

export type MarkSheetProblemsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type MarkSheetProblemsResponses = {
    /**
     * The sheet with a Problems column
     */
    200: unknown;
};

export type SpInvoicesLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sp-invoices/lookups';
};

export type SpInvoicesLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SpInvoicesLookupsResponses = {
    /**
     * Successful Response
     */
    200: SpInvoiceLookupsRead;
};

export type SpInvoicesLookupsResponse = SpInvoicesLookupsResponses[keyof SpInvoicesLookupsResponses];

export type ListSpInvoicesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this service provider
         */
        party_id?: number | null;
        /**
         * Invoice number contains
         */
        q?: string | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * Type equals
         */
        kind?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/sp-invoices';
};

export type ListSpInvoicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSpInvoicesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSpInvoiceRead;
};

export type ListSpInvoicesResponse = ListSpInvoicesResponses[keyof ListSpInvoicesResponses];

export type CreateSpInvoiceData = {
    body: SpInvoiceWrite;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sp-invoices';
};

export type CreateSpInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateSpInvoiceResponses = {
    /**
     * Successful Response
     */
    201: SpInvoiceRead;
};

export type CreateSpInvoiceResponse = CreateSpInvoiceResponses[keyof CreateSpInvoiceResponses];

export type EditSpInvoiceData = {
    body: SpInvoiceWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sp-invoices/{invoice_id}';
};

export type EditSpInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type EditSpInvoiceResponses = {
    /**
     * Successful Response
     */
    200: SpInvoiceRead;
};

export type EditSpInvoiceResponse = EditSpInvoiceResponses[keyof EditSpInvoiceResponses];

export type DeleteSpInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/sp-invoices/{invoice_id}/delete';
};

export type DeleteSpInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteSpInvoiceResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteSpInvoiceResponse = DeleteSpInvoiceResponses[keyof DeleteSpInvoiceResponses];

export type SpPaymentsLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sp-payments/lookups';
};

export type SpPaymentsLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type SpPaymentsLookupsResponses = {
    /**
     * Successful Response
     */
    200: SpPaymentLookupsRead;
};

export type SpPaymentsLookupsResponse = SpPaymentsLookupsResponses[keyof SpPaymentsLookupsResponses];

export type ListSpPaymentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only payments against service provider invoices in this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only payments against service provider invoices with this service provider
         */
        party_id?: number | null;
        /**
         * Only payments against this service provider invoice
         */
        invoice_id?: number | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/sp-payments';
};

export type ListSpPaymentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListSpPaymentsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSpPaymentRead;
};

export type ListSpPaymentsResponse = ListSpPaymentsResponses[keyof ListSpPaymentsResponses];

export type RecordSpPaymentData = {
    body: SettlementWrite;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/sp-payments';
};

export type RecordSpPaymentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordSpPaymentResponses = {
    /**
     * Successful Response
     */
    201: SettlementRead;
};

export type RecordSpPaymentResponse = RecordSpPaymentResponses[keyof RecordSpPaymentResponses];

export type VoidSpPaymentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        sp_payment_id: number;
    };
    query?: never;
    url: '/v1/sp-payments/{sp_payment_id}/void';
};

export type VoidSpPaymentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VoidSpPaymentResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VoidSpPaymentResponse = VoidSpPaymentResponses[keyof VoidSpPaymentResponses];

export type StockLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stock/lookups';
};

export type StockLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StockLookupsResponses = {
    /**
     * Successful Response
     */
    200: Array<NamedRead>;
};

export type StockLookupsResponse = StockLookupsResponses[keyof StockLookupsResponses];

export type StockFiltersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse to read
         */
        warehouse_id: number;
    };
    url: '/v1/stock/filters';
};

export type StockFiltersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StockFiltersResponses = {
    /**
     * Successful Response
     */
    200: StockFiltersRead;
};

export type StockFiltersResponse = StockFiltersResponses[keyof StockFiltersResponses];

export type GetStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
        /**
         * The warehouse to read
         */
        warehouse_id: number;
        /**
         * Only these SKU codes
         */
        sku_code?: Array<string> | null;
        /**
         * SKU name contains
         */
        name?: string | null;
        /**
         * Brand name contains
         */
        brand?: string | null;
        /**
         * Only SKUs this vendor sources here
         */
        vendor_id?: number | null;
        /**
         * Only SKUs this retailer sells; shows its codes
         */
        retailer_id?: number | null;
        /**
         * Only SKUs with these retailer product codes
         */
        retailer_sku?: Array<string> | null;
        /**
         * Count allotted boxes in box quantity
         */
        include_allotted?: boolean;
        /**
         * Take blocked units out of available
         */
        exclude_blocked?: boolean;
    };
    url: '/v1/stock';
};

export type GetStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetStockResponses = {
    /**
     * Successful Response
     */
    200: ListResourceStockRead;
};

export type GetStockResponse = GetStockResponses[keyof GetStockResponses];

export type ExportStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse to read
         */
        warehouse_id: number;
        /**
         * Only these SKU codes
         */
        sku_code?: Array<string> | null;
        /**
         * SKU name contains
         */
        name?: string | null;
        /**
         * Brand name contains
         */
        brand?: string | null;
        /**
         * Only SKUs this vendor sources here
         */
        vendor_id?: number | null;
        /**
         * Only SKUs this retailer sells; shows its codes
         */
        retailer_id?: number | null;
        /**
         * Only SKUs with these retailer product codes
         */
        retailer_sku?: Array<string> | null;
        /**
         * Count allotted boxes in box quantity
         */
        include_allotted?: boolean;
        /**
         * Take blocked units out of available
         */
        exclude_blocked?: boolean;
    };
    url: '/v1/stock/export';
};

export type ExportStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportStockResponses = {
    /**
     * Every listed SKU, as .xlsx
     */
    200: unknown;
};

export type StockAgeingLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/stock-ageing/lookups';
};

export type StockAgeingLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StockAgeingLookupsResponses = {
    /**
     * Successful Response
     */
    200: StockAgeingLookupsRead;
};

export type StockAgeingLookupsResponse = StockAgeingLookupsResponses[keyof StockAgeingLookupsResponses];

export type ListStockAgeingData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse; ages are per warehouse
         */
        warehouse_id: number;
        /**
         * SKU code or name contains
         */
        q?: string | null;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/stock-ageing';
};

export type ListStockAgeingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListStockAgeingResponses = {
    /**
     * Successful Response
     */
    200: ListResourceSkuAgeingRead;
};

export type ListStockAgeingResponse = ListStockAgeingResponses[keyof ListStockAgeingResponses];

export type StockAgeingCoverageData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse; ages are per warehouse
         */
        warehouse_id: number;
    };
    url: '/v1/stock-ageing/coverage';
};

export type StockAgeingCoverageErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StockAgeingCoverageResponses = {
    /**
     * Successful Response
     */
    200: AgeingCoverageRead;
};

export type StockAgeingCoverageResponse = StockAgeingCoverageResponses[keyof StockAgeingCoverageResponses];

export type StockAgeingBoxesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse; ages are per warehouse
         */
        warehouse_id: number;
        /**
         * The SKU
         */
        sku_id: number;
    };
    url: '/v1/stock-ageing/boxes';
};

export type StockAgeingBoxesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type StockAgeingBoxesResponses = {
    /**
     * Successful Response
     */
    200: Array<BoxAgeingRead>;
};

export type StockAgeingBoxesResponse = StockAgeingBoxesResponses[keyof StockAgeingBoxesResponses];

export type ExportStockAgeingData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse; ages are per warehouse
         */
        warehouse_id: number;
        /**
         * SKU code or name contains
         */
        q?: string | null;
    };
    url: '/v1/stock-ageing/export';
};

export type ExportStockAgeingErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportStockAgeingResponses = {
    /**
     * The SKU grid as .xlsx
     */
    200: unknown;
};

export type VendorInvoicesLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-invoices/lookups';
};

export type VendorInvoicesLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VendorInvoicesLookupsResponses = {
    /**
     * Successful Response
     */
    200: VendorInvoiceLookupsRead;
};

export type VendorInvoicesLookupsResponse = VendorInvoicesLookupsResponses[keyof VendorInvoicesLookupsResponses];

export type ListVendorInvoicesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only this vendor
         */
        party_id?: number | null;
        /**
         * Invoice number contains
         */
        q?: string | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/vendor-invoices';
};

export type ListVendorInvoicesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorInvoicesResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVendorInvoiceRead;
};

export type ListVendorInvoicesResponse = ListVendorInvoicesResponses[keyof ListVendorInvoicesResponses];

export type CreateVendorInvoiceData = {
    body: VendorInvoiceWrite;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-invoices';
};

export type CreateVendorInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateVendorInvoiceResponses = {
    /**
     * Successful Response
     */
    201: VendorInvoiceDetailRead;
};

export type CreateVendorInvoiceResponse = CreateVendorInvoiceResponses[keyof CreateVendorInvoiceResponses];

export type VendorInvoiceLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        header_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{header_id}/lines';
};

export type VendorInvoiceLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VendorInvoiceLinesResponses = {
    /**
     * Successful Response
     */
    200: Array<VendorInvoiceLineRead>;
};

export type VendorInvoiceLinesResponse = VendorInvoiceLinesResponses[keyof VendorInvoiceLinesResponses];

export type CheckVendorInvoiceData = {
    body: VendorInvoiceWrite;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-invoices/check';
};

export type CheckVendorInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckVendorInvoiceResponses = {
    /**
     * Successful Response
     */
    200: VendorInvoiceCheckRead;
};

export type CheckVendorInvoiceResponse = CheckVendorInvoiceResponses[keyof CheckVendorInvoiceResponses];

export type GetVendorInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{invoice_id}';
};

export type GetVendorInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorInvoiceResponses = {
    /**
     * Successful Response
     */
    200: VendorInvoiceDetailRead;
};

export type GetVendorInvoiceResponse = GetVendorInvoiceResponses[keyof GetVendorInvoiceResponses];

export type EditVendorInvoiceData = {
    body: VendorInvoiceEdit;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{invoice_id}';
};

export type EditVendorInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type EditVendorInvoiceResponses = {
    /**
     * Successful Response
     */
    200: VendorInvoiceDetailRead;
};

export type EditVendorInvoiceResponse = EditVendorInvoiceResponses[keyof EditVendorInvoiceResponses];

export type ExportVendorInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{invoice_id}/export';
};

export type ExportVendorInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportVendorInvoiceResponses = {
    /**
     * The invoice as a workbook
     */
    200: unknown;
};

export type CheckVendorInvoiceLinesData = {
    body: VendorInvoiceLinesWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{invoice_id}/lines/check';
};

export type CheckVendorInvoiceLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckVendorInvoiceLinesResponses = {
    /**
     * Successful Response
     */
    200: VendorInvoiceCheckRead;
};

export type CheckVendorInvoiceLinesResponse = CheckVendorInvoiceLinesResponses[keyof CheckVendorInvoiceLinesResponses];

export type ReplaceVendorInvoiceLinesData = {
    body: VendorInvoiceLinesWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{invoice_id}/lines';
};

export type ReplaceVendorInvoiceLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceVendorInvoiceLinesResponses = {
    /**
     * Successful Response
     */
    200: VendorInvoiceDetailRead;
};

export type ReplaceVendorInvoiceLinesResponse = ReplaceVendorInvoiceLinesResponses[keyof ReplaceVendorInvoiceLinesResponses];

export type DeleteVendorInvoiceData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        invoice_id: number;
    };
    query?: never;
    url: '/v1/vendor-invoices/{invoice_id}/delete';
};

export type DeleteVendorInvoiceErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteVendorInvoiceResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteVendorInvoiceResponse = DeleteVendorInvoiceResponses[keyof DeleteVendorInvoiceResponses];

export type ListVendorOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Restrict to one warehouse
         */
        warehouse_id?: number | null;
        /**
         * Restrict to one vendor
         */
        vendor_id?: number | null;
        /**
         * Exact code
         */
        vo_code?: string | null;
        /**
         * Code contains
         */
        q?: string | null;
        /**
         * Has a line whose SKU code contains
         */
        sku_code?: string | null;
        /**
         * Created at or after
         */
        created_from?: string | null;
        /**
         * Created before
         */
        created_to?: string | null;
        /**
         * Ordering
         */
        sort?: VendorOrderSort;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/vendor-orders';
};

export type ListVendorOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorOrdersResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVendorOrderRead;
};

export type ListVendorOrdersResponse = ListVendorOrdersResponses[keyof ListVendorOrdersResponses];

export type CreateVendorOrderData = {
    body: VendorOrderCreate;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-orders';
};

export type CreateVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CreateVendorOrderResponses = {
    /**
     * Successful Response
     */
    201: VendorOrderDetail;
};

export type CreateVendorOrderResponse = CreateVendorOrderResponses[keyof CreateVendorOrderResponses];

export type VendorOrderLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-orders/lookups';
};

export type VendorOrderLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VendorOrderLookupsResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderLookupsRead;
};

export type VendorOrderLookupsResponse = VendorOrderLookupsResponses[keyof VendorOrderLookupsResponses];

export type ListVendorOrderCodesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The warehouse to list
         */
        warehouse_id: number;
    };
    url: '/v1/vendor-orders/codes';
};

export type ListVendorOrderCodesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorOrderCodesResponses = {
    /**
     * Successful Response
     */
    200: Array<VendorOrderCodeRead>;
};

export type ListVendorOrderCodesResponse = ListVendorOrderCodesResponses[keyof ListVendorOrderCodesResponses];

export type CheckVendorOrderData = {
    body: VendorOrderCreate;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * The order being edited, if any
         */
        vo_id?: number | null;
    };
    url: '/v1/vendor-orders/check';
};

export type CheckVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type CheckVendorOrderResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderCheckResult;
};

export type CheckVendorOrderResponse = CheckVendorOrderResponses[keyof CheckVendorOrderResponses];

export type DeleteVendorOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}';
};

export type DeleteVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type DeleteVendorOrderResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteVendorOrderResponse = DeleteVendorOrderResponses[keyof DeleteVendorOrderResponses];

export type GetVendorOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}';
};

export type GetVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type GetVendorOrderResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderDetail;
};

export type GetVendorOrderResponse = GetVendorOrderResponses[keyof GetVendorOrderResponses];

export type UpdateVendorOrderData = {
    body: VendorOrderUpdate;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}';
};

export type UpdateVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type UpdateVendorOrderResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderDetail;
};

export type UpdateVendorOrderResponse = UpdateVendorOrderResponses[keyof UpdateVendorOrderResponses];

export type ShiftVendorOrderData = {
    body: VendorOrderShift;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}/shift';
};

export type ShiftVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ShiftVendorOrderResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderDetail;
};

export type ShiftVendorOrderResponse = ShiftVendorOrderResponses[keyof ShiftVendorOrderResponses];

export type MergeVendorOrdersData = {
    body: VendorOrderMerge;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}/merge';
};

export type MergeVendorOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type MergeVendorOrdersResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderDetail;
};

export type MergeVendorOrdersResponse = MergeVendorOrdersResponses[keyof MergeVendorOrdersResponses];

export type ReplaceVendorOrderLinesData = {
    body: VendorOrderLinesWrite;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}/lines';
};

export type ReplaceVendorOrderLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReplaceVendorOrderLinesResponses = {
    /**
     * Successful Response
     */
    200: VendorOrderDetail;
};

export type ReplaceVendorOrderLinesResponse = ReplaceVendorOrderLinesResponses[keyof ReplaceVendorOrderLinesResponses];

export type ExportVendorOrderData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vo_id: number;
    };
    query?: never;
    url: '/v1/vendor-orders/{vo_id}/export';
};

export type ExportVendorOrderErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportVendorOrderResponses = {
    /**
     * An .xlsx of the lines
     */
    200: unknown;
};

export type VendorPaymentsLookupsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-payments/lookups';
};

export type VendorPaymentsLookupsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VendorPaymentsLookupsResponses = {
    /**
     * Successful Response
     */
    200: VendorPaymentLookupsRead;
};

export type VendorPaymentsLookupsResponse = VendorPaymentsLookupsResponses[keyof VendorPaymentsLookupsResponses];

export type ListVendorPaymentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Only payments against vendor invoices in this warehouse
         */
        warehouse_id?: number | null;
        /**
         * Only payments against vendor invoices with this vendor
         */
        party_id?: number | null;
        /**
         * Only payments against this vendor invoice
         */
        invoice_id?: number | null;
        /**
         * On or after
         */
        date_from?: string | null;
        /**
         * Before
         */
        date_to?: string | null;
        /**
         * Include voided rows
         */
        include_void?: boolean;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/vendor-payments';
};

export type ListVendorPaymentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ListVendorPaymentsResponses = {
    /**
     * Successful Response
     */
    200: ListResourceVendorPaymentRead;
};

export type ListVendorPaymentsResponse = ListVendorPaymentsResponses[keyof ListVendorPaymentsResponses];

export type RecordVendorPaymentData = {
    body: SettlementWrite;
    headers?: {
        authorization?: string | null;
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/vendor-payments';
};

export type RecordVendorPaymentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type RecordVendorPaymentResponses = {
    /**
     * Successful Response
     */
    201: SettlementRead;
};

export type RecordVendorPaymentResponse = RecordVendorPaymentResponses[keyof RecordVendorPaymentResponses];

export type VoidVendorPaymentData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path: {
        vendor_payment_id: number;
    };
    query?: never;
    url: '/v1/vendor-payments/{vendor_payment_id}/void';
};

export type VoidVendorPaymentErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type VoidVendorPaymentResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VoidVendorPaymentResponse = VoidVendorPaymentResponses[keyof VoidVendorPaymentResponses];

export type ReportOptionsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query?: never;
    url: '/v1/reports/options';
};

export type ReportOptionsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportOptionsResponses = {
    /**
     * Successful Response
     */
    200: ReportOptions;
};

export type ReportOptionsResponse = ReportOptionsResponses[keyof ReportOptionsResponses];

export type ReportMpSalesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<MpSalesGrouping>;
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * Read each order's day in this IANA zone, e.g. Asia/Dubai, rather than its marketplace's own, the default.
         */
        tz?: string | null;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/mp-sales';
};

export type ReportMpSalesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportMpSalesResponses = {
    /**
     * Successful Response
     */
    200: MpSalesReport;
};

export type ReportMpSalesResponse = ReportMpSalesResponses[keyof ReportMpSalesResponses];

export type ReportNonmpSalesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<NonMpSalesGrouping>;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        warehouse_ids?: Array<number> | null;
        retailer_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/nonmp-sales';
};

export type ReportNonmpSalesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportNonmpSalesResponses = {
    /**
     * Successful Response
     */
    200: NonMpSalesReport;
};

export type ReportNonmpSalesResponse = ReportNonmpSalesResponses[keyof ReportNonmpSalesResponses];

export type ReportMpPaymentsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<MpPaymentsGrouping>;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * payment: the day paid (default); order: the day ordered.
         */
        date_basis?: DateBasis;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/mp-payments';
};

export type ReportMpPaymentsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportMpPaymentsResponses = {
    /**
     * Successful Response
     */
    200: MpPaymentsReport;
};

export type ReportMpPaymentsResponse = ReportMpPaymentsResponses[keyof ReportMpPaymentsResponses];

export type ReportMpPayoutsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<MpPayoutsGrouping>;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/mp-payouts';
};

export type ReportMpPayoutsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportMpPayoutsResponses = {
    /**
     * Successful Response
     */
    200: MpPayoutsReport;
};

export type ReportMpPayoutsResponse = ReportMpPayoutsResponses[keyof ReportMpPayoutsResponses];

export type ReportNonmpReceiptsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<NonMpReceiptsGrouping>;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        warehouse_ids?: Array<number> | null;
        retailer_ids?: Array<number> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/nonmp-receipts';
};

export type ReportNonmpReceiptsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportNonmpReceiptsResponses = {
    /**
     * Successful Response
     */
    200: NonMpReceiptsReport;
};

export type ReportNonmpReceiptsResponse = ReportNonmpReceiptsResponses[keyof ReportNonmpReceiptsResponses];

export type ReportReceivablesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<ReceivablesGrouping>;
        warehouse_ids?: Array<number> | null;
        retailer_ids?: Array<number> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/receivables';
};

export type ReportReceivablesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportReceivablesResponses = {
    /**
     * Successful Response
     */
    200: ReceivablesReport;
};

export type ReportReceivablesResponse = ReportReceivablesResponses[keyof ReportReceivablesResponses];

export type ReportPnlData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<PnlGrouping>;
        /**
         * Leave out invoices to our own companies.
         */
        exclude_intercompany?: boolean;
        /**
         * Only the groups whose CP is below zero; the totals are theirs.
         */
        loss_only?: boolean;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * Warehouses: the invoices they raised and the stores linked to them.
         */
        warehouse_ids?: Array<number> | null;
        /**
         * Marketplaces: their stores' order lines and ads.
         */
        marketplace_ids?: Array<number> | null;
        /**
         * Stores: their order lines and ads.
         */
        store_ids?: Array<number> | null;
        /**
         * Retailers: their invoices. Given with stores or marketplaces, the report holds both: those stores' lines and these retailers' invoices.
         */
        retailer_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/pnl';
};

export type ReportPnlErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportPnlResponses = {
    /**
     * Successful Response
     */
    200: PnlReport;
};

export type ReportPnlResponse = ReportPnlResponses[keyof ReportPnlResponses];

export type ReportSkuProfitabilityData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * loss: the loss-makers only (the default). all: every row.
         */
        view?: ProfitabilityView;
        /**
         * sku: a SKU at a store (the default). store or marketplace: the SKUs summed to it — Partner View's levels; these take view=all only.
         */
        level?: ProfitabilityLevel;
        /**
         * Warehouses: their invoices, the stores linked to them, their stock.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        /**
         * Text the SKU code or name, the brand or the store contains, in any case.
         */
        search?: string | null;
        /**
         * A field of the row. Unset: profit, worst first, for the loss-makers; sales, biggest first, for every row.
         */
        sort?: string | null;
        /**
         * Unset: the view's own order, else ascending.
         */
        order?: VentawizReportsQuerySortOrder | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/sku-profitability';
};

export type ReportSkuProfitabilityErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportSkuProfitabilityResponses = {
    /**
     * Successful Response
     */
    200: SkuProfitabilityReport;
};

export type ReportSkuProfitabilityResponse = ReportSkuProfitabilityResponses[keyof ReportSkuProfitabilityResponses];

export type ReportSkuOrdersData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The SKU row's store.
         */
        store_id: number;
        /**
         * The SKU row's SKU.
         */
        sku_id: number;
        /**
         * The SKU row's currency, ISO 4217.
         */
        currency_code: string;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/sku-orders';
};

export type ReportSkuOrdersErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportSkuOrdersResponses = {
    /**
     * Successful Response
     */
    200: SkuOrdersReport;
};

export type ReportSkuOrdersResponse = ReportSkuOrdersResponses[keyof ReportSkuOrdersResponses];

export type ReportSkuInvoiceLinesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The SKU row's store.
         */
        store_id: number;
        /**
         * The SKU row's SKU.
         */
        sku_id: number;
        /**
         * The SKU row's currency, ISO 4217.
         */
        currency_code: string;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/sku-invoice-lines';
};

export type ReportSkuInvoiceLinesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportSkuInvoiceLinesResponses = {
    /**
     * Successful Response
     */
    200: SkuInvoiceLinesReport;
};

export type ReportSkuInvoiceLinesResponse = ReportSkuInvoiceLinesResponses[keyof ReportSkuInvoiceLinesResponses];

export type ReportWhStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<WhStockGrouping>;
        warehouse_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/wh-stock';
};

export type ReportWhStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportWhStockResponses = {
    /**
     * Successful Response
     */
    200: WhStockReport;
};

export type ReportWhStockResponse = ReportWhStockResponses[keyof ReportWhStockResponses];

export type ReportFbaStockData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<FbaStockGrouping>;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/fba-stock';
};

export type ReportFbaStockErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportFbaStockResponses = {
    /**
     * Successful Response
     */
    200: FbaStockReport;
};

export type ReportFbaStockResponse = ReportFbaStockResponses[keyof ReportFbaStockResponses];

export type ReportInTransitData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<InTransitGrouping>;
        warehouse_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/in-transit';
};

export type ReportInTransitErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportInTransitResponses = {
    /**
     * Successful Response
     */
    200: InTransitReport;
};

export type ReportInTransitResponse = ReportInTransitResponses[keyof ReportInTransitResponses];

export type ReportAdsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<AdsGrouping>;
        /**
         * Convert every amount into this ISO 4217 currency at the latest exchange rates, and merge the currencies into one. A currency with no rate is left out and listed in fx.unconverted. Unset, amounts stay in their own currencies.
         */
        convert_to?: string | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/ads';
};

export type ReportAdsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportAdsResponses = {
    /**
     * Successful Response
     */
    200: AdsReport;
};

export type ReportAdsResponse = ReportAdsResponses[keyof ReportAdsResponses];

export type ReportTrafficData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<TrafficGrouping>;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/traffic';
};

export type ReportTrafficErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportTrafficResponses = {
    /**
     * Successful Response
     */
    200: TrafficReport;
};

export type ReportTrafficResponse = ReportTrafficResponses[keyof ReportTrafficResponses];

export type ReportVendorSalesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<VendorSalesGrouping>;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/vendor-sales';
};

export type ReportVendorSalesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportVendorSalesResponses = {
    /**
     * Successful Response
     */
    200: VendorSalesReport;
};

export type ReportVendorSalesResponse = ReportVendorSalesResponses[keyof ReportVendorSalesResponses];

export type ReportVendorInventoryData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<VendorInventoryGrouping>;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/vendor-inventory';
};

export type ReportVendorInventoryErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportVendorInventoryResponses = {
    /**
     * Successful Response
     */
    200: VendorInventoryReport;
};

export type ReportVendorInventoryResponse = ReportVendorInventoryResponses[keyof ReportVendorInventoryResponses];

export type ReportListingsData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Group by these, in order.
         */
        group_by: Array<ListingsGrouping>;
        /**
         * Warehouses: the stores linked to any of them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/listings';
};

export type ReportListingsErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportListingsResponses = {
    /**
     * Successful Response
     */
    200: ListingsReport;
};

export type ReportListingsResponse = ReportListingsResponses[keyof ReportListingsResponses];

export type ReportBusinessViewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Warehouses: their stock and invoices, and the stores linked to them. Unset with marketplaces, the warehouses linked to their stores.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Stores: their order lines, listings and ads, and the warehouses linked to them. Narrows the marketplaces.
         */
        store_ids?: Array<number> | null;
        /**
         * sku: one row per SKU; listing: one per ASIN and seller SKU.
         */
        view?: Grain;
        /**
         * Sort keys, first first, each `field` or `field:asc` / `field:desc`. An amount sorts only when the view holds one currency. Given, `sort` and `order` are ignored.
         */
        sorts?: Array<string> | null;
        /**
         * Conditions every row must pass, as a JSON list of {"field", "operator", "value"}: text fields take contains, equals, startsWith, endsWith, isEmpty, isNotEmpty, isAnyOf and isNoneOf (a list); figures take equals, !=, >, >=, <, <=, isEmpty and isNotEmpty.
         */
        filter?: string | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/business-view';
};

export type ReportBusinessViewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportBusinessViewResponses = {
    /**
     * Successful Response
     */
    200: BusinessViewReport;
};

export type ReportBusinessViewResponse = ReportBusinessViewResponses[keyof ReportBusinessViewResponses];

export type BusinessViewValuesData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * A text field of the row
         */
        field: string;
        /**
         * Values containing this, any case
         */
        q?: string | null;
        /**
         * next_cursor from the previous page
         */
        cursor?: string | null;
        /**
         * Values per page
         */
        limit?: number;
        /**
         * Warehouses: their stock and invoices, and the stores linked to them. Unset with marketplaces, the warehouses linked to their stores.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Stores: their order lines, listings and ads, and the warehouses linked to them. Narrows the marketplaces.
         */
        store_ids?: Array<number> | null;
        /**
         * sku: one row per SKU; listing: one per ASIN and seller SKU.
         */
        view?: Grain;
        /**
         * Sort keys, first first, each `field` or `field:asc` / `field:desc`. An amount sorts only when the view holds one currency. Given, `sort` and `order` are ignored.
         */
        sorts?: Array<string> | null;
        /**
         * Conditions every row must pass, as a JSON list of {"field", "operator", "value"}: text fields take contains, equals, startsWith, endsWith, isEmpty, isNotEmpty, isAnyOf and isNoneOf (a list); figures take equals, !=, >, >=, <, <=, isEmpty and isNotEmpty.
         */
        filter?: string | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
    };
    url: '/v1/reports/business-view/values';
};

export type BusinessViewValuesErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type BusinessViewValuesResponses = {
    /**
     * Successful Response
     */
    200: BusinessViewValues;
};

export type BusinessViewValuesResponse = BusinessViewValuesResponses[keyof BusinessViewValuesResponses];

export type ExportBusinessViewData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Warehouses: their stock and invoices, and the stores linked to them. Unset with marketplaces, the warehouses linked to their stores.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        /**
         * Stores: their order lines, listings and ads, and the warehouses linked to them. Narrows the marketplaces.
         */
        store_ids?: Array<number> | null;
        /**
         * sku: one row per SKU; listing: one per ASIN and seller SKU.
         */
        view?: Grain;
        /**
         * Sort keys, first first, each `field` or `field:asc` / `field:desc`. An amount sorts only when the view holds one currency. Given, `sort` and `order` are ignored.
         */
        sorts?: Array<string> | null;
        /**
         * Conditions every row must pass, as a JSON list of {"field", "operator", "value"}: text fields take contains, equals, startsWith, endsWith, isEmpty, isNotEmpty, isAnyOf and isNoneOf (a list); figures take equals, !=, >, >=, <, <=, isEmpty and isNotEmpty.
         */
        filter?: string | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
    };
    url: '/v1/reports/business-view/export';
};

export type ExportBusinessViewErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ExportBusinessViewResponses = {
    /**
     * The workbook
     */
    200: unknown;
};

export type ReportPaymentReconciliationData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * Only the orders of stores linked to these warehouses.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        brand_ids?: Array<number> | null;
        /**
         * Text the order number, the SKU code, the ASIN or the seller SKU contains, in any case.
         */
        search?: string | null;
        /**
         * Only the lines standing here: any of matched, unmatched, partially_matched, returned.
         */
        status?: Array<ReconciliationStatus> | null;
        /**
         * First day, inclusive.
         */
        date_from: string;
        /**
         * Last day, inclusive.
         */
        date_to: string;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/payment-reconciliation';
};

export type ReportPaymentReconciliationErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportPaymentReconciliationResponses = {
    /**
     * Successful Response
     */
    200: PaymentReconciliationReport;
};

export type ReportPaymentReconciliationResponse = ReportPaymentReconciliationResponses[keyof ReportPaymentReconciliationResponses];

export type ReportReviewDataData = {
    body?: never;
    headers?: {
        authorization?: string | null;
    };
    path?: never;
    query: {
        /**
         * The previous period's first day.
         */
        previous_from: string;
        /**
         * The previous period's last day.
         */
        previous_to: string;
        /**
         * The current period's first day.
         */
        current_from: string;
        /**
         * The current period's last day.
         */
        current_to: string;
        /**
         * Warehouses: their stock and invoices, and the stores linked to them.
         */
        warehouse_ids?: Array<number> | null;
        marketplace_ids?: Array<number> | null;
        store_ids?: Array<number> | null;
        /**
         * Only the SKUs focused at a store in scope.
         */
        focused_only?: boolean;
        /**
         * Leave out the SKUs that neither sold nor hold stock.
         */
        hide_inactive?: boolean;
        brand_ids?: Array<number> | null;
        category_keys?: Array<string> | null;
        subcategory_ids?: Array<string> | null;
        /**
         * Text a code contains, in any case: the SKU code, the listing's ASIN and seller SKU, and the report's own codes (box, order, seller SKU) where it has them.
         */
        search?: string | null;
        /**
         * SKUs by exact code; the rows of any of them.
         */
        sku_codes?: Array<string> | null;
        /**
         * A field of the rows as grouped: a grouping's fields, currency_code or a figure. Unset, the report's main figure.
         */
        sort?: string | null;
        order?: VentawizReportsQuerySortOrder;
        /**
         * 1-based page number
         */
        page?: number;
        /**
         * Rows per page
         */
        limit?: number;
    };
    url: '/v1/reports/review-data';
};

export type ReportReviewDataErrors = {
    /**
     * No credential, or it could not be verified
     */
    401: unknown;
    /**
     * Authenticated, but not permitted
     */
    403: unknown;
    /**
     * Not found, or not visible to this caller
     */
    404: unknown;
    /**
     * Valid request, but the current state forbids it
     */
    409: unknown;
    /**
     * Validation failed
     */
    422: unknown;
};

export type ReportReviewDataResponses = {
    /**
     * Successful Response
     */
    200: ReviewDataReport;
};

export type ReportReviewDataResponse = ReportReviewDataResponses[keyof ReportReviewDataResponses];

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};